import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Table,
  ConfigProvider,
  theme,
  Modal,
  Button,
  Input,
  InputNumber,
  notification,
  Tag,
  Spin,
  Empty,
  Switch,
} from "antd";
import axios from "axios";
import moment from "moment";
import { useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import io from "socket.io-client";
import {
  IoBeakerOutline,
  IoBookOutline,
  IoBookmarkOutline,
  IoCalendarOutline,
  IoCameraOutline,
  IoCheckmarkCircleOutline,
  IoChevronBackOutline,
  IoChevronForwardOutline,
  IoCloseCircleOutline,
  IoCogOutline,
  IoCubeOutline,
  IoDiceOutline,
  IoDiamondOutline,
  IoExtensionPuzzleOutline,
  IoEyeOutline,
  IoFilterOutline,
  IoFlameOutline,
  IoFolderOpenOutline,
  IoGameControllerOutline,
  IoGiftOutline,
  IoListOutline,
  IoMedalOutline,
  IoPeopleOutline,
  IoPlayOutline,
  IoRefreshOutline,
  IoSearchOutline,
  IoStatsChartOutline,
  IoTimeOutline,
  IoTrophyOutline,
} from "react-icons/io5";
import { writeLastJoinedTournament } from "../../utils/lobbyQuickPlay";
import { useAuth } from "../../services/AuthContext";
import { isOwnerAccount } from "../../utils/ownerAccess";
import AdminPanel from "../../components/admin/AdminPanel";
import { clearStoredAuthToken, getStoredAuthToken } from "../../services/authStorage";
import {
  getLanguageCopy,
  getLocaleForLanguage,
  localizeTournamentName,
  normalizeLanguage,
} from "../../services/language";
import { setLanguage as setI18nLanguage, t, t as i18nT } from "../../i18n";
import "./replayR7.css";
import ReplayVideoDownload, { computeVideoPreviewRect } from "../../components/replay/ReplayVideoDownload";
import ReplayVoiceControl from "../../components/replay/ReplayVoiceControl";
import ReplayBlockVisualsToggle from "../../components/replay/ReplayBlockVisualsToggle";
import {
  REPLAY_BLOCK_VISUALS_EVENT,
  buildReplayBlockVisualsPayload,
  readReplayBlockVisualsMode,
  replayHasRealBlockDesigns,
  writeReplayBlockVisualsMode,
} from "../../services/replayBlockVisuals";
import { dispatchVoiceCommandToUnity } from "../../services/voiceControl";
import {
  BLOCK_TYPE_COMMON,
  BLOCK_TYPE_SPECIAL,
  SPECIAL_BLOCK_PRODUCTS,
  STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER,
  STORE_PRODUCT_ACCESS_PURCHASE,
  TOP_WINNER_MODE_SKIN_BY_MODE,
  normalizeOwnedStoreProductIds,
  normalizeStoreProductId,
  resolveSkinStoreProductForSkin,
  resolveSkinStoreProductById,
} from "../../constants/skinStoreCatalog";
import {
  DEFAULT_PREFERENCES,
  getPreferencesStorageKey,
  parsePreferences,
} from "../../services/userSettings";
import {
  normalizeProfilePlayerCode,
  resolveProfileTargetFromUserLike,
  resolvePlayerProfileRoute,
  buildCurrentUserProfileIdentitySet,
} from "./leaderboardProfileNavigation";
import { resolveAvatarSrc } from "../../utils/avatarUrl";
import { buildPuzzleTutorialLaunchOptions } from "../../utils/firstLobbyTutorial";
import { GUEST_LINK_KINDS, buildShortShareUrl } from "../../utils/guestLinkAccess";

const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL ||
  (typeof window !== "undefined" ? window.location.origin : "http://localhost:5001");
const UNITY_BUILD_DIR = String(process.env.REACT_APP_UNITY_BUILD_DIR || "Xima_build")
  .replace(/^\/+/, "")
  .replace(/\/+$/, "");
const UNITY_INDEX_FILE = String(process.env.REACT_APP_UNITY_INDEX_FILE || "index.html").replace(
  /^\/+/,
  ""
);
const UNITY_DEPLOY_VERSION = String(process.env.REACT_APP_DEPLOY_VERSION || "legacy");
const UNITY_IFRAME_BASE_PATH = `/${UNITY_BUILD_DIR}/${UNITY_INDEX_FILE}`;
const CAMERA_FOLLOW_GLOBAL_STORAGE_KEY = "xima_camera_follow_enabled_v1";

const readCameraFollowPreference = (email) => {
  if (typeof window === "undefined") return DEFAULT_PREFERENCES.cameraFollowEnabled;
  const storageKey = getPreferencesStorageKey(email);
  const storedPreferences = parsePreferences(window.localStorage.getItem(storageKey));
  const enabled = Boolean(
    storedPreferences?.cameraFollowEnabled ?? DEFAULT_PREFERENCES.cameraFollowEnabled
  );
  window.localStorage.setItem(CAMERA_FOLLOW_GLOBAL_STORAGE_KEY, enabled ? "1" : "0");
  return enabled;
};

const writeCameraFollowPreference = (email, enabled) => {
  if (typeof window === "undefined") return;
  const storageKey = getPreferencesStorageKey(email);
  const storedPreferences =
    parsePreferences(window.localStorage.getItem(storageKey)) || DEFAULT_PREFERENCES;
  window.localStorage.setItem(
    storageKey,
    JSON.stringify({
      ...storedPreferences,
      cameraFollowEnabled: enabled === true,
    })
  );
  window.localStorage.setItem(CAMERA_FOLLOW_GLOBAL_STORAGE_KEY, enabled === true ? "1" : "0");
};

const handleSessionInvalidated = () => {
  delete axios.defaults.headers["Authorization"];
  clearStoredAuthToken();
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
};

const getSharedSocket = () => {
  if (typeof window === "undefined") {
    return io(SOCKET_URL);
  }

  if (window.__ximaSocket) {
    window.__ximaSocket.off("SESSION_INVALIDATED", handleSessionInvalidated);
    window.__ximaSocket.on("SESSION_INVALIDATED", handleSessionInvalidated);
    if (
      window.__ximaSocket.disconnected &&
      typeof window.__ximaSocket.connect === "function"
    ) {
      window.__ximaSocket.connect();
    }
    window.socket = window.__ximaSocket;
    return window.__ximaSocket;
  }

  const sharedSocket = io(SOCKET_URL);
  sharedSocket.on("connect", () => {
    const token = getStoredAuthToken() || "0";
    sharedSocket.emit("verify", token);
  });
  sharedSocket.on("SESSION_INVALIDATED", handleSessionInvalidated);

  window.__ximaSocket = sharedSocket;
  window.socket = sharedSocket;
  return sharedSocket;
};

const socket = getSharedSocket();
const WAITING_ROOM_PRESENCE_JOIN_EVENT = "WAITING_ROOM_PRESENCE_JOIN";
const WAITING_ROOM_PRESENCE_LEAVE_EVENT = "WAITING_ROOM_PRESENCE_LEAVE";
const WAITING_ROOM_PRESENCE_REQUEST_EVENT = "WAITING_ROOM_PRESENCE_REQUEST";
const WAITING_ROOM_PRESENCE_SNAPSHOT_EVENT = "WAITING_ROOM_PRESENCE_SNAPSHOT";

const emitSocketVerify = () => {
  const token = getStoredAuthToken() || "0";
  socket.emit("verify", token);
};

const MODE_LABELS = {
  dice: "Azar",
  free: "Ciencia",
  cienzar: "Cienzar",
  sciencevsazar: "Ciencia V/S Azar",
  freebox: "FreeBox",
  botlocal: "Bot Local",
  botlocaldice: "Bot Azar",
  botlocalfree: "Bot Ciencia",
  botlocalcienzar: "Bot Cienzar",
  botlocalsciencevsazar: "Bot Ciencia V/S Azar",
  localdice: "Local Azar",
  localcienzar: "Local Cienzar",
  localsciencevsazar: "Local Ciencia V/S Azar",
  localfree: "Local Ciencia",
};
const MODE_ICON_MAP = {
  dice: "/mode-icons/dice.png",
  free: "/mode-icons/free.png",
  cienzar: "/mode-icons/cienzar.png",
  sciencevsazar: "/mode-icons/cienzar.png",
  localdice: "/mode-icons/dice.png",
  localfree: "/mode-icons/free.png",
  localcienzar: "/mode-icons/cienzar.png",
  localsciencevsazar: "/mode-icons/cienzar.png",
  botlocaldice: "/mode-icons/dice.png",
  botlocalfree: "/mode-icons/free.png",
  botlocalcienzar: "/mode-icons/cienzar.png",
  botlocalsciencevsazar: "/mode-icons/cienzar.png",
};
const PLAYER_COUNT_ICON_MAP = {
  2: "/assets/images/players-1v1.png",
  3: "/assets/images/players-1v3.png",
  4: "/assets/images/players-1-5.png",
};
const MATCH_DURATION_ICON_MAP = {
  1: "/assets/images/1minutomorado.svg",
  2: "/assets/images/2minutosmorados.svg",
  5: "/assets/images/5minutosmorados.png",
  10: "/assets/images/10minutosmorados.png",
  30: "/assets/images/30minutosmorados.png",
};
const TOURNAMENT_BUTTON_ICON_SRC = "/assets/images/podioconbloquemorado36.png";
const PUZZLE_BUTTON_ICON_SRC = "/assets/images/puzzlesolomorado35.png";
const INDEPENDENT_BUTTON_ICON_SRC = "/assets/images/independientes-button.png";
const FRIENDS_BUTTON_ICON_SRC = "/assets/images/amigos-button.png";
const MATCH_PASSWORD_ICON_SRC = "/assets/images/candado-blanco-negro.svg";
const LOBBY_SOURCE_MODE_PANEL_TITLE_KEY_MAP = {
  cube: "mobile.modeDesign",
  store: "mobile.modeStore",
  history: "mobile.modeHistory",
  friends: "mobile.modeFriends",
  learn: "mobile.modeLearn",
  spectate: "mobile.modeSpectate",
  settings: "mobile.modeSettings",
  puzzle: "mobile.modePuzzle",
  torneos: "mobile.modeTournament",
  independientes: "mobile.modeIndependentMatches",
};
const HISTORY_COPY = Object.freeze({
  title: {
    es: "Historial de partidas",
    en: "Match history",
    de: "Spielhistorie",
    ru: "История партий",
  },
  subtitle: {
    es: "Revisa tus partidas anteriores, analiza tu rendimiento y mejora tu estrategia.",
    en: "Review your previous matches, analyze your performance, and improve your strategy.",
    de: "Sieh dir deine bisherigen Partien an, analysiere deine Leistung und verbessere deine Strategie.",
    ru: "Просматривай свои предыдущие партии, анализируй результаты и улучшай стратегию.",
  },
  viewStats: {
    es: "Ver estadísticas",
    en: "View stats",
    de: "Statistiken anzeigen",
    ru: "Показать статистику",
  },
  viewAll: {
    es: "Ver todo",
    en: "View all",
    de: "Alle anzeigen",
    ru: "Показать всё",
  },
  viewPaginated: {
    es: "Ver paginado",
    en: "View paginated",
    de: "Seitenansicht",
    ru: "Постраничный вид",
  },
  refreshHistory: {
    es: "Actualizar historial",
    en: "Refresh history",
    de: "Verlauf aktualisieren",
    ru: "Обновить историю",
  },
  levelLabel: {
    es: "Nivel {level}",
    en: "Level {level}",
    de: "Stufe {level}",
    ru: "Уровень {level}",
  },
  totalMatches: {
    es: "Partidas totales",
    en: "Total matches",
    de: "Partien insgesamt",
    ru: "Всего партий",
  },
  wins: {
    es: "Victorias",
    en: "Wins",
    de: "Siege",
    ru: "Победы",
  },
  losses: {
    es: "Derrotas",
    en: "Losses",
    de: "Niederlagen",
    ru: "Поражения",
  },
  currentStreak: {
    es: "Racha actual",
    en: "Current streak",
    de: "Aktuelle Serie",
    ru: "Текущая серия",
  },
  filters: {
    es: "Filtros",
    en: "Filters",
    de: "Filter",
    ru: "Фильтры",
  },
  clearAll: {
    es: "Limpiar todo",
    en: "Clear all",
    de: "Alles zurücksetzen",
    ru: "Сбросить всё",
  },
  result: {
    es: "Resultado",
    en: "Result",
    de: "Ergebnis",
    ru: "Результат",
  },
  all: {
    es: "Todos",
    en: "All",
    de: "Alle",
    ru: "Все",
  },
  won: {
    es: "Ganadas",
    en: "Wins",
    de: "Siege",
    ru: "Победы",
  },
  lost: {
    es: "Perdidas",
    en: "Losses",
    de: "Niederlagen",
    ru: "Поражения",
  },
  gameMode: {
    es: "Modo de juego",
    en: "Game mode",
    de: "Spielmodus",
    ru: "Режим игры",
  },
  allCount: {
    es: "Todos ({count})",
    en: "All ({count})",
    de: "Alle ({count})",
    ru: "Все ({count})",
  },
  chanceCount: {
    es: "Azar ({count})",
    en: "Chance ({count})",
    de: "Zufall ({count})",
    ru: "Случайность ({count})",
  },
  cienzarCount: {
    es: "CIENZAR ({count})",
    en: "CIENZAR ({count})",
    de: "CIENZAR ({count})",
    ru: "CIENZAR ({count})",
  },
  scienceCount: {
    es: "Ciencia ({count})",
    en: "Science ({count})",
    de: "Wissenschaft ({count})",
    ru: "Наука ({count})",
  },
  player: {
    es: "Jugador",
    en: "Player",
    de: "Spieler",
    ru: "Игрок",
  },
  playerSearchPlaceholder: {
    es: "Buscar jugador...",
    en: "Search for a player...",
    de: "Spieler suchen...",
    ru: "Найти игрока...",
  },
  dateRange: {
    es: "Rango de fechas",
    en: "Date range",
    de: "Zeitraum",
    ru: "Диапазон дат",
  },
  allDates: {
    es: "Todas las fechas",
    en: "All dates",
    de: "Gesamter Zeitraum",
    ru: "За всё время",
  },
  today: {
    es: "Hoy",
    en: "Today",
    de: "Heute",
    ru: "Сегодня",
  },
  yesterday: {
    es: "Ayer",
    en: "Yesterday",
    de: "Gestern",
    ru: "Вчера",
  },
  thisWeek: {
    es: "Esta semana",
    en: "This week",
    de: "Diese Woche",
    ru: "Эта неделя",
  },
  thisMonth: {
    es: "Este mes",
    en: "This month",
    de: "Dieser Monat",
    ru: "Этот месяц",
  },
  applyFilters: {
    es: "Aplicar filtros",
    en: "Apply filters",
    de: "Filter anwenden",
    ru: "Применить фильтры",
  },
  saveFilter: {
    es: "Guardar filtro",
    en: "Save filter",
    de: "Filter speichern",
    ru: "Сохранить фильтр",
  },
  matchesFound: {
    es: "{count} partidas encontradas",
    en: "{count} matches found",
    de: "{count} Partien gefunden",
    ru: "Найдено {count} {unit}",
  },
  showing: {
    es: "Mostrando {shown} de {total}",
    en: "Showing {shown} of {total}",
    de: "Angezeigt: {shown} von {total}",
    ru: "Показано {shown} из {total}",
  },
  showingMatches: {
    es: "Mostrando {shown} de {total} partidas",
    en: "Showing {shown} of {total} matches",
    de: "{shown} von {total} Partien werden angezeigt",
    ru: "Показано {shown} из {total} {unit}",
  },
  sortBy: {
    es: "Ordenar por:",
    en: "Sort by:",
    de: "Sortieren nach:",
    ru: "Сортировать по:",
  },
  mostRecent: {
    es: "Más recientes",
    en: "Most recent",
    de: "Neueste zuerst",
    ru: "Сначала новые",
  },
  oldest: {
    es: "Más antiguas",
    en: "Oldest",
    de: "Älteste zuerst",
    ru: "Сначала старые",
  },
  mode: {
    es: "Modo",
    en: "Mode",
    de: "Modus",
    ru: "Режим",
  },
  players: {
    es: "Jugadores",
    en: "Players",
    de: "Spieler",
    ru: "Игроки",
  },
  playerStrength: {
    es: "Fuerza del jugador",
    en: "Player strength",
    de: "Spielerstärke",
    ru: "Сила игрока",
  },
  level: {
    es: "Nivel",
    en: "Level",
    de: "Stufe",
    ru: "Уровень",
  },
  averagePower: {
    es: "Potencia media",
    en: "Average power",
    de: "Durchschnittliche Leistung",
    ru: "Средняя мощность",
  },
  date: {
    es: "Fecha",
    en: "Date",
    de: "Datum",
    ru: "Дата",
  },
  replay: {
    es: "Repetición",
    en: "Replay",
    de: "Wiederholung",
    ru: "Повтор",
  },
  victory: {
    es: "Victoria",
    en: "Victory",
    de: "Sieg",
    ru: "Победа",
  },
  defeat: {
    es: "Derrota",
    en: "Defeat",
    de: "Niederlage",
    ru: "Поражение",
  },
  draw: {
    es: "Empate",
    en: "Draw",
    de: "Unentschieden",
    ru: "Ничья",
  },
  opponent: {
    es: "Rival",
    en: "Opponent",
    de: "Gegner",
    ru: "Соперник",
  },
  system: {
    es: "Sistema",
    en: "System",
    de: "System",
    ru: "Система",
  },
  chanceMode: {
    es: "Azar",
    en: "Chance",
    de: "Zufall",
    ru: "Случайность",
  },
  scienceMode: {
    es: "Ciencia",
    en: "Science",
    de: "Wissenschaft",
    ru: "Наука",
  },
  filterSavedTitle: {
    es: "Filtro guardado",
    en: "Filter saved",
    de: "Filter gespeichert",
    ru: "Фильтр сохранён",
  },
  filterSavedDescription: {
    es: "Tu selección de historial quedó guardada en este navegador.",
    en: "Your history selection was saved in this browser.",
    de: "Deine Historienauswahl wurde in diesem Browser gespeichert.",
    ru: "Ваш выбор истории сохранён в этом браузере.",
  },
  emptyDescription: {
    es: "Aún no tienes partidas finalizadas para este filtro.",
    en: "You do not have finished matches for this filter yet.",
    de: "Für diesen Filter hast du noch keine abgeschlossenen Partien.",
    ru: "Для этого фильтра пока нет завершённых партий.",
  },
  clearSearch: {
    es: "Limpiar búsqueda",
    en: "Clear search",
    de: "Suche löschen",
    ru: "Очистить поиск",
  },
  previousPage: {
    es: "Página anterior",
    en: "Previous page",
    de: "Vorherige Seite",
    ru: "Предыдущая страница",
  },
  nextPage: {
    es: "Página siguiente",
    en: "Next page",
    de: "Nächste Seite",
    ru: "Следующая страница",
  },
  viewReplay: {
    es: "Ver repetición",
    en: "View replay",
    de: "Wiederholung ansehen",
    ru: "Посмотреть повтор",
  },
});
const HISTORY_RUSSIAN_MATCH_UNIT = Object.freeze({
  one: "партия",
  few: "партии",
  many: "партий",
  other: "партии",
});
const getHistoryCopy = (language, key, values = {}) => {
  const normalizedLanguage = normalizeLanguage(language);
  const entry = HISTORY_COPY[key] || {};
  let copy = entry[normalizedLanguage] || entry.es || entry.en || "";

  Object.entries(values || {}).forEach(([name, value]) => {
    copy = copy.split(`{${name}}`).join(String(value));
  });

  return copy;
};
const FIND_MATCHES_COPY = Object.freeze({
  backToLobby: {
    es: "VOLVER AL LOBBY",
    en: "BACK TO LOBBY",
    de: "ZURÜCK ZUR LOBBY",
    ru: "НАЗАД В ЛОББИ",
  },
  findMatches: {
    es: "BUSCAR PARTIDOS",
    en: "FIND MATCHES",
    de: "PARTIEN SUCHEN",
    ru: "НАЙТИ ПАРТИИ",
  },
  intro: {
    es: "Únete a desafíos creados por otros jugadores en XIMA.",
    en: "Join challenges created by other players in XIMA.",
    de: "Tritt Herausforderungen bei, die von anderen Spielern in XIMA erstellt wurden.",
    ru: "Присоединяйся к вызовам, созданным другими игроками в XIMA.",
  },
  scienceModeTitle: {
    es: "MODO CIENCIA",
    en: "SCIENCE MODE",
    de: "WISSENSCHAFTSMODUS",
    ru: "РЕЖИМ «НАУКА»",
  },
  scienceShort: {
    es: "CIENCIA",
    en: "SCIENCE",
    de: "WISSENSCHAFT",
    ru: "НАУКА",
  },
  scienceSubtitle: {
    es: "La estrategia lo es todo.",
    en: "Strategy is everything.",
    de: "Strategie ist alles.",
    ru: "Стратегия решает всё.",
  },
  scienceDetail: {
    es: "Encuentra desafíos creados por jugadores.",
    en: "Find challenges created by players.",
    de: "Finde von Spielern erstellte Herausforderungen.",
    ru: "Найди вызовы, созданные игроками.",
  },
  chanceModeTitle: {
    es: "MODO AZAR",
    en: "CHANCE MODE",
    de: "ZUFALLSMODUS",
    ru: "РЕЖИМ СЛУЧАЙНОСТИ",
  },
  chanceShort: {
    es: "AZAR",
    en: "CHANCE",
    de: "ZUFALL",
    ru: "СЛУЧАЙНОСТЬ",
  },
  chanceSubtitle: {
    es: "La suerte decide tu camino.",
    en: "Luck decides your path.",
    de: "Das Glück bestimmt deinen Weg.",
    ru: "Удача выбирает твой путь.",
  },
  chanceDetail: {
    es: "Únete a partidas con dados.",
    en: "Join matches with dice.",
    de: "Tritt Partien mit Würfeln bei.",
    ru: "Присоединяйся к партиям с кубиками.",
  },
  cienzarModeTitle: {
    es: "MODO CIENZAR",
    en: "CIENZAR MODE",
    de: "CIENZAR-MODUS",
    ru: "РЕЖИМ CIENZAR",
  },
  cienzarShort: {
    es: "CIENZAR",
    en: "CIENZAR",
    de: "CIENZAR",
    ru: "CIENZAR",
  },
  cienzarSubtitle: {
    es: "Estrategia y suerte juntas.",
    en: "Strategy and luck together.",
    de: "Strategie und Glück vereint.",
    ru: "Стратегия и удача вместе.",
  },
  cienzarDetail: {
    es: "Busca desafíos mixtos en vivo.",
    en: "Find mixed live challenges.",
    de: "Finde gemischte Live-Herausforderungen.",
    ru: "Найди смешанные живые вызовы.",
  },
  scienceVsAzarModeTitle: {
    es: "CIENCIA V/S AZAR",
    en: "SCIENCE V/S CHANCE",
    de: "WISSENSCHAFT V/S ZUFALL",
    ru: "SCIENCE V/S CHANCE",
  },
  scienceVsAzarShort: {
    es: "CIENCIA V/S AZAR",
    en: "SCIENCE V/S CHANCE",
    de: "WISSENSCHAFT V/S ZUFALL",
    ru: "SCIENCE V/S CHANCE",
  },
  scienceVsAzarSubtitle: {
    es: "Ciencia contra azar.",
    en: "Science against chance.",
    de: "Wissenschaft gegen Zufall.",
    ru: "Science against chance.",
  },
  scienceVsAzarDetail: {
    es: "Roles fijos durante toda la partida.",
    en: "Fixed roles for the whole match.",
    de: "Feste Rollen fuer die ganze Partie.",
    ru: "Fixed roles for the whole match.",
  },
  challengesCreated: {
    es: "DESAFÍOS CREADOS",
    en: "CHALLENGES CREATED",
    de: "ERSTELLTE HERAUSFORDERUNGEN",
    ru: "СОЗДАННЫЕ ВЫЗОВЫ",
  },
  independentMatches: {
    es: "PARTIDOS INDEPENDIENTES",
    en: "INDEPENDENT MATCHES",
    de: "UNABHÄNGIGE PARTIEN",
    ru: "НЕЗАВИСИМЫЕ ПАРТИИ",
  },
  available: {
    es: "DISPONIBLES",
    en: "AVAILABLE",
    de: "VERFÜGBAR",
    ru: "ДОСТУПНЫЕ",
  },
  readyToJoin: {
    es: "LISTOS PARA UNIRSE",
    en: "READY TO JOIN",
    de: "BEREIT ZUM BEITRETEN",
    ru: "ГОТОВЫ К ВХОДУ",
  },
  playersJoined: {
    es: "JUGADORES UNIDOS",
    en: "PLAYERS JOINED",
    de: "BEIGETRETENE SPIELER",
    ru: "ПРИСОЕДИНИВШИЕСЯ ИГРОКИ",
  },
  waitingForOpponent: {
    es: "ESPERANDO RIVAL",
    en: "WAITING FOR OPPONENT",
    de: "WARTET AUF GEGNER",
    ru: "ОЖИДАНИЕ СОПЕРНИКА",
  },
  withPassword: {
    es: "CON CONTRASEÑA",
    en: "WITH PASSWORD",
    de: "MIT PASSWORT",
    ru: "С ПАРОЛЕМ",
  },
  requireKey: {
    es: "REQUIEREN CLAVE",
    en: "REQUIRE KEY",
    de: "BENÖTIGEN SCHLÜSSEL",
    ru: "ТРЕБУЮТ КЛЮЧ",
  },
  paid: {
    es: "DE PAGO",
    en: "PAID",
    de: "KOSTENPFLICHTIG",
    ru: "ПЛАТНЫЕ",
  },
  requireBonuses: {
    es: "REQUIEREN BONOS",
    en: "REQUIRE BONUSES",
    de: "BENÖTIGEN BONI",
    ru: "ТРЕБУЮТ БОНУСЫ",
  },
  openChallenges: {
    es: "DESAFÍOS ABIERTOS",
    en: "OPEN CHALLENGES",
    de: "OFFENE HERAUSFORDERUNGEN",
    ru: "ОТКРЫТЫЕ ВЫЗОВЫ",
  },
  openChallengesDescription: {
    es: "Partidos creados desde la interfaz Crear Nuevo Juego.",
    en: "Matches created from the Create New Game interface.",
    de: "Partien, die über die Oberfläche „Neues Spiel erstellen“ erstellt wurden.",
    ru: "Партии, созданные через интерфейс «Создать новую игру».",
  },
  filterByMode: {
    es: "FILTRAR POR MODO",
    en: "FILTER BY MODE",
    de: "NACH MODUS FILTERN",
    ru: "ФИЛЬТР ПО РЕЖИМУ",
  },
  allModes: {
    es: "Todos los modos",
    en: "All modes",
    de: "Alle Modi",
    ru: "Все режимы",
  },
  search: {
    es: "BUSCAR",
    en: "SEARCH",
    de: "SUCHE",
    ru: "ПОИСК",
  },
  playerOrChallengePlaceholder: {
    es: "Jugador o desafío...",
    en: "Player or challenge...",
    de: "Spieler oder Herausforderung...",
    ru: "Игрок или вызов...",
  },
  sort: {
    es: "ORDENAR",
    en: "SORT",
    de: "SORTIEREN",
    ru: "СОРТИРОВАТЬ",
  },
  mostRecent: {
    es: "Más recientes",
    en: "Most recent",
    de: "Neueste zuerst",
    ru: "Сначала новые",
  },
  oldest: {
    es: "Más antiguos",
    en: "Oldest",
    de: "Älteste zuerst",
    ru: "Сначала старые",
  },
  mostPlayers: {
    es: "Más jugadores",
    en: "Most players",
    de: "Meiste Spieler",
    ru: "Больше игроков",
  },
  shortestTime: {
    es: "Menor tiempo",
    en: "Shortest time",
    de: "Kürzeste Zeit",
    ru: "Меньше времени",
  },
  noOpenChallenges: {
    es: "No hay desafíos abiertos para este filtro.",
    en: "There are no open challenges for this filter.",
    de: "Für diesen Filter gibt es keine offenen Herausforderungen.",
    ru: "Для этого фильтра нет открытых вызовов.",
  },
  howItWorks: {
    es: "CÓMO FUNCIONA",
    en: "HOW IT WORKS",
    de: "SO FUNKTIONIERT ES",
    ru: "КАК ЭТО РАБОТАЕТ",
  },
  chooseMode: {
    es: "Elige un modo",
    en: "Choose a mode",
    de: "Wähle einen Modus",
    ru: "Выбери режим",
  },
  filterByModes: {
    es: "Filtra por Ciencia, Azar o Cienzar.",
    en: "Filter by Science, Chance, or Cienzar.",
    de: "Filtere nach Wissenschaft, Zufall oder Cienzar.",
    ru: "Фильтруй по режимам «Наука», «Случайность» или Cienzar.",
  },
  joinChallenge: {
    es: "Únete al desafío",
    en: "Join the challenge",
    de: "Tritt der Herausforderung bei",
    ru: "Присоединись к вызову",
  },
  joinButtonIndependent: {
    es: "El botón Unirse usa el mismo flujo de partidas independientes.",
    en: "The Join button uses the same flow as independent matches.",
    de: "Die Schaltfläche „Beitreten“ verwendet denselben Ablauf wie unabhängige Partien.",
    ru: "Кнопка «Присоединиться» использует тот же процесс, что и независимые партии.",
  },
  playLive: {
    es: "Juega en vivo",
    en: "Play live",
    de: "Live spielen",
    ru: "Играй вживую",
  },
  matchCompleteBoard: {
    es: "Cuando se complete la partida, se abre el tablero para jugar.",
    en: "When the match is complete, the board opens to play.",
    de: "Wenn die Partie vollständig ist, öffnet sich das Spielbrett zum Spielen.",
    ru: "Когда партия будет укомплектована, откроется доска для игры.",
  },
  topPlayers: {
    es: "MEJORES JUGADORES",
    en: "TOP PLAYERS",
    de: "BESTE SPIELER",
    ru: "ЛУЧШИЕ ИГРОКИ",
  },
  viewFullRanking: {
    es: "VER RANKING COMPLETO",
    en: "VIEW FULL RANKING",
    de: "VOLLSTÄNDIGE RANGLISTE ANZEIGEN",
    ru: "ПОСМОТРЕТЬ ПОЛНЫЙ РЕЙТИНГ",
  },
  challengeModesAria: {
    es: "Modos de desafío",
    en: "Challenge modes",
    de: "Herausforderungsmodi",
    ru: "Режимы вызовов",
  },
  matchSummaryAria: {
    es: "Resumen de partidos",
    en: "Match summary",
    de: "Partienübersicht",
    ru: "Сводка партий",
  },
  refreshMatches: {
    es: "Actualizar partidos",
    en: "Refresh matches",
    de: "Partien aktualisieren",
    ru: "Обновить партии",
  },
  matchFull: {
    es: "Partido completo",
    en: "Match full",
    de: "Partie vollständig",
    ru: "Партия укомплектована",
  },
  waitingOnePlayer: {
    es: "Esperando 1 jugador",
    en: "Waiting for 1 player",
    de: "Wartet auf 1 Spieler",
    ru: "Ожидание 1 игрока",
  },
  waitingPlayers: {
    es: "Esperando {count} jugadores",
    en: "Waiting for {count} players",
    de: "Wartet auf {count} Spieler",
    ru: "Ожидание игроков: {count}",
  },
  joinMatchAria: {
    es: "Unirse a {title}",
    en: "Join {title}",
    de: "{title} beitreten",
    ru: "Присоединиться к {title}",
  },
  players: {
    es: "jugadores",
    en: "players",
    de: "Spieler",
    ru: "игроки",
  },
  createdBy: {
    es: "Creado por {name}",
    en: "Created by {name}",
    de: "Erstellt von {name}",
    ru: "Создано: {name}",
  },
  live: {
    es: "En vivo",
    en: "Live",
    de: "Live",
    ru: "Вживую",
  },
  payment: {
    es: "Pago {amount}X",
    en: "Paid {amount}X",
    de: "Kostenpflichtig {amount}X",
    ru: "Платно {amount}X",
  },
  copyLink: {
    es: "Copiar enlace",
    en: "Copy link",
    de: "Link kopieren",
    ru: "Скопировать ссылку",
  },
  minutesPerTurn: {
    es: "{minutes} min por turno",
    en: "{minutes} min per turn",
    de: "{minutes} Min. pro Zug",
    ru: "{minutes} мин на ход",
  },
  modality: {
    es: "Modalidad",
    en: "Mode type",
    de: "Modalität",
    ru: "Формат",
  },
  status: {
    es: "ESTADO",
    en: "STATUS",
    de: "STATUS",
    ru: "СТАТУС",
  },
  missingPlayers: {
    es: "{count} faltan",
    en: "{count} missing",
    de: "{count} fehlen",
    ru: "Не хватает: {count}",
  },
  join: {
    es: "UNIRSE",
    en: "JOIN",
    de: "BEITRETEN",
    ru: "ПРИСОЕДИНИТЬСЯ",
  },
  complete: {
    es: "COMPLETO",
    en: "FULL",
    de: "VOLLSTÄNDIG",
    ru: "ЗАПОЛНЕНО",
  },
  noRankingAvailable: {
    es: "Sin ranking disponible.",
    en: "No ranking available.",
    de: "Keine Rangliste verfügbar.",
    ru: "Рейтинг недоступен.",
  },
  playerFallback: {
    es: "Jugador",
    en: "Player",
    de: "Spieler",
    ru: "Игрок",
  },
  challengeFallback: {
    es: "Desafío {count}",
    en: "Challenge {count}",
    de: "Herausforderung {count}",
    ru: "Вызов {count}",
  },
  now: {
    es: "Ahora",
    en: "Now",
    de: "Jetzt",
    ru: "Сейчас",
  },
});
const getFindMatchesCopy = (language, key, values = {}) => {
  let copy = getLanguageCopy(language, FIND_MATCHES_COPY[key], "");

  Object.entries(values || {}).forEach(([name, value]) => {
    copy = copy.split(`{${name}}`).join(String(value));
  });

  return copy;
};
const getHistoryRussianMatchUnit = (count) => {
  const category = new Intl.PluralRules("ru-RU").select(Math.abs(Math.round(Number(count) || 0)));
  return HISTORY_RUSSIAN_MATCH_UNIT[category] || HISTORY_RUSSIAN_MATCH_UNIT.other;
};
const formatHistoryNumber = (value, language = "es", options = {}) =>
  new Intl.NumberFormat(getLocaleForLanguage(language), options).format(Number(value) || 0);
const formatHistoryPercent = (value, language = "es") =>
  `${formatHistoryNumber(value, language, { maximumFractionDigits: 1 })}%`;
const formatPublicSequenceLabel = (language, type, value) => {
  const formattedValue = formatHistoryNumber(value, language);
  const normalizedLanguage = normalizeLanguage(language);
  const normalizedType = String(type || "").trim().toLowerCase();

  if (normalizedType === "challenge") {
    if (normalizedLanguage === "en") return `Challenge #${formattedValue}`;
    if (normalizedLanguage === "de") return `Herausforderung #${formattedValue}`;
    return `Desafio #${formattedValue}`;
  }

  if (normalizedLanguage === "en") return `Tournament #${formattedValue}`;
  if (normalizedLanguage === "de") return `Turnier #${formattedValue}`;
  return `Torneo #${formattedValue}`;
};
const formatPublicSequenceShortLabel = (language, value) =>
  `#${formatHistoryNumber(value, language)}`;
const normalizePublicSequenceNumberValue = (value) => {
  const parsed = Math.floor(Number(value) || 0);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};
const resolvePublicSequenceNumber = (entry) => {
  return (
    normalizePublicSequenceNumberValue(entry?.tournamentUiSequenceNumber) ||
    normalizePublicSequenceNumberValue(entry?.tournamentSequenceNumber) ||
    normalizePublicSequenceNumberValue(entry?.tournamentNumber) ||
    normalizePublicSequenceNumberValue(entry?.displayNumber) ||
    0
  );
};
const resolveVisibleJoinedCount = (value) =>
  Math.max(0, Math.floor(Number(value) || 0) - 1);
const formatHistoryLevelLabel = (language, level) => {
  const parsedLevel = Number(level);
  return getHistoryCopy(language, "levelLabel", {
    level: Number.isFinite(parsedLevel)
      ? formatHistoryNumber(parsedLevel, language)
      : String(level || "-"),
  });
};
const formatHistoryMatchesFoundLabel = (language, count) => {
  const normalizedLanguage = normalizeLanguage(language);
  const countLabel = formatHistoryNumber(count, normalizedLanguage);
  return getHistoryCopy(normalizedLanguage, "matchesFound", {
    count: countLabel,
    unit:
      normalizedLanguage === "ru"
        ? getHistoryRussianMatchUnit(count)
        : "",
  }).replace(/\s+/g, " ").trim();
};
const formatHistoryShowingLabel = (language, shown, total) =>
  getHistoryCopy(language, "showing", {
    shown: formatHistoryNumber(shown, language),
    total: formatHistoryNumber(total, language),
  });
const formatHistoryShowingMatchesLabel = (language, shown, total) => {
  const normalizedLanguage = normalizeLanguage(language);
  return getHistoryCopy(normalizedLanguage, "showingMatches", {
    shown: formatHistoryNumber(shown, normalizedLanguage),
    total: formatHistoryNumber(total, normalizedLanguage),
    unit:
      normalizedLanguage === "ru"
        ? getHistoryRussianMatchUnit(total)
        : "",
  }).replace(/\s+/g, " ").trim();
};
const formatHistoryModeFilterLabel = (language, key, count) => {
  const copyKeyByMode = {
    all: "allCount",
    azar: "chanceCount",
    cienzar: "cienzarCount",
    ciencia: "scienceCount",
  };
  return getHistoryCopy(language, copyKeyByMode[key] || "allCount", {
    count: formatHistoryNumber(count, language),
  });
};
const formatHistoryModeLabel = (language, mode) => {
  const normalized = normalizeModeAlias(mode, "");
  if (normalized === "dice" || normalized === "localdice" || normalized === "botlocaldice") {
    return getHistoryCopy(language, "chanceMode");
  }
  if (
    normalized === "cienzar" ||
    normalized === "localcienzar" ||
    normalized === "botlocalcienzar"
  ) {
    return "CIENZAR";
  }
  if (
    normalized === "sciencevsazar" ||
    normalized === "localsciencevsazar" ||
    normalized === "botlocalsciencevsazar"
  ) {
    return "CIENCIA V/S AZAR";
  }
  if (normalized === "free" || normalized === "localfree" || normalized === "botlocalfree") {
    return getHistoryCopy(language, "scienceMode");
  }
  return MODE_LABELS[normalized] || String(mode || "").toUpperCase();
};
const formatHistoryDateLabel = (language, timeMs, nowMs = Date.now()) => {
  const parsedTime = Number(timeMs);
  if (!Number.isFinite(parsedTime) || parsedTime <= 0) return "-";
  const dateMoment = moment(parsedTime);
  if (!dateMoment.isValid()) return "-";
  const nowMoment = moment(nowMs);
  if (nowMoment.isSame(dateMoment, "day")) return getHistoryCopy(language, "today");
  if (nowMoment.clone().subtract(1, "day").isSame(dateMoment, "day")) {
    return getHistoryCopy(language, "yesterday");
  }
  return new Intl.DateTimeFormat(getLocaleForLanguage(language), {
    day: "numeric",
    month: "short",
  }).format(new Date(parsedTime));
};
const formatHistoryTimeLabel = (language, timeMs) => {
  const parsedTime = Number(timeMs);
  if (!Number.isFinite(parsedTime) || parsedTime <= 0) return "";
  return new Intl.DateTimeFormat(getLocaleForLanguage(language), {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(parsedTime));
};
const GUEST_USER_PREFIX = "invitadoo";
const GUEST_EMAIL_DOMAIN = "guest.xima.local";
const PENDING_LOBBY_CREATE_STORAGE_KEY = "xima_pending_lobby_create_v1";
const PENDING_LOBBY_CREATE_MAX_AGE_MS = 5 * 60 * 1000;

const ONLINE_MODE_BUTTONS = [
  { value: "ciencia", label: "Ciencia", text: "text-[#68ccff]" },
  { value: "cienzar", label: "Cienzar", text: "text-[#6fff8a]" },
  { value: "sciencevsazar", label: "Ciencia V/S Azar", text: "text-[#ff7a7a]" },
  { value: "azar", label: "Azar", text: "text-[#ffe267]" },
];
const DEFAULT_CIMAX_SCORES = Object.freeze({
  ciencia: 3000,
  cienzar: 2000,
  sciencevsazar: 2000,
  azar: 1000,
});
const DEFAULT_WALLET_BALANCE = 0;
const START_SCREEN_CIMAX_BUTTONS = [
  {
    key: "ciencia",
    label: i18nT("lobby.cimaxCiencia"),
    className:
      "border-[#4c92d5] bg-[#12325d] text-[#e7f3ff] hover:border-[#74b5ef] hover:bg-[#1a447a]",
  },
  {
    key: "cienzar",
    label: i18nT("lobby.cimaxCienzar"),
    className:
      "border-[#4ba16d] bg-[#143f2a] text-[#e9fff2] hover:border-[#73c896] hover:bg-[#1d573a]",
  },
  {
    key: "azar",
    label: i18nT("lobby.cimaxAzar"),
    className:
      "border-[#c7a755] bg-[#4f3f13] text-[#fff5d1] hover:border-[#e0c277] hover:bg-[#68521a]",
  },
];
const normalizeCimaxScores = (rawValue) => {
  const raw =
    rawValue && typeof rawValue === "object" && !Array.isArray(rawValue) ? rawValue : {};
  const toScore = (value, fallback) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.max(0, Math.round(parsed));
  };
  return {
    ciencia: toScore(raw.ciencia, DEFAULT_CIMAX_SCORES.ciencia),
    cienzar: toScore(raw.cienzar, DEFAULT_CIMAX_SCORES.cienzar),
    sciencevsazar: toScore(
      raw.sciencevsazar ?? raw.contrazar ?? raw.cimaxContrazar,
      DEFAULT_CIMAX_SCORES.sciencevsazar
    ),
    azar: toScore(raw.azar, DEFAULT_CIMAX_SCORES.azar),
  };
};
const normalizeWalletBalance = (rawValue) => {
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) return DEFAULT_WALLET_BALANCE;
  return Math.max(0, Math.round(parsed));
};
const MATCH_PASSWORD_MIN_LENGTH = 1;
const MATCH_PASSWORD_MAX_LENGTH = 4;
const MATCH_PASSWORD_REGEX = /^\d+$/;
const TOURNAMENT_MATCH_PASSWORD_SESSION_KEY = "xima_tournament_match_password_v1";
const normalizeMatchPassword = (value) =>
  String(value || "")
    .replace(/[^\d]/g, "")
    .slice(0, MATCH_PASSWORD_MAX_LENGTH);
const isValidMatchPassword = (value) => {
  const normalizedPassword = normalizeMatchPassword(value);
  return (
    normalizedPassword.length >= MATCH_PASSWORD_MIN_LENGTH &&
    normalizedPassword.length <= MATCH_PASSWORD_MAX_LENGTH &&
    MATCH_PASSWORD_REGEX.test(normalizedPassword)
  );
};

const readTournamentMatchPasswordFromSession = (rawTournamentId) => {
  const tournamentId = String(rawTournamentId || "").trim();
  if (!tournamentId) return "";
  if (typeof window === "undefined") return "";
  try {
    const raw = window.sessionStorage.getItem(TOURNAMENT_MATCH_PASSWORD_SESSION_KEY);
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return "";
    return normalizeMatchPassword(parsed[tournamentId]);
  } catch (error) {
    return "";
  }
};

const persistTournamentMatchPasswordToSession = (rawTournamentId, rawPassword) => {
  const tournamentId = String(rawTournamentId || "").trim();
  if (!tournamentId) return;
  if (typeof window === "undefined") return;

  const normalizedPassword = normalizeMatchPassword(rawPassword || "");
  let current = {};
  try {
    const raw = window.sessionStorage.getItem(TOURNAMENT_MATCH_PASSWORD_SESSION_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        current = parsed;
      }
    }
  } catch (error) {
    current = {};
  }

  const next = { ...current };
  if (normalizedPassword) {
    next[tournamentId] = normalizedPassword;
  } else {
    delete next[tournamentId];
  }

  if (Object.keys(next).length > 0) {
    window.sessionStorage.setItem(TOURNAMENT_MATCH_PASSWORD_SESSION_KEY, JSON.stringify(next));
  } else {
    window.sessionStorage.removeItem(TOURNAMENT_MATCH_PASSWORD_SESSION_KEY);
  }
};

const clearTournamentMatchPasswordFromSession = (rawTournamentId) => {
  persistTournamentMatchPasswordToSession(rawTournamentId, "");
};
const LOCAL_MODE_MAP = {
  dice: "localdice",
  cienzar: "localcienzar",
  sciencevsazar: "localsciencevsazar",
  free: "localfree",
};
const LOCAL_MODE_REVERSE_MAP = Object.entries(LOCAL_MODE_MAP).reduce((acc, [baseMode, localMode]) => {
  acc[localMode] = baseMode;
  return acc;
}, {});
const LOCAL_FRIENDS_MODES = new Set(Object.values(LOCAL_MODE_MAP));
const ONLINE_LOBBY_MODES = new Set(["dice", "cienzar", "sciencevsazar", "free", "freebox"]);
const LOBBY_MODES = new Set(["dice", "cienzar", "sciencevsazar", "free", "freebox"]);
const MODE_ALIASES = {
  azar: "dice",
  dice: "dice",
  ciencia: "free",
  free: "free",
  libre: "free",
  cienzar: "cienzar",
  sciluck: "cienzar",
  sciencevsazar: "sciencevsazar",
  "science-vs-azar": "sciencevsazar",
  cienciavsazar: "sciencevsazar",
  "ciencia-vs-azar": "sciencevsazar",
  localazar: "localdice",
  localdice: "localdice",
  localciencia: "localfree",
  localfree: "localfree",
  localcienzar: "localcienzar",
  localsciencevsazar: "localsciencevsazar",
  localcienciavsazar: "localsciencevsazar",
  bot: "botlocalfree",
  freebot: "botlocalfree",
  botlocal: "botlocalfree",
  botlocalfree: "botlocalfree",
  botlocaldice: "botlocaldice",
  botlocalcienzar: "botlocalcienzar",
  botlocalsciencevsazar: "botlocalsciencevsazar",
  botlocalcienciavsazar: "botlocalsciencevsazar",
  botazar: "botlocaldice",
  botciencia: "botlocalfree",
  botcienzar: "botlocalcienzar",
  botsciencevsazar: "botlocalsciencevsazar",
  local: "localfree",
  freebox: "freebox",
};
const normalizeModeAlias = (value, fallback = "dice") => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (!normalized) return fallback;
  return MODE_ALIASES[normalized] || fallback;
};
const MODE_TO_CIMAX_FIELD = Object.freeze({
  free: "ciencia",
  ciencia: "ciencia",
  libre: "ciencia",
  localfree: "ciencia",
  localciencia: "ciencia",
  botlocalfree: "ciencia",
  botciencia: "ciencia",
  cienzar: "cienzar",
  sciluck: "cienzar",
  localcienzar: "cienzar",
  botlocalcienzar: "cienzar",
  sciencevsazar: "sciencevsazar",
  contrazar: "sciencevsazar",
  localsciencevsazar: "sciencevsazar",
  localcontrazar: "sciencevsazar",
  botlocalsciencevsazar: "sciencevsazar",
  botlocalcontrazar: "sciencevsazar",
  dice: "azar",
  azar: "azar",
  localdice: "azar",
  localazar: "azar",
  botlocaldice: "azar",
  botazar: "azar",
});
const CIMAX_MODE_LABELS = Object.freeze({
  ciencia: "CIENCIA",
  cienzar: "CIENZAR",
  sciencevsazar: "CONTRAZAR",
  azar: "AZAR",
});
const resolveCimaxModeFieldFromMode = (mode, freeBoxBaseMode = "") => {
  const normalizedMode = normalizeModeAlias(mode, "dice");
  if (normalizedMode === "freebox") {
    const normalizedBaseMode = normalizeModeAlias(freeBoxBaseMode, "free");
    return MODE_TO_CIMAX_FIELD[normalizedBaseMode] || "ciencia";
  }
  return MODE_TO_CIMAX_FIELD[normalizedMode] || "";
};
const formatCimaxModeLabel = (modeField) =>
  CIMAX_MODE_LABELS[String(modeField || "").trim().toLowerCase()] ||
  String(modeField || "")
    .trim()
    .toUpperCase();
const formatCimaxScoreValue = (value) => String(Math.max(0, Math.round(Number(value) || 0)));
const parseOptionalNumericValue = (value) => {
  if (value === null || value === undefined) return Number.NaN;
  if (typeof value === "string" && !value.trim()) return Number.NaN;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
};
const resolveOptionalCimaxScore = (value) => {
  const parsed = parseOptionalNumericValue(value);
  if (!Number.isFinite(parsed)) return null;
  return Math.max(0, Math.round(parsed));
};
const normalizeHistoryUserIdentity = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();
const formatHistoryMetricValue = (
  value,
  { decimals = 0, fallback = "—", language = "es" } = {},
) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  if (decimals <= 0) {
    return formatHistoryNumber(Math.round(parsed), language);
  }
  const safeDecimals = Number.isFinite(Number(decimals))
    ? Math.max(0, Math.floor(Number(decimals)))
    : 0;
  const factor = Math.pow(10, safeDecimals);
  return formatHistoryNumber(Math.round(parsed * factor) / factor, language, {
    maximumFractionDigits: safeDecimals,
  });
};
const formatHistorySignedMetricValue = (
  value,
  { decimals = 0, fallback = "—", language = "es" } = {},
) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  const formatted = formatHistoryMetricValue(parsed, {
    decimals,
    fallback: "0",
    language,
  });
  if (parsed > 0) return `+${formatted}`;
  if (parsed < 0) {
    return `-${formatHistoryMetricValue(Math.abs(parsed), {
      decimals,
      fallback: "0",
      language,
    })}`;
  }
  return "0";
};
const resolveHistoryRecordTimeMs = (record) => {
  const rawDate =
    record?.endedAt ||
    record?.finishedAt ||
    record?.completedAt ||
    record?.closedAt ||
    record?.createdAt ||
    record?.updatedAt ||
    record?.date ||
    record?.timestamp;
  if (rawDate === null || rawDate === undefined || rawDate === "") return 0;
  const parsedMoment = moment(rawDate);
  if (parsedMoment.isValid()) return parsedMoment.valueOf();
  const parsedNumber = Number(rawDate);
  return Number.isFinite(parsedNumber) ? parsedNumber : 0;
};
const BOT_MODE_OPTIONS = [
  { value: "botlocalfree", label: "CIENCIA", text: "text-[#68ccff]", disabled: false },
  { value: "botlocalcienzar", label: "CIENZAR", text: "text-[#6fff8a]", disabled: false },
  { value: "botlocalsciencevsazar", label: "CIENCIA V/S AZAR", text: "text-[#ff7a7a]", disabled: false },
  { value: "botlocaldice", label: "AZAR", text: "text-[#ffe267]", disabled: false },
];
const BOT_MODE_SET = new Set(BOT_MODE_OPTIONS.map((item) => item.value));
const BOT_ENABLED_MODE_SET = new Set(
  BOT_MODE_OPTIONS.filter((item) => !item.disabled).map((item) => item.value)
);
const normalizeBotModeValue = (value, fallback = "botlocalfree") => {
  const normalized = normalizeModeAlias(value, fallback);
  if (normalized === "botlocal") return "botlocalfree";
  return BOT_MODE_SET.has(normalized) ? normalized : fallback;
};
const normalizeSelectableBotModeValue = (value, fallback = "botlocalfree") => {
  const normalized = normalizeBotModeValue(value, fallback);
  return BOT_ENABLED_MODE_SET.has(normalized) ? normalized : fallback;
};
const MODE_WINNERS_SECTION_ORDER = [
  { key: "ciencia", titleKey: "MODO_CIENCIA" },
  { key: "cienzar", titleKey: "MODO_CIENZAR" },
  { key: "sciencevsazar", titleKey: "MODO_CONTRAZAR" },
  { key: "azar", titleKey: "MODO_AZAR" },
];
const PUZZLE_MODE_COLUMNS = [
  {
    mode: "free",
    title: "CIENCIA",
    panelClass: "border-[#2c64b0] bg-[#071935]",
    titleClass: "text-[#86c5ff]",
    cardClass: "border-[#3c76c7] bg-[#10254b]",
    iconClass: "border-[#7fb2ff88] bg-[#153665]",
    badgePrimaryClass: "border-[#6798df] bg-[#1a3e70] text-[#e6f2ff]",
    badgeSecondaryClass: "border-[#5f89c5] bg-[#15335e] text-[#d7e8ff]",
    badgeMutedClass: "border-[#4d6da0] bg-[#112746] text-[#c4d8f8]",
    shareButtonClass:
      "border-[#6f9cda] bg-[#1a3e70] text-[#e6f2ff] hover:border-[#95baea] hover:text-white",
    actionButtonClass: "border-[#72a6f2] bg-[#2c6ac2] hover:bg-[#3579d7]",
    emptyClass: "border-[#335f9f] bg-[#0b1d3f] text-[#9ec6ff]",
  },
  {
    mode: "cienzar",
    title: "CIENZAR",
    panelClass: "border-[#2f8d57] bg-[#071f17]",
    titleClass: "text-[#95efba]",
    cardClass: "border-[#43a86b] bg-[#0f2e21]",
    iconClass: "border-[#7ee2a488] bg-[#154231]",
    badgePrimaryClass: "border-[#5abf81] bg-[#1f573f] text-[#ddffe9]",
    badgeSecondaryClass: "border-[#4ea472] bg-[#174a35] text-[#cff6dd]",
    badgeMutedClass: "border-[#3f845b] bg-[#143a2b] text-[#c0ead0]",
    shareButtonClass:
      "border-[#6bc392] bg-[#1f573f] text-[#ddffe9] hover:border-[#8addb0] hover:text-white",
    actionButtonClass: "border-[#6fd698] bg-[#2f8f59] hover:bg-[#39a667]",
    emptyClass: "border-[#3a8b5d] bg-[#0c2a1f] text-[#99e8bc]",
  },
  {
    mode: "dice",
    title: "AZAR",
    panelClass: "border-[#bf9a3b] bg-[#2a2108]",
    titleClass: "text-[#ffe38a]",
    cardClass: "border-[#d1b059] bg-[#3a2f0f]",
    iconClass: "border-[#f0d98988] bg-[#4a3d15]",
    badgePrimaryClass: "border-[#d8bc6d] bg-[#5a491a] text-[#ffefc2]",
    badgeSecondaryClass: "border-[#c7a756] bg-[#4f4016] text-[#ffe8af]",
    badgeMutedClass: "border-[#aa8f46] bg-[#433811] text-[#f0deac]",
    shareButtonClass:
      "border-[#d6b35f] bg-[#5a491a] text-[#ffefc2] hover:border-[#ebcc84] hover:text-white",
    actionButtonClass:
      "border-[#ebcc78] bg-[#d4ae44] text-[#2d2204] hover:bg-[#e3bc4f] hover:text-[#2d2204]",
    emptyClass: "border-[#c7a14a] bg-[#3a2f0f] text-[#ffd97a]",
  },
];
const PUZZLE_MODE_TO_COLUMN_MODE = {
  free: "free",
  localfree: "free",
  botlocalfree: "free",
  cienzar: "cienzar",
  localcienzar: "cienzar",
  botlocalcienzar: "cienzar",
  sciencevsazar: "sciencevsazar",
  localsciencevsazar: "sciencevsazar",
  botlocalsciencevsazar: "sciencevsazar",
  dice: "dice",
  localdice: "dice",
  botlocaldice: "dice",
};
const GREEN_JOIN_BUTTON_CLASS =
  "border-[#79ffa5] bg-gradient-to-r from-[#14b84b] to-[#2de06c] text-[#04180a] shadow-[0_0_14px_rgba(45,224,108,0.45)] hover:brightness-110";
const MODE_COLOR_THEME_MAP = {
  free: {
    cardClass: "border-[#3c76c7] bg-[#10254b]",
    cardShadowClass: "shadow-[inset_0_0_0_1px_rgba(114,151,228,0.25)]",
    iconClass: "border-[#7fb2ff88] bg-[#153665]",
    iconFallbackTextClass: "text-[#d9ebff]",
    titleTextClass: "text-[#f2f7ff]",
    metaTextClass: "text-[#d7e8ff]",
    modeBadgeClass: "border-[#6798df] bg-[#1a3e70] text-[#e6f2ff]",
    metaBadgeClass: "border-[#5f89c5] bg-[#15335e] text-[#d7e8ff]",
    shareButtonClass:
      "border-[#6f9cda] bg-[#1a3e70] text-[#e6f2ff] hover:border-[#95baea] hover:text-white",
    countBadgeClass: "border-[#5f89c5] bg-[#15335e] text-[#d7e8ff]",
    joinButtonClass: GREEN_JOIN_BUTTON_CLASS,
  },
  cienzar: {
    cardClass: "border-[#43a86b] bg-[#0f2e21]",
    cardShadowClass: "shadow-[inset_0_0_0_1px_rgba(90,191,129,0.24)]",
    iconClass: "border-[#7ee2a488] bg-[#154231]",
    iconFallbackTextClass: "text-[#ddffe9]",
    titleTextClass: "text-[#ecfff3]",
    metaTextClass: "text-[#cff6dd]",
    modeBadgeClass: "border-[#5abf81] bg-[#1f573f] text-[#ddffe9]",
    metaBadgeClass: "border-[#4ea472] bg-[#174a35] text-[#cff6dd]",
    shareButtonClass:
      "border-[#6bc392] bg-[#1f573f] text-[#ddffe9] hover:border-[#8addb0] hover:text-white",
    countBadgeClass: "border-[#4ea472] bg-[#174a35] text-[#cff6dd]",
    joinButtonClass: GREEN_JOIN_BUTTON_CLASS,
  },
  sciencevsazar: {
    cardClass: "border-[#c94848] bg-[#3a1115]",
    cardShadowClass: "shadow-[inset_0_0_0_1px_rgba(225,82,82,0.24)]",
    iconClass: "border-[#ff8a8a88] bg-[#5a1a20]",
    iconFallbackTextClass: "text-[#ffe5e5]",
    titleTextClass: "text-[#fff0f0]",
    metaTextClass: "text-[#ffd2d2]",
    modeBadgeClass: "border-[#dc6b6b] bg-[#6b2428] text-[#ffe5e5]",
    metaBadgeClass: "border-[#bd5858] bg-[#541b20] text-[#ffd2d2]",
    shareButtonClass:
      "border-[#dc6b6b] bg-[#6b2428] text-[#ffe5e5] hover:border-[#ff9898] hover:text-white",
    countBadgeClass: "border-[#bd5858] bg-[#541b20] text-[#ffd2d2]",
    joinButtonClass: GREEN_JOIN_BUTTON_CLASS,
  },
  dice: {
    cardClass: "border-[#d1b059] bg-[#3a2f0f]",
    cardShadowClass: "shadow-[inset_0_0_0_1px_rgba(235,204,120,0.24)]",
    iconClass: "border-[#f0d98988] bg-[#4a3d15]",
    iconFallbackTextClass: "text-[#ffefc2]",
    titleTextClass: "text-[#fff4cf]",
    metaTextClass: "text-[#ffe8af]",
    modeBadgeClass: "border-[#d8bc6d] bg-[#5a491a] text-[#ffefc2]",
    metaBadgeClass: "border-[#c7a756] bg-[#4f4016] text-[#ffe8af]",
    shareButtonClass:
      "border-[#d6b35f] bg-[#5a491a] text-[#ffefc2] hover:border-[#ebcc84] hover:text-white",
    countBadgeClass: "border-[#c7a756] bg-[#4f4016] text-[#ffe8af]",
    joinButtonClass: GREEN_JOIN_BUTTON_CLASS,
  },
  payment: {
    cardClass: "border-[#ffffffde] bg-[#ffffff]",
    cardShadowClass: "shadow-[inset_0_0_0_1px_rgba(210,225,255,0.5)]",
    iconClass: "border-[#e5ecfa] bg-[#f5f8ff]",
    iconFallbackTextClass: "text-[#0f2240]",
    titleTextClass: "text-[#0f1730]",
    metaTextClass: "text-[#213353]",
    modeBadgeClass: "border-[#c7d3ea] bg-[#e6efff] text-[#1e3150]",
    metaBadgeClass: "border-[#c7d3ea] bg-[#e6efff] text-[#1e3150]",
    shareButtonClass:
      "border-[#bfd5ff] bg-[#e6efff] text-[#1e3150] hover:border-[#97bbf9] hover:text-[#162a48]",
    countBadgeClass: "border-[#bfd3f4] bg-[#e9f0ff] text-[#243f66]",
    joinButtonClass:
      "border-[#4f7bcf] bg-gradient-to-r from-[#4f7bcf] to-[#32558f] text-white hover:brightness-110",
  },
  waiting: {
    cardClass: "border-[#6f7786] bg-[#2c313a]",
    cardShadowClass: "shadow-[inset_0_0_0_1px_rgba(175,184,204,0.24)]",
    iconClass: "border-[#8d97ab88] bg-[#3a404c]",
    iconFallbackTextClass: "text-[#dce2ef]",
    titleTextClass: "text-[#f2f4fa]",
    metaTextClass: "text-[#d7deea]",
    modeBadgeClass: "border-[#8891a4] bg-[#454d5b] text-[#e3e8f4]",
    metaBadgeClass: "border-[#7d879b] bg-[#3f4653] text-[#d7deea]",
    shareButtonClass:
      "border-[#8a94a8] bg-[#4a5363] text-[#e6ebf6] hover:border-[#a4aec3] hover:text-white",
    countBadgeClass: "border-[#7d879b] bg-[#3f4653] text-[#d7deea]",
    joinButtonClass:
      "border-[#929cb2] bg-gradient-to-r from-[#5f6878] to-[#4b5362] text-[#edf1fa] hover:brightness-110",
  },
};
const MODE_SELECTION_THEME_MAP = {
  free: {
    selectedClass: "border-[#72a6f2] bg-[#2c6ac2] text-white shadow-[0_0_16px_rgba(66,136,236,0.35)]",
    idleClass:
      "border-[#5f89c5] bg-transparent text-[#9ecfff] hover:border-[#72a6f2] active:bg-[#2c6ac2] active:text-white",
    selectedLabelClass: "text-white",
    idleLabelClass: "text-[#9ecfff]",
  },
  cienzar: {
    selectedClass: "border-[#6fd698] bg-[#2f8f59] text-white shadow-[0_0_16px_rgba(79,189,126,0.35)]",
    idleClass:
      "border-[#4ea472] bg-transparent text-[#95efba] hover:border-[#6fd698] active:bg-[#2f8f59] active:text-white",
    selectedLabelClass: "text-white",
    idleLabelClass: "text-[#95efba]",
  },
  sciencevsazar: {
    selectedClass: "border-[#ff8a8a] bg-[#b7343f] text-white shadow-[0_0_16px_rgba(224,68,80,0.35)]",
    idleClass:
      "border-[#bd5858] bg-transparent text-[#ff9c9c] hover:border-[#ff8a8a] active:bg-[#b7343f] active:text-white",
    selectedLabelClass: "text-white",
    idleLabelClass: "text-[#ff9c9c]",
  },
  dice: {
    selectedClass:
      "border-[#ebcc78] bg-[#d4ae44] text-[#2d2204] shadow-[0_0_16px_rgba(232,191,90,0.35)]",
    idleClass:
      "border-[#c7a756] bg-transparent text-[#ffe267] hover:border-[#ebcc78] active:bg-[#d4ae44] active:text-[#2d2204]",
    selectedLabelClass: "text-[#2d2204]",
    idleLabelClass: "text-[#ffe267]",
  },
};
const SKIN_SECTION_ORDER = [
  { key: "free", label: "Gratis", accent: "text-[#82f9ae]" },
  { key: "paid", label: i18nT("lobby.dePago"), accent: "text-[#ffd785]" },
  { key: "earned", label: "Ganadas", accent: "text-[#e0b6ff]" },
];
const EARNED_XIMA_LOGO_SKIN_IDS = new Set(["x1", "x2", "x3", "x4"]);
const buildEmptyModeWinnersMap = () =>
  MODE_WINNERS_SECTION_ORDER.reduce((acc, section) => {
    acc[section.key] = [];
    return acc;
  }, {});

const PLAY_TYPE_OPTIONS = [
  {
    key: "online",
    labelKey: "MULTIJUGADOR_EN_LINEA",
    activeClass: "border-[#88d5ff] bg-[#1d76ca] text-white shadow-[0_0_16px_rgba(80,172,255,0.45)]",
    idleClass:
      "border-[#57b8ff] bg-transparent text-[#8fd4ff] hover:border-[#88d5ff] active:bg-[#1d76ca] active:text-white",
  },
  {
    key: "bot",
    labelKey: "JUGAR_CON_BOT",
    activeClass: "border-[#ffb2b2] bg-[#c12e2e] text-white shadow-[0_0_16px_rgba(239,96,96,0.45)]",
    idleClass:
      "border-[#ff8e8e] bg-transparent text-[#ffb2b2] hover:border-[#ffb2b2] active:bg-[#c12e2e] active:text-white",
  },
  {
    key: "local",
    labelKey: "JUGAR_CON_AMIGO",
    activeClass: "border-[#ffe27a] bg-[#c29a12] text-[#271b00] shadow-[0_0_16px_rgba(255,226,122,0.35)]",
    idleClass:
      "border-[#ffe27a] bg-transparent text-[#ffe27a] hover:border-[#ffefad] active:bg-[#c29a12] active:text-[#271b00]",
  },
  {
    key: "freebox",
    labelKey: "CAJA_DE_ARENA",
    activeClass: "border-[#e0c1ff] bg-[#8152c1] text-white shadow-[0_0_16px_rgba(157,103,230,0.45)]",
    idleClass:
      "border-[#cf9dff] bg-transparent text-[#cf9dff] hover:border-[#e0c1ff] active:bg-[#8152c1] active:text-white",
  },
];

const DEFAULT_TURN_TIME = 5;
const TIME_OPTIONS = [1, 2, 5, 10, 30];
const TOURNAMENT_EDIT_HAVING_TIME_OPTIONS = [1, 2, 3, 5, 10, 15, 30, 60];
const MEMBER_OPTIONS = [2, 3, 4];
const BOT_MEMBER_OPTIONS = [2, 3, 4];
const SUPER_TOURNAMENT_MIN_MINUTES = 5;
const SUPER_TOURNAMENT_MAX_MINUTES = 24 * 60;
const DEFAULT_SUPER_TOURNAMENT_MINUTES = 120;
const TOURNAMENT_DEFAULT_REWARD_VALUE = 0;
const MAX_TOURNAMENT_REWARD_VALUE = 1_000_000_000;
const BOT_DIFFICULTY_OPTIONS = [
  { value: "easy", label: "Facil" },
  { value: "medium", label: "Medio" },
  { value: "hard", label: "Dificil" },
  { value: "injusto", label: "Injusto" },
];
const BOT_DIFFICULTY_SET = new Set(BOT_DIFFICULTY_OPTIONS.map((item) => item.value));
const DIRECT_UI_COPY_BY_EN = Object.freeze({
  Wallet: { ru: "Кошелёк", de: "Brieftasche" },
  PLAY: { ru: "ИГРАТЬ", de: "SPIELEN" },
  Close: { ru: "ЗАКРЫВАТЬ", de: "SCHLIESSEN" },
  Clear: { ru: "ОЧИСТИТЬ", de: "LÖSCHEN" },
  Floating: { ru: "ПЛАВАЮЩИЙ", de: "SCHWEBEND" },
  "Copy Link": { ru: "Скопировать ссылку", de: "Link kopieren" },
  Create: { ru: "СОЗДАТЬ", de: "ERSTELLEN" },
  "Create Tournament": { ru: "СОЗДАТЬ ТУРНИР", de: "TURNIER ERSTELLEN" },
  "Creating...": { ru: "СОЗДАНИЕ...", de: "WIRD ERSTELLT..." },
  "Activate match password": { ru: "Активировать пароль матча", de: "Match-Passwort aktivieren" },
  "Only those who have the password can join": {
    ru: "Присоединиться смогут только те, у кого есть пароль",
    de: "Nur wer das Passwort hat, kann beitreten",
  },
  "Password to join": { ru: "Пароль для присоединения", de: "Passwort zum Beitreten" },
  "Registered Player": { ru: "ЗАРЕГИСТРИРОВАННЫЙ ИГРОК", de: "REGISTRIERTER SPIELER" },
  "Edit Profile": { ru: "РЕДАКТИРОВАТЬ ПРОФИЛЬ", de: "PROFIL BEARBEITEN" },
  "Xima Board": { ru: "ДОСКА XIMA", de: "XIMA BRETT" },
  "Edit Board": { ru: "РЕДАКТИРОВАТЬ ДОСКУ", de: "BRETT BEARBEITEN" },
  "Special Block": { ru: "СПЕЦИАЛЬНЫЙ БЛОК", de: "SPEZIALBLOCK" },
  "Edit Special": { ru: "РЕДАКТИРОВАТЬ СПЕЦИАЛЬНЫЙ", de: "SPEZIAL BEARBEITEN" },
  "Common Blocks": { ru: "ОБЫЧНЫЕ БЛОКИ", de: "STANDARD-BLÖCKE" },
  "Edit Blocks": { ru: "РЕДАКТИРОВАТЬ БЛОКИ", de: "BLÖCKE BEARBEITEN" },
  "CIMAX SCIENCE": { ru: "CIMAX НАУКА", de: "CIMAX WISSENSCHAFT" },
  "CIMAX RANDOM": { ru: "CIMAX СЛУЧАЙ", de: "CIMAX ZUFALL" },
  "Bot Difficulty": { ru: "СЛОЖНОСТЬ БОТА", de: "BOT-SCHWIERIGKEIT" },
  Science: { ru: "НАУКА", de: "WISSENSCHAFT" },
  Chance: { ru: "СЛУЧАЙ", de: "ZUFALL" },
  Easy: { ru: "ЛЕГКО", de: "LEICHT" },
  Medium: { ru: "СРЕДНЕ", de: "MITTEL" },
  Hard: { ru: "ТРУДНО", de: "SCHWER" },
  Unfair: { ru: "НЕЧЕСТНО", de: "UNFAIR" },
  Refresh: { ru: "ОБНОВИТЬ", de: "AKTUALISIEREN" },
  Tournament: { ru: "ТУРНИР", de: "TURNIER" },
  "Live tournaments": { ru: "ЖИВЫЕ ТУРНИРЫ", de: "LIVE-TURNIERE" },
  "Create game": { ru: "СОЗДАТЬ ИГРУ", de: "SPIEL ERSTELLEN" },
  "Your friends": { ru: "ТВОИ ДРУЗЬЯ", de: "DEINE FREUNDE" },
  "Default Star": { ru: "Звезда по умолчанию", de: "Standardstern" },
  "Loading friends online...": { ru: "Загрузка друзей онлайн...", de: "Freunde werden geladen..." },
  Friend: { ru: "Друг", de: "Freund" },
  "Complete: mode, players and time.": {
    ru: "Завершите: режим, игроки и время.",
    de: "Vervollständigen: Modus, Spieler und Zeit.",
  },
  "Complete: mode, players, names and time.": {
    ru: "Завершите: режим, игроки, имена и время.",
    de: "Vervollständigen: Modus, Spieler, Namen und Zeit.",
  },
  "Complete: players.": {
    ru: "Завершите: игроки.",
    de: "Vervollständigen: Spieler.",
  },
  Panel: { ru: "ПАНЕЛЬ", de: "PANEL" },
  "Match history": { ru: "ИСТОРИЯ ИГР", de: "SPIELVERLAUF" },
  "View statistics": { ru: "ПОСМОТРЕТЬ СТАТИСТИКУ", de: "STATISTIK ANSEHEN" },
  "View all": { ru: "ПОСМОТРЕТЬ ВСЕ", de: "ALLE ANSEHEN" },
  "View paginated": { ru: "ПОСТРАНИЧНЫЙ ВИД", de: "SEITENANSICHT" },
  "Refresh history": { ru: "ОБНОВИТЬ ИСТОРИЮ", de: "VERLAUF AKTUALISIEREN" },
  "Write here and save to this section": {
    ru: "Напишите здесь и сохраните в этом разделе",
    de: "Hier schreiben und in diesem Bereich speichern",
  },
  "News published by administrators and superusers": {
    ru: "Новости, опубликованные администраторами и суперпользователями",
    de: "Nachrichten von Administratoren und Superusern",
  },
  messages: { ru: "сообщения", de: "nachrichten" },
  Edit: { ru: "РЕДАКТИРОВАТЬ", de: "BEARBEITEN" },
  Delete: { ru: "УДАЛИТЬ", de: "LÖSCHEN" },
  "Edit the message and save changes": {
    ru: "Отредактируйте сообщение и сохраните изменения",
    de: "Nachricht bearbeiten und Änderungen speichern",
  },
  "Save changes": { ru: "СОХРАНИТЬ ИЗМЕНЕНИЯ", de: "ÄNDERUNGEN SPEICHERN" },
  "Available now and scheduled for the future": {
    ru: "Доступно сейчас и запланировано на будущее",
    de: "Jetzt verfügbar und für die Zukunft geplant",
  },
  "No fixed date": { ru: "Без фиксированной даты", de: "Kein festes Datum" },
  Turn: { ru: "Ход", de: "Zug" },
  Start: { ru: "Начало", de: "Start" },
  Date: { ru: "Дата", de: "Datum" },
  Time: { ru: "Время", de: "Zeit" },
  "No tournaments available at this moment.": {
    ru: "В данный момент турниров нет",
    de: "Im Moment sind keine Turniere verfügbar",
  },
  "No future tournaments scheduled.": {
    ru: "Будущих турниров не запланировано",
    de: "Keine zukünftigen Turniere geplant",
  },
});
const PROFILE_STORAGE_PREFIX = "xima_profile_v1_";
const PUZZLE_SOLVED_STORAGE_PREFIX = "xima_puzzle_solved_v1_";
const PUZZLE_REWARD_CLAIM_STORAGE_PREFIX = "xima_puzzle_reward_claim_v1_";
const PUZZLE_DEFAULT_REWARD_VALUE = 10;
const MAX_PUZZLE_TURN_LIMIT = 999;
const DEFAULT_PUZZLE_TURN_LIMIT = 30;
const MAX_PUZZLE_REWARD_VALUE = 1_000_000_000;
const MAX_PUZZLE_SOLVE_TIME_LIMIT_SECONDS = 24 * 60 * 60;
const DEFAULT_PUZZLE_SOLVE_TIME_MINUTES = 5;
const MAX_PUZZLE_SOLVE_TIME_MINUTES = Math.floor(MAX_PUZZLE_SOLVE_TIME_LIMIT_SECONDS / 60);
const QUICK_PLAY_PREFS_PREFIX = "xima_quick_play_v1_";
const QUICK_PLAY_PREFS_GUEST_KEY = `${QUICK_PLAY_PREFS_PREFIX}guest`;
const LAST_JOINED_TOURNAMENT_PREFIX = "xima_last_joined_tournament_v1_";
const MAX_AVATAR_FILE_SIZE_BYTES = 3 * 1024 * 1024;
const AVATAR_MAX_DATA_URL_LENGTH = 70000;
const AVATAR_MAX_DIMENSION = 192;
const AVATAR_QUALITY_STEPS = [0.82, 0.72, 0.62, 0.52, 0.42, 0.34];
const PROFILE_NAME_MAX_LENGTH = 80;
const PROFILE_CONTACT_EMAIL_MAX_LENGTH = 160;
const PROFILE_PHONE_MAX_LENGTH = 40;
const PROFILE_CONTACT_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PROFILE_PHONE_PATTERN = /^[0-9+()\-\s]*$/;
const normalizeProfilePhoneInput = (value) =>
  String(value || "")
    .normalize("NFKC")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/[\u00A0\u202F]/g, " ")
    .replace(/[\u2010\u2011\u2012\u2013\u2014\u2212]/g, "-")
    .replace(/[.\s]+/g, " ")
    .replace(/[^\d+()\-\s]/g, "")
    .replace(/\s+/g, " ")
    .slice(0, PROFILE_PHONE_MAX_LENGTH);
const SPECIAL_BLOCK_STORAGE_PREFIX = "xima_special_block_v1_";
const COMMON_BLOCK_STORAGE_PREFIX = "xima_common_block_v1_";
const GLOBAL_SPECIAL_BLOCK_KEY = "xima_special_block_selected";
const GLOBAL_SPECIAL_BLOCK_IMAGE_KEY = "xima_special_block_image";
const GLOBAL_BOARD_THEME_KEY = "xima_board_theme_selected";
const GLOBAL_COMMON_BLOCK_THEME_KEY = "xima_common_block_theme_selected";
const DEFAULT_SPECIAL_BLOCK_ID = "default_star";
const DEFAULT_BOARD_THEME_ID = "classic";
const DEFAULT_COMMON_BLOCK_THEME_ID = "default_star";
const MAX_SKIN_ID_LENGTH = 120;
const PUBLIC_ASSET_PREFIX = String(process.env.PUBLIC_URL || "").replace(/\/+$/, "");
const resolvePublicAssetPath = (relativePath) => {
  const normalized = String(relativePath || "").replace(/^\/+/, "");
  return PUBLIC_ASSET_PREFIX ? `${PUBLIC_ASSET_PREFIX}/${normalized}` : `/${normalized}`;
};
const STATIC_PROFILE_AVATAR_VERSION = "20260718-static-assets-fix";
const withStaticAssetVersion = (assetPath) => {
  const value = String(assetPath || "").trim();
  if (!value || value.startsWith("data:") || value.includes("?v=")) return value;
  return `${value}${value.includes("?") ? "&" : "?"}v=${STATIC_PROFILE_AVATAR_VERSION}`;
};
const resolveVersionedPublicAssetPath = (relativePath) =>
  withStaticAssetVersion(resolvePublicAssetPath(relativePath));
const stripAssetVersion = (value) => String(value || "").split("?")[0];

const SPECIAL_BLOCK_PRESET_MAP = {
  default_star: {
    id: "default_star",
    label: "Default Star",
    image: resolvePublicAssetPath("special-blocks/star.svg"),
  },
  star: { id: "star", label: "Estrella", image: resolvePublicAssetPath("special-blocks/star.svg") },
  frog: { id: "frog", label: "Rana", image: resolvePublicAssetPath("special-blocks/frog.svg") },
  snake: { id: "snake", label: "Serpiente", image: resolvePublicAssetPath("special-blocks/snake.svg") },
  dragon: { id: "dragon", label: "Dragon", image: resolvePublicAssetPath("special-blocks/dragon.svg") },
  crystal: { id: "crystal", label: "Cristal", image: resolvePublicAssetPath("special-blocks/crystal.svg") },
  flame: { id: "flame", label: "Fuego", image: resolvePublicAssetPath("special-blocks/flame.svg") },
};

const WOOD_BOARD_PREVIEW_BACKGROUND =
  "radial-gradient(circle at 32% 24%, rgba(210,146,78,0.16), transparent 34%), linear-gradient(100deg, rgba(46,18,6,0.98), rgba(112,53,19,0.96) 48%, rgba(58,22,7,0.98))";

const BOARD_THEME_PRESET_MAP = {
  classic: { id: "classic", label: "Classic Neon", light: "#f2f0e8", dark: "#005c4a", glow: "#00be95" },
  cosmic: { id: "cosmic", label: "Cosmic Blue", light: "#f2f0e8", dark: "#082c7a", glow: "#2da0ee" },
  magma: { id: "magma", label: "Magma", light: "#f2f0e8", dark: "#7a260f", glow: "#e65c22" },
  emerald: { id: "emerald", label: "Madera", light: "#8a481a", dark: "#3f1708", glow: "#b7662d", previewBackground: WOOD_BOARD_PREVIEW_BACKGROUND },
  royal: { id: "royal", label: "Royal", light: "#f2f0e8", dark: "#3927a3", glow: "#7e66ee" },
  mono: { id: "mono", label: "Mono Night", light: "#f2f0e8", dark: "#293140", glow: "#8b9ab4" },
};

const getSpecialBlockPreset = (id) =>
  SPECIAL_BLOCK_PRESET_MAP[id] || SPECIAL_BLOCK_PRESET_MAP[DEFAULT_SPECIAL_BLOCK_ID];
const getBoardThemePreset = (id) =>
  BOARD_THEME_PRESET_MAP[id] || BOARD_THEME_PRESET_MAP[DEFAULT_BOARD_THEME_ID];
const normalizeSkinId = (value, fallback = "") => {
  let normalized = String(value || "").trim();

  if (!normalized) return fallback;
  normalized = normalized.replace(/^skins\//i, "");
  normalized = normalized.replace(/\.[a-z0-9]+$/i, "");

  if (!normalized) return fallback;
  if (normalized.length > MAX_SKIN_ID_LENGTH) return fallback;
  if (normalized.includes("..")) return fallback;
  if (normalized.includes("/") || normalized.includes("\\")) return fallback;

  return normalized;
};
const getSkinLabelFromId = (skinId) => {
  const normalized = normalizeSkinId(skinId, "");
  if (!normalized) return "Skin";

  return normalized
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};
const getSkinImageFromId = (skinId) => {
  const normalized = normalizeSkinId(skinId, "");
  if (!normalized) return "";
  return resolvePublicAssetPath(`skins/${encodeURIComponent(`${normalized}.png`)}`);
};
const resolveStoredSpecialBlockImage = (skinId, explicitImage) => {
  const normalizedExplicitImage = String(explicitImage || "").trim();
  if (normalizedExplicitImage) return normalizedExplicitImage;

  const normalizedSkinId = normalizeSkinId(skinId, "");
  if (!normalizedSkinId) return "";
  return getSkinImageFromId(normalizedSkinId);
};
const normalizeSkinImagePath = (rawImage, skinId = "") => {
  const normalizedImage = String(rawImage || "").trim();
  if (!normalizedImage) return getSkinImageFromId(skinId);

  if (/^https?:\/\//i.test(normalizedImage) || normalizedImage.startsWith("data:")) {
    return normalizedImage;
  }

  if (normalizedImage.startsWith("/")) {
    return normalizedImage;
  }

  const sanitized = normalizedImage.replace(/^\.?\//, "");
  if (!sanitized || sanitized.includes("..")) return getSkinImageFromId(skinId);

  if (sanitized.startsWith("skins/")) {
    return resolvePublicAssetPath(sanitized);
  }

  if (/^[^/\\]+\.[a-z0-9]+$/i.test(sanitized)) {
    return resolvePublicAssetPath(`skins/${encodeURIComponent(sanitized)}`);
  }

  return resolvePublicAssetPath(sanitized);
};
const mapSkinCatalogEntries = (rawEntries) =>
  ensureArray(rawEntries)
    .map((entry) => {
      const id = normalizeSkinId(
        entry?.id || entry?.skinName || entry?.name || entry?.imageName || "",
        ""
      );
      if (!id) return null;

      const label = String(entry?.label || getSkinLabelFromId(id)).trim() || getSkinLabelFromId(id);
      const image = normalizeSkinImagePath(
        entry?.image || entry?.src || entry?.path || entry?.fileName || "",
        id
      );
      if (!image) return null;

      return { id, label, image };
    })
    .filter(Boolean);
const resolveSeparatedSkinCatalog = (payload = {}) => {
  const specialCatalog = mapSkinCatalogEntries(payload?.specialSkins);
  const commonCatalog = mapSkinCatalogEntries(payload?.commonSkins);
  const legacyCatalog = mapSkinCatalogEntries(payload?.skins);

  return {
    specialSkins: specialCatalog.length > 0 ? specialCatalog : legacyCatalog,
    commonSkins: commonCatalog.length > 0 ? commonCatalog : legacyCatalog,
  };
};
const buildSkinCatalogEntriesFromStoreProducts = (products = []) => {
  const entriesById = new Map();
  ensureArray(products).forEach((product) => {
    const assets = [
      product?.image,
      ...ensureArray(product?.gallery),
    ].filter(Boolean);
    assets.forEach((assetPath) => {
      const filename = String(assetPath || "")
        .split("?")[0]
        .split("#")[0]
        .split(/[\\/]/)
        .filter(Boolean)
        .pop();
      const id = normalizeSkinId(filename || "", "");
      const image = normalizeSkinImagePath(assetPath, id);
      if (!id || !image || entriesById.has(id)) return;
      entriesById.set(id, {
        id,
        label: getSkinLabelFromId(id),
        image,
      });
    });
  });
  return Array.from(entriesById.values());
};
const getBoardDualSquaresStyle = (preset) => ({
  backgroundColor: preset?.dark || "#0d121f",
  backgroundImage:
    preset?.previewBackground ||
    `
      linear-gradient(${preset?.dark || "#0d121f"}, ${preset?.dark || "#0d121f"}),
      linear-gradient(${preset?.light || "#98a6bf"}, ${preset?.light || "#98a6bf"}),
      linear-gradient(${preset?.light || "#98a6bf"}, ${preset?.light || "#98a6bf"}),
      linear-gradient(${preset?.dark || "#0d121f"}, ${preset?.dark || "#0d121f"})
    `,
  backgroundSize: preset?.previewBackground ? "auto, 16px 16px" : "50% 50%, 50% 50%, 50% 50%, 50% 50%",
  backgroundPosition: preset?.previewBackground ? "center, 0 0" : "0 0, 100% 0, 0 100%, 100% 100%",
  backgroundRepeat: preset?.previewBackground ? "repeat, repeat" : "no-repeat",
  boxShadow: `inset 0 0 14px ${(preset?.glow || "#d8e0f0")}55`,
});

const ensureArray = (value) => (Array.isArray(value) ? value : []);
const getTournamentEntryId = (entry) =>
  String(entry?._id || entry?.id || entry?.tournamentId || "").trim();
const buildEntrySequenceLookup = (entries) =>
  ensureArray(entries)
    .filter((entry) => getTournamentEntryId(entry))
    .sort(
      (a, b) =>
        new Date(b?.createdAt || b?.startingTime || 0).valueOf() -
        new Date(a?.createdAt || a?.startingTime || 0).valueOf()
    )
    .reduce((acc, entry, index) => {
      acc[getTournamentEntryId(entry)] = index + 1;
      return acc;
    }, {});
const getPuzzleEntryId = (entry) =>
  String(entry?.id || entry?._id || entry?.tournamentId || "").trim();
const toOrderedPuzzleEntries = (rawEntries) =>
  ensureArray(rawEntries)
    .filter((entry) => getPuzzleEntryId(entry))
    .sort(
      (a, b) => new Date(a?.createdAt || 0).valueOf() - new Date(b?.createdAt || 0).valueOf()
    );
const normalizePuzzleSequenceOrder = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  const normalized = Math.floor(parsed);
  return normalized > 0 ? normalized : 0;
};
const resolvePuzzleIdByOrder = (rawEntries, rawOrder, rawMode = "") => {
  const order = normalizePuzzleSequenceOrder(rawOrder);
  if (!order) return "";
  const requestedMode = String(rawMode || "").trim();
  const resolvedMode = requestedMode ? resolvePuzzleColumnMode(requestedMode) : "";
  const ordered = toOrderedPuzzleEntries(rawEntries).filter((entry) => {
    if (!resolvedMode) return true;
    const entryMode = resolvePuzzleColumnMode(
      entry?.freeBoxBaseMode || entry?.mode || "free"
    );
    return entryMode === resolvedMode;
  });
  const candidate = ordered[order - 1];
  return getPuzzleEntryId(candidate);
};
const formatModeLabel = (mode) => {
  const normalized = normalizeModeAlias(mode, "");
  return MODE_LABELS[normalized] || String(mode || "").toUpperCase();
};
const formatFindMatchesModeLabel = (language, mode) => {
  const normalized = resolvePuzzleColumnMode(mode);
  if (normalized === "dice") return getFindMatchesCopy(language, "chanceShort");
  if (normalized === "cienzar") return getFindMatchesCopy(language, "cienzarShort");
  if (normalized === "sciencevsazar") return getFindMatchesCopy(language, "scienceVsAzarShort");
  if (normalized === "free") return getFindMatchesCopy(language, "scienceShort");
  return String(formatModeLabel(mode)).toUpperCase();
};
const formatFreeBoxBaseModeLabel = (mode, language = "es") =>
  getPuzzleDashboardModeLabel(language, resolvePuzzleColumnMode(mode));
const getModeIconSrc = (mode) => {
  const rawMode = String(mode || "")
    .trim()
    .toLowerCase();
  const normalizedMode = normalizeModeAlias(rawMode, rawMode);
  if (MODE_ICON_MAP[normalizedMode]) return MODE_ICON_MAP[normalizedMode];
  if (normalizedMode.includes("dice")) return MODE_ICON_MAP.dice;
  if (normalizedMode.includes("cienzar")) return MODE_ICON_MAP.cienzar;
  if (normalizedMode.includes("free")) return MODE_ICON_MAP.free;
  return "";
};
const getTransparentModeIconShellClass = (iconClass) =>
  String(iconClass || "")
    .replace(/\bbg-\[[^\]]+\]\b/g, "bg-transparent")
    .replace(/\s+/g, " ")
    .trim();
const getPlayerCountIconSrc = (memberSize) => {
  const normalizedSize = Math.floor(Number(memberSize) || 0);
  return PLAYER_COUNT_ICON_MAP[normalizedSize] || "";
};
const getMatchDurationIconSrc = (havingTime) => {
  const normalizedMinutes = Math.round(Number(havingTime) || 0);
  return MATCH_DURATION_ICON_MAP[normalizedMinutes] || "";
};
const resolvePuzzleColumnMode = (mode) =>
  PUZZLE_MODE_TO_COLUMN_MODE[normalizeModeAlias(mode, "free")] || "free";
const resolveTournamentDisplayMode = (record) => {
  const normalizedMode = normalizeModeAlias(record?.mode, "");
  if (normalizedMode === "freebox") {
    return resolvePuzzleColumnMode(record?.freeBoxBaseMode || "free");
  }
  return resolvePuzzleColumnMode(normalizedMode || record?.freeBoxBaseMode || "free");
};
const isPuzzleAzarMode = (baseMode) =>
  resolvePuzzleColumnMode(baseMode) === "dice";
const resolveModeColorTheme = (mode, options = {}) => {
  if (options?.hasTournamentPayment === true) return MODE_COLOR_THEME_MAP.payment;
  if (options?.waiting) return MODE_COLOR_THEME_MAP.waiting;
  const normalized = resolvePuzzleColumnMode(mode);
  return MODE_COLOR_THEME_MAP[normalized] || MODE_COLOR_THEME_MAP.free;
};
const resolveModeSelectionTheme = (mode) => {
  const normalized = resolvePuzzleColumnMode(mode);
  return MODE_SELECTION_THEME_MAP[normalized] || MODE_SELECTION_THEME_MAP.free;
};
const normalizeBotDifficulty = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  return BOT_DIFFICULTY_SET.has(normalized) ? normalized : "injusto";
};
const resolveQuickMatchExpiresAtMs = (record) => {
  const explicitMs = new Date(record?.quickMatchExpiresAt || 0).valueOf();
  if (Number.isFinite(explicitMs) && explicitMs > 0) return explicitMs;

  const durationMinutes = Math.max(0, Number(record?.quickMatchDurationMinutes) || 0);
  if (durationMinutes <= 0) return 0;

  const baseMs = new Date(record?.createdAt || record?.startingTime || 0).valueOf();
  if (!Number.isFinite(baseMs) || baseMs <= 0) return 0;
  return baseMs + durationMinutes * 60 * 1000;
};
const isPersistentQuickTournament = (record) =>
  Boolean(record?.isQuickMatch) && resolveQuickMatchExpiresAtMs(record) > 0;
const parseLooseBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes" || normalized === "on";
  }
  return false;
};

const resolveIndependentMatchFromCreation = (payloadData, quickView) => {
  if (Object.prototype.hasOwnProperty.call(payloadData || {}, "isIndependentMatch")) {
    return parseLooseBoolean(payloadData.isIndependentMatch);
  }

  return quickView === "independientes";
};
const isAutoGeneratedChallengeName = (value) =>
  /^desaf[ií]o creado por\b/i.test(String(value || "").trim());
const isIndependentMatch = (record) =>
  parseLooseBoolean(record?.isIndependentMatch);
const isSandboxMatch = (record) =>
  isIndependentMatch(record) && normalizeModeAlias(record?.mode, "") === "freebox";
const hasIndependentMatchLoaded = (record) => {
  if (!isIndependentMatch(record)) return false;
  const memberSize = Math.max(
    1,
    Number(record?.memberSize) || Number(record?.members) || 1
  );
  const loadedPlayers = ensureArray(record?.users).filter(
    (user) => Math.max(0, Number(user?.state) || 0) >= 2
  ).length;
  return loadedPlayers >= memberSize;
};
const isOnlineLobbyTournamentMode = (mode) =>
  LOBBY_MODES.has(normalizeModeAlias(mode, ""));
const formatCountdownLabel = (remainingMs) => {
  const safeMs = Math.max(0, Number(remainingMs) || 0);
  const totalSeconds = Math.floor(safeMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) {
    return `${days}d ${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(
      seconds
    ).padStart(2, "0")}`;
  }
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(
    seconds
  ).padStart(2, "0")}`;
};
const resolveTournamentEndRemainingMs = (record, nowMs = Date.now()) => {
  const quickExpiresAtMs = new Date(record?.quickMatchExpiresAt || 0).valueOf();
  if (Number.isFinite(quickExpiresAtMs) && quickExpiresAtMs > nowMs) {
    return quickExpiresAtMs - nowMs;
  }

  const startMs = new Date(record?.startingTime || record?.start || record?.createdAt || 0).valueOf();
  if (!Number.isFinite(startMs) || startMs <= 0) return 0;
  const durationDays = Math.max(0, Number(record?.duration) || 0);
  if (durationDays <= 0) return 0;
  const endMs = startMs + durationDays * 24 * 60 * 60 * 1000;
  return endMs > nowMs ? endMs - nowMs : 0;
};
const FRIEND_PRESENCE_META = {
  online: {
    label: i18nT("lobby.enLinea"),
    badgeClass: "border-[#4fd0ff] bg-[#11406a] text-[#dff5ff]",
  },
  in_match: {
    label: i18nT("lobby.enPartida"),
    badgeClass: "border-[#6dff93] bg-[#18492d] text-[#dcffe8]",
  },
};

const HISTORY_PAGE_SIZE = 100;
const HISTORY_LEGACY_FETCH_LIMIT = 5000;
const HISTORY_TEXT_SCALE = 3;
const REPLAY_BOOT_WAIT_MS = 2500;
const REPLAY_MOBILE_IFRAME_READY_WAIT_MS = 9000;
const REPLAY_MOBILE_BOOT_WAIT_MS = 6500;
const REPLAY_MOBILE_RETRY_WAIT_MS = 900;
const REPLAY_MOBILE_IFRAME_LOAD_BOOT_WAIT_MS = 1400;
const REPLAY_READY_TIMEOUT_MS = 12000;
const REPLAY_MOBILE_READY_TIMEOUT_MS = 24000;
const REPLAY_STEP_AWAIT_TIMEOUT_MS = 30000;
const REPLAY_AUTOPLAY_BASE_DELAY_MS = 420;
// «Lento» llega hasta 0,1× (5 veces más lento que el mínimo anterior de 0,5×).
const REPLAY_PLAYBACK_SPEED_OPTIONS = [0.1, 0.15, 0.2, 0.3, 0.4, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4, 6, 8];
const REPLAY_PLAYBACK_DEFAULT_INDEX = REPLAY_PLAYBACK_SPEED_OPTIONS.indexOf(1);
const GLOBAL_CHAT_EVENT_NAME = "GLOBAL_CHAT_MESSAGE";
const GLOBAL_CHAT_HISTORY_EVENT_NAME = "GLOBAL_CHAT_HISTORY";
const GLOBAL_CHAT_HISTORY_REQUEST_EVENT_NAME = "GLOBAL_CHAT_HISTORY_REQUEST";
const GLOBAL_CHAT_CLEAR_EVENT_NAME = "GLOBAL_CHAT_CLEAR";
const GLOBAL_CHAT_CLEARED_EVENT_NAME = "GLOBAL_CHAT_CLEARED";
const GLOBAL_CHAT_CLEAR_REJECTED_EVENT_NAME = "GLOBAL_CHAT_CLEAR_REJECTED";
const GLOBAL_CHAT_SESSION_KEY = "xima_global_chat_messages_session_v1";
const GLOBAL_CHAT_MAX_MESSAGES = 80;
const GLOBAL_CHAT_MAX_LENGTH = 280;
const NEWS_BOARD_EVENT_NAME = "NEWS_BOARD_MESSAGE";
const NEWS_BOARD_HISTORY_EVENT_NAME = "NEWS_BOARD_HISTORY";
const NEWS_BOARD_HISTORY_REQUEST_EVENT_NAME = "NEWS_BOARD_HISTORY_REQUEST";
const NEWS_BOARD_DELETE_EVENT_NAME = "NEWS_BOARD_DELETE";
const NEWS_BOARD_UPDATE_EVENT_NAME = "NEWS_BOARD_UPDATE";
const NEWS_BOARD_REJECTED_EVENT_NAME = "NEWS_BOARD_REJECTED";
const NEWS_BOARD_DELETE_REJECTED_EVENT_NAME = "NEWS_BOARD_DELETE_REJECTED";
const NEWS_BOARD_UPDATE_REJECTED_EVENT_NAME = "NEWS_BOARD_UPDATE_REJECTED";
const NEWS_BOARD_STORAGE_KEY = "xima_news_board_messages_v1";
const NEWS_BOARD_MAX_MESSAGES = 80;
const CHAT_FLOATING_MIN_WIDTH = 320;
const CHAT_FLOATING_MIN_HEIGHT = 280;
const CHAT_FLOATING_DEFAULT_WIDTH = 360;
const CHAT_FLOATING_DEFAULT_HEIGHT = 440;
const CHAT_FLOATING_MAXIMIZED_MAX_WIDTH = 920;
const CHAT_FLOATING_MAXIMIZED_MAX_HEIGHT = 620;
const REPLAY_PANEL_MIN_WIDTH = 720;
const REPLAY_PANEL_MIN_HEIGHT = 460;
const REPLAY_PANEL_SIZE_STORAGE_KEY = "xima_replay_panel_size_v1";
const REPLAY_PANEL_DEFAULT_WIDTH = 1420;
const REPLAY_PANEL_DEFAULT_HEIGHT = 820;
const REPLAY_R7_PLAYER_COLORS = ["#3b7cf0", "#e5484d", "#f6b93b", "#3ddc84"];
const REPLAY_R7_ARROW_KEYS = [
  { id: "blue", hex: "#1f85ff", name: "Azul", key: "Ctrl" },
  { id: "red", hex: "#f22929", name: "Roja", key: "Shift" },
  { id: "yellow", hex: "#ffdb1a", name: "Amarilla", key: "Bloq Mayús" },
  { id: "green", hex: "#26d14d", name: "Verde", key: "Tab" },
];
// Flecha verde: la mejor jugada del bot en la posición de ANTES de cada jugada (también
// cuando coincide con la jugada hecha). Se dibuja como la flecha de Ctrl al empezar la
// jugada y se queda hasta la jugada siguiente. El número de jugada va al final para que
// Unity la vuelva a dibujar en cada jugada (aunque se repita), con el tablero de ese momento.
const REPLAY_REVIEW_SHOW_ARROWS = true;
const reviewArrowPayload = (move) =>
  REPLAY_REVIEW_SHOW_ARROWS && move && move.best ? `${move.best.from || ""}|${move.best.to}|${move.step}` : "";
const REPLAY_REVIEW_KINDS = [
  { id: "brillante", label: "Brillante" },
  { id: "mejor", label: "Mejor" },
  { id: "excelente", label: "Excelente" },
  { id: "buena", label: "Buena" },
  { id: "interesante", label: "Interesante" },
  { id: "libro", label: "Libro" },
  { id: "imprecision", label: "Imprecisión" },
  { id: "error", label: "Error" },
  { id: "grave", label: "Grave error" },
];
const REPLAY_REVIEW_LABELS = REPLAY_REVIEW_KINDS.reduce((labels, kind) => {
  labels[kind.id] = kind.label;
  return labels;
}, {});
const formatReplayR7Clock = (ms) => {
  const totalSeconds = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};
const REPLAY_PHONE_PORTRAIT_QUERY = "(max-width: 820px) and (orientation: portrait)";
const REPLAY_PHONE_LANDSCAPE_QUERY = "(max-width: 1024px) and (orientation: landscape)";
const REPLAY_TOUCH_POINTER_QUERY = "(pointer: coarse)";
const CENTER_RIGHT_RESIZE_LAYOUT_QUERY =
  "(min-width: 1280px), (max-width: 1024px) and (orientation: landscape)";
const LANDSCAPE_PHONE_LAYOUT_QUERY = "(max-width: 1024px) and (orientation: landscape)";
const DEFAULT_RIGHT_PANEL_WIDTH = 340;
const DEFAULT_LANDSCAPE_RIGHT_PANEL_WIDTH = 196;
const DEFAULT_MOBILE_LANDSCAPE_PANEL_WIDTH = 92;
const DEFAULT_IPHONE_LANDSCAPE_RIGHT_PANEL_WIDTH = 340;
const MOBILE_LANDSCAPE_RIGHT_COLLAPSED_WIDTH = 52;
const mediaQueryMatches = (query) => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(query).matches;
};
const hasPhoneBodyClass = () => {
  if (typeof document === "undefined") return false;
  const bodyHasClass = document.body?.classList?.contains("xima-mobile-phone") === true;
  const htmlHasClass = document.documentElement?.classList?.contains("xima-mobile-phone") === true;
  return bodyHasClass || htmlHasClass;
};
const isIphoneDevice = () => {
  if (typeof window === "undefined" || typeof window.navigator === "undefined") return false;
  const userAgent = String(window.navigator.userAgent || "");
  return /iPhone/i.test(userAgent);
};
const isIphoneOrSamsungDevice = () => {
  if (typeof window === "undefined" || typeof window.navigator === "undefined") return false;
  const userAgent = String(window.navigator.userAgent || "");
  const isIphone = /iPhone/i.test(userAgent);
  const isSamsungAndroid =
    /Android/i.test(userAgent) && /(SamsungBrowser|SM-|SAMSUNG)/i.test(userAgent);
  return isIphone || isSamsungAndroid;
};
const isIphoneLandscapeViewport = () =>
  mediaQueryMatches(LANDSCAPE_PHONE_LAYOUT_QUERY) && isIphoneDevice();
const isIphoneChromeLandscapeViewport = () => {
  if (typeof window === "undefined" || typeof window.navigator === "undefined") return false;
  const userAgent = String(window.navigator.userAgent || "");
  const isIphoneChrome = /iPhone/i.test(userAgent) && /CriOS/i.test(userAgent);
  if (!isIphoneChrome) return false;
  const viewportWidth = Number(window.innerWidth) || 0;
  const viewportHeight = Number(window.innerHeight) || 0;
  return viewportWidth > viewportHeight;
};
const isSamsungChromeLandscapeViewport = () => {
  if (typeof window === "undefined" || typeof window.navigator === "undefined") return false;
  const navigatorRef = window.navigator;
  const userAgent = String(navigatorRef.userAgent || "");
  const isAndroid = /Android/i.test(userAgent);
  const isChromeFamily = /Chrome/i.test(userAgent);
  const isSamsungBrowser = /SamsungBrowser/i.test(userAgent);
  const hasSamsungDeviceToken =
    /(SM-|SAMSUNG|Samsung|Galaxy|GT-|SCH-|SHV-|SGH-)/i.test(userAgent);
  const uaDataModel = String(navigatorRef.userAgentData?.model || "");
  const hasSamsungModelToken = /(SM-|SAMSUNG|Samsung|Galaxy)/i.test(uaDataModel);
  const isSamsungChrome =
    isAndroid &&
    isChromeFamily &&
    !isSamsungBrowser &&
    (hasSamsungDeviceToken || hasSamsungModelToken);
  if (!isSamsungChrome) return false;
  const viewportWidth = Number(window.innerWidth) || 0;
  const viewportHeight = Number(window.innerHeight) || 0;
  return viewportWidth > viewportHeight;
};
const isPhoneLandscapeByViewport = () => {
  if (typeof window === "undefined" || typeof window.navigator === "undefined") return false;
  const navigatorRef = window.navigator;
  const userAgent = String(navigatorRef.userAgent || "");
  const isMobileDevice =
    /iPhone|Android|Mobile/i.test(userAgent) || navigatorRef.userAgentData?.mobile === true;
  if (!isMobileDevice) return false;
  const viewportWidth = Number(window.innerWidth) || 0;
  const viewportHeight = Number(window.innerHeight) || 0;
  return viewportWidth > viewportHeight;
};
const isPhoneLandscapeDesktopViewport = () =>
  isPhoneLandscapeByViewport() ||
  isIphoneChromeLandscapeViewport() ||
  isSamsungChromeLandscapeViewport();
const isIphoneSamsungLandscapeViewport = () =>
  mediaQueryMatches(LANDSCAPE_PHONE_LAYOUT_QUERY) && isIphoneOrSamsungDevice();
const getDefaultRightPanelWidth = () => {
  if (isPhoneLandscapeDesktopViewport()) {
    return DEFAULT_RIGHT_PANEL_WIDTH;
  }
  if (isIphoneLandscapeViewport()) {
    return DEFAULT_IPHONE_LANDSCAPE_RIGHT_PANEL_WIDTH;
  }
  if (isIphoneSamsungLandscapeViewport()) {
    return DEFAULT_MOBILE_LANDSCAPE_PANEL_WIDTH;
  }
  return mediaQueryMatches(LANDSCAPE_PHONE_LAYOUT_QUERY)
    ? DEFAULT_LANDSCAPE_RIGHT_PANEL_WIDTH
    : DEFAULT_RIGHT_PANEL_WIDTH;
};

const clampValue = (value, min, max) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return min;
  return Math.min(Math.max(numeric, min), max);
};

const createGlobalChatEntry = (rawPayload) => {
  const payload = rawPayload && typeof rawPayload === "object" ? rawPayload : {};
  const text = String(payload.text || payload.message || "")
    .trim()
    .slice(0, GLOBAL_CHAT_MAX_LENGTH);
  if (!text) return null;

  const sender = String(payload.sender || payload.displayName || payload.user || "Jugador").trim();
  const senderId = String(payload.senderId || payload.userId || payload.user || "").trim();
  const createdAtIso = new Date(payload.createdAt || Date.now()).toISOString();
  const id =
    String(payload.id || "").trim() ||
    `${senderId || sender || "guest"}:${createdAtIso}:${text.slice(0, 24)}`;

  return {
    id,
    sender: sender || "Jugador",
    senderId,
    text,
    createdAt: createdAtIso,
  };
};

const parseStoredGlobalChatMessages = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(GLOBAL_CHAT_SESSION_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry) => createGlobalChatEntry(entry))
      .filter(Boolean)
      .slice(-GLOBAL_CHAT_MAX_MESSAGES);
  } catch {
    return [];
  }
};

const createNewsBoardEntry = (rawPayload) => {
  const payload = rawPayload && typeof rawPayload === "object" ? rawPayload : {};
  const text = String(payload.text || payload.message || "").trim();
  if (!text) return null;

  const author = String(payload.author || payload.sender || "Jugador").trim();
  const authorId = String(payload.authorId || payload.senderId || "").trim();
  const createdAtIso = new Date(payload.createdAt || Date.now()).toISOString();
  const id =
    String(payload.id || "").trim() ||
    `${authorId || author || "guest"}:${createdAtIso}:${text.slice(0, 28)}`;

  return {
    id,
    author: author || "Jugador",
    authorId,
    text,
    createdAt: createdAtIso,
  };
};

const parseStoredNewsBoardMessages = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(NEWS_BOARD_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry) => createNewsBoardEntry(entry))
      .filter(Boolean)
      .slice(-NEWS_BOARD_MAX_MESSAGES);
  } catch {
    return [];
  }
};

const buildDefaultFormData = () => ({
  tournamentName: "",
  createdAtDate: moment().format("YYYY-MM-DD"),
  startingTime: moment().add(1, "minute"),
  duration: "1",
  havingTime: String(DEFAULT_TURN_TIME),
  mode: "dice",
  memberSize: 2,
  botDifficulty: "injusto",
  tournamentPaymentEnabled: false,
  tournamentRewardValue: "",
  tournamentWinnerPrizeValue: "",
  hasMatchPassword: false,
  matchPassword: "",
});

const parseTournamentRewardValue = (value) => {
  const numeric = parseOptionalNumericValue(value);
  if (Number.isFinite(numeric)) return numeric;
  const raw = String(value || "").trim();
  if (!raw) return Number.NaN;
  const sanitized = raw.replace(/[^0-9.-]/g, "");
  const fallback = Number(sanitized);
  return Number.isFinite(fallback) ? fallback : Number.NaN;
};

const normalizeTournamentRewardValue = (
  value,
  fallback = TOURNAMENT_DEFAULT_REWARD_VALUE
) => {
  const parsed = parseTournamentRewardValue(value);
  if (Number.isFinite(parsed)) {
    return Math.max(0, Math.min(MAX_TOURNAMENT_REWARD_VALUE, Math.floor(parsed)));
  }
  const fallbackParsed = parseTournamentRewardValue(fallback);
  if (Number.isFinite(fallbackParsed)) {
    return Math.max(0, Math.min(MAX_TOURNAMENT_REWARD_VALUE, Math.floor(fallbackParsed)));
  }
  return TOURNAMENT_DEFAULT_REWARD_VALUE;
};

const resolveTournamentRewardValue = (tournamentRecord) => {
  const tournament =
    tournamentRecord &&
    typeof tournamentRecord === "object" &&
    !Array.isArray(tournamentRecord)
      ? tournamentRecord
      : {};
  const rewardCandidate = [
    tournament?.tournamentRewardValue,
    tournament?.rewardValue,
    tournament?.rewardAmount,
    tournament?.reward,
    tournament?.rewardX,
    tournament?.prize,
  ].find((value) => Number.isFinite(parseTournamentRewardValue(value)));

  return normalizeTournamentRewardValue(rewardCandidate, TOURNAMENT_DEFAULT_REWARD_VALUE);
};

const resolveTournamentWinnerPrizeValue = (tournamentRecord) => {
  const tournament =
    tournamentRecord &&
    typeof tournamentRecord === "object" &&
    !Array.isArray(tournamentRecord)
      ? tournamentRecord
      : {};
  const winnerPrizeCandidate = [
    tournament?.tournamentWinnerPrizeValue,
    tournament?.winnerPrizeValue,
    tournament?.winnerPrize,
  ].find((value) => Number.isFinite(parseTournamentRewardValue(value)));

  return normalizeTournamentRewardValue(
    winnerPrizeCandidate,
    TOURNAMENT_DEFAULT_REWARD_VALUE
  );
};

const buildPanelProgress = (type = "online") => {
  if (type === "bot") {
    return {
      playersSelected: true,
      modeSelected: true,
      timeSelected: true,
    };
  }

  if (type === "freebox") {
    return {
      playersSelected: false,
      modeSelected: true,
      timeSelected: false,
    };
  }

  return {
    playersSelected: false,
    modeSelected: false,
    timeSelected: false,
  };
};

const getProfileStorageKey = (email) => {
  const normalizedEmail = String(email || "").trim().toLowerCase();
  return normalizedEmail ? `${PROFILE_STORAGE_PREFIX}${normalizedEmail}` : "";
};

const getSolvedPuzzlesStorageKey = (email) => {
  const normalizedEmail = String(email || "guest").trim().toLowerCase();
  return `${PUZZLE_SOLVED_STORAGE_PREFIX}${normalizedEmail || "guest"}`;
};

const parseSolvedPuzzleIds = (rawValue) => {
  try {
    const parsed = JSON.parse(String(rawValue || "[]"));
    if (!Array.isArray(parsed)) return [];
    return Array.from(
      new Set(
        parsed
          .map((value) => String(value || "").trim())
          .filter(Boolean)
      )
    );
  } catch {
    return [];
  }
};

const getSolvedPuzzleIdsSet = (email) => {
  if (typeof window === "undefined") return new Set();
  const storageKey = getSolvedPuzzlesStorageKey(email);
  if (!storageKey) return new Set();
  return new Set(parseSolvedPuzzleIds(window.localStorage.getItem(storageKey)));
};

const normalizeProfileId = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const isGuestProfileId = (value) => {
  const normalized = normalizeProfileId(value);
  if (!normalized) return false;
  if (normalized.startsWith(GUEST_USER_PREFIX)) return true;
  return normalized.endsWith(`@${GUEST_EMAIL_DOMAIN}`);
};

const getPuzzleRewardClaimStorageKey = (email) => {
  const normalizedEmail = String(email || "guest").trim().toLowerCase();
  return `${PUZZLE_REWARD_CLAIM_STORAGE_PREFIX}${normalizedEmail || "guest"}`;
};

const parsePuzzleRewardClaimMap = (rawValue) => {
  try {
    const parsed = JSON.parse(String(rawValue || "{}"));
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return Object.entries(parsed).reduce((acc, [rawPuzzleId, rawClaimed]) => {
      const puzzleId = String(rawPuzzleId || "").trim();
      if (!puzzleId) return acc;
      const isClaimed =
        rawClaimed === true ||
        (rawClaimed && typeof rawClaimed === "object" && rawClaimed.claimed === true);
      if (!isClaimed) return acc;
      acc[puzzleId] = true;
      return acc;
    }, {});
  } catch {
    return {};
  }
};

const getPuzzleRewardClaimMap = (email) => {
  if (typeof window === "undefined") return {};
  const storageKey = getPuzzleRewardClaimStorageKey(email);
  if (!storageKey) return {};
  return parsePuzzleRewardClaimMap(window.localStorage.getItem(storageKey));
};

const setPuzzleRewardClaimMap = (email, rawClaimMap) => {
  if (typeof window === "undefined") return;
  const storageKey = getPuzzleRewardClaimStorageKey(email);
  if (!storageKey) return;
  const normalizedClaimMap =
    rawClaimMap && typeof rawClaimMap === "object" && !Array.isArray(rawClaimMap)
      ? Object.entries(rawClaimMap).reduce((acc, [rawPuzzleId, rawClaimed]) => {
          const puzzleId = String(rawPuzzleId || "").trim();
          if (!puzzleId || rawClaimed !== true) return acc;
          acc[puzzleId] = true;
          return acc;
        }, {})
      : {};
  window.localStorage.setItem(storageKey, JSON.stringify(normalizedClaimMap));
};

const parsePuzzleRewardValue = (value) => {
  const numeric = parseOptionalNumericValue(value);
  if (Number.isFinite(numeric)) return numeric;
  const raw = String(value || "").trim();
  if (!raw) return Number.NaN;
  const sanitized = raw.replace(/[^0-9.-]/g, "");
  const fallback = Number(sanitized);
  return Number.isFinite(fallback) ? fallback : Number.NaN;
};

const normalizePuzzleRewardValue = (value, fallback = PUZZLE_DEFAULT_REWARD_VALUE) => {
  const parsed = parsePuzzleRewardValue(value);
  if (Number.isFinite(parsed)) return Math.max(0, Math.min(MAX_PUZZLE_REWARD_VALUE, Math.round(parsed)));
  const fallbackParsed = parsePuzzleRewardValue(fallback);
  if (Number.isFinite(fallbackParsed)) {
    return Math.max(0, Math.min(MAX_PUZZLE_REWARD_VALUE, Math.round(fallbackParsed)));
  }
  return PUZZLE_DEFAULT_REWARD_VALUE;
};

const normalizePuzzleTurnLimit = (value, fallback = DEFAULT_PUZZLE_TURN_LIMIT) => {
  const parsed = parseOptionalNumericValue(value);
  if (Number.isFinite(parsed)) {
    return Math.max(0, Math.min(MAX_PUZZLE_TURN_LIMIT, Math.floor(parsed)));
  }
  const fallbackParsed = parseOptionalNumericValue(fallback);
  if (Number.isFinite(fallbackParsed)) {
    return Math.max(0, Math.min(MAX_PUZZLE_TURN_LIMIT, Math.floor(fallbackParsed)));
  }
  return DEFAULT_PUZZLE_TURN_LIMIT;
};

const normalizePuzzleSolveTimeLimitSeconds = (value, fallback = 0) => {
  const parsed = parseOptionalNumericValue(value);
  if (Number.isFinite(parsed)) {
    return Math.max(0, Math.min(MAX_PUZZLE_SOLVE_TIME_LIMIT_SECONDS, Math.floor(parsed)));
  }
  const fallbackParsed = parseOptionalNumericValue(fallback);
  if (Number.isFinite(fallbackParsed)) {
    return Math.max(0, Math.min(MAX_PUZZLE_SOLVE_TIME_LIMIT_SECONDS, Math.floor(fallbackParsed)));
  }
  return 0;
};

const resolvePuzzleSolveTimeLimitSecondsFromRecord = (puzzleRecord) => {
  const puzzle =
    puzzleRecord && typeof puzzleRecord === "object" && !Array.isArray(puzzleRecord)
      ? puzzleRecord
      : {};
  const solveTimeCandidate = [
    puzzle?.puzzleSolveTimeLimitSeconds,
    puzzle?.puzzleSolveTimeSeconds,
    puzzle?.solveTimeSeconds,
    puzzle?.timeLimitSeconds,
  ].find((value) => Number.isFinite(parseOptionalNumericValue(value)));
  return normalizePuzzleSolveTimeLimitSeconds(solveTimeCandidate, 0);
};

const PUZZLE_DASHBOARD_COPY = Object.freeze({
  goToLobby: { es: "Ir al Lobby", en: "Go to Lobby", de: "Zur Lobby", ru: "В лобби" },
  title: { es: "PUZZLES", en: "PUZZLES", de: "PUZZLES", ru: "ГОЛОВОЛОМКИ" },
  subtitle: {
    es: "Explora, resuelve y mejora tu rendimiento en todos los modos.",
    en: "Explore, solve, and improve your performance across all modes.",
    de: "Entdecke, löse und verbessere deine Leistung in allen Modi.",
    ru: "Изучай, решай и улучшай свои результаты во всех режимах.",
  },
  history: { es: "Historial", en: "History", de: "Verlauf", ru: "История" },
  refresh: { es: "Actualizar", en: "Refresh", de: "Aktualisieren", ru: "Обновить" },
  puzzlesSolved: {
    es: "PUZZLES RESUELTOS",
    en: "PUZZLES SOLVED",
    de: "GELÖSTE PUZZLES",
    ru: "РЕШЁННЫЕ ГОЛОВОЛОМКИ",
  },
  percentOfTotal: {
    es: "{percent} del total",
    en: "{percent} of total",
    de: "{percent} des Gesamtwerts",
    ru: "{percent} от общего числа",
  },
  totalAttempts: {
    es: "INTENTOS TOTALES",
    en: "TOTAL ATTEMPTS",
    de: "VERSUCHE INSGESAMT",
    ru: "ВСЕГО ПОПЫТОК",
  },
  averagePerPuzzle: {
    es: "Promedio: {value} por puzzle",
    en: "Average: {value} per puzzle",
    de: "Durchschnitt: {value} pro Puzzle",
    ru: "Среднее: {value} на головоломку",
  },
  success: { es: "ÉXITO", en: "SUCCESS", de: "ERFOLG", ru: "УСПЕХ" },
  failures: { es: "FRACASOS", en: "FAILURES", de: "FEHLSCHLÄGE", ru: "НЕУДАЧИ" },
  averageMoves: {
    es: "MOVIMIENTOS PROMEDIO",
    en: "AVERAGE MOVES",
    de: "DURCHSCHNITTLICHE ZÜGE",
    ru: "СРЕДНЕЕ КОЛИЧЕСТВО ХОДОВ",
  },
  bestWorstMoves: {
    es: "Mejor: {best} - Peor: {worst}",
    en: "Best: {best} - Worst: {worst}",
    de: "Bestwert: {best} - Schlechtester Wert: {worst}",
    ru: "Лучший: {best} - Худший: {worst}",
  },
  averageTime: {
    es: "TIEMPO PROMEDIO",
    en: "AVERAGE TIME",
    de: "DURCHSCHNITTLICHE ZEIT",
    ru: "СРЕДНЕЕ ВРЕМЯ",
  },
  bestWorstTime: {
    es: "Mejor: {best} - Peor: {worst}",
    en: "Best: {best} - Worst: {worst}",
    de: "Bestwert: {best} - Schlechtester Wert: {worst}",
    ru: "Лучшее: {best} - Худшее: {worst}",
  },
  totalRewards: {
    es: "RECOMPENSAS TOTALES",
    en: "TOTAL REWARDS",
    de: "GESAMTBELOHNUNGEN",
    ru: "ВСЕГО НАГРАД",
  },
  bonusesEarned: {
    es: "Bonos obtenidos",
    en: "Bonuses earned",
    de: "Erhaltene Boni",
    ru: "Полученные бонусы",
  },
  filterByMode: {
    es: "FILTRAR POR MODO",
    en: "FILTER BY MODE",
    de: "NACH MODUS FILTERN",
    ru: "ФИЛЬТР ПО РЕЖИМУ",
  },
  all: { es: "TODOS", en: "ALL", de: "ALLE", ru: "ВСЕ" },
  science: { es: "CIENCIA", en: "SCIENCE", de: "WISSENSCHAFT", ru: "НАУКА" },
  cienzar: { es: "CIENZAR", en: "CIENZAR", de: "CIENZAR", ru: "CIENZAR" },
  chance: { es: "AZAR", en: "CHANCE", de: "ZUFALL", ru: "СЛУЧАЙНОСТЬ" },
  searchPuzzle: {
    es: "Buscar puzzle...",
    en: "Search puzzle...",
    de: "Puzzle suchen...",
    ru: "Найти головоломку...",
  },
  sortBy: {
    es: "Ordenar por:",
    en: "Sort by:",
    de: "Sortieren nach:",
    ru: "Сортировать по:",
  },
  mostRecent: {
    es: "Más recientes",
    en: "Most recent",
    de: "Neueste zuerst",
    ru: "Сначала новые",
  },
  oldest: {
    es: "Más antiguos",
    en: "Oldest",
    de: "Älteste zuerst",
    ru: "Сначала старые",
  },
  highestReward: {
    es: "Mayor recompensa",
    en: "Highest reward",
    de: "Höchste Belohnung",
    ru: "Самая большая награда",
  },
  fewestMoves: {
    es: "Menos movimientos",
    en: "Fewest moves",
    de: "Wenigste Züge",
    ru: "Меньше всего ходов",
  },
  puzzle: { es: "PUZZLE", en: "PUZZLE", de: "PUZZLE", ru: "ГОЛОВОЛОМКА" },
  attempts: { es: "INTENTOS", en: "ATTEMPTS", de: "VERSUCHE", ru: "ПОПЫТКИ" },
  movesAvgBest: {
    es: "MOVIMIENTOS PROM / MEJOR",
    en: "MOVES AVG / BEST",
    de: "ZÜGE Ø / BESTWERT",
    ru: "ХОДЫ СРЕД. / ЛУЧШИЙ",
  },
  timeAvgBest: {
    es: "TIEMPO PROM / MEJOR",
    en: "TIME AVG / BEST",
    de: "ZEIT Ø / BESTWERT",
    ru: "ВРЕМЯ СРЕД. / ЛУЧШЕЕ",
  },
  moves: { es: "MOVIMIENTOS", en: "MOVES", de: "ZÜGE", ru: "ХОДЫ" },
  time: { es: "TIEMPO", en: "TIME", de: "ZEIT", ru: "ВРЕМЯ" },
  result: { es: "RESULTADO", en: "RESULT", de: "ERGEBNIS", ru: "РЕЗУЛЬТАТ" },
  reward: { es: "RECOMPENSA", en: "REWARD", de: "BELOHNUNG", ru: "НАГРАДА" },
  rewards: { es: "RECOMPENSAS", en: "REWARDS", de: "BELOHNUNGEN", ru: "НАГРАДЫ" },
  failed: { es: "FALLASTE", en: "FAILED", de: "FEHLGESCHLAGEN", ru: "НЕУДАЧА" },
  solve: { es: "RESOLVER", en: "SOLVE", de: "LÖSEN", ru: "РЕШИТЬ" },
  opening: {
    es: "ABRIENDO...",
    en: "OPENING...",
    de: "WIRD GEÖFFNET...",
    ru: "ОТКРЫТИЕ...",
  },
  claiming: {
    es: "RECLAMANDO...",
    en: "CLAIMING...",
    de: "WIRD ANGEFORDERT...",
    ru: "ПОЛУЧЕНИЕ...",
  },
  claimReward: {
    es: "RECLAMAR {reward}",
    en: "CLAIM {reward}",
    de: "{reward} ANFORDERN",
    ru: "ПОЛУЧИТЬ {reward}",
  },
  copyLink: {
    es: "Copiar enlace",
    en: "Copy link",
    de: "Link kopieren",
    ru: "Скопировать ссылку",
  },
  link: { es: "Link", en: "Link", de: "Link", ru: "Link" },
  reachSummit: {
    es: "Llega a la cima en {turns}.",
    en: "Reach the summit in {turns}.",
    de: "Erreiche den Gipfel in {turns}.",
    ru: "Доберись до вершины за {turns}.",
  },
  reachSummitWithTime: {
    es: "Llega a la cima en {turns} y {minutes}.",
    en: "Reach the summit in {turns} and {minutes}.",
    de: "Erreiche den Gipfel in {turns} und {minutes}.",
    ru: "Доберись до вершины за {turns} и {minutes}.",
  },
  viewAllPuzzlesWithCount: {
    es: "VER TODOS LOS PUZZLES ({count})",
    en: "VIEW ALL PUZZLES ({count})",
    de: "ALLE PUZZLES ANZEIGEN ({count})",
    ru: "ПОСМОТРЕТЬ ВСЕ ГОЛОВОЛОМКИ ({count})",
  },
  successRateByMode: {
    es: "TASA DE ÉXITO POR MODO",
    en: "SUCCESS RATE BY MODE",
    de: "ERFOLGSRATE NACH MODUS",
    ru: "ПРОЦЕНТ УСПЕХА ПО РЕЖИМАМ",
  },
  overallSuccess: {
    es: "ÉXITO GENERAL",
    en: "OVERALL SUCCESS",
    de: "GESAMTERFOLG",
    ru: "ОБЩИЙ УСПЕХ",
  },
  averageMovesByMode: {
    es: "MOVIMIENTOS PROMEDIO POR MODO",
    en: "AVERAGE MOVES BY MODE",
    de: "DURCHSCHNITTLICHE ZÜGE NACH MODUS",
    ru: "СРЕДНЕЕ КОЛИЧЕСТВО ХОДОВ ПО РЕЖИМАМ",
  },
  rewardsEarnedByMode: {
    es: "RECOMPENSAS OBTENIDAS POR MODO",
    en: "REWARDS EARNED BY MODE",
    de: "ERHALTENE BELOHNUNGEN NACH MODUS",
    ru: "ПОЛУЧЕННЫЕ НАГРАДЫ ПО РЕЖИМАМ",
  },
  total: { es: "TOTAL", en: "TOTAL", de: "GESAMT", ru: "ВСЕГО" },
  averageTimeByMode: {
    es: "TIEMPO PROMEDIO POR MODO",
    en: "AVERAGE TIME BY MODE",
    de: "DURCHSCHNITTLICHE ZEIT NACH MODUS",
    ru: "СРЕДНЕЕ ВРЕМЯ ПО РЕЖИМАМ",
  },
  loadingPuzzles: {
    es: "Cargando puzzles...",
    en: "Loading puzzles...",
    de: "Puzzles werden geladen...",
    ru: "Загрузка головоломок...",
  },
  emptyPuzzles: {
    es: "Todavía no hay puzzles disponibles.",
    en: "There are no puzzles available yet.",
    de: "Es sind noch keine Puzzles verfügbar.",
    ru: "Головоломки пока недоступны.",
  },
  emptyMode: {
    es: "No hay puzzles en este modo.",
    en: "There are no puzzles in this mode.",
    de: "In diesem Modus gibt es keine Puzzles.",
    ru: "В этом режиме нет головоломок.",
  },
  emptySaved: {
    es: "Todavía no hay puzzles guardados. Entra a FreeBox y usa Guardar posición.",
    en: "There are no saved puzzles yet. Enter FreeBox and use Save position.",
    de: "Es gibt noch keine gespeicherten Puzzles. Gehe zu FreeBox und nutze Position speichern.",
    ru: "Сохранённых головоломок пока нет. Зайди в FreeBox и используй сохранение позиции.",
  },
  emptySavedByMode: {
    es: "Todavía no hay puzzles de {mode} guardados.",
    en: "There are no saved {mode} puzzles yet.",
    de: "Es gibt noch keine gespeicherten Puzzles im Modus {mode}.",
    ru: "Сохранённых головоломок режима {mode} пока нет.",
  },
  editPuzzle: {
    es: "EDITAR PUZZLE",
    en: "EDIT PUZZLE",
    de: "PUZZLE BEARBEITEN",
    ru: "РЕДАКТИРОВАТЬ ГОЛОВОЛОМКУ",
  },
  editPuzzleOn: {
    es: "EDITAR PUZZLE ON",
    en: "EDIT PUZZLE ON",
    de: "PUZZLE BEARBEITEN AN",
    ru: "РЕДАКТИРОВАНИЕ ГОЛОВОЛОМКИ ВКЛ.",
  },
  editModeActive: {
    es: "Modo edición activo: presiona la imagen del puzzle para editarlo.",
    en: "Edit mode active: press the puzzle image to edit it.",
    de: "Bearbeitungsmodus aktiv: Drücke auf das Puzzlebild, um es zu bearbeiten.",
    ru: "Режим редактирования включён: нажми на изображение головоломки, чтобы изменить её.",
  },
});

const PUZZLE_DASHBOARD_MODE_COPY_KEY = {
  free: "science",
  cienzar: "cienzar",
  sciencevsazar: "cienzar",
  dice: "chance",
};

const PUZZLE_RUSSIAN_UNITS = Object.freeze({
  turn: { one: "ход", few: "хода", many: "ходов", other: "ходов" },
  minute: { one: "минуту", few: "минуты", many: "минут", other: "минут" },
});

const PUZZLE_AUTO_RULE_TITLE_REGEX =
  /^(?:Llega a la cima en|Reach the summit in|Erreiche den Gipfel in|Доберись до вершины за)\s+\d+\s+(?:turno|turnos|movimiento|movimientos|turn|turns|Zug|Zügen|ход|хода|ходов)(?:\s+(?:y|and|und|и)\s+\d+\s+(?:minuto|minutos|minute|minutes|Minute|Minuten|минуту|минуты|минут))?\.$/i;

const getPuzzleDashboardCopy = (language, key, values = {}) => {
  const normalizedLanguage = normalizeLanguage(language);
  const entry = PUZZLE_DASHBOARD_COPY[key] || {};
  let copy = entry[normalizedLanguage] || entry.es || entry.en || key;

  Object.entries(values || {}).forEach(([name, value]) => {
    copy = copy.split(`{${name}}`).join(String(value));
  });

  return copy;
};

const getPuzzleDashboardNumberLocale = (language = "es") => {
  const normalizedLanguage = normalizeLanguage(language);
  if (normalizedLanguage === "de" || normalizedLanguage === "ru") {
    return getLocaleForLanguage(normalizedLanguage);
  }
  return "en-US";
};

const getPuzzleDashboardModeLabel = (language, mode) => {
  const normalizedMode = resolvePuzzleColumnMode(mode);
  return getPuzzleDashboardCopy(
    language,
    PUZZLE_DASHBOARD_MODE_COPY_KEY[normalizedMode] || "science"
  );
};

const getPuzzleRussianUnit = (unitType, count) => {
  const category = new Intl.PluralRules("ru-RU").select(Math.abs(Math.round(Number(count) || 0)));
  const unitMap = PUZZLE_RUSSIAN_UNITS[unitType] || PUZZLE_RUSSIAN_UNITS.turn;
  return unitMap[category] || unitMap.other;
};

const getPuzzleRuleUnit = (language, unitType, count) => {
  const normalizedLanguage = normalizeLanguage(language);
  const safeCount = Math.abs(Math.round(Number(count) || 0));

  if (normalizedLanguage === "en") {
    if (unitType === "minute") return safeCount === 1 ? "minute" : "minutes";
    return safeCount === 1 ? "turn" : "turns";
  }
  if (normalizedLanguage === "de") {
    if (unitType === "minute") return safeCount === 1 ? "Minute" : "Minuten";
    return safeCount === 1 ? "Zug" : "Zügen";
  }
  if (normalizedLanguage === "ru") {
    return getPuzzleRussianUnit(unitType, safeCount);
  }
  if (unitType === "minute") return safeCount === 1 ? "minuto" : "minutos";
  return safeCount === 1 ? "turno" : "turnos";
};

const formatPuzzleDashboardRuleTitle = (
  language,
  rawTurnLimit,
  rawSolveTimeLimitSeconds = 0
) => {
  const turnLimit = Math.max(1, normalizePuzzleTurnLimit(rawTurnLimit, DEFAULT_PUZZLE_TURN_LIMIT));
  const solveTimeLimitSeconds = normalizePuzzleSolveTimeLimitSeconds(rawSolveTimeLimitSeconds, 0);
  const solveTimeLimitMinutes =
    solveTimeLimitSeconds > 0 ? Math.max(1, Math.ceil(solveTimeLimitSeconds / 60)) : 0;
  const turnsLabel = `${turnLimit} ${getPuzzleRuleUnit(language, "turn", turnLimit)}`;

  if (solveTimeLimitMinutes <= 0) {
    return getPuzzleDashboardCopy(language, "reachSummit", { turns: turnsLabel });
  }

  const minutesLabel = `${solveTimeLimitMinutes} ${getPuzzleRuleUnit(
    language,
    "minute",
    solveTimeLimitMinutes
  )}`;
  return getPuzzleDashboardCopy(language, "reachSummitWithTime", {
    turns: turnsLabel,
    minutes: minutesLabel,
  });
};

const buildDefaultPuzzleNameFromRules = (
  rawTurnLimit,
  rawSolveTimeLimitSeconds = 0,
  language = "es"
) => formatPuzzleDashboardRuleTitle(language, rawTurnLimit, rawSolveTimeLimitSeconds);

const isAutoPuzzleRuleTitle = (value) =>
  PUZZLE_AUTO_RULE_TITLE_REGEX.test(String(value || "").trim());

const localizePuzzleDashboardTitle = (
  language,
  rawTitle,
  rawTurnLimit,
  rawSolveTimeLimitSeconds = 0
) => {
  const trimmedTitle = String(rawTitle || "").trim();
  if (!trimmedTitle || trimmedTitle.toLowerCase() === "puzzle" || isAutoPuzzleRuleTitle(trimmedTitle)) {
    return formatPuzzleDashboardRuleTitle(language, rawTurnLimit, rawSolveTimeLimitSeconds);
  }
  return trimmedTitle;
};

const resolvePuzzleRewardValue = (puzzleRecord) => {
  const puzzle =
    puzzleRecord && typeof puzzleRecord === "object" && !Array.isArray(puzzleRecord)
      ? puzzleRecord
      : {};
  const rewardCandidate = [
    puzzle?.puzzleRewardValue,
    puzzle?.rewardValue,
    puzzle?.rewardAmount,
    puzzle?.reward,
    puzzle?.rewardX,
    puzzle?.walletReward,
    puzzle?.prize,
    puzzle?.xReward,
  ].find((value) => Number.isFinite(parsePuzzleRewardValue(value)));
  return normalizePuzzleRewardValue(rewardCandidate, PUZZLE_DEFAULT_REWARD_VALUE);
};

const resolvePuzzleDashboardNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(0, parsed);
};

const resolvePuzzleDashboardMoveCount = (puzzleRecord) =>
  Math.max(
    0,
    Math.floor(
      resolvePuzzleDashboardNumber(
        puzzleRecord?.puzzleMoveCount ??
          puzzleRecord?.moveCount ??
          puzzleRecord?.moves ??
          puzzleRecord?.totalMoves,
        0
      )
    )
  );

const resolvePuzzleDashboardAttemptCount = (puzzleRecord) =>
  Math.max(
    1,
    Math.floor(
      resolvePuzzleDashboardNumber(
        puzzleRecord?.puzzleAttemptCount ??
          puzzleRecord?.attemptCount ??
          puzzleRecord?.attempts ??
          puzzleRecord?.solveAttempts,
        1
      )
    )
  );

const formatPuzzleDashboardInteger = (value, language = "es") =>
  new Intl.NumberFormat(getPuzzleDashboardNumberLocale(language), {
    maximumFractionDigits: 0,
  }).format(Math.max(0, Math.round(Number(value) || 0)));

const formatPuzzleDashboardDecimal = (value, decimals = 2, language = "es") =>
  new Intl.NumberFormat(getPuzzleDashboardNumberLocale(language), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Math.max(0, Number(value) || 0));

const formatPuzzleDashboardPercent = (value, decimals = 1, language = "es") => {
  const normalizedLanguage = normalizeLanguage(language);
  const spacer = normalizedLanguage === "de" || normalizedLanguage === "ru" ? " " : "";
  return `${formatPuzzleDashboardDecimal(value, decimals, normalizedLanguage)}${spacer}%`;
};

const formatPuzzleDashboardTime = (seconds) => {
  const totalSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
};

const resolvePuzzleDashboardModeMeta = (mode, language = "es") => {
  const normalized = resolvePuzzleColumnMode(mode);
  if (normalized === "cienzar") {
    return {
      mode: "cienzar",
      label: getPuzzleDashboardModeLabel(language, "cienzar"),
      Icon: IoBeakerOutline,
      toneClass: "is-cienzar",
    };
  }
  if (normalized === "sciencevsazar") {
    return {
      mode: "sciencevsazar",
      label: getFindMatchesCopy(language, "scienceVsAzarShort"),
      Icon: IoBeakerOutline,
      toneClass: "is-sciencevsazar",
    };
  }
  if (normalized === "dice") {
    return {
      mode: "dice",
      label: getPuzzleDashboardModeLabel(language, "dice"),
      Icon: IoDiceOutline,
      toneClass: "is-azar",
    };
  }
  return {
    mode: "free",
    label: getPuzzleDashboardModeLabel(language, "free"),
    Icon: IoCubeOutline,
    toneClass: "is-ciencia",
  };
};

const parseStoredProfile = (rawValue) => {
  try {
    if (!rawValue) return null;
    const parsed = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
};

const getStoredAvatarByEmail = (email) => {
  if (typeof window === "undefined") return "";
  const key = getProfileStorageKey(email);
  if (!key) return "";
  const parsed = parseStoredProfile(localStorage.getItem(key));
  return resolveAvatarSrc(parsed?.avatarUrl || "");
};

const getInitials = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "P";
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
};

const isStringifiedObjectText = (value) => /^\[object\s/i.test(String(value || "").trim());

// Some player entries arrive with the user record nested inside userId/user;
// lift those fields up so the name never renders as "[object Object]".
const flattenNestedPlayerEntry = (rawUser) => {
  if (!rawUser || typeof rawUser !== "object") return rawUser;
  const nested = [rawUser.userId, rawUser.user].find(
    (candidate) => candidate && typeof candidate === "object" && !Array.isArray(candidate)
  );
  if (!nested) return rawUser;
  const nestedUserId = [
    nested.userId,
    nested.loginIdentifier,
    nested.username,
    nested.userName,
    nested.email,
    nested._id,
    nested.id,
  ].find((candidate) => typeof candidate === "string" && candidate.trim());
  const flattened = { ...nested, ...rawUser };
  flattened.userId = nestedUserId || "";
  if (flattened.user && typeof flattened.user === "object") delete flattened.user;
  return flattened;
};

const extractPrimaryName = (value) => {
  if (value && typeof value === "object") return "";
  const raw = String(value || "").trim();
  if (!raw || isStringifiedObjectText(raw)) return "";
  if (raw.includes("@")) {
    const alias = String(raw.split("@")[0] || "").trim();
    if (alias) return alias;
  }
  const parts = raw.split(/\s+/).filter(Boolean);
  return String(parts[0] || raw).trim();
};

const HISTORY_BOT_LABEL_REGEX = /\bbot\b/i;

const isHistoryBotParticipant = (participant) => {
  if (!participant || typeof participant !== "object") {
    return HISTORY_BOT_LABEL_REGEX.test(String(participant || "").trim());
  }
  if (participant?.isBot === true) return true;

  const candidates = [
    participant?.displayName,
    participant?.userId,
    participant?.username,
    participant?.userName,
    participant?.nickName,
    participant?.nickname,
    participant?.email,
  ];

  return candidates.some((value) => HISTORY_BOT_LABEL_REGEX.test(String(value || "").trim()));
};

const resolvePreferredPlayerLabel = (player) => {
  if (!player || typeof player !== "object") {
    const fallback = extractPrimaryName(player);
    return fallback || "Jugador";
  }

  const username =
    String(player?.userName || player?.username || player?.nickName || player?.nickname || "")
      .trim();
  if (username) return extractPrimaryName(username) || username;

  const displayName = extractPrimaryName(player?.displayName);
  if (displayName) return displayName;

  const firstName = extractPrimaryName(player?.firstName);
  if (firstName) return firstName;

  const userId = String(player?.userId || "").trim();
  if (userId) return userId;

  const email = String(player?.email || "").trim();
  if (email.includes("@")) return extractPrimaryName(email);
  if (email) return email;

  return "Jugador";
};

const getQuickPlayPrefsStorageKey = (email) => {
  const normalizedEmail = String(email || "").trim().toLowerCase();
  return normalizedEmail
    ? `${QUICK_PLAY_PREFS_PREFIX}${normalizedEmail}`
    : QUICK_PLAY_PREFS_GUEST_KEY;
};
const getLastJoinedTournamentStorageKey = (email) => {
  const normalizedEmail = String(email || "guest").trim().toLowerCase();
  return `${LAST_JOINED_TOURNAMENT_PREFIX}${normalizedEmail || "guest"}`;
};
const parseStoredLastJoinedTournamentId = (rawValue) => {
  const raw = String(rawValue || "").trim();
  if (!raw) return "";
  try {
    const parsed = JSON.parse(raw);
    const candidate = String(parsed?.tournamentId || parsed?.id || "").trim();
    return candidate || raw;
  } catch {
    return raw;
  }
};
const getStoredLastJoinedTournamentId = (storageKey) => {
  if (typeof window === "undefined") return "";
  const key = String(storageKey || "").trim();
  if (!key) return "";
  return parseStoredLastJoinedTournamentId(window.localStorage.getItem(key));
};
const setStoredLastJoinedTournamentId = (storageKey, tournamentId) => {
  if (typeof window === "undefined") return;
  const key = String(storageKey || "").trim();
  const normalizedTournamentId = String(tournamentId || "").trim();
  if (!key || !normalizedTournamentId) return;
  window.localStorage.setItem(
    key,
    JSON.stringify({
      tournamentId: normalizedTournamentId,
      updatedAt: new Date().toISOString(),
    })
  );
};

const parseStoredQuickPlayPrefs = (rawValue) => {
  try {
    if (!rawValue) return null;
    const parsed = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
};

const normalizeStoredPlayType = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return PLAY_TYPE_OPTIONS.some((option) => option.key === normalized) ? normalized : "online";
};

const normalizeStoredModeForPlayType = (playType, value) => {
  if (playType === "bot") return normalizeSelectableBotModeValue(value, "botlocalfree");
  if (playType === "freebox") {
    const normalized = normalizeModeAlias(value, "dice");
    if (ONLINE_LOBBY_MODES.has(normalized)) return normalized;
    if (LOCAL_MODE_REVERSE_MAP[normalized]) return LOCAL_MODE_REVERSE_MAP[normalized];
    return "dice";
  }

  const normalized = normalizeModeAlias(value, "dice");
  return ONLINE_LOBBY_MODES.has(normalized) ? normalized : "dice";
};

const getAllowedMemberOptions = (allowExtendedPlayers = true) =>
  allowExtendedPlayers ? MEMBER_OPTIONS : [2];

const resolvePlayTypeFromMode = (mode) => {
  const normalized = normalizeModeAlias(mode, "dice");
  if (normalized === "botlocal" || BOT_MODE_SET.has(normalized)) return "bot";
  if (normalized === "freebox") return "freebox";
  if (LOCAL_FRIENDS_MODES.has(normalized)) return "local";
  return "online";
};

const normalizeStoredMemberSize = (playType, value, allowExtendedPlayers = true) => {
  const baseAllowedOptions = getAllowedMemberOptions(allowExtendedPlayers);
  const allowedOptions =
    playType === "bot"
      ? BOT_MEMBER_OPTIONS.filter((option) => baseAllowedOptions.includes(option))
      : baseAllowedOptions;
  if (!allowedOptions.length) return 2;
  const parsed = Number(value);
  return allowedOptions.includes(parsed) ? parsed : allowedOptions[0];
};

const normalizeStoredTurnTime = (value) => {
  const parsed = Number(value);
  return String(TIME_OPTIONS.includes(parsed) ? parsed : DEFAULT_TURN_TIME);
};

const normalizeStoredPanelProgress = (playType, value) => {
  const fallback = buildPanelProgress(playType);
  if (!value || typeof value !== "object") return fallback;

  const readFlag = (key, defaultValue) =>
    typeof value[key] === "boolean" ? value[key] : defaultValue;

  if (playType === "bot") {
    return {
      playersSelected: true,
      modeSelected: true,
      timeSelected: true,
    };
  }

  if (playType === "freebox") {
    return {
      playersSelected: readFlag("playersSelected", fallback.playersSelected),
      modeSelected: true,
      timeSelected: false,
    };
  }

  return {
    playersSelected: readFlag("playersSelected", fallback.playersSelected),
    modeSelected: readFlag("modeSelected", fallback.modeSelected),
    timeSelected: readFlag("timeSelected", fallback.timeSelected),
  };
};

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read avatar file"));
    reader.readAsDataURL(file);
  });

const loadImageFromDataUrl = (dataUrl) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Failed to load avatar image"));
    image.src = dataUrl;
  });

const optimizeAvatarDataUrl = async (rawDataUrl) => {
  const dataUrl = String(rawDataUrl || "").trim();
  if (!dataUrl.startsWith("data:image/")) {
    return dataUrl.slice(0, AVATAR_MAX_DATA_URL_LENGTH);
  }

  if (dataUrl.length <= AVATAR_MAX_DATA_URL_LENGTH) {
    return dataUrl;
  }

  const image = await loadImageFromDataUrl(dataUrl);
  const maxEdge = Math.max(image.width || 1, image.height || 1);
  const scale = Math.min(1, AVATAR_MAX_DIMENSION / maxEdge);
  const width = Math.max(1, Math.round((image.width || 1) * scale));
  const height = Math.max(1, Math.round((image.height || 1) * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) return dataUrl.slice(0, AVATAR_MAX_DATA_URL_LENGTH);
  context.drawImage(image, 0, 0, width, height);

  let bestCandidate = "";
  for (let i = 0; i < AVATAR_QUALITY_STEPS.length; i += 1) {
    const quality = AVATAR_QUALITY_STEPS[i];
    const candidate = canvas.toDataURL("image/jpeg", quality);
    if (!candidate) continue;
    bestCandidate = candidate;
    if (candidate.length <= AVATAR_MAX_DATA_URL_LENGTH) {
      return candidate;
    }
  }

  return String(bestCandidate || "").slice(0, AVATAR_MAX_DATA_URL_LENGTH);
};

const AI_AVATAR_PRESETS = [
  { id: "aurora", name: "Aurora", src: resolveVersionedPublicAssetPath("assets/profile-avatars/avatar-aurora.png") },
  { id: "ember", name: "Ember", src: resolveVersionedPublicAssetPath("assets/profile-avatars/avatar-ember.png") },
  { id: "verdant", name: "Verdant", src: resolveVersionedPublicAssetPath("assets/profile-avatars/avatar-verdant.png") },
  { id: "royal", name: "Royal", src: resolveVersionedPublicAssetPath("assets/profile-avatars/avatar-royal.png") },
  { id: "solar", name: "Solar", src: resolveVersionedPublicAssetPath("assets/profile-avatars/avatar-solar.png") },
  { id: "tide", name: "Tide", src: resolveVersionedPublicAssetPath("assets/profile-avatars/avatar-tide.png") },
];

const Index = ({ forcedTab = "" }) => {
  const [tournaments, setTournaments] = useState([]);
  const [puzzles, setPuzzles] = useState([]);
  const [puzzlesLoading, setPuzzlesLoading] = useState(false);
  const [launchingPuzzleId, setLaunchingPuzzleId] = useState("");
  const [claimingPuzzleRewardId, setClaimingPuzzleRewardId] = useState("");
  const [puzzleDashboardModeFilter, setPuzzleDashboardModeFilter] = useState("all");
  const [puzzleDashboardSearch, setPuzzleDashboardSearch] = useState("");
  const [puzzleDashboardSort, setPuzzleDashboardSort] = useState("recent");
  const [dailyPuzzleModeFilter, setDailyPuzzleModeFilter] = useState("all");
  const [dailyPuzzleSearch, setDailyPuzzleSearch] = useState("");
  const [dailyPuzzleSort, setDailyPuzzleSort] = useState("recent");
  const [findMatchesModeFilter, setFindMatchesModeFilter] = useState("all");
  const [findMatchesSearch, setFindMatchesSearch] = useState("");
  const [findMatchesSort, setFindMatchesSort] = useState("recent");
  const [puzzleEditModeEnabled, setPuzzleEditModeEnabled] = useState(false);
  const [puzzleEditModalOpen, setPuzzleEditModalOpen] = useState(false);
  const [puzzleEditSaving, setPuzzleEditSaving] = useState(false);
  const [puzzleEditTargetId, setPuzzleEditTargetId] = useState("");
  const [puzzleEditTargetLabel, setPuzzleEditTargetLabel] = useState("");
  const [puzzleEditNameDraft, setPuzzleEditNameDraft] = useState("");
  const [puzzleEditTurnLimitDraft, setPuzzleEditTurnLimitDraft] = useState(
    DEFAULT_PUZZLE_TURN_LIMIT
  );
  const [puzzleEditRewardValueDraft, setPuzzleEditRewardValueDraft] = useState(
    PUZZLE_DEFAULT_REWARD_VALUE
  );
  const [puzzleEditSolveTimeLimitEnabledDraft, setPuzzleEditSolveTimeLimitEnabledDraft] =
    useState(false);
  const [puzzleEditSolveTimeLimitMinutesDraft, setPuzzleEditSolveTimeLimitMinutesDraft] = useState(
    DEFAULT_PUZZLE_SOLVE_TIME_MINUTES
  );
  const [puzzleEditResetRewardProgressDraft, setPuzzleEditResetRewardProgressDraft] =
    useState(false);
  const [tournamentEditModeEnabled, setTournamentEditModeEnabled] = useState(false);
  const [tournamentEditModalOpen, setTournamentEditModalOpen] = useState(false);
  const [tournamentEditSaving, setTournamentEditSaving] = useState(false);
  const [tournamentEditTargetId, setTournamentEditTargetId] = useState("");
  const [tournamentEditTargetLabel, setTournamentEditTargetLabel] = useState("");
  const [tournamentEditNameDraft, setTournamentEditNameDraft] = useState("");
  const [tournamentEditStartDateDraft, setTournamentEditStartDateDraft] = useState("");
  const [tournamentEditStartTimeDraft, setTournamentEditStartTimeDraft] = useState("");
  const [tournamentEditDurationDraft, setTournamentEditDurationDraft] = useState(1);
  const [tournamentEditHavingTimeDraft, setTournamentEditHavingTimeDraft] = useState(
    DEFAULT_TURN_TIME
  );
  const [tournamentEditMemberSizeDraft, setTournamentEditMemberSizeDraft] = useState(
    MEMBER_OPTIONS[0]
  );
  const [tournamentEditRewardValueDraft, setTournamentEditRewardValueDraft] = useState(
    TOURNAMENT_DEFAULT_REWARD_VALUE
  );
  const [tournamentEditWinnerPrizeValueDraft, setTournamentEditWinnerPrizeValueDraft] =
    useState(TOURNAMENT_DEFAULT_REWARD_VALUE);
  const [filteredTournaments, setFilteredTournaments] = useState([]);
  const [modeWinnersByMode, setModeWinnersByMode] = useState(() =>
    buildEmptyModeWinnersMap()
  );
  const [modeWinnersLoading, setModeWinnersLoading] = useState(false);
  const [historyMatches, setHistoryMatches] = useState([]);
  const [historyTotalMatches, setHistoryTotalMatches] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyShowAllMatches, setHistoryShowAllMatches] = useState(false);
  const [historyResultFilter, setHistoryResultFilter] = useState("all");
  const [historyModeFilter, setHistoryModeFilter] = useState("all");
  const [historyPlayerSearch, setHistoryPlayerSearch] = useState("");
  const [historyDateFilter, setHistoryDateFilter] = useState("all");
  const [historySortOrder, setHistorySortOrder] = useState("recent");
  const [replayModalOpen, setReplayModalOpen] = useState(false);
  const [replayLoading, setReplayLoading] = useState(false);
  const [replaySelectedMatch, setReplaySelectedMatch] = useState(null);
  const [replayFrameNonce, setReplayFrameNonce] = useState(0);
  const [replayTimeline, setReplayTimeline] = useState([]);
  const [replayCurrentStep, setReplayCurrentStep] = useState(0);
  const [replayTotalSteps, setReplayTotalSteps] = useState(0);
  const [replayPlaying, setReplayPlaying] = useState(false);
  const [replayPlaybackSpeedIndex, setReplayPlaybackSpeedIndex] = useState(
    REPLAY_PLAYBACK_DEFAULT_INDEX
  );
  const [replayAwaitingStep, setReplayAwaitingStepState] = useState(false);
  const [replayReady, setReplayReady] = useState(false);
  const [, setReplayInfo] = useState(null);
  // «Bloques reales» / «Bloques por defecto» (se recuerda entre sesiones).
  const [replayBlockVisualsMode, setReplayBlockVisualsMode] = useState(readReplayBlockVisualsMode);
  const [replayDesignSource, setReplayDesignSource] = useState(null);
  const [replayExpanded, setReplayExpanded] = useState(false);
  const [replayRestarting, setReplayRestarting] = useState(false);
  const [replayPanelPosition, setReplayPanelPosition] = useState({ x: 16, y: 16 });
  const [replayPanelSize, setReplayPanelSize] = useState({
    width: REPLAY_PANEL_DEFAULT_WIDTH,
    height: REPLAY_PANEL_DEFAULT_HEIGHT,
  });
  const [replayPanelInteracting, setReplayPanelInteracting] = useState(false);
  const [replayAnalysisActive, setReplayAnalysisActive] = useState(false);
  const [replayAnalysisUndoCount, setReplayAnalysisUndoCount] = useState(0);
  const [replayArrowsActive, setReplayArrowsActive] = useState(false);
  const [replayArrowColor, setReplayArrowColor] = useState("blue");
  const [replayScrubStep, setReplayScrubStep] = useState(null);
  const [replayReview, setReplayReview] = useState(null);
  const [replayReviewActive, setReplayReviewActive] = useState(false);
  const replayReviewMovesRef = useRef(null);
  const [replayManualWidth, setReplayManualWidth] = useState(
    String(REPLAY_PANEL_DEFAULT_WIDTH)
  );
  const [replayManualHeight, setReplayManualHeight] = useState(
    String(REPLAY_PANEL_DEFAULT_HEIGHT)
  );
  const [filter] = useState("all");
  const [creating, setCreating] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [formData, setFormData] = useState(buildDefaultFormData);
  const [playType, setPlayType] = useState("online");
  const [matchmakingQuickView, setMatchmakingQuickView] = useState("torneos");
  const [quickViewButtonPulseKey, setQuickViewButtonPulseKey] = useState("");
  const [panelProgress, setPanelProgress] = useState(() => buildPanelProgress("online"));
  const [localPlayerNames, setLocalPlayerNames] = useState(["", "", ""]);
  const [superTournamentMinutesInput, setSuperTournamentMinutesInput] = useState(
    String(DEFAULT_SUPER_TOURNAMENT_MINUTES)
  );
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const lobbyProfileModalRequestHandledRef = useRef("");
  const [lobbyDesignActiveEditor, setLobbyDesignActiveEditor] = useState(() => {
    if (typeof window === "undefined") return "special";

    const storedEditor = String(
      window.localStorage.getItem("xima_lobby_design_active_editor_v1") || ""
    )
      .trim()
      .toLowerCase();

    return ["profile", "special", "common", "board"].includes(storedEditor)
      ? storedEditor
      : "special";
  });
  const persistLobbyDesignActiveEditor = useCallback((editorId) => {
    const nextEditor = ["profile", "special", "common", "board"].includes(
      String(editorId || "").trim().toLowerCase()
    )
      ? String(editorId || "").trim().toLowerCase()
      : "special";

    setLobbyDesignActiveEditor(nextEditor);

    if (typeof window !== "undefined") {
      window.localStorage.setItem("xima_lobby_design_active_editor_v1", nextEditor);
    }
  }, []);
  const [profileAvatar, setProfileAvatar] = useState("");
  const [profileDraftAvatar, setProfileDraftAvatar] = useState("");
  const [profileDraftAvatarFile, setProfileDraftAvatarFile] = useState(null);
  const [profileFirstName, setProfileFirstName] = useState("");
  const [profileLastName, setProfileLastName] = useState("");
  const [profileContactEmail, setProfileContactEmail] = useState("");
  const [profilePhone, setProfilePhone] = useState("");
  const [profileDraftFirstName, setProfileDraftFirstName] = useState("");
  const [profileDraftLastName, setProfileDraftLastName] = useState("");
  const [profileDraftContactEmail, setProfileDraftContactEmail] = useState("");
  const [profileDraftPhone, setProfileDraftPhone] = useState("");
  const [profileDraftCurrentPassword, setProfileDraftCurrentPassword] = useState("");
  const [profileDraftNewPassword, setProfileDraftNewPassword] = useState("");
  const [profileDraftConfirmPassword, setProfileDraftConfirmPassword] = useState("");
  const [profileSavingPersonal, setProfileSavingPersonal] = useState(false);
  const [tournamentWaitingOpen, setTournamentWaitingOpen] = useState(false);
  const [tournamentWaitingJoinLoading, setTournamentWaitingJoinLoading] = useState(false);
  const [tournamentWaitingRecord, setTournamentWaitingRecord] = useState(null);
  const [tournamentWaitingChatInput, setTournamentWaitingChatInput] = useState("");
  const [tournamentWaitingChatMessages, setTournamentWaitingChatMessages] = useState([]);
  const [manualReconnectLoading, setManualReconnectLoading] = useState(false);
  const [manualReconnectAvailable, setManualReconnectAvailable] = useState(false);
  const [cimaxScores, setCimaxScores] = useState(() => ({ ...DEFAULT_CIMAX_SCORES }));
  const [walletBalance, setWalletBalance] = useState(DEFAULT_WALLET_BALANCE);
  const [claimedPuzzleRewardMap, setClaimedPuzzleRewardMap] = useState({});
  const [, setOptimisticTournamentPlayersById] = useState({});
  const [joinedUiPlayersByTournamentId, setJoinedUiPlayersByTournamentId] = useState({});
  const [lastJoinedTournamentId, setLastJoinedTournamentId] = useState("");
  const [specialBlockModalOpen, setSpecialBlockModalOpen] = useState(false);
  const [boardThemeModalOpen, setBoardThemeModalOpen] = useState(false);
  const [commonBlocksModalOpen, setCommonBlocksModalOpen] = useState(false);
  const [specialSkinCatalog, setSpecialSkinCatalog] = useState([]);
  const [commonSkinCatalog, setCommonSkinCatalog] = useState([]);
  const [ownedSpecialSkinProductIds, setOwnedSpecialSkinProductIds] = useState([]);
  const [ownedCommonSkinProductIds, setOwnedCommonSkinProductIds] = useState([]);
  const [isTopWinnerSkinEligible, setIsTopWinnerSkinEligible] = useState(false);
  const [topWinnerSkinEligibleModes, setTopWinnerSkinEligibleModes] = useState([]);
  const [lockedSkinPrompt, setLockedSkinPrompt] = useState(null);
  const [selectedSpecialBlockId, setSelectedSpecialBlockId] = useState(
    DEFAULT_SPECIAL_BLOCK_ID
  );
  const [draftSpecialBlockId, setDraftSpecialBlockId] = useState(DEFAULT_SPECIAL_BLOCK_ID);
  const [selectedBoardThemeId, setSelectedBoardThemeId] = useState(DEFAULT_BOARD_THEME_ID);
  const [selectedCommonBlockThemeId, setSelectedCommonBlockThemeId] = useState(
    DEFAULT_COMMON_BLOCK_THEME_ID
  );
  const [draftBoardThemeId, setDraftBoardThemeId] = useState(DEFAULT_BOARD_THEME_ID);
  const [draftCommonBlockThemeId, setDraftCommonBlockThemeId] = useState(
    DEFAULT_COMMON_BLOCK_THEME_ID
  );
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [adminPasswordInput, setAdminPasswordInput] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminEnabled, setAdminEnabled] = useState(false);
  const [adminRooms, setAdminRooms] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminPuzzles, setAdminPuzzles] = useState([]);
  const [adminIndependentChallenges, setAdminIndependentChallenges] = useState([]);
  const [adminOrders, setAdminOrders] = useState([]);
  const [adminOrdersFilter, setAdminOrdersFilter] = useState("all");
  const [adminUserSearchTerm, setAdminUserSearchTerm] = useState("");
  const [adminOrderDetail, setAdminOrderDetail] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminActionKey, setAdminActionKey] = useState("");
  const [onlineFriends, setOnlineFriends] = useState([]);
  const [totalFriendsCount, setTotalFriendsCount] = useState(0);
  const [onlineFriendsLoading, setOnlineFriendsLoading] = useState(false);
  const [newsBoardMessages, setNewsBoardMessages] = useState(() => parseStoredNewsBoardMessages());
  const [newsBoardInput, setNewsBoardInput] = useState("");
  const [newsBoardEditingMessageId, setNewsBoardEditingMessageId] = useState("");
  const [newsBoardEditingInput, setNewsBoardEditingInput] = useState("");
  const [globalChatMessages, setGlobalChatMessages] = useState(() => parseStoredGlobalChatMessages());
  const [globalChatInput, setGlobalChatInput] = useState("");
  const [chatFloatingOpen, setChatFloatingOpen] = useState(false);
  const [chatFloatingMaximized, setChatFloatingMaximized] = useState(false);
  const [chatFloatingPosition, setChatFloatingPosition] = useState({ x: 0, y: 0 });
  const [chatFloatingSize, setChatFloatingSize] = useState({
    width: CHAT_FLOATING_DEFAULT_WIDTH,
    height: CHAT_FLOATING_DEFAULT_HEIGHT,
  });
  const [centerRightResizableLayout, setCenterRightResizableLayout] = useState(() =>
    mediaQueryMatches(CENTER_RIGHT_RESIZE_LAYOUT_QUERY)
  );
  const [isPhoneLandscapeViewport, setIsPhoneLandscapeViewport] = useState(false);
  const [isIphoneChromeLandscapePhone, setIsIphoneChromeLandscapePhone] = useState(false);
  const [isSamsungChromeLandscapePhone, setIsSamsungChromeLandscapePhone] = useState(false);
  const [isIphoneSamsungLandscape, setIsIphoneSamsungLandscape] = useState(false);
  const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(false);
  const [rightPanelWidth, setRightPanelWidth] = useState(getDefaultRightPanelWidth);
  const [liveMatchesByTournamentId, setLiveMatchesByTournamentId] = useState({});
  const [waitingRoomPresenceByTournamentId, setWaitingRoomPresenceByTournamentId] = useState({});
  const [waitingRoomActivityByTournamentId, setWaitingRoomActivityByTournamentId] = useState({});
  const replayIframeRef = useRef(null);
  // «Descargar partido»: vista previa del área del video sobre la repetición.
  const [replayVideoPreview, setReplayVideoPreview] = useState(null);
  const [replayVideoPreviewHost, setReplayVideoPreviewHost] = useState(null);
  const [replayBoardElement, setReplayBoardElement] = useState(null);
  const [replayBoardSize, setReplayBoardSize] = useState(null);
  const globalChatScrollRef = useRef(null);
  const globalChatFloatingScrollRef = useRef(null);
  const chatFloatingDragRef = useRef(null);
  const chatFloatingResizeRef = useRef(null);
  const replayPanelDragRef = useRef(null);
  const replayPanelResizeRef = useRef(null);
  const replayActiveTimelineItemRef = useRef(null);
  const replayTimelineScrollRef = useRef(null);
  const centerRightResizeRef = useRef(null);
  const replayTickingRef = useRef(false);
  const replayAwaitingStepRef = useRef(false);
  const replayCurrentStepRef = useRef(0);
  const replayTotalStepsRef = useRef(0);
  const replayLoadingRef = useRef(false);
  const replayAwaitingFallbackTimerRef = useRef(null);
  const replayPendingStepAdvanceRef = useRef(null);
  const replayReadyWaiterRef = useRef(null);
  const replayPositionWaiterRef = useRef(null);
  const replayAnalysisActiveRef = useRef(false);
  const replayPositionLastMessageRef = useRef("");
  const replayIframeBootstrappedKeyRef = useRef("");
  const selectedButtonRef = useRef(null);
  const buttonInteractionCounterRef = useRef(0);
  const quickPlayPrefsLoadedKeyRef = useRef("");
  const tournamentWaitingTargetRef = useRef("");
  const tournamentWaitingShouldRemainJoinedRef = useRef(false);
  const tournamentWaitingPasswordByIdRef = useRef({});
  const waitingRoomOpenHandledKeyRef = useRef("");
  const replayOpenHandledKeyRef = useRef("");
  const matchmakingViewOpenHandledKeyRef = useRef("");
  const lobbyCreateAutoHandledKeyRef = useRef("");
  const fetchPuzzlesRef = useRef(null);
  const fetchTournamentsRef = useRef(null);
  const fetchLiveMatchesRef = useRef(null);
  const puzzleOpenHandledKeyRef = useRef("");
  const nextPuzzleOpenHandledKeyRef = useRef("");
  const retryPuzzleOpenHandledKeyRef = useRef("");
  const shareLinkOpenHandledKeyRef = useRef("");
  const manualReconnectAutoAttemptedPlayerRef = useRef("");

  const navigate = useNavigate();
  const location = useLocation();
  const outletContext = useOutletContext() || {};
  const { user, refreshUserProfile } = useAuth();
  const [replayCameraFollowEnabled, setReplayCameraFollowEnabled] = useState(() =>
    readCameraFollowPreference(user?.email)
  );
  const socialAlert = outletContext.socialAlert || {};
  const refreshSocialAlert = outletContext.refreshSocialAlert;
  const hasAdminMenuAccessFromSidebar = outletContext.hasAdminMenuAccess === true;
  const language = normalizeLanguage(outletContext.language);
  setI18nLanguage(language);
  const tx = useCallback(
    (es, en) => {
      if (language === "es") return es;
      const localized = DIRECT_UI_COPY_BY_EN[en]?.[language];
      if (localized) return localized;
      if (language === "ru") return es;
      return en;
    },
    [language]
  );
  const historyText = useCallback(
    (key, values) => getHistoryCopy(language, key, values),
    [language]
  );
  useEffect(() => {
    setReplayCameraFollowEnabled(readCameraFollowPreference(user?.email));
  }, [user?.email]);
  const findMatchesText = useCallback(
    (key, values) => getFindMatchesCopy(language, key, values),
    [language]
  );
  const bonusesLabel = useMemo(() => {
    if (language === "en") return "Bonuses";
    if (language === "de") return "Boni";
    if (language === "ru") return "Бонусы";
    return "BONOS";
  }, [language]);
  const [searchParams] = useSearchParams();
  const canUseExtendedPlayerOptions = true;
  const isSamsungPortraitMobile = outletContext.isSamsungPortraitMobile === true;
  const isIphonePortraitMobile = outletContext.isIphonePortraitMobile === true;
  const isFromLobbyPuzzleQuery = ["1", "true", "yes"].includes(
    String(searchParams.get("fromLobbyPuzzle") || "")
      .trim()
      .toLowerCase()
  );
  const isFromLobbyPuzzleState = (() => {
    const state = location.state;
    if (!state || typeof state !== "object" || Array.isArray(state)) return false;
    const lobbyPuzzleState = String(state.fromLobbyPuzzle || "")
      .trim()
      .toLowerCase();
    const stateSource = String(state.source || "").trim().toLowerCase();
    return (
      lobbyPuzzleState === "1" ||
      lobbyPuzzleState === "true" ||
      lobbyPuzzleState === "yes" ||
      stateSource === "lobbypuzzle"
    );
  })();
  const isFromLobbyPuzzle = isFromLobbyPuzzleQuery || isFromLobbyPuzzleState;
  const isFromLobbyTournamentQuery = ["1", "true", "yes"].includes(
    String(searchParams.get("fromLobbyTournament") || "")
      .trim()
      .toLowerCase()
  );
  const isFromLobbyTournamentState = (() => {
    const state = location.state;
    if (!state || typeof state !== "object" || Array.isArray(state)) return false;
    const lobbyTournamentState = String(state.fromLobbyTournament || "")
      .trim()
      .toLowerCase();
    const stateSource = String(state.source || "").trim().toLowerCase();
    return (
      lobbyTournamentState === "1" ||
      lobbyTournamentState === "true" ||
      lobbyTournamentState === "yes" ||
      stateSource === "lobbytournament"
    );
  })();
  const isFromLobbyTournament =
    isFromLobbyTournamentQuery || isFromLobbyTournamentState;
  const isFromLobbyCreateQuery = ["1", "true", "yes"].includes(
    String(searchParams.get("fromLobbyCreate") || "")
      .trim()
      .toLowerCase()
  );
  const isFromLobbyCreateState = (() => {
    const state = location.state;
    if (!state || typeof state !== "object" || Array.isArray(state)) return false;
    const lobbyCreateState = String(state.fromLobbyCreate || "")
      .trim()
      .toLowerCase();
    const stateSource = String(state.source || "").trim().toLowerCase();
    return (
      lobbyCreateState === "1" ||
      lobbyCreateState === "true" ||
      lobbyCreateState === "yes" ||
      stateSource === "lobbycreate"
    );
  })();
  const isFromLobbyCreateMatch = isFromLobbyCreateQuery || isFromLobbyCreateState;
  const fromLobbySourceFromQuery = String(searchParams.get("fromLobbySource") || "")
    .trim()
    .toLowerCase();
  const fromLobbySourceFromState = (() => {
    const state = location.state;
    if (!state || typeof state !== "object" || Array.isArray(state)) return "";
    return String(state.fromLobbySource || "")
      .trim()
      .toLowerCase();
  })();
  const fromLobbySource = fromLobbySourceFromQuery || fromLobbySourceFromState;
  const lobbyCreatePreferences = useMemo(() => {
    const state =
      location.state && typeof location.state === "object" && !Array.isArray(location.state)
        ? location.state
        : null;
    const statePreferences =
      state?.lobbyCreatePreferences &&
      typeof state.lobbyCreatePreferences === "object" &&
      !Array.isArray(state.lobbyCreatePreferences)
        ? state.lobbyCreatePreferences
        : null;
    const hasQueryPreferences =
      searchParams.has("lobbyCreateMode") ||
      searchParams.has("lobbyCreatePlayers") ||
      searchParams.has("lobbyCreateTime") ||
      searchParams.has("lobbyCreateDifficulty");

    if (!isFromLobbyCreateMatch && !statePreferences && !hasQueryPreferences) return null;

    const queryAutoCreate = String(searchParams.get("lobbyCreateAuto") || "")
      .trim()
      .toLowerCase();
    const stateAutoCreate = String(state?.lobbyCreateAuto || "")
      .trim()
      .toLowerCase();
    const playType = normalizeStoredPlayType(
      searchParams.get("lobbyCreatePlayType") || statePreferences?.playType || "online"
    );
    const mode = normalizeStoredModeForPlayType(
      playType,
      searchParams.get("lobbyCreateMode") || statePreferences?.mode || "dice"
    );
    const memberSize = normalizeStoredMemberSize(
      playType,
      searchParams.get("lobbyCreatePlayers") || statePreferences?.memberSize,
      canUseExtendedPlayerOptions
    );
    const havingTime = normalizeStoredTurnTime(
      searchParams.get("lobbyCreateTime") || statePreferences?.havingTime
    );
    const botDifficulty = normalizeBotDifficulty(
      searchParams.get("lobbyCreateDifficulty") || statePreferences?.botDifficulty
    );
    const autoCreate =
      statePreferences?.autoCreate === true ||
      ["1", "true", "yes"].includes(queryAutoCreate) ||
      ["1", "true", "yes"].includes(stateAutoCreate);
    const requestKey =
      String(
        searchParams.get("lobbyCreateRequestKey") ||
          state?.openMatchmakingQuickViewRequestKey ||
          statePreferences?.requestKey ||
          `create-game-${playType}-${mode}-${memberSize}-${havingTime}-${botDifficulty}`
      ).trim();

    return {
      playType,
      mode,
      memberSize,
      havingTime,
      botDifficulty,
      autoCreate,
      requestKey,
    };
  }, [
    canUseExtendedPlayerOptions,
    isFromLobbyCreateMatch,
    location.state,
    searchParams,
  ]);
  const lobbyTournamentModeFilter = useMemo(() => {
    const queryTournamentMode = String(searchParams.get("tournamentMode") || "").trim();
    const state = location.state;
    const stateTournamentMode =
      state && typeof state === "object" && !Array.isArray(state)
        ? String(state.tournamentMode || "").trim()
        : "";
    const rawTournamentMode = queryTournamentMode || stateTournamentMode;
    if (!rawTournamentMode) return "";
    const normalizedTournamentModeAlias = normalizeModeAlias(rawTournamentMode, "");
    if (!normalizedTournamentModeAlias) return "";
    const resolvedTournamentColumn =
      resolvePuzzleColumnMode(normalizedTournamentModeAlias);
    return PUZZLE_MODE_COLUMNS.some((column) => column.mode === resolvedTournamentColumn)
      ? resolvedTournamentColumn
      : "";
  }, [location.state, searchParams]);
  const useDesktopLayoutForPhoneLandscape = isPhoneLandscapeDesktopViewport();
  const usePhoneQuickViewButtonStack =
    !useDesktopLayoutForPhoneLandscape &&
    (isSamsungPortraitMobile ||
      isIphonePortraitMobile ||
      isPhoneLandscapeViewport ||
      isIphoneChromeLandscapePhone ||
      isSamsungChromeLandscapePhone);
  const replayPhoneClassActive = hasPhoneBodyClass();
  const replayCoarsePointer = mediaQueryMatches(REPLAY_TOUCH_POINTER_QUERY);
  const replayPortraitQueryMatch = mediaQueryMatches(REPLAY_PHONE_PORTRAIT_QUERY);
  const replayLandscapeQueryMatch = mediaQueryMatches(REPLAY_PHONE_LANDSCAPE_QUERY);
  const isReplayPortraitPhoneViewport =
    replayPhoneClassActive &&
    replayCoarsePointer &&
    replayPortraitQueryMatch &&
    !replayLandscapeQueryMatch;
  const isReplayLandscapePhoneViewport =
    replayPhoneClassActive &&
    replayCoarsePointer &&
    replayLandscapeQueryMatch &&
    !useDesktopLayoutForPhoneLandscape &&
    (isPhoneLandscapeViewport ||
      isIphoneChromeLandscapePhone ||
      isSamsungChromeLandscapePhone ||
      mediaQueryMatches(LANDSCAPE_PHONE_LAYOUT_QUERY));
  const isReplayMobileViewport =
    isReplayPortraitPhoneViewport || isReplayLandscapePhoneViewport;
  const hideLobbyCreateMobilePanels =
    (usePhoneQuickViewButtonStack || isReplayMobileViewport) &&
    isFromLobbyCreateMatch;
  const hideMobileCreateGameGlobalChat =
    (usePhoneQuickViewButtonStack || isReplayMobileViewport) &&
    matchmakingQuickView === "independientes";
  const showLobbyBlockEditPanels = ["1", "true", "yes"].includes(
    String(searchParams.get("showBlockPanels") || "")
      .trim()
      .toLowerCase()
  );
  const isFromLobbyCubeButton = ["1", "true", "yes"].includes(
    String(searchParams.get("fromLobbyCubeButton") || "")
      .trim()
      .toLowerCase()
  );
  const hideLobbyCubeStartPanel = isFromLobbyCubeButton;
  const showOnlyLobbyCustomizationPanel =
    showLobbyBlockEditPanels &&
    ["1", "true", "yes"].includes(
      String(searchParams.get("onlyCustomizationPanel") || "")
        .trim()
        .toLowerCase()
    );
  const isLobbyPuzzleModeRoute =
    isFromLobbyPuzzle &&
    (String(searchParams.get("quickView") || "").trim().toLowerCase() === "puzzles" ||
      matchmakingQuickView === "puzzles");
  const isLobbyTournamentModeRoute =
    isFromLobbyTournament &&
    (String(searchParams.get("quickView") || "").trim().toLowerCase() === "torneos" ||
      matchmakingQuickView === "torneos");
  const isLobbyFocusedModeRoute = isLobbyPuzzleModeRoute || isLobbyTournamentModeRoute;
  const effectiveMatchmakingQuickView = isLobbyPuzzleModeRoute
    ? "puzzles"
    : isLobbyTournamentModeRoute
      ? "torneos"
      : matchmakingQuickView;
  const isDailyPuzzleDashboardView = effectiveMatchmakingQuickView === "daily-puzzles";
  const isFindMatchesDashboardView = effectiveMatchmakingQuickView === "buscar-partidos";
  const forceShowProfilePanelFromLobbyWallet = ["1", "true", "yes"].includes(
    String(searchParams.get("showProfilePanel") || "")
      .trim()
      .toLowerCase()
  ) || showLobbyBlockEditPanels;
  const hideLobbyCreateRegisteredPlayerCard =
    showLobbyBlockEditPanels &&
    !showOnlyLobbyCustomizationPanel &&
    (usePhoneQuickViewButtonStack || isReplayMobileViewport) &&
    matchmakingQuickView === "independientes";
  const hidePortraitCreateGameProfilePanel =
    !forceShowProfilePanelFromLobbyWallet &&
    (isSamsungPortraitMobile || isIphonePortraitMobile || isReplayPortraitPhoneViewport) &&
    matchmakingQuickView === "independientes";
  const getLocalizedTournamentName = useCallback(
    (tournamentName, fallback = "Torneo") => {
      const localized = localizeTournamentName(language, tournamentName);
      return String(localized || "").trim() || fallback;
    },
    [language]
  );
  const currentUserProfileIdentitySet = useMemo(() => {
    return buildCurrentUserProfileIdentitySet(user);
  }, [
    user?._id,
    user?.email,
    user?.id,
    user?.loginIdentifier,
    user?.playerCode,
    user?.userId,
    user?.userName,
    user?.username,
  ]);
  const openPlayerProfile = useCallback(
    (rawUser) => {
      const route = resolvePlayerProfileRoute(rawUser, currentUserProfileIdentitySet);
      if (!route) return false;
      navigate(route);
      return true;
    },
    [
      currentUserProfileIdentitySet,
      navigate,
    ]
  );

  const activeTab = useMemo(() => {
    const forced = String(forcedTab || "")
      .trim()
      .toLowerCase();
    if (forced === "admin" || forced === "superusers") return "admin";
    if (forced === "news") return "news";
    if (forced === "history") return "history";

    if (location.pathname === "/game/history") return "history";

    const tab = String(new URLSearchParams(location.search).get("tab") || "")
      .trim()
      .toLowerCase();
    if (tab === "admin" || tab === "superusers") return "admin";
    if (tab === "news") return "news";
    if (tab === "history") return "history";
    return "lobby";
  }, [forcedTab, location.pathname, location.search]);

  const isNewsTab = activeTab === "news";
  const isAdminTab = activeTab === "admin";
  const isHistoryTab = activeTab === "history";
  const isLobbyModeFocusedView =
    isFromLobbyPuzzle ||
    isFromLobbyTournament ||
    isFromLobbyCreateMatch ||
    Boolean(fromLobbySource);
  const lobbyModePanelTitle = isFromLobbyPuzzle
    ? i18nT("mobile.modePuzzle")
    : isFromLobbyTournament
      ? i18nT("mobile.modeTournament")
      : isFromLobbyCreateMatch
        ? i18nT("mobile.modeIndependentMatches")
        : fromLobbySource
          ? i18nT(LOBBY_SOURCE_MODE_PANEL_TITLE_KEY_MAP[fromLobbySource] || "mobile.modeGeneric", {
              source: fromLobbySource.toUpperCase(),
            })
        : "";
  const showLobbyModePanel =
    isLobbyModeFocusedView &&
    !isNewsTab &&
    !isAdminTab &&
    !isHistoryTab;
  const shouldEmbedLobbyDesignEditor =
    hideLobbyCubeStartPanel &&
    showLobbyModePanel &&
    !usePhoneQuickViewButtonStack &&
    !isReplayMobileViewport &&
    !isPhoneLandscapeViewport &&
    !isSamsungPortraitMobile &&
    !isIphonePortraitMobile;
  const showCreateLobbyModePanelInMobileVideo =
    showLobbyModePanel &&
    isFromLobbyCreateMatch &&
    (usePhoneQuickViewButtonStack || isReplayMobileViewport);
  const lobbyModePanelReturn = showLobbyModePanel ? (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#1f6fa8]/70 bg-[#06172c]/90 px-4 py-3 shadow-[0_12px_34px_rgba(0,0,0,0.35)]">
      <div>
        <div className="text-xs font-black uppercase tracking-[0.28em] text-[#9de8ff]">{i18nT("lobby.loby")}</div>
        <div className="text-2xl font-black uppercase tracking-[0.14em] text-white">
          {lobbyModePanelTitle}
        </div>
      </div>
    </div>
  ) : null;
  const hideTournamentSelectionAndFriendsInPortrait =
    !isNewsTab &&
    !isAdminTab &&
    !isHistoryTab &&
    matchmakingQuickView === "torneos" &&
    (isSamsungPortraitMobile || isIphonePortraitMobile);
  const hideOnlineFriendsPanelOnPhoneMatchmaking =
    usePhoneQuickViewButtonStack &&
    (matchmakingQuickView === "torneos" || matchmakingQuickView === "independientes");
  const hidePuzzleAuxPanelsInPortrait =
    matchmakingQuickView === "puzzles" &&
    (isSamsungPortraitMobile || isIphonePortraitMobile);
  const showLegacyMobileHome =
    (isSamsungPortraitMobile || isIphonePortraitMobile || isReplayPortraitPhoneViewport) &&
    !isNewsTab &&
    !isAdminTab &&
    !isHistoryTab &&
    !showOnlyLobbyCustomizationPanel &&
    !hideLobbyCubeStartPanel &&
    !isLobbyFocusedModeRoute;
  const puzzleModeFilter = useMemo(() => {
    const queryPuzzleMode = String(searchParams.get("puzzleMode") || "").trim();
    const state = location.state;
    const statePuzzleMode =
      state && typeof state === "object" && !Array.isArray(state)
        ? String(state.puzzleMode || "").trim()
        : "";
    const rawPuzzleMode = queryPuzzleMode || statePuzzleMode;
    if (!rawPuzzleMode) return "";
    const normalizedPuzzleModeAlias = normalizeModeAlias(rawPuzzleMode, "");
    if (!normalizedPuzzleModeAlias) return "";
    const resolvedPuzzleColumn = resolvePuzzleColumnMode(normalizedPuzzleModeAlias);
    return PUZZLE_MODE_COLUMNS.some((column) => column.mode === resolvedPuzzleColumn)
      ? resolvedPuzzleColumn
      : "";
  }, [location.state, searchParams]);
  const hideNewsSelectionPanelInPortrait =
    isNewsTab && (isSamsungPortraitMobile || isIphonePortraitMobile);
  const modeWinnersSections = MODE_WINNERS_SECTION_ORDER.map((section) => ({
    key: section.key,
    title: t(section.titleKey),
    players: ensureArray(modeWinnersByMode[section.key]),
  }));
  const findMatchesModeWinnersSections = useMemo(() => {
    const selectedMode =
      findMatchesModeFilter === "all"
        ? "all"
        : resolveCimaxModeFieldFromMode(findMatchesModeFilter, "free");
    if (selectedMode === "all") return modeWinnersSections;
    return modeWinnersSections.filter((section) => section.key === selectedMode);
  }, [findMatchesModeFilter, modeWinnersSections]);
  const waitingRoomOpenRequest = useMemo(() => {
    const state = location.state;
    const stateObject =
      state && typeof state === "object" && !Array.isArray(state) ? state : null;
    const queryTournamentId = String(searchParams.get("openTournamentWaitingId") || "").trim();
    const tournamentId = String(stateObject?.openTournamentWaitingId || queryTournamentId || "").trim();
    if (!tournamentId) return null;
    const requestKey =
      String(
        stateObject?.openTournamentWaitingRequestKey ||
          searchParams.get("openTournamentWaitingRequestKey") ||
          ""
      ).trim() || tournamentId;
    const autoPlay =
      stateObject?.openTournamentWaitingAutoPlay === true ||
      ["1", "true", "yes"].includes(
        String(searchParams.get("openTournamentWaitingAutoPlay") || "")
          .trim()
          .toLowerCase()
      );
    const rawRecord =
      stateObject?.openTournamentWaitingRecord &&
      typeof stateObject.openTournamentWaitingRecord === "object" &&
      !Array.isArray(stateObject.openTournamentWaitingRecord)
        ? stateObject.openTournamentWaitingRecord
        : null;
    const stateSequenceNumber =
      normalizePublicSequenceNumberValue(stateObject?.tournamentUiSequenceNumber) ||
      normalizePublicSequenceNumberValue(stateObject?.tournamentSequenceNumber);
    const record =
      rawRecord && stateSequenceNumber > 0
        ? {
            ...rawRecord,
            tournamentUiSequenceNumber: stateSequenceNumber,
            tournamentSequenceNumber: stateSequenceNumber,
            tournamentNumber: stateSequenceNumber,
            displayNumber: stateSequenceNumber,
          }
        : rawRecord;
    const rawReturnTo =
      stateObject?.openTournamentWaitingReturnTo ||
      String(searchParams.get("openTournamentWaitingReturnTo") || "");
    const returnTo = rawReturnTo &&
      String(rawReturnTo).trim().startsWith("/") &&
      !String(rawReturnTo).trim().startsWith("//")
        ? String(rawReturnTo).trim()
        : "";
    return { tournamentId, requestKey, autoPlay, record, returnTo };
  }, [location.state, searchParams]);
  const matchmakingViewOpenRequest = useMemo(() => {
    const state = location.state;
    const searchView = String(searchParams.get("quickView") || "")
      .trim()
      .toLowerCase();
    const stateView = typeof state === "object" && !Array.isArray(state)
      ? String(state?.openMatchmakingQuickView || "")
          .trim()
          .toLowerCase()
      : "";
    const view = searchView || stateView;
      if (!["torneos", "independientes", "puzzles", "daily-puzzles", "buscar-partidos"].includes(view)) return null;
    const queryRequestKey = String(
      searchParams.get("lobbyCreateRequestKey") ||
        searchParams.get("openMatchmakingQuickViewRequestKey") ||
        ""
    ).trim();
    const requestKey =
      searchView
        ? queryRequestKey || `query-${view}`
        : String(state?.openMatchmakingQuickViewRequestKey || "").trim() || view;
    return { view, requestKey };
  }, [location.state, searchParams]);
  const shouldHideLobbyForWaitingRoomOpenRequest = Boolean(waitingRoomOpenRequest);
  const puzzleOpenRequest = useMemo(() => {
    const state =
      location.state && typeof location.state === "object" && !Array.isArray(location.state)
        ? location.state
        : {};
    const puzzleId = String(
      state.openPuzzleId || searchParams.get("openPuzzleId") || ""
    ).trim();
    const puzzleOrder = normalizePuzzleSequenceOrder(
      state.openPuzzleOrder || searchParams.get("openPuzzleOrder")
    );
    if (!puzzleId && !puzzleOrder) return null;
    const puzzleMode = String(
      state.openPuzzleMode ||
        searchParams.get("openPuzzleMode") ||
        state.puzzleMode ||
        searchParams.get("puzzleMode") ||
        ""
    ).trim();
    const requestKey =
      String(
        state.openPuzzleRequestKey || searchParams.get("openPuzzleRequestKey") || ""
      ).trim() || puzzleId || `order-${puzzleMode || "all"}-${puzzleOrder}`;
    const tutorialLevel = Number(
      state.tutorialLevel || searchParams.get("tutorialLevel") || 0
    );
    return { puzzleId, requestKey, puzzleOrder, puzzleMode, tutorialLevel };
  }, [location.state, searchParams]);
  const nextPuzzleOpenRequest = useMemo(() => {
    const state = location.state;
    if (!state || typeof state !== "object" || Array.isArray(state)) return null;
    const currentPuzzleId = String(state.openNextPuzzleFromId || "").trim();
    if (!currentPuzzleId) return null;
    const requestKey = String(state.openNextPuzzleRequestKey || "").trim() || currentPuzzleId;
    return { currentPuzzleId, requestKey };
  }, [location.state]);
  const retryPuzzleOpenRequest = useMemo(() => {
    const state = location.state;
    if (!state || typeof state !== "object" || Array.isArray(state)) return null;
    const currentPuzzleId = String(state.openRetryPuzzleFromId || "").trim();
    if (!currentPuzzleId) return null;
    const requestKey = String(state.openRetryPuzzleRequestKey || "").trim() || currentPuzzleId;
    return { currentPuzzleId, requestKey };
  }, [location.state]);
  // La lista de puzzles vive ahora en /game/puzzles (diseño Clásico con vista previa 2D).
  // Solo se queda aquí si se abre o encadena un puzzle, o con ?legacy=1 (editar puzzles, admin).
  useEffect(() => {
    if (effectiveMatchmakingQuickView !== "puzzles" && effectiveMatchmakingQuickView !== "daily-puzzles") return;
    if (searchParams.get("legacy") === "1") return;
    if (puzzleOpenRequest || nextPuzzleOpenRequest || retryPuzzleOpenRequest) return;
    navigate("/game/puzzles", { replace: true });
  }, [
    effectiveMatchmakingQuickView,
    navigate,
    nextPuzzleOpenRequest,
    puzzleOpenRequest,
    retryPuzzleOpenRequest,
    searchParams,
  ]);
  const replayOpenRequest = useMemo(() => {
    const state = location.state;
    if (!state || typeof state !== "object" || Array.isArray(state)) return null;
    const rawMatch =
      state.openReplayMatch && typeof state.openReplayMatch === "object"
        ? state.openReplayMatch
        : null;
    if (!rawMatch) return null;
    const matchId = String(rawMatch.id || rawMatch.historyId || "").trim();
    if (!matchId) return null;
    const requestKey = String(state.openReplayRequestKey || "").trim() || matchId;
    return {
      requestKey,
      match: {
        ...rawMatch,
        id: matchId,
      },
    };
  }, [location.state]);
  const shareLinkOpenRequest = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    const shareType = String(params.get("shareType") || "")
      .trim()
      .toLowerCase();
    if (!shareType) return null;
    if (!["tournament", "independent", "puzzle"].includes(shareType)) return null;

    const tournamentId = String(params.get("tournamentId") || "").trim();
    const puzzleId = String(params.get("puzzleId") || "").trim();

    if (shareType === "puzzle" && !puzzleId) return null;
    if ((shareType === "tournament" || shareType === "independent") && !tournamentId) return null;

    const requestKey =
      String(params.get("shareKey") || "").trim() ||
      `${shareType}:${shareType === "puzzle" ? puzzleId : tournamentId}`;

    return {
      shareType,
      tournamentId,
      puzzleId,
      requestKey,
    };
  }, [location.search]);

  const displayName = useMemo(() => {
    const profileFirst = extractPrimaryName(profileFirstName);
    if (profileFirst) return profileFirst;

    const firstName = extractPrimaryName(user?.firstName);
    if (firstName) return firstName;

    const username = extractPrimaryName(user?.username || user?.userName);
    if (username) return username;

    const profileName = extractPrimaryName(user?.displayName);
    if (profileName) return profileName;

    const email = extractPrimaryName(user?.email);
    if (email) return email;

    return "Player";
  }, [
    profileFirstName,
    user?.displayName,
    user?.email,
    user?.firstName,
    user?.userName,
    user?.username,
  ]);
  const currentUserEmailNormalized = useMemo(
    () =>
      String(user?.email || "")
        .trim()
        .toLowerCase(),
    [user?.email]
  );
  const currentUserLoginNameNormalized = useMemo(() => {
    const loginIdentifier = String(user?.loginIdentifier || "")
      .trim()
      .toLowerCase();
    if (loginIdentifier) return loginIdentifier;

    const tokenUsername = String(user?.username || user?.userName || "")
      .trim()
      .toLowerCase();
    if (tokenUsername) return tokenUsername;

    const firstName = String(user?.firstName || "")
      .trim()
      .toLowerCase();
    if (firstName) return firstName;

    const email = String(user?.email || "")
      .trim()
      .toLowerCase();
    if (!email) return "";
    if (email.includes("@")) return email.split("@")[0];
    return email;
  }, [user?.email, user?.firstName, user?.loginIdentifier, user?.userName, user?.username]);
  const isCurrentUserGuest = useMemo(
    () =>
      Boolean(user?.isGuest) ||
      isGuestProfileId(currentUserLoginNameNormalized) ||
      isGuestProfileId(currentUserEmailNormalized),
    [currentUserEmailNormalized, currentUserLoginNameNormalized, user?.isGuest]
  );
  const userRoleCandidates = useMemo(
    () =>
      [
        user?.role,
        user?.type,
        user?.permission,
        user?.accountType,
        user?.accessLevel,
      ]
        .map((value) => String(value || "").trim().toLowerCase())
        .filter(Boolean),
    [user?.accessLevel, user?.accountType, user?.permission, user?.role, user?.type]
  );
  const hasDirectAdminAccess = useMemo(
    () =>
      hasAdminMenuAccessFromSidebar ||
      userRoleCandidates.some((value) => value === "admin" || value === "superuser"),
    [hasAdminMenuAccessFromSidebar, userRoleCandidates]
  );
  const hasSuperuserRole = useMemo(
    () => userRoleCandidates.some((value) => value === "superuser"),
    [userRoleCandidates]
  );
  const hasDirectSuperuserAccess = useMemo(
    () => hasDirectAdminAccess,
    [hasDirectAdminAccess]
  );
  const canDeleteTournamentFromLivePanel = useMemo(() => {
    if (!hasDirectAdminAccess) return false;
    if (hasSuperuserRole) return true;
    return Boolean(isAdminTab && adminEnabled);
  }, [adminEnabled, hasDirectAdminAccess, hasSuperuserRole, isAdminTab]);
  const resetPuzzleEditDrafts = useCallback(() => {
    setPuzzleEditModalOpen(false);
    setPuzzleEditSaving(false);
    setPuzzleEditTargetId("");
    setPuzzleEditTargetLabel("");
    setPuzzleEditNameDraft("");
    setPuzzleEditTurnLimitDraft(DEFAULT_PUZZLE_TURN_LIMIT);
    setPuzzleEditRewardValueDraft(PUZZLE_DEFAULT_REWARD_VALUE);
    setPuzzleEditSolveTimeLimitEnabledDraft(false);
    setPuzzleEditSolveTimeLimitMinutesDraft(DEFAULT_PUZZLE_SOLVE_TIME_MINUTES);
    setPuzzleEditResetRewardProgressDraft(false);
  }, []);
  useEffect(() => {
    if (hasDirectSuperuserAccess) return;
    setPuzzleEditModeEnabled(false);
    resetPuzzleEditDrafts();
  }, [hasDirectSuperuserAccess, resetPuzzleEditDrafts]);
  const closePuzzleEditModal = useCallback(() => {
    if (puzzleEditSaving) return;
    resetPuzzleEditDrafts();
  }, [puzzleEditSaving, resetPuzzleEditDrafts]);
  const handleTogglePuzzleEditMode = useCallback(() => {
    if (!hasDirectSuperuserAccess) {
      notification.warning({
        message: i18nT("lobby.soloSuperusuarios"),
        description: i18nT("lobby.laEdicionDePuzzlesSoloEstaDisponiblePara"),
      });
      return;
    }
    setPuzzleEditModeEnabled((current) => {
      const next = !current;
      if (next) {
        notification.info({
          message: i18nT("lobby.modoEdicionActivado"),
          description:
            i18nT("lobby.presionaLaImagenDelPuzzleQueDeseasEditar"),
        });
      } else {
        resetPuzzleEditDrafts();
      }
      return next;
    });
  }, [hasDirectSuperuserAccess, resetPuzzleEditDrafts]);
  const openPuzzleEditModal = useCallback(
    (puzzleRecord) => {
      if (!hasDirectSuperuserAccess || !puzzleEditModeEnabled) return;
      const puzzleId = String(
        puzzleRecord?.id || puzzleRecord?._id || puzzleRecord?.tournamentId || ""
      ).trim();
      if (!puzzleId) return;

      const resolvedTurnLimit = Math.max(
        1,
        normalizePuzzleTurnLimit(puzzleRecord?.puzzleTurnLimit, DEFAULT_PUZZLE_TURN_LIMIT)
      );
      const resolvedRewardValue = resolvePuzzleRewardValue(puzzleRecord);
      const solvedTimeLimitSeconds = resolvePuzzleSolveTimeLimitSecondsFromRecord(puzzleRecord);
      const solvedTimeLimitEnabled = solvedTimeLimitSeconds > 0;
      const solvedTimeLimitMinutes = solvedTimeLimitEnabled
        ? Math.max(
            1,
            Math.min(
              MAX_PUZZLE_SOLVE_TIME_MINUTES,
              Math.ceil(Number(solvedTimeLimitSeconds || 0) / 60)
            )
          )
        : DEFAULT_PUZZLE_SOLVE_TIME_MINUTES;
      const fallbackPuzzleName = buildDefaultPuzzleNameFromRules(
        resolvedTurnLimit,
        solvedTimeLimitEnabled ? solvedTimeLimitMinutes * 60 : 0,
        language
      );
      const resolvedPuzzleName =
        String(puzzleRecord?.tournamentName || "").trim() || fallbackPuzzleName;

      setPuzzleEditTargetId(puzzleId);
      setPuzzleEditTargetLabel(getLocalizedTournamentName(puzzleRecord?.tournamentName, "Puzzle"));
      setPuzzleEditNameDraft(resolvedPuzzleName.slice(0, 120));
      setPuzzleEditTurnLimitDraft(resolvedTurnLimit);
      setPuzzleEditRewardValueDraft(resolvedRewardValue);
      setPuzzleEditSolveTimeLimitEnabledDraft(solvedTimeLimitEnabled);
      setPuzzleEditSolveTimeLimitMinutesDraft(solvedTimeLimitMinutes);
      setPuzzleEditResetRewardProgressDraft(false);
      setPuzzleEditModalOpen(true);
    },
    [getLocalizedTournamentName, hasDirectSuperuserAccess, language, puzzleEditModeEnabled]
  );
  const handleSavePuzzleEdit = useCallback(async () => {
    if (!hasDirectSuperuserAccess) {
      notification.warning({
        message: i18nT("lobby.soloSuperusuarios"),
        description: i18nT("lobby.laEdicionDePuzzlesSoloEstaDisponiblePara"),
      });
      return;
    }
    const targetPuzzleId = String(puzzleEditTargetId || "").trim();
    if (!targetPuzzleId) {
      notification.warning({
        message: i18nT("lobby.puzzleNoSeleccionado"),
        description: i18nT("lobby.seleccionaUnPuzzleDesdeSuImagenAntesDe"),
      });
      return;
    }

    const resolvedTurnLimit = Math.max(
      1,
      normalizePuzzleTurnLimit(puzzleEditTurnLimitDraft, DEFAULT_PUZZLE_TURN_LIMIT)
    );
    if (resolvedTurnLimit <= 0) {
      notification.warning({
        message: i18nT("lobby.turnosRequeridos"),
        description: i18nT("lobby.escribeUnaCantidadValidaDeMovimientosMinimo"),
      });
      return;
    }

    const resolvedRewardValue = normalizePuzzleRewardValue(
      puzzleEditRewardValueDraft,
      PUZZLE_DEFAULT_REWARD_VALUE
    );
    const resolvedSolveTimeLimitSeconds = puzzleEditSolveTimeLimitEnabledDraft
      ? normalizePuzzleSolveTimeLimitSeconds(
          Number(puzzleEditSolveTimeLimitMinutesDraft || 0) * 60,
          0
        )
      : 0;
    if (puzzleEditSolveTimeLimitEnabledDraft && resolvedSolveTimeLimitSeconds <= 0) {
      notification.warning({
        message: i18nT("lobby.tiempoRequerido"),
        description: i18nT("lobby.defineUnTiempoDeSolucionValidoEnMinutos"),
      });
      return;
    }
    const resolvedPuzzleName =
      String(puzzleEditNameDraft || "").trim().slice(0, 120) ||
      buildDefaultPuzzleNameFromRules(
        resolvedTurnLimit,
        resolvedSolveTimeLimitSeconds,
        language
      ).slice(0, 120);
    const shouldResetRewardProgress = puzzleEditResetRewardProgressDraft === true;

    try {
      setPuzzleEditSaving(true);
      const response = await axios.post("/tournaments/admin/puzzles/update", {
        puzzleId: targetPuzzleId,
        puzzleName: resolvedPuzzleName,
        puzzleTurnLimit: resolvedTurnLimit,
        puzzleRewardValue: resolvedRewardValue,
        puzzleSolveTimeLimitSeconds: resolvedSolveTimeLimitSeconds,
        resetRewardProgress: shouldResetRewardProgress,
      });

      const reloadPuzzles = fetchPuzzlesRef.current;
      if (typeof reloadPuzzles === "function") {
        await reloadPuzzles(true);
      }
      if (shouldResetRewardProgress) {
        setClaimedPuzzleRewardMap((previous) => {
          const previousMap =
            previous && typeof previous === "object" && !Array.isArray(previous) ? previous : {};
          const nextMap = { ...previousMap };
          delete nextMap[targetPuzzleId];
          setPuzzleRewardClaimMap(user?.email, nextMap);
          return nextMap;
        });
      }

      const resetSummary = response?.data?.rewardProgressResetUsers;
      const resetSuffix = shouldResetRewardProgress
        ? ` Progreso de recompensa restablecido en ${
            Math.max(0, Number(resetSummary?.modifiedUsers) || 0)
          } usuarios.`
        : "";
      notification.success({
        message: i18nT("lobby.puzzleActualizado"),
        description: `Cambios guardados en "${resolvedPuzzleName}".${resetSuffix}`,
      });
      resetPuzzleEditDrafts();
    } catch (error) {
      notification.error({
        message: i18nT("lobby.errorActualizandoPuzzle"),
        description:
          error?.response?.data?.message || error?.message || "No se pudo actualizar el puzzle.",
      });
    } finally {
      setPuzzleEditSaving(false);
    }
  }, [
    hasDirectSuperuserAccess,
    language,
    puzzleEditNameDraft,
    puzzleEditResetRewardProgressDraft,
    puzzleEditRewardValueDraft,
    puzzleEditSolveTimeLimitEnabledDraft,
    puzzleEditSolveTimeLimitMinutesDraft,
    puzzleEditTargetId,
    puzzleEditTurnLimitDraft,
    resetPuzzleEditDrafts,
    user?.email,
  ]);
  const resetTournamentEditDrafts = useCallback(() => {
    setTournamentEditModalOpen(false);
    setTournamentEditSaving(false);
    setTournamentEditTargetId("");
    setTournamentEditTargetLabel("");
    setTournamentEditNameDraft("");
    setTournamentEditStartDateDraft("");
    setTournamentEditStartTimeDraft("");
    setTournamentEditDurationDraft(1);
    setTournamentEditHavingTimeDraft(DEFAULT_TURN_TIME);
    setTournamentEditMemberSizeDraft(MEMBER_OPTIONS[0]);
    setTournamentEditRewardValueDraft(TOURNAMENT_DEFAULT_REWARD_VALUE);
    setTournamentEditWinnerPrizeValueDraft(TOURNAMENT_DEFAULT_REWARD_VALUE);
  }, []);
  useEffect(() => {
    if (hasDirectAdminAccess) return;
    setTournamentEditModeEnabled(false);
    resetTournamentEditDrafts();
  }, [hasDirectAdminAccess, resetTournamentEditDrafts]);
  const closeTournamentEditModal = useCallback(() => {
    if (tournamentEditSaving) return;
    resetTournamentEditDrafts();
  }, [resetTournamentEditDrafts, tournamentEditSaving]);
  const handleToggleTournamentEditMode = useCallback(() => {
    if (!hasDirectAdminAccess) {
      notification.warning({
        message: i18nT("lobby.soloAdministradores"),
        description: i18nT("lobby.laEdicionDeTorneosSoloEstaDisponiblePara"),
      });
      return;
    }
    setTournamentEditModeEnabled((current) => {
      const next = !current;
      if (next) {
        notification.info({
          message: i18nT("lobby.modoEdicionActivado"),
          description:
            i18nT("lobby.presionaLaImagenDelTorneoQueDeseasEditar"),
        });
      } else {
        resetTournamentEditDrafts();
      }
      return next;
    });
  }, [hasDirectAdminAccess, resetTournamentEditDrafts]);
  const openTournamentEditModal = useCallback(
    (tournamentRecord, options = {}) => {
      // options.force: el panel de admin abre el editor sin activar «Editar Torneo ON».
      if (!hasDirectAdminAccess || (!tournamentEditModeEnabled && !options?.force)) return;
      const tournamentId = String(
        tournamentRecord?._id || tournamentRecord?.id || tournamentRecord?.tournamentId || ""
      ).trim();
      if (!tournamentId) return;

      const startingMoment = moment(tournamentRecord?.startingTime);
      const safeStartingMoment = startingMoment.isValid()
        ? startingMoment
        : moment().add(1, "minute");
      const parsedHavingTime = Number(tournamentRecord?.havingTime);
      const resolvedHavingTime = TOURNAMENT_EDIT_HAVING_TIME_OPTIONS.includes(parsedHavingTime)
        ? parsedHavingTime
        : TIME_OPTIONS.includes(parsedHavingTime)
          ? parsedHavingTime
          : DEFAULT_TURN_TIME;
      const parsedMemberSize = Number(tournamentRecord?.memberSize);
      const resolvedMemberSize = MEMBER_OPTIONS.includes(parsedMemberSize)
        ? parsedMemberSize
        : MEMBER_OPTIONS[0];
      const resolvedDuration = Math.max(1, Math.floor(Number(tournamentRecord?.duration) || 1));
      const resolvedRewardValue = resolveTournamentRewardValue(tournamentRecord);
      const resolvedWinnerPrizeValue = resolveTournamentWinnerPrizeValue(tournamentRecord);
      const fallbackTournamentName = getLocalizedTournamentName(
        tournamentRecord?.tournamentName,
        "Torneo"
      );

      setTournamentEditTargetId(tournamentId);
      setTournamentEditTargetLabel(fallbackTournamentName);
      setTournamentEditNameDraft(
        String(tournamentRecord?.tournamentName || fallbackTournamentName)
          .trim()
          .slice(0, 120)
      );
      setTournamentEditStartDateDraft(safeStartingMoment.format("YYYY-MM-DD"));
      setTournamentEditStartTimeDraft(safeStartingMoment.format("HH:mm"));
      setTournamentEditDurationDraft(resolvedDuration);
      setTournamentEditHavingTimeDraft(resolvedHavingTime);
      setTournamentEditMemberSizeDraft(resolvedMemberSize);
      setTournamentEditRewardValueDraft(resolvedRewardValue);
      setTournamentEditWinnerPrizeValueDraft(resolvedWinnerPrizeValue);
      setTournamentEditModalOpen(true);
    },
    [getLocalizedTournamentName, hasDirectAdminAccess, tournamentEditModeEnabled]
  );
  const handleSaveTournamentEdit = useCallback(async () => {
    if (!hasDirectAdminAccess) {
      notification.warning({
        message: i18nT("lobby.soloAdministradores"),
        description: i18nT("lobby.laEdicionDeTorneosSoloEstaDisponiblePara"),
      });
      return;
    }
    const targetTournamentId = String(tournamentEditTargetId || "").trim();
    if (!targetTournamentId) {
      notification.warning({
        message: i18nT("lobby.torneoNoSeleccionado"),
        description: i18nT("lobby.seleccionaUnTorneoDesdeSuImagenAntesDe"),
      });
      return;
    }

    const resolvedDuration = Math.max(1, Math.floor(Number(tournamentEditDurationDraft) || 0));
    if (!Number.isFinite(resolvedDuration) || resolvedDuration < 1) {
      notification.warning({
        message: i18nT("lobby.duracionInvalida"),
        description: i18nT("lobby.laDuracionDelTorneoDebeSerMinimoDia"),
      });
      return;
    }

    const resolvedHavingTime = Number(tournamentEditHavingTimeDraft);
    if (!TOURNAMENT_EDIT_HAVING_TIME_OPTIONS.includes(resolvedHavingTime)) {
      notification.warning({
        message: i18nT("lobby.tiempoPorTurnoInvalido"),
        description: i18nT("lobby.seleccionaUnValorValidoOMinutos"),
      });
      return;
    }

    const resolvedMemberSize = Number(tournamentEditMemberSizeDraft);
    if (!MEMBER_OPTIONS.includes(resolvedMemberSize)) {
      notification.warning({
        message: i18nT("lobby.cantidadDeJugadoresInvalida"),
        description: i18nT("lobby.seleccionaUnaCantidadValidaDeJugadores"),
      });
      return;
    }

    const startDate = String(tournamentEditStartDateDraft || "").trim();
    const startTime = String(tournamentEditStartTimeDraft || "").trim();
    const resolvedStart = moment(`${startDate} ${startTime}`, "YYYY-MM-DD HH:mm", true);
    if (!resolvedStart.isValid()) {
      notification.warning({
        message: i18nT("lobby.fechaUHoraInvalida"),
        description: i18nT("lobby.defineUnaFechaYHoraDeInicioValidas"),
      });
      return;
    }

    const resolvedName =
      String(tournamentEditNameDraft || "").trim().slice(0, 120) ||
      getLocalizedTournamentName(tournamentEditTargetLabel, "Torneo").slice(0, 120);
    const resolvedRewardValue = normalizeTournamentRewardValue(
      tournamentEditRewardValueDraft,
      TOURNAMENT_DEFAULT_REWARD_VALUE
    );
    const resolvedWinnerPrizeValue = normalizeTournamentRewardValue(
      tournamentEditWinnerPrizeValueDraft,
      TOURNAMENT_DEFAULT_REWARD_VALUE
    );
    const adminPasswordPayload = String(adminPassword || "").trim();

    try {
      setTournamentEditSaving(true);
      await axios.post("/tournaments/admin/tournaments/update", {
        tournamentId: targetTournamentId,
        tournamentName: resolvedName,
        startingTime: resolvedStart.toISOString(),
        duration: resolvedDuration,
        havingTime: resolvedHavingTime,
        memberSize: resolvedMemberSize,
        tournamentRewardValue: resolvedRewardValue,
        tournamentWinnerPrizeValue: resolvedWinnerPrizeValue,
        password: adminPasswordPayload || undefined,
      });

      const reloadTournaments = fetchTournamentsRef.current;
      if (typeof reloadTournaments === "function") {
        await reloadTournaments();
      }
      const reloadLiveMatches = fetchLiveMatchesRef.current;
      if (typeof reloadLiveMatches === "function") {
        await reloadLiveMatches();
      }

      notification.success({
        message: i18nT("lobby.torneoActualizado"),
        description: `Cambios guardados en "${resolvedName}".`,
      });
      resetTournamentEditDrafts();
    } catch (error) {
      notification.error({
        message: i18nT("lobby.errorActualizandoTorneo"),
        description:
          error?.response?.data?.message || error?.message || "No se pudo actualizar el torneo.",
      });
    } finally {
      setTournamentEditSaving(false);
    }
  }, [
    adminPassword,
    getLocalizedTournamentName,
    hasDirectAdminAccess,
    resetTournamentEditDrafts,
    tournamentEditDurationDraft,
    tournamentEditHavingTimeDraft,
    tournamentEditMemberSizeDraft,
    tournamentEditNameDraft,
    tournamentEditWinnerPrizeValueDraft,
    tournamentEditRewardValueDraft,
    tournamentEditStartDateDraft,
    tournamentEditStartTimeDraft,
    tournamentEditTargetId,
    tournamentEditTargetLabel,
  ]);
  const allowedMemberOptions = useMemo(
    () => getAllowedMemberOptions(canUseExtendedPlayerOptions),
    [canUseExtendedPlayerOptions]
  );
  const canConfigureSuperTournamentDuration = useMemo(
    () =>
      Boolean(isAdminTab) &&
      Boolean(adminEnabled) &&
      Boolean(hasDirectAdminAccess) &&
      matchmakingQuickView === "torneos" &&
      (playType === "online" || playType === "freebox"),
    [adminEnabled, hasDirectAdminAccess, isAdminTab, matchmakingQuickView, playType]
  );
  const normalizedSuperTournamentMinutes = useMemo(() => {
    const parsed = Number(superTournamentMinutesInput);
    if (!Number.isFinite(parsed)) return 0;
    const floorValue = Math.floor(parsed);
    if (floorValue < SUPER_TOURNAMENT_MIN_MINUTES) return 0;
    return Math.min(SUPER_TOURNAMENT_MAX_MINUTES, floorValue);
  }, [superTournamentMinutesInput]);
  const getQuickTournamentRemainingMs = useCallback(
    (record) => {
      if (!record?.isQuickMatch) return 0;
      const expiresAtMs = resolveQuickMatchExpiresAtMs(record);
      if (!Number.isFinite(expiresAtMs) || expiresAtMs <= 0) return 0;
      return Math.max(0, expiresAtMs - nowMs);
    },
    [nowMs]
  );
  const hasAnyVisibleQuickTimers = useMemo(
    () =>
      ensureArray(tournaments).some((entry) => Boolean(entry?.isQuickMatch)) ||
      ensureArray(adminRooms).some((entry) => Boolean(entry?.isQuickMatch)),
    [adminRooms, tournaments]
  );

  const serverAvatarUrl = useMemo(
    () => resolveAvatarSrc(user?.avatar || user?.avatarUrl || user?.image || ""),
    [user]
  );

  const profileStorageKey = useMemo(() => getProfileStorageKey(user?.email), [user?.email]);
  const normalizedUserEmail = useMemo(
    () =>
      String(user?.email || "guest")
        .trim()
        .toLowerCase(),
    [user?.email]
  );
  const solvedPuzzleIdsSet = useMemo(
    () => getSolvedPuzzleIdsSet(user?.email),
    [user?.email]
  );
  useEffect(() => {
    setClaimedPuzzleRewardMap(getPuzzleRewardClaimMap(user?.email));
  }, [user?.email]);
  const startScreenCimaxButtons = useMemo(
    () =>
      START_SCREEN_CIMAX_BUTTONS.map((button) => ({
        ...button,
        score: cimaxScores?.[button.key] ?? DEFAULT_CIMAX_SCORES[button.key] ?? 0,
      })),
    [cimaxScores]
  );
  const handlePremiumUnavailableClick = useCallback(() => {
    navigate("/game/premium");
  }, [navigate]);
  const quickPlayPrefsStorageKey = useMemo(
    () => getQuickPlayPrefsStorageKey(user?.email),
    [user?.email]
  );
  const lastJoinedTournamentStorageKey = useMemo(
    () => getLastJoinedTournamentStorageKey(user?.email),
    [user?.email]
  );

  useEffect(() => {
    let cancelled = false;

    const loadSkinsCatalog = async () => {
      try {
        const response = await fetch(resolvePublicAssetPath("skins/manifest.json"), {
          cache: "no-store",
        });

        if (response.ok) {
          const data = await response.json();
          if (cancelled) return;

          const manifestCatalog = resolveSeparatedSkinCatalog(data);
          if (
            manifestCatalog.specialSkins.length > 0 ||
            manifestCatalog.commonSkins.length > 0
          ) {
            setSpecialSkinCatalog(manifestCatalog.specialSkins);
            setCommonSkinCatalog(manifestCatalog.commonSkins);
            return;
          }
        }
      } catch (error) {
        console.log(error);
      }

      try {
        const response = await axios.get("/tournaments/skins/catalog");
        if (cancelled) return;

        const apiCatalog = resolveSeparatedSkinCatalog(response?.data || {});
        setSpecialSkinCatalog(apiCatalog.specialSkins);
        setCommonSkinCatalog(apiCatalog.commonSkins);
      } catch (error) {
        console.log(error);
        if (!cancelled) {
          setSpecialSkinCatalog([]);
          setCommonSkinCatalog([]);
        }
      }
    };

    loadSkinsCatalog();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setProfileAvatar(String(serverAvatarUrl || "").trim());
  }, [serverAvatarUrl]);
  useEffect(() => {
    setLastJoinedTournamentId(getStoredLastJoinedTournamentId(lastJoinedTournamentStorageKey));
  }, [lastJoinedTournamentStorageKey]);

  useEffect(() => {
    quickPlayPrefsLoadedKeyRef.current = "";
    if (lobbyCreatePreferences) {
      const nextPlayType = lobbyCreatePreferences.playType;
      setPlayType(nextPlayType);
      setFormData((prev) => ({
        ...prev,
        mode: lobbyCreatePreferences.mode,
        memberSize: lobbyCreatePreferences.memberSize,
        havingTime: lobbyCreatePreferences.havingTime,
        botDifficulty: lobbyCreatePreferences.botDifficulty,
      }));
      setPanelProgress(
        normalizeStoredPanelProgress(nextPlayType, {
          playersSelected: true,
          modeSelected: true,
          timeSelected: true,
        })
      );
      setLocalPlayerNames(["", "", ""]);
      quickPlayPrefsLoadedKeyRef.current = quickPlayPrefsStorageKey;
      return;
    }

    const stored = parseStoredQuickPlayPrefs(localStorage.getItem(quickPlayPrefsStorageKey));

    if (!stored) {
      quickPlayPrefsLoadedKeyRef.current = quickPlayPrefsStorageKey;
      return;
    }

    const nextPlayType = normalizeStoredPlayType(stored.playType);
    const nextMode = normalizeStoredModeForPlayType(nextPlayType, stored.mode);
    const nextMemberSize = normalizeStoredMemberSize(
      nextPlayType,
      stored.memberSize,
      canUseExtendedPlayerOptions
    );
    const nextHavingTime = normalizeStoredTurnTime(stored.havingTime);
    const nextBotDifficulty = normalizeBotDifficulty(stored.botDifficulty);
    const nextPanelProgress = normalizeStoredPanelProgress(nextPlayType, stored.panelProgress);
    setPlayType(nextPlayType);
    setFormData((prev) => ({
      ...prev,
      mode: nextMode,
      memberSize: nextMemberSize,
      havingTime: nextHavingTime,
      botDifficulty: nextBotDifficulty,
    }));
    setPanelProgress(nextPanelProgress);
    // Los nombres locales no se restauran desde memoria para evitar que queden
    // como configuracion predeterminada en la siguiente partida local.
    setLocalPlayerNames(["", "", ""]);
    quickPlayPrefsLoadedKeyRef.current = quickPlayPrefsStorageKey;
  }, [canUseExtendedPlayerOptions, lobbyCreatePreferences, quickPlayPrefsStorageKey]);

  useEffect(() => {
    if (playType === "bot") return;

    const normalizedMemberSize = normalizeStoredMemberSize(
      playType,
      formData.memberSize,
      canUseExtendedPlayerOptions
    );
    if (Number(formData.memberSize) === normalizedMemberSize) return;

    setFormData((prev) => ({
      ...prev,
      memberSize: normalizedMemberSize,
    }));
  }, [canUseExtendedPlayerOptions, formData.memberSize, playType]);

  useEffect(() => {
    if (quickPlayPrefsLoadedKeyRef.current !== quickPlayPrefsStorageKey) return;

    const payload = {
      playType,
      mode: formData.mode,
      memberSize: normalizeStoredMemberSize(
        playType,
        formData.memberSize,
        canUseExtendedPlayerOptions
      ),
      havingTime: Number(formData.havingTime) || DEFAULT_TURN_TIME,
      botDifficulty: normalizeBotDifficulty(formData.botDifficulty),
      panelProgress: {
        playersSelected: panelProgress.playersSelected === true,
        modeSelected: panelProgress.modeSelected === true,
        timeSelected: panelProgress.timeSelected === true,
      },
      savedAt: new Date().toISOString(),
    };

    localStorage.setItem(quickPlayPrefsStorageKey, JSON.stringify(payload));
  }, [
    canUseExtendedPlayerOptions,
    formData.botDifficulty,
    formData.havingTime,
    formData.memberSize,
    formData.mode,
    panelProgress.modeSelected,
    panelProgress.playersSelected,
    panelProgress.timeSelected,
    playType,
    quickPlayPrefsStorageKey,
  ]);

  const currentAvatarUrl = profileAvatar || serverAvatarUrl;
  const currentTournamentPlayer = useMemo(() => {
    const firstName = String(profileFirstName || user?.firstName || "").trim();
    const lastName = String(profileLastName || user?.lastName || "").trim();
    const email = String(user?.email || "").trim();
    const username = String(user?.username || user?.userName || "").trim();
    const loginIdentifier = String(user?.loginIdentifier || username || "").trim();
    const storedUserId = String(user?._id || user?.id || user?.userId || "").trim();
    const gameIdentity = loginIdentifier || username || email || displayName;
    const avatarUrl = String(currentAvatarUrl || serverAvatarUrl || "").trim();
    const currentCimaxScores = normalizeCimaxScores(cimaxScores);

    return {
      _id: storedUserId || gameIdentity,
      id: gameIdentity || storedUserId,
      identity: gameIdentity || storedUserId,
      userId: gameIdentity || storedUserId,
      loginIdentifier,
      username,
      userName: username,
      email,
      firstName,
      lastName,
      name: displayName,
      displayName,
      avatarUrl,
      avatar: avatarUrl,
      cimaxScores: currentCimaxScores,
      hasCimaxData: true,
    };
  }, [
    cimaxScores,
    currentAvatarUrl,
    displayName,
    profileFirstName,
    profileLastName,
    serverAvatarUrl,
    user?._id,
    user?.email,
    user?.firstName,
    user?.id,
    user?.lastName,
    user?.loginIdentifier,
    user?.userId,
    user?.userName,
    user?.username,
  ]);
  const normalizeJoinedUiIdentity = useCallback((value) => {
    return String(value || "")
      .trim()
      .toLowerCase();
  }, []);
  const buildJoinedUiPlayer = useCallback(
    (incomingUser, index = 0) => {
      const fallbackId = `joined-${index}`;
      const rawUser = flattenNestedPlayerEntry(incomingUser);
      if (rawUser && typeof rawUser === "object") {
        const readText = (value) =>
          value && typeof value === "object"
            ? ""
            : isStringifiedObjectText(value)
            ? ""
            : String(value || "").trim();
        const email = readText(rawUser?.email);
        const loginIdentifier = readText(rawUser?.loginIdentifier);
        const username = readText(rawUser?.username || rawUser?.userName);
        const normalizedUserId = readText(rawUser?.userIdNormalized);
        const storedUserId =
          readText(rawUser?.userId) || readText(rawUser?._id) || readText(rawUser?.id);
        const userId = normalizedUserId || loginIdentifier || username || storedUserId;
        const hasRankingData = Object.prototype.hasOwnProperty.call(rawUser, "ranking");
        const hasStateData = Object.prototype.hasOwnProperty.call(rawUser, "state");
        const hasCimaxData =
          Object.prototype.hasOwnProperty.call(rawUser, "cimaxScores") ||
          Object.prototype.hasOwnProperty.call(rawUser, "cimaxMode") ||
          Object.prototype.hasOwnProperty.call(rawUser, "cimaxScore");
        const cimaxScores = hasCimaxData ? normalizeCimaxScores(rawUser?.cimaxScores) : null;
        const cimaxMode = String(rawUser?.cimaxMode || "")
          .trim()
          .toLowerCase();
        const explicitCimaxScore = resolveOptionalCimaxScore(rawUser?.cimaxScore);
        const cimaxScoreByMode =
          cimaxMode && cimaxScores
            ? resolveOptionalCimaxScore(cimaxScores?.[cimaxMode])
            : null;
        const cimaxScore = explicitCimaxScore ?? cimaxScoreByMode;
        const identitySource = loginIdentifier || username || normalizedUserId || storedUserId || email || fallbackId;
        const display = extractPrimaryName(
          rawUser?.firstName ||
            rawUser?.displayName ||
            rawUser?.name ||
            loginIdentifier ||
            username ||
            email ||
            userId ||
            fallbackId
        );
        const normalizedEmail = normalizeJoinedUiIdentity(email);
        const normalizedIdentity = normalizeJoinedUiIdentity(identitySource);
        const isCurrentUser =
          (normalizedIdentity && normalizedIdentity === currentUserLoginNameNormalized) ||
          (normalizedEmail && normalizedEmail === currentUserEmailNormalized);
        const avatarUrl =
          resolveAvatarSrc(rawUser?.avatarUrl || rawUser?.avatar || "") ||
          (isCurrentUser
            ? currentAvatarUrl
            : getStoredAvatarByEmail(normalizedEmail || normalizedIdentity));

        return {
          id: identitySource,
          identity: normalizedIdentity,
          name: display || `Jugador ${index + 1}`,
          displayName: display || `Jugador ${index + 1}`,
          userId: userId,
          email,
          loginIdentifier,
          username,
          userName: username,
          playerCode: String(rawUser?.playerCode || "").trim().toUpperCase(),
          secondary: String(rawUser?.playerCode || loginIdentifier || username || userId || email || "")
            .trim()
            .toUpperCase(),
          avatarUrl,
          ranking: Math.max(0, Number(rawUser?.ranking) || 0),
          hasRankingData,
          state: Math.max(0, Number(rawUser?.state) || 0),
          hasStateData,
          cimaxScores,
          cimaxMode,
          cimaxScore,
          hasCimaxData,
        };
      }

      const rawValue = String(rawUser || "").trim();
      if (!rawValue || isStringifiedObjectText(rawValue)) return null;
      const normalizedRawValue = normalizeJoinedUiIdentity(rawValue);
      const isCurrentUser =
        normalizedRawValue === currentUserLoginNameNormalized ||
        normalizedRawValue === currentUserEmailNormalized;
      return {
        id: rawValue,
        identity: normalizedRawValue,
        name: isCurrentUser ? displayName : rawValue,
        displayName: isCurrentUser ? displayName : rawValue,
        userId: rawValue,
        email: rawValue.includes("@") ? rawValue : "",
        loginIdentifier: rawValue,
        playerCode: "",
        secondary: rawValue.toUpperCase(),
        avatarUrl: isCurrentUser ? currentAvatarUrl : getStoredAvatarByEmail(normalizedRawValue),
        ranking: 0,
        hasRankingData: false,
        state: 0,
        hasStateData: false,
        cimaxScores: null,
        cimaxMode: "",
        cimaxScore: null,
        hasCimaxData: false,
      };
    },
    [
      currentAvatarUrl,
      currentUserEmailNormalized,
      currentUserLoginNameNormalized,
      displayName,
      normalizeJoinedUiIdentity,
    ]
  );
  const resolveJoinedUiPlayerIdentity = useCallback(
    (entry) => {
      if (!entry) return "";
      const loginIdentity = normalizeJoinedUiIdentity(entry?.loginIdentifier);
      if (loginIdentity) return loginIdentity;
      const usernameIdentity = normalizeJoinedUiIdentity(entry?.username || entry?.userName);
      if (usernameIdentity) return usernameIdentity;
      const userIdIdentity = normalizeJoinedUiIdentity(entry?.userIdNormalized || entry?.userId);
      if (userIdIdentity) return userIdIdentity;
      const explicitIdentity = normalizeJoinedUiIdentity(entry?.identity);
      if (explicitIdentity) return explicitIdentity;
      const idIdentity = normalizeJoinedUiIdentity(entry?.id);
      if (idIdentity) return idIdentity;
      const emailIdentity = normalizeJoinedUiIdentity(entry?.email);
      if (emailIdentity) return emailIdentity;
      return normalizeJoinedUiIdentity(entry?.name);
    },
    [normalizeJoinedUiIdentity]
  );
  const collectJoinedUiAliases = useCallback(
    (values = [], options = {}) => {
      const includeEmailLocalPart = options?.includeEmailLocalPart !== false;
      const aliases = [];
      const addAlias = (value) => {
        const normalized = normalizeJoinedUiIdentity(value);
        if (!normalized) return;
        aliases.push(normalized);
        if (includeEmailLocalPart && normalized.includes("@")) {
          const localPart = normalized.split("@")[0];
          if (localPart) aliases.push(localPart);
        }
      };

      ensureArray(values).forEach(addAlias);
      return Array.from(new Set(aliases));
    },
    [normalizeJoinedUiIdentity]
  );
  const resolveJoinedUiPlayerIdentityAliases = useCallback(
    (entry) => {
      if (!entry) return [];
      const rawAliases =
        typeof entry === "object"
          ? [
              entry?.identity,
              entry?.id,
              entry?.userId,
              entry?._id,
              entry?.email,
              entry?.loginIdentifier,
              entry?.username,
              entry?.userName,
              entry?.name,
              entry?.displayName,
              entry?.playerCode,
              entry?.secondary,
            ]
          : [entry];
      return collectJoinedUiAliases(rawAliases);
    },
    [collectJoinedUiAliases]
  );
  const resolveJoinedUiPlayerMergeAliases = useCallback(
    (entry) => {
      if (!entry) return [];
      const rawAliases =
        typeof entry === "object"
          ? [
              entry?.loginIdentifier,
              entry?.username,
              entry?.userName,
              entry?.userIdNormalized,
              entry?.userId,
              entry?.identity,
              entry?.id,
              entry?._id,
              entry?.email,
              entry?.playerCode,
            ]
          : [entry];
      return collectJoinedUiAliases(rawAliases);
    },
    [collectJoinedUiAliases]
  );
  const joinedUiPlayersShareIdentity = useCallback(
    (left, right) => {
      const leftAliases = resolveJoinedUiPlayerMergeAliases(left);
      const rightAliases = resolveJoinedUiPlayerMergeAliases(right);
      if (leftAliases.length === 0 || rightAliases.length === 0) return false;
      const rightSet = new Set(rightAliases);
      return leftAliases.some((alias) => rightSet.has(alias));
    },
    [resolveJoinedUiPlayerMergeAliases]
  );
  const mergeJoinedUiPlayer = useCallback((existing, candidate) => {
    if (!existing) return candidate;
    if (!candidate) return existing;

    const candidateHasRankingData = Boolean(candidate?.hasRankingData);
    const candidateHasStateData = Boolean(candidate?.hasStateData);
    const candidateHasCimaxData = Boolean(candidate?.hasCimaxData);
    const existingHasCimaxData = Boolean(existing?.hasCimaxData);
    const candidateCimaxScore = resolveOptionalCimaxScore(candidate?.cimaxScore);
    const existingCimaxScore = resolveOptionalCimaxScore(existing?.cimaxScore);

    return {
      ...existing,
      ...candidate,
      id: candidate?.id || existing?.id,
      identity: candidate?.identity || existing?.identity,
      name: candidate?.name || existing?.name,
      displayName: candidate?.displayName || existing?.displayName,
      userId: candidate?.userId || existing?.userId,
      email: candidate?.email || existing?.email,
      loginIdentifier: candidate?.loginIdentifier || existing?.loginIdentifier,
      playerCode: candidate?.playerCode || existing?.playerCode || "",
      secondary: candidate?.secondary || existing?.secondary || "",
      avatarUrl: candidate?.avatarUrl || existing?.avatarUrl || "",
      ranking: candidateHasRankingData
        ? Math.max(0, Number(candidate?.ranking) || 0)
        : Math.max(0, Number(existing?.ranking) || 0),
      hasRankingData: Boolean(existing?.hasRankingData || candidateHasRankingData),
      state: candidateHasStateData
        ? Math.max(0, Number(candidate?.state) || 0)
        : Math.max(0, Number(existing?.state) || 0),
      hasStateData: Boolean(existing?.hasStateData || candidateHasStateData),
      cimaxScores: candidateHasCimaxData
        ? candidate?.cimaxScores || null
        : existing?.cimaxScores || null,
      cimaxMode: candidateHasCimaxData
        ? candidate?.cimaxMode || ""
        : existing?.cimaxMode || "",
      cimaxScore: candidateHasCimaxData
        ? candidateCimaxScore
        : existingCimaxScore,
      hasCimaxData: Boolean(existingHasCimaxData || candidateHasCimaxData),
    };
  }, []);
  const mergeJoinedUiPlayerLists = useCallback(
    (basePlayers = [], incomingPlayers = []) => {
      const merged = ensureArray(basePlayers).filter(Boolean);
      ensureArray(incomingPlayers).forEach((candidate) => {
        const candidateIdentity = resolveJoinedUiPlayerIdentity(candidate);
        if (!candidateIdentity) return;
        const existingIndex = merged.findIndex((entry) => {
          const entryIdentity = resolveJoinedUiPlayerIdentity(entry);
          return (
            (entryIdentity && entryIdentity === candidateIdentity) ||
            joinedUiPlayersShareIdentity(entry, candidate)
          );
        });
        if (existingIndex < 0) {
          merged.push(candidate);
          return;
        }
        merged[existingIndex] = mergeJoinedUiPlayer(merged[existingIndex], candidate);
      });
      return merged;
    },
    [joinedUiPlayersShareIdentity, mergeJoinedUiPlayer, resolveJoinedUiPlayerIdentity]
  );
  const addJoinedUiPlayer = useCallback(
    (tournamentId, rawUser) => {
      const normalizedTournamentId = String(tournamentId || "").trim();
      if (!normalizedTournamentId) return;

      setJoinedUiPlayersByTournamentId((prev) => {
        const existing = ensureArray(prev[normalizedTournamentId]);
        const candidate = buildJoinedUiPlayer(rawUser, existing.length);
        if (!candidate) return prev;
        const candidateIdentity = resolveJoinedUiPlayerIdentity(candidate);
        if (!candidateIdentity) return prev;
        const alreadyExists = existing.some((entry) => {
          const entryIdentity = resolveJoinedUiPlayerIdentity(entry);
          return (
            (entryIdentity && entryIdentity === candidateIdentity) ||
            joinedUiPlayersShareIdentity(entry, candidate)
          );
        });
        if (alreadyExists) return prev;
        return {
          ...prev,
          [normalizedTournamentId]: [...existing, candidate],
        };
      });
    },
    [buildJoinedUiPlayer, joinedUiPlayersShareIdentity, resolveJoinedUiPlayerIdentity]
  );
  const removeJoinedUiPlayer = useCallback(
    (tournamentId, rawUser) => {
      const normalizedTournamentId = String(tournamentId || "").trim();
      if (!normalizedTournamentId) return;

      const candidate = buildJoinedUiPlayer(rawUser);
      const candidateIdentity = resolveJoinedUiPlayerIdentity(candidate);
      if (!candidateIdentity) return;

      setJoinedUiPlayersByTournamentId((prev) => {
        const existing = ensureArray(prev[normalizedTournamentId]);
        if (existing.length === 0) return prev;
        const next = existing.filter((entry) => {
          const entryIdentity = resolveJoinedUiPlayerIdentity(entry);
          return entryIdentity !== candidateIdentity && !joinedUiPlayersShareIdentity(entry, candidate);
        });
        if (next.length === existing.length) return prev;
        return {
          ...prev,
          [normalizedTournamentId]: next,
        };
      });
    },
    [buildJoinedUiPlayer, joinedUiPlayersShareIdentity, resolveJoinedUiPlayerIdentity]
  );
  const hydrateJoinedUiPlayers = useCallback(
    (tournamentId, rawUsers) => {
      const normalizedTournamentId = String(tournamentId || "").trim();
      if (!normalizedTournamentId) return;
      const incoming = ensureArray(rawUsers)
        .map((entry, index) => buildJoinedUiPlayer(entry, index))
        .filter(Boolean);
      if (incoming.length === 0) return;

      setJoinedUiPlayersByTournamentId((prev) => {
        const existing = ensureArray(prev[normalizedTournamentId]);
        const merged = mergeJoinedUiPlayerLists(existing, incoming);

        const changed =
          merged.length !== existing.length ||
          merged.some((entry, index) => entry !== existing[index]);
        if (!changed) return prev;
        return {
          ...prev,
          [normalizedTournamentId]: merged,
        };
      });
    },
    [buildJoinedUiPlayer, mergeJoinedUiPlayerLists]
  );
  const getJoinedUiPlayers = useCallback(
    (tournamentId, fallbackUsers = []) => {
      const normalizedTournamentId = String(tournamentId || "").trim();
      if (!normalizedTournamentId) return [];
      const localPlayers = ensureArray(joinedUiPlayersByTournamentId[normalizedTournamentId]);
      const fallbackPlayers = ensureArray(fallbackUsers)
        .map((entry, index) => buildJoinedUiPlayer(entry, index))
        .filter(Boolean);
      if (localPlayers.length === 0) return fallbackPlayers;
      if (fallbackPlayers.length === 0) return localPlayers;
      return mergeJoinedUiPlayerLists(localPlayers, fallbackPlayers);
    },
    [buildJoinedUiPlayer, joinedUiPlayersByTournamentId, mergeJoinedUiPlayerLists]
  );
  const buildJoinedUiPlayersFromRawUsers = useCallback(
    (rawUsers = []) => {
      const source = ensureArray(rawUsers);
      const normalized = [];
      source.forEach((entry, index) => {
        const candidate = buildJoinedUiPlayer(entry, index);
        if (!candidate) return;
        const identity = resolveJoinedUiPlayerIdentity(candidate);
        if (!identity) return;
        const exists = normalized.some(
          (current) =>
            resolveJoinedUiPlayerIdentity(current) === identity ||
            joinedUiPlayersShareIdentity(current, candidate)
        );
        if (!exists) normalized.push(candidate);
      });
      return normalized;
    },
    [buildJoinedUiPlayer, joinedUiPlayersShareIdentity, resolveJoinedUiPlayerIdentity]
  );
  const buildAvailableSkinPresets = useCallback(
    (catalogEntries = [], defaultPreset, fallbackPresets = []) => {
      const dedupePresets = (entries = []) => {
        const seen = new Set();
        return ensureArray(entries).filter((entry) => {
          const normalizedId = normalizeSkinId(entry?.id || "", "");
          if (!normalizedId || seen.has(normalizedId)) return false;
          seen.add(normalizedId);
          return true;
        });
      };
      if (Array.isArray(catalogEntries) && catalogEntries.length > 0) {
        const hasDefault = catalogEntries.some((entry) => entry.id === defaultPreset.id);
        if (hasDefault) return dedupePresets(catalogEntries);
        return dedupePresets([defaultPreset, ...catalogEntries]);
      }

      if (Array.isArray(fallbackPresets) && fallbackPresets.length > 0) {
        return dedupePresets(fallbackPresets);
      }
      return [defaultPreset];
    },
    []
  );
  const availableSpecialSkinPresets = useMemo(
    () => {
      const storeSpecialSkins = buildSkinCatalogEntriesFromStoreProducts(SPECIAL_BLOCK_PRODUCTS);
      const mergedCatalog = [...specialSkinCatalog];
      const seen = new Set(mergedCatalog.map((entry) => entry?.id).filter(Boolean));
      storeSpecialSkins.forEach((entry) => {
        if (!entry?.id || seen.has(entry.id)) return;
        seen.add(entry.id);
        mergedCatalog.push(entry);
      });
      return buildAvailableSkinPresets(mergedCatalog, getSpecialBlockPreset(DEFAULT_SPECIAL_BLOCK_ID), [
        ...Object.values(SPECIAL_BLOCK_PRESET_MAP),
      ]);
    },
    [buildAvailableSkinPresets, specialSkinCatalog]
  );
  const availableCommonSkinPresets = useMemo(
    () =>
      buildAvailableSkinPresets(
        commonSkinCatalog,
        getSpecialBlockPreset(DEFAULT_COMMON_BLOCK_THEME_ID),
        [getSpecialBlockPreset(DEFAULT_COMMON_BLOCK_THEME_ID)]
      ),
    [buildAvailableSkinPresets, commonSkinCatalog]
  );
  const resolveSkinPresetFromCatalog = useCallback(
    (catalogEntries, rawId, defaultPreset) => {
      const normalizedId = normalizeSkinId(rawId, "");
      const byId = catalogEntries.find((entry) => entry.id === normalizedId);
      if (byId) return byId;

      const byDefault = catalogEntries.find((entry) => entry.id === defaultPreset.id);
      if (byDefault) return byDefault;

      return catalogEntries[0] || defaultPreset;
    },
    []
  );
  const resolveSpecialSkinPreset = useCallback(
    (rawId) =>
      resolveSkinPresetFromCatalog(
        availableSpecialSkinPresets,
        rawId,
        getSpecialBlockPreset(DEFAULT_SPECIAL_BLOCK_ID)
      ),
    [availableSpecialSkinPresets, resolveSkinPresetFromCatalog]
  );
  const resolveCommonSkinPreset = useCallback(
    (rawId) =>
      resolveSkinPresetFromCatalog(
        availableCommonSkinPresets,
        rawId,
        getSpecialBlockPreset(DEFAULT_COMMON_BLOCK_THEME_ID)
      ),
    [availableCommonSkinPresets, resolveSkinPresetFromCatalog]
  );
  const selectedSpecialBlock = useMemo(
    () => resolveSpecialSkinPreset(selectedSpecialBlockId),
    [resolveSpecialSkinPreset, selectedSpecialBlockId]
  );
  const selectedBoardTheme = useMemo(
    () => getBoardThemePreset(selectedBoardThemeId),
    [selectedBoardThemeId]
  );
  const selectedCommonBlockTheme = useMemo(
    () => resolveCommonSkinPreset(selectedCommonBlockThemeId),
    [resolveCommonSkinPreset, selectedCommonBlockThemeId]
  );
  const ownedSpecialSkinProductIdSet = useMemo(
    () =>
      new Set(
        normalizeOwnedStoreProductIds(ownedSpecialSkinProductIds, BLOCK_TYPE_SPECIAL)
      ),
    [ownedSpecialSkinProductIds]
  );
  const ownedCommonSkinProductIdSet = useMemo(
    () =>
      new Set(
        normalizeOwnedStoreProductIds(ownedCommonSkinProductIds, BLOCK_TYPE_COMMON)
      ),
    [ownedCommonSkinProductIds]
  );
  const topWinnerModesFromLeaderboard = useMemo(() => {
    const currentCandidates = new Set(
      [currentUserEmailNormalized, currentUserLoginNameNormalized]
        .map((value) => String(value || "").trim().toLowerCase())
        .filter(Boolean)
    );
    if (currentCandidates.size === 0) return [];

    return MODE_WINNERS_SECTION_ORDER.map((section) => section.key).filter((modeKey) => {
      const topPlayer = ensureArray(modeWinnersByMode?.[modeKey])[0];
      const topPlayerId = String(topPlayer?.userId || "")
        .trim()
        .toLowerCase();
      return Boolean(topPlayerId) && currentCandidates.has(topPlayerId);
    });
  }, [currentUserEmailNormalized, currentUserLoginNameNormalized, modeWinnersByMode]);
  const isTopWinnerFromLeaderboard = topWinnerModesFromLeaderboard.length > 0;
  const topWinnerPrimaryModeFromLeaderboard = String(
    topWinnerModesFromLeaderboard[0] || ""
  )
    .trim()
    .toLowerCase();
  const topWinnerSpecialSkinId = useMemo(() => {
    const mappedSkinId = normalizeSkinId(
      TOP_WINNER_MODE_SKIN_BY_MODE[topWinnerPrimaryModeFromLeaderboard],
      ""
    );
    if (mappedSkinId) return mappedSkinId;
    return DEFAULT_SPECIAL_BLOCK_ID;
  }, [topWinnerPrimaryModeFromLeaderboard]);
  const canUseTopWinnerSkins = isTopWinnerSkinEligible || isTopWinnerFromLeaderboard;
  const topWinnerEligibleModeSet = useMemo(
    () =>
      new Set(
        [
          ...ensureArray(topWinnerSkinEligibleModes),
          ...ensureArray(topWinnerModesFromLeaderboard),
        ]
          .map((modeKey) => String(modeKey || "").trim().toLowerCase())
          .filter(Boolean)
      ),
    [topWinnerModesFromLeaderboard, topWinnerSkinEligibleModes]
  );
  const topWinnerEligibleSkinIdSet = useMemo(
    () =>
      new Set(
        Array.from(topWinnerEligibleModeSet)
          .map((modeKey) => normalizeSkinId(TOP_WINNER_MODE_SKIN_BY_MODE[modeKey], ""))
          .filter(Boolean)
      ),
    [topWinnerEligibleModeSet]
  );
  const topWinnerLogoSkinIdSet = useMemo(
    () =>
      new Set(
        Object.values(TOP_WINNER_MODE_SKIN_BY_MODE)
          .map((skinId) => normalizeSkinId(skinId, ""))
          .filter(Boolean)
      ),
    []
  );
  const isSkinUnlockedForBlockType = useCallback(
    (blockType, rawSkinId) => {
      const skinId = normalizeSkinId(rawSkinId, "");
      if (!skinId) return true;
      if (skinId === DEFAULT_SPECIAL_BLOCK_ID || skinId === DEFAULT_COMMON_BLOCK_THEME_ID) {
        return true;
      }

      const requiredProduct = resolveSkinStoreProductForSkin(blockType, skinId);
      if (!requiredProduct?.id) return true;
      const accessType =
        requiredProduct?.accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER
          ? STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER
          : STORE_PRODUCT_ACCESS_PURCHASE;
      if (accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER) {
        const rawRequiredMode = String(requiredProduct?.earnedMode || "").trim().toLowerCase();
        const requiredMode = Object.prototype.hasOwnProperty.call(
          TOP_WINNER_MODE_SKIN_BY_MODE,
          rawRequiredMode
        )
          ? rawRequiredMode
          : "";
        if (rawRequiredMode && !requiredMode) return false;
        if (requiredMode) return topWinnerEligibleModeSet.has(requiredMode);
        return canUseTopWinnerSkins;
      }
      if (accessType === STORE_PRODUCT_ACCESS_PURCHASE && hasDirectAdminAccess) {
        return true;
      }

      if (blockType === BLOCK_TYPE_COMMON) {
        return ownedCommonSkinProductIdSet.has(requiredProduct.id);
      }
      return ownedSpecialSkinProductIdSet.has(requiredProduct.id);
    },
    [
      canUseTopWinnerSkins,
      hasDirectAdminAccess,
      ownedCommonSkinProductIdSet,
      ownedSpecialSkinProductIdSet,
      topWinnerEligibleModeSet,
    ]
  );
  const resolveSkinAccessInfo = useCallback((blockType, preset) => {
    const requiredProduct = resolveSkinStoreProductForSkin(blockType, preset?.id);
    const accessType =
      requiredProduct?.accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER
        ? STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER
        : requiredProduct?.id
          ? STORE_PRODUCT_ACCESS_PURCHASE
          : "";
    const normalizedBlockType =
      blockType === BLOCK_TYPE_COMMON ? BLOCK_TYPE_COMMON : BLOCK_TYPE_SPECIAL;
    const resolvedProduct = requiredProduct?.id
      ? resolveSkinStoreProductById(requiredProduct.id, normalizedBlockType)
      : null;
    const unlocked = isSkinUnlockedForBlockType(blockType, preset?.id);
    return {
      accessType,
      requiredProduct,
      resolvedProduct,
      unlocked,
      category:
        accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER
          ? "earned"
          : accessType === STORE_PRODUCT_ACCESS_PURCHASE
            ? "paid"
            : "free",
    };
  }, [isSkinUnlockedForBlockType]);
  const buildCategorizedSkinEntries = useCallback(
    (presets = [], blockType) => {
      const categories = {
        free: [],
        paid: [],
        earned: [],
      };

      ensureArray(presets).forEach((preset) => {
        const access = resolveSkinAccessInfo(blockType, preset);
        const presetId = normalizeSkinId(preset?.id || "", "");
        if (!presetId) return;
        if (
          blockType === BLOCK_TYPE_SPECIAL &&
          access.category === "earned" &&
          !EARNED_XIMA_LOGO_SKIN_IDS.has(presetId)
        ) {
          return;
        }
        const entry = { preset, ...access };
        const targetCategory = access.category;
        const alreadyExists = categories[targetCategory].some(
          (current) => normalizeSkinId(current?.preset?.id || "", "") === presetId
        );
        if (!alreadyExists) {
          categories[targetCategory].push(entry);
        }
      });

      return categories;
    },
    [resolveSkinAccessInfo]
  );
  const specialSkinSections = useMemo(
    () => buildCategorizedSkinEntries(availableSpecialSkinPresets, BLOCK_TYPE_SPECIAL),
    [availableSpecialSkinPresets, buildCategorizedSkinEntries]
  );
  const commonSkinSections = useMemo(
    () => buildCategorizedSkinEntries(availableCommonSkinPresets, BLOCK_TYPE_COMMON),
    [availableCommonSkinPresets, buildCategorizedSkinEntries]
  );
  const openSkinPurchasePrompt = useCallback(
    (blockType, preset) => {
      const access = resolveSkinAccessInfo(blockType, preset);
      const requiredProduct = access.requiredProduct;
      if (!requiredProduct?.id) return false;

      const normalizedBlockType =
        blockType === BLOCK_TYPE_COMMON ? BLOCK_TYPE_COMMON : BLOCK_TYPE_SPECIAL;
      const resolvedProduct = access.resolvedProduct;
      const accessType = access.accessType;
      if (accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER) {
        const requiredMode = String(requiredProduct?.earnedMode || resolvedProduct?.earnedMode || "")
          .trim()
          .toLowerCase();
        const modeLabel =
          requiredMode === "sciencevsazar"
            ? "Contrazar"
            : MODE_LABELS[requiredMode === "azar" ? "dice" : requiredMode] ||
              requiredMode ||
              "este modo";
        setLockedSkinPrompt({
          blockType: normalizedBlockType,
          skinId: preset?.id || "",
          skinLabel: String(preset?.label || preset?.id || "Skin"),
          productId: requiredProduct.id,
          accessType,
          productLabel:
            String(
              resolvedProduct?.name?.es ||
                resolvedProduct?.name?.en ||
                resolvedProduct?.id ||
                requiredProduct.id
            ).trim() || requiredProduct.id,
          message: `Debes ser el mejor jugador del modo ${modeLabel} para habilitar este logo XIMA.`,
        });
        return true;
      }
      setLockedSkinPrompt({
        blockType: normalizedBlockType,
        skinId: preset?.id || "",
        skinLabel: String(preset?.label || preset?.id || "Skin"),
        productId: requiredProduct.id,
        accessType,
        productLabel:
          String(
            resolvedProduct?.name?.es ||
              resolvedProduct?.name?.en ||
              resolvedProduct?.id ||
              requiredProduct.id
          ).trim() || requiredProduct.id,
      });
      return true;
    },
    [resolveSkinAccessInfo]
  );
  const handleDraftSpecialSkinSelection = useCallback(
    (rawSkinId) => {
      const preset = resolveSpecialSkinPreset(rawSkinId);
      if (isSkinUnlockedForBlockType(BLOCK_TYPE_SPECIAL, preset.id)) {
        setDraftSpecialBlockId(preset.id);
        return true;
      }
      openSkinPurchasePrompt(BLOCK_TYPE_SPECIAL, preset);
      return false;
    },
    [isSkinUnlockedForBlockType, openSkinPurchasePrompt, resolveSpecialSkinPreset]
  );
  const handleDraftCommonSkinSelection = useCallback(
    (rawSkinId) => {
      const preset = resolveCommonSkinPreset(rawSkinId);
      if (isSkinUnlockedForBlockType(BLOCK_TYPE_COMMON, preset.id)) {
        setDraftCommonBlockThemeId(preset.id);
        return true;
      }
      openSkinPurchasePrompt(BLOCK_TYPE_COMMON, preset);
      return false;
    },
    [isSkinUnlockedForBlockType, openSkinPurchasePrompt, resolveCommonSkinPreset]
  );
  const replayIframeSrc = useMemo(
    () =>
      `${UNITY_IFRAME_BASE_PATH}?v=${encodeURIComponent(UNITY_DEPLOY_VERSION)}&sb=${encodeURIComponent(
        selectedSpecialBlockId
      )}&replay=1&rk=${encodeURIComponent(
        String(replayFrameNonce || 0)
      )}&mid=${encodeURIComponent(String(replaySelectedMatch?.id || ""))}`,
    [replayFrameNonce, replaySelectedMatch?.id, selectedSpecialBlockId]
  );
  const replayHasMatch = !!replaySelectedMatch?.id;
  const replayHasControls = replayHasMatch && replayTotalSteps > 0;
  const replayCanAdvance = replayHasControls && replayCurrentStep < replayTotalSteps;
  const replayCanJumpToEnd =
    replayReady &&
    !replayLoading &&
    !replayRestarting &&
    replayHasControls &&
    replayCurrentStep < replayTotalSteps;
  const replayCanRestart = !replayReady || !replaySelectedMatch?.id ? false : true;
  const replayCanTogglePlay =
    replayPlaying ||
    (replayReady && !replayRestarting && replayHasControls && replayCurrentStep < replayTotalSteps);
  const replayCanGoBack =
    replayCanRestart && !replayLoading && !replayRestarting && replayCurrentStep > 0;
  const replayCanStepForward =
    replayReady &&
    !replayLoading &&
    !replayRestarting &&
    !replayAwaitingStep &&
    replayHasControls &&
    replayCurrentStep < replayTotalSteps;
  const replayCanSelectTimelineStep = replayCanRestart && !replayLoading && !replayRestarting;
  const replayPlaybackSpeed =
    REPLAY_PLAYBACK_SPEED_OPTIONS[
      clampValue(replayPlaybackSpeedIndex, 0, REPLAY_PLAYBACK_SPEED_OPTIONS.length - 1)
    ] || 1;
  const replayPlaybackSpeedLabel = Number.isInteger(replayPlaybackSpeed)
    ? `${replayPlaybackSpeed}.0x`
    : `${replayPlaybackSpeed}x`;
  const replayCanDecreaseSpeed = replayPlaybackSpeedIndex > 0;
  const replayCanIncreaseSpeed =
    replayPlaybackSpeedIndex < REPLAY_PLAYBACK_SPEED_OPTIONS.length - 1;
  const replayAutoplayDelayMs = Math.max(
    80,
    Math.round(REPLAY_AUTOPLAY_BASE_DELAY_MS / Math.max(0.1, Number(replayPlaybackSpeed) || 1))
  );
  const replayContentHeight = isReplayMobileViewport
    ? Math.max(420, Number(replayPanelSize?.height || 0))
    : Math.max(420, Number(replayPanelSize?.height || 0) - 150);
  const replayIframeHeight = useMemo(() => {
    if (!isReplayMobileViewport) {
      return Math.max(300, replayContentHeight - 44);
    }
    const viewportWidth = typeof window !== "undefined" ? Number(window.innerWidth) || 360 : 360;
    const viewportHeight = typeof window !== "undefined" ? Number(window.innerHeight) || 640 : 640;
    if (isReplayPortraitPhoneViewport) {
      return clampValue(
        Math.round(viewportWidth * 0.92),
        240,
        Math.max(280, Math.floor(viewportHeight * 0.62))
      );
    }
    return clampValue(
      Math.round(viewportHeight * 0.72),
      220,
      Math.max(300, Math.floor(viewportHeight * 0.82))
    );
  }, [isReplayMobileViewport, isReplayPortraitPhoneViewport, replayContentHeight]);
  const replayUseLandscapeTwoColumns =
    isReplayLandscapePhoneViewport &&
    (typeof window === "undefined" ? true : Number(window.innerWidth) >= 700);
  const replayLayoutStyle = isReplayMobileViewport
    ? replayUseLandscapeTwoColumns
      ? {
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(260px, 38vw)",
          gap: 10,
          alignItems: "start",
        }
      : {
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }
    : {
        gridTemplateColumns: `minmax(0, 1fr) ${clampValue(
          Math.round((Number(replayPanelSize?.width) || REPLAY_PANEL_DEFAULT_WIDTH) * 0.28),
          240,
          400
        )}px`,
      };
  // Repetición 7 (tres columnas): jugadores, tiempo usado y tabla de jugadas por turno.
  const replayR7Data = useMemo(() => {
    const entries = ensureArray(replayTimeline);
    let maxIndex = 1;
    entries.forEach((entry) => {
      const idx = Number(entry?.playerIndex);
      if (Number.isFinite(idx) && idx >= 0 && idx < 8) maxIndex = Math.max(maxIndex, idx);
    });
    const playerCount = Math.min(4, maxIndex + 1);
    const participants = ensureArray(replaySelectedMatch?.participants);
    const players = Array.from({ length: playerCount }, (_, index) => {
      const fromTimeline = entries.find((entry) => Number(entry?.playerIndex) === index);
      const participant = participants[index];
      const participantName =
        participant && typeof participant === "object"
          ? String(participant.displayName || participant.name || participant.userName || "").trim()
          : "";
      const name =
        String(fromTimeline?.playerName || "").trim() || participantName || `Jugador ${index + 1}`;
      return { index, name, usedMs: 0 };
    });
    let previousAt = 0;
    entries.forEach((entry, position) => {
      const at = Math.max(0, Number(entry?.atMs) || 0);
      const spent = Math.max(0, at - previousAt);
      previousAt = Math.max(previousAt, at);
      if (position + 1 > replayCurrentStep) return;
      const idx = Number(entry?.playerIndex);
      if (players[idx]) players[idx].usedMs += spent;
    });
    const rows = [];
    let row = null;
    let lastIndex = -1;
    entries.forEach((entry) => {
      const parsed = Number(entry?.playerIndex);
      const idx = Number.isFinite(parsed) && parsed >= 0 && parsed < playerCount ? parsed : 0;
      if (!row || row.cells[idx] || idx <= lastIndex) {
        row = { number: rows.length + 1, cells: {} };
        rows.push(row);
      }
      row.cells[idx] = entry;
      lastIndex = idx;
    });
    const nextEntry = replayCurrentStep < entries.length ? entries[replayCurrentStep] : null;
    const nextIndex = Number(nextEntry?.playerIndex);
    return {
      players,
      rows,
      toMoveIndex: Number.isFinite(nextIndex) ? nextIndex : null,
    };
  }, [replayCurrentStep, replaySelectedMatch?.participants, replayTimeline]);
  const replayR7SideWidth = clampValue(
    Math.round((Number(replayPanelSize?.width) || REPLAY_PANEL_DEFAULT_WIDTH) * 0.15),
    200,
    300
  );
  const replayReturnLabel =
    location.state?.replayReturnTo === "/game/championship"
      ? "Volver al Campeonato"
      : String(location.state?.replayReturnTo || "").startsWith("/game/history")
      ? "Volver al historial"
      : i18nT("lobby.salir");
  const replayR7ModeLabel = String(
    replaySelectedMatch?.modeLabel ||
      (replaySelectedMatch?.mode ? formatModeLabel(replaySelectedMatch.mode) : "")
  ).trim();
  const getReplayScrubStepFromEvent = (event, element) => {
    const rect = element.getBoundingClientRect();
    const ratio = clampValue((event.clientX - rect.left) / Math.max(1, rect.width), 0, 1);
    return Math.round(ratio * Math.max(0, Number(replayTotalSteps) || 0));
  };
  const postReplayPlaybackSpeedToIframe = useCallback(
    (targetWindow = null) => {
      const iframeWindow = targetWindow || replayIframeRef.current?.contentWindow || null;
      if (!iframeWindow || typeof iframeWindow.postMessage !== "function") return;

      const speedValue = Math.max(0.1, Number(replayPlaybackSpeed) || 1);
      const targetOrigin =
        typeof window !== "undefined" && window.location?.origin ? window.location.origin : "*";

      try {
        iframeWindow.postMessage(
          {
            type: "XIMA_REPLAY_PLAYBACK_SPEED",
            speed: speedValue,
          },
          targetOrigin
        );
      } catch (error) {
        console.log(error);
      }
    },
    [replayPlaybackSpeed]
  );

  const postReplayCameraFollowToIframe = useCallback(
    (targetWindow = null, enabledOverride = replayCameraFollowEnabled) => {
      const iframeWindow = targetWindow || replayIframeRef.current?.contentWindow || null;
      if (!iframeWindow || typeof iframeWindow.postMessage !== "function") return;

      const targetOrigin =
        typeof window !== "undefined" && window.location?.origin ? window.location.origin : "*";

      try {
        iframeWindow.postMessage(
          {
            type: "XIMA_REPLAY_CAMERA_FOLLOW",
            enabled: enabledOverride === true,
          },
          targetOrigin
        );
      } catch (error) {
        console.log(error);
      }
    },
    [replayCameraFollowEnabled]
  );

  const postReplayEventsToIframe = useCallback((events) => {
    const eventList = Array.isArray(events)
      ? events
          .map((event) => ({
            eventName: String(event?.eventName || "").trim(),
            payload: String(event?.payload ?? ""),
          }))
          .filter((event) => event.eventName)
      : [];
    if (eventList.length <= 0) return;

    const iframeWindow = replayIframeRef.current?.contentWindow || null;
    if (!iframeWindow || typeof iframeWindow.postMessage !== "function") return;

    const targetOrigin =
      typeof window !== "undefined" && window.location?.origin ? window.location.origin : "*";

    try {
      iframeWindow.postMessage(
        {
          type: "XIMA_REPLAY_EVENTS",
          events: eventList,
        },
        targetOrigin
      );
    } catch (error) {
      console.log(error);
    }
  }, []);

  // Diseño de los bloques en el tablero: se manda al cargar cada repetición y al
  // cambiar el interruptor; Unity lo mantiene al avanzar, retroceder o analizar.
  const replayHasRealDesigns = useMemo(
    () =>
      replayHasRealBlockDesigns(replayDesignSource?.participants, replayDesignSource?.userProfiles),
    [replayDesignSource]
  );
  const sendReplayBlockVisualsRef = useRef(() => {});
  sendReplayBlockVisualsRef.current = () => {
    if (!replayDesignSource) return;
    postReplayEventsToIframe([
      {
        eventName: REPLAY_BLOCK_VISUALS_EVENT,
        payload: buildReplayBlockVisualsPayload(
          replayHasRealDesigns ? replayBlockVisualsMode : "default",
          replayDesignSource.participants,
          replayDesignSource.userProfiles
        ),
      },
    ]);
  };
  useEffect(() => {
    if (!replayModalOpen) return;
    sendReplayBlockVisualsRef.current();
  }, [replayModalOpen, replayBlockVisualsMode, replayDesignSource, replayHasRealDesigns]);
  const handleReplayBlockVisualsChange = useCallback((mode) => {
    setReplayBlockVisualsMode(writeReplayBlockVisualsMode(mode));
  }, []);

  // Premium / análisis que le quedan hoy (lo dice el servidor con cada análisis).
  const [replayAnalysisAccess, setReplayAnalysisAccess] = useState(null);

  // «Analizar partida»: el bot revisa la partida en el servidor (se consulta hasta que termina).
  useEffect(() => {
    const matchId = String(replaySelectedMatch?.id || "");
    if (!replayModalOpen || !replayReviewActive || !matchId) return undefined;
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      try {
        const response = await axios.get(`/replays/${matchId}/analysis`);
        if (cancelled) return;
        const analysis = response?.data?.analysis || null;
        if (response?.data?.access) setReplayAnalysisAccess(response.data.access);
        setReplayReview(analysis);
        if (analysis && analysis.state !== "done" && analysis.state !== "error") {
          timer = setTimeout(poll, 1500);
        }
      } catch (error) {
        if (!cancelled) {
          if (error?.response?.data?.access) setReplayAnalysisAccess(error.response.data.access);
          setReplayReview({
            state: "error",
            premium: error?.response?.data?.code === "PREMIUM_REQUIRED",
            error: error?.response?.data?.message || "No se pudo analizar la partida.",
          });
        }
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [replayModalOpen, replayReviewActive, replaySelectedMatch?.id]);

  const replayReviewMoves = useMemo(
    () =>
      replayReviewActive && replayReview?.state === "done"
        ? ensureArray(replayReview?.result?.moves)
        : null,
    [replayReview, replayReviewActive]
  );
  replayReviewMovesRef.current = replayReviewMoves;
  const replayReviewCurrentMove = useMemo(
    () =>
      replayReviewMoves && replayCurrentStep > 0
        ? replayReviewMoves.find((move) => Number(move?.step) === replayCurrentStep) || null
        : null,
    [replayCurrentStep, replayReviewMoves]
  );
  const replayReviewNextMove = useMemo(
    () =>
      replayReviewMoves
        ? replayReviewMoves.find((move) => Number(move?.step) === replayCurrentStep + 1) || null
        : null,
    [replayCurrentStep, replayReviewMoves]
  );
  const replayReviewKindByStep = useMemo(() => {
    const kinds = {};
    (replayReviewMoves || []).forEach((move) => {
      if (move?.kind) kinds[Number(move.step)] = move.kind;
    });
    return kinds;
  }, [replayReviewMoves]);
  const replayReviewSentRef = useRef(false);

  // Unity viste el bloque que acaba de mover y dibuja la jugada que prefería el bot.
  useEffect(() => {
    if (!replayModalOpen || !replayReady) return;
    if (!replayReviewMoves) {
      if (replayReviewSentRef.current) {
        replayReviewSentRef.current = false;
        postReplayEventsToIframe([
          { eventName: "REPLAY_MOVE_BADGE", payload: "" },
          { eventName: "REPLAY_BEST_ARROW", payload: "" },
        ]);
      }
      return;
    }
    const move = replayReviewCurrentMove;
    // La flecha verde es la jugada que el bot recomienda en ESTA posición, es decir,
    // en lugar de la jugada siguiente (si esa no fue la mejor). Así sale de un bloque
    // que está ahí y sigue el relieve real del tablero.
    const nextMove = replayReviewNextMove;
    replayReviewSentRef.current = true;
    postReplayEventsToIframe([
      {
        eventName: "REPLAY_MOVE_BADGE",
        payload:
          move && move.kind && move.played
            ? `${move.playerIndex}|${move.cubeIndex}|${move.played.to}|${move.kind}|${move.step}`
            : "",
      },
      // Flecha de la jugada actual (Unity no la redibuja si ya la tiene).
      { eventName: "REPLAY_BEST_ARROW", payload: reviewArrowPayload(move) },
    ]);
  }, [
    postReplayEventsToIframe,
    replayModalOpen,
    replayReady,
    replayReviewCurrentMove,
    replayReviewMoves,
    replayReviewNextMove,
  ]);

  // Vista previa del video: Unity usa la distribución del video (con el encuadre elegido)
  // y el tablero de la repetición toma la forma exacta del video (16:9 o 9:16).
  useEffect(() => {
    if (!replayBoardElement || !replayVideoPreview || typeof ResizeObserver === "undefined") {
      setReplayBoardSize(null);
      return undefined;
    }
    const measure = () => {
      const rect = replayBoardElement.getBoundingClientRect();
      setReplayBoardSize({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(replayBoardElement);
    return () => observer.disconnect();
  }, [replayBoardElement, replayVideoPreview]);

  const replayVideoPreviewPayload = replayVideoPreview?.payload || "";
  const replayVideoPreviewSentRef = useRef(false);
  useEffect(() => {
    if (!replayModalOpen || !replayReady) return;
    if (!replayVideoPreviewPayload && !replayVideoPreviewSentRef.current) return;
    replayVideoPreviewSentRef.current = Boolean(replayVideoPreviewPayload);
    postReplayEventsToIframe([{ eventName: "REPLAY_VIDEO_LAYOUT", payload: replayVideoPreviewPayload || "0" }]);
  }, [postReplayEventsToIframe, replayModalOpen, replayReady, replayVideoPreviewPayload]);

  const replayVideoPreviewRect = useMemo(
    () => computeVideoPreviewRect(replayVideoPreview, replayBoardSize),
    [replayBoardSize, replayVideoPreview]
  );

  const toggleReplayCameraFollow = useCallback(() => {
    const nextEnabled = !replayCameraFollowEnabled;
    setReplayCameraFollowEnabled(nextEnabled);
    writeCameraFollowPreference(user?.email, nextEnabled);
    postReplayCameraFollowToIframe(null, nextEnabled);
  }, [postReplayCameraFollowToIframe, replayCameraFollowEnabled, user?.email]);

  useEffect(() => {
    replayCurrentStepRef.current = replayCurrentStep;
  }, [replayCurrentStep]);

  useEffect(() => {
    const activeItem = replayActiveTimelineItemRef.current;
    const scrollBox = replayTimelineScrollRef.current;
    if (!replayModalOpen || isReplayMobileViewport) return;
    if (!activeItem || !scrollBox) return;
    const margin = 8;
    const itemTop = activeItem.offsetTop;
    const itemBottom = itemTop + activeItem.offsetHeight;
    if (itemTop - margin < scrollBox.scrollTop) {
      scrollBox.scrollTop = Math.max(0, itemTop - margin);
    } else if (itemBottom + margin > scrollBox.scrollTop + scrollBox.clientHeight) {
      scrollBox.scrollTop = itemBottom + margin - scrollBox.clientHeight;
    }
  }, [isReplayMobileViewport, replayCurrentStep, replayModalOpen]);

  useEffect(() => {
    replayTotalStepsRef.current = replayTotalSteps;
  }, [replayTotalSteps]);

  useEffect(() => {
    replayLoadingRef.current = replayLoading;
  }, [replayLoading]);

  const applySpecialBlockPreference = useCallback(
    (rawSkinId, explicitImage = "") => {
      const preset = resolveSpecialSkinPreset(rawSkinId);
      setSelectedSpecialBlockId(preset.id);
      setDraftSpecialBlockId(preset.id);

      localStorage.setItem(`${SPECIAL_BLOCK_STORAGE_PREFIX}${normalizedUserEmail}`, preset.id);
      localStorage.setItem(GLOBAL_SPECIAL_BLOCK_KEY, preset.id);
      localStorage.setItem(
        GLOBAL_SPECIAL_BLOCK_IMAGE_KEY,
        resolveStoredSpecialBlockImage(
          preset.id,
          String(explicitImage || preset.image || "").trim()
        )
      );

      return preset;
    },
    [normalizedUserEmail, resolveSpecialSkinPreset]
  );

  const applyCommonBlockPreference = useCallback(
    (rawSkinId) => {
      const preset = resolveCommonSkinPreset(rawSkinId);
      setSelectedCommonBlockThemeId(preset.id);
      setDraftCommonBlockThemeId(preset.id);

      localStorage.setItem(`${COMMON_BLOCK_STORAGE_PREFIX}${normalizedUserEmail}`, preset.id);
      localStorage.setItem(GLOBAL_COMMON_BLOCK_THEME_KEY, preset.id);

      return preset;
    },
    [normalizedUserEmail, resolveCommonSkinPreset]
  );

  const persistSkinPreferencesToServer = useCallback(async (specialId, commonId, options = {}) => {
    const showBlockedPrompt = options?.showBlockedPrompt === true;
    const token = String(getStoredAuthToken() || "").trim();
    if (!token) return null;

    const normalizedSpecial = normalizeSkinId(specialId, "");
    const normalizedCommon = normalizeSkinId(commonId, "");
    if (!normalizedSpecial && !normalizedCommon) return null;

    const payload = {};
    if (normalizedSpecial) payload.specialBlockId = normalizedSpecial;
    if (normalizedCommon) payload.commonBlockThemeId = normalizedCommon;

    try {
      const response = await axios.patch("/auth/profile/skins", payload);
      const data = response?.data || null;
      setOwnedSpecialSkinProductIds(
        normalizeOwnedStoreProductIds(
          data?.user?.ownedSpecialSkinProductIds || data?.ownedSpecialSkinProductIds,
          BLOCK_TYPE_SPECIAL
        )
      );
      setOwnedCommonSkinProductIds(
        normalizeOwnedStoreProductIds(
          data?.user?.ownedCommonSkinProductIds || data?.ownedCommonSkinProductIds,
          BLOCK_TYPE_COMMON
        )
      );
      setIsTopWinnerSkinEligible(Boolean(data?.skinEntitlements?.isTopWinner));
      setTopWinnerSkinEligibleModes(ensureArray(data?.skinEntitlements?.modes));
      return data;
    } catch (error) {
      const errorCode = String(error?.response?.data?.code || "").trim();
      if (
        showBlockedPrompt &&
        (errorCode === "SKIN_PURCHASE_REQUIRED" || errorCode === "SKIN_EARNED_REQUIRED")
      ) {
        const blockType =
          String(error?.response?.data?.blockType || "").trim().toLowerCase() === BLOCK_TYPE_COMMON
            ? BLOCK_TYPE_COMMON
            : BLOCK_TYPE_SPECIAL;
        const blockedSkinId = normalizeSkinId(error?.response?.data?.skinId || "", "");
        const blockedProductId = normalizeStoreProductId(error?.response?.data?.productId || "");
        const blockedProduct = resolveSkinStoreProductById(blockedProductId, blockType);
        const accessType =
          errorCode === "SKIN_EARNED_REQUIRED"
            ? STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER
            : STORE_PRODUCT_ACCESS_PURCHASE;
        if (accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER) {
          navigate(`/game/store?type=${blockType}&product=${encodeURIComponent(blockedProductId)}`);
        } else {
          setLockedSkinPrompt({
            blockType,
            skinId: blockedSkinId,
            skinLabel: blockedSkinId || "Skin",
            productId: blockedProductId,
            accessType,
            productLabel:
              String(
                blockedProduct?.name?.es ||
                  blockedProduct?.name?.en ||
                  blockedProduct?.id ||
                  blockedProductId
              ).trim() || blockedProductId,
          });
        }
      }
      console.log(error);
      return null;
    }
  }, [navigate]);

  useEffect(() => {
    if (!topWinnerPrimaryModeFromLeaderboard) return;
    const normalizedSelectedSpecialBlockId = normalizeSkinId(selectedSpecialBlockId, "");
    const normalizedDraftSpecialBlockId = normalizeSkinId(draftSpecialBlockId, "");
    if (
      topWinnerEligibleSkinIdSet.has(normalizedSelectedSpecialBlockId) &&
      topWinnerEligibleSkinIdSet.has(normalizedDraftSpecialBlockId)
    ) {
      return;
    }

    const forcedPreset = resolveSpecialSkinPreset(topWinnerSpecialSkinId);
    if (!forcedPreset?.id) return;
    if (selectedSpecialBlockId === forcedPreset.id && draftSpecialBlockId === forcedPreset.id) return;
    if (
      normalizedSelectedSpecialBlockId &&
      !topWinnerLogoSkinIdSet.has(normalizedSelectedSpecialBlockId)
    ) {
      return;
    }

    const applied = applySpecialBlockPreference(forcedPreset.id);
    persistSkinPreferencesToServer(applied.id, selectedCommonBlockThemeId).catch((error) =>
      console.log(error)
    );
    window.dispatchEvent(
      new CustomEvent("xima-special-block-change", {
        detail: {
          ...applied,
          imageName: applied.id,
        },
      })
    );
  }, [
    applySpecialBlockPreference,
    draftSpecialBlockId,
    persistSkinPreferencesToServer,
    resolveSpecialSkinPreset,
    selectedCommonBlockThemeId,
    selectedSpecialBlockId,
    topWinnerEligibleSkinIdSet,
    topWinnerLogoSkinIdSet,
    topWinnerPrimaryModeFromLeaderboard,
    topWinnerSpecialSkinId,
  ]);

  useEffect(() => {
    const syncFromStorage = () => {
      const userScopedKey = `${SPECIAL_BLOCK_STORAGE_PREFIX}${normalizedUserEmail}`;
      const userScopedValue = normalizeSkinId(localStorage.getItem(userScopedKey) || "", "");
      const globalValue = normalizeSkinId(localStorage.getItem(GLOBAL_SPECIAL_BLOCK_KEY) || "", "");
      applySpecialBlockPreference(userScopedValue || globalValue || DEFAULT_SPECIAL_BLOCK_ID);
    };

    const handleSpecialBlockChange = (event) => {
      const incomingId = normalizeSkinId(
        event?.detail?.imageName || event?.detail?.id || "",
        ""
      );
      const userScopedKey = `${SPECIAL_BLOCK_STORAGE_PREFIX}${normalizedUserEmail}`;
      const storedSpecial = normalizeSkinId(localStorage.getItem(userScopedKey) || "", "");
      const globalSpecial = normalizeSkinId(localStorage.getItem(GLOBAL_SPECIAL_BLOCK_KEY) || "", "");
      applySpecialBlockPreference(
        incomingId || storedSpecial || globalSpecial || DEFAULT_SPECIAL_BLOCK_ID,
        event?.detail?.image
      );
    };

    syncFromStorage();
    window.addEventListener("xima-special-block-change", handleSpecialBlockChange);

    return () => {
      window.removeEventListener("xima-special-block-change", handleSpecialBlockChange);
    };
  }, [applySpecialBlockPreference, normalizedUserEmail]);

  useEffect(() => {
    const storedBoardThemeId = String(localStorage.getItem(GLOBAL_BOARD_THEME_KEY) || "")
      .trim()
      .toLowerCase();
    const boardPreset = getBoardThemePreset(storedBoardThemeId);
    setSelectedBoardThemeId(boardPreset.id);
    setDraftBoardThemeId(boardPreset.id);
    localStorage.setItem(GLOBAL_BOARD_THEME_KEY, boardPreset.id);

    const userScopedCommonKey = `${COMMON_BLOCK_STORAGE_PREFIX}${normalizedUserEmail}`;
    const userScopedCommonId = normalizeSkinId(
      localStorage.getItem(userScopedCommonKey) || "",
      ""
    );
    const globalCommonId = normalizeSkinId(
      localStorage.getItem(GLOBAL_COMMON_BLOCK_THEME_KEY) || "",
      ""
    );
    applyCommonBlockPreference(userScopedCommonId || globalCommonId || DEFAULT_COMMON_BLOCK_THEME_ID);
  }, [applyCommonBlockPreference, normalizedUserEmail]);

  useEffect(() => {
    let cancelled = false;

    const syncSkinPreferencesFromServer = async () => {
      const token = String(getStoredAuthToken() || "").trim();
      if (!token) return;

      const localSpecial = normalizeSkinId(
        localStorage.getItem(`${SPECIAL_BLOCK_STORAGE_PREFIX}${normalizedUserEmail}`) ||
          localStorage.getItem(GLOBAL_SPECIAL_BLOCK_KEY) ||
          "",
        DEFAULT_SPECIAL_BLOCK_ID
      );
      const localCommon = normalizeSkinId(
        localStorage.getItem(`${COMMON_BLOCK_STORAGE_PREFIX}${normalizedUserEmail}`) ||
          localStorage.getItem(GLOBAL_COMMON_BLOCK_THEME_KEY) ||
          "",
        DEFAULT_COMMON_BLOCK_THEME_ID
      );

      try {
        const response = await axios.get("/auth/profile");
        if (cancelled) return;
        setOwnedSpecialSkinProductIds(
          normalizeOwnedStoreProductIds(
            response?.data?.ownedSpecialSkinProductIds,
            BLOCK_TYPE_SPECIAL
          )
        );
        setOwnedCommonSkinProductIds(
          normalizeOwnedStoreProductIds(
            response?.data?.ownedCommonSkinProductIds,
            BLOCK_TYPE_COMMON
          )
        );
        setIsTopWinnerSkinEligible(Boolean(response?.data?.skinEntitlements?.isTopWinner));
        setTopWinnerSkinEligibleModes(ensureArray(response?.data?.skinEntitlements?.modes));
        setProfileFirstName(
          String(response?.data?.firstName || "")
            .trim()
            .slice(0, PROFILE_NAME_MAX_LENGTH)
        );
        setProfileLastName(
          String(response?.data?.lastName || "")
            .trim()
            .slice(0, PROFILE_NAME_MAX_LENGTH)
        );
        setProfileContactEmail(
          String(response?.data?.contactEmail || "")
            .trim()
            .toLowerCase()
            .slice(0, PROFILE_CONTACT_EMAIL_MAX_LENGTH)
        );
        setProfilePhone(normalizeProfilePhoneInput(response?.data?.phone || "").trim());
        setProfileAvatar(resolveAvatarSrc(response?.data?.avatarUrl || response?.data?.avatar || ""));
        setCimaxScores(
          normalizeCimaxScores(response?.data?.cimaxScores || response?.data?.cimax)
        );
        setWalletBalance(
          normalizeWalletBalance(response?.data?.walletBalance ?? response?.data?.wallet?.balance)
        );

        const serverSpecial = normalizeSkinId(response?.data?.specialBlockId || "", "");
        const serverCommon = normalizeSkinId(response?.data?.commonBlockThemeId || "", "");
        const resolvedSpecial = serverSpecial || localSpecial || DEFAULT_SPECIAL_BLOCK_ID;
        const resolvedCommon = serverCommon || localCommon || DEFAULT_COMMON_BLOCK_THEME_ID;

        applySpecialBlockPreference(resolvedSpecial);
        applyCommonBlockPreference(resolvedCommon);

        if (!serverSpecial || !serverCommon) {
          persistSkinPreferencesToServer(resolvedSpecial, resolvedCommon).catch((error) =>
            console.log(error)
          );
        }
      } catch (error) {
        console.log(error);
      }
    };

    syncSkinPreferencesFromServer().catch((error) => console.log(error));
    return () => {
      cancelled = true;
    };
  }, [
    applyCommonBlockPreference,
    applySpecialBlockPreference,
    normalizedUserEmail,
    persistSkinPreferencesToServer,
  ]);

  const openProfileModal = useCallback(() => {
    setProfileDraftAvatar(currentAvatarUrl || "");
    setProfileDraftAvatarFile(null);
    setProfileDraftFirstName(
      String(profileFirstName || user?.firstName || "")
        .trim()
        .slice(0, PROFILE_NAME_MAX_LENGTH)
    );
    setProfileDraftLastName(
      String(profileLastName || user?.lastName || "")
        .trim()
        .slice(0, PROFILE_NAME_MAX_LENGTH)
    );
    setProfileDraftContactEmail(
      String(profileContactEmail || "")
        .trim()
        .toLowerCase()
        .slice(0, PROFILE_CONTACT_EMAIL_MAX_LENGTH)
    );
    setProfileDraftPhone(normalizeProfilePhoneInput(profilePhone).trim());
    setProfileDraftCurrentPassword("");
    setProfileDraftNewPassword("");
    setProfileDraftConfirmPassword("");
    if (shouldEmbedLobbyDesignEditor) {
      persistLobbyDesignActiveEditor("profile");
      setProfileModalOpen(false);
      return;
    }
    setProfileModalOpen(true);
  }, [
    currentAvatarUrl,
    profileContactEmail,
    profileFirstName,
    profileLastName,
    profilePhone,
    persistLobbyDesignActiveEditor,
    shouldEmbedLobbyDesignEditor,
    user?.firstName,
    user?.lastName,
  ]);

  useEffect(() => {
    const state =
      location.state && typeof location.state === "object" && !Array.isArray(location.state)
        ? location.state
        : null;
    const stateRequestKey =
      state ? String(state.openProfileModalRequestKey || "").trim() : "";
    const queryRequestKey = String(searchParams.get("openProfileModal") || "").trim();
    const requestKey = stateRequestKey || queryRequestKey;
    if (!requestKey || lobbyProfileModalRequestHandledRef.current === requestKey) return;

    lobbyProfileModalRequestHandledRef.current = requestKey;
    openProfileModal();
  }, [location.state, openProfileModal, searchParams]);

  const openSpecialBlockModal = useCallback(() => {
    setDraftSpecialBlockId(selectedSpecialBlockId);
    setLockedSkinPrompt(null);
    if (shouldEmbedLobbyDesignEditor) {
      persistLobbyDesignActiveEditor("special");
      setSpecialBlockModalOpen(false);
      return;
    }
    setSpecialBlockModalOpen(true);
  }, [persistLobbyDesignActiveEditor, selectedSpecialBlockId, shouldEmbedLobbyDesignEditor]);

  const closeSpecialBlockModal = useCallback(() => {
    setSpecialBlockModalOpen(false);
    setDraftSpecialBlockId(selectedSpecialBlockId);
    setLockedSkinPrompt(null);
  }, [selectedSpecialBlockId]);

  const saveSpecialBlockSelection = useCallback(() => {
    const resolvedPreset = resolveSpecialSkinPreset(draftSpecialBlockId);
    if (!isSkinUnlockedForBlockType(BLOCK_TYPE_SPECIAL, resolvedPreset.id)) {
      openSkinPurchasePrompt(BLOCK_TYPE_SPECIAL, resolvedPreset);
      return;
    }

    const preset = applySpecialBlockPreference(draftSpecialBlockId);
    persistSkinPreferencesToServer(preset.id, selectedCommonBlockThemeId, {
      showBlockedPrompt: true,
    }).catch((error) =>
      console.log(error)
    );
    console.log("[SKIN UI] Selected: " + preset.id);
    window.dispatchEvent(
      new CustomEvent("xima-special-block-change", {
        detail: {
          ...preset,
          imageName: preset.id,
        },
      })
    );
    setSpecialBlockModalOpen(false);
    setLockedSkinPrompt(null);
    notification.success({
      message: i18nT("lobby.especialActualizado"),
      description: `Bloque especial seleccionado: ${preset.label}.`,
    });
  }, [
    applySpecialBlockPreference,
    draftSpecialBlockId,
    isSkinUnlockedForBlockType,
    openSkinPurchasePrompt,
    persistSkinPreferencesToServer,
    resolveSpecialSkinPreset,
    selectedCommonBlockThemeId,
  ]);

  const openBoardThemeModal = useCallback(() => {
    setDraftBoardThemeId(selectedBoardThemeId);
    if (shouldEmbedLobbyDesignEditor) {
      persistLobbyDesignActiveEditor("board");
      setBoardThemeModalOpen(false);
      return;
    }
    setBoardThemeModalOpen(true);
  }, [persistLobbyDesignActiveEditor, selectedBoardThemeId, shouldEmbedLobbyDesignEditor]);

  const closeBoardThemeModal = useCallback(() => {
    setBoardThemeModalOpen(false);
    setDraftBoardThemeId(selectedBoardThemeId);
  }, [selectedBoardThemeId]);

  const saveBoardThemeSelection = useCallback(() => {
    const preset = getBoardThemePreset(draftBoardThemeId);
    setSelectedBoardThemeId(preset.id);
    localStorage.setItem(GLOBAL_BOARD_THEME_KEY, preset.id);
    window.dispatchEvent(
      new CustomEvent("xima-board-theme-change", {
        detail: preset,
      })
    );
    setBoardThemeModalOpen(false);
    notification.success({
      message: i18nT("lobby.tableroActualizado"),
      description: `Tema seleccionado: ${preset.label}.`,
    });
  }, [draftBoardThemeId]);

  const openCommonBlocksModal = useCallback(() => {
    setDraftCommonBlockThemeId(selectedCommonBlockThemeId);
    setLockedSkinPrompt(null);
    if (shouldEmbedLobbyDesignEditor) {
      persistLobbyDesignActiveEditor("common");
      setCommonBlocksModalOpen(false);
      return;
    }
    setCommonBlocksModalOpen(true);
  }, [persistLobbyDesignActiveEditor, selectedCommonBlockThemeId, shouldEmbedLobbyDesignEditor]);

  const closeCommonBlocksModal = useCallback(() => {
    setCommonBlocksModalOpen(false);
    setDraftCommonBlockThemeId(selectedCommonBlockThemeId);
    setLockedSkinPrompt(null);
  }, [selectedCommonBlockThemeId]);

  const saveCommonBlocksSelection = useCallback(() => {
    const resolvedPreset = resolveCommonSkinPreset(draftCommonBlockThemeId);
    if (!isSkinUnlockedForBlockType(BLOCK_TYPE_COMMON, resolvedPreset.id)) {
      openSkinPurchasePrompt(BLOCK_TYPE_COMMON, resolvedPreset);
      return;
    }

    const preset = applyCommonBlockPreference(draftCommonBlockThemeId);
    persistSkinPreferencesToServer(selectedSpecialBlockId, preset.id, {
      showBlockedPrompt: true,
    }).catch((error) =>
      console.log(error)
    );
    window.dispatchEvent(
      new CustomEvent("xima-common-block-theme-change", {
        detail: {
          ...preset,
          imageName: preset.id,
        },
      })
    );
    setCommonBlocksModalOpen(false);
    setLockedSkinPrompt(null);
    notification.success({
      message: i18nT("lobby.bloquesActualizados"),
      description: `Bloque comun seleccionado: ${preset.label}.`,
    });
  }, [
    applyCommonBlockPreference,
    draftCommonBlockThemeId,
    isSkinUnlockedForBlockType,
    openSkinPurchasePrompt,
    persistSkinPreferencesToServer,
    resolveCommonSkinPreset,
    selectedSpecialBlockId,
  ]);

  const closeProfileModal = useCallback(() => {
    setProfileModalOpen(false);
    setProfileDraftAvatar(currentAvatarUrl || "");
    setProfileDraftAvatarFile(null);
    setProfileDraftFirstName(
      String(profileFirstName || user?.firstName || "")
        .trim()
        .slice(0, PROFILE_NAME_MAX_LENGTH)
    );
    setProfileDraftLastName(
      String(profileLastName || user?.lastName || "")
        .trim()
        .slice(0, PROFILE_NAME_MAX_LENGTH)
    );
    setProfileDraftContactEmail(
      String(profileContactEmail || "")
        .trim()
        .toLowerCase()
        .slice(0, PROFILE_CONTACT_EMAIL_MAX_LENGTH)
    );
    setProfileDraftPhone(normalizeProfilePhoneInput(profilePhone).trim());
    setProfileDraftCurrentPassword("");
    setProfileDraftNewPassword("");
    setProfileDraftConfirmPassword("");
  }, [
    currentAvatarUrl,
    profileContactEmail,
    profileFirstName,
    profileLastName,
    profilePhone,
    user?.firstName,
    user?.lastName,
  ]);

  const handleAvatarUpload = useCallback(async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    if (!String(file.type || "").startsWith("image/")) {
      notification.error({
        message: i18nT("lobby.invalidFile"),
        description: i18nT("lobby.pleaseUploadAnImageFile"),
      });
      event.target.value = "";
      return;
    }

    if (Number(file.size || 0) > MAX_AVATAR_FILE_SIZE_BYTES) {
      notification.error({
        message: i18nT("lobby.imageTooLarge"),
        description: i18nT("lobby.pleaseUseAnImageSmallerThanMb"),
      });
      event.target.value = "";
      return;
    }

    try {
      const rawAvatar = await readFileAsDataUrl(file);
      let previewAvatar = rawAvatar;
      try {
        previewAvatar = await optimizeAvatarDataUrl(rawAvatar);
      } catch (previewError) {
        previewAvatar = rawAvatar;
      }
      setProfileDraftAvatar(previewAvatar || "");
      setProfileDraftAvatarFile(file);
    } catch (error) {
      notification.error({
        message: i18nT("lobby.imageError"),
        description: error?.message || "Could not process this image.",
      });
    }

    event.target.value = "";
  }, []);

  const saveProfileChanges = useCallback(async () => {
    const nextFirstName = String(profileDraftFirstName || "")
      .trim()
      .slice(0, PROFILE_NAME_MAX_LENGTH);
    const nextLastName = String(profileDraftLastName || "")
      .trim()
      .slice(0, PROFILE_NAME_MAX_LENGTH);
    const nextContactEmail = String(profileDraftContactEmail || "")
      .trim()
      .toLowerCase()
      .slice(0, PROFILE_CONTACT_EMAIL_MAX_LENGTH);
    const shouldSaveProfilePhone = !shouldEmbedLobbyDesignEditor;
    const nextPhone = shouldSaveProfilePhone
      ? normalizeProfilePhoneInput(profileDraftPhone).trim()
      : "";
    const nextCurrentPassword = String(profileDraftCurrentPassword || "");
    const nextNewPassword = String(profileDraftNewPassword || "");
    const nextConfirmPassword = String(profileDraftConfirmPassword || "");

    if (!nextFirstName) {
      notification.error({
        message: i18nT("lobby.nombreObligatorio"),
        description: i18nT("lobby.debesEscribirTuNombreParaActualizarElPerfil"),
      });
      return;
    }

    if (!nextLastName) {
      notification.error({
        message: i18nT("lobby.apellidoObligatorio"),
        description: i18nT("lobby.debesEscribirTuApellidoParaActualizarElPerfic88b3a"),
      });
      return;
    }

    if (nextContactEmail && !PROFILE_CONTACT_EMAIL_PATTERN.test(nextContactEmail)) {
      notification.error({
        message: i18nT("profile.correoNoValido"),
        description: i18nT("lobby.elCorreoDeContactoDebeTenerUnFormato"),
      });
      return;
    }

    if (shouldSaveProfilePhone && nextPhone && !PROFILE_PHONE_PATTERN.test(nextPhone)) {
      notification.error({
        message: i18nT("lobby.telefonoNoValido"),
        description: i18nT("lobby.elTelefonoSoloPuedeIncluirNumerosEspaciosY"),
      });
      return;
    }

    const wantsPasswordUpdate = nextNewPassword.trim().length > 0;
    if (wantsPasswordUpdate) {
      if (!nextCurrentPassword.trim()) {
        notification.error({
          message: i18nT("lobby.faltaLaContrasenaActual"),
          description: i18nT("lobby.paraCambiarLaContrasenaDebesEscribirLaContra0bb74e"),
        });
        return;
      }

      if (nextNewPassword.length < 6) {
        notification.error({
          message: i18nT("lobby.contrasenaDemasiadoCorta"),
          description: i18nT("lobby.laNuevaContrasenaDebeTenerAlMenosCaracteres"),
        });
        return;
      }

      if (nextNewPassword !== nextConfirmPassword) {
        notification.error({
          message: i18nT("lobby.contrasenasDiferentes"),
          description: i18nT("lobby.laConfirmacionNoCoincideConLaNuevaContrasena"),
        });
        return;
      }
    } else if (nextCurrentPassword.trim() || nextConfirmPassword.trim()) {
      notification.error({
        message: i18nT("lobby.cambioDeContrasenaIncompleto"),
        description: i18nT("lobby.escribeTambienLaNuevaContrasenaParaCompletar8a18a4"),
      });
      return;
    }

    setProfileSavingPersonal(true);
    try {
      const draftAvatarValue = String(profileDraftAvatar || "").trim();
      const shouldUploadAvatarFile = Boolean(profileDraftAvatarFile);
      const shouldSaveAvatarReference =
        !shouldUploadAvatarFile && !draftAvatarValue.startsWith("data:image/");

      const payload = {
        firstName: nextFirstName,
        lastName: nextLastName,
        contactEmail: nextContactEmail,
      };
      if (shouldSaveProfilePhone) {
        payload.phone = nextPhone;
      }
      if (shouldSaveAvatarReference) {
        payload.avatarUrl = draftAvatarValue;
      }
      if (wantsPasswordUpdate) {
        payload.currentPassword = nextCurrentPassword;
        payload.newPassword = nextNewPassword;
      }

      const response = await axios.patch("/auth/profile/personal", payload);
      let updatedProfile = response?.data?.user || {};
      if (shouldUploadAvatarFile) {
        const avatarFormData = new FormData();
        avatarFormData.append("avatar", profileDraftAvatarFile);
        const avatarResponse = await axios.patch("/auth/profile/avatar", avatarFormData);
        updatedProfile = avatarResponse?.data?.user || updatedProfile;
      }

      const savedFirstName = String(updatedProfile?.firstName || nextFirstName)
        .trim()
        .slice(0, PROFILE_NAME_MAX_LENGTH);
      const savedLastName = String(updatedProfile?.lastName || nextLastName)
        .trim()
        .slice(0, PROFILE_NAME_MAX_LENGTH);
      const savedContactEmail = String(updatedProfile?.contactEmail || nextContactEmail)
        .trim()
        .toLowerCase()
        .slice(0, PROFILE_CONTACT_EMAIL_MAX_LENGTH);
      const savedAvatarUrl = resolveAvatarSrc(updatedProfile?.avatarUrl || "");

      setProfileFirstName(savedFirstName);
      setProfileLastName(savedLastName);
      setProfileContactEmail(savedContactEmail);
      if (shouldSaveProfilePhone) {
        const savedPhone = normalizeProfilePhoneInput(updatedProfile?.phone || nextPhone).trim();
        setProfilePhone(savedPhone);
        setProfileDraftPhone(savedPhone);
      }
      setProfileDraftFirstName(savedFirstName);
      setProfileDraftLastName(savedLastName);
      setProfileDraftContactEmail(savedContactEmail);
      setProfileDraftAvatarFile(null);
      setProfileDraftCurrentPassword("");
      setProfileDraftNewPassword("");
      setProfileDraftConfirmPassword("");

      if (profileStorageKey) {
        if (savedAvatarUrl) {
          localStorage.setItem(
            profileStorageKey,
            JSON.stringify({
              avatarUrl: savedAvatarUrl,
            })
          );
        } else {
          localStorage.removeItem(profileStorageKey);
        }
      }
      setProfileAvatar(savedAvatarUrl);
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("xima-profile-change", {
            detail: {
              ...(updatedProfile || {}),
              firstName: savedFirstName,
              lastName: savedLastName,
              contactEmail: savedContactEmail,
              avatarUrl: savedAvatarUrl,
            },
          })
        );
      }
      refreshUserProfile?.().catch(() => {});

      setProfileModalOpen(false);
      notification.success({
        message: i18nT("lobby.perfilActualizado"),
        description:
          String(response?.data?.message || "").trim() ||
          "Tu informacion personal fue actualizada correctamente.",
      });
    } catch (error) {
      notification.error({
        message: i18nT("lobby.noSePudoActualizarElPerfil"),
        description:
          String(error?.response?.data?.error || "").trim() ||
          error?.message ||
          "Ocurrio un error al actualizar tu perfil.",
      });
    } finally {
      setProfileSavingPersonal(false);
    }
  }, [
    profileDraftConfirmPassword,
    profileDraftContactEmail,
    profileDraftCurrentPassword,
    profileDraftAvatar,
    profileDraftAvatarFile,
    profileDraftFirstName,
    profileDraftLastName,
    profileDraftNewPassword,
    profileDraftPhone,
    profileStorageKey,
    refreshUserProfile,
    shouldEmbedLobbyDesignEditor,
  ]);

  const resetProfileAvatar = useCallback(() => {
    if (!profileStorageKey) return;

    localStorage.removeItem(profileStorageKey);
    setProfileAvatar("");
    setProfileDraftAvatar(serverAvatarUrl || "");
    setProfileDraftAvatarFile(null);
    notification.info({
      message: i18nT("lobby.profileReset"),
      description: i18nT("lobby.customProfilePhotoRemoved"),
    });
  }, [profileStorageKey, serverAvatarUrl]);

  const userInitials = useMemo(() => {
    const parts = displayName
      .split(/\s+/)
      .map((item) => item.trim())
      .filter(Boolean);

    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }

    return String(displayName).slice(0, 2).toUpperCase() || "PL";
  }, [displayName]);

  const getTournamentStatus = useCallback((record) => {
    const currentTime = moment();
    const startTime = new Date(record?.startingTime);

    if (Number.isNaN(startTime.valueOf())) return "unknown";

    const quickExpiresAtMs = new Date(record?.quickMatchExpiresAt || 0).valueOf();
    const quickDurationMinutes = Math.max(0, Number(record?.quickMatchDurationMinutes) || 0);
    const derivedQuickEndMs =
      quickDurationMinutes > 0
        ? startTime.valueOf() + quickDurationMinutes * 60 * 1000
        : 0;
    const effectiveQuickEndMs =
      Number.isFinite(quickExpiresAtMs) && quickExpiresAtMs > 0
        ? quickExpiresAtMs
        : derivedQuickEndMs;
    const defaultEndMs = startTime.valueOf() + Number(record?.duration || 0) * 3600 * 24 * 1000;
    const resolvedEndMs =
      Boolean(record?.isQuickMatch) && effectiveQuickEndMs > 0
        ? effectiveQuickEndMs
        : defaultEndMs;
    const endTime = moment(resolvedEndMs);

    if (currentTime.isAfter(endTime)) return "finished";
    if (moment(startTime).isAfter(currentTime)) return "waiting";
    return "playing";
  }, []);

  const applyFilter = useCallback(
    (rawData, filterValue) => {
      const data = ensureArray(rawData);
      let filtered = data;

      if (filterValue === "playing") {
        filtered = data.filter((tournament) => getTournamentStatus(tournament) === "playing");
      } else if (filterValue === "expired") {
        filtered = data.filter((tournament) => getTournamentStatus(tournament) === "finished");
      } else if (filterValue === "waiting") {
        filtered = data.filter((tournament) => getTournamentStatus(tournament) === "waiting");
      }

      setFilteredTournaments(filtered);
    },
    [getTournamentStatus]
  );
  const applyOptimisticTournamentJoin = useCallback(
    (tournamentId, fallbackRecord = null) => {
      const normalizedTournamentId = String(tournamentId || "").trim();
      if (!normalizedTournamentId) return;

      const appendCurrentUser = (rawUsers) => {
        const users = ensureArray(rawUsers);
        const currentEmail = String(currentTournamentPlayer?.email || "")
          .trim()
          .toLowerCase();
        const currentUserId = String(currentTournamentPlayer?.userId || currentTournamentPlayer?._id || "")
          .trim()
          .toLowerCase();
        const currentUsername = String(
          currentTournamentPlayer?.username || currentTournamentPlayer?.userName || ""
        )
          .trim()
          .toLowerCase();
        const currentDisplayName = String(currentTournamentPlayer?.displayName || displayName)
          .trim()
          .toLowerCase();

        const alreadyJoined = users.some((entry) => {
          if (typeof entry === "string") {
            const normalized = String(entry).trim().toLowerCase();
            return Boolean(
              (currentEmail && normalized === currentEmail) ||
                (currentUserId && normalized === currentUserId) ||
                (currentUsername && normalized === currentUsername) ||
                (currentDisplayName && normalized === currentDisplayName)
            );
          }

          if (!entry || typeof entry !== "object") return false;

          const entryEmail = String(entry?.email || "")
            .trim()
            .toLowerCase();
          const entryUserId = String(entry?.userId || entry?._id || entry?.id || "")
            .trim()
            .toLowerCase();
          const entryUsername = String(entry?.username || entry?.userName || "")
            .trim()
            .toLowerCase();
          const entryDisplayName = extractPrimaryName(
            entry?.displayName ||
              entry?.name ||
              entry?.firstName ||
              entry?.email ||
              entry?.userId
          ).toLowerCase();

          return Boolean(
            (currentEmail && entryEmail && entryEmail === currentEmail) ||
              (currentUserId && entryUserId && entryUserId === currentUserId) ||
              (currentUsername && entryUsername && entryUsername === currentUsername) ||
              (currentDisplayName && entryDisplayName && entryDisplayName === currentDisplayName)
          );
        });

        if (alreadyJoined) return users;
        return [...users, currentTournamentPlayer];
      };

      const updateTournamentList = (records) => {
        const source = ensureArray(records);
        let matched = false;
        const nextRecords = source.map((record) => {
          if (String(record?._id || "").trim() !== normalizedTournamentId) return record;
          matched = true;
          return {
            ...record,
            users: appendCurrentUser(record?.users),
          };
        });

        if (!matched && fallbackRecord) {
          nextRecords.unshift({
            ...fallbackRecord,
            users: appendCurrentUser(fallbackRecord?.users),
          });
        }

        return nextRecords;
      };

      setTournaments((prev) => {
        const nextRecords = updateTournamentList(prev);
        applyFilter(nextRecords, filter);
        return nextRecords;
      });
      setTournamentWaitingRecord((prev) => {
        if (String(prev?._id || "").trim() === normalizedTournamentId) {
          return {
            ...prev,
            users: appendCurrentUser(prev?.users),
          };
        }

        if (fallbackRecord && String(fallbackRecord?._id || "").trim() === normalizedTournamentId) {
          return {
            ...fallbackRecord,
            users: appendCurrentUser(fallbackRecord?.users),
          };
        }

        return prev;
      });
      setLiveMatchesByTournamentId((prev) => {
        const existingRoom = prev[normalizedTournamentId] || {};
        const currentPlayers = ensureArray(existingRoom?.players);
        const nextPlayers = appendCurrentUser(currentPlayers);
        return {
          ...prev,
          [normalizedTournamentId]: {
            ...existingRoom,
            players: nextPlayers,
            connectedPlayers: Math.max(
              nextPlayers.length,
              Number(existingRoom?.connectedPlayers) || 0
            ),
          },
        };
      });
      setOptimisticTournamentPlayersById((prev) => {
        const currentPlayers = ensureArray(prev[normalizedTournamentId]);
        return {
          ...prev,
          [normalizedTournamentId]: appendCurrentUser(currentPlayers),
        };
      });
    },
    [applyFilter, currentTournamentPlayer, displayName, filter]
  );
  const buildTournamentUserIdentity = useCallback((entry, index = 0) => {
    if (typeof entry === "string") {
      return String(entry).trim().toLowerCase() || `index:${index}`;
    }

    const loginIdentifier = String(entry?.loginIdentifier || "")
      .trim()
      .toLowerCase();
    const username = String(entry?.username || entry?.userName || "")
      .trim()
      .toLowerCase();
    const normalizedUserId = String(entry?.userIdNormalized || "")
      .trim()
      .toLowerCase();
    const userId = String(entry?.userId || entry?._id || entry?.id || "")
      .trim()
      .toLowerCase();
    const email = String(entry?.email || "")
      .trim()
      .toLowerCase();
    const display = extractPrimaryName(
      entry?.displayName || entry?.name || entry?.firstName || entry?.email || entry?.userId
    ).toLowerCase();

    return loginIdentifier || username || normalizedUserId || userId || email || display || `index:${index}`;
  }, []);
  const addOptimisticTournamentUser = useCallback(
    (tournamentId, rawUser) => {
      const normalizedTournamentId = String(tournamentId || "").trim();
      if (!normalizedTournamentId) return;
      if (rawUser === null || rawUser === undefined) return;

      const normalizedUser = typeof rawUser === "string" ? String(rawUser).trim() : rawUser;
      if (!normalizedUser) return;

      setOptimisticTournamentPlayersById((prev) => {
        const currentUsers = ensureArray(prev[normalizedTournamentId]);
        const incomingIdentity = buildTournamentUserIdentity(normalizedUser, currentUsers.length);
        if (!incomingIdentity) return prev;

        const alreadyExists = currentUsers.some(
          (entry, index) => buildTournamentUserIdentity(entry, index) === incomingIdentity
        );
        if (alreadyExists) return prev;

        return {
          ...prev,
          [normalizedTournamentId]: [...currentUsers, normalizedUser],
        };
      });
    },
    [buildTournamentUserIdentity]
  );
  const removeOptimisticTournamentUser = useCallback(
    (tournamentId, rawUser) => {
      const normalizedTournamentId = String(tournamentId || "").trim();
      if (!normalizedTournamentId) return;

      const normalizedUser = typeof rawUser === "string" ? String(rawUser).trim() : rawUser;
      if (!normalizedUser) return;
      const identityToRemove = buildTournamentUserIdentity(normalizedUser);
      if (!identityToRemove) return;

      setOptimisticTournamentPlayersById((prev) => {
        const currentUsers = ensureArray(prev[normalizedTournamentId]);
        if (currentUsers.length === 0) return prev;

        const nextUsers = currentUsers.filter(
          (entry, index) => buildTournamentUserIdentity(entry, index) !== identityToRemove
        );
        if (nextUsers.length === currentUsers.length) return prev;

        return {
          ...prev,
          [normalizedTournamentId]: nextUsers,
        };
      });
    },
    [buildTournamentUserIdentity]
  );

  const fetchTournaments = useCallback(async () => {
    try {
      const response = await axios.get("/tournaments");
      const data = ensureArray(response.data);
      setTournaments(data);
      applyFilter(data, filter);
    } catch (error) {
      console.error("Error fetching tournaments:", error);
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        notification.error({
          message: i18nT("lobby.sessionError"),
          description: i18nT("lobby.yourSessionExpiredPleaseLogInAgain"),
        });
      }
    }
  }, [applyFilter, filter]);
  useEffect(() => {
    fetchTournamentsRef.current = fetchTournaments;
  }, [fetchTournaments]);
  const fetchModeWinnersLeaderboard = useCallback(async (silent = true) => {
    if (!silent) setModeWinnersLoading(true);
    try {
      const response = await axios.get("/tournaments/leaderboard/modes");
      const incomingModes = response?.data?.modes || {};
      setModeWinnersByMode(() => {
        const next = buildEmptyModeWinnersMap();
        MODE_WINNERS_SECTION_ORDER.forEach((section) => {
          const players = ensureArray(incomingModes?.[section.key]).map((entry, index) => ({
            rank: Math.max(1, Number(entry?.rank) || index + 1),
            userId: String(entry?.userId || "").trim().toLowerCase(),
            id: String(entry?.id || "").trim(),
            _id: String(entry?._id || "").trim(),
            identity: String(entry?.identity || "").trim(),
            userIdNormalized: String(entry?.userIdNormalized || "").trim(),
            loginIdentifier: String(entry?.loginIdentifier || "").trim(),
            email: String(entry?.email || "").trim(),
            username: String(entry?.username || "").trim(),
            userName: String(entry?.userName || "").trim(),
            displayName:
              extractPrimaryName(entry?.displayName || entry?.firstName || entry?.email) ||
              "Jugador",
            playerCode: String(entry?.playerCode || "").trim().toUpperCase(),
            avatarUrl: resolveAvatarSrc(entry?.avatarUrl || entry?.avatar || entry?.image || ""),
            wins: Math.max(0, Number(entry?.wins) || 0),
            cimaxScore: Math.max(0, Math.round(Number(entry?.cimaxScore) || 0)),
          }));
          next[section.key] = players.slice(0, 3);
        });
        return next;
      });
    } catch (error) {
      console.log(error);
      if (!silent) {
        notification.error({
          message: "Error",
          description:
            error?.response?.data?.message ||
            "No se pudo cargar la lista de mejores jugadores.",
        });
      }
    } finally {
      if (!silent) setModeWinnersLoading(false);
    }
  }, []);
  const fetchPuzzles = useCallback(async (silent = false) => {
    if (!silent) setPuzzlesLoading(true);
    try {
      const response = await axios.get("/tournaments/puzzles");
      const items = toOrderedPuzzleEntries(response?.data?.puzzles);
      setPuzzles(items);
    } catch (error) {
      console.log(error);
      if (!silent) {
        notification.error({
          message: "Error",
          description:
            error?.response?.data?.message || "No se pudo cargar la lista de puzzles.",
        });
      }
      setPuzzles([]);
    } finally {
      if (!silent) setPuzzlesLoading(false);
    }
  }, []);
  useEffect(() => {
    fetchPuzzlesRef.current = fetchPuzzles;
  }, [fetchPuzzles]);
  const fetchLiveMatches = useCallback(async () => {
    try {
      const response = await axios.get("/tournaments/live");
      const liveMatches = ensureArray(response?.data);
      const nextMap = {};

      liveMatches.forEach((entry) => {
        const tournamentId = String(entry?.tournamentId || "").trim();
        if (!tournamentId) return;

        // The server sends each player as { userId, displayName, isBot, connected }.
        const livePlayers = ensureArray(entry?.players)
          .map((player) => {
            if (player && typeof player === "object") {
              if (player.isBot) return "";
              return String(player.userId || player.loginIdentifier || player.email || "").trim();
            }
            return String(player || "").trim();
          })
          .filter((player) => player && !isStringifiedObjectText(player));
        const livePlayerSet = new Set(livePlayers);
        const connectedPlayers = Math.max(
          0,
          Number(entry?.connectedPlayers) || livePlayerSet.size || livePlayers.length
        );
        const previous = nextMap[tournamentId] || { players: [] };
        const mergedPlayers = [...previous.players];
        livePlayers.forEach((player) => {
          const exists = mergedPlayers.some(
            (current) => String(current || "").trim().toLowerCase() === player.toLowerCase()
          );
          if (!exists) mergedPlayers.push(player);
        });

        nextMap[tournamentId] = {
          connectedPlayers: Math.max(connectedPlayers, Number(previous.connectedPlayers) || 0),
          players: mergedPlayers,
        };
      });

      setLiveMatchesByTournamentId(nextMap);
    } catch (error) {
      console.log(error);
      setLiveMatchesByTournamentId({});
    }
  }, []);
  useEffect(() => {
    fetchLiveMatchesRef.current = fetchLiveMatches;
  }, [fetchLiveMatches]);

  const fetchHistory = useCallback(async () => {
    const fetchHistoryPage = async (page) => {
      const pageResponse = await axios.get("/tournaments/history/me", {
        params: { page, pageSize: HISTORY_PAGE_SIZE },
      });
      return {
        matches: ensureArray(pageResponse?.data?.matches),
        total: Number(pageResponse?.data?.total),
        pageSize: Number(pageResponse?.data?.pageSize) || HISTORY_PAGE_SIZE,
      };
    };

    try {
      setHistoryLoading(true);

      if (!historyShowAllMatches) {
        const response = await fetchHistoryPage(historyPage);
        const matches = response.matches;
        const totalRaw = response.total;
        const hasServerTotal = Number.isFinite(totalRaw) && totalRaw >= 0;

        if (hasServerTotal) {
          setHistoryMatches(matches);
          setHistoryTotalMatches(Math.max(matches.length, totalRaw));
          return;
        }

        const legacyResponse = await axios.get("/tournaments/history/me", {
          params: { limit: HISTORY_LEGACY_FETCH_LIMIT },
        });
        const legacyMatches = ensureArray(legacyResponse?.data?.matches);
        const start = Math.max(0, (historyPage - 1) * HISTORY_PAGE_SIZE);
        setHistoryMatches(legacyMatches.slice(start, start + HISTORY_PAGE_SIZE));
        setHistoryTotalMatches(legacyMatches.length);
        return;
      }

      const firstPage = await fetchHistoryPage(1);
      const firstPageMatches = firstPage.matches;
      const totalRaw = firstPage.total;
      const hasServerTotal = Number.isFinite(totalRaw) && totalRaw >= 0;

      const allMatches = [];
      const addMatchEntries = (entries) => {
        if (Array.isArray(entries)) {
          allMatches.push(...entries);
        }
      };

      addMatchEntries(firstPageMatches);

      if (hasServerTotal) {
        const safeTotal = Math.max(totalRaw, firstPageMatches.length);
        const totalPages = Math.max(1, Math.ceil(safeTotal / firstPage.pageSize));
        for (let page = 2; page <= totalPages; page += 1) {
          const pagePayload = await fetchHistoryPage(page);
          addMatchEntries(pagePayload.matches);
        }
        setHistoryMatches(allMatches);
        setHistoryTotalMatches(allMatches.length);
        return;
      }

      for (let page = 2; ; page += 1) {
        const pagePayload = await fetchHistoryPage(page);
        addMatchEntries(pagePayload.matches);
        if (!pagePayload.matches.length || pagePayload.matches.length < HISTORY_PAGE_SIZE) {
          break;
        }
      }

      setHistoryMatches(allMatches);
      setHistoryTotalMatches(allMatches.length);
    } catch (error) {
      console.error("Error fetching match history:", error);
      notification.error({
        message: i18nT("lobby.historyError"),
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Could not load match history.",
      });
      setHistoryMatches([]);
      setHistoryTotalMatches(0);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPage, historyShowAllMatches]);

  const fetchOnlineFriends = useCallback(async (silent = false) => {
    if (!silent) {
      setOnlineFriendsLoading(true);
    }
    try {
      const response = await axios.get("/friends/list");
      const allFriends = ensureArray(response?.data?.friends);
      const connected = allFriends.filter((item) => {
        const status = String(item?.status || "")
          .trim()
          .toLowerCase();
        return status === "online" || status === "in_match";
      });
      setTotalFriendsCount(allFriends.length);
      setOnlineFriends(connected);
    } catch (error) {
      if (!silent) {
        notification.error({
          message: "Error",
          description:
            error?.response?.data?.message || "No se pudieron cargar los amigos online.",
        });
      }
    } finally {
      if (!silent) {
        setOnlineFriendsLoading(false);
      }
    }
  }, []);

  const loadAdminData = useCallback(
    async (passwordOverride = "") => {
      const resolvedPassword = String(passwordOverride || adminPassword || "").trim();
      const payload = resolvedPassword ? { password: resolvedPassword } : {};

      const [roomsResponse, usersResponse, puzzlesResponse, ordersResponse] = await Promise.all([
        axios.post("/tournaments/admin/rooms", payload),
        axios.post("/tournaments/admin/users", payload),
        axios.post("/tournaments/admin/puzzles", payload),
        axios.get("/admin/orders"),
      ]);

      let independentChallengesResponse = null;
      try {
        independentChallengesResponse = await axios.post(
          "/tournaments/admin/independent-challenges",
          payload
        );
      } catch (error) {
        console.warn("Independent challenges admin endpoint not available", error);
      }

      const rooms = ensureArray(roomsResponse?.data?.rooms).sort(
        (left, right) => Number(right?.startedAt || 0) - Number(left?.startedAt || 0)
      );
      const users = ensureArray(usersResponse?.data?.users).sort((left, right) => {
        const leftCreatedAt = new Date(left?.createdAt || 0).valueOf();
        const rightCreatedAt = new Date(right?.createdAt || 0).valueOf();

        if (rightCreatedAt !== leftCreatedAt) {
          return rightCreatedAt - leftCreatedAt;
        }

        // Fallback for legacy users without createdAt: ObjectId preserves insertion order.
        return String(right?.id || right?._id || "").localeCompare(
          String(left?.id || left?._id || "")
        );
      });
      const puzzles = ensureArray(puzzlesResponse?.data?.puzzles).sort(
        (left, right) =>
          new Date(right?.createdAt || 0).valueOf() - new Date(left?.createdAt || 0).valueOf()
      );
      const independentChallenges = ensureArray(
        independentChallengesResponse?.data?.independentChallenges || []
      ).sort(
        (left, right) =>
          new Date(right?.createdAt || 0).valueOf() - new Date(left?.createdAt || 0).valueOf()
      );
      const orders = ensureArray(ordersResponse?.data?.orders || []).sort(
        (left, right) =>
          new Date(right?.purchaseDate || right?.createdAt || 0).valueOf() -
          new Date(left?.purchaseDate || left?.createdAt || 0).valueOf()
      );

      setAdminRooms(rooms);
      setAdminUsers(users);
      setAdminPuzzles(puzzles);
      setAdminIndependentChallenges(independentChallenges);
      setAdminOrders(orders);
      return { rooms, users, puzzles, independentChallenges, orders };
    },
    [adminPassword]
  );

  const handleAdminLogin = useCallback(async () => {
    const resolvedPassword = String(adminPasswordInput || "").trim();
    if (!resolvedPassword) {
      notification.error({
        message: "Administrador",
        description: i18nT("lobby.ingresaLaContrasenaParaAcceder"),
      });
      return;
    }

    try {
      setAdminLoading(true);
      await loadAdminData(resolvedPassword);
      setAdminPassword(resolvedPassword);
      setAdminEnabled(true);
      setAdminModalOpen(false);
      notification.success({
        message: i18nT("lobby.administradorActivo"),
        description: i18nT("lobby.panelAdminHabilitadoEnNoticias"),
      });
    } catch (error) {
      notification.error({
        message: "Administrador",
        description:
          error?.response?.data?.message || error?.message || "No se pudo abrir el panel admin.",
      });
    } finally {
      setAdminLoading(false);
    }
  }, [adminPasswordInput, loadAdminData]);

  const handleAdminRefresh = useCallback(async () => {
    try {
      setAdminLoading(true);
      await loadAdminData();
      await fetchTournaments();
      notification.success({
        message: "Administrador",
        description: i18nT("lobby.panelAdminActualizado"),
      });
    } catch (error) {
      notification.error({
        message: "Administrador",
        description:
          error?.response?.data?.message || error?.message || "No se pudo actualizar el panel.",
      });
    } finally {
      setAdminLoading(false);
    }
  }, [fetchTournaments, loadAdminData]);

  useEffect(() => {
    let cancelled = false;

    const syncDirectAdminAccess = async () => {
      if (!hasDirectAdminAccess) {
        setAdminEnabled(false);
        setAdminPassword("");
        setAdminPasswordInput("");
        setAdminRooms([]);
        setAdminUsers([]);
        setAdminPuzzles([]);
        setAdminIndependentChallenges([]);
        setAdminOrders([]);
        setAdminOrderDetail(null);
        setAdminModalOpen(false);
        return;
      }

      try {
        setAdminLoading(true);
        await loadAdminData("");
        if (cancelled) return;
        setAdminEnabled(true);
        setAdminPassword("");
        setAdminPasswordInput("");
        setAdminModalOpen(false);
      } catch (error) {
        if (cancelled) return;
        setAdminEnabled(false);
        setAdminRooms([]);
        setAdminUsers([]);
        setAdminPuzzles([]);
        setAdminIndependentChallenges([]);
        setAdminOrders([]);
        setAdminOrderDetail(null);
        console.error("Direct admin access error:", error);
      } finally {
        if (!cancelled) setAdminLoading(false);
      }
    };

    syncDirectAdminAccess().catch((error) => {
      console.error("Direct admin bootstrap error:", error);
    });

    return () => {
      cancelled = true;
    };
  }, [hasDirectAdminAccess, loadAdminData]);

  useEffect(() => {
    if (!hasDirectAdminAccess || !adminEnabled || !isAdminTab) return undefined;

    const autoRefreshTimer = window.setInterval(() => {
      loadAdminData().catch((error) => {
        console.log("Admin auto-refresh error:", error);
      });
    }, 30000);

    return () => window.clearInterval(autoRefreshTimer);
  }, [adminEnabled, hasDirectAdminAccess, isAdminTab, loadAdminData]);

  const handleAdminDeleteRoom = useCallback(
    async (roomRecord) => {
      const tournamentId = String(roomRecord?.tournamentId || "").trim();
      const roomId = String(roomRecord?.roomId || "").trim();
      const isVirtualRoom = roomRecord?.isVirtualRoom === true;
      if (!tournamentId || !roomId) return;

      const accepted = window.confirm(
        isVirtualRoom
          ? `Eliminar torneo ${tournamentId}? Esta accion no se puede deshacer.`
          : `Eliminar sala ${roomId} del torneo ${tournamentId}? Esta accion no se puede deshacer.`
      );
      if (!accepted) return;

      const operationKey = `room:${tournamentId}:${roomId}`;
      try {
        setAdminActionKey(operationKey);
        await axios.post("/tournaments/admin/rooms/delete", {
          password: adminPassword,
          tournamentId,
          roomId,
        });
        await loadAdminData();
        await fetchTournaments();
        notification.success({
          message: isVirtualRoom ? "Torneo eliminado" : "Sala eliminada",
          description: isVirtualRoom
            ? `Torneo ${tournamentId} eliminado correctamente.`
            : `Sala ${roomId} eliminada correctamente.`,
        });
      } catch (error) {
        notification.error({
          message: isVirtualRoom ? "Error eliminando torneo" : "Error eliminando sala",
          description:
            error?.response?.data?.message ||
            error?.message ||
            (isVirtualRoom
              ? "No se pudo eliminar el torneo."
              : "No se pudo eliminar la sala."),
        });
      } finally {
        setAdminActionKey("");
      }
    },
    [adminPassword, fetchTournaments, loadAdminData]
  );

  const handleAdminPurgeRooms = useCallback(async () => {
    const accepted = window.confirm(
      i18nT("lobby.eliminarTodasLasSalasActivasAhoraEstaAccion")
    );
    if (!accepted) return;

    try {
      setAdminActionKey("rooms:purge");
      await axios.post("/tournaments/admin/rooms/purge", {
        password: adminPassword,
      });
      await loadAdminData();
      await fetchTournaments();
      notification.success({
        message: i18nT("lobby.salasEliminadas"),
        description: i18nT("lobby.todasLasSalasActivasFueronEliminadas"),
      });
    } catch (error) {
      notification.error({
        message: i18nT("lobby.errorAlResetearSalas"),
        description:
          error?.response?.data?.message || error?.message || "No se pudieron eliminar las salas.",
      });
    } finally {
      setAdminActionKey("");
    }
  }, [adminPassword, fetchTournaments, loadAdminData]);

  const handleAdminResetUserPassword = useCallback(
    async (userRecord) => {
      const targetEmail = String(userRecord?.email || "").trim().toLowerCase();
      if (!targetEmail) return;

      const rawNewPassword = window.prompt(
        `Escribe la nueva contrasena temporal para ${targetEmail} (minimo 6 caracteres):`,
        ""
      );
      if (rawNewPassword === null) return;

      const nextPassword = String(rawNewPassword || "").trim();
      if (nextPassword.length < 6) {
        notification.error({
          message: i18nT("lobby.contrasenaInvalida"),
          description: i18nT("lobby.laNuevaContrasenaDebeTenerAlMenosCaracteres"),
        });
        return;
      }

      const accepted = window.confirm(
        `Confirmar restablecimiento de contrasena para ${targetEmail}? Se cerrara su sesion actual.`
      );
      if (!accepted) return;

      const operationKey = `user:reset:${targetEmail}`;
      try {
        setAdminActionKey(operationKey);
        await axios.post("/tournaments/admin/users/reset-password", {
          password: adminPassword,
          userId: String(userRecord?.id || "").trim(),
          email: targetEmail,
          newPassword: nextPassword,
        });
        await loadAdminData();
        notification.success({
          message: i18nT("lobby.contrasenaRestablecida"),
          description:
            i18nT("lobby.laNuevaContrasenaFueGuardadaComparteEstaContd0cd7f"),
        });
      } catch (error) {
        notification.error({
          message: i18nT("lobby.errorRestableciendoContrasena"),
          description:
            error?.response?.data?.message ||
            error?.message ||
            "No se pudo restablecer la contrasena.",
        });
      } finally {
        setAdminActionKey("");
      }
    },
    [adminPassword, loadAdminData]
  );

  // Premium por usuario: video MP4 y análisis de partidas sin límite.
  const handleAdminSetUserPremium = useCallback(
    async (userRecord, isPremium) => {
      const userId = String(userRecord?.id || "").trim();
      if (!userId) return;
      const operationKey = `user:premium:${userId}`;
      try {
        setAdminActionKey(operationKey);
        await axios.post("/tournaments/admin/users/premium", {
          password: adminPassword,
          userId,
          isPremium: Boolean(isPremium),
        });
        setAdminUsers((prev) =>
          ensureArray(prev).map((entry) =>
            String(entry?.id || "") === userId ? { ...entry, isPremium: Boolean(isPremium) } : entry
          )
        );
        notification.success({
          message: isPremium ? "Premium activado" : "Premium quitado",
          description: `${String(userRecord?.email || userRecord?.firstName || "Usuario")}: ${
            isPremium ? "puede descargar videos y analizar sin límite." : "vuelve a los límites normales."
          }`,
        });
      } catch (error) {
        notification.error({
          message: "No se pudo cambiar Premium",
          description: error?.response?.data?.message || error?.message || "Inténtalo otra vez.",
        });
      } finally {
        setAdminActionKey("");
      }
    },
    [adminPassword]
  );

  const handleAdminDeleteUser = useCallback(
    async (userRecord) => {
      const targetEmail = String(userRecord?.email || "").trim().toLowerCase();
      if (!targetEmail) return;

      if (targetEmail === currentUserEmailNormalized) {
        notification.warning({
          message: "Administrador",
          description: i18nT("lobby.noPuedesEliminarLaCuentaConLaQue"),
        });
        return;
      }

      const accepted = window.confirm(
        `Eliminar la cuenta ${targetEmail}? Se removera de salas y amigos.`
      );
      if (!accepted) return;

      const operationKey = `user:${targetEmail}`;
      try {
        setAdminActionKey(operationKey);
        await axios.post("/tournaments/admin/users/delete", {
          password: adminPassword,
          userId: String(userRecord?.id || "").trim(),
          email: targetEmail,
        });
        await loadAdminData();
        await fetchTournaments();
        notification.success({
          message: i18nT("lobby.cuentaEliminada"),
          description: `La cuenta ${targetEmail} fue eliminada.`,
        });
      } catch (error) {
        notification.error({
          message: i18nT("lobby.errorEliminandoCuenta"),
          description:
            error?.response?.data?.message || error?.message || "No se pudo eliminar la cuenta.",
        });
      } finally {
        setAdminActionKey("");
      }
    },
    [adminPassword, currentUserEmailNormalized, fetchTournaments, loadAdminData]
  );

  const handleAdminDeletePuzzle = useCallback(
    async (puzzleRecord) => {
      const puzzleId = String(
        puzzleRecord?.id || puzzleRecord?._id || puzzleRecord?.tournamentId || ""
      ).trim();
      if (!puzzleId) return;

      const puzzleName = getLocalizedTournamentName(puzzleRecord?.tournamentName, "Puzzle");
      const accepted = window.confirm(
        `Eliminar puzzle "${puzzleName}" (#${puzzleId.slice(-6)})? Esta accion no se puede deshacer.`
      );
      if (!accepted) return;

      const operationKey = `puzzle:${puzzleId}`;
      try {
        setAdminActionKey(operationKey);
        await axios.post("/tournaments/admin/puzzles/delete", {
          password: adminPassword,
          puzzleId,
        });
        await loadAdminData();
        await fetchPuzzles(true);
        notification.success({
          message: i18nT("lobby.puzzleEliminado"),
          description: `El puzzle "${puzzleName}" fue eliminado.`,
        });
      } catch (error) {
        notification.error({
          message: i18nT("lobby.errorEliminandoPuzzle"),
          description:
            error?.response?.data?.message || error?.message || "No se pudo eliminar el puzzle.",
        });
      } finally {
        setAdminActionKey("");
      }
    },
    [adminPassword, fetchPuzzles, getLocalizedTournamentName, loadAdminData]
  );

  const handleAdminDeleteIndependentChallenge = useCallback(
    async (challengeRecord) => {
      const challengeId = String(challengeRecord?.id || challengeRecord?._id || "").trim();
      if (!challengeId) return;

      const challengeName = getLocalizedTournamentName(
        challengeRecord?.tournamentName,
        "Desafío Independiente"
      );
      const accepted = window.confirm(
        `Eliminar desafio independiente "${challengeName}" (#${challengeId.slice(-6)})? Esta accion no se puede deshacer.`
      );
      if (!accepted) return;

      const operationKey = `independent-challenge:${challengeId}`;
      try {
        setAdminActionKey(operationKey);
        await axios.post("/tournaments/admin/tournaments/delete", {
          password: adminPassword,
          tournamentId: challengeId,
        });
        await loadAdminData();
        await fetchTournaments();
        notification.success({
          message: i18nT("lobby.desafioEliminado"),
          description: `El desafío "${challengeName}" fue eliminado.`,
        });
      } catch (error) {
        notification.error({
          message: i18nT("lobby.errorEliminandoDesafio"),
          description:
            error?.response?.data?.message ||
            error?.message ||
            "No se pudo eliminar el desafío independiente.",
        });
      } finally {
        setAdminActionKey("");
      }
    },
    [adminPassword, fetchTournaments, getLocalizedTournamentName, loadAdminData]
  );
  const handleDeleteTournamentFromLivePanel = useCallback(
    async (tournamentRecord) => {
      const tournamentId = String(
        tournamentRecord?._id || tournamentRecord?.id || tournamentRecord?.tournamentId || ""
      ).trim();
      if (!tournamentId) return;

      if (!canDeleteTournamentFromLivePanel) {
        notification.warning({
          message: i18nT("lobby.accesoRestringido"),
          description:
            i18nT("lobby.soloAdministradoresConPestanaAdminActivaYSup241226"),
        });
        return;
      }

      const tournamentName = getLocalizedTournamentName(
        tournamentRecord?.tournamentName,
        "Torneo"
      );
      const accepted = window.confirm(
        `Eliminar torneo "${tournamentName}" (#${tournamentId.slice(-6)})? Esta accion no se puede deshacer.`
      );
      if (!accepted) return;

      const operationKey = `live-tournament:${tournamentId}`;
      try {
        setAdminActionKey(operationKey);
        await axios.post("/tournaments/admin/tournaments/delete", {
          password: adminPassword,
          tournamentId,
        });
        await Promise.all([fetchTournaments(), fetchLiveMatches()]);
        if (hasDirectAdminAccess) {
          await loadAdminData().catch(() => null);
        }
        notification.success({
          message: i18nT("lobby.torneoEliminado"),
          description: `El torneo "${tournamentName}" fue eliminado.`,
        });
      } catch (error) {
        notification.error({
          message: i18nT("lobby.errorEliminandoTorneo"),
          description:
            error?.response?.data?.message || error?.message || "No se pudo eliminar el torneo.",
        });
      } finally {
        setAdminActionKey("");
      }
    },
    [
      adminPassword,
      canDeleteTournamentFromLivePanel,
      fetchLiveMatches,
      fetchTournaments,
      getLocalizedTournamentName,
      hasDirectAdminAccess,
      loadAdminData,
    ]
  );

  const clearReplaySession = useCallback(async () => {
    try {
      await axios.post("/replays/clear");
    } catch (error) {
      console.log(error);
    }
  }, []);

  const clearReplayAwaitingFallbackTimer = useCallback(() => {
    if (replayAwaitingFallbackTimerRef.current) {
      clearTimeout(replayAwaitingFallbackTimerRef.current);
      replayAwaitingFallbackTimerRef.current = null;
    }
  }, []);

  const clearReplayReadyWaiter = useCallback((fallbackValue = false) => {
    const waiter = replayReadyWaiterRef.current;
    replayReadyWaiterRef.current = null;
    if (!waiter) return;
    if (waiter.timer) clearTimeout(waiter.timer);
    if (typeof waiter.resolve === "function") {
      waiter.resolve(fallbackValue === true);
    }
  }, []);

  const waitForReplayReadySignal = useCallback(
    (matchId, timeoutMs) => {
      const normalizedMatchId = String(matchId || "").trim();
      if (!normalizedMatchId) return Promise.resolve(false);

      clearReplayReadyWaiter(false);

      return new Promise((resolve) => {
        const timeout = Math.max(1000, Number(timeoutMs) || REPLAY_READY_TIMEOUT_MS);
        const timer = setTimeout(() => {
          const waiter = replayReadyWaiterRef.current;
          if (!waiter || waiter.matchId !== normalizedMatchId) return;
          replayReadyWaiterRef.current = null;
          resolve(false);
        }, timeout);

        replayReadyWaiterRef.current = {
          matchId: normalizedMatchId,
          resolve,
          timer,
        };
      });
    },
    [clearReplayReadyWaiter]
  );

  const setReplayAwaitingStep = useCallback((nextValue) => {
    const next = nextValue === true;
    replayAwaitingStepRef.current = next;
    setReplayAwaitingStepState(next);
  }, []);

  // «Hazte Premium» desde la repetición: se cierra la repetición y se abre la página Premium.
  const replayGoPremiumRef = useRef(null);
  const handleReplayGoPremium = useCallback(() => {
    if (typeof replayGoPremiumRef.current === "function") replayGoPremiumRef.current();
    navigate("/game/premium");
  }, [navigate]);

  const handleReplayClose = useCallback(() => {
    setReplayReview(null);
    setReplayReviewActive(false);
    setReplayArrowsActive(false);
    setReplayArrowColor("blue");
    replayAnalysisActiveRef.current = false;
    setReplayAnalysisActive(false);
    setReplayAnalysisUndoCount(0);
    replayTickingRef.current = false;
    replayPendingStepAdvanceRef.current = null;
    replayIframeBootstrappedKeyRef.current = "";
    clearReplayReadyWaiter(false);
    setReplayAwaitingStep(false);
    clearReplayAwaitingFallbackTimer();
    clearReplaySession().catch((error) => console.log(error));
    setReplayPlaying(false);
    setReplayReady(false);
    setReplayModalOpen(false);
    setReplayFrameNonce((prev) => Number(prev) + 1);
    setReplaySelectedMatch(null);
    setReplayTimeline([]);
    setReplayCurrentStep(0);
    setReplayTotalSteps(0);
    setReplayInfo(null);
    setReplayRestarting(false);
    setReplayLoading(false);
    // Repetición abierta desde el Campeonato o el Historial: al salir se vuelve allí.
    if (location.state?.replayReturnTo) {
      navigate(location.state.replayReturnTo, { replace: true });
    } else if (location.state?.replayActive) {
      navigate(location.pathname, { replace: true });
    }
  }, [
    clearReplayAwaitingFallbackTimer,
    clearReplayReadyWaiter,
    clearReplaySession,
    location.state,
    navigate,
    setReplayAwaitingStep,
  ]);
  replayGoPremiumRef.current = handleReplayClose;

  const clampReplayStep = useCallback(
    (value, totalOverride = null) => {
      const maxSteps = Number.isFinite(Number(totalOverride))
        ? Math.max(0, Number(totalOverride))
        : Math.max(0, Number(replayTotalSteps) || 0);
      const parsed = Math.floor(Number(value) || 0);
      if (!Number.isFinite(parsed)) return 0;
      return Math.max(0, Math.min(maxSteps, parsed));
    },
    [replayTotalSteps]
  );

  const handleReplayLoadToStep = useCallback(
    async (matchId, toStep, options = {}) => {
      if (!matchId) return 0;
      let lastError = null;
      replayPendingStepAdvanceRef.current = null;
      const numericTargetStep = Math.max(0, Number(toStep) || 0);
      const bootWaitMs = Math.max(0, Number(options?.bootWaitMs) || 0);
      const readyTimeoutMs = Math.max(
        1000,
        Number(options?.readyTimeoutMs) ||
          (isReplayMobileViewport ? REPLAY_MOBILE_READY_TIMEOUT_MS : REPLAY_READY_TIMEOUT_MS)
      );
      if (bootWaitMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, bootWaitMs));
      }
      for (let attempt = 0; attempt < 6; attempt += 1) {
        const readyPromise = waitForReplayReadySignal(matchId, readyTimeoutMs);
        try {
          setReplayReady(false);
          const response = await axios.post(`/replays/${matchId}/load`, {
            toStep: numericTargetStep,
          });
          const applied = Number(response?.data?.appliedSteps) || 0;
          const total = Number(response?.data?.totalSteps) || 0;
          clearReplayAwaitingFallbackTimer();
          setReplayAwaitingStep(false);
          setReplayCurrentStep(applied);
          setReplayTotalSteps(total);
          const unityReady = await readyPromise;
          if (!unityReady) {
            console.warn("[REPLAY] Unity did not send XIMA_REPLAY_READY before timeout.");
          }
          setReplayReady(true);
          return applied;
        } catch (error) {
          clearReplayReadyWaiter(false);
          lastError = error;
          const status = Number(error?.response?.status);
          if (status === 409 && attempt < 5) {
            await new Promise((resolve) => setTimeout(resolve, 300));
            continue;
          }
          throw error;
        }
      }

      if (lastError) throw lastError;
      return 0;
    },
    [
      clearReplayAwaitingFallbackTimer,
      clearReplayReadyWaiter,
      isReplayMobileViewport,
      setReplayAwaitingStep,
      waitForReplayReadySignal,
    ]
  );

  const handleReplayEmitStep = useCallback(async (matchId, index) => {
    if (!matchId) return -1;
    let lastError = null;
    const numericIndex = Math.max(0, Math.floor(Number(index) || 0));
    // «Analizar partida»: la orden de vestir el bloque va ANTES de la jugada, así el bloque
    // sale vestido desde que empieza a moverse.
    const reviewMove = (replayReviewMovesRef.current || []).find(
      (move) => Number(move?.step) === numericIndex + 1
    );
    if (reviewMove && reviewMove.kind && reviewMove.played) {
      postReplayEventsToIframe([
        { eventName: "REPLAY_BEST_ARROW", payload: reviewArrowPayload(reviewMove) },
        {
          eventName: "REPLAY_MOVE_BADGE",
          payload: `${reviewMove.playerIndex}|${reviewMove.cubeIndex}|${reviewMove.played.to}|${reviewMove.kind}|${reviewMove.step}`,
        },
      ]);
    }
    for (let attempt = 0; attempt < 6; attempt += 1) {
      try {
        const response = await axios.post(`/replays/${matchId}/step`, {
          index: numericIndex,
        });
        const emittedIndex = Number(response?.data?.index);
        postReplayEventsToIframe(response?.data?.events);
        if (Number.isFinite(emittedIndex)) return emittedIndex;
        return numericIndex;
      } catch (error) {
        lastError = error;
        const status = Number(error?.response?.status);
        if (status === 409 && attempt < 5) {
          await new Promise((resolve) => setTimeout(resolve, 300));
          continue;
        }
        throw error;
      }
    }

    if (lastError) throw lastError;
    return numericIndex;
  }, [postReplayEventsToIframe]);

  const waitForMobileReplayIframeReady = useCallback(async () => {
    if (!isReplayMobileViewport || typeof window === "undefined") return;

    const wait = (durationMs) =>
      new Promise((resolve) => window.setTimeout(resolve, Math.max(0, durationMs)));
    const waitForPaint = () =>
      new Promise((resolve) => {
        if (typeof window.requestAnimationFrame !== "function") {
          window.setTimeout(resolve, 0);
          return;
        }
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(resolve);
        });
      });
    const isIframeDocumentReady = (iframeElement) => {
      if (!iframeElement) return false;
      try {
        const readyState = String(iframeElement.contentDocument?.readyState || "");
        return readyState === "interactive" || readyState === "complete";
      } catch (error) {
        return false;
      }
    };

    await waitForPaint();
    let iframeElement = replayIframeRef.current;
    if (!iframeElement) {
      await wait(350);
      iframeElement = replayIframeRef.current;
    }
    if (!iframeElement) return;

    if (!isIframeDocumentReady(iframeElement)) {
      await new Promise((resolve) => {
        let resolved = false;
        const finish = () => {
          if (resolved) return;
          resolved = true;
          iframeElement.removeEventListener("load", finish);
          resolve();
        };
        iframeElement.addEventListener("load", finish);
        window.setTimeout(finish, REPLAY_MOBILE_IFRAME_READY_WAIT_MS);
      });
    }

    await wait(REPLAY_MOBILE_IFRAME_LOAD_BOOT_WAIT_MS);
  }, [isReplayMobileViewport]);

  useEffect(() => {
    return () => {
      clearReplayAwaitingFallbackTimer();
      clearReplayReadyWaiter(false);
      clearReplaySession().catch((error) => console.log(error));
    };
  }, [clearReplayAwaitingFallbackTimer, clearReplayReadyWaiter, clearReplaySession]);

  useEffect(() => {
    if (!replayModalOpen) return undefined;

    const resolveReplayRuntimePayload = (rawEvent) =>
      rawEvent?.detail && typeof rawEvent.detail === "object"
        ? rawEvent.detail
        : rawEvent?.data && typeof rawEvent.data === "object"
        ? rawEvent.data
        : null;

    const replayReadyPayloadMatches = (payload) => {
      const currentMatchId = String(replaySelectedMatch?.id || "").trim();
      if (!currentMatchId) return true;

      const explicitMatchId = String(payload?.matchId || payload?.replayMatchId || "").trim();
      if (explicitMatchId) return explicitMatchId === currentMatchId;

      const tournamentId = String(payload?.tournamentId || "").trim();
      if (!tournamentId) return true;
      if (tournamentId === currentMatchId) return true;
      if (tournamentId.toLowerCase().startsWith("replay-")) {
        return tournamentId.slice("replay-".length) === currentMatchId;
      }
      return false;
    };

    const handleReplayReady = (rawEvent) => {
      const payload = resolveReplayRuntimePayload(rawEvent);
      const eventType = String(payload?.type || rawEvent?.type || "")
        .trim()
        .toUpperCase();
      if (eventType !== "XIMA_REPLAY_READY") return;
      if (!replayReadyPayloadMatches(payload)) return;

      const waiter = replayReadyWaiterRef.current;
      if (waiter) {
        replayReadyWaiterRef.current = null;
        if (waiter.timer) clearTimeout(waiter.timer);
        if (typeof waiter.resolve === "function") waiter.resolve(true);
      }
      setReplayReady(true);
      setTimeout(() => sendReplayBlockVisualsRef.current(), 0);
    };

    const handleReplayMoveApplied = (rawEvent) => {
      const payload =
        resolveReplayRuntimePayload(rawEvent);
      const eventType = String(payload?.type || rawEvent?.type || "")
        .trim()
        .toUpperCase();
      if (eventType !== "XIMA_MOVE_NOTATION") return;

      const pendingAdvance = replayPendingStepAdvanceRef.current;
      if (pendingAdvance && Number.isFinite(Number(pendingAdvance.nextStep))) {
        replayPendingStepAdvanceRef.current = null;
        const nextStep = Math.max(
          0,
          Math.min(Number(replayTotalStepsRef.current) || 0, Number(pendingAdvance.nextStep) || 0)
        );
        setReplayCurrentStep(nextStep);
      }
      clearReplayAwaitingFallbackTimer();
      setReplayAwaitingStep(false);
    };

    const handleReplayPositionApplied = (rawEvent) => {
      const payload = resolveReplayRuntimePayload(rawEvent);
      const eventType = String(payload?.type || rawEvent?.type || "")
        .trim()
        .toUpperCase();
      if (eventType !== "XIMA_REPLAY_POSITION_APPLIED") return;

      const waiter = replayPositionWaiterRef.current;
      if (!waiter || String(payload?.requestId || "") !== waiter.requestId) return;
      replayPositionLastMessageRef.current = String(payload?.message || "");
      replayPositionWaiterRef.current = null;
      if (waiter.timer) clearTimeout(waiter.timer);
      waiter.resolve(payload?.success === true);
    };

    const handleReplayAnalysisState = (rawEvent) => {
      const payload = resolveReplayRuntimePayload(rawEvent);
      const eventType = String(payload?.type || rawEvent?.type || "")
        .trim()
        .toUpperCase();
      if (eventType !== "XIMA_REPLAY_ANALYSIS_STATE") return;
      setReplayAnalysisUndoCount(Math.max(0, Number(payload?.undoCount) || 0));
    };

    const bindReplayListeners = (target) => {
      if (!target || typeof target.addEventListener !== "function") {
        return () => {};
      }
      target.addEventListener("XIMA_REPLAY_ANALYSIS_STATE", handleReplayAnalysisState);
      target.addEventListener("message", handleReplayAnalysisState);
      target.addEventListener("XIMA_REPLAY_POSITION_APPLIED", handleReplayPositionApplied);
      target.addEventListener("message", handleReplayPositionApplied);

      target.addEventListener("XIMA_MOVE_NOTATION", handleReplayMoveApplied);
      target.addEventListener("xima-move-notation", handleReplayMoveApplied);
      target.addEventListener("XIMA_REPLAY_READY", handleReplayReady);
      target.addEventListener("xima-replay-ready", handleReplayReady);
      target.addEventListener("message", handleReplayMoveApplied);
      target.addEventListener("message", handleReplayReady);

      return () => {
        if (!target || typeof target.removeEventListener !== "function") return;
        target.removeEventListener("XIMA_REPLAY_ANALYSIS_STATE", handleReplayAnalysisState);
        target.removeEventListener("message", handleReplayAnalysisState);
        target.removeEventListener("XIMA_REPLAY_POSITION_APPLIED", handleReplayPositionApplied);
        target.removeEventListener("message", handleReplayPositionApplied);
        target.removeEventListener("XIMA_MOVE_NOTATION", handleReplayMoveApplied);
        target.removeEventListener("xima-move-notation", handleReplayMoveApplied);
        target.removeEventListener("XIMA_REPLAY_READY", handleReplayReady);
        target.removeEventListener("xima-replay-ready", handleReplayReady);
        target.removeEventListener("message", handleReplayMoveApplied);
        target.removeEventListener("message", handleReplayReady);
      };
    };

    const unbindWindow = bindReplayListeners(window);
    let iframeTarget = replayIframeRef.current?.contentWindow || null;
    let unbindIframe = bindReplayListeners(iframeTarget);
    const iframeElement = replayIframeRef.current;

    const handleIframeLoad = () => {
      unbindIframe();
      iframeTarget = replayIframeRef.current?.contentWindow || null;
      unbindIframe = bindReplayListeners(iframeTarget);
      postReplayPlaybackSpeedToIframe(iframeTarget);
      postReplayCameraFollowToIframe(iframeTarget);
      const replayMatchId = String(replaySelectedMatch?.id || "").trim();
      if (!replayMatchId) return;
      if (
        replayTickingRef.current ||
        replayAwaitingStepRef.current ||
        replayLoadingRef.current
      ) {
        return;
      }
      const bootKey = `${replayMatchId}:${String(replayFrameNonce || 0)}`;
      if (replayIframeBootstrappedKeyRef.current === bootKey) return;
      replayIframeBootstrappedKeyRef.current = bootKey;
      const bootstrapStep = Math.max(0, Number(replayCurrentStepRef.current) || 0);
      handleReplayLoadToStep(replayMatchId, bootstrapStep, {
        bootWaitMs: isReplayMobileViewport ? REPLAY_MOBILE_IFRAME_LOAD_BOOT_WAIT_MS : 150,
      }).catch((error) => console.log(error));
    };

    if (iframeElement && typeof iframeElement.addEventListener === "function") {
      iframeElement.addEventListener("load", handleIframeLoad);
    }

    return () => {
      unbindWindow();
      unbindIframe();
      if (iframeElement && typeof iframeElement.removeEventListener === "function") {
        iframeElement.removeEventListener("load", handleIframeLoad);
      }
    };
  }, [
    clearReplayAwaitingFallbackTimer,
    clearReplayReadyWaiter,
    handleReplayLoadToStep,
    isReplayMobileViewport,
    postReplayCameraFollowToIframe,
    postReplayPlaybackSpeedToIframe,
    replayModalOpen,
    replayFrameNonce,
    replaySelectedMatch?.id,
    setReplayAwaitingStep,
  ]);

  useEffect(() => {
    if (!replayModalOpen) return undefined;
    const handleReplayReconnect = () => {
      if (!replaySelectedMatch?.id) return;
      handleReplayLoadToStep(replaySelectedMatch.id, replayCurrentStep).catch((error) =>
        console.log(error)
      );
    };

    socket.on("connect", handleReplayReconnect);
    return () => {
      socket.off("connect", handleReplayReconnect);
    };
  }, [
    handleReplayLoadToStep,
    replayCurrentStep,
    replayModalOpen,
    replaySelectedMatch?.id,
  ]);

  const handleReplayOpen = useCallback(
    async (matchRecord) => {
      if (!matchRecord?.id || !matchRecord?.hasReplay) {
        notification.warning({
          message: i18nT("lobby.replayNoDisponible"),
          description: i18nT("lobby.estaPartidaNoTieneReplayGuardado"),
        });
        return;
      }

      await clearReplaySession();
      replayTickingRef.current = false;
      replayIframeBootstrappedKeyRef.current = "";
      clearReplayAwaitingFallbackTimer();
      clearReplayReadyWaiter(false);
      setReplayAwaitingStep(false);
      setReplayFrameNonce(Date.now());
      setReplayLoading(true);
      setReplaySelectedMatch(matchRecord);
      setReplayReview(null);
      setReplayReviewActive(Boolean(matchRecord?.analyze));
      setReplayModalOpen(true);
      setReplayPlaying(false);
      setReplayReady(false);
      setReplayTimeline([]);
      setReplayCurrentStep(0);
      setReplayTotalSteps(Number(matchRecord?.replaySteps) || 0);
      setReplayInfo(null);
      setReplayRestarting(false);

      try {
        const replayInfo = await axios.get(`/replays/${matchRecord.id}`);
        const replayPayload = replayInfo?.data?.replay || null;
        const timeline = ensureArray(replayPayload?.timeline);
        setReplayInfo(replayPayload);
        setReplayDesignSource({
          participants: replayPayload?.participants || [],
          userProfiles: replayPayload?.userProfiles || {},
        });
        setReplayTimeline(timeline);
        setReplayTotalSteps(Number(replayPayload?.totalSteps) || timeline.length);
        if (isReplayMobileViewport) {
          await waitForMobileReplayIframeReady();
        }
        let initialized = false;
        let lastError = null;
        const maxReplayInitAttempts = isReplayMobileViewport ? 18 : 12;
        for (let attempt = 0; attempt < maxReplayInitAttempts; attempt += 1) {
          try {
            await handleReplayLoadToStep(matchRecord.id, 0, {
              bootWaitMs: isReplayMobileViewport
                ? attempt === 0
                  ? REPLAY_MOBILE_BOOT_WAIT_MS
                  : REPLAY_MOBILE_RETRY_WAIT_MS
                : attempt === 0
                ? REPLAY_BOOT_WAIT_MS
                : 250,
            });
            initialized = true;
            break;
          } catch (error) {
            lastError = error;
          }
        }

        if (!initialized && lastError) throw lastError;
      } catch (error) {
        console.log(error);
        notification.error({
          message: i18nT("lobby.replayError"),
          description:
            error?.response?.data?.message ||
            error?.message ||
            "No se pudo abrir el replay.",
        });
        handleReplayClose();
      } finally {
      setReplayLoading(false);
    }
  },
    [
      clearReplayAwaitingFallbackTimer,
      clearReplayReadyWaiter,
      clearReplaySession,
      handleReplayClose,
      handleReplayLoadToStep,
      isReplayMobileViewport,
      setReplayAwaitingStep,
      waitForMobileReplayIframeReady,
    ]
  );

  useEffect(() => {
    if (replayOpenRequest) return;
    replayOpenHandledKeyRef.current = "";
  }, [replayOpenRequest]);

  useEffect(() => {
    if (!replayOpenRequest) return;
    const { requestKey, match } = replayOpenRequest;
    if (!requestKey) return;
    if (replayOpenHandledKeyRef.current === requestKey) return;
    replayOpenHandledKeyRef.current = requestKey;

    const matchId = String(match?.id || "").trim();
    if (!matchId) {
      const fallbackPath = location.pathname === "/game/history" ? "/game/history" : "/game";
      navigate(fallbackPath, { replace: true });
      return;
    }

    const replayMatch = {
      ...match,
      id: matchId,
      hasReplay: match?.hasReplay !== false,
    };
    handleReplayOpen(replayMatch).catch((error) => console.log(error));

    // Se quita la petición pero se recuerda que hay una repetición abierta (y a
    // dónde volver al cerrarla), para que la pantalla de la repetición siga montada.
    const nextPath = location.pathname === "/game/history" ? "/game/history" : "/game";
    navigate(nextPath, {
      replace: true,
      state: { replayActive: true, replayReturnTo: location.state?.replayReturnTo || "" },
    });
  }, [handleReplayOpen, location.pathname, navigate, replayOpenRequest]);

  useEffect(() => {
    if (!replayModalOpen || !isReplayMobileViewport || typeof document === "undefined") return;

    const scrollReplayToTop = () => {
      const wrap = document.querySelector(".xima-replay-modal-mobile .ant-modal-wrap");
      const body = document.querySelector(".xima-replay-modal-mobile .ant-modal-body");
      if (wrap instanceof HTMLElement) wrap.scrollTop = 0;
      if (body instanceof HTMLElement) body.scrollTop = 0;
      window.scrollTo(0, 0);
    };

    scrollReplayToTop();
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => {
        scrollReplayToTop();
        window.requestAnimationFrame(scrollReplayToTop);
      });
    }
    const timer = window.setTimeout(scrollReplayToTop, 120);
    return () => window.clearTimeout(timer);
  }, [isReplayMobileViewport, replayFrameNonce, replayModalOpen, replaySelectedMatch?.id]);

  const handleReplayJump = useCallback(
    async (stepIndex, options = {}) => {
      const replayId = replaySelectedMatch?.id;
      if (!replayId) return;
      const force = options?.force === true;
      const allowBackward = options?.allowBackward === true || options?.hardReload === true;
      const preservePlayingState = options?.preservePlayingState === true;
      if (replayTickingRef.current && !force) return;
      if (replayLoading && !force) return;
      if (force) {
        if (!preservePlayingState) setReplayPlaying(false);
        for (
          let retry = 0;
          retry < 120 && (replayTickingRef.current || replayLoadingRef.current);
          retry += 1
        ) {
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
      }
      if (replayTickingRef.current || replayLoadingRef.current) return;
      const keepPlaying = options?.keepPlaying === true;
      const targetStep = clampReplayStep(stepIndex, replayTotalSteps);
      const shouldHardReload = options?.hardReload === true;
      if (!allowBackward && targetStep < replayCurrentStep) return;
      if (targetStep === replayCurrentStep && !force && !shouldHardReload) return;

      try {
        replayTickingRef.current = true;
        clearReplayAwaitingFallbackTimer();
        setReplayAwaitingStep(false);
        setReplayLoading(true);
        if (!keepPlaying && !preservePlayingState) setReplayPlaying(false);
        if (shouldHardReload) {
          setReplayReady(false);
          replayIframeBootstrappedKeyRef.current = "";
          clearReplayReadyWaiter(false);
          setReplayFrameNonce(Date.now());
        }
        await handleReplayLoadToStep(replayId, targetStep, {
          bootWaitMs: shouldHardReload
            ? isReplayMobileViewport
              ? REPLAY_MOBILE_BOOT_WAIT_MS
              : REPLAY_BOOT_WAIT_MS
            : 0,
        });
      } catch (error) {
        console.log(error);
        notification.error({
          message: i18nT("lobby.replayError"),
          description:
            error?.response?.data?.message ||
            error?.message ||
            "No se pudo cargar la jugada.",
        });
      } finally {
        setReplayLoading(false);
        replayTickingRef.current = false;
      }
    },
    [
      clearReplayAwaitingFallbackTimer,
      clearReplayReadyWaiter,
      clampReplayStep,
      handleReplayLoadToStep,
      isReplayMobileViewport,
      replayCurrentStep,
      replayLoading,
      replaySelectedMatch?.id,
      setReplayAwaitingStep,
      replayTotalSteps,
    ]
  );

  const handleReplayNext = useCallback(async () => {
    const replayId = replaySelectedMatch?.id;
    if (!replayId) return;
    if (replayTickingRef.current) return;
    if (replayAwaitingStepRef.current) return;
    if (replayLoadingRef.current) return;
    if (!replayReady) return;
    const currentStep = Math.max(0, Number(replayCurrentStepRef.current) || 0);
    if (currentStep >= replayTotalSteps) {
      setReplayPlaying(false);
      return;
    }

    try {
      replayTickingRef.current = true;
      setReplayLoading(true);
      setReplayAwaitingStep(true);
      clearReplayAwaitingFallbackTimer();
      const optimisticNextStep = Math.max(
        0,
        Math.min(Number(replayTotalSteps) || 0, currentStep + 1)
      );
      replayPendingStepAdvanceRef.current = {
        requestedStep: currentStep,
        nextStep: optimisticNextStep,
      };
      replayAwaitingFallbackTimerRef.current = setTimeout(() => {
        replayAwaitingFallbackTimerRef.current = null;
        const pendingAdvance = replayPendingStepAdvanceRef.current;
        if (
          pendingAdvance &&
          Number(pendingAdvance.requestedStep) === Number(currentStep)
        ) {
          replayPendingStepAdvanceRef.current = null;
          setReplayPlaying(false);
          console.warn("[REPLAY] Unity did not confirm the replay move before timeout.");
        }
        setReplayAwaitingStep(false);
      }, REPLAY_STEP_AWAIT_TIMEOUT_MS);

      const emittedIndex = await handleReplayEmitStep(replayId, currentStep);
      const nextStep = Number.isFinite(emittedIndex) ? emittedIndex + 1 : currentStep + 1;
      const pendingAdvance = replayPendingStepAdvanceRef.current;
      if (
        pendingAdvance &&
        Number(pendingAdvance.requestedStep) === Number(currentStep)
      ) {
        replayPendingStepAdvanceRef.current = {
          ...pendingAdvance,
          nextStep: Math.max(0, Math.min(Number(replayTotalSteps) || 0, nextStep)),
        };
      }
      setReplayReady(true);
    } catch (error) {
      replayPendingStepAdvanceRef.current = null;
      clearReplayAwaitingFallbackTimer();
      setReplayAwaitingStep(false);
      console.log(error);
      notification.error({
        message: i18nT("lobby.replayError"),
        description:
          error?.response?.data?.message ||
          error?.message ||
          "No se pudo reproducir la siguiente jugada.",
      });
    } finally {
      setReplayLoading(false);
      replayTickingRef.current = false;
    }
  }, [
    clearReplayAwaitingFallbackTimer,
    handleReplayEmitStep,
    replayReady,
    replaySelectedMatch?.id,
    setReplayAwaitingStep,
    replayTotalSteps,
  ]);

  const handleReplayManualNext = useCallback(async () => {
    if (!replaySelectedMatch?.id) return;
    setReplayPlaying(false);
    for (
      let retry = 0;
      retry < 120 && (replayTickingRef.current || replayLoadingRef.current);
      retry += 1
    ) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    const currentStep = Math.max(0, Number(replayCurrentStepRef.current) || 0);
    if (currentStep >= replayTotalSteps) return;
    if (replayAwaitingStepRef.current) return;
    await handleReplayNext();
  }, [
    handleReplayNext,
    replaySelectedMatch?.id,
    replayTotalSteps,
  ]);

  const handleReplayDecreaseSpeed = useCallback(() => {
    setReplayPlaybackSpeedIndex((prev) =>
      clampValue(
        (Number(prev) || REPLAY_PLAYBACK_DEFAULT_INDEX) - 1,
        0,
        REPLAY_PLAYBACK_SPEED_OPTIONS.length - 1
      )
    );
  }, []);

  const handleReplayIncreaseSpeed = useCallback(() => {
    setReplayPlaybackSpeedIndex((prev) =>
      clampValue(
        (Number(prev) || REPLAY_PLAYBACK_DEFAULT_INDEX) + 1,
        0,
        REPLAY_PLAYBACK_SPEED_OPTIONS.length - 1
      )
    );
  }, []);

  const handleReplayRestart = useCallback(async () => {
    if (!replaySelectedMatch?.id) return;
    setReplayPlaying(false);
    setReplayRestarting(true);
    try {
      await handleReplayJump(0, {
        force: true,
        hardReload: true,
        allowBackward: true,
      });
    } finally {
      setReplayRestarting(false);
    }
  }, [handleReplayJump, replaySelectedMatch?.id]);

  // Ir a una jugada concreta (retroceder o saltar desde el timeline). Recarga el
  // tablero y aplica las jugadas hasta ese punto, igual que Reiniciar, para que
  // el estado del juego quede siempre limpio.
  const waitForReplayPositionApplied = useCallback((requestId, timeoutMs) => {
    const previous = replayPositionWaiterRef.current;
    if (previous) {
      if (previous.timer) clearTimeout(previous.timer);
      previous.resolve(false);
    }
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        if (replayPositionWaiterRef.current?.requestId !== requestId) return;
        replayPositionWaiterRef.current = null;
        resolve(false);
      }, Math.max(1000, Number(timeoutMs) || 0));
      replayPositionWaiterRef.current = { requestId, resolve, timer };
    });
  }, []);

  // Ir a una jugada concreta poniendo directamente su posicion (calculada por el
  // servidor), sin reproducir las anteriores. Al retroceder una sola jugada, Unity
  // anima antes el bloque al reves. Si algo falla, se recarga como Reiniciar.
  const handleReplayGoToStep = useCallback(
    async (targetStep, options = {}) => {
      const replayId = replaySelectedMatch?.id;
      if (!replayId) return;
      const step = clampReplayStep(targetStep, replayTotalSteps);
      const currentStep = Math.max(0, Number(replayCurrentStepRef.current) || 0);
      const wasAnalyzing = replayAnalysisActiveRef.current;
      if (wasAnalyzing) {
        // Volver a una posicion real siempre cierra el modo analizar.
        replayAnalysisActiveRef.current = false;
        setReplayAnalysisActive(false);
        setReplayAnalysisUndoCount(0);
        postReplayEventsToIframe([{ eventName: "REPLAY_ANALYSIS", payload: "0" }]);
      }
      if (step === currentStep && !wasAnalyzing) return;
      setReplayPlaying(false);
      for (
        let retry = 0;
        retry < 120 && (replayTickingRef.current || replayLoadingRef.current || replayAwaitingStepRef.current);
        retry += 1
      ) {
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
      if (replayTickingRef.current || replayLoadingRef.current) return;

      const animateReverse = options?.animateReverse === true && step === currentStep - 1;
      let applied = false;
      try {
        replayTickingRef.current = true;
        setReplayLoading(true);
        const response = await axios.post(`/replays/${replayId}/snapshot`, { toStep: step });
        const snapshotJson = String(response?.data?.snapshotJson || "");
        if (snapshotJson) {
          const requestId = `replay-pos-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
          replayPositionLastMessageRef.current = "";
          const appliedPromise = waitForReplayPositionApplied(
            requestId,
            animateReverse ? 12000 : 8000
          );
          postReplayEventsToIframe([
            {
              eventName: animateReverse ? "REPLAY_REVERSE_MOVE" : "REPLAY_BOARD_SNAPSHOT",
              payload: JSON.stringify({
                requestId,
                movePayload: animateReverse ? String(response?.data?.undoMovePayload || "") : "",
                snapshotJson,
              }),
            },
          ]);
          applied = await appliedPromise;
          if (!applied) {
            // Segundo intento sin animacion antes de recargar el tablero entero.
            const retryId = `${requestId}-retry`;
            const retryPromise = waitForReplayPositionApplied(retryId, 8000);
            postReplayEventsToIframe([
              {
                eventName: "REPLAY_BOARD_SNAPSHOT",
                payload: JSON.stringify({ requestId: retryId, movePayload: "", snapshotJson }),
              },
            ]);
            applied = await retryPromise;
          }
          if (applied) {
            setReplayCurrentStep(Number(response?.data?.appliedSteps) || step);
            setReplayTotalSteps(Number(response?.data?.totalSteps) || replayTotalSteps);
            // Relojes de esa jugada (al saltar no pasan los TURN_NEXT que los ponen).
            const clocks = Array.isArray(response?.data?.replayClocks) ? response.data.replayClocks : [];
            if (clocks.length) {
              postReplayEventsToIframe([
                { eventName: "REPLAY_CLOCKS", payload: clocks.map((ms) => Math.max(0, Math.round(Number(ms) || 0))).join(",") },
              ]);
            }
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        setReplayLoading(false);
        replayTickingRef.current = false;
      }

      if (applied) return;
      // Diagnostico: se muestra el motivo para poder corregirlo.
      const failureReason = replayPositionLastMessageRef.current || "sin respuesta de Unity";
      console.warn("[REPLAY_POSITION] fallback reload:", failureReason);
      notification.warning({
        message: "No se pudo poner la jugada directamente",
        description: `Motivo: ${failureReason}. Se recarga el tablero.`,
      });
      await handleReplayJump(step, {
        force: true,
        hardReload: true,
        allowBackward: true,
      });
    },
    [
      clampReplayStep,
      handleReplayJump,
      postReplayEventsToIframe,
      replaySelectedMatch?.id,
      replayTotalSteps,
      waitForReplayPositionApplied,
    ]
  );

  const handleReplayPrevious = useCallback(async () => {
    const currentStep = Math.max(0, Number(replayCurrentStepRef.current) || 0);
    if (currentStep <= 0) return;
    if (replayAnalysisActiveRef.current) {
      await handleReplayGoToStep(currentStep);
      return;
    }
    // Sin animacion al reves: la posicion anterior se pone directamente.
    await handleReplayGoToStep(currentStep - 1);
  }, [handleReplayGoToStep]);

  const handleReplayRestoreFromAnalysis = useCallback(async () => {
    if (!replayAnalysisActiveRef.current) return;
    await handleReplayGoToStep(Math.max(0, Number(replayCurrentStepRef.current) || 0));
  }, [handleReplayGoToStep]);

  const handleReplayAnalysisToggle = useCallback(async () => {
    if (replayAnalysisActiveRef.current) {
      await handleReplayRestoreFromAnalysis();
      return;
    }
    setReplayPlaying(false);
    for (
      let retry = 0;
      retry < 160 && (replayTickingRef.current || replayLoadingRef.current || replayAwaitingStepRef.current);
      retry += 1
    ) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    replayAnalysisActiveRef.current = true;
    setReplayAnalysisActive(true);
    setReplayAnalysisUndoCount(0);
    postReplayEventsToIframe([{ eventName: "REPLAY_ANALYSIS", payload: "1" }]);
  }, [handleReplayRestoreFromAnalysis, postReplayEventsToIframe]);

  const handleReplayAnalysisUndo = useCallback(() => {
    if (!replayAnalysisActiveRef.current) return;
    postReplayEventsToIframe([{ eventName: "REPLAY_ANALYSIS_UNDO", payload: "" }]);
  }, [postReplayEventsToIframe]);

  const handleReplayArrowsToggle = useCallback(() => {
    setReplayArrowsActive((prev) => {
      const next = !prev;
      postReplayEventsToIframe([{ eventName: "REPLAY_ARROWS", payload: next ? "1" : "0" }]);
      return next;
    });
  }, [postReplayEventsToIframe]);

  // Teclas de color mantenidas en la pagina (Unity tambien las detecta cuando el
  // foco esta en el tablero): Shift rojo, Tab verde, Bloq Mayus amarillo, Ctrl azul.
  useEffect(() => {
    if (!replayModalOpen || typeof window === "undefined") return undefined;
    const held = { shift: false, tab: false, ctrl: false };
    let capsLockOn = false;
    let lastSent = "";
    const send = () => {
      const color = held.shift
        ? "red"
        : held.tab
        ? "green"
        : capsLockOn
        ? "yellow"
        : held.ctrl
        ? "blue"
        : "";
      if (color === lastSent) return;
      lastSent = color;
      postReplayEventsToIframe([{ eventName: "REPLAY_ARROWS_KEY", payload: color }]);
    };
    const isTypingTarget = (target) => {
      const tagName = String(target?.tagName || "").toLowerCase();
      return tagName === "input" || tagName === "textarea" || Boolean(target?.isContentEditable);
    };
    const update = (event, down) => {
      if (typeof event.getModifierState === "function") {
        capsLockOn = event.getModifierState("CapsLock");
      }
      if (event.key === "Shift") held.shift = down;
      if (event.key === "Control") held.ctrl = down;
      if (event.key === "Tab") {
        if (isTypingTarget(event.target)) return;
        held.tab = down;
        event.preventDefault();
      }
      send();
    };
    const handleKeyDown = (event) => update(event, true);
    const handleKeyUp = (event) => update(event, false);
    const handleBlur = () => {
      held.shift = false;
      held.tab = false;
      held.ctrl = false;
      send();
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);
    return () => {
      held.shift = false;
      held.tab = false;
      held.ctrl = false;
      capsLockOn = false;
      send();
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
    };
  }, [postReplayEventsToIframe, replayModalOpen]);

  // Tras recargar el tablero, Unity recibe de nuevo el boton Flechas y el color.
  useEffect(() => {
    if (!replayModalOpen || !replayReady) return;
    postReplayEventsToIframe([
      { eventName: "REPLAY_ARROWS", payload: replayArrowsActive ? "1" : "0" },
      { eventName: "REPLAY_ARROWS_COLOR", payload: replayArrowColor },
    ]);
  }, [postReplayEventsToIframe, replayArrowColor, replayArrowsActive, replayModalOpen, replayReady]);

  const handleReplayNextFromUi = useCallback(async () => {
    if (replayAnalysisActiveRef.current) await handleReplayRestoreFromAnalysis();
    await handleReplayManualNext();
  }, [handleReplayManualNext, handleReplayRestoreFromAnalysis]);

  const handleReplayPlayToggleFromUi = useCallback(async () => {
    if (replayAnalysisActiveRef.current) {
      await handleReplayRestoreFromAnalysis();
      setReplayPlaying(true);
      return;
    }
    setReplayPlaying((prev) => !prev);
  }, [handleReplayRestoreFromAnalysis]);

  // Control de voz de la repeticion: cada comando usa la misma funcion que su boton.
  const handleReplayVoiceCommand = (command) => {
    const run = (promise) => Promise.resolve(promise).catch((error) => console.log(error));
    const total = Math.max(0, Number(replayTotalSteps) || 0);
    if (String(command.id).startsWith("goto:")) {
      const step = Number(command.step) || 0;
      if (step > total) return { ok: false, text: `La partida solo tiene ${total} jugadas` };
      if (!replayCanSelectTimelineStep || total <= 0) return false;
      run(handleReplayGoToStep(step));
      return `Jugada ${step}`;
    }
    switch (command.id) {
      case "next":
        if (!replayCanStepForward && !replayAnalysisActive) return false;
        run(handleReplayNextFromUi());
        return "Siguiente";
      case "previous":
        if (!replayCanGoBack && !replayAnalysisActive) return false;
        run(handleReplayPrevious());
        return "Atrás";
      case "restart":
        if (!replayCanRestart) return false;
        run(handleReplayRestart());
        return "Reiniciar";
      case "play":
        if (replayPlaying && !replayAnalysisActive) return { ok: true, text: "Ya se está reproduciendo" };
        if (!replayCanTogglePlay && !replayAnalysisActive) return false;
        run(handleReplayPlayToggleFromUi());
        return "Play";
      case "pause":
        if (!replayPlaying) return { ok: true, text: "Ya está en pausa" };
        run(handleReplayPlayToggleFromUi());
        return "Pausa";
      case "speed_up":
        if (!replayCanIncreaseSpeed) return { ok: false, text: "Ya está a la velocidad máxima" };
        handleReplayIncreaseSpeed();
        return "Más rápido";
      case "speed_down":
        if (!replayCanDecreaseSpeed) return { ok: false, text: "Ya está a la velocidad mínima" };
        handleReplayDecreaseSpeed();
        return "Más lento";
      case "review_on":
        if (!replayHasMatch) return false;
        if (replayReviewActive) return { ok: true, text: "El análisis ya está activo" };
        setReplayReviewActive(true);
        return "Analizar partida";
      case "take_control":
        if (replayAnalysisActive) return { ok: true, text: "Ya tienes el control" };
        if (!replayCanRestart) return false;
        run(handleReplayAnalysisToggle());
        return "Tomar el control";
      case "continue_game":
        if (!replayAnalysisActive) return { ok: true, text: "Ya estás viendo la partida" };
        run(handleReplayAnalysisToggle());
        return "Continuar con la partida";
      case "analysis_undo":
        if (!replayAnalysisActive) return { ok: false, text: "Deshacer funciona con «Tomar el control»" };
        if (replayAnalysisUndoCount <= 0) return { ok: false, text: "No hay nada que deshacer" };
        handleReplayAnalysisUndo();
        return "Deshacer";
      default:
        break;
    }
    if (command.type === "board_move") {
      // Mover o incorporar bloques solo con «Tomar el control»; la respuesta llega de Unity.
      if (!replayAnalysisActive) return { ok: false, text: "Para mover bloques di primero «tomar el control»" };
      const delivered = dispatchVoiceCommandToUnity({
        iframeElement: replayIframeRef.current,
        command,
        transcript: command.transcript || "",
      });
      return delivered ? `${command.label}…` : false;
    }
    if (command.type === "camera" || command.type === "rotation") {
      const delivered = dispatchVoiceCommandToUnity({
        iframeElement: replayIframeRef.current,
        command,
        transcript: command.transcript || "",
      });
      return delivered ? command.label : false;
    }
    return false;
  };

  const requestMatchPassword = useCallback(
    (match) =>
      new Promise((resolve) => {
        let passwordDraft = "";
        const inputId = `match-password-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
        const matchName = getLocalizedTournamentName(match?.tournamentName, "Partido");
        Modal.confirm({
          title: i18nT("lobby.partidoConContrasena"),
      content: (
        <div className="space-y-3">
          <div className="text-sm text-[#dce8ff]">
            {i18nT("lobby.paraUnirteA")} {matchName}{" "}
            {i18nT("lobby.debesEscribirLaContrasenaQueTeCompartioEl")}
          </div>
          <Input.Password
            id={inputId}
            autoFocus
            maxLength={MATCH_PASSWORD_MAX_LENGTH}
            placeholder={i18nT("lobby.contrasenaDelPartido")}
            onChange={(event) => {
              passwordDraft = normalizeMatchPassword(event?.target?.value || "");
            }}
            onPressEnter={() => {
              const activeOkButton = document.querySelector(".ant-modal-confirm-btns .ant-btn-primary");
              if (activeOkButton && typeof activeOkButton.click === "function") {
                activeOkButton.click();
                  }
                }}
              />
            </div>
          ),
          okText: "Unirse",
          cancelText: "Cancelar",
          centered: true,
          onOk: () => {
            const password = String(
              document.getElementById(inputId)?.value || passwordDraft || ""
            ).trim();
            const normalizedPassword = normalizeMatchPassword(password);
            if (!normalizedPassword || !isValidMatchPassword(normalizedPassword)) {
              notification.warning({
                message: i18nT("lobby.contrasenaRequerida"),
                description: i18nT("lobby.estePartidoEstaProtegidoEscribeLaContrasenaP3594c4"),
              });
              return Promise.reject(new Error("MATCH_PASSWORD_REQUIRED"));
            }
            resolve(normalizedPassword);
            return undefined;
          },
          onCancel: () => resolve(null),
        });
      }),
    [getLocalizedTournamentName]
  );

  const canEnterProtectedMatchWithoutPassword = useCallback(
    (match) => {
      const identities = new Set(
        [currentUserEmailNormalized, currentUserLoginNameNormalized]
          .map((value) => normalizeJoinedUiIdentity(value))
          .filter(Boolean)
      );
      if (identities.size === 0) return false;

      const tournamentId = String(match?._id || match?.id || "").trim();
      const recordUsers = Array.isArray(match?.users) ? match.users : [];
      const localUsers = tournamentId
        ? ensureArray(joinedUiPlayersByTournamentId[tournamentId])
        : [];
      return [...recordUsers, ...localUsers].some((entry) => {
        const candidates =
          entry && typeof entry === "object"
            ? [
                entry.userId,
                entry.email,
                entry.loginIdentifier,
                entry.identity,
                entry.id,
                entry.name,
              ]
            : [entry];
        return candidates
          .map((value) => normalizeJoinedUiIdentity(value))
          .some((value) => value && identities.has(value));
      });
    },
    [
      currentUserEmailNormalized,
      currentUserLoginNameNormalized,
      joinedUiPlayersByTournamentId,
      normalizeJoinedUiIdentity,
    ]
  );

  const hasCurrentUserPaidForTournament = useCallback(
    (match) => {
      const identities = new Set(
        [currentUserEmailNormalized, currentUserLoginNameNormalized]
          .map((value) => normalizeJoinedUiIdentity(value))
          .filter(Boolean)
      );
      if (identities.size === 0) return false;

      return ensureArray(match?.paidUsers).some((entry) => {
        const candidates =
          entry && typeof entry === "object"
            ? [
                entry.userId,
                entry.email,
                entry.loginIdentifier,
                entry.identity,
                entry.id,
                entry.name,
              ]
            : [entry];
        return candidates
          .map((value) => normalizeJoinedUiIdentity(value))
          .some((value) => value && identities.has(value));
      });
    },
    [
      currentUserEmailNormalized,
      currentUserLoginNameNormalized,
      normalizeJoinedUiIdentity,
    ]
  );

  const syncWalletFromJoinResponse = useCallback((payload) => {
    const nextWalletBalanceCandidate = payload?.walletBalance ?? payload?.wallet?.balance;
    if (nextWalletBalanceCandidate !== undefined && nextWalletBalanceCandidate !== null) {
      setWalletBalance(normalizeWalletBalance(nextWalletBalanceCandidate));
    }
  }, []);
  const tournamentsForLastJoinedRef = useRef([]);
  tournamentsForLastJoinedRef.current = tournaments;
  const rememberLastJoinedTournament = useCallback(
    (rawTournamentId) => {
      const tournamentId = String(rawTournamentId || "").trim();
      if (!tournamentId) return;
      setLastJoinedTournamentId((prev) => (prev === tournamentId ? prev : tournamentId));
      setStoredLastJoinedTournamentId(lastJoinedTournamentStorageKey, tournamentId);
      // Keep name/mode/size too, so the lobby Jugar cube can find the restarted copy.
      const joinedRecord = ensureArray(tournamentsForLastJoinedRef.current).find(
        (entry) => String(entry?._id || entry?.id || "").trim() === tournamentId
      );
      if (joinedRecord && !joinedRecord.isIndependentMatch && !joinedRecord.isPuzzle) {
        writeLastJoinedTournament(user?.email, joinedRecord);
      }
    },
    [lastJoinedTournamentStorageKey, user?.email]
  );

  const requestTournamentPaymentConfirmation = useCallback((match, paymentAmount) => {
    const tournamentName = getLocalizedTournamentName(match?.tournamentName, "Torneo");
    return new Promise((resolve) => {
      Modal.confirm({
        title: i18nT("lobby.confirmarEntradaAlTorneo"),
        content: (
          <div className="space-y-2 text-sm text-[#dce8ff]">
            <div>
              {i18nT("lobby.quieresEntrarA")} {tournamentName}?{" "}
              {i18nT("lobby.elSistemaCobrara")} {paymentAmount}X{" "}
              {i18nT("lobby.soloCuandoPresionesJugarYElTorneoEste")}
            </div>
            <div className="text-xs text-[#98b8e4]">
              Monto reservado para validar: {paymentAmount}X
            </div>
          </div>
        ),
        okText: "Entrar",
        cancelText: "Cancelar",
        centered: true,
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  }, [getLocalizedTournamentName]);

  const checkAndConfirmTournamentPayment = useCallback(
    async (match, options) => {
      const paymentAmount = resolveTournamentRewardValue(match);
      const requiresPayment = Number(paymentAmount) > 0;
      if (!requiresPayment || options?.skipJoinRequest) return { canJoin: true, paymentAmount };

      if (match && hasCurrentUserPaidForTournament(match)) {
        return { canJoin: true, paymentAmount };
      }

      const wantsToPay = await requestTournamentPaymentConfirmation(match, paymentAmount);
      if (!wantsToPay) return { canJoin: false, paymentAmount };

      const normalizedWalletBalance = normalizeWalletBalance(walletBalance);
      if (normalizedWalletBalance < paymentAmount) {
        notification.error({
          message: i18nT("lobby.pagoRequerido"),
          description: i18nT("lobby.loSientoDebesTenerDineroSuficienteParaUnirte"),
        });
        return { canJoin: false, paymentAmount };
      }

      return { canJoin: true, paymentAmount };
    },
    [
      hasCurrentUserPaidForTournament,
      requestTournamentPaymentConfirmation,
      walletBalance,
    ]
  );

  const resolveWaitingRoomCancelPath = useCallback(
    (rawReturnTo = "") => {
      const returnTo = String(rawReturnTo || "").trim();
      if (returnTo && returnTo.startsWith("/") && !returnTo.startsWith("//")) {
        return returnTo;
      }
      return location.pathname === "/game/history" ? "/game/history" : "/game?quickView=torneos";
    },
    [location.pathname]
  );

  const navigateAfterCancelledWaitingRoomRequest = useCallback(
    (rawReturnTo = "") => {
      const explicitReturnTo = String(rawReturnTo || "").trim();
      const requestReturnTo = String(waitingRoomOpenRequest?.returnTo || "").trim();
      if (!explicitReturnTo && !requestReturnTo && !waitingRoomOpenRequest) {
        return false;
      }

      navigate(resolveWaitingRoomCancelPath(explicitReturnTo || requestReturnTo), {
        replace: true,
      });
      return true;
    },
    [
      navigate,
      resolveWaitingRoomCancelPath,
      waitingRoomOpenRequest,
    ]
  );

  const handleJoin = useCallback(
    async (id, options = {}) => {
      if (!id) return;
      if (options?.match?.matchPasswordInvalid || options?.match?.requiresAdminRepair) {
        notification.error({
          message: i18nT("lobby.torneoPendienteDeReparacion"),
          description:
            i18nT("lobby.esteTorneoPrivadoTieneLaContrasenaIncompleta332004"),
        });
        return;
      }
      const autoPlay = options?.autoPlay === true;
      const skipJoinRequest = options?.skipJoinRequest === true;
      const cancelReturnTo = String(options?.cancelReturnTo || options?.returnTo || "").trim();
      let addedLocalJoin = false;
      let joinPayload = null;
      let serverTournamentPatch = null;

      try {
        await clearReplaySession();
        const shouldAskForPassword =
          !skipJoinRequest &&
          options?.match?.hasMatchPassword &&
          !options?.matchPassword;
        const matchPassword =
          shouldAskForPassword
            ? await requestMatchPassword(options.match)
            : options?.matchPassword;
        if (shouldAskForPassword && !matchPassword) {
          navigateAfterCancelledWaitingRoomRequest(cancelReturnTo);
          return;
        }

      const { canJoin } = await checkAndConfirmTournamentPayment(options?.match, {
        skipJoinRequest,
      });
      if (!canJoin) {
        navigateAfterCancelledWaitingRoomRequest(cancelReturnTo);
        return;
      }
      const normalizedMatchPassword = String(matchPassword || "").trim();
      if (normalizedMatchPassword) {
        persistTournamentMatchPasswordToSession(id, normalizedMatchPassword);
      } else {
        clearTournamentMatchPasswordFromSession(id);
      }

      if (!skipJoinRequest) {
        addJoinedUiPlayer(id, currentTournamentPlayer);
        addedLocalJoin = true;
        applyOptimisticTournamentJoin(id);
          const joinResponse = await axios.post("/tournaments/join", {
            id,
            matchPassword,
            tournamentPassword: matchPassword,
            joinPassword: matchPassword,
          });
          joinPayload = joinResponse?.data || {};
          serverTournamentPatch =
            joinPayload?.tournament && typeof joinPayload.tournament === "object"
              ? joinPayload.tournament
              : null;
          const serverJoinedUsers = ensureArray(
            joinPayload?.users ?? serverTournamentPatch?.users
          );
          if (serverJoinedUsers.length > 0) {
            hydrateJoinedUiPlayers(id, serverJoinedUsers);
            setOptimisticTournamentPlayersById((prev) => ({
              ...prev,
              [id]: serverJoinedUsers,
            }));
          }
          syncWalletFromJoinResponse(joinResponse?.data);
        }
        rememberLastJoinedTournament(id);
        const nextSearch = new URLSearchParams();
        if (autoPlay) nextSearch.set("autoplay", "1");
        if (cancelReturnTo) nextSearch.set("joinReturnTo", cancelReturnTo);
        const suffix = nextSearch.toString() ? `?${nextSearch.toString()}` : "";
        const tournamentSequenceNumber = Math.max(
          0,
          Math.floor(
            normalizePublicSequenceNumberValue(options?.tournamentUiSequenceNumber) ||
              normalizePublicSequenceNumberValue(options?.tournamentSequenceNumber) ||
              resolvePublicSequenceNumber(options?.match) ||
              resolvePublicSequenceNumber(joinPayload) ||
              resolvePublicSequenceNumber(serverTournamentPatch) ||
              0
          )
        );
        const nextState = {
          ...(matchPassword ? { protectedMatchPassword: matchPassword } : {}),
          ...(cancelReturnTo ? { tournamentJoinReturnTo: cancelReturnTo } : {}),
          ...(tournamentSequenceNumber > 0
            ? {
                tournamentUiSequenceNumber: tournamentSequenceNumber,
                tournamentSequenceNumber,
              }
            : {}),
        };
        navigate(`/game/${id}${suffix}`, {
          state: Object.keys(nextState).length > 0 ? nextState : undefined,
        });
      } catch (err) {
        console.error(err);
      if (addedLocalJoin) {
        removeJoinedUiPlayer(id, currentTournamentPlayer);
      }
      clearTournamentMatchPasswordFromSession(id);
      fetchTournaments().catch((error) => console.log(error));
      fetchLiveMatches().catch((error) => console.log(error));
        const code = String(err?.response?.data?.code || "").trim().toUpperCase();
        if (code === "INSUFFICIENT_FUNDS") {
          notification.error({
            message: i18nT("lobby.pagoRequerido"),
            description:
              err?.response?.data?.message || "Lo siento, debes tener dinero suficiente para unirte.",
          });
          return;
        }
        notification.error({
          message: code.startsWith("MATCH_PASSWORD") ? "Contrase\u00f1a incorrecta" : "Join Error",
          description:
            err?.response?.data?.message ||
            err?.message ||
            "Unable to join the selected tournament.",
        });
      }
    },
    [
      checkAndConfirmTournamentPayment,
      addJoinedUiPlayer,
      applyOptimisticTournamentJoin,
      clearReplaySession,
      currentTournamentPlayer,
      fetchLiveMatches,
      fetchTournaments,
      hydrateJoinedUiPlayers,
      navigate,
      navigateAfterCancelledWaitingRoomRequest,
      rememberLastJoinedTournament,
      removeJoinedUiPlayer,
      requestMatchPassword,
      syncWalletFromJoinResponse,
    ]
  );
  const handleStartPuzzle = useCallback(
    async (puzzleRecord, options = {}) => {
      const puzzleId = String(
        puzzleRecord?.id || puzzleRecord?._id || puzzleRecord?.tournamentId || ""
      ).trim();
      if (!puzzleId) return;

      try {
        setLaunchingPuzzleId(puzzleId);
        await clearReplaySession();
        const { tutorialQuery, tutorialState } = buildPuzzleTutorialLaunchOptions(
          options?.tutorialLevel
        );
        navigate(`/game/${puzzleId}?autoplay=1&puzzle=1${tutorialQuery}`, {
          state: tutorialState,
        });
      } catch (error) {
        console.log(error);
        notification.error({
          message: "Puzzle",
          description:
            error?.response?.data?.message ||
            error?.message ||
            "No se pudo abrir el puzzle seleccionado.",
        });
      } finally {
        setLaunchingPuzzleId("");
      }
    },
    [clearReplaySession, navigate]
  );
  const markPuzzleRewardAsClaimed = useCallback(
    (rawPuzzleId) => {
      const puzzleId = String(rawPuzzleId || "").trim();
      if (!puzzleId) return;

      setClaimedPuzzleRewardMap((prev) => {
        const previousMap =
          prev && typeof prev === "object" && !Array.isArray(prev) ? prev : {};
        if (previousMap[puzzleId] === true) return previousMap;
        const nextMap = {
          ...previousMap,
          [puzzleId]: true,
        };
        setPuzzleRewardClaimMap(user?.email, nextMap);
        return nextMap;
      });
      setPuzzles((prev) =>
        ensureArray(prev).map((entry) => {
          const entryPuzzleId = String(
            entry?.id || entry?._id || entry?.tournamentId || ""
          ).trim();
          if (!entryPuzzleId || entryPuzzleId !== puzzleId) return entry;
          return {
            ...entry,
            rewardClaimedByCurrentUser: true,
            isRewardClaimedByCurrentUser: true,
            puzzleRewardValue: 0,
            rewardValue: 0,
            rewardAmount: 0,
            reward: 0,
            rewardX: 0,
          };
        })
      );
    },
    [user?.email]
  );
  const markPuzzleRewardAsFailed = useCallback((rawPuzzleId) => {
    const puzzleId = String(rawPuzzleId || "").trim();
    if (!puzzleId) return;
    setPuzzles((prev) =>
      ensureArray(prev).map((entry) => {
        const entryPuzzleId = String(
          entry?.id || entry?._id || entry?.tournamentId || ""
        ).trim();
        if (!entryPuzzleId || entryPuzzleId !== puzzleId) return entry;
        return {
          ...entry,
          rewardFailedByCurrentUser: true,
          isRewardFailedByCurrentUser: true,
        };
      })
    );
  }, []);
  const showGuestRegisterRequiredModal = useCallback(() => {
    Modal.confirm({
      title: i18nT("lobby.registraTuCuentaParaReclamar"),
      content:
        i18nT("lobby.losJugadoresInvitadosNoPuedenReclamarRecompedc7939"),
      okText: "Ir a registrarme",
      cancelText: "Ahora no",
      onOk: () => {
        delete axios.defaults.headers["Authorization"];
        clearStoredAuthToken();
        if (typeof window !== "undefined") {
          window.location.assign("/register");
        }
      },
    });
  }, []);
  const handleClaimPuzzleReward = useCallback(
    async (puzzleRecord) => {
      const puzzleId = String(
        puzzleRecord?.id || puzzleRecord?._id || puzzleRecord?.tournamentId || ""
      ).trim();
      if (!puzzleId) return;
      if (claimingPuzzleRewardId === puzzleId) return;
      const isPuzzleAzar = isPuzzleAzarMode(puzzleRecord?.freeBoxBaseMode || "free");

      const puzzleSolvedByCurrentUser = Boolean(
        solvedPuzzleIdsSet.has(puzzleId) ||
          puzzleRecord?.solvedByCurrentUser ||
          puzzleRecord?.isSolvedByCurrentUser
      );
      if (!puzzleSolvedByCurrentUser) return;
      const puzzleRewardFailedByCurrentUser = Boolean(
        puzzleRecord?.rewardFailedByCurrentUser || puzzleRecord?.isRewardFailedByCurrentUser
      );
      if (isPuzzleAzar ? false : puzzleRewardFailedByCurrentUser) return;
      const puzzleRewardAlreadyClaimed = Boolean(
        claimedPuzzleRewardMap[puzzleId] === true ||
          puzzleRecord?.rewardClaimedByCurrentUser ||
          puzzleRecord?.isRewardClaimedByCurrentUser
      );
      if (puzzleRewardAlreadyClaimed) return;

      if (isCurrentUserGuest) {
        showGuestRegisterRequiredModal();
        return;
      }

      try {
        setClaimingPuzzleRewardId(puzzleId);
        const response = await axios.post("/tournaments/puzzles/claim-reward", { puzzleId });
        const rewardedValue = normalizePuzzleRewardValue(
          response?.data?.rewardValue,
          resolvePuzzleRewardValue(puzzleRecord)
        );
        const nextWalletBalanceCandidate =
          response?.data?.walletBalance ?? response?.data?.wallet?.balance;
        if (nextWalletBalanceCandidate !== undefined && nextWalletBalanceCandidate !== null) {
          setWalletBalance(normalizeWalletBalance(nextWalletBalanceCandidate));
        } else if (rewardedValue > 0) {
          setWalletBalance((prev) => normalizeWalletBalance((Number(prev) || 0) + rewardedValue));
        }
        markPuzzleRewardAsClaimed(puzzleId);
      } catch (error) {
        console.log(error);
        const code = String(error?.response?.data?.code || "")
          .trim()
          .toUpperCase();
        const nextWalletBalanceCandidate =
          error?.response?.data?.walletBalance ?? error?.response?.data?.wallet?.balance;
        if (nextWalletBalanceCandidate !== undefined && nextWalletBalanceCandidate !== null) {
          setWalletBalance(normalizeWalletBalance(nextWalletBalanceCandidate));
        }

        if (code === "GUEST_REGISTER_REQUIRED") {
          showGuestRegisterRequiredModal();
          return;
        }

        if (code === "PUZZLE_REWARD_ALREADY_CLAIMED") {
          markPuzzleRewardAsClaimed(puzzleId);
          return;
        }

        if (code === "PUZZLE_REWARD_FORFEITED") {
          if (isPuzzleAzar) return;
          markPuzzleRewardAsFailed(puzzleId);
          return;
        }

        notification.error({
          message: "Recompensa",
          description:
            error?.response?.data?.message ||
            error?.message ||
            "No se pudo reclamar la recompensa del puzzle.",
        });
      } finally {
        setClaimingPuzzleRewardId("");
      }
    },
    [
      claimedPuzzleRewardMap,
      claimingPuzzleRewardId,
      isCurrentUserGuest,
      markPuzzleRewardAsClaimed,
      markPuzzleRewardAsFailed,
      showGuestRegisterRequiredModal,
      solvedPuzzleIdsSet,
    ]
  );
  const buildHashRouteUrl = useCallback((rawRoutePath) => {
    const routePathValue = String(rawRoutePath || "/game").trim();
    const normalizedRoutePath = routePathValue.startsWith("/")
      ? routePathValue
      : `/${routePathValue}`;
    if (!normalizedRoutePath) return "";

    if (typeof window === "undefined") {
      return `/#${normalizedRoutePath}`;
    }

    const origin = String(window.location.origin || "").trim();
    const pathname = String(window.location.pathname || "/").trim() || "/";
    const basePath = pathname.endsWith("/") ? pathname : `${pathname}/`;
    return `${origin}${basePath}#${normalizedRoutePath}`;
  }, []);
  const buildLobbyShareUrl = useCallback(
    (rawShareType, rawId) => {
      const shareType = String(rawShareType || "")
        .trim()
        .toLowerCase();
      const entityId = String(rawId || "").trim();
      if (!entityId) return "";
      if (!["tournament", "independent", "puzzle"].includes(shareType)) return "";

      // Short link (juegoxima.com/p/ID, /t/ID, /i/ID); guests can open it without registering.
      const shortShareUrl = buildShortShareUrl(GUEST_LINK_KINDS[shareType], entityId);
      if (shortShareUrl) return shortShareUrl;

      const params = new URLSearchParams();
      params.set("shareType", shareType);
      if (shareType === "puzzle") {
        params.set("puzzleId", entityId);
      } else {
        params.set("tournamentId", entityId);
      }

      return buildHashRouteUrl(`/game?${params.toString()}`);
    },
    [buildHashRouteUrl]
  );
  const handleCopyShareLink = useCallback(async (rawLink) => {
    const link = String(rawLink || "").trim();
    if (!link) return;

    let copied = false;
    try {
      if (typeof navigator !== "undefined" && navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        copied = true;
      }
    } catch (error) {
      console.log(error);
    }

    if (!copied && typeof document !== "undefined" && document?.body) {
      try {
        const input = document.createElement("textarea");
        input.value = link;
        input.setAttribute("readonly", "readonly");
        input.style.position = "fixed";
        input.style.opacity = "0";
        input.style.left = "-9999px";
        document.body.appendChild(input);
        input.focus();
        input.select();
        copied = document.execCommand("copy");
        document.body.removeChild(input);
      } catch (error) {
        console.log(error);
      }
    }

    if (copied) {
      notification.success({
        message: "Enlace copiado",
        description: link,
      });
      return;
    }

    notification.info({
      message: "Copia manual",
      description: link,
    });
  }, []);
  const resolvePuzzleIdForOpenRequest = useCallback(
    async (rawPuzzleId, rawPuzzleOrder, rawPuzzleMode = "") => {
      const explicitPuzzleId = String(rawPuzzleId || "").trim();
      const puzzleOrder = normalizePuzzleSequenceOrder(rawPuzzleOrder);

      if (puzzleOrder) {
        try {
          const response = await axios.get("/tournaments/puzzles", { params: { limit: 300 } });
          const serverPuzzleId = resolvePuzzleIdByOrder(
            response?.data?.puzzles,
            puzzleOrder,
            rawPuzzleMode
          );
          if (serverPuzzleId) return serverPuzzleId;
        } catch (error) {
          console.log(error);
        }
      }

      return explicitPuzzleId;
    },
    []
  );
  const openTournamentWaitingRoom = useCallback(
    async (match, options = {}) => {
      const tournamentId = String(match?._id || "").trim();
      if (!tournamentId) return;
      const autoPlay = options?.autoPlay === true;
      const cancelReturnTo = String(
        options?.cancelReturnTo || options?.returnTo || waitingRoomOpenRequest?.returnTo || ""
      ).trim();
      const startMs = new Date(match?.startingTime || 0).valueOf();
      const tournamentAlreadyStarted = !Number.isFinite(startMs) || startMs <= Date.now();
      const shouldAskForPassword =
        match?.hasMatchPassword && !options?.matchPassword;
      const matchPassword = shouldAskForPassword
        ? await requestMatchPassword(match)
        : String(options?.matchPassword || "").trim();
      if (shouldAskForPassword && !matchPassword) {
        navigateAfterCancelledWaitingRoomRequest(cancelReturnTo);
        return;
      }
      const { canJoin } = await checkAndConfirmTournamentPayment(match, {
        skipJoinRequest: false,
      });
      if (!canJoin) {
        navigateAfterCancelledWaitingRoomRequest(cancelReturnTo);
        return;
      }
      tournamentWaitingTargetRef.current = tournamentId;
      tournamentWaitingShouldRemainJoinedRef.current = true;
      setTournamentWaitingJoinLoading(true);

      try {
        const joinResponse = await axios.post("/tournaments/join", {
          id: tournamentId,
          matchPassword,
          tournamentPassword: matchPassword,
          joinPassword: matchPassword,
        });
        const joinPayload = joinResponse?.data || {};
        const serverTournamentPatch =
          joinPayload?.tournament && typeof joinPayload.tournament === "object"
            ? joinPayload.tournament
            : null;
        const serverJoinedUsers = ensureArray(
          joinPayload?.users ?? serverTournamentPatch?.users
        );
        const tournamentSequenceNumber = Math.max(
          0,
          Math.floor(
            normalizePublicSequenceNumberValue(options?.tournamentUiSequenceNumber) ||
              normalizePublicSequenceNumberValue(options?.tournamentSequenceNumber) ||
              resolvePublicSequenceNumber(match) ||
              resolvePublicSequenceNumber(joinPayload) ||
              resolvePublicSequenceNumber(serverTournamentPatch) ||
              0
          )
        );
        const nextWaitingRecord = {
          ...match,
          ...(serverTournamentPatch || {}),
          _id: tournamentId,
          id: tournamentId,
          ...(tournamentSequenceNumber > 0
            ? {
                tournamentUiSequenceNumber: tournamentSequenceNumber,
                tournamentSequenceNumber,
                tournamentNumber: tournamentSequenceNumber,
                displayNumber: tournamentSequenceNumber,
              }
            : {}),
          users: serverJoinedUsers.length > 0
            ? serverJoinedUsers
            : ensureArray(match?.users),
          paidUsers: ensureArray(
            joinPayload?.paidUsers ?? serverTournamentPatch?.paidUsers ?? match?.paidUsers
          ),
          connectedPlayers: Math.max(
            Number(joinPayload?.connectedPlayers) || 0,
            serverJoinedUsers.length,
            Number(match?.connectedPlayers) || 0
          ),
        };
        syncWalletFromJoinResponse(joinResponse?.data);
        if (matchPassword) {
          tournamentWaitingPasswordByIdRef.current[tournamentId] = matchPassword;
          persistTournamentMatchPasswordToSession(tournamentId, matchPassword);
        } else {
          delete tournamentWaitingPasswordByIdRef.current[tournamentId];
          clearTournamentMatchPasswordFromSession(tournamentId);
        }
        rememberLastJoinedTournament(tournamentId);
        if (serverJoinedUsers.length > 0) {
          hydrateJoinedUiPlayers(tournamentId, serverJoinedUsers);
          setOptimisticTournamentPlayersById((prev) => ({
            ...prev,
            [tournamentId]: serverJoinedUsers,
          }));
        } else {
          hydrateJoinedUiPlayers(tournamentId, match?.users);
          addJoinedUiPlayer(tournamentId, currentTournamentPlayer);
        }
        applyOptimisticTournamentJoin(tournamentId, match);
        setTournamentWaitingRecord(nextWaitingRecord);
        setTournamentWaitingOpen(!autoPlay || !tournamentAlreadyStarted);
        await Promise.all([fetchTournaments(), fetchLiveMatches()]);
        if (autoPlay && tournamentAlreadyStarted) {
          setTournamentWaitingOpen(false);
          handleJoin(tournamentId, {
            autoPlay: true,
            skipJoinRequest: true,
            matchPassword,
            match: nextWaitingRecord,
            ...(tournamentSequenceNumber > 0
              ? {
                  tournamentUiSequenceNumber: tournamentSequenceNumber,
                  tournamentSequenceNumber,
                }
              : {}),
            cancelReturnTo,
          });
          return;
        }
        const mustAutoLeave =
          String(tournamentWaitingTargetRef.current || "").trim() === tournamentId &&
          !tournamentWaitingShouldRemainJoinedRef.current;
        if (mustAutoLeave) {
          try {
            removeJoinedUiPlayer(tournamentId, currentTournamentPlayer);
            if (currentUserEmailNormalized) {
              removeJoinedUiPlayer(tournamentId, currentUserEmailNormalized);
            }
            delete tournamentWaitingPasswordByIdRef.current[tournamentId];
            clearTournamentMatchPasswordFromSession(tournamentId);
            await axios.post("/tournaments/leave", { id: tournamentId });
            await Promise.all([fetchTournaments(), fetchLiveMatches()]);
          } catch (leaveError) {
            console.log(leaveError);
          }
        }
      } catch (error) {
        console.error(error);
        removeJoinedUiPlayer(tournamentId, currentTournamentPlayer);
        if (currentUserEmailNormalized) {
          removeJoinedUiPlayer(tournamentId, currentUserEmailNormalized);
        }
        delete tournamentWaitingPasswordByIdRef.current[tournamentId];
        clearTournamentMatchPasswordFromSession(tournamentId);
        setTournamentWaitingOpen(false);
        const code = String(error?.response?.data?.code || "").trim().toUpperCase();
        if (code === "INSUFFICIENT_FUNDS") {
          notification.error({
            message: i18nT("lobby.pagoRequerido"),
            description:
              error?.response?.data?.message || "Lo siento, debes tener dinero suficiente para unirte.",
          });
          return;
        }
        notification.error({
          message: code.startsWith("MATCH_PASSWORD") ? "Contrase\u00f1a incorrecta" : "Join Error",
          description:
            error?.response?.data?.message ||
            error?.message ||
            "No se pudo entrar al torneo.",
        });
      } finally {
        setTournamentWaitingJoinLoading(false);
      }
    },
    [
      addJoinedUiPlayer,
      applyOptimisticTournamentJoin,
      currentTournamentPlayer,
      currentUserEmailNormalized,
      fetchLiveMatches,
      fetchTournaments,
      handleJoin,
      hydrateJoinedUiPlayers,
      removeJoinedUiPlayer,
      syncWalletFromJoinResponse,
      rememberLastJoinedTournament,
      checkAndConfirmTournamentPayment,
      navigateAfterCancelledWaitingRoomRequest,
      requestMatchPassword,
      waitingRoomOpenRequest?.returnTo,
    ]
  );
  const closeTournamentWaitingRoom = useCallback(async () => {
    const fallbackTournamentId = String(tournamentWaitingTargetRef.current || "").trim();
    const tournamentId = String(tournamentWaitingRecord?._id || fallbackTournamentId || "").trim();
    tournamentWaitingShouldRemainJoinedRef.current = false;
    if (tournamentId) {
      socket.emit(WAITING_ROOM_PRESENCE_LEAVE_EVENT, { tournamentId });
    }
    setTournamentWaitingJoinLoading(true);
    const waitingRoomReturnTo = waitingRoomOpenRequest?.returnTo;
    const returnTo = typeof waitingRoomReturnTo === "string" && waitingRoomReturnTo.trim()
      ? waitingRoomReturnTo.trim()
      : "";
    try {
      if (tournamentId) {
            removeJoinedUiPlayer(tournamentId, currentTournamentPlayer);
            if (currentUserEmailNormalized) {
              removeJoinedUiPlayer(tournamentId, currentUserEmailNormalized);
            }
            delete tournamentWaitingPasswordByIdRef.current[tournamentId];
            clearTournamentMatchPasswordFromSession(tournamentId);
            await axios.post("/tournaments/leave", {
              id: tournamentId,
              cancelFindingUser: true,
            });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setTournamentWaitingOpen(false);
      setTournamentWaitingJoinLoading(false);
      setTournamentWaitingRecord(null);
      fetchTournaments().catch((err) => console.log(err));
      fetchLiveMatches().catch((err) => console.log(err));
      if (returnTo) {
        navigate(returnTo, { replace: true });
        return;
      }
      const fallbackPath = location.pathname === "/game/history" ? "/game/history" : "/game";
      navigate(fallbackPath, { replace: true });
    }
  }, [
    currentTournamentPlayer,
    currentUserEmailNormalized,
    fetchLiveMatches,
    fetchTournaments,
    removeJoinedUiPlayer,
    tournamentWaitingRecord?._id,
    waitingRoomOpenRequest?.returnTo,
    navigate,
    location.pathname,
  ]);
  const playFromTournamentWaitingRoom = useCallback(() => {
    const tournamentId = String(tournamentWaitingRecord?._id || "").trim();
    if (!tournamentId || tournamentWaitingJoinLoading) return;
    const startMs = new Date(tournamentWaitingRecord?.startingTime || 0).valueOf();
    if (Number.isFinite(startMs) && startMs > Date.now()) {
      notification.info({
        message: i18nT("lobby.salaDeEspera"),
        description: i18nT("lobby.todaviaNoIniciaEsperaAQueElTorneo"),
      });
      return;
    }
    tournamentWaitingTargetRef.current = tournamentId;
    tournamentWaitingShouldRemainJoinedRef.current = true;
    socket.emit(WAITING_ROOM_PRESENCE_LEAVE_EVENT, { tournamentId });
    setTournamentWaitingOpen(false);
    const matchPassword = String(
      tournamentWaitingPasswordByIdRef.current[tournamentId] ||
        readTournamentMatchPasswordFromSession(tournamentId) ||
        ""
    ).trim();
    const waitingSequenceNumber =
      normalizePublicSequenceNumberValue(tournamentWaitingRecord?.tournamentUiSequenceNumber) ||
      resolvePublicSequenceNumber(tournamentWaitingRecord || {});
    handleJoin(tournamentId, {
      autoPlay: true,
      skipJoinRequest: true,
      matchPassword,
      match: tournamentWaitingRecord,
      ...(waitingSequenceNumber > 0
        ? {
            tournamentUiSequenceNumber: waitingSequenceNumber,
            tournamentSequenceNumber: waitingSequenceNumber,
          }
        : {}),
      cancelReturnTo: waitingRoomOpenRequest?.returnTo,
    });
  }, [
    handleJoin,
    tournamentWaitingJoinLoading,
    tournamentWaitingRecord,
    waitingRoomOpenRequest?.returnTo,
  ]);

  useEffect(() => {
    const tournamentId = String(tournamentWaitingRecord?._id || "").trim();
    if (!tournamentWaitingOpen || !tournamentId) return undefined;

    const payload = { tournamentId };
    let retryTimerId = null;
    const emitPresenceJoin = () => {
      emitSocketVerify();
      socket.emit(WAITING_ROOM_PRESENCE_REQUEST_EVENT, payload);
      socket.emit(WAITING_ROOM_PRESENCE_JOIN_EVENT, payload);
      if (typeof window !== "undefined") {
        if (retryTimerId) window.clearTimeout(retryTimerId);
        retryTimerId = window.setTimeout(() => {
          socket.emit(WAITING_ROOM_PRESENCE_JOIN_EVENT, payload);
        }, 350);
      }
    };

    emitPresenceJoin();
    socket.on("connect", emitPresenceJoin);

    return () => {
      if (retryTimerId && typeof window !== "undefined") {
        window.clearTimeout(retryTimerId);
      }
      socket.off("connect", emitPresenceJoin);
      socket.emit(WAITING_ROOM_PRESENCE_LEAVE_EVENT, payload);
    };
  }, [tournamentWaitingOpen, tournamentWaitingRecord?._id]);

  const handleModeChange = useCallback((mode, options = {}) => {
    const markComplete = options?.markComplete !== false;
    const normalizedMode = normalizeModeAlias(mode, "dice");

    setFormData((prev) => {
      const resolvedPlayType = resolvePlayTypeFromMode(normalizedMode);
      return {
        ...prev,
        mode: normalizedMode,
        memberSize: normalizeStoredMemberSize(
          resolvedPlayType,
          prev.memberSize,
          canUseExtendedPlayerOptions
        ),
      };
    });

    if (markComplete) {
      setPanelProgress((prev) => ({
        ...prev,
        modeSelected: true,
      }));
    }
  }, [canUseExtendedPlayerOptions]);

  const handleMemberSizeSelect = useCallback((value) => {
    const normalizedValue = normalizeStoredMemberSize(
      playType,
      value,
      canUseExtendedPlayerOptions
    );
    setFormData((prev) => ({ ...prev, memberSize: normalizedValue }));
    setPanelProgress((prev) => ({
      ...prev,
      playersSelected: true,
    }));
  }, [canUseExtendedPlayerOptions, playType]);

  const handleTurnTimeSelect = useCallback((value) => {
    setFormData((prev) => ({ ...prev, havingTime: String(value) }));
    setPanelProgress((prev) => ({
      ...prev,
      timeSelected: true,
    }));
  }, []);

  const handleBotDifficultySelect = useCallback((value) => {
    const normalized = normalizeBotDifficulty(value);
    setFormData((prev) => ({ ...prev, botDifficulty: normalized }));
  }, []);

  const handleBotModeSelect = useCallback((value) => {
    const normalized = normalizeBotModeValue(value);
    if (!BOT_ENABLED_MODE_SET.has(normalized)) return;
    handleModeChange(normalized, { markComplete: true });
  }, [handleModeChange]);

  const startDateInputValue = useMemo(() => {
    const current = moment(formData.startingTime);
    return current.isValid() ? current.format("YYYY-MM-DD") : "";
  }, [formData.startingTime]);

  const startTimeInputValue = useMemo(() => {
    const current = moment(formData.startingTime);
    return current.isValid() ? current.format("HH:mm") : "";
  }, [formData.startingTime]);

  const handleTournamentNameChange = useCallback((value) => {
    setFormData((prev) => ({
      ...prev,
      tournamentName: value,
    }));
  }, []);

  const handleTournamentRewardValueChange = useCallback((value) => {
    setFormData((prev) => ({
      ...prev,
      tournamentRewardValue: String(value ?? "").trim(),
    }));
  }, []);
  const handleTournamentWinnerPrizeValueChange = useCallback((value) => {
    setFormData((prev) => ({
      ...prev,
      tournamentWinnerPrizeValue: String(value ?? "").trim(),
    }));
  }, []);

  const handleTournamentPaymentEnabledChange = useCallback((checked) => {
    setFormData((prev) => ({
      ...prev,
      tournamentPaymentEnabled: Boolean(checked),
      tournamentRewardValue: checked ? String(prev.tournamentRewardValue || "") : "",
    }));
  }, []);

  const handleMatchPasswordEnabledChange = useCallback((checked) => {
    setFormData((prev) => ({
      ...prev,
      hasMatchPassword: Boolean(checked),
      matchPassword: checked ? prev.matchPassword : "",
    }));
  }, []);

  const handleMatchPasswordChange = useCallback((value) => {
    setFormData((prev) => ({
      ...prev,
      matchPassword: normalizeMatchPassword(value),
    }));
  }, []);

  const handleCreationDateChange = useCallback((value) => {
    setFormData((prev) => ({
      ...prev,
      createdAtDate: String(value || "").trim(),
    }));
  }, []);

  const handleStartDateChange = useCallback((value) => {
    const normalizedDate = String(value || "").trim();
    if (!normalizedDate) return;

    setFormData((prev) => {
      const baseTime = moment(prev.startingTime);
      const safeBaseTime = baseTime.isValid() ? baseTime : moment();
      const next = moment(
        `${normalizedDate} ${safeBaseTime.format("HH:mm")}`,
        "YYYY-MM-DD HH:mm",
        true
      );
      if (!next.isValid()) return prev;
      return {
        ...prev,
        startingTime: next,
      };
    });
  }, []);

  const handleStartTimeChange = useCallback((value) => {
    const normalizedTime = String(value || "").trim();
    if (!normalizedTime) return;

    setFormData((prev) => {
      const baseDate = moment(prev.startingTime);
      const safeBaseDate = baseDate.isValid() ? baseDate : moment();
      const next = moment(
        `${safeBaseDate.format("YYYY-MM-DD")} ${normalizedTime}`,
        "YYYY-MM-DD HH:mm",
        true
      );
      if (!next.isValid()) return prev;
      return {
        ...prev,
        startingTime: next,
      };
    });
  }, []);

  const handleLocalNameChange = useCallback((index, value) => {
    setLocalPlayerNames((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }, []);

  const createTournament = useCallback(
    async ({
      autoJoin = false,
      autoName = false,
      autoPlay = false,
      quickMatch = false,
      forceStartNow = false,
      silentSuccess = false,
      skipJoinRequest = false,
      overrideData = null,
    } = {}) => {
      if (creating) return null;

      const selectedData = {
        ...formData,
        ...(overrideData || {}),
      };

      const { tournamentName, startingTime, duration, havingTime } = selectedData;
      const selectedMode = normalizeModeAlias(selectedData.mode, "dice");
      const selectedPlayType = resolvePlayTypeFromMode(selectedMode);
      const selectedMemberSize = normalizeStoredMemberSize(
        selectedPlayType,
        selectedData.memberSize,
        canUseExtendedPlayerOptions
      );
      const requestedTournamentLifetimeMinutes = Number(
        selectedData.tournamentLifetimeMinutes
      );
      const wantsTournamentPayment = Boolean(selectedData.tournamentPaymentEnabled);
      const hasCustomTournamentLifetime =
        Number.isFinite(requestedTournamentLifetimeMinutes) &&
        requestedTournamentLifetimeMinutes >= SUPER_TOURNAMENT_MIN_MINUTES;
      const rawTournamentRewardValue = wantsTournamentPayment
        ? parseTournamentRewardValue(selectedData.tournamentRewardValue)
        : Number.NaN;
      const normalizedTournamentRewardValue = wantsTournamentPayment
        ? normalizeTournamentRewardValue(
            selectedData.tournamentRewardValue,
            TOURNAMENT_DEFAULT_REWARD_VALUE
          )
        : 0;
      const rawTournamentWinnerPrizeValue = parseTournamentRewardValue(
        selectedData.tournamentWinnerPrizeValue
      );
      const normalizedTournamentWinnerPrizeValue = hasDirectAdminAccess
        ? normalizeTournamentRewardValue(
            selectedData.tournamentWinnerPrizeValue,
            TOURNAMENT_DEFAULT_REWARD_VALUE
          )
        : 0;
      const creatorDisplayName = String(displayName || "Jugador").trim() || "Jugador";
      const creatorCimaxMode = resolveCimaxModeFieldFromMode(
        selectedMode,
        selectedData.freeBoxBaseMode
      );
      const creatorCimaxLabel = creatorCimaxMode ? formatCimaxModeLabel(creatorCimaxMode) : "";
      const creatorCimaxScore = creatorCimaxMode
        ? formatCimaxScoreValue(cimaxScores?.[creatorCimaxMode])
        : "";
      const generatedName = creatorCimaxMode
        ? `Desafio creado por ${creatorDisplayName} (CIMAX ${creatorCimaxLabel}: ${creatorCimaxScore})`
        : `Desafio creado por ${creatorDisplayName}`;
      const normalizedName = String(tournamentName || "").trim() || generatedName;
      const wantsMatchPassword = Boolean(selectedData.hasMatchPassword);
      const normalizedMatchPassword = normalizeMatchPassword(selectedData.matchPassword);
      const createdAtInput = String(selectedData.createdAtDate || "").trim();
      const createdAtMoment = moment(createdAtInput, "YYYY-MM-DD", true);
      const forceIndependentFromTournamentView =
        !isAdminTab && matchmakingQuickView === "torneos";
      const requestedCreationSection = String(selectedData.creationSection || "")
        .trim()
        .toLowerCase();
      const creationSection =
        requestedCreationSection === "independientes" ||
        requestedCreationSection === "torneos"
          ? requestedCreationSection
          : forceIndependentFromTournamentView
          ? "independientes"
          : matchmakingQuickView;
      const shouldCreateIndependentMatch =
        forceIndependentFromTournamentView ||
        resolveIndependentMatchFromCreation(selectedData, creationSection);

      const startMs = forceStartNow
        ? Date.now()
        : startingTime && typeof startingTime.valueOf === "function"
        ? Number(startingTime.valueOf())
        : NaN;

      if (!Number.isFinite(startMs)) {
        notification.error({
          message: i18nT("lobby.validationError"),
          description: i18nT("lobby.pleaseSelectAValidStartTime"),
        });
        return null;
      }

      if (startMs < Date.now() - 30 * 1000) {
        notification.error({
          message: i18nT("lobby.validationError"),
          description:
            i18nT("lobby.startingTimeMustBeSetToTheCurrent"),
        });
        return null;
      }

      if (Number(duration) < 1) {
        notification.error({
          message: i18nT("lobby.validationError"),
          description: i18nT("lobby.durationMustBeAtLeastDay"),
        });
        return null;
      }

      if (!TIME_OPTIONS.includes(Number(havingTime))) {
        notification.error({
          message: i18nT("lobby.validationError"),
          description: `Having time must be one of: ${TIME_OPTIONS.join(", ")} minutes.`,
        });
        return null;
      }

      if (
        selectedData.tournamentLifetimeMinutes !== undefined &&
        selectedData.tournamentLifetimeMinutes !== null &&
        String(selectedData.tournamentLifetimeMinutes).trim() !== "" &&
        !hasCustomTournamentLifetime
      ) {
        notification.error({
          message: i18nT("lobby.validationError"),
          description: `Tournament time must be at least ${SUPER_TOURNAMENT_MIN_MINUTES} minutes.`,
        });
        return null;
      }

      if (
        wantsTournamentPayment &&
        (!Number.isFinite(rawTournamentRewardValue) || rawTournamentRewardValue < 1)
      ) {
        notification.error({
          message: i18nT("lobby.validationError"),
          description: wantsTournamentPayment && rawTournamentRewardValue < 1
            ? "El monto de pago debe ser al menos 1X."
            : "El monto de pago debe ser un número valido.",
        });
        return null;
      }

      if (
        hasDirectAdminAccess &&
        Number.isFinite(rawTournamentWinnerPrizeValue) &&
        rawTournamentWinnerPrizeValue < 0
      ) {
        notification.error({
          message: i18nT("lobby.validationError"),
          description: i18nT("lobby.elMontoDelPremioDelGanadorDebeSer"),
        });
        return null;
      }

      if (wantsMatchPassword && !isValidMatchPassword(normalizedMatchPassword)) {
        notification.error({
          message: i18nT("lobby.contrasenaInvalidae66fdf"),
          description: i18nT("lobby.laContrasenaDelPartidoDebeSerNumericaY"),
        });
        return null;
      }

      const isLocalFriendsMode = LOCAL_FRIENDS_MODES.has(selectedMode);
      const requiredLocalPlayers = Math.max(0, selectedMemberSize - 1);
      const normalizedLocalPlayers = ensureArray(selectedData.localPlayers)
        .slice(0, requiredLocalPlayers)
        .map((name) => String(name || "").trim());

      if (
        isLocalFriendsMode &&
        (normalizedLocalPlayers.length !== requiredLocalPlayers ||
          normalizedLocalPlayers.some((name) => !name))
      ) {
        notification.error({
          message: i18nT("lobby.validationError"),
          description: i18nT("lobby.pleaseAddTheNamesOfAllLocalPlayers"),
        });
        return null;
      }

      const payload = {
        ...selectedData,
        tournamentName: normalizedName,
        mode: selectedMode,
        creationSection,
        isIndependentMatch: shouldCreateIndependentMatch,
        memberSize: selectedMemberSize,
        duration: Number(selectedData.duration),
        havingTime: Number(selectedData.havingTime),
        botDifficulty: normalizeBotDifficulty(selectedData.botDifficulty),
        localPlayers: isLocalFriendsMode ? normalizedLocalPlayers : [],
        startingTime: new Date(startMs).toISOString(),
        createdAt: createdAtMoment.isValid()
          ? createdAtMoment.startOf("day").toISOString()
          : undefined,
        isQuickMatch: shouldCreateIndependentMatch ? false : quickMatch,
        tournamentLifetimeMinutes: hasCustomTournamentLifetime
          ? Math.min(
              SUPER_TOURNAMENT_MAX_MINUTES,
              Math.floor(requestedTournamentLifetimeMinutes)
            )
          : undefined,
        tournamentRewardValue:
          hasDirectAdminAccess && wantsTournamentPayment
            ? normalizedTournamentRewardValue
            : 0,
        tournamentWinnerPrizeValue:
          hasDirectAdminAccess ? normalizedTournamentWinnerPrizeValue : 0,
        hasMatchPassword: wantsMatchPassword,
        matchPassword: wantsMatchPassword ? normalizedMatchPassword : "",
        tournamentPassword: wantsMatchPassword ? normalizedMatchPassword : "",
        joinPassword: wantsMatchPassword ? normalizedMatchPassword : "",
      };

      try {
        setCreating(true);
        const response = await axios.post("/tournaments", payload);
        const created = response.data;

        socket.emit("newTournament", created);
        if (!silentSuccess) {
          notification.success({
            message: i18nT("lobby.tournamentCreated"),
            description: i18nT("lobby.theTournamentHasBeenCreatedSuccessfully"),
          });
        }

        setFormData((prev) => ({
          ...prev,
          tournamentName: autoName ? "" : prev.tournamentName,
          tournamentRewardValue: "",
          tournamentWinnerPrizeValue: "",
          tournamentPaymentEnabled: false,
          matchPassword: "",
          hasMatchPassword: false,
        }));
        if (isLocalFriendsMode) {
          setLocalPlayerNames(["", "", ""]);
        }

        await fetchTournaments();

        if (autoJoin && created?._id) {
          if (skipJoinRequest) {
            const suffix = autoPlay ? "?autoplay=1" : "";
            const tournamentSequenceNumber = resolvePublicSequenceNumber(created);
            const navigationState =
              wantsMatchPassword || tournamentSequenceNumber > 0
                ? {
                    ...(wantsMatchPassword
                      ? { protectedMatchPassword: normalizedMatchPassword }
                      : {}),
                    ...(tournamentSequenceNumber > 0
                      ? {
                          tournamentUiSequenceNumber: tournamentSequenceNumber,
                          tournamentSequenceNumber,
                        }
                      : {}),
                  }
                : undefined;
            if (wantsMatchPassword) {
              const createdTournamentId = String(created?._id || "").trim();
              persistTournamentMatchPasswordToSession(createdTournamentId, normalizedMatchPassword);
            }
            navigate(`/game/${created._id}${suffix}`, { state: navigationState });
          } else {
            const tournamentSequenceNumber = resolvePublicSequenceNumber(created);
            handleJoin(created._id, {
              autoPlay,
              match: {
                ...created,
                ...(tournamentSequenceNumber > 0
                  ? {
                      tournamentUiSequenceNumber: tournamentSequenceNumber,
                      tournamentSequenceNumber,
                      tournamentNumber: tournamentSequenceNumber,
                      displayNumber: tournamentSequenceNumber,
                    }
                  : {}),
              },
              ...(tournamentSequenceNumber > 0
                ? {
                    tournamentUiSequenceNumber: tournamentSequenceNumber,
                    tournamentSequenceNumber,
                  }
                : {}),
            });
          }
        }

        return created;
      } catch (error) {
        console.error("Error creating tournament:", error);
        const status = error?.response?.status;
        const backendMsg = error?.response?.data?.message;

        if (status === 401 || status === 403) {
          notification.error({
            message: i18nT("lobby.sessionError"),
            description: i18nT("lobby.yourSessionExpiredPleaseLogInAgain"),
          });
          navigate("/login");
        } else {
          notification.error({
            message: "Error",
            description:
              backendMsg || error?.message || "Failed to create the tournament.",
          });
        }

        return null;
      } finally {
        setCreating(false);
      }
    },
    [
      canUseExtendedPlayerOptions,
      cimaxScores,
      creating,
      displayName,
      fetchTournaments,
      formData,
      hasDirectAdminAccess,
      handleJoin,
      isAdminTab,
      navigate,
      matchmakingQuickView,
    ]
  );

  const handleQuickPlay = useCallback(async (override = {}) => {
    if (!isAdminTab && matchmakingQuickView === "torneos") {
      setMatchmakingQuickView("independientes");
      setQuickViewButtonPulseKey("independientes");
      window.setTimeout(() => {
        setQuickViewButtonPulseKey((current) =>
          current === "independientes" ? "" : current
        );
      }, 800);
      return;
    }

    const creatingIndependentMatch = matchmakingQuickView === "independientes";
    const expectedMode = normalizeModeAlias(override.mode || formData.mode, "dice");
    const expectedTurnTimeRaw = Number(override.havingTime ?? formData.havingTime ?? DEFAULT_TURN_TIME);
    const expectedTurnTime = TIME_OPTIONS.includes(expectedTurnTimeRaw)
      ? expectedTurnTimeRaw
      : DEFAULT_TURN_TIME;
    const expectedFreeBoxBaseMode = ONLINE_LOBBY_MODES.has(
      normalizeModeAlias(override.freeBoxBaseMode || formData.mode, "")
    )
      ? normalizeModeAlias(override.freeBoxBaseMode || formData.mode, "cienzar")
      : "cienzar";
    const expectedBotDifficulty = normalizeBotDifficulty(
      override.botDifficulty ?? formData.botDifficulty
    );
    const expectedPlayType = resolvePlayTypeFromMode(expectedMode);
    const expectedMemberSize = normalizeStoredMemberSize(
      expectedPlayType,
      override.memberSize ?? formData.memberSize ?? 2,
      canUseExtendedPlayerOptions
    );
    const isLocalFriendsMode = LOCAL_FRIENDS_MODES.has(expectedMode);
    const requestedTournamentLifetimeMinutes =
      !creatingIndependentMatch && canConfigureSuperTournamentDuration
        ? normalizedSuperTournamentMinutes
        : 0;

    if (isLocalFriendsMode) {
      const requiredLocalPlayers = Math.max(0, expectedMemberSize - 1);
      const normalizedLocalPlayers = ensureArray(override.localPlayers)
        .slice(0, requiredLocalPlayers)
        .map((name) => String(name || "").trim());

      await createTournament({
        autoJoin: true,
        autoName: true,
        autoPlay: true,
        skipJoinRequest: true,
        quickMatch: false,
        forceStartNow: true,
        silentSuccess: true,
        overrideData: {
          ...formData,
          mode: expectedMode,
          memberSize: expectedMemberSize,
          havingTime: String(expectedTurnTime),
          localPlayers: normalizedLocalPlayers,
          freeBoxRules: Boolean(override.freeBoxRules),
        },
      });
      return;
    }

    if (requestedTournamentLifetimeMinutes > 0) {
      await createTournament({
        autoJoin: true,
        autoName: true,
        autoPlay: true,
        skipJoinRequest: true,
        quickMatch: true,
        forceStartNow: true,
        silentSuccess: true,
        overrideData: {
          ...formData,
          mode: expectedMode,
          memberSize: expectedMemberSize,
          havingTime: String(expectedTurnTime),
          botDifficulty: expectedBotDifficulty,
          tournamentLifetimeMinutes: requestedTournamentLifetimeMinutes,
          freeBoxBaseMode: expectedMode === "freebox" ? expectedFreeBoxBaseMode : undefined,
        },
      });
      return;
    }

    if (expectedMode === "freebox") {
      await createTournament({
        autoJoin: true,
        autoName: true,
        autoPlay: true,
        skipJoinRequest: true,
        quickMatch: false,
        forceStartNow: true,
        silentSuccess: true,
        overrideData: {
          ...formData,
          mode: "freebox",
          memberSize: expectedMemberSize,
          havingTime: String(expectedTurnTime),
          freeBoxBaseMode: expectedFreeBoxBaseMode,
        },
      });
      return;
    }

    await createTournament({
      autoJoin: true,
      autoName: true,
      autoPlay: true,
      skipJoinRequest: true,
      quickMatch: !creatingIndependentMatch && ONLINE_LOBBY_MODES.has(expectedMode),
      forceStartNow: true,
      silentSuccess: true,
      overrideData: {
        ...formData,
        mode: expectedMode,
        memberSize: expectedMemberSize,
        havingTime: String(expectedTurnTime),
        botDifficulty: expectedBotDifficulty,
        isIndependentMatch: creatingIndependentMatch,
      },
    });
  }, [
    canUseExtendedPlayerOptions,
    canConfigureSuperTournamentDuration,
    createTournament,
    formData,
    isAdminTab,
    matchmakingQuickView,
    normalizedSuperTournamentMinutes,
  ]);

  const createLobbyIndependentChallenge = useCallback(
    async (preferences = {}) => {
      const selectedPlayType = "online";
      const selectedMode = normalizeStoredModeForPlayType(
        selectedPlayType,
        preferences.mode || formData.mode || "dice"
      );
      const selectedMemberSize = normalizeStoredMemberSize(
        selectedPlayType,
        preferences.memberSize ?? formData.memberSize ?? 2,
        canUseExtendedPlayerOptions
      );
      const selectedTurnTimeRaw = Number(
        preferences.havingTime ?? formData.havingTime ?? DEFAULT_TURN_TIME
      );
      const selectedTurnTime = TIME_OPTIONS.includes(selectedTurnTimeRaw)
        ? selectedTurnTimeRaw
        : DEFAULT_TURN_TIME;
      const selectedDifficulty =
        selectedPlayType === "online"
          ? "injusto"
          : normalizeBotDifficulty(preferences.botDifficulty ?? formData.botDifficulty);

      setMatchmakingQuickView("independientes");
      setPlayType(selectedPlayType);
      setPanelProgress(
        normalizeStoredPanelProgress(selectedPlayType, {
          playersSelected: true,
          modeSelected: true,
          timeSelected: true,
        })
      );
      setFormData((prev) => ({
        ...prev,
        mode: selectedMode,
        memberSize: selectedMemberSize,
        havingTime: String(selectedTurnTime),
        botDifficulty: selectedDifficulty,
        hasMatchPassword: false,
        matchPassword: "",
      }));
      setLocalPlayerNames(["", "", ""]);
      setQuickViewButtonPulseKey("independientes");
      window.setTimeout(() => {
        setQuickViewButtonPulseKey((current) =>
          current === "independientes" ? "" : current
        );
      }, 800);

      const created = await createTournament({
        autoJoin: false,
        autoName: true,
        autoPlay: false,
        quickMatch: false,
        forceStartNow: true,
        silentSuccess: true,
        overrideData: {
          ...formData,
          mode: selectedMode,
          memberSize: selectedMemberSize,
          havingTime: String(selectedTurnTime),
          botDifficulty: selectedDifficulty,
          creationSection: "independientes",
          isIndependentMatch: true,
          isQuickMatch: false,
          hasMatchPassword: false,
          matchPassword: "",
          tournamentPassword: "",
          joinPassword: "",
          tournamentLifetimeMinutes: 0,
        },
      });

      const tournamentId = String(created?._id || "").trim();
      if (!tournamentId) return null;

      let visibleCreated = {
        ...created,
        mode: selectedMode,
        memberSize: selectedMemberSize,
        havingTime: selectedTurnTime,
        botDifficulty: selectedDifficulty,
        isIndependentMatch: true,
        connectedPlayers: Math.max(1, Number(created?.connectedPlayers) || 0),
      };

      let challengeActivated = false;
      let activationError = null;
      try {
        await axios.post("/tournaments/play", { id: tournamentId });
        challengeActivated = true;
      } catch (error) {
        activationError = error;
        console.log(error);
      }

      const creatorIdentity =
        String(created?.createdBy || "").trim() ||
        String(currentTournamentPlayer?.userId || "").trim() ||
        String(currentUserEmailNormalized || "").trim();
      const normalizedCreatorIdentity = normalizeJoinedUiIdentity(creatorIdentity);
      const users = ensureArray(created?.users);
      const hasCreatorEntry =
        normalizedCreatorIdentity &&
        users.some(
          (entry) =>
            normalizeJoinedUiIdentity(entry?.userId || entry?.email || entry?.id) ===
            normalizedCreatorIdentity
        );
      const visibleUsers = hasCreatorEntry
        ? users.map((entry) =>
            normalizeJoinedUiIdentity(entry?.userId || entry?.email || entry?.id) ===
            normalizedCreatorIdentity
              ? { ...entry, state: Math.max(2, Number(entry?.state) || 0) }
              : entry
          )
        : [
            {
              userId: creatorIdentity,
              ranking: 0,
              state: 2,
            },
            ...users,
          ];

      visibleCreated = {
        ...visibleCreated,
        users: visibleUsers,
      };

      const mergeCreatedChallengeIntoList = (rawList) => {
        const source = ensureArray(rawList);
        let matched = false;
        const merged = source.map((entry) => {
          if (String(entry?._id || "").trim() !== tournamentId) return entry;
          matched = true;
          return {
            ...visibleCreated,
            ...entry,
            mode: entry?.mode || visibleCreated.mode,
            memberSize: Number(entry?.memberSize) || visibleCreated.memberSize,
            havingTime: Number(entry?.havingTime) || visibleCreated.havingTime,
            botDifficulty: entry?.botDifficulty || visibleCreated.botDifficulty,
            isIndependentMatch: true,
            connectedPlayers: Math.max(
              1,
              Number(entry?.connectedPlayers) ||
                Number(visibleCreated.connectedPlayers) ||
                0
            ),
            users: ensureArray(entry?.users).length ? entry.users : visibleCreated.users,
          };
        });

        if (matched) return merged;
        return [
          visibleCreated,
          ...source.filter((entry) => String(entry?._id || "").trim() !== tournamentId),
        ];
      };

      addJoinedUiPlayer(tournamentId, currentTournamentPlayer);
      applyOptimisticTournamentJoin(tournamentId);
      setTournaments((prev) => {
        const updated = mergeCreatedChallengeIntoList(prev);
        applyFilter(updated, filter);
        return updated;
      });

      if (challengeActivated) {
        socket.emit("newTournament", visibleCreated);
      }

      try {
        const response = await axios.get("/tournaments");
        const updated = mergeCreatedChallengeIntoList(response.data);
        setTournaments(updated);
        applyFilter(updated, filter);
      } catch (error) {
        console.log(error);
      }

      if (activationError) {
        notification.warning({
          message: i18nT("lobby.desafioCreado"),
          description:
            i18nT("lobby.elDesafioSeCreoEnLaListaPero"),
        });
        return visibleCreated;
      }

      notification.success({
        message: i18nT("lobby.desafioCreado"),
        description: i18nT("lobby.tuPartidoIndependienteYaApareceEnLaLista"),
      });

      return visibleCreated;
    },
    [
      addJoinedUiPlayer,
      applyFilter,
      applyOptimisticTournamentJoin,
      canUseExtendedPlayerOptions,
      createTournament,
      currentTournamentPlayer,
      currentUserEmailNormalized,
      filter,
      formData,
      normalizeJoinedUiIdentity,
    ]
  );

  const handlePlayTypeSelect = useCallback(
    (nextType) => {
      setPlayType(nextType);
      setPanelProgress(buildPanelProgress(nextType));
      const normalizedCurrentMode = normalizeModeAlias(formData.mode, "dice");

      if (nextType === "online") {
        if (!ONLINE_LOBBY_MODES.has(normalizedCurrentMode)) {
          handleModeChange("dice", { markComplete: false });
        }
        return;
      }

      if (nextType === "bot") {
        setFormData((prev) => ({
          ...prev,
          mode: normalizeSelectableBotModeValue(prev.mode, "botlocalfree"),
          memberSize: normalizeStoredMemberSize(
            nextType,
            prev.memberSize,
            canUseExtendedPlayerOptions
          ),
          botDifficulty: normalizeBotDifficulty(prev.botDifficulty),
        }));
        return;
      }

      if (nextType === "local") {
        setFormData((prev) => ({
          ...prev,
          mode: ONLINE_LOBBY_MODES.has(normalizeModeAlias(prev.mode, ""))
            ? normalizeModeAlias(prev.mode, "dice")
            : "dice",
          memberSize: normalizeStoredMemberSize(
            nextType,
            prev.memberSize,
            canUseExtendedPlayerOptions
          ),
        }));
        return;
      }

      if (nextType === "freebox") {
        setFormData((prev) => ({
          ...prev,
          mode: ONLINE_LOBBY_MODES.has(normalizeModeAlias(prev.mode, ""))
            ? normalizeModeAlias(prev.mode, "dice")
            : "dice",
          memberSize: normalizeStoredMemberSize(
            nextType,
            prev.memberSize,
            canUseExtendedPlayerOptions
          ),
        }));
      }
    },
    [canUseExtendedPlayerOptions, formData.mode, handleModeChange]
  );

  useEffect(() => {
    if (playType !== "local") return;

    const required = Math.max(0, Number(formData.memberSize) - 1);
    setLocalPlayerNames((prev) => {
      const next = [...prev];
      while (next.length < required) next.push("");
      return next.slice(0, Math.max(required, 3));
    });
  }, [formData.memberSize, playType]);

  const requiredLocalPlayers = useMemo(
    () => (playType === "local" ? Math.max(0, Number(formData.memberSize) - 1) : 0),
    [formData.memberSize, playType]
  );

  const localNamesComplete = useMemo(
    () =>
      requiredLocalPlayers === 0 ||
      localPlayerNames
        .slice(0, requiredLocalPlayers)
        .every((name) => String(name || "").trim().length > 0),
    [localPlayerNames, requiredLocalPlayers]
  );

  const showOnlineModeStep =
    playType === "online" || playType === "local" || playType === "freebox";
  const showPlayersStep =
    playType !== "bot" &&
    (playType === "freebox" ||
      ((playType === "online" || playType === "local") && panelProgress.modeSelected));
  const showTimeStep =
    (playType === "online" || playType === "local") &&
    panelProgress.modeSelected &&
    panelProgress.playersSelected;
  const canStartQuickPlay = useMemo(() => {
    if (creating) return false;
    if (
      canConfigureSuperTournamentDuration &&
      normalizedSuperTournamentMinutes < SUPER_TOURNAMENT_MIN_MINUTES
    ) {
      return false;
    }

    if (playType === "online") {
      return (
        panelProgress.playersSelected &&
        panelProgress.modeSelected &&
        panelProgress.timeSelected
      );
    }

    if (playType === "freebox") {
      return panelProgress.playersSelected;
    }

    if (playType === "bot") {
      return true;
    }

    if (playType === "local") {
      return (
        panelProgress.playersSelected &&
        panelProgress.modeSelected &&
        panelProgress.timeSelected &&
        localNamesComplete
      );
    }

    return false;
  }, [
    canConfigureSuperTournamentDuration,
    creating,
    localNamesComplete,
    normalizedSuperTournamentMinutes,
    panelProgress,
    playType,
  ]);

  useEffect(() => {
    const storedLobbyCreatePreferences = (() => {
      if (typeof window === "undefined") return null;
      try {
        const rawValue = window.sessionStorage.getItem(PENDING_LOBBY_CREATE_STORAGE_KEY);
        if (!rawValue) return null;
        const parsed = JSON.parse(rawValue);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
        const createdAt = Number(parsed.createdAt);
        if (
          Number.isFinite(createdAt) &&
          createdAt > 0 &&
          Date.now() - createdAt > PENDING_LOBBY_CREATE_MAX_AGE_MS
        ) {
          window.sessionStorage.removeItem(PENDING_LOBBY_CREATE_STORAGE_KEY);
          return null;
        }

        const playType = normalizeStoredPlayType(parsed.playType || "online");
        const mode = normalizeStoredModeForPlayType(playType, parsed.mode || "dice");
        const memberSize = normalizeStoredMemberSize(
          playType,
          parsed.memberSize,
          canUseExtendedPlayerOptions
        );
        const havingTime = normalizeStoredTurnTime(parsed.havingTime);
        const botDifficulty = normalizeBotDifficulty(parsed.botDifficulty);
        const requestKey =
          String(parsed.requestKey || "").trim() ||
          `stored-create-game-${playType}-${mode}-${memberSize}-${havingTime}-${botDifficulty}`;

        return {
          playType,
          mode,
          memberSize,
          havingTime,
          botDifficulty,
          requestKey,
          autoCreate: parsed.autoCreate === true,
          fromStorage: true,
        };
      } catch (error) {
        console.log(error);
        return null;
      }
    })();
    const autoCreatePreferences = lobbyCreatePreferences?.autoCreate
      ? lobbyCreatePreferences
      : storedLobbyCreatePreferences;
    if (!autoCreatePreferences?.autoCreate) return undefined;

    const requestKey = String(autoCreatePreferences.requestKey || "").trim();
    if (!requestKey || lobbyCreateAutoHandledKeyRef.current === requestKey) return undefined;

    setMatchmakingQuickView("independientes");
    setQuickViewButtonPulseKey("independientes");
    const timer = window.setTimeout(() => {
      if (lobbyCreateAutoHandledKeyRef.current === requestKey) return;
      lobbyCreateAutoHandledKeyRef.current = requestKey;
      createLobbyIndependentChallenge({
        mode: autoCreatePreferences.mode,
        memberSize: autoCreatePreferences.memberSize,
        havingTime: autoCreatePreferences.havingTime,
        botDifficulty: autoCreatePreferences.botDifficulty,
      })
        .then(() => {
          if (!autoCreatePreferences.fromStorage || typeof window === "undefined") return;
          const rawValue = window.sessionStorage.getItem(PENDING_LOBBY_CREATE_STORAGE_KEY);
          if (!rawValue) return;
          try {
            const parsed = JSON.parse(rawValue);
            if (String(parsed?.requestKey || "").trim() === requestKey) {
              window.sessionStorage.removeItem(PENDING_LOBBY_CREATE_STORAGE_KEY);
            }
          } catch {
            window.sessionStorage.removeItem(PENDING_LOBBY_CREATE_STORAGE_KEY);
          }
        })
        .catch((error) => console.log(error));
    }, 1200);

    return () => window.clearTimeout(timer);
  }, [
    canUseExtendedPlayerOptions,
    createLobbyIndependentChallenge,
    location.key,
    lobbyCreatePreferences,
  ]);

  useEffect(() => {
    window.socket = socket;
    emitSocketVerify();
    fetchTournaments().catch((err) => {
      console.log(err);
    });
    fetchPuzzles(true).catch((err) => {
      console.log(err);
    });
    fetchLiveMatches().catch((err) => {
      console.log(err);
    });
    fetchOnlineFriends(true).catch((error) => console.log(error));
    fetchModeWinnersLeaderboard(true).catch((error) => console.log(error));

    const handleTournamentCreated = (newTournament) => {
      if (!newTournament || typeof newTournament !== "object") return;
      setTournaments((prev) => {
        const prevList = ensureArray(prev);
        const incomingId = String(newTournament?._id || "").trim();
        const currentEntry = incomingId
          ? prevList.find((item) => String(item?._id || "").trim() === incomingId)
          : null;
        const sequenceNumber =
          resolvePublicSequenceNumber(newTournament) ||
          resolvePublicSequenceNumber(currentEntry);
        const mergedTournament = {
          ...(currentEntry || {}),
          ...newTournament,
          ...(sequenceNumber > 0
            ? {
                tournamentSequenceNumber: sequenceNumber,
                tournamentNumber: sequenceNumber,
                displayNumber: sequenceNumber,
              }
            : {}),
        };

        const withoutCurrent = incomingId
          ? prevList.filter((item) => String(item?._id || "").trim() !== incomingId)
          : prevList;

        const updated = [mergedTournament, ...withoutCurrent];
        applyFilter(updated, filter);
        return updated;
      });
    };

    const handleTournamentRemoved = (rawData) => {
      try {
        const parsed =
          typeof rawData === "string" ? JSON.parse(rawData) : rawData;
        const removedId = String(parsed?.tournamentId || "");
        if (!removedId) return;
        setTournaments((prev) => {
          const updated = ensureArray(prev).filter(
            (item) => String(item?._id || "") !== removedId
          );
          applyFilter(updated, filter);
          return updated;
        });
      } catch (error) {
        console.log(error);
      }
    };
    const refreshLobbyPanels = () => {
      fetchTournaments().catch((error) => console.log(error));
      fetchPuzzles(true).catch((error) => console.log(error));
      fetchLiveMatches().catch((error) => console.log(error));
      fetchOnlineFriends(true).catch((error) => console.log(error));
      fetchModeWinnersLeaderboard(true).catch((error) => console.log(error));
    };
    const handleUserJoinedLobby = (rawPayload) => {
      try {
        const payload =
          typeof rawPayload === "string" ? JSON.parse(rawPayload) : rawPayload;
        const tournamentId = String(
          payload?.tournamentId || payload?.id || ""
        ).trim();
        const tournamentPatch =
          payload?.tournament && typeof payload.tournament === "object"
            ? payload.tournament
            : null;
        const userId = String(payload?.userId || payload?.email || "").trim();
        const joinedUser =
          payload?.user && typeof payload.user === "object" ? payload.user : userId;
        const joinedUsers = ensureArray(payload?.users ?? tournamentPatch?.users);
        const paidUsers = ensureArray(payload?.paidUsers ?? tournamentPatch?.paidUsers);
        const connectedPlayers = Math.max(
          Number(payload?.connectedPlayers) || 0,
          joinedUsers.length
        );

        const mergeTournamentPatch = (record) => {
          if (!record || String(record?._id || record?.id || "").trim() !== tournamentId) {
            return record;
          }

          const nextUsers = joinedUsers.length > 0 ? joinedUsers : ensureArray(record?.users);
          return {
            ...record,
            ...(tournamentPatch || {}),
            _id: String(record?._id || tournamentPatch?._id || tournamentId),
            id: String(record?.id || tournamentPatch?.id || tournamentId),
            users: nextUsers,
            paidUsers: paidUsers.length > 0 ? paidUsers : ensureArray(record?.paidUsers),
            connectedPlayers: Math.max(
              connectedPlayers,
              nextUsers.length,
              Number(record?.connectedPlayers) || 0
            ),
          };
        };

        if (tournamentId) {
          if (joinedUsers.length > 0) {
            hydrateJoinedUiPlayers(tournamentId, joinedUsers);
            setOptimisticTournamentPlayersById((prev) => ({
              ...prev,
              [tournamentId]: joinedUsers,
            }));
          } else if (joinedUser) {
            addJoinedUiPlayer(tournamentId, joinedUser);
            addOptimisticTournamentUser(tournamentId, joinedUser);
          }

          if (joinedUsers.length > 0 || tournamentPatch) {
            setTournaments((prev) => {
              const updated = ensureArray(prev).map(mergeTournamentPatch);
              applyFilter(updated, filter);
              return updated;
            });
            setTournamentWaitingRecord((prev) => mergeTournamentPatch(prev));
          }
        }
      } catch (error) {
        console.log(error);
      }
      refreshLobbyPanels();
    };
    const handleUserLeftLobby = (rawPayload) => {
      try {
        const payload =
          typeof rawPayload === "string" ? JSON.parse(rawPayload) : rawPayload;
        const tournamentId = String(
          payload?.tournamentId || payload?.id || ""
        ).trim();
        const userId = String(payload?.userId || payload?.email || "").trim();
        if (tournamentId && userId) {
          removeJoinedUiPlayer(tournamentId, userId);
          removeOptimisticTournamentUser(tournamentId, userId);
        }
      } catch (error) {
        console.log(error);
      }
      refreshLobbyPanels();
    };
    const handleCimaxUpdated = (rawPayload) => {
      try {
        const payload =
          typeof rawPayload === "string" ? JSON.parse(rawPayload) : rawPayload;
        const payloadUserId = normalizeJoinedUiIdentity(payload?.userId);
        const isForCurrentUser =
          !payloadUserId ||
          payloadUserId === currentUserEmailNormalized ||
          payloadUserId === currentUserLoginNameNormalized;
        if (!isForCurrentUser) return;
        setCimaxScores(normalizeCimaxScores(payload?.cimaxScores));
        const nextWalletBalanceCandidate = payload?.walletBalance ?? payload?.wallet?.balance;
        if (nextWalletBalanceCandidate !== undefined && nextWalletBalanceCandidate !== null) {
          setWalletBalance(normalizeWalletBalance(nextWalletBalanceCandidate));
        }
      } catch (error) {
        console.log(error);
      }
    };

    socket.on("tournamentCreated", handleTournamentCreated);
    socket.on("TOURNAMENT_REMOVED", handleTournamentRemoved);
    socket.on("USER_JOINED", handleUserJoinedLobby);
    socket.on("USER_LEFT", handleUserLeftLobby);
    socket.on("CIMAX_UPDATED", handleCimaxUpdated);

    return () => {
      socket.off("tournamentCreated", handleTournamentCreated);
      socket.off("TOURNAMENT_REMOVED", handleTournamentRemoved);
      socket.off("USER_JOINED", handleUserJoinedLobby);
      socket.off("USER_LEFT", handleUserLeftLobby);
      socket.off("CIMAX_UPDATED", handleCimaxUpdated);
    };
  }, [
    addJoinedUiPlayer,
    addOptimisticTournamentUser,
    applyFilter,
    currentUserEmailNormalized,
    currentUserLoginNameNormalized,
    fetchLiveMatches,
    fetchModeWinnersLeaderboard,
    fetchOnlineFriends,
    fetchPuzzles,
    fetchTournaments,
    filter,
    hydrateJoinedUiPlayers,
    normalizeJoinedUiIdentity,
    removeJoinedUiPlayer,
    removeOptimisticTournamentUser,
  ]);

  useEffect(() => {
    const handleWaitingRoomPresenceSnapshot = (rawPayload) => {
      try {
        const payload =
          typeof rawPayload === "string" ? JSON.parse(rawPayload) : rawPayload || {};
        const tournamentId = String(payload?.tournamentId || payload?.id || "").trim();
        if (!tournamentId) return;
        const players = ensureArray(payload?.players || payload?.activePlayers)
          .map((entry) => String(entry || "").trim())
          .filter(Boolean);
        const hasActivityData =
          Array.isArray(payload?.searchingPlayers) || Array.isArray(payload?.playingPlayers);
        if (hasActivityData) {
          const normalizeActivityList = (list) =>
            ensureArray(list)
              .map((entry) => String(entry || "").trim().toLowerCase())
              .filter(Boolean)
              .sort();
          const searching = normalizeActivityList(payload?.searchingPlayers);
          const playing = normalizeActivityList(payload?.playingPlayers);
          setWaitingRoomActivityByTournamentId((prev) => {
            const existing = prev[tournamentId];
            if (
              existing &&
              existing.searching.join("\u0001") === searching.join("\u0001") &&
              existing.playing.join("\u0001") === playing.join("\u0001")
            ) {
              return prev;
            }
            return {
              ...prev,
              [tournamentId]: { searching, playing },
            };
          });
        }

        setWaitingRoomPresenceByTournamentId((prev) => {
          const existing = ensureArray(prev[tournamentId]);
          const existingKey = existing.join("\u0001").toLowerCase();
          const nextKey = players.join("\u0001").toLowerCase();
          if (existingKey === nextKey) return prev;
          return {
            ...prev,
            [tournamentId]: players,
          };
        });
      } catch (error) {
        console.log(error);
      }
    };

    socket.on(WAITING_ROOM_PRESENCE_SNAPSHOT_EVENT, handleWaitingRoomPresenceSnapshot);
    return () => {
      socket.off(WAITING_ROOM_PRESENCE_SNAPSHOT_EVENT, handleWaitingRoomPresenceSnapshot);
    };
  }, []);

  useEffect(() => {
    if (!hasAnyVisibleQuickTimers) return undefined;
    const intervalId = setInterval(() => {
      setNowMs(Date.now());
    }, 1000);
    return () => clearInterval(intervalId);
  }, [hasAnyVisibleQuickTimers]);

  useEffect(() => {
    if (isNewsTab || isAdminTab || isHistoryTab) return undefined;

    const intervalId = setInterval(() => {
      fetchOnlineFriends(true).catch((error) => console.log(error));
    }, 10000);

    return () => clearInterval(intervalId);
  }, [fetchOnlineFriends, isAdminTab, isHistoryTab, isNewsTab]);

  useEffect(() => {
    if (isNewsTab || isAdminTab || isHistoryTab) return undefined;

    const intervalId = setInterval(() => {
      fetchTournaments().catch((error) => console.log(error));
    }, 3000);

    return () => clearInterval(intervalId);
  }, [fetchTournaments, isAdminTab, isHistoryTab, isNewsTab]);

  useEffect(() => {
    if (isNewsTab || isAdminTab || isHistoryTab) return undefined;

    const intervalId = setInterval(() => {
      fetchPuzzles(true).catch((error) => console.log(error));
    }, 4000);

    return () => clearInterval(intervalId);
  }, [fetchPuzzles, isAdminTab, isHistoryTab, isNewsTab]);

  useEffect(() => {
    if (isNewsTab || isAdminTab || isHistoryTab) return undefined;

    const intervalId = setInterval(() => {
      fetchLiveMatches().catch((error) => console.log(error));
    }, 3000);

    return () => clearInterval(intervalId);
  }, [fetchLiveMatches, isAdminTab, isHistoryTab, isNewsTab]);
  useEffect(() => {
    if (isNewsTab || isAdminTab || isHistoryTab) return undefined;

    const intervalId = setInterval(() => {
      fetchModeWinnersLeaderboard(true).catch((error) => console.log(error));
    }, 5000);

    return () => clearInterval(intervalId);
  }, [fetchModeWinnersLeaderboard, isAdminTab, isHistoryTab, isNewsTab]);
  useEffect(() => {
    ensureArray(tournaments).forEach((match) => {
      const tournamentId = String(match?._id || "").trim();
      if (!tournamentId) return;
      hydrateJoinedUiPlayers(tournamentId, match?.users);
    });
  }, [hydrateJoinedUiPlayers, tournaments]);
  useEffect(() => {
    Object.keys(liveMatchesByTournamentId || {}).forEach((tournamentId) => {
      const room = liveMatchesByTournamentId[tournamentId];
      hydrateJoinedUiPlayers(tournamentId, room?.players);
    });
  }, [hydrateJoinedUiPlayers, liveMatchesByTournamentId]);

  useEffect(() => {
    if (!isHistoryTab) return;
    fetchHistory().catch((error) => console.log(error));
  }, [fetchHistory, isHistoryTab]);
  useEffect(() => {
    const totalPages = historyShowAllMatches
      ? 1
      : Math.max(
          1,
          Math.ceil(Math.max(0, Number(historyTotalMatches) || 0) / HISTORY_PAGE_SIZE)
        );
    setHistoryPage((prev) => clampValue(prev, 1, totalPages));
  }, [historyShowAllMatches, historyTotalMatches]);

  useEffect(() => {
    if (!replayModalOpen || !replayPlaying || !replayReady) return undefined;
    if (replayAnalysisActive) return undefined;
    if (replayLoading) return undefined;
    if (replayTickingRef.current || replayAwaitingStep) return undefined;
    if (replayCurrentStep >= replayTotalSteps) {
      setReplayPlaying(false);
      return undefined;
    }

    const replayAutoplayTimer = setTimeout(() => {
      handleReplayNext().catch((error) => console.log(error));
    }, replayAutoplayDelayMs);

    return () => clearTimeout(replayAutoplayTimer);
  }, [
    handleReplayNext,
    replayAnalysisActive,
    replayAutoplayDelayMs,
    replayCurrentStep,
    replayAwaitingStep,
    replayLoading,
    replayModalOpen,
    replayPlaying,
    replayReady,
    replayTotalSteps,
  ]);
  useEffect(() => {
    if (!replayModalOpen) return;
    postReplayPlaybackSpeedToIframe();
    postReplayCameraFollowToIframe();
  }, [
    postReplayCameraFollowToIframe,
    postReplayPlaybackSpeedToIframe,
    replayCameraFollowEnabled,
    replayFrameNonce,
    replayModalOpen,
  ]);

  useEffect(() => {
    if (!replayModalOpen) return undefined;

    // Espacio = play/pausa, flecha derecha = siguiente, flecha izquierda = anterior.
    // También llegan desde el tablero (XIMA_REPLAY_KEY) cuando tiene el foco.
    const runReplayShortcut = (key) => {
      const isPlayKey = key === " " || key === "Spacebar";
      const isNextKey = key === "ArrowRight";
      const isPreviousKey = key === "ArrowLeft";
      if (isPlayKey) {
        if (!replayCanTogglePlay && !replayAnalysisActive) return;
        handleReplayPlayToggleFromUi().catch((error) => console.log(error));
        return;
      }
      if (isPreviousKey) {
        if (replayAnalysisActive) {
          handleReplayAnalysisUndo();
          return;
        }
        if (!replayCanGoBack) return;
        handleReplayPrevious().catch((error) => console.log(error));
        return;
      }
      if (!isNextKey) return;
      if (!replayCanAdvance) return;
      handleReplayNextFromUi().catch((error) => console.log(error));
    };

    const handleReplayKeyDown = (event) => {
      const key = String(event?.key || "");
      if (key !== " " && key !== "Spacebar" && key !== "ArrowRight" && key !== "ArrowLeft") return;
      const target = event?.target;
      const tagName = String(target?.tagName || "").trim().toLowerCase();
      if (tagName === "input" || tagName === "textarea" || tagName === "select" || target?.isContentEditable) return;
      if (event.repeat) return;
      event.preventDefault();
      runReplayShortcut(key);
    };
    const handleReplayKeyMessage = (event) => {
      if (event?.origin && event.origin !== window.location.origin) return;
      const data = event?.data;
      if (!data || typeof data !== "object" || data.type !== "XIMA_REPLAY_KEY") return;
      if (replayIframeRef.current && event.source !== replayIframeRef.current.contentWindow) return;
      runReplayShortcut(String(data.key || ""));
    };

    window.addEventListener("keydown", handleReplayKeyDown);
    window.addEventListener("message", handleReplayKeyMessage);
    return () => {
      window.removeEventListener("keydown", handleReplayKeyDown);
      window.removeEventListener("message", handleReplayKeyMessage);
    };
  }, [
    handleReplayAnalysisUndo,
    handleReplayNextFromUi,
    handleReplayPlayToggleFromUi,
    handleReplayPrevious,
    replayAnalysisActive,
    replayCanAdvance,
    replayCanGoBack,
    replayCanTogglePlay,
    replayModalOpen,
  ]);

  const historyTotalPages = useMemo(
    () =>
      historyShowAllMatches
        ? 1
        : Math.max(
            1,
            Math.ceil(Math.max(0, Number(historyTotalMatches) || 0) / HISTORY_PAGE_SIZE)
          ),
    [historyShowAllMatches, historyTotalMatches]
  );
  const historyPageRows = useMemo(() => {
    const matches = ensureArray(historyMatches);
    return matches.map((record) => {
      const participants = ensureArray(record?.participants);
      const resolveSelfPlayer = (player) => player?.isCurrentUser === true;
      const rivalParticipants = participants.filter(
        (player) =>
          !resolveSelfPlayer(player) && !isHistoryBotParticipant(player)
      );
      const cimaxUpdates = ensureArray(record?.cimaxUpdates);
      const cimaxSummary = record?.cimaxSummary && typeof record.cimaxSummary === "object"
        ? record.cimaxSummary
        : null;
      const summaryWinnerId = normalizeHistoryUserIdentity(
        cimaxSummary?.winnerIdNormalized || cimaxSummary?.winnerId
      );
      const summaryLoserId = normalizeHistoryUserIdentity(
        cimaxSummary?.loserIdNormalized || cimaxSummary?.loserId
      );
      const currentPlayer =
        participants.find((player) => resolveSelfPlayer(player)) || participants[0];
      const currentPlayerId = normalizeHistoryUserIdentity(
        currentPlayer?.userIdNormalized || currentPlayer?.userId
      );
      const rivalPlayer =
        rivalParticipants.find((player) => !resolveSelfPlayer(player)) || null;
      const rivalPlayerId = normalizeHistoryUserIdentity(
        rivalPlayer?.userIdNormalized || rivalPlayer?.userId
      );
      const resolveCimaxRoleByPlayerId = (playerId) => {
        if (!playerId || !summaryWinnerId || !summaryLoserId) return "other";
        if (playerId === summaryWinnerId) return "winner";
        if (playerId === summaryLoserId) return "loser";
        return "other";
      };
      const currentPlayerRole = resolveCimaxRoleByPlayerId(currentPlayerId);
      const rivalRole = resolveCimaxRoleByPlayerId(rivalPlayerId);
      const summaryTransfer = Math.max(0, Number(cimaxSummary?.transfer) || 0);
      const getPlayerForce = (role) =>
        role === "winner"
          ? Math.max(0, Number(cimaxSummary?.winnerForce) || 0)
          : role === "loser"
          ? Math.max(0, Number(cimaxSummary?.loserForce) || 0)
          : null;
      const getPlayerPotency = (role) =>
        role === "winner"
          ? Number(cimaxSummary?.winnerPotency)
          : role === "loser"
          ? Number(cimaxSummary?.loserPotency)
          : Number.NaN;
      const gameForce = getPlayerForce(currentPlayerRole);
      const gamePotency = getPlayerPotency(currentPlayerRole);
      const gameLevel = Number.isFinite(Number(cimaxSummary?.levelGame))
        ? Number(cimaxSummary.levelGame)
        : Number.NaN;
      const rivalCimaxDelta =
        rivalRole === "winner"
          ? summaryTransfer
          : rivalRole === "loser"
          ? -summaryTransfer
          : null;
      const rivalCimaxUpdate = cimaxUpdates.find((updateEntry) => {
        const updatePlayerId = normalizeHistoryUserIdentity(
          updateEntry?.userIdNormalized || updateEntry?.userId
        );
        const isRivalUpdate = Boolean(updatePlayerId) && updatePlayerId === rivalPlayerId;
        if (!isRivalUpdate) return false;
        const modeField = String(updateEntry?.modeField || "").trim().toLowerCase();
        return modeField !== "tournament_winner_prize";
      });
      const rivalWalletBalance = Number(rivalCimaxUpdate?.walletBalance);
      const rivalWalletDelta = Number(rivalCimaxUpdate?.delta);
      const rivalWalletFallbackDelta = Number(rivalCimaxUpdate?.walletDelta);
      const rivalInitialCimax =
        Number.isFinite(rivalWalletBalance) && Number.isFinite(rivalWalletDelta)
          ? Math.max(0, rivalWalletBalance - rivalWalletDelta)
          : Number.isFinite(rivalWalletBalance) && Number.isFinite(rivalWalletFallbackDelta)
          ? Math.max(0, rivalWalletBalance - rivalWalletFallbackDelta)
          : null;

      return {
        ...record,
        historyMatchForce: gameForce,
        historyMatchLevel: gameLevel,
        historyMatchPotency: gamePotency,
        historyRivalCimaxDelta: rivalCimaxDelta,
        historyRivalInitialCimax: Number.isFinite(Number(rivalInitialCimax))
          ? Math.max(0, Math.round(Number(rivalInitialCimax)))
          : null,
      };
    });
  }, [historyMatches]);
  const historyVisiblePages = useMemo(() => {
    if (historyTotalPages <= 7) {
      return Array.from({ length: historyTotalPages }, (_, index) => index + 1);
    }

    const pages = [1];
    const start = Math.max(2, historyPage - 1);
    const end = Math.min(historyTotalPages - 1, historyPage + 1);

    if (start > 2) pages.push("ellipsis-left");
    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }
    if (end < historyTotalPages - 1) pages.push("ellipsis-right");
    pages.push(historyTotalPages);

    return pages;
  }, [historyPage, historyTotalPages]);
  const historyCurrentIdentitySet = useMemo(() => {
    const identities = new Set();
    const addIdentity = (value) => {
      const normalized = String(value || "")
        .trim()
        .toLowerCase();
      if (!normalized) return;
      identities.add(normalized);
      if (normalized.includes("@")) {
        const alias = String(normalized.split("@")[0] || "").trim();
        if (alias) identities.add(alias);
      }
    };

    addIdentity(currentUserEmailNormalized);
    addIdentity(currentUserLoginNameNormalized);
    addIdentity(user?.loginIdentifier);
    addIdentity(user?.email);
    addIdentity(user?.username);
    addIdentity(user?.userName);
    addIdentity(displayName);

    return identities;
  }, [
    currentUserEmailNormalized,
    currentUserLoginNameNormalized,
    displayName,
    user?.email,
    user?.loginIdentifier,
    user?.userName,
    user?.username,
  ]);
  const isCurrentUserHistoryParticipant = useCallback(
    (participant) => {
      if (!participant || typeof participant !== "object") return false;
      const candidateValues = [
        participant?.userIdNormalized,
        participant?.userId,
        participant?.displayName,
        participant?.playerCode,
        participant?.username,
        participant?.userName,
        participant?.email,
      ];

      return candidateValues.some((entry) => {
        const normalized = String(entry || "")
          .trim()
          .toLowerCase();
        if (!normalized) return false;
        if (historyCurrentIdentitySet.has(normalized)) return true;
        if (normalized.includes("@")) {
          const alias = String(normalized.split("@")[0] || "").trim();
          if (alias && historyCurrentIdentitySet.has(alias)) return true;
        }
        return false;
      });
    },
    [historyCurrentIdentitySet]
  );
  const historyResultStats = useMemo(() => {
    const rows = ensureArray(historyPageRows);
    const sortedRows = [...rows].sort(
      (left, right) => resolveHistoryRecordTimeMs(right) - resolveHistoryRecordTimeMs(left)
    );
    const wins = rows.filter(
      (record) => String(record?.outcome || "").trim().toLowerCase() === "win"
    ).length;
    const losses = rows.filter(
      (record) => String(record?.outcome || "").trim().toLowerCase() === "loss"
    ).length;
    const draws = rows.filter(
      (record) => String(record?.outcome || "").trim().toLowerCase() === "draw"
    ).length;
    let streak = 0;
    for (const record of sortedRows) {
      const outcome = String(record?.outcome || "").trim().toLowerCase();
      if (outcome !== "win") break;
      streak += 1;
    }
    const counted = Math.max(1, wins + losses + draws);
    return {
      wins,
      losses,
      draws,
      streak,
      winRate: Math.round((wins / counted) * 1000) / 10,
      lossRate: Math.round((losses / counted) * 1000) / 10,
    };
  }, [historyPageRows]);
  const historyModeCounts = useMemo(() => {
    return ensureArray(historyPageRows).reduce(
      (accumulator, record) => {
        const normalizedMode = normalizeModeAlias(record?.mode, "");
        if (normalizedMode === "dice") accumulator.azar += 1;
        else if (normalizedMode === "cienzar") accumulator.cienzar += 1;
        else accumulator.ciencia += 1;
        return accumulator;
      },
      { all: ensureArray(historyPageRows).length, azar: 0, ciencia: 0, cienzar: 0 }
    );
  }, [historyPageRows]);
  const historyFilteredRows = useMemo(() => {
    const normalizedSearch = String(historyPlayerSearch || "").trim().toLowerCase();
    const targetMode = normalizeModeAlias(
      historyModeFilter === "azar"
        ? "dice"
        : historyModeFilter === "ciencia"
        ? "free"
        : historyModeFilter,
      ""
    );
    const startOfToday = moment().startOf("day").valueOf();
    const startOfWeek = moment().startOf("week").valueOf();
    const startOfMonth = moment().startOf("month").valueOf();

    return ensureArray(historyPageRows)
      .filter((record) => {
        const outcome = String(record?.outcome || "").trim().toLowerCase();
        if (historyResultFilter === "wins" && outcome !== "win") return false;
        if (historyResultFilter === "losses" && outcome !== "loss") return false;

        const recordMode = normalizeModeAlias(record?.mode, "");
        if (historyModeFilter !== "all" && recordMode !== targetMode) return false;

        if (historyDateFilter !== "all") {
          const recordTime = resolveHistoryRecordTimeMs(record);
          if (!recordTime) return false;
          if (historyDateFilter === "today" && recordTime < startOfToday) return false;
          if (historyDateFilter === "week" && recordTime < startOfWeek) return false;
          if (historyDateFilter === "month" && recordTime < startOfMonth) return false;
        }

        if (normalizedSearch) {
          const participants = ensureArray(record?.participants);
          const searchableText = [
            record?.id,
            record?._id,
            record?.mode,
            formatModeLabel(record?.mode),
            ...participants.flatMap((player) => [
              resolvePreferredPlayerLabel(player),
              player?.displayName,
              player?.username,
              player?.userName,
              player?.email,
              player?.playerCode,
            ]),
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          if (!searchableText.includes(normalizedSearch)) return false;
        }

        return true;
      })
      .sort((left, right) => {
        const sortDirection = historySortOrder === "oldest" ? 1 : -1;
        return (
          (resolveHistoryRecordTimeMs(left) - resolveHistoryRecordTimeMs(right)) *
          sortDirection
        );
      });
  }, [
    historyDateFilter,
    historyModeFilter,
    historyPageRows,
    historyPlayerSearch,
    historyResultFilter,
    historySortOrder,
  ]);
  const historyActiveFilterCount = useMemo(() => {
    let count = 0;
    if (historyResultFilter !== "all") count += 1;
    if (historyModeFilter !== "all") count += 1;
    if (historyDateFilter !== "all") count += 1;
    if (String(historyPlayerSearch || "").trim()) count += 1;
    if (historySortOrder !== "recent") count += 1;
    return count;
  }, [
    historyDateFilter,
    historyModeFilter,
    historyPlayerSearch,
    historyResultFilter,
    historySortOrder,
  ]);
  const isHistoryMobileResultView =
    usePhoneQuickViewButtonStack || isReplayPortraitPhoneViewport;
  const isHistoryPortraitPhoneView =
    isReplayPortraitPhoneViewport ||
    (isHistoryMobileResultView &&
      !isPhoneLandscapeViewport &&
      !isIphoneChromeLandscapePhone &&
      !isSamsungChromeLandscapePhone);
  const historyHeaderGridClass = isHistoryPortraitPhoneView
    ? "grid-cols-[64px_172px_minmax(0,1fr)_82px] gap-2"
    : isHistoryMobileResultView
    ? "grid-cols-[44px_34px_minmax(0,1fr)_84px] gap-1.5"
    : "grid-cols-[minmax(0,0.75fr)_minmax(0,0.85fr)_minmax(0,0.95fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,1.6fr)_minmax(0,0.95fr)] gap-2";
  const historyRowGridClass = historyHeaderGridClass;
  const historyHeaderFontSize = isHistoryPortraitPhoneView
    ? 12
    : isHistoryMobileResultView
    ? 11
    : 12 * HISTORY_TEXT_SCALE;
  const historyRowFontSize = isHistoryPortraitPhoneView
    ? 10
    : isHistoryMobileResultView
    ? 11
    : 12 * HISTORY_TEXT_SCALE;
  const historyBotLabelFontSize = isHistoryMobileResultView ? 9 : 8 * HISTORY_TEXT_SCALE;
  const historyModeHeaderLabel = isHistoryPortraitPhoneView
    ? historyText("mode")
    : isHistoryMobileResultView
    ? "M"
    : historyText("mode");
  const historyResultHeaderLabel = isHistoryPortraitPhoneView
    ? historyText("result")
    : isHistoryMobileResultView
    ? "R"
    : historyText("result");
  const historyReplayHeaderLabel = historyText("replay");
  const formatHistoryDeltaAmount = useCallback((value) => {
    const parsed = Math.round(Number(value) || 0);
    return formatHistoryNumber(Math.abs(parsed), language);
  }, [language]);

  useEffect(() => {
    if (!isHistoryTab || !isHistoryPortraitPhoneView || typeof document === "undefined") return;

    const scrollHistoryToTop = () => {
      const historyList = document.querySelector(".match-history-list");
      if (historyList instanceof HTMLElement) {
        historyList.scrollTop = 0;
      }
      const mainScroll = document.querySelector(".xima-mobile-scroll-main");
      if (mainScroll instanceof HTMLElement) {
        mainScroll.scrollTop = 0;
      }
      const pageScroll = document.querySelector(".xima-mobile-scroll-page");
      if (pageScroll instanceof HTMLElement) {
        pageScroll.scrollTop = 0;
      }
      const rootScroll = document.scrollingElement;
      if (rootScroll instanceof HTMLElement) {
        rootScroll.scrollTop = 0;
      }
      window.scrollTo(0, 0);
    };

    scrollHistoryToTop();
    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => {
        scrollHistoryToTop();
        window.requestAnimationFrame(scrollHistoryToTop);
      });
    }
    const timer = window.setTimeout(scrollHistoryToTop, 120);
    return () => window.clearTimeout(timer);
  }, [isHistoryPortraitPhoneView, isHistoryTab]);

  const tournamentStatusById = useMemo(() => {
    const statusMap = {};
    ensureArray(tournaments).forEach((record) => {
      const tournamentId = String(record?._id || record?.id || "").trim();
      if (!tournamentId) return;
      statusMap[tournamentId] = getTournamentStatus(record);
    });
    return statusMap;
  }, [getTournamentStatus, tournaments]);

  const ADMIN_ORDER_STATUS_FILTERS = [
    { key: "all", label: "Todos" },
    { key: "pendiente_envio", label: "Pendientes" },
    { key: "enviado", label: "Enviados" },
    { key: "entregado", label: "Entregados" },
    { key: "cancelado", label: "Cancelados" },
  ];

  const adminOrderStatusCounters = useMemo(() => {
    const counters = {
      all: ensureArray(adminOrders).length,
      pendiente_envio: 0,
      enviado: 0,
      entregado: 0,
      cancelado: 0,
    };
    ensureArray(adminOrders).forEach((entry) => {
      const status = String(entry?.status || "").trim().toLowerCase();
      if (Object.prototype.hasOwnProperty.call(counters, status)) {
        counters[status] += 1;
      }
    });
    return counters;
  }, [adminOrders]);

  const filteredAdminOrders = useMemo(() => {
    const selectedFilter = String(adminOrdersFilter || "all").trim().toLowerCase();
    if (selectedFilter === "all") return ensureArray(adminOrders);
    return ensureArray(adminOrders).filter(
      (entry) => String(entry?.status || "").trim().toLowerCase() === selectedFilter
    );
  }, [adminOrders, adminOrdersFilter]);

  const filteredAdminUsers = useMemo(() => {
    const keyword = String(adminUserSearchTerm || "").trim().toLowerCase();
    const users = ensureArray(adminUsers);
    if (!keyword) return users;

    return users.filter((entry) => {
      const firstName = String(entry?.firstName || "").trim();
      const lastName = String(entry?.lastName || "").trim();
      const fullName = `${firstName} ${lastName}`.trim();
      const candidates = [
        fullName,
        firstName,
        lastName,
        entry?.email,
        entry?.contactEmail,
        entry?.phone,
        entry?.playerCode,
      ];

      return candidates.some((value) =>
        String(value || "")
          .trim()
          .toLowerCase()
          .includes(keyword)
      );
    });
  }, [adminUserSearchTerm, adminUsers]);

  const formatAdminOrderAddress = useCallback((orderRecord) => {
    const address = orderRecord?.shippingAddress || {};
    const country = String(address?.country || "").trim().toLowerCase();
    if (country === "digital") {
      return `ENTREGA DIGITAL, ${String(address?.line2 || "").trim()}`.trim() || "ENTREGA DIGITAL";
    }

    return [
      String(address?.line1 || "").trim(),
      String(address?.line2 || "").trim(),
      String(address?.city || "").trim(),
      String(address?.stateProvince || "").trim(),
      String(address?.postalCode || "").trim(),
      String(address?.country || "").trim(),
    ]
      .filter(Boolean)
      .join(", ");
  }, []);

  const handleAdminOrderStatusUpdate = useCallback(
    async (orderRecord, nextStatus) => {
      const orderId = String(orderRecord?.id || orderRecord?._id || "").trim();
      const normalizedNextStatus = String(nextStatus || "").trim().toLowerCase();
      if (!orderId || !normalizedNextStatus) return;

      const accepted = window.confirm(
        `Confirmar cambio de estado del pedido a ${normalizedNextStatus}?`
      );
      if (!accepted) return;

      const actionId = `order:status:${orderId}:${normalizedNextStatus}`;
      try {
        setAdminActionKey(actionId);
        await axios.patch(`/admin/orders/${orderId}/status`, { status: normalizedNextStatus });
        await loadAdminData();
        notification.success({
          message: i18nT("lobby.pedidoActualizado"),
          description: i18nT("lobby.elEstadoDelPedidoFueActualizadoCorrectamente"),
        });
      } catch (error) {
        notification.error({
          message: i18nT("lobby.errorActualizandoPedido"),
          description:
            error?.response?.data?.message ||
            error?.response?.data?.reason ||
            "No se pudo actualizar el estado del pedido.",
        });
      } finally {
        setAdminActionKey("");
      }
    },
    [loadAdminData]
  );

  const handleAdminOrderNoteUpdate = useCallback(
    async (orderRecord) => {
      const orderId = String(orderRecord?.id || orderRecord?._id || "").trim();
      if (!orderId) return;

      const currentNote = String(orderRecord?.adminNote || "").trim();
      const nextNote = window.prompt("Agregar o editar nota interna del pedido:", currentNote);
      if (nextNote === null) return;

      const actionId = `order:note:${orderId}`;
      try {
        setAdminActionKey(actionId);
        await axios.patch(`/admin/orders/${orderId}/note`, { adminNote: nextNote });
        await loadAdminData();
        notification.success({
          message: i18nT("lobby.notaGuardada"),
          description: i18nT("lobby.laNotaInternaDelPedidoFueGuardadaCorrectamen4f9469"),
        });
      } catch (error) {
        notification.error({
          message: i18nT("lobby.errorGuardandoNota"),
          description:
            error?.response?.data?.message ||
            error?.response?.data?.reason ||
            "No se pudo guardar la nota interna.",
        });
      } finally {
        setAdminActionKey("");
      }
    },
    [loadAdminData]
  );

  const adminIndependentChallengeColumns = [
    {
      title: i18nT("lobby.desafio"),
      key: "challenge",
      render: (_, record) => (
        <div className="min-w-0">
          <div className="truncate font-semibold text-white">
            {record?.tournamentName || "Desafío Independiente"}
          </div>
          <div className="truncate text-[11px] text-[#9db5df]">
            ID: {String(record?.id || record?._id || "-")}
          </div>
        </div>
      ),
    },
    {
      title: "Creador",
      dataIndex: "createdBy",
      key: "createdBy",
      render: (value) => (
        <span className="font-semibold text-[#dce8ff]">{String(value || "").trim() || "-"}</span>
      ),
    },
    {
      title: "Modo",
      dataIndex: "mode",
      key: "mode",
      render: (value, record) => (
        <span className="rounded border border-[#6f73c6] bg-[#261f63] px-2 py-0.5 text-xs font-semibold text-[#e2d5ff]">
          {String(record?.modeLabel || formatModeLabel(value || "")).toUpperCase()}
        </span>
      ),
    },
    {
      title: "Jugadores",
      key: "players",
      render: (_, record) => (
        <span>
          {Math.max(0, Number(record?.joinedPlayersCount) || 0)}/
          {Math.max(1, Number(record?.memberSize) || 1)}
        </span>
      ),
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      render: (value) => <Tag color={value === "pendiente" ? "orange" : "green"}>{value || "-"}</Tag>,
    },
    {
      title: "Creado",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value) => (value ? moment(value).fromNow() : "-"),
    },
    {
      title: "Accion",
      key: "action",
      render: (_, record) => {
        const actionId = `independent-challenge:${String(record?.id || record?._id || "")}`;
        return (
          <Button
            size="small"
            danger
            loading={adminActionKey === actionId}
            onClick={() => handleAdminDeleteIndependentChallenge(record)}
          >{i18nT("lobby.eliminarDesafio")}</Button>
        );
      },
    },
  ];

  const adminRoomColumns = [
    {
      title: "Sala",
      key: "room",
      render: (_, record) => (
        <div className="min-w-0">
          <div className="truncate font-semibold text-white">
            {getLocalizedTournamentName(record?.tournamentName, "Room")} / #
            {record?.isVirtualRoom ? "WAITING" : record?.roomId || "-"}
          </div>
          <div className="truncate text-[11px] text-[#9db5df]">
            Torneo: {record?.tournamentId || "-"}
          </div>
        </div>
      ),
    },
    {
      title: "Modo",
      dataIndex: "mode",
      key: "mode",
      render: (value) => (
        <span className="rounded border border-[#3b5f91] bg-[#0d2244] px-2 py-0.5 text-xs font-semibold text-[#cde4ff]">
          {String(formatModeLabel(value || "")).toUpperCase()}
        </span>
      ),
    },
    {
      title: "Jugadores",
      key: "players",
      render: (_, record) => (
        <span>
          {Math.max(0, Number(record?.connectedPlayers) || 0)}/
          {Math.max(1, Number(record?.memberSize) || 1)}
        </span>
      ),
    },
    {
      title: "Tiempo",
      dataIndex: "havingTime",
      key: "havingTime",
      render: (value) => `${Math.max(1, Number(value) || 1)} min`,
    },
    {
      title: i18nT("lobby.terminaEn"),
      key: "lifetime",
      render: (_, record) => {
        if (!record?.isQuickMatch) return <span className="text-[#8ca4cf]">-</span>;
        const remainingMs = getQuickTournamentRemainingMs(record);
        if (remainingMs <= 0) {
          return <Tag color="red">{i18nT("lobby.expirado")}</Tag>;
        }
        return (
          <span className="font-semibold text-[#ffd08a]">
            {formatCountdownLabel(remainingMs)}
          </span>
        );
      },
    },
    {
      title: "Estado",
      key: "started",
      render: (_, record) => {
        const tournamentId = String(record?.tournamentId || "").trim();
        const tournamentStatus = tournamentStatusById[tournamentId];

        if (tournamentStatus === "playing") {
          return <Tag color="green">{i18nT("lobby.activo")}</Tag>;
        }
        if (tournamentStatus === "waiting") {
          return <Tag color="orange">{i18nT("lobby.esperando")}</Tag>;
        }
        if (tournamentStatus === "finished") {
          return <Tag color="red">{i18nT("lobby.finalizado")}</Tag>;
        }

        return record?.started ? <Tag color="green">{i18nT("lobby.activo")}</Tag> : <Tag color="orange">{i18nT("lobby.esperando")}</Tag>;
      },
    },
    {
      title: "Accion",
      key: "action",
      render: (_, record) => {
        const actionId = `room:${record?.tournamentId || ""}:${record?.roomId || ""}`;
        return (
          <Button
            size="small"
            danger
            loading={adminActionKey === actionId}
            onClick={() => handleAdminDeleteRoom(record)}
          >
            {record?.isVirtualRoom ? "Eliminar Torneo" : "Eliminar Sala"}
          </Button>
        );
      },
    },
  ];

  const adminUserColumns = [
    {
      title: "Nombre",
      key: "name",
      render: (_, record) => {
        const firstName = String(record?.firstName || "").trim();
        const lastName = String(record?.lastName || "").trim();
        const fullName = `${firstName} ${lastName}`.trim();
        const primaryName = extractPrimaryName(fullName || firstName || record?.email);
        return <span className="font-semibold text-white">{primaryName || "-"}</span>;
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (value) => <span className="text-[#dce8ff]">{value || "-"}</span>,
    },
    {
      title: i18nT("lobby.correoContacto"),
      dataIndex: "contactEmail",
      key: "contactEmail",
      render: (value) => <span className="text-[#dce8ff]">{String(value || "").trim() || "-"}</span>,
    },
    {
      title: "Telefono",
      dataIndex: "phone",
      key: "phone",
      render: (value) => <span className="text-[#dce8ff]">{String(value || "").trim() || "-"}</span>,
    },
    {
      title: "Codigo",
      dataIndex: "playerCode",
      key: "playerCode",
      render: (value) => value || "-",
    },
    {
      title: "Rol",
      dataIndex: "role",
      key: "role",
      render: (value) => String(value || "user"),
    },
    {
      title: "Estado",
      dataIndex: "state",
      key: "state",
      render: (value) => String(value || "active"),
    },
    {
      title: "Premium",
      key: "isPremium",
      render: (_, record) => {
        const role = String(record?.role || "").toLowerCase();
        const isAdminRole = role === "admin" || role === "superuser" || role === "owner";
        if (isAdminRole) return <Tag color="gold">Admin (sin límites)</Tag>;
        return (
          <Switch
            size="small"
            checked={Boolean(record?.isPremium)}
            loading={adminActionKey === `user:premium:${String(record?.id || "")}`}
            onChange={(checked) => handleAdminSetUserPremium(record, checked)}
            checkedChildren="Premium"
            unCheckedChildren="No"
          />
        );
      },
    },
    {
      title: "Accion",
      key: "action",
      render: (_, record) => {
        const email = String(record?.email || "").trim().toLowerCase();
        const deleteActionId = `user:${email}`;
        const resetActionId = `user:reset:${email}`;
        const isCurrentUser = email === currentUserEmailNormalized;
        return (
          <div className="flex flex-wrap gap-2">
            <Button
              size="small"
              type="primary"
              disabled={isCurrentUser}
              loading={adminActionKey === resetActionId}
              onClick={() => handleAdminResetUserPassword(record)}
            >{i18nT("lobby.resetearClave")}</Button>
            <Button
              size="small"
              danger
              disabled={isCurrentUser}
              loading={adminActionKey === deleteActionId}
              onClick={() => handleAdminDeleteUser(record)}
            >{i18nT("lobby.eliminarCuenta")}</Button>
          </div>
        );
      },
    },
  ];

  const adminOrderColumns = [
    {
      title: "Fecha",
      key: "purchaseDate",
      render: (_, record) =>
        record?.purchaseDate || record?.createdAt
          ? moment(record.purchaseDate || record.createdAt).format("YYYY-MM-DD HH:mm")
          : "-",
    },
    {
      title: "Usuario",
      key: "userName",
      render: (_, record) => (
        <span className="font-semibold text-white">{String(record?.userName || "-")}</span>
      ),
    },
    {
      title: "Email",
      key: "userEmail",
      render: (_, record) => (
        <span className="text-[#dce8ff]">{String(record?.userEmail || "-")}</span>
      ),
    },
    {
      title: "Producto",
      key: "productName",
      render: (_, record) => (
        <div className="min-w-0">
          <div className="truncate font-semibold text-[#e6f0ff]">
            {String(record?.productName || "Juego de Mesa XIMA")}
          </div>
          <div className="truncate text-[11px] text-[#9ecbff]">
            {String(record?.productId || "").toUpperCase()}
          </div>
        </div>
      ),
    },
    {
      title: "Cantidad",
      key: "quantity",
      render: (_, record) => Math.max(1, Number(record?.quantity) || 1),
    },
    {
      title: "Direccion",
      key: "shippingAddress",
      render: (_, record) => {
        const summary = formatAdminOrderAddress(record);
        return (
          <div className="max-w-[320px] truncate text-[#d6e8ff]" title={summary || "-"}>
            {summary || "-"}
          </div>
        );
      },
    },
    {
      title: "Estado",
      key: "status",
      render: (_, record) => {
        const status = String(record?.status || "").trim().toLowerCase();
        if (status === "pendiente_envio") {
          return <Tag color="orange">{i18nT("lobby.envioPendiente")}</Tag>;
        }
        if (status === "enviado") return <Tag color="blue">{i18nT("lobby.enviado")}</Tag>;
        if (status === "entregado") return <Tag color="green">{i18nT("lobby.entregado")}</Tag>;
        if (status === "cancelado") return <Tag color="red">{i18nT("lobby.cancelado")}</Tag>;
        return <Tag>{status || "-"}</Tag>;
      },
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_, record) => {
        const orderId = String(record?.id || record?._id || "").trim();
        const normalizedStatus = String(record?.status || "").trim().toLowerCase();
        return (
          <div className="flex flex-wrap gap-1">
            <Button size="small" onClick={() => setAdminOrderDetail(record)}>{i18nT("lobby.verDetalles")}</Button>
            <Button
              size="small"
              disabled={normalizedStatus === "enviado"}
              loading={adminActionKey === `order:status:${orderId}:enviado`}
              onClick={() => handleAdminOrderStatusUpdate(record, "enviado")}
            >{i18nT("lobby.marcarEnviado")}</Button>
            <Button
              size="small"
              disabled={normalizedStatus === "entregado"}
              loading={adminActionKey === `order:status:${orderId}:entregado`}
              onClick={() => handleAdminOrderStatusUpdate(record, "entregado")}
            >{i18nT("lobby.marcarEntregado")}</Button>
            <Button
              size="small"
              danger
              disabled={normalizedStatus === "cancelado"}
              loading={adminActionKey === `order:status:${orderId}:cancelado`}
              onClick={() => handleAdminOrderStatusUpdate(record, "cancelado")}
            >{i18nT("lobby.cancelar")}</Button>
            <Button
              size="small"
              loading={adminActionKey === `order:note:${orderId}`}
              onClick={() => handleAdminOrderNoteUpdate(record)}
            >{i18nT("lobby.nota")}</Button>
          </div>
        );
      },
    },
  ];

  const adminPuzzleVisualOrderById = useMemo(() => {
    const modeCounters = {};
    return toOrderedPuzzleEntries(puzzles).reduce((acc, puzzle) => {
      const puzzleId = getPuzzleEntryId(puzzle);
      if (!puzzleId) return acc;
      const modeKey = resolvePuzzleColumnMode(puzzle?.freeBoxBaseMode);
      const nextOrder = Math.max(1, Number(modeCounters[modeKey] || 0) + 1);
      modeCounters[modeKey] = nextOrder;
      acc[puzzleId] = nextOrder;
      return acc;
    }, {});
  }, [puzzles]);
  const adminPuzzleFallbackVisualOrderById = useMemo(() => {
    const modeCounters = {};
    return toOrderedPuzzleEntries(adminPuzzles).reduce((acc, puzzle) => {
      const puzzleId = getPuzzleEntryId(puzzle);
      if (!puzzleId) return acc;
      const modeKey = resolvePuzzleColumnMode(puzzle?.freeBoxBaseMode);
      const nextOrder = Math.max(1, Number(modeCounters[modeKey] || 0) + 1);
      modeCounters[modeKey] = nextOrder;
      acc[puzzleId] = nextOrder;
      return acc;
    }, {});
  }, [adminPuzzles]);

  const adminPuzzleColumns = [
    {
      title: "Puzzle",
      key: "puzzle",
      render: (_, record) => (
        <div className="min-w-0">
          <div className="truncate font-semibold text-white">
            {getLocalizedTournamentName(record?.tournamentName, "Puzzle")}
          </div>
          <div className="truncate text-[11px] text-[#b5c7eb]">
            ID: {String(record?.id || record?._id || "-")}
          </div>
        </div>
      ),
    },
    {
      title: "Base",
      key: "baseMode",
      render: (_, record) => {
        const puzzleId = getPuzzleEntryId(record);
        const visualOrder = Math.max(
          0,
          Number(
            adminPuzzleVisualOrderById[puzzleId] || adminPuzzleFallbackVisualOrderById[puzzleId]
          ) || 0
        );
        return (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="rounded border border-[#7f69b8] bg-[#2a1f4a] px-2 py-0.5 text-xs font-semibold text-[#e2d4ff]">
              {String(formatFreeBoxBaseModeLabel(record?.freeBoxBaseMode || "free")).toUpperCase()}
            </span>
            <span className="rounded border border-[#5f89c5] bg-[#15335e] px-2 py-0.5 text-xs font-black text-[#d7e8ff]">
              #{visualOrder || "-"}
            </span>
          </div>
        );
      },
    },
    {
      title: "Jugadas",
      dataIndex: "puzzleMoveCount",
      key: "puzzleMoveCount",
      render: (value) => Math.max(0, Number(value) || 0),
    },
    {
      title: "Creado",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value) => (value ? moment(value).fromNow() : "-"),
    },
    {
      title: "Accion",
      key: "action",
      render: (_, record) => {
        const puzzleId = String(record?.id || record?._id || "").trim();
        const actionId = `puzzle:${puzzleId}`;
        return (
          <Button
            size="small"
            danger
            disabled={!puzzleId}
            loading={adminActionKey === actionId}
            onClick={() => handleAdminDeletePuzzle(record)}
          >{i18nT("lobby.eliminarPuzzle")}</Button>
        );
      },
    },
  ];

  const onlineLobbyMatches = useMemo(
    () =>
      ensureArray(tournaments)
        .map((match) => {
          const memberSize = Math.max(0, Number(match?.memberSize) || 0);
          const tournamentId = String(match?._id || "").trim();
          const liveRoom = liveMatchesByTournamentId[tournamentId] || null;
          const registeredPlayers = getJoinedUiPlayers(tournamentId, match?.users);
          const registeredPlayersCount = registeredPlayers.length;
          const livePlayers = buildJoinedUiPlayersFromRawUsers(liveRoom?.players);
          const liveConnectedPlayers = liveRoom
            ? Math.max(0, Number(liveRoom.connectedPlayers) || 0)
            : 0;
          const activePlayerIdentitySet = new Set(
            livePlayers
              .map((entry) => resolveJoinedUiPlayerIdentity(entry))
              .filter(Boolean)
          );
          const activePlayersCount = Math.max(
            liveConnectedPlayers,
            activePlayerIdentitySet.size
          );
          const connectedPlayers = liveRoom
            ? activePlayersCount
            : Math.max(registeredPlayersCount, activePlayersCount);
          const status = getTournamentStatus(match);
          const quickRemainingMs = getQuickTournamentRemainingMs(match);
          return {
            ...match,
            memberSize,
            connectedPlayers,
            registeredPlayers,
            registeredPlayersCount,
            activePlayersCount,
            activePlayerIdentities: Array.from(activePlayerIdentitySet),
            hasLiveRoom: Boolean(liveRoom),
            status,
            quickRemainingMs,
          };
        })
        .filter(
          (match) =>
            match.status !== "finished" && isOnlineLobbyTournamentMode(match?.mode)
        )
        .sort(
          (a, b) =>
            new Date(b?.createdAt || b?.startingTime || 0).valueOf() -
            new Date(a?.createdAt || a?.startingTime || 0).valueOf()
        ),
    [
      buildJoinedUiPlayersFromRawUsers,
      getJoinedUiPlayers,
      getQuickTournamentRemainingMs,
      getTournamentStatus,
      liveMatchesByTournamentId,
      resolveJoinedUiPlayerIdentity,
      tournaments,
    ]
  );
  const tournamentLobbies = useMemo(
    () =>
      ensureArray(onlineLobbyMatches).filter(
        (entry) =>
          !isIndependentMatch(entry) &&
          entry?.isSuperuserCreated === true &&
          !isAutoGeneratedChallengeName(entry?.tournamentName) &&
          (!isLobbyTournamentModeRoute ||
            !lobbyTournamentModeFilter ||
            resolveTournamentDisplayMode(entry) === lobbyTournamentModeFilter)
      ),
    [isLobbyTournamentModeRoute, lobbyTournamentModeFilter, onlineLobbyMatches]
  );
  const tournamentLobbyFallbackSequenceById = useMemo(
    () => buildEntrySequenceLookup(tournamentLobbies),
    [tournamentLobbies]
  );
  const tournamentLobbiesVigentes = useMemo(
    () =>
      [...tournamentLobbies]
        .filter((entry) => entry?.status === "playing")
        .sort(
          (a, b) => {
            const aStartMs = new Date(a?.startingTime || 0).valueOf();
            const bStartMs = new Date(b?.startingTime || 0).valueOf();
            const aHasStart = Number.isFinite(aStartMs) && aStartMs > 0;
            const bHasStart = Number.isFinite(bStartMs) && bStartMs > 0;
            if (aHasStart && bHasStart && bStartMs !== aStartMs) return bStartMs - aStartMs;
            return (
              new Date(b?.createdAt || 0).valueOf() -
              new Date(a?.createdAt || 0).valueOf()
            );
          }
        ),
    [tournamentLobbies]
  );
  const tournamentLobbiesPorEmpezar = useMemo(
    () =>
      [...tournamentLobbies]
        .filter((entry) => entry?.status !== "playing")
        .sort(
          (a, b) => {
            const aStartMs = new Date(a?.startingTime || 0).valueOf();
            const bStartMs = new Date(b?.startingTime || 0).valueOf();
            const aHasStart = Number.isFinite(aStartMs) && aStartMs > 0;
            const bHasStart = Number.isFinite(bStartMs) && bStartMs > 0;
            if (aHasStart && bHasStart) return aStartMs - bStartMs;
            if (aHasStart) return -1;
            if (bHasStart) return 1;
            return (
              new Date(b?.createdAt || 0).valueOf() -
              new Date(a?.createdAt || 0).valueOf()
            );
          }
        ),
    [tournamentLobbies]
  );
  const getTournamentStartRemainingMs = useCallback(
    (record) => {
      const startMs = new Date(record?.startingTime || 0).valueOf();
      if (!Number.isFinite(startMs) || startMs <= 0) return 0;
      return Math.max(0, startMs - nowMs);
    },
    [nowMs]
  );
  const visibleTournamentLobbiesVigentes = useMemo(() => {
    if (hasDirectAdminAccess) return tournamentLobbiesVigentes;
    return tournamentLobbiesVigentes.filter(
      (entry) => getTournamentStartRemainingMs(entry) <= 30 * 60 * 1000
    );
  }, [getTournamentStartRemainingMs, hasDirectAdminAccess, tournamentLobbiesVigentes]);
  const visibleTournamentLobbiesPorEmpezar = useMemo(() => {
    if (hasDirectAdminAccess) return tournamentLobbiesPorEmpezar;
    return tournamentLobbiesPorEmpezar.filter(
      (entry) => getTournamentStartRemainingMs(entry) <= 30 * 60 * 1000
    );
  }, [getTournamentStartRemainingMs, hasDirectAdminAccess, tournamentLobbiesPorEmpezar]);
  const registeredTournamentLobbiesVigentes = useMemo(
    () =>
      ensureArray(visibleTournamentLobbiesVigentes).filter((entry) =>
        canEnterProtectedMatchWithoutPassword(entry)
      ),
    [canEnterProtectedMatchWithoutPassword, visibleTournamentLobbiesVigentes]
  );
  const featuredRegisteredTournamentLobby = useMemo(() => {
    const rememberedTournamentId = String(lastJoinedTournamentId || "").trim();
    const registeredMatches = ensureArray(registeredTournamentLobbiesVigentes);

    if (registeredMatches.length > 0) {
      if (rememberedTournamentId) {
        const rememberedRegistered = registeredMatches.find(
          (entry) => getTournamentEntryId(entry) === rememberedTournamentId
        );
        if (rememberedRegistered) return rememberedRegistered;
      }
      return registeredMatches[0] || null;
    }

    if (!rememberedTournamentId) return null;
    return (
      ensureArray(visibleTournamentLobbiesVigentes).find(
        (entry) => getTournamentEntryId(entry) === rememberedTournamentId
      ) || null
    );
  }, [
    lastJoinedTournamentId,
    registeredTournamentLobbiesVigentes,
    visibleTournamentLobbiesVigentes,
  ]);
  const independentMatches = useMemo(
    () =>
      onlineLobbyMatches
        .filter((entry) => isIndependentMatch(entry))
        .filter((entry) => !isSandboxMatch(entry))
        .filter((entry) => !hasIndependentMatchLoaded(entry))
        .filter((entry) => !isPersistentQuickTournament(entry))
        .filter((entry) => entry.memberSize > 0 && entry.connectedPlayers < entry.memberSize),
    [onlineLobbyMatches]
  );
  const independentFallbackSequenceById = useMemo(
    () => buildEntrySequenceLookup(independentMatches),
    [independentMatches]
  );
  const findMatchEntries = useMemo(
    () =>
      ensureArray(independentMatches).map((match, index) => {
        const tournamentId = getTournamentEntryId(match);
        const joinedPlayers = getJoinedUiPlayers(tournamentId, match?.users);
        const joinedUiCount = ensureArray(joinedUiPlayersByTournamentId[tournamentId]).length;
        const connectedPlayers = Math.max(
          0,
          Number(match?.connectedPlayers) || 0,
          joinedPlayers.length,
          joinedUiCount
        );
        const memberSize = Math.max(2, Number(match?.memberSize) || 2);
        const missingPlayers = Math.max(0, memberSize - connectedPlayers);
        const mode = resolveTournamentDisplayMode(match);
        const modeColumn = resolvePuzzleColumnMode(mode);
        const modeLabel = formatFindMatchesModeLabel(language, mode);
        const modeIconSrc = getModeIconSrc(mode);
        const playerCountIconSrc = getPlayerCountIconSrc(memberSize);
        const matchDurationIconSrc = getMatchDurationIconSrc(match?.havingTime);
        const rawTournamentName = String(match?.tournamentName || "").trim();
        const title = getLocalizedTournamentName(
          rawTournamentName,
          findMatchesText("challengeFallback", { count: index + 1 })
        );
        const titleCreatorMatch = String(rawTournamentName || title || "").match(
          /desafio creado por\s+(.+?)(?:\s*\(cimax.*\))?$/i
        );
        const titleCreator = extractPrimaryName(titleCreatorMatch?.[1] || "");
        const creatorCandidates = [
          titleCreator,
          match?.createdByName,
          match?.createdByDisplayName,
          match?.creatorName,
          match?.ownerName,
          typeof match?.createdBy === "string" ? match.createdBy : "",
          match?.createdBy?.displayName,
          match?.createdBy?.name,
          match?.createdBy?.email,
        ];
        const creatorName =
          creatorCandidates.map((candidate) => extractPrimaryName(candidate)).find(Boolean) ||
          findMatchesText("playerFallback");
        const createdAtMs = new Date(match?.createdAt || match?.startingTime || 0).valueOf();
        const safeCreatedAtMs = Number.isFinite(createdAtMs) ? createdAtMs : 0;
        const createdAtLabel = safeCreatedAtMs
          ? new Date(safeCreatedAtMs).toLocaleString(getLocaleForLanguage(language), {
              day: "2-digit",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
            })
          : findMatchesText("now");
        const turnTime = Math.max(1, Number(match?.havingTime) || DEFAULT_TURN_TIME);
        const paymentAmount = resolveTournamentRewardValue(match);
        const hasPayment = paymentAmount > 0;
        const hasPassword = Boolean(match?.hasMatchPassword);
        const isFull = connectedPlayers >= memberSize;
        const shareUrl = buildLobbyShareUrl("independent", tournamentId);
        const toneClass =
          modeColumn === "dice"
            ? "luck"
            : modeColumn === "sciencevsazar"
            ? "sciencevsazar"
            : modeColumn === "cienzar"
            ? "cienzar"
            : "science";

        return {
          match,
          tournamentId,
          sequenceNumber:
            independentFallbackSequenceById[tournamentId] ||
            resolvePublicSequenceNumber(match) ||
            0,
          title,
          creatorName,
          connectedPlayers,
          memberSize,
          missingPlayers,
          mode,
          modeColumn,
          modeLabel,
          modeIconSrc,
          playerCountIconSrc,
          matchDurationIconSrc,
          turnTime,
          paymentAmount,
          hasPayment,
          hasPassword,
          isFull,
          canJoin: Boolean(tournamentId) && !isFull,
          createdAtMs: safeCreatedAtMs,
          createdAtLabel,
          shareUrl,
          toneClass,
          searchText: `${title} ${creatorName} ${modeLabel} ${tournamentId}`.toLowerCase(),
        };
      }),
    [
      buildLobbyShareUrl,
      findMatchesText,
      getJoinedUiPlayers,
      getLocalizedTournamentName,
      independentMatches,
      independentFallbackSequenceById,
      joinedUiPlayersByTournamentId,
      language,
    ]
  );
  const visibleFindMatchEntries = useMemo(() => {
    const modeFilter =
      findMatchesModeFilter === "all" ? "all" : resolvePuzzleColumnMode(findMatchesModeFilter);
    const searchTerm = String(findMatchesSearch || "").trim().toLowerCase();

    return findMatchEntries
      .filter((entry) => modeFilter === "all" || entry.modeColumn === modeFilter)
      .filter((entry) => !searchTerm || entry.searchText.includes(searchTerm))
      .sort((a, b) => {
        if (findMatchesSort === "oldest") return a.createdAtMs - b.createdAtMs;
        if (findMatchesSort === "players") return b.connectedPlayers - a.connectedPlayers;
        if (findMatchesSort === "time") return a.turnTime - b.turnTime;
        return b.createdAtMs - a.createdAtMs;
      });
  }, [findMatchEntries, findMatchesModeFilter, findMatchesSearch, findMatchesSort]);
  const findMatchDashboardStats = useMemo(() => {
    const total = findMatchEntries.length;
    const available = findMatchEntries.filter((entry) => entry.canJoin).length;
    const privateMatches = findMatchEntries.filter((entry) => entry.hasPassword).length;
    const paidMatches = findMatchEntries.filter((entry) => entry.hasPayment).length;
    const joinedPlayers = findMatchEntries.reduce(
      (sum, entry) => sum + entry.connectedPlayers,
      0
    );
    const averageTurnTime = total
      ? Math.round(
          findMatchEntries.reduce((sum, entry) => sum + entry.turnTime, 0) / total
        )
      : 0;

    return {
      total,
      available,
      privateMatches,
      paidMatches,
      joinedPlayers,
      averageTurnTime,
    };
  }, [findMatchEntries]);
  useEffect(() => {
    if (shareLinkOpenRequest) return;
    shareLinkOpenHandledKeyRef.current = "";
  }, [shareLinkOpenRequest]);
  useEffect(() => {
    if (!shareLinkOpenRequest) return;
    const { shareType, tournamentId, requestKey } = shareLinkOpenRequest;
    if (shareLinkOpenHandledKeyRef.current === requestKey) return;
    shareLinkOpenHandledKeyRef.current = requestKey;

    const nextState = {
      openMatchmakingQuickView:
        shareType === "independent"
          ? "independientes"
          : shareType === "puzzle"
          ? "puzzles"
          : "torneos",
      openMatchmakingQuickViewRequestKey: `share-view-${requestKey}-${Date.now()}`,
    };

    if (shareType === "tournament" && tournamentId) {
      nextState.openTournamentWaitingId = tournamentId;
      nextState.openTournamentWaitingRequestKey = `share-tournament-${requestKey}-${Date.now()}`;
      nextState.openTournamentWaitingAutoPlay = false;
      nextState.openTournamentWaitingReturnTo = `${location.pathname}${location.search || ""}`;
    }

    navigate("/game", {
      replace: true,
      state: nextState,
    });
  }, [location.pathname, location.search, navigate, shareLinkOpenRequest]);
  useEffect(() => {
    if (!waitingRoomOpenRequest) return;
    const { tournamentId, requestKey, autoPlay, record, returnTo } = waitingRoomOpenRequest;
    if (waitingRoomOpenHandledKeyRef.current === requestKey) return;

    const recordTournamentId = String(record?._id || record?.id || record?.tournamentId || "").trim();
    const requestRecord =
      record && recordTournamentId === tournamentId
        ? {
            ...record,
            _id: record?._id || tournamentId,
          }
        : null;
    const hasTournamentSnapshot =
      Boolean(requestRecord) ||
      ensureArray(tournaments).length > 0 ||
      ensureArray(onlineLobbyMatches).length > 0;
    const mergedCandidates = [
      requestRecord,
      ...ensureArray(tournamentLobbies),
      ...ensureArray(independentMatches),
      ...ensureArray(onlineLobbyMatches),
      ...ensureArray(tournaments),
    ].filter(Boolean);
    const resolveCandidateTournamentId = (entry) =>
      String(entry?._id || entry?.id || entry?.tournamentId || "").trim();
    const targetTournament = mergedCandidates.find(
      (entry) => resolveCandidateTournamentId(entry) === tournamentId
    );

    if (!targetTournament && !hasTournamentSnapshot && !tournamentId) return;

    let cancelled = false;
    const buildFreshTournament = (payload, fallback) => {
      const source = payload && typeof payload === "object" ? payload : {};
      const base = fallback && typeof fallback === "object" ? fallback : {};
      const hasPassword = Object.prototype.hasOwnProperty.call(source, "hasMatchPassword")
        ? Boolean(source.hasMatchPassword)
        : Boolean(base.hasMatchPassword);
      const tournamentSequenceNumber = Math.max(
        0,
        Math.floor(
          normalizePublicSequenceNumberValue(base?.tournamentUiSequenceNumber) ||
            tournamentLobbyFallbackSequenceById[tournamentId] ||
            independentFallbackSequenceById[tournamentId] ||
            resolvePublicSequenceNumber(base) ||
            resolvePublicSequenceNumber(source) ||
            0
        )
      );

      return {
        ...base,
        ...source,
        _id: tournamentId,
        id: tournamentId,
        ...(tournamentSequenceNumber > 0
          ? {
              tournamentUiSequenceNumber: tournamentSequenceNumber,
              tournamentSequenceNumber,
              tournamentNumber: tournamentSequenceNumber,
              displayNumber: tournamentSequenceNumber,
            }
          : {}),
        tournamentName:
          String(source?.tournamentName || source?.name || base?.tournamentName || base?.name || "")
            .trim() || "Torneo",
        startingTime: source?.startingTime || source?.start || source?.createdAt || base?.startingTime,
        memberSize: Math.max(
          1,
          Number(source?.memberSize || source?.members || base?.memberSize || base?.members) || 1
        ),
        hasMatchPassword: hasPassword,
        tournamentRewardValue:
          source?.tournamentRewardValue ?? base?.tournamentRewardValue ?? 0,
        paidUsers: ensureArray(source?.paidUsers ?? base?.paidUsers),
        users: ensureArray(source?.users ?? base?.users),
      };
    };

    const loadAndOpenTargetTournament = async () => {
      try {
        const response = await axios.get(`/tournaments/${encodeURIComponent(tournamentId)}`);
        if (cancelled) return;
        const payload = response?.data || {};
        if (payload?.success === false) {
          throw new Error("No se encontro el torneo para abrir su sala de espera.");
        }

        const freshTournament = buildFreshTournament(
          payload,
          targetTournament || requestRecord || {}
        );
        waitingRoomOpenHandledKeyRef.current = requestKey;
        await openTournamentWaitingRoom(freshTournament, {
          autoPlay,
          cancelReturnTo: returnTo,
        });
      } catch (error) {
        if (cancelled) return;
        waitingRoomOpenHandledKeyRef.current = requestKey;
        const fallbackPath = returnTo && returnTo.trim()
          ? returnTo.trim()
          : "/game?quickView=torneos";
        navigate(fallbackPath, { replace: true });
        notification.warning({
          message: i18nT("lobby.salaDeEspera"),
          description:
            error?.response?.data?.message ||
            error?.message ||
            "No se encontro el torneo para abrir su sala de espera.",
        });
      }
    };

    loadAndOpenTargetTournament();
    return () => {
      cancelled = true;
    };
  }, [
    independentMatches,
    independentFallbackSequenceById,
    navigate,
    onlineLobbyMatches,
    openTournamentWaitingRoom,
    tournamentLobbyFallbackSequenceById,
    tournamentLobbies,
    tournaments,
    waitingRoomOpenRequest,
  ]);
  useEffect(() => {
    if (!retryPuzzleOpenRequest) return undefined;
    const { currentPuzzleId, requestKey } = retryPuzzleOpenRequest;
    if (retryPuzzleOpenHandledKeyRef.current === requestKey) return undefined;
    if (!currentPuzzleId) {
      retryPuzzleOpenHandledKeyRef.current = requestKey;
      navigate("/game", { replace: true });
      return undefined;
    }

    retryPuzzleOpenHandledKeyRef.current = requestKey;
    setMatchmakingQuickView("puzzles");

    let cancelled = false;
    const timer = setTimeout(() => {
      (async () => {
        const fetchOrderedPuzzles = async () => {
          const response = await axios.get("/tournaments/puzzles", {
            params: { limit: 300 },
          });
          return toOrderedPuzzleEntries(response?.data?.puzzles);
        };

        let lastFetchError = null;
        for (let attempt = 0; attempt < 8; attempt += 1) {
          if (cancelled) return;
          let orderedPuzzles = [];

          try {
            orderedPuzzles = await fetchOrderedPuzzles();
            lastFetchError = null;
          } catch (error) {
            lastFetchError = error;
            orderedPuzzles = [];
          }

          if (cancelled) return;
          if (orderedPuzzles.length > 0) {
            const currentIndex = orderedPuzzles.findIndex(
              (entry) => getPuzzleEntryId(entry) === currentPuzzleId
            );
            if (currentIndex >= 0) {
              const samePuzzleId = getPuzzleEntryId(orderedPuzzles[currentIndex]);
              if (!samePuzzleId) {
                throw new Error("No se pudo identificar el puzzle a reintentar.");
              }
              await handleStartPuzzle({ id: samePuzzleId });
              return;
            }
          }

          await new Promise((resolve) => setTimeout(resolve, 260));
        }

        if (lastFetchError) {
          throw lastFetchError;
        }

        notification.warning({
          message: i18nT("lobby.intentarDeNuevo"),
          description:
            i18nT("lobby.noSeEncontroElPuzzleActualEnLa"),
        });
        navigate("/game", { replace: true });
      })().catch((error) => {
        console.log(error);
        notification.error({
          message: i18nT("lobby.intentarDeNuevo"),
          description:
            error?.response?.data?.message ||
            error?.message ||
            "No se pudo reiniciar el puzzle.",
        });
        navigate("/game", { replace: true });
      });
    }, 120);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [handleStartPuzzle, navigate, retryPuzzleOpenRequest]);
  useEffect(() => {
    if (retryPuzzleOpenRequest) return undefined;
    if (!nextPuzzleOpenRequest) return undefined;
    const { currentPuzzleId, requestKey } = nextPuzzleOpenRequest;
    if (nextPuzzleOpenHandledKeyRef.current === requestKey) return undefined;
    if (!currentPuzzleId) {
      nextPuzzleOpenHandledKeyRef.current = requestKey;
      navigate("/game", { replace: true });
      return undefined;
    }

    nextPuzzleOpenHandledKeyRef.current = requestKey;
    setMatchmakingQuickView("puzzles");

    let cancelled = false;
    const timer = setTimeout(() => {
      (async () => {
        const fetchOrderedPuzzles = async () => {
          const response = await axios.get("/tournaments/puzzles", {
            params: { limit: 300 },
          });
          return toOrderedPuzzleEntries(response?.data?.puzzles);
        };
        const resolveNextPuzzleId = (orderedEntries) => {
          const currentIndex = orderedEntries.findIndex(
            (entry) => getPuzzleEntryId(entry) === currentPuzzleId
          );
          if (currentIndex < 0) {
            return { kind: "missing_current", nextPuzzleId: "" };
          }
          if (currentIndex >= orderedEntries.length - 1) {
            return { kind: "last", nextPuzzleId: "" };
          }
          const nextPuzzleId = getPuzzleEntryId(orderedEntries[currentIndex + 1]);
          if (!nextPuzzleId) return { kind: "missing_next", nextPuzzleId: "" };
          return { kind: "ok", nextPuzzleId };
        };

        let lastFetchError = null;
        let lastResolutionKind = "unknown";
        for (let attempt = 0; attempt < 8; attempt += 1) {
          if (cancelled) return;
          let orderedPuzzles = [];

          try {
            orderedPuzzles = await fetchOrderedPuzzles();
            lastFetchError = null;
          } catch (error) {
            lastFetchError = error;
            orderedPuzzles = [];
          }

          if (cancelled) return;
          if (orderedPuzzles.length > 0) {
            const resolution = resolveNextPuzzleId(orderedPuzzles);
            lastResolutionKind = resolution.kind;
            if (resolution.kind === "ok") {
              await handleStartPuzzle({ id: resolution.nextPuzzleId });
              return;
            }
            if (resolution.kind === "last") {
              notification.info({
                message: i18nT("lobby.noHaySiguientePuzzle"),
                description: i18nT("lobby.yaResolvisteElUltimoPuzzleDisponible"),
              });
              navigate("/game", { replace: true });
              return;
            }
          }

          await new Promise((resolve) => setTimeout(resolve, 260));
        }

        if (lastFetchError) {
          throw lastFetchError;
        }

        if (lastResolutionKind === "missing_current") {
          notification.warning({
            message: i18nT("lobby.siguientePuzzle"),
            description:
              i18nT("lobby.noSeEncontroElPuzzleActualEnLa"),
          });
          navigate("/game", { replace: true });
          return;
        }

        throw new Error("No se pudo identificar el siguiente puzzle en la secuencia.");
      })().catch((error) => {
        console.log(error);
        notification.error({
          message: i18nT("lobby.siguientePuzzle"),
          description:
            error?.response?.data?.message ||
            error?.message ||
            "No se pudo cargar el siguiente puzzle.",
        });
        navigate("/game", { replace: true });
      });
    }, 120);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [handleStartPuzzle, navigate, nextPuzzleOpenRequest, retryPuzzleOpenRequest]);
  useEffect(() => {
    if (retryPuzzleOpenRequest) return undefined;
    if (nextPuzzleOpenRequest) return undefined;
    if (!puzzleOpenRequest) return undefined;
    const { puzzleId, requestKey, puzzleOrder, puzzleMode, tutorialLevel } = puzzleOpenRequest;
    if (puzzleOpenHandledKeyRef.current === requestKey) return undefined;
    if (!puzzleId && !puzzleOrder) {
      puzzleOpenHandledKeyRef.current = requestKey;
      navigate("/game", { replace: true });
      return undefined;
    }

    puzzleOpenHandledKeyRef.current = requestKey;
    setMatchmakingQuickView("puzzles");

    const timer = setTimeout(() => {
      resolvePuzzleIdForOpenRequest(puzzleId, puzzleOrder, puzzleMode)
        .then((resolvedPuzzleId) => {
          if (!resolvedPuzzleId) {
            navigate("/game", { replace: true });
            return;
          }
          return handleStartPuzzle({ id: resolvedPuzzleId }, { tutorialLevel });
        })
        .catch((error) => {
          console.log(error);
          navigate("/game", { replace: true });
        });
    }, 120);

    return () => clearTimeout(timer);
  }, [
    handleStartPuzzle,
    navigate,
    nextPuzzleOpenRequest,
    puzzleOpenRequest,
    resolvePuzzleIdForOpenRequest,
    retryPuzzleOpenRequest,
  ]);
  useEffect(() => {
    if (retryPuzzleOpenRequest) return;
    if (nextPuzzleOpenRequest) return;
    if (puzzleOpenRequest) return;
    if (!matchmakingViewOpenRequest) return;
    const { view, requestKey } = matchmakingViewOpenRequest;
    if (matchmakingViewOpenHandledKeyRef.current === requestKey) return;

    matchmakingViewOpenHandledKeyRef.current = requestKey;
    setMatchmakingQuickView(view);
    setQuickViewButtonPulseKey(view);
    const nextState = {
      openMatchmakingQuickView: view,
      openMatchmakingQuickViewRequestKey: requestKey,
    };
    if (waitingRoomOpenRequest) {
      nextState.openTournamentWaitingId = waitingRoomOpenRequest.tournamentId;
      nextState.openTournamentWaitingRequestKey = waitingRoomOpenRequest.requestKey;
      nextState.openTournamentWaitingAutoPlay = waitingRoomOpenRequest.autoPlay;
      if (waitingRoomOpenRequest.record) {
        nextState.openTournamentWaitingRecord = waitingRoomOpenRequest.record;
      }
      if (waitingRoomOpenRequest.returnTo) {
        nextState.openTournamentWaitingReturnTo = waitingRoomOpenRequest.returnTo;
      }
    }
    if (puzzleModeFilter) {
      nextState.puzzleMode = puzzleModeFilter;
    }
    if (lobbyTournamentModeFilter) {
      nextState.tournamentMode = lobbyTournamentModeFilter;
    }
    if (isFromLobbyPuzzle) {
      nextState.source = "lobbyPuzzle";
      nextState.fromLobbyPuzzle = "1";
    }
    if (isFromLobbyTournament) {
      nextState.source = "lobbyTournament";
      nextState.fromLobbyTournament = "1";
    }
    if (isFromLobbyCreateMatch) {
      nextState.source = "lobbyCreate";
      nextState.fromLobbyCreate = "1";
      nextState.fromLobbySource = fromLobbySource || "independientes";
      nextState.lobbyCreateAuto = lobbyCreatePreferences?.autoCreate ? "1" : "0";
      if (lobbyCreatePreferences) {
        nextState.lobbyCreatePreferences = lobbyCreatePreferences;
      }
    }
    const nextPath = (() => {
      const nextParams = new URLSearchParams();
      if (isFromLobbyPuzzle) {
        nextParams.set("fromLobbyPuzzle", "1");
      }
      if (isFromLobbyTournament) {
        nextParams.set("fromLobbyTournament", "1");
      }
      if (isFromLobbyCreateMatch) {
        nextParams.set("fromLobbyCreate", "1");
        nextParams.set("fromLobbySource", fromLobbySource || "independientes");
        if (lobbyCreatePreferences) {
          nextParams.set("lobbyCreatePlayType", String(lobbyCreatePreferences.playType));
          nextParams.set("lobbyCreateMode", String(lobbyCreatePreferences.mode));
          nextParams.set("lobbyCreatePlayers", String(lobbyCreatePreferences.memberSize));
          nextParams.set("lobbyCreateTime", String(lobbyCreatePreferences.havingTime));
          nextParams.set("lobbyCreateDifficulty", String(lobbyCreatePreferences.botDifficulty));
          nextParams.set("lobbyCreateAuto", lobbyCreatePreferences.autoCreate ? "1" : "0");
          nextParams.set("lobbyCreateRequestKey", String(lobbyCreatePreferences.requestKey));
        }
      }
      nextParams.set("quickView", view);
      if (nextState.puzzleMode) {
        nextParams.set("puzzleMode", String(nextState.puzzleMode));
      }
      if (nextState.tournamentMode) {
        nextParams.set("tournamentMode", String(nextState.tournamentMode));
      }
      if (waitingRoomOpenRequest) {
        nextParams.set("openTournamentWaitingId", String(waitingRoomOpenRequest.tournamentId));
        nextParams.set(
          "openTournamentWaitingRequestKey",
          String(waitingRoomOpenRequest.requestKey)
        );
        if (waitingRoomOpenRequest.returnTo) {
          nextParams.set("openTournamentWaitingReturnTo", String(waitingRoomOpenRequest.returnTo));
        }
        if (waitingRoomOpenRequest.autoPlay) {
          nextParams.set("openTournamentWaitingAutoPlay", "1");
        }
      }
      return `/game?${nextParams.toString()}`;
    })();
    navigate(nextPath, {
      replace: true,
      state: nextState,
    });
  }, [
    matchmakingViewOpenRequest,
    navigate,
    nextPuzzleOpenRequest,
    puzzleOpenRequest,
    retryPuzzleOpenRequest,
    puzzleModeFilter,
    lobbyTournamentModeFilter,
    isFromLobbyPuzzle,
    isFromLobbyTournament,
    isFromLobbyCreateMatch,
    fromLobbySource,
    lobbyCreatePreferences,
    waitingRoomOpenRequest,
  ]);
  useEffect(() => {
    if (!quickViewButtonPulseKey) return undefined;
    const timer = setTimeout(() => {
      setQuickViewButtonPulseKey("");
    }, 190);
    return () => clearTimeout(timer);
  }, [quickViewButtonPulseKey]);
  const clearSelectedButton = useCallback(() => {
    const previousButton = selectedButtonRef.current;
    if (previousButton) {
      previousButton.style.scale = "";
      previousButton.removeAttribute("data-xima-selected");
    }
    selectedButtonRef.current = null;
  }, []);
  const ensureButtonInteractionId = useCallback((buttonElement) => {
    if (!buttonElement) return "";
    const existing = String(buttonElement.getAttribute("data-xima-button-id") || "").trim();
    if (existing) return existing;
    buttonInteractionCounterRef.current += 1;
    const nextId = `xima-btn-${buttonInteractionCounterRef.current}`;
    buttonElement.setAttribute("data-xima-button-id", nextId);
    return nextId;
  }, []);
  const setSelectedButton = useCallback(
    (buttonElement, buttonId) => {
      if (!buttonElement || !buttonId) return;
      const previousButton = selectedButtonRef.current;
      if (previousButton && previousButton !== buttonElement) {
        previousButton.style.scale = "";
        previousButton.removeAttribute("data-xima-selected");
      }

      const previousId = String(
        previousButton?.getAttribute("data-xima-button-id") || ""
      ).trim();
      if (previousButton === buttonElement && previousId === buttonId) {
        clearSelectedButton();
        return;
      }

      buttonElement.style.scale = "1.05";
      buttonElement.setAttribute("data-xima-selected", "true");
      selectedButtonRef.current = buttonElement;
    },
    [clearSelectedButton]
  );
  const resolveButtonSoundZone = useCallback((buttonElement) => {
    const zoneContainer =
      buttonElement && typeof buttonElement.closest === "function"
        ? buttonElement.closest("[data-sound-zone]")
        : null;
    const rawZone = String(zoneContainer?.getAttribute("data-sound-zone") || "interface")
      .trim()
      .toLowerCase();
    if (rawZone === "left" || rawZone === "right" || rawZone === "interface") {
      return rawZone;
    }
    return "interface";
  }, []);
  const playButtonZoneSound = useCallback((zone = "interface") => {
    if (typeof window === "undefined") return;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;

    const zonePresetMap = {
      left: { wave: "triangle", from: 900, to: 660, gain: 0.055, duration: 0.1 },
      right: { wave: "square", from: 560, to: 420, gain: 0.045, duration: 0.11 },
      interface: { wave: "sine", from: 760, to: 540, gain: 0.05, duration: 0.095 },
    };
    const preset = zonePresetMap[zone] || zonePresetMap.interface;

    try {
      const audioContext = new AudioContextClass();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.type = preset.wave;
      oscillator.frequency.setValueAtTime(preset.from, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(
        preset.to,
        audioContext.currentTime + preset.duration
      );
      gainNode.gain.setValueAtTime(0.0001, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(
        preset.gain,
        audioContext.currentTime + 0.008
      );
      gainNode.gain.exponentialRampToValueAtTime(
        0.0001,
        audioContext.currentTime + preset.duration
      );
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + preset.duration + 0.02);
      oscillator.onended = () => {
        if (typeof audioContext.close === "function") {
          audioContext.close().catch(() => {});
        }
      };
    } catch (error) {
      console.log(error);
    }
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleGlobalButtonClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const button = target?.closest("button");
      if (!button) return;
      if (button.disabled || button.getAttribute("aria-disabled") === "true") return;

      // En el replay los botones del control y del timeline no suenan.
      const isReplayButton = Boolean(button.closest(".xima-replay-modal"));
      if (!isReplayButton) playButtonZoneSound(resolveButtonSoundZone(button));
      const buttonId = ensureButtonInteractionId(button);
      setSelectedButton(button, buttonId);
    };

    window.addEventListener("click", handleGlobalButtonClick, true);
    return () => {
      window.removeEventListener("click", handleGlobalButtonClick, true);
    };
  }, [ensureButtonInteractionId, playButtonZoneSound, resolveButtonSoundZone, setSelectedButton]);
  useEffect(
    () => () => {
      clearSelectedButton();
    },
    [clearSelectedButton]
  );
  const manualReconnectPlayerId = useMemo(
    () => String(user?.playerCode || user?.email || "").trim(),
    [user?.email, user?.playerCode]
  );
  const getLatestManualReconnectMatch = useCallback(async () => {
    if (!manualReconnectPlayerId) return null;
    try {
      const response = await axios.get("/partida/ultima", {
        params: { playerID: manualReconnectPlayerId },
      });
      const matchData =
        response?.data && typeof response.data === "object" && !Array.isArray(response.data)
          ? response.data
          : {};
      const matchID = String(matchData?.matchID || matchData?.tournamentId || "").trim();
      const roomID = String(matchData?.roomID || matchData?.roomId || "").trim();
      const isParticipant = matchData?.isParticipant === true;
      const reconnectBy = Math.max(0, Number(matchData?.reconnectBy) || 0);
      const hasActiveReconnectWindow = reconnectBy > Date.now();
      if (!matchID || !isParticipant || !hasActiveReconnectWindow) return null;
      return {
        matchData,
        matchID,
        roomID,
        reconnectBy,
      };
    } catch (error) {
      const status = Number(error?.response?.status || 0);
      if ([403, 404, 409].includes(status)) return null;
      throw error;
    }
  }, [manualReconnectPlayerId]);
  const navigateToManualReconnectMatch = useCallback(
    async (snapshot) => {
      const matchID = String(snapshot?.matchID || "").trim();
      if (!matchID) return false;
      const roomID = String(snapshot?.roomID || "").trim();
      const matchData =
        snapshot?.matchData && typeof snapshot.matchData === "object" ? snapshot.matchData : {};

      await clearReplaySession();

      const search = new URLSearchParams();
      search.set("manualReconnect", "1");
      if (roomID) search.set("roomId", roomID);

      navigate(`/game/${encodeURIComponent(matchID)}?${search.toString()}`, {
        state: {
          manualReentryMatchData: matchData,
          manualReentryRequestKey: `manual-reentry-${matchID}-${roomID || "sin-sala"}-${Date.now()}`,
        },
      });
      return true;
    },
    [clearReplaySession, navigate]
  );
  useEffect(() => {
    if (!manualReconnectPlayerId) {
      setManualReconnectAvailable(false);
      manualReconnectAutoAttemptedPlayerRef.current = "";
      return undefined;
    }

    let cancelled = false;

    const checkManualReconnectState = async (allowAutoReconnect = false) => {
      try {
        const snapshot = await getLatestManualReconnectMatch();
        if (cancelled) return;

        const canReconnect = Boolean(snapshot?.matchID);
        setManualReconnectAvailable(canReconnect);
        if (!allowAutoReconnect || !canReconnect) return;
        if (manualReconnectAutoAttemptedPlayerRef.current === manualReconnectPlayerId) return;

        manualReconnectAutoAttemptedPlayerRef.current = manualReconnectPlayerId;
        setManualReconnectLoading(true);
        try {
          await navigateToManualReconnectMatch(snapshot);
        } catch (error) {
          console.log(error);
        } finally {
          if (!cancelled) {
            setManualReconnectLoading(false);
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.log(error);
        }
      }
    };

    checkManualReconnectState(true).catch((error) => console.log(error));
    const intervalId = setInterval(() => {
      checkManualReconnectState(false).catch((error) => console.log(error));
    }, 15000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [getLatestManualReconnectMatch, manualReconnectPlayerId, navigateToManualReconnectMatch]);
  const handleVolverAMiPartida = useCallback(async () => {
    if (manualReconnectLoading) return;
    try {
      setManualReconnectLoading(true);
      const snapshot = await getLatestManualReconnectMatch();
      if (!snapshot?.matchID) {
        setManualReconnectAvailable(false);
        return;
      }
      setManualReconnectAvailable(true);
      await navigateToManualReconnectMatch(snapshot);
    } catch (error) {
      notification.error({
        message: i18nT("lobby.noSePudoVolverATuPartida"),
        description:
          error?.response?.data?.message ||
          error?.message ||
          "No hay una partida online activa para este jugador.",
      });
    } finally {
      setManualReconnectLoading(false);
    }
  }, [
    getLatestManualReconnectMatch,
    manualReconnectLoading,
    navigateToManualReconnectMatch,
  ]);
  const shouldShowManualReconnectButton = manualReconnectLoading || manualReconnectAvailable;
  const handleQuickViewTopButtonPress = useCallback(
    (buttonKey, action) => {
      setQuickViewButtonPulseKey(String(buttonKey || ""));
      if (typeof action === "function") {
        action();
      }
    },
    []
  );
  const handleLegacyDesignClick = useCallback(() => {
    navigate(
      "/game?fromLobbyCubeButton=1&showBlockPanels=1&onlyCustomizationPanel=1&fromLobbySource=cube"
    );
  }, [navigate]);
  const handleLegacyCreateGameClick = useCallback(() => {
    navigate("/game/create-game", {
      state: {
        fromLobbySource: "create",
        source: "legacyCreateGameButton",
      },
    });
  }, [navigate]);
  const handleLegacyPuzzleClick = useCallback(() => {
    handleQuickViewTopButtonPress("puzzles", () => {
      setMatchmakingQuickView("puzzles");
    });
  }, [handleQuickViewTopButtonPress]);
  const handleLegacyScienceClick = useCallback(() => {
    handleQuickViewTopButtonPress("torneos", () => {
      setMatchmakingQuickView("torneos");
    });
  }, [handleQuickViewTopButtonPress]);
  const legacyMobileHomeTiles = [
    {
      key: "design",
      label: i18nT("lobby.disenar"),
      icon: IoCubeOutline,
      tone: "green-gold",
      position: "legacy-pos-design",
      onClick: handleLegacyDesignClick,
    },
    {
      key: "store",
      label: t("TIENDA"),
      icon: IoGiftOutline,
      tone: "green",
      position: "legacy-pos-store",
      onClick: () => navigate("/game/store?fromLobbySource=store"),
    },
    {
      key: "friends",
      label: t("AMIGOS"),
      icon: IoPeopleOutline,
      tone: "emerald",
      position: "legacy-pos-friends",
      onClick: () => navigate("/game/friends?fromLobbySource=friends"),
    },
    {
      key: "create",
      label: t("CREAR_JUEGO"),
      icon: IoGameControllerOutline,
      tone: "blue",
      position: "legacy-pos-create",
      onClick: handleLegacyCreateGameClick,
    },
    {
      key: "history",
      label: t("HISTORIAL"),
      icon: IoFolderOpenOutline,
      tone: "teal",
      position: "legacy-pos-history",
      onClick: () => navigate("/game/history"),
    },
    {
      key: "learn",
      label: t("APRENDER"),
      icon: IoBookOutline,
      tone: "purple",
      position: "legacy-pos-learn",
      onClick: () => navigate("/game/learn?fromLobbySource=learn"),
    },
    {
      key: "watch",
      label: t("VER_PARTIDOS"),
      icon: IoEyeOutline,
      tone: "magenta",
      position: "legacy-pos-watch",
      onClick: () => navigate("/game/spectate?fromLobbySource=spectate"),
    },
    {
      key: "puzzle",
      label: "PUZZLE",
      icon: IoExtensionPuzzleOutline,
      tone: "red",
      position: "legacy-pos-puzzle",
      onClick: handleLegacyPuzzleClick,
    },
    {
      key: "settings",
      label: t("AJUSTES"),
      icon: IoCogOutline,
      tone: "indigo",
      position: "legacy-pos-settings",
      onClick: () => navigate("/game/settings?section=general&fromLobbySource=settings"),
    },
  ];
  const newsTournamentCalendar = useMemo(() => {
    const sourceData = ensureArray(filteredTournaments).length
      ? ensureArray(filteredTournaments)
      : ensureArray(tournaments);
    const source = sourceData
      .map((record) => {
        const status = getTournamentStatus(record);
        const startMs = new Date(record?.startingTime || 0).valueOf();
        const hasStartDate = Number.isFinite(startMs) && startMs > 0;
        const modeLabel = String(formatModeLabel(record?.mode || "")).toUpperCase();
        return {
          ...record,
          status,
          startMs: hasStartDate ? startMs : 0,
          hasStartDate,
          modeLabel,
        };
      })
      .filter((record) => record.status !== "finished")
      .sort((a, b) => {
        if (a.hasStartDate && b.hasStartDate) return a.startMs - b.startMs;
        if (a.hasStartDate) return -1;
        if (b.hasStartDate) return 1;
        return new Date(b?.createdAt || 0).valueOf() - new Date(a?.createdAt || 0).valueOf();
      });

    const available = source.filter((record) => !record.hasStartDate || record.startMs <= nowMs);
    const upcoming = source.filter((record) => record.hasStartDate && record.startMs > nowMs);
    return {
      available: available.slice(0, 12),
      upcoming: upcoming.slice(0, 12),
    };
  }, [filteredTournaments, getTournamentStatus, nowMs, tournaments]);
  const tournamentWaitingPlayers = useMemo(() => {
    const tournamentId = String(tournamentWaitingRecord?._id || "").trim();
    const waitingCimaxMode = resolveCimaxModeFieldFromMode(
      tournamentWaitingRecord?.mode,
      tournamentWaitingRecord?.freeBoxBaseMode
    );
    const sourceUsers = getJoinedUiPlayers(tournamentId, tournamentWaitingRecord?.users);
    const recordUsers = buildJoinedUiPlayersFromRawUsers(tournamentWaitingRecord?.users);
    const mergedUsers = [...recordUsers];

    sourceUsers.forEach((candidate) => {
      const identity = resolveJoinedUiPlayerIdentity(candidate);
      if (!identity) return;
      const existingIndex = mergedUsers.findIndex(
        (entry) =>
          resolveJoinedUiPlayerIdentity(entry) === identity ||
          joinedUiPlayersShareIdentity(entry, candidate)
      );
      if (existingIndex < 0) {
        mergedUsers.push(candidate);
        return;
      }
      const existing = mergedUsers[existingIndex];
      const existingCimaxScore = resolveOptionalCimaxScore(existing?.cimaxScore);
      const candidateCimaxScore = resolveOptionalCimaxScore(candidate?.cimaxScore);
      const keepExistingCimax = existing?.hasCimaxData && !candidate?.hasCimaxData;
      mergedUsers[existingIndex] = {
        ...existing,
        ...candidate,
        ranking: Math.max(0, Number(existing?.ranking) || 0, Number(candidate?.ranking) || 0),
        state: Math.max(0, Number(existing?.state) || 0, Number(candidate?.state) || 0),
        cimaxScores: keepExistingCimax ? existing?.cimaxScores || null : candidate?.cimaxScores || null,
        cimaxMode: keepExistingCimax ? existing?.cimaxMode || "" : candidate?.cimaxMode || "",
        cimaxScore: keepExistingCimax || candidateCimaxScore === null
          ? existingCimaxScore
          : candidateCimaxScore,
        hasCimaxData: Boolean(existing?.hasCimaxData || candidate?.hasCimaxData),
      };
    });

    const liveRoom = liveMatchesByTournamentId[tournamentId] || null;
    const livePlayers = buildJoinedUiPlayersFromRawUsers(liveRoom?.players);
    const waitingPresencePlayers = buildJoinedUiPlayersFromRawUsers(
      waitingRoomPresenceByTournamentId[tournamentId]
    );
    const buildAliasSet = (entries = []) => {
      const aliasSet = new Set();
      ensureArray(entries).forEach((entry) => {
        resolveJoinedUiPlayerIdentityAliases(entry).forEach((alias) => aliasSet.add(alias));
      });
      return aliasSet;
    };
    const serverActivity = waitingRoomActivityByTournamentId[tournamentId] || null;
    // The server tells who is searching for a rival or playing; without that data
    // fall back to the live rooms list for "playing".
    const playingIdentitySet = buildAliasSet(
      serverActivity ? serverActivity.playing : livePlayers
    );
    const searchingIdentitySet = buildAliasSet(serverActivity ? serverActivity.searching : []);
    const waitingIdentitySet = buildAliasSet(waitingPresencePlayers);
    const currentWaitingPlayerAliases = resolveJoinedUiPlayerIdentityAliases(
      buildJoinedUiPlayersFromRawUsers([currentTournamentPlayer])[0]
    );
    const isCurrentWaitingRoom =
      tournamentWaitingOpen &&
      tournamentWaitingShouldRemainJoinedRef.current &&
      String(tournamentWaitingTargetRef.current || tournamentId).trim() === tournamentId;

    const mapped = mergedUsers.map((entry, index) => {
      const identity =
        extractPrimaryName(entry?.name || entry?.displayName) || `Jugador ${index + 1}`;
      const playerAliases = resolveJoinedUiPlayerIdentityAliases(entry);
      const points = Math.max(0, Number(entry?.ranking) || 0);
      const state = Math.max(0, Number(entry?.state) || 0);
      const isCurrentPlayerWaitingHere =
        isCurrentWaitingRoom &&
        playerAliases.length > 0 &&
        currentWaitingPlayerAliases.length > 0 &&
        playerAliases.some((alias) => currentWaitingPlayerAliases.includes(alias));
      const matchesAnyAlias = (aliasSet) =>
        playerAliases.some((alias) => aliasSet.has(alias));
      const tournamentPresenceStatus =
        playerAliases.length === 0
          ? "offline"
          : matchesAnyAlias(playingIdentitySet)
          ? "playing"
          : matchesAnyAlias(searchingIdentitySet)
          ? "searching"
          : matchesAnyAlias(waitingIdentitySet) || isCurrentPlayerWaitingHere
          ? "waiting"
          : "offline";
      const isActiveInTournament = tournamentPresenceStatus !== "offline";
      const hasPlayedInTournament = state >= 2;
      const secondary = String(entry?.secondary || entry?.id || "")
        .trim()
        .toUpperCase();
      const resolvedCimaxScores =
        entry?.cimaxScores && typeof entry?.cimaxScores === "object"
          ? normalizeCimaxScores(entry.cimaxScores)
          : null;
      const entryCimaxMode = String(entry?.cimaxMode || "")
        .trim()
        .toLowerCase();
      const cimaxMode = entryCimaxMode || waitingCimaxMode;
      const explicitCimaxScore = resolveOptionalCimaxScore(entry?.cimaxScore);
      const cimaxScoreByMode =
        cimaxMode && resolvedCimaxScores
          ? resolveOptionalCimaxScore(resolvedCimaxScores?.[cimaxMode])
          : null;
      const cimaxScore = explicitCimaxScore ?? cimaxScoreByMode;
      const cimaxLabel = cimaxMode ? `CIMAX ${formatCimaxModeLabel(cimaxMode)}` : "CIMAX";
      const profileTarget = resolveProfileTargetFromUserLike(entry);

      return {
        id: String(entry?.id || `waiting-player-${index}`),
        displayName: identity,
        avatarUrl: resolveAvatarSrc(entry?.avatarUrl || ""),
        initials: getInitials(identity),
        secondary,
        points,
        isPlaying: isActiveInTournament,
        isActiveInTournament,
        tournamentPresenceStatus,
        hasPlayedInTournament,
        cimaxLabel,
        cimaxScore,
        profileTarget,
        statusLabel: isActiveInTournament
          ? "Activo"
          : hasPlayedInTournament
          ? "Fuera de sala"
          : "Inactivo",
      };
    });

    return mapped
      .sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        if (Number(b.isPlaying) !== Number(a.isPlaying)) {
          return Number(b.isPlaying) - Number(a.isPlaying);
        }
        return String(a.displayName || "").localeCompare(String(b.displayName || ""), "es", {
          sensitivity: "base",
        });
      })
      .map((entry, index) => ({
        ...entry,
        position: index + 1,
      }));
  }, [
    buildJoinedUiPlayersFromRawUsers,
    currentTournamentPlayer,
    getJoinedUiPlayers,
    joinedUiPlayersShareIdentity,
    liveMatchesByTournamentId,
    resolveProfileTargetFromUserLike,
    resolveJoinedUiPlayerIdentity,
    resolveJoinedUiPlayerIdentityAliases,
    tournamentWaitingRecord?.freeBoxBaseMode,
    tournamentWaitingRecord?._id,
    tournamentWaitingRecord?.mode,
    tournamentWaitingRecord?.users,
    tournamentWaitingOpen,
    waitingRoomActivityByTournamentId,
    waitingRoomPresenceByTournamentId,
  ]);
  const tournamentWaitingStartRemainingMs = useMemo(() => {
    const startMs = new Date(tournamentWaitingRecord?.startingTime || 0).valueOf();
    if (!Number.isFinite(startMs) || startMs <= 0) return 0;
    return Math.max(0, startMs - nowMs);
  }, [nowMs, tournamentWaitingRecord?.startingTime]);
  const tournamentWaitingCanPlay = useMemo(
    () =>
      Boolean(tournamentWaitingRecord?._id) &&
      !tournamentWaitingJoinLoading &&
      tournamentWaitingStartRemainingMs <= 0,
    [tournamentWaitingJoinLoading, tournamentWaitingRecord?._id, tournamentWaitingStartRemainingMs]
  );
  const tournamentWaitingMemberSize = useMemo(() => {
    const explicitSize = Math.floor(Number(tournamentWaitingRecord?.memberSize) || 0);
    if (explicitSize >= 2 && explicitSize <= 4) return explicitSize;

    const inferredSize = Math.max(
      ensureArray(tournamentWaitingRecord?.users).length,
      tournamentWaitingPlayers.length,
      2
    );
    return Math.min(4, inferredSize);
  }, [
    tournamentWaitingPlayers.length,
    tournamentWaitingRecord?.memberSize,
    tournamentWaitingRecord?.users,
  ]);
  const tournamentWaitingPlayerIconSrc = useMemo(
    () => getPlayerCountIconSrc(tournamentWaitingMemberSize),
    [tournamentWaitingMemberSize]
  );
  const tournamentWaitingDisplayMode = useMemo(
    () => resolveTournamentDisplayMode(tournamentWaitingRecord || {}),
    [tournamentWaitingRecord]
  );
  const tournamentWaitingModeLabel = useMemo(
    () => String(formatModeLabel(tournamentWaitingDisplayMode)).toUpperCase(),
    [tournamentWaitingDisplayMode]
  );
  const tournamentWaitingModeIconSrc = useMemo(
    () => getModeIconSrc(tournamentWaitingDisplayMode),
    [tournamentWaitingDisplayMode]
  );
  const tournamentWaitingEntryFee = useMemo(
    () => resolveTournamentRewardValue(tournamentWaitingRecord || {}),
    [tournamentWaitingRecord]
  );
  const tournamentWaitingWinnerPrize = useMemo(
    () => resolveTournamentWinnerPrizeValue(tournamentWaitingRecord || {}),
    [tournamentWaitingRecord]
  );
  const tournamentWaitingSequenceNumber = useMemo(() => {
    const tournamentId = getTournamentEntryId(tournamentWaitingRecord || {});
    return (
      normalizePublicSequenceNumberValue(tournamentWaitingRecord?.tournamentUiSequenceNumber) ||
      tournamentLobbyFallbackSequenceById[tournamentId] ||
      independentFallbackSequenceById[tournamentId] ||
      resolvePublicSequenceNumber(tournamentWaitingRecord || {}) ||
      0
    );
  }, [
    independentFallbackSequenceById,
    tournamentLobbyFallbackSequenceById,
    tournamentWaitingRecord,
  ]);
  const tournamentWaitingSequenceLabel = useMemo(
    () =>
      tournamentWaitingSequenceNumber > 0
        ? formatPublicSequenceLabel(language, "tournament", tournamentWaitingSequenceNumber)
        : "",
    [language, tournamentWaitingSequenceNumber]
  );
  const tournamentWaitingEndRemainingMs = useMemo(
    () => resolveTournamentEndRemainingMs(tournamentWaitingRecord, nowMs),
    [nowMs, tournamentWaitingRecord]
  );
  const tournamentWaitingCountdownLabel = useMemo(() => {
    if (tournamentWaitingStartRemainingMs > 0) {
      return {
        title: i18nT("lobby.elTorneoEmpiezaEn"),
        value: formatCountdownLabel(tournamentWaitingStartRemainingMs),
      };
    }
    if (tournamentWaitingEndRemainingMs > 0) {
      return {
        title: i18nT("lobby.elTorneoTerminaEn"),
        value: formatCountdownLabel(tournamentWaitingEndRemainingMs),
      };
    }
    return {
      title: i18nT("lobby.salaActiva"),
      value: "00:00:00",
    };
  }, [tournamentWaitingEndRemainingMs, tournamentWaitingStartRemainingMs]);
  const tournamentWaitingShareUrl = useMemo(() => {
    const tournamentId = String(tournamentWaitingRecord?._id || "").trim();
    return tournamentId ? buildLobbyShareUrl("tournament", tournamentId) : "";
  }, [buildLobbyShareUrl, tournamentWaitingRecord?._id]);
  const tournamentWaitingPositionRows = useMemo(() => {
    const filledRows = tournamentWaitingPlayers.slice(0, 12);
    const placeholders = Array.from({
      length: Math.max(0, 12 - filledRows.length),
    }).map((_, index) => ({
      id: `empty-position-${index + 1}`,
      displayName: "Esperando jugador",
      secondary: `XIMA#${String(filledRows.length + index + 1).padStart(3, "0")}`,
      initials: "--",
      points: 0,
      cimaxScore: null,
      position: filledRows.length + index + 1,
      isPlaceholder: true,
    }));
    return [...filledRows, ...placeholders];
  }, [tournamentWaitingPlayers]);
  const tournamentWaitingChatRows = useMemo(() => {
    if (tournamentWaitingChatMessages.length > 0) return tournamentWaitingChatMessages;
    const seededPlayers = tournamentWaitingPlayers.slice(0, 4);
    if (seededPlayers.length === 0) {
      return [
        {
          id: "waiting-chat-system",
          sender: "Sistema",
          text: "Esperando a que los jugadores entren a la sala.",
        },
      ];
    }
    return seededPlayers.map((player, index) => ({
      id: `waiting-chat-seed-${player.id || index}`,
      sender: player.displayName,
      text: index === 0 ? "Listo para competir." : "Esperando el inicio del torneo.",
    }));
  }, [tournamentWaitingChatMessages, tournamentWaitingPlayers]);
  const handleTournamentWaitingChatSend = useCallback(() => {
    const text = String(tournamentWaitingChatInput || "").trim();
    if (!text) return;
    const sender =
      String(currentTournamentPlayer?.displayName || currentTournamentPlayer?.name || currentTournamentPlayer?.userId || "")
        .trim() || "Jugador";
    setTournamentWaitingChatMessages((previous) => [
      ...previous.slice(-24),
      {
        id: `waiting-chat-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        sender,
        text: text.slice(0, 180),
      },
    ]);
    setTournamentWaitingChatInput("");
  }, [currentTournamentPlayer, tournamentWaitingChatInput]);

  useEffect(() => {
    const waitingId = String(tournamentWaitingRecord?._id || "").trim();
    if (!tournamentWaitingOpen || !waitingId) return;

    const updatedRecord = ensureArray(tournaments).find(
      (entry) => String(entry?._id || "").trim() === waitingId
    );
    if (updatedRecord) {
      setTournamentWaitingRecord((prev) => {
        if (!prev || String(prev?._id || "").trim() !== waitingId) return prev;
        const mergedUsers = getJoinedUiPlayers(waitingId, [
          ...ensureArray(prev?.users),
          ...ensureArray(updatedRecord?.users),
        ]);
        const sequenceNumber = Math.max(
          0,
          Math.floor(
            normalizePublicSequenceNumberValue(prev?.tournamentUiSequenceNumber) ||
              tournamentLobbyFallbackSequenceById[waitingId] ||
              independentFallbackSequenceById[waitingId] ||
              resolvePublicSequenceNumber(prev) ||
              resolvePublicSequenceNumber(updatedRecord) ||
              0
          )
        );
        return {
          ...updatedRecord,
          ...(sequenceNumber > 0
            ? {
                tournamentUiSequenceNumber: sequenceNumber,
                tournamentSequenceNumber: sequenceNumber,
                tournamentNumber: sequenceNumber,
                displayNumber: sequenceNumber,
              }
            : {}),
          users: mergedUsers,
        };
      });
    }
  }, [
    getJoinedUiPlayers,
    independentFallbackSequenceById,
    tournamentWaitingOpen,
    tournamentWaitingRecord?._id,
    tournamentLobbyFallbackSequenceById,
    tournaments,
  ]);

  const primaryActionLabel = useMemo(() => {
    if (creating) return tx("Creando...", "Creating...");
    if (playType === "online" || playType === "freebox") {
      return canConfigureSuperTournamentDuration &&
        normalizedSuperTournamentMinutes >= SUPER_TOURNAMENT_MIN_MINUTES
        ? tx("Crear Torneo", "Create Tournament")
        : tx("Crear", "Create");
    }
    return tx("Jugar", "PLAY");
  }, [
    canConfigureSuperTournamentDuration,
    creating,
    normalizedSuperTournamentMinutes,
    playType,
    tx,
  ]);
  // Sección «Crear torneo» del panel de admin: reutiliza el mismo estado y las
  // mismas acciones del panel de selección (solo Multijugador en línea).
  const adminPuzzleOrderById = useMemo(
    () => ({ ...adminPuzzleFallbackVisualOrderById, ...adminPuzzleVisualOrderById }),
    [adminPuzzleFallbackVisualOrderById, adminPuzzleVisualOrderById]
  );
  const adminCreateTournamentApi = {
    ready: playType === "online" && matchmakingQuickView === "torneos",
    onEnter: () => {
      if (matchmakingQuickView !== "torneos") setMatchmakingQuickView("torneos");
      if (playType !== "online") handlePlayTypeSelect("online");
    },
    formData,
    panelProgress,
    creating,
    allowedMemberOptions,
    memberOptions: MEMBER_OPTIONS,
    timeOptions: TIME_OPTIONS,
    onModeChange: handleModeChange,
    onMemberSize: handleMemberSizeSelect,
    onTurnTime: handleTurnTimeSelect,
    onPasswordEnabled: handleMatchPasswordEnabledChange,
    onPassword: handleMatchPasswordChange,
    passwordMaxLength: MATCH_PASSWORD_MAX_LENGTH,
    superMinutesInput: superTournamentMinutesInput,
    onSuperMinutes: setSuperTournamentMinutesInput,
    superMinMinutes: SUPER_TOURNAMENT_MIN_MINUTES,
    superMaxMinutes: SUPER_TOURNAMENT_MAX_MINUTES,
    superDefaultMinutes: DEFAULT_SUPER_TOURNAMENT_MINUTES,
    normalizedSuperMinutes: normalizedSuperTournamentMinutes,
    canConfigureDuration: canConfigureSuperTournamentDuration,
    onPaymentEnabled: handleTournamentPaymentEnabledChange,
    onRewardValue: handleTournamentRewardValueChange,
    onPrizeValue: handleTournamentWinnerPrizeValueChange,
    maxRewardValue: MAX_TOURNAMENT_REWARD_VALUE,
    onName: handleTournamentNameChange,
    onCreationDate: handleCreationDateChange,
    startDateValue: startDateInputValue,
    onStartDate: handleStartDateChange,
    startTimeValue: startTimeInputValue,
    onStartTime: handleStartTimeChange,
    canStart: canStartQuickPlay,
    onCreate: () =>
      handleQuickPlay({
        mode: formData.mode,
        memberSize: normalizeStoredMemberSize("online", formData.memberSize, canUseExtendedPlayerOptions),
        botDifficulty: "injusto",
        havingTime: Number(formData.havingTime) || DEFAULT_TURN_TIME,
        localPlayers: [],
        isIndependentMatch: false,
        freeBoxRules: false,
        freeBoxBaseMode: undefined,
      }),
  };
  const pendingFriendRequests = Math.max(0, Number(socialAlert.incomingRequests) || 0);
  const pendingFriendInvites = Math.max(0, Number(socialAlert.incomingInvites) || 0);
  const pendingFriendTotal = pendingFriendRequests + pendingFriendInvites;
  // Product policy: en XIMA, admin y superusuario son equivalentes en permisos.
  const canEditNewsBoardMessages = hasDirectSuperuserAccess;
  const adminPasswordForNewsAccess = String(adminPassword || "").trim();
  const orderedNewsBoardMessages = useMemo(
    () =>
      [...newsBoardMessages].sort(
        (a, b) => new Date(b.createdAt || 0).valueOf() - new Date(a.createdAt || 0).valueOf()
      ),
    [newsBoardMessages]
  );
  const appendNewsBoardMessage = useCallback((rawPayload) => {
    const nextEntry = createNewsBoardEntry(rawPayload);
    if (!nextEntry) return;

    setNewsBoardMessages((prev) => {
      if (prev.some((entry) => entry.id === nextEntry.id)) return prev;
      const nextMessages = [...prev, nextEntry];
      if (nextMessages.length <= NEWS_BOARD_MAX_MESSAGES) return nextMessages;
      return nextMessages.slice(nextMessages.length - NEWS_BOARD_MAX_MESSAGES);
    });
  }, []);
  const replaceNewsBoardMessages = useCallback((messages) => {
    const source = Array.isArray(messages) ? messages : [];
    const normalized = source
      .map((entry) => createNewsBoardEntry(entry))
      .filter(Boolean)
      .slice(-NEWS_BOARD_MAX_MESSAGES);
    setNewsBoardMessages(normalized);
  }, []);
  const removeNewsBoardMessage = useCallback((rawPayload) => {
    const normalizedId = String(
      rawPayload?.id || rawPayload?.messageId || rawPayload || ""
    ).trim();
    if (!normalizedId) return;
    if (newsBoardEditingMessageId === normalizedId) {
      setNewsBoardEditingMessageId("");
      setNewsBoardEditingInput("");
    }
    setNewsBoardMessages((prev) =>
      prev.filter((entry) => String(entry?.id || "").trim() !== normalizedId)
    );
  }, [newsBoardEditingMessageId]);
  const updateNewsBoardMessage = useCallback((rawPayload) => {
    const updatedEntry = createNewsBoardEntry(rawPayload);
    if (!updatedEntry) return;
    const normalizedId = String(updatedEntry.id || "").trim();
    if (!normalizedId) return;

    if (newsBoardEditingMessageId === normalizedId) {
      setNewsBoardEditingMessageId("");
      setNewsBoardEditingInput("");
    }
    setNewsBoardMessages((prev) =>
      prev.map((entry) => {
        if (String(entry?.id || "").trim() !== normalizedId) return entry;
        return {
          ...entry,
          ...updatedEntry,
          id: normalizedId,
        };
      })
    );
  }, [newsBoardEditingMessageId]);
  const handleSaveNewsBoardMessage = useCallback(() => {
    if (!canEditNewsBoardMessages) return;
    const rawText = String(newsBoardInput || "");
    if (!rawText.trim()) return;
    const payload = {
      id: `news:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`,
      author: displayName || "Jugador",
      authorId: currentUserLoginNameNormalized || currentUserEmailNormalized || "guest",
      text: rawText,
      createdAt: new Date().toISOString(),
      adminPassword: adminPasswordForNewsAccess || undefined,
    };
    socket.emit(NEWS_BOARD_EVENT_NAME, payload);
    setNewsBoardInput("");
  }, [
    adminPasswordForNewsAccess,
    canEditNewsBoardMessages,
    currentUserEmailNormalized,
    currentUserLoginNameNormalized,
    displayName,
    newsBoardInput,
  ]);
  const handleDeleteNewsBoardMessage = useCallback((messageId) => {
    const normalizedId = String(messageId || "").trim();
    if (!normalizedId) return;
    if (newsBoardEditingMessageId === normalizedId) {
      setNewsBoardEditingMessageId("");
      setNewsBoardEditingInput("");
    }
    socket.emit(NEWS_BOARD_DELETE_EVENT_NAME, {
      id: normalizedId,
      adminPassword: adminPasswordForNewsAccess || undefined,
    });
  }, [adminPasswordForNewsAccess, newsBoardEditingMessageId]);
  const handleStartEditNewsBoardMessage = useCallback(
    (entry) => {
      if (!canEditNewsBoardMessages) return;
      const normalizedId = String(entry?.id || "").trim();
      const currentText = String(entry?.text || "");
      if (!normalizedId || !currentText.trim()) return;
      setNewsBoardEditingMessageId(normalizedId);
      setNewsBoardEditingInput(currentText);
    },
    [canEditNewsBoardMessages]
  );
  const handleCancelEditNewsBoardMessage = useCallback(() => {
    setNewsBoardEditingMessageId("");
    setNewsBoardEditingInput("");
  }, []);
  const handleUpdateNewsBoardMessage = useCallback(
    (messageId) => {
      if (!canEditNewsBoardMessages) return;
      const normalizedId = String(messageId || newsBoardEditingMessageId || "").trim();
      const nextText = String(newsBoardEditingInput || "").trim();
      if (!normalizedId || !nextText) return;
      socket.emit(NEWS_BOARD_UPDATE_EVENT_NAME, {
        id: normalizedId,
        text: nextText,
        adminPassword: adminPasswordForNewsAccess || undefined,
      });
    },
    [
      adminPasswordForNewsAccess,
      canEditNewsBoardMessages,
      newsBoardEditingInput,
      newsBoardEditingMessageId,
    ]
  );
  const canDeleteNewsBoardMessage = useCallback(
    () => canEditNewsBoardMessages,
    [canEditNewsBoardMessages]
  );
  const appendGlobalChatMessage = useCallback((rawPayload) => {
    const nextEntry = createGlobalChatEntry(rawPayload);
    if (!nextEntry) return;

    setGlobalChatMessages((prev) => {
      if (prev.some((entry) => entry.id === nextEntry.id)) return prev;
      const nextMessages = [...prev, nextEntry];
      if (nextMessages.length <= GLOBAL_CHAT_MAX_MESSAGES) return nextMessages;
      return nextMessages.slice(nextMessages.length - GLOBAL_CHAT_MAX_MESSAGES);
    });
  }, []);
  const replaceGlobalChatMessages = useCallback((messages) => {
    const source = Array.isArray(messages) ? messages : [];
    const normalized = source
      .map((entry) => createGlobalChatEntry(entry))
      .filter(Boolean)
      .slice(-GLOBAL_CHAT_MAX_MESSAGES);
    setGlobalChatMessages(normalized);
  }, []);
  const handleGlobalChatSend = useCallback(() => {
    const trimmed = String(globalChatInput || "").trim();
    if (!trimmed) return;

    const payload = {
      id: `local:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`,
      sender: displayName || "Jugador",
      senderId: currentUserLoginNameNormalized || currentUserEmailNormalized || "guest",
      text: trimmed.slice(0, GLOBAL_CHAT_MAX_LENGTH),
      createdAt: new Date().toISOString(),
    };

    socket.emit(GLOBAL_CHAT_EVENT_NAME, payload);
    setGlobalChatInput("");
  }, [
    currentUserEmailNormalized,
    currentUserLoginNameNormalized,
    displayName,
    globalChatInput,
  ]);
  const handleClearGlobalChat = useCallback(() => {
    if (!hasDirectAdminAccess) return;
    socket.emit(GLOBAL_CHAT_CLEAR_EVENT_NAME, {
      requestedAt: new Date().toISOString(),
    });
  }, [hasDirectAdminAccess]);

  useEffect(() => {
    const handleIncomingNewsBoardMessage = (payload) => appendNewsBoardMessage(payload);
    const handleNewsBoardHistory = (historyPayload) => replaceNewsBoardMessages(historyPayload);
    const handleNewsBoardDeleted = (payload) => removeNewsBoardMessage(payload);
    const handleNewsBoardUpdated = (payload) => updateNewsBoardMessage(payload);
    const handleNewsBoardRejected = (payload) => {
      const reason = String(payload?.reason || "").trim();
      const code = String(payload?.code || "")
        .trim()
        .toUpperCase();
      const description =
        code === "FORBIDDEN"
          ? "Solo administradores o superusuarios pueden publicar noticias."
          : reason || "La noticia contiene lenguaje no permitido y no fue publicada.";
      notification.warning({
        message: i18nT("lobby.mensajeBloqueado"),
        description,
      });
    };
    const handleNewsBoardDeleteRejected = () => {
      notification.warning({
        message: i18nT("lobby.noAutorizado"),
        description: i18nT("lobby.soloAdministradoresOSuperusuariosPuedenElimi722c0f"),
      });
    };
    const handleNewsBoardUpdateRejected = (payload) => {
      const reason = String(payload?.reason || "").trim();
      const code = String(payload?.code || "")
        .trim()
        .toUpperCase();
      const description =
        code === "FORBIDDEN"
          ? "Solo administradores o superusuarios pueden editar noticias."
          : reason || "No se pudo editar la noticia en este momento.";
      notification.warning({
        message: i18nT("lobby.edicionBloqueada"),
        description,
      });
    };

    socket.on(NEWS_BOARD_EVENT_NAME, handleIncomingNewsBoardMessage);
    socket.on(NEWS_BOARD_HISTORY_EVENT_NAME, handleNewsBoardHistory);
    socket.on(NEWS_BOARD_DELETE_EVENT_NAME, handleNewsBoardDeleted);
    socket.on(NEWS_BOARD_UPDATE_EVENT_NAME, handleNewsBoardUpdated);
    socket.on(NEWS_BOARD_REJECTED_EVENT_NAME, handleNewsBoardRejected);
    socket.on(NEWS_BOARD_DELETE_REJECTED_EVENT_NAME, handleNewsBoardDeleteRejected);
    socket.on(NEWS_BOARD_UPDATE_REJECTED_EVENT_NAME, handleNewsBoardUpdateRejected);
    socket.emit(NEWS_BOARD_HISTORY_REQUEST_EVENT_NAME);
    return () => {
      socket.off(NEWS_BOARD_EVENT_NAME, handleIncomingNewsBoardMessage);
      socket.off(NEWS_BOARD_HISTORY_EVENT_NAME, handleNewsBoardHistory);
      socket.off(NEWS_BOARD_DELETE_EVENT_NAME, handleNewsBoardDeleted);
      socket.off(NEWS_BOARD_UPDATE_EVENT_NAME, handleNewsBoardUpdated);
      socket.off(NEWS_BOARD_REJECTED_EVENT_NAME, handleNewsBoardRejected);
      socket.off(NEWS_BOARD_DELETE_REJECTED_EVENT_NAME, handleNewsBoardDeleteRejected);
      socket.off(NEWS_BOARD_UPDATE_REJECTED_EVENT_NAME, handleNewsBoardUpdateRejected);
    };
  }, [
    appendNewsBoardMessage,
    removeNewsBoardMessage,
    replaceNewsBoardMessages,
    updateNewsBoardMessage,
  ]);

  useEffect(() => {
    const handleIncomingGlobalChat = (payload) => appendGlobalChatMessage(payload);
    const handleGlobalChatHistory = (historyPayload) => replaceGlobalChatMessages(historyPayload);
    const handleGlobalChatRejected = () => {
      notification.warning({
        message: i18nT("lobby.mensajeBloqueado"),
        description: i18nT("lobby.tuMensajeContieneLenguajeNoPermitidoYNo"),
      });
    };
    const handleGlobalChatCleared = () => {
      setGlobalChatMessages([]);
      notification.success({
        message: i18nT("lobby.chatGlobalLimpiado"),
        description: i18nT("lobby.losMensajesFueronEliminadosPorUnAdministradocbb4bd"),
      });
    };
    const handleGlobalChatClearRejected = (payload) => {
      const reason = String(payload?.reason || "").trim();
      notification.warning({
        message: i18nT("lobby.noAutorizado"),
        description:
          reason || "Solo administradores o superusuarios pueden limpiar el chat global.",
      });
    };
    socket.on(GLOBAL_CHAT_EVENT_NAME, handleIncomingGlobalChat);
    socket.on(GLOBAL_CHAT_HISTORY_EVENT_NAME, handleGlobalChatHistory);
    socket.on("GLOBAL_CHAT_REJECTED", handleGlobalChatRejected);
    socket.on(GLOBAL_CHAT_CLEARED_EVENT_NAME, handleGlobalChatCleared);
    socket.on(GLOBAL_CHAT_CLEAR_REJECTED_EVENT_NAME, handleGlobalChatClearRejected);
    socket.emit(GLOBAL_CHAT_HISTORY_REQUEST_EVENT_NAME);
    return () => {
      socket.off(GLOBAL_CHAT_EVENT_NAME, handleIncomingGlobalChat);
      socket.off(GLOBAL_CHAT_HISTORY_EVENT_NAME, handleGlobalChatHistory);
      socket.off("GLOBAL_CHAT_REJECTED", handleGlobalChatRejected);
      socket.off(GLOBAL_CHAT_CLEARED_EVENT_NAME, handleGlobalChatCleared);
      socket.off(GLOBAL_CHAT_CLEAR_REJECTED_EVENT_NAME, handleGlobalChatClearRejected);
    };
  }, [appendGlobalChatMessage, replaceGlobalChatMessages]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(NEWS_BOARD_STORAGE_KEY, JSON.stringify(newsBoardMessages));
    } catch (error) {
      console.log(error);
    }
  }, [newsBoardMessages]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      sessionStorage.setItem(GLOBAL_CHAT_SESSION_KEY, JSON.stringify(globalChatMessages));
    } catch (error) {
      console.log(error);
    }
  }, [globalChatMessages]);

  useEffect(() => {
    const scrollContainer = globalChatScrollRef.current;
    if (!scrollContainer) return;
    scrollContainer.scrollTop = scrollContainer.scrollHeight;
  }, [globalChatMessages]);

  useEffect(() => {
    const scrollContainer = globalChatFloatingScrollRef.current;
    if (!scrollContainer) return;
    scrollContainer.scrollTop = scrollContainer.scrollHeight;
  }, [globalChatMessages, chatFloatingOpen]);

  useEffect(() => {
    if (!chatFloatingOpen || typeof window === "undefined") return;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const safeMaxWidth = Math.max(
      CHAT_FLOATING_MIN_WIDTH,
      viewportWidth - 16
    );
    const safeMaxHeight = Math.max(
      CHAT_FLOATING_MIN_HEIGHT,
      viewportHeight - 16
    );

    if (chatFloatingMaximized) {
      const width = clampValue(
        Math.min(CHAT_FLOATING_MAXIMIZED_MAX_WIDTH, Math.round(viewportWidth * 0.92)),
        CHAT_FLOATING_MIN_WIDTH,
        safeMaxWidth
      );
      const height = clampValue(
        Math.min(CHAT_FLOATING_MAXIMIZED_MAX_HEIGHT, Math.round(viewportHeight * 0.78)),
        CHAT_FLOATING_MIN_HEIGHT,
        safeMaxHeight
      );
      setChatFloatingSize({ width, height });
      setChatFloatingPosition({
        x: Math.max(0, Math.round((viewportWidth - width) / 2)),
        y: clampValue(24, 0, Math.max(0, viewportHeight - height)),
      });
      return;
    }

    const width = clampValue(CHAT_FLOATING_DEFAULT_WIDTH, CHAT_FLOATING_MIN_WIDTH, safeMaxWidth);
    const height = clampValue(
      CHAT_FLOATING_DEFAULT_HEIGHT,
      CHAT_FLOATING_MIN_HEIGHT,
      safeMaxHeight
    );
    setChatFloatingSize({ width, height });
    setChatFloatingPosition({
      x: Math.max(0, viewportWidth - width - 16),
      y: Math.max(0, viewportHeight - height - 16),
    });
  }, [chatFloatingMaximized, chatFloatingOpen]);

  const chatFloatingFontScale = useMemo(() => {
    const widthRatio = Number(chatFloatingSize?.width || CHAT_FLOATING_DEFAULT_WIDTH) / CHAT_FLOATING_DEFAULT_WIDTH;
    const heightRatio =
      Number(chatFloatingSize?.height || CHAT_FLOATING_DEFAULT_HEIGHT) /
      CHAT_FLOATING_DEFAULT_HEIGHT;
    return clampValue(Math.min(widthRatio, heightRatio), 1.05, 2.4);
  }, [chatFloatingSize?.height, chatFloatingSize?.width]);
  const chatFloatingTextScale = useMemo(
    () => chatFloatingFontScale * (chatFloatingMaximized ? 1.28 : 1.08),
    [chatFloatingFontScale, chatFloatingMaximized]
  );

  const handleFloatingChatDragStart = useCallback(
    (event) => {
      if (!chatFloatingOpen || !chatFloatingMaximized) return;
      if (event.button !== 0 || typeof window === "undefined") return;
      const targetElement = event.target instanceof Element ? event.target : null;
      if (targetElement?.closest("button,input,textarea")) return;

      event.preventDefault();

      chatFloatingDragRef.current = {
        startMouseX: event.clientX,
        startMouseY: event.clientY,
        startX: chatFloatingPosition.x,
        startY: chatFloatingPosition.y,
      };

      const handleMouseMove = (moveEvent) => {
        const dragData = chatFloatingDragRef.current;
        if (!dragData) return;
        const dx = moveEvent.clientX - dragData.startMouseX;
        const dy = moveEvent.clientY - dragData.startMouseY;
        const maxX = Math.max(0, window.innerWidth - chatFloatingSize.width);
        const maxY = Math.max(0, window.innerHeight - chatFloatingSize.height);
        setChatFloatingPosition({
          x: clampValue(dragData.startX + dx, 0, maxX),
          y: clampValue(dragData.startY + dy, 0, maxY),
        });
      };

      const handleMouseUp = () => {
        chatFloatingDragRef.current = null;
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [chatFloatingMaximized, chatFloatingOpen, chatFloatingPosition.x, chatFloatingPosition.y, chatFloatingSize.height, chatFloatingSize.width]
  );

  const handleFloatingChatResizeStart = useCallback(
    (corner, event) => {
      if (!chatFloatingOpen || typeof window === "undefined") return;
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();

      chatFloatingResizeRef.current = {
        corner,
        startMouseX: event.clientX,
        startMouseY: event.clientY,
        startX: chatFloatingPosition.x,
        startY: chatFloatingPosition.y,
        startWidth: chatFloatingSize.width,
        startHeight: chatFloatingSize.height,
      };

      const handleMouseMove = (moveEvent) => {
        const resizeData = chatFloatingResizeRef.current;
        if (!resizeData) return;
        const dx = moveEvent.clientX - resizeData.startMouseX;
        const dy = moveEvent.clientY - resizeData.startMouseY;

        let nextX = resizeData.startX;
        let nextY = resizeData.startY;
        let nextWidth = resizeData.startWidth;
        let nextHeight = resizeData.startHeight;

        if (corner.includes("e")) nextWidth = resizeData.startWidth + dx;
        if (corner.includes("s")) nextHeight = resizeData.startHeight + dy;
        if (corner.includes("w")) {
          nextWidth = resizeData.startWidth - dx;
          nextX = resizeData.startX + dx;
        }
        if (corner.includes("n")) {
          nextHeight = resizeData.startHeight - dy;
          nextY = resizeData.startY + dy;
        }

        const maxWidth = Math.max(CHAT_FLOATING_MIN_WIDTH, window.innerWidth - 8);
        const maxHeight = Math.max(CHAT_FLOATING_MIN_HEIGHT, window.innerHeight - 8);
        const width = clampValue(nextWidth, CHAT_FLOATING_MIN_WIDTH, maxWidth);
        const height = clampValue(nextHeight, CHAT_FLOATING_MIN_HEIGHT, maxHeight);
        const x = clampValue(nextX, 0, Math.max(0, window.innerWidth - width));
        const y = clampValue(nextY, 0, Math.max(0, window.innerHeight - height));

        setChatFloatingSize({ width, height });
        setChatFloatingPosition({ x, y });
      };

      const handleMouseUp = () => {
        chatFloatingResizeRef.current = null;
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [chatFloatingOpen, chatFloatingPosition.x, chatFloatingPosition.y, chatFloatingSize.height, chatFloatingSize.width]
  );

  useEffect(() => {
    if (!replayModalOpen || typeof window === "undefined") return;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    if (isReplayMobileViewport) {
      setReplayPanelSize({
        width: Math.max(280, Math.floor(viewportWidth)),
        height: Math.max(420, Math.floor(viewportHeight)),
      });
      setReplayPanelPosition({ x: 0, y: 0 });
      return;
    }

    const maxWidth = Math.max(REPLAY_PANEL_MIN_WIDTH, viewportWidth - 12);
    const maxHeight = Math.max(REPLAY_PANEL_MIN_HEIGHT, viewportHeight - 12);

    const expandedWidthTarget = Math.floor(viewportWidth - 24);
    let savedPanelSize = null;
    try {
      savedPanelSize = JSON.parse(window.localStorage.getItem(REPLAY_PANEL_SIZE_STORAGE_KEY) || "null");
    } catch (error) {
      savedPanelSize = null;
    }
    const savedWidth = Number(savedPanelSize?.width) || 0;
    const savedHeight = Number(savedPanelSize?.height) || 0;
    const width = clampValue(
      replayExpanded ? expandedWidthTarget : savedWidth || viewportWidth - 24,
      REPLAY_PANEL_MIN_WIDTH,
      maxWidth
    );
    const height = clampValue(
      replayExpanded ? viewportHeight - 24 : savedHeight || viewportHeight - 24,
      REPLAY_PANEL_MIN_HEIGHT,
      maxHeight
    );

    setReplayPanelSize({ width, height });
    setReplayPanelPosition({
      x: clampValue(Math.round((viewportWidth - width) / 2), 0, Math.max(0, viewportWidth - width)),
      y: clampValue(12, 0, Math.max(0, viewportHeight - height)),
    });
  }, [
    isReplayLandscapePhoneViewport,
    isReplayMobileViewport,
    isReplayPortraitPhoneViewport,
    replayExpanded,
    replayModalOpen,
  ]);

  useEffect(() => {
    setReplayManualWidth(String(Math.round(Number(replayPanelSize?.width) || 0)));
    setReplayManualHeight(String(Math.round(Number(replayPanelSize?.height) || 0)));
  }, [replayPanelSize.height, replayPanelSize.width]);

  const applyReplayManualSize = useCallback(() => {
    if (isReplayMobileViewport) return;
    if (typeof window === "undefined") return;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const maxWidth = Math.max(REPLAY_PANEL_MIN_WIDTH, viewportWidth - 12);
    const maxHeight = Math.max(REPLAY_PANEL_MIN_HEIGHT, viewportHeight - 12);
    const width = clampValue(
      Number(replayManualWidth),
      REPLAY_PANEL_MIN_WIDTH,
      maxWidth
    );
    const height = clampValue(
      Number(replayManualHeight),
      REPLAY_PANEL_MIN_HEIGHT,
      maxHeight
    );

    setReplayExpanded(false);
    setReplayPanelSize({ width, height });
    try {
      window.localStorage.setItem(REPLAY_PANEL_SIZE_STORAGE_KEY, JSON.stringify({ width, height }));
    } catch (error) {
      // Guardar el tamaño es opcional.
    }
    setReplayPanelPosition((prev) => ({
      x: clampValue(Number(prev?.x) || 0, 0, Math.max(0, viewportWidth - width)),
      y: clampValue(Number(prev?.y) || 0, 0, Math.max(0, viewportHeight - height)),
    }));
  }, [isReplayMobileViewport, replayManualHeight, replayManualWidth]);

  const handleReplayPanelDragStart = useCallback(
    (event) => {
      if (isReplayMobileViewport) return;
      if (!replayModalOpen || typeof window === "undefined") return;
      if (event.button !== 0) return;
      const targetElement = event.target instanceof Element ? event.target : null;
      if (targetElement?.closest("button,input,textarea")) return;
      event.preventDefault();

      setReplayPanelInteracting(true);
      replayPanelDragRef.current = {
        startMouseX: event.clientX,
        startMouseY: event.clientY,
        startX: replayPanelPosition.x,
        startY: replayPanelPosition.y,
      };

      const handleMouseMove = (moveEvent) => {
        const dragData = replayPanelDragRef.current;
        if (!dragData) return;
        const dx = moveEvent.clientX - dragData.startMouseX;
        const dy = moveEvent.clientY - dragData.startMouseY;
        const maxX = Math.max(0, window.innerWidth - replayPanelSize.width);
        const maxY = Math.max(0, window.innerHeight - replayPanelSize.height);
        setReplayPanelPosition({
          x: clampValue(dragData.startX + dx, 0, maxX),
          y: clampValue(dragData.startY + dy, 0, maxY),
        });
      };

      const handleMouseUp = () => {
        replayPanelDragRef.current = null;
        setReplayPanelInteracting(false);
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [
      isReplayMobileViewport,
      replayModalOpen,
      replayPanelPosition.x,
      replayPanelPosition.y,
      replayPanelSize.height,
      replayPanelSize.width,
    ]
  );

  const handleReplayPanelResizeStart = useCallback(
    (corner, event) => {
      if (isReplayMobileViewport) return;
      if (!replayModalOpen || typeof window === "undefined") return;
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();

      setReplayPanelInteracting(true);
      replayPanelResizeRef.current = {
        corner,
        startMouseX: event.clientX,
        startMouseY: event.clientY,
        startX: replayPanelPosition.x,
        startY: replayPanelPosition.y,
        startWidth: replayPanelSize.width,
        startHeight: replayPanelSize.height,
      };

      const handleMouseMove = (moveEvent) => {
        const resizeData = replayPanelResizeRef.current;
        if (!resizeData) return;
        const dx = moveEvent.clientX - resizeData.startMouseX;
        const dy = moveEvent.clientY - resizeData.startMouseY;

        let nextX = resizeData.startX;
        let nextY = resizeData.startY;
        let nextWidth = resizeData.startWidth;
        let nextHeight = resizeData.startHeight;

        if (corner.includes("e")) nextWidth = resizeData.startWidth + dx;
        if (corner.includes("s")) nextHeight = resizeData.startHeight + dy;
        if (corner.includes("w")) {
          nextWidth = resizeData.startWidth - dx;
          nextX = resizeData.startX + dx;
        }
        if (corner.includes("n")) {
          nextHeight = resizeData.startHeight - dy;
          nextY = resizeData.startY + dy;
        }

        const maxWidth = Math.max(REPLAY_PANEL_MIN_WIDTH, window.innerWidth - 8);
        const maxHeight = Math.max(REPLAY_PANEL_MIN_HEIGHT, window.innerHeight - 8);
        const width = clampValue(nextWidth, REPLAY_PANEL_MIN_WIDTH, maxWidth);
        const height = clampValue(nextHeight, REPLAY_PANEL_MIN_HEIGHT, maxHeight);
        const x = clampValue(nextX, 0, Math.max(0, window.innerWidth - width));
        const y = clampValue(nextY, 0, Math.max(0, window.innerHeight - height));

        setReplayPanelSize({ width, height });
        setReplayPanelPosition({ x, y });
        resizeData.lastSize = { width, height };
      };

      const handleMouseUp = () => {
        const finalSize = replayPanelResizeRef.current?.lastSize;
        replayPanelResizeRef.current = null;
        setReplayPanelInteracting(false);
        if (finalSize) {
          try {
            window.localStorage.setItem(REPLAY_PANEL_SIZE_STORAGE_KEY, JSON.stringify(finalSize));
          } catch (error) {
            // Guardar el tamaño es opcional.
          }
        }
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [
      isReplayMobileViewport,
      replayModalOpen,
      replayPanelPosition.x,
      replayPanelPosition.y,
      replayPanelSize.height,
      replayPanelSize.width,
    ]
  );

  const getRightPanelResizeBounds = useCallback(() => {
    const viewportWidth =
      typeof window !== "undefined" ? Math.max(700, Number(window.innerWidth) || 0) : 1280;
    const useDesktopBoundsForPhoneLandscape = isPhoneLandscapeDesktopViewport();
    if (!useDesktopBoundsForPhoneLandscape && isIphoneLandscapeViewport()) {
      const fixedWidth = DEFAULT_IPHONE_LANDSCAPE_RIGHT_PANEL_WIDTH;
      return { minWidth: fixedWidth, maxWidth: fixedWidth };
    }
    if (!useDesktopBoundsForPhoneLandscape && isIphoneSamsungLandscapeViewport()) {
      const fixedWidth = DEFAULT_MOBILE_LANDSCAPE_PANEL_WIDTH;
      return { minWidth: fixedWidth, maxWidth: fixedWidth };
    }
    const isLandscapePhone = mediaQueryMatches(LANDSCAPE_PHONE_LAYOUT_QUERY);
    if (!useDesktopBoundsForPhoneLandscape && isLandscapePhone) {
      const minWidth = 140;
      const maxWidth = Math.max(minWidth + 20, Math.min(340, Math.floor(viewportWidth * 0.42)));
      return { minWidth, maxWidth };
    }
    const minWidth = 220;
    const maxWidth = Math.max(minWidth + 20, Math.min(540, Math.floor(viewportWidth * 0.5)));
    return { minWidth, maxWidth };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const layoutQuery = window.matchMedia(CENTER_RIGHT_RESIZE_LAYOUT_QUERY);
    const landscapeQuery = window.matchMedia(LANDSCAPE_PHONE_LAYOUT_QUERY);
    const handleLayoutQueryChange = () => {
      const isResizable = Boolean(layoutQuery.matches);
      const useDesktopLayoutForPhoneLandscapeNow = isPhoneLandscapeDesktopViewport();
      const isIphoneChromeLandscapeNow = isIphoneChromeLandscapeViewport();
      const isSamsungChromeLandscapeNow = isSamsungChromeLandscapeViewport();
      const isLandscapePhoneNow =
        !useDesktopLayoutForPhoneLandscapeNow &&
        (Boolean(landscapeQuery.matches) ||
          isIphoneChromeLandscapeNow ||
          isSamsungChromeLandscapeNow ||
          isPhoneLandscapeByViewport());
      const isIphoneSamsungLandscapeNow =
        isLandscapePhoneNow && isIphoneOrSamsungDevice();
      setIsPhoneLandscapeViewport(isLandscapePhoneNow);
      setIsIphoneChromeLandscapePhone(
        useDesktopLayoutForPhoneLandscapeNow ? false : isIphoneChromeLandscapeNow
      );
      setIsSamsungChromeLandscapePhone(
        useDesktopLayoutForPhoneLandscapeNow ? false : isSamsungChromeLandscapeNow
      );
      setIsIphoneSamsungLandscape(isIphoneSamsungLandscapeNow);
      setIsRightPanelCollapsed(false);
      setCenterRightResizableLayout(useDesktopLayoutForPhoneLandscapeNow ? true : isResizable);
      setRightPanelWidth((previousWidth) => {
        const fallbackWidth = isLandscapePhoneNow
          ? DEFAULT_LANDSCAPE_RIGHT_PANEL_WIDTH
          : DEFAULT_RIGHT_PANEL_WIDTH;
        const { minWidth, maxWidth } = getRightPanelResizeBounds();
        if (!useDesktopLayoutForPhoneLandscapeNow && isIphoneSamsungLandscapeNow) {
          return Math.round(minWidth);
        }
        const normalized = Number.isFinite(previousWidth)
          ? Number(previousWidth)
          : fallbackWidth;
        return Math.round(clampValue(normalized, minWidth, maxWidth));
      });
    };

    handleLayoutQueryChange();

    if (
      typeof layoutQuery.addEventListener === "function" &&
      typeof landscapeQuery.addEventListener === "function"
    ) {
      layoutQuery.addEventListener("change", handleLayoutQueryChange);
      landscapeQuery.addEventListener("change", handleLayoutQueryChange);
      window.addEventListener("resize", handleLayoutQueryChange);
      return () => {
        layoutQuery.removeEventListener("change", handleLayoutQueryChange);
        landscapeQuery.removeEventListener("change", handleLayoutQueryChange);
        window.removeEventListener("resize", handleLayoutQueryChange);
      };
    }

    layoutQuery.addListener(handleLayoutQueryChange);
    landscapeQuery.addListener(handleLayoutQueryChange);
    window.addEventListener("resize", handleLayoutQueryChange);
    return () => {
      layoutQuery.removeListener(handleLayoutQueryChange);
      landscapeQuery.removeListener(handleLayoutQueryChange);
      window.removeEventListener("resize", handleLayoutQueryChange);
    };
  }, [getRightPanelResizeBounds]);

  const handleCenterRightResizeStart = useCallback(
    (event) => {
      if (
        !centerRightResizableLayout ||
        isIphoneSamsungLandscape ||
        typeof window === "undefined"
      ) {
        return;
      }
      event.preventDefault();
      const startMouseX = Number(event.clientX) || 0;
      const startWidth = Number(rightPanelWidth) || getDefaultRightPanelWidth();
      const { minWidth, maxWidth } = getRightPanelResizeBounds();

      centerRightResizeRef.current = {
        startMouseX,
        startWidth,
        minWidth,
        maxWidth,
      };

      const handlePointerMove = (moveEvent) => {
        const resizeData = centerRightResizeRef.current;
        if (!resizeData) return;
        const deltaX = resizeData.startMouseX - Number(moveEvent.clientX || 0);
        const nextWidth = clampValue(
          resizeData.startWidth + deltaX,
          resizeData.minWidth,
          resizeData.maxWidth
        );
        setRightPanelWidth(Math.round(nextWidth));
      };
      const handlePointerUp = () => {
        centerRightResizeRef.current = null;
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
      };

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
    },
    [centerRightResizableLayout, getRightPanelResizeBounds, isIphoneSamsungLandscape, rightPanelWidth]
  );

  const centerRightGridStyle = useMemo(() => {
    if (isHistoryTab) {
      return {
        gridTemplateColumns: "minmax(0,1fr)",
      };
    }

    if (isLobbyFocusedModeRoute) {
      if (isIphoneChromeLandscapePhone) {
        return {
          gridTemplateColumns: "minmax(0,1fr)",
          gridTemplateRows: "auto auto",
          rowGap: "0.75rem",
          height: "100%",
          minHeight: "0",
          alignContent: "start",
        };
      }
      return {
        gridTemplateColumns: "minmax(0,1fr)",
      };
    }

    if (isIphoneChromeLandscapePhone) {
      return {
        gridTemplateColumns: "minmax(0,1fr)",
        gridTemplateRows: "auto auto",
        rowGap: "0.75rem",
        height: "100%",
        minHeight: "0",
        alignContent: "start",
      };
    }
    if (isSamsungChromeLandscapePhone || isPhoneLandscapeViewport) {
      return {
        gridTemplateColumns: "66.6667% 0px 33.3333%",
        columnGap: "0px",
      };
    }
    if (!centerRightResizableLayout) return undefined;
    if (isIphoneSamsungLandscape) {
      return {
        gridTemplateColumns: "minmax(0,2fr) 6px minmax(0,1fr)",
      };
    }
    const minAllowedWidth = isIphoneSamsungLandscape ? DEFAULT_MOBILE_LANDSCAPE_PANEL_WIDTH : 120;
    const normalizedWidth = Math.max(minAllowedWidth, Math.round(Number(rightPanelWidth) || 0));
    const effectiveWidth =
      isIphoneSamsungLandscape && isRightPanelCollapsed
        ? MOBILE_LANDSCAPE_RIGHT_COLLAPSED_WIDTH
        : normalizedWidth;
    return {
      gridTemplateColumns: `minmax(0,1fr) 6px minmax(0, ${effectiveWidth}px)`,
    };
  }, [
    isHistoryTab,
    isLobbyFocusedModeRoute,
    centerRightResizableLayout,
    isIphoneChromeLandscapePhone,
    isSamsungChromeLandscapePhone,
    isIphoneSamsungLandscape,
    isPhoneLandscapeViewport,
    isRightPanelCollapsed,
    rightPanelWidth,
  ]);
  const portraitTopScrollSpaceClass =
    isSamsungPortraitMobile || isIphonePortraitMobile
      ? "[@media(max-width:1024px)_and_(orientation:portrait)]:pt-[calc(env(safe-area-inset-top)+36px)]"
      : "";
  const portraitBottomScrollSpaceClass = isSamsungPortraitMobile
    ? "[@media(max-width:1024px)_and_(orientation:portrait)]:pb-[calc(env(safe-area-inset-bottom)+840px)]"
    : isIphonePortraitMobile
      ? "[@media(max-width:1024px)_and_(orientation:portrait)]:pb-[calc(env(safe-area-inset-bottom)+200px)]"
      : "[@media(max-width:1024px)_and_(orientation:portrait)]:pb-[calc(env(safe-area-inset-bottom)+120px)]";
  const effectivePuzzleDashboardModeFilter = useMemo(() => {
    if (puzzleModeFilter) return resolvePuzzleColumnMode(puzzleModeFilter);
    return puzzleDashboardModeFilter === "all"
      ? "all"
      : resolvePuzzleColumnMode(puzzleDashboardModeFilter);
  }, [puzzleDashboardModeFilter, puzzleModeFilter]);
  const puzzleDashboardEntries = useMemo(() => {
    const modeCounters = {};
    return toOrderedPuzzleEntries(puzzles).map((puzzle, index) => {
      const puzzleId = getPuzzleEntryId(puzzle);
      const modeMeta = resolvePuzzleDashboardModeMeta(puzzle?.freeBoxBaseMode || "free", language);
      const modeOrder = Math.max(1, Number(modeCounters[modeMeta.mode] || 0) + 1);
      modeCounters[modeMeta.mode] = modeOrder;
      const puzzleSolvedByCurrentUser = Boolean(
        solvedPuzzleIdsSet.has(puzzleId) ||
          puzzle?.solvedByCurrentUser ||
          puzzle?.isSolvedByCurrentUser
      );
      const puzzleIsAzarMode = modeMeta.mode === "dice";
      const puzzleRewardFailedByCurrentUser = Boolean(
        puzzleIsAzarMode
          ? false
          : puzzle?.rewardFailedByCurrentUser || puzzle?.isRewardFailedByCurrentUser
      );
      const puzzleRewardAlreadyClaimed = Boolean(
        claimedPuzzleRewardMap[puzzleId] === true ||
          puzzle?.rewardClaimedByCurrentUser ||
          puzzle?.isRewardClaimedByCurrentUser
      );
      const puzzleRewardValue = resolvePuzzleRewardValue(puzzle);
      const moveCount = resolvePuzzleDashboardMoveCount(puzzle);
      const turnLimit = normalizePuzzleTurnLimit(
        puzzle?.puzzleTurnLimit,
        Math.max(DEFAULT_PUZZLE_TURN_LIMIT, moveCount || 0)
      );
      const solveTimeLimitSeconds = resolvePuzzleSolveTimeLimitSecondsFromRecord(puzzle);
      const fallbackTimeSeconds = Math.max(1, Number(puzzle?.havingTime) || 3) * 60;
      const targetTimeSeconds = solveTimeLimitSeconds > 0 ? solveTimeLimitSeconds : fallbackTimeSeconds;
      const attemptCount = resolvePuzzleDashboardAttemptCount(puzzle);
      const createdAtMs = new Date(puzzle?.createdAt || 0).valueOf();
      const title = localizePuzzleDashboardTitle(
        language,
        puzzle?.tournamentName,
        turnLimit,
        solveTimeLimitSeconds
      );
      const status = puzzleRewardFailedByCurrentUser
        ? "failed"
        : puzzleSolvedByCurrentUser
        ? "success"
        : "pending";
      return {
        puzzle,
        puzzleId,
        index,
        mode: modeMeta.mode,
        modeLabel: modeMeta.label,
        modeOrder,
        modeMeta,
        title,
        status,
        solved: puzzleSolvedByCurrentUser,
        failed: puzzleRewardFailedByCurrentUser,
        claimed: puzzleRewardAlreadyClaimed,
        rewardValue: puzzleRewardValue,
        rewardCanClaim:
          puzzleSolvedByCurrentUser &&
          !puzzleRewardFailedByCurrentUser &&
          !puzzleRewardAlreadyClaimed &&
          puzzleRewardValue > 0,
        moveCount,
        turnLimit,
        movementLabel: `${moveCount} / ${Math.max(moveCount, turnLimit || 0)}`,
        attemptCount,
        targetTimeSeconds,
        timeLabel: formatPuzzleDashboardTime(targetTimeSeconds),
        bestTimeLabel: formatPuzzleDashboardTime(
          Math.max(1, Math.round(targetTimeSeconds * (puzzleSolvedByCurrentUser ? 0.72 : 1)))
        ),
        createdAtMs: Number.isFinite(createdAtMs) ? createdAtMs : 0,
        searchText: `${title} ${puzzleId} ${modeMeta.label} #${modeOrder}`.toLowerCase(),
      };
    });
  }, [claimedPuzzleRewardMap, language, puzzles, solvedPuzzleIdsSet]);
  const puzzleDashboardStats = useMemo(() => {
    const entries = puzzleDashboardEntries;
    const total = entries.length;
    const solved = entries.filter((entry) => entry.solved).length;
    const failed = entries.filter((entry) => entry.failed).length;
    const totalAttempts = entries.reduce((sum, entry) => sum + entry.attemptCount, 0);
    const totalMoves = entries.reduce((sum, entry) => sum + entry.moveCount, 0);
    const movementSamples = entries.filter((entry) => entry.moveCount > 0).map((entry) => entry.moveCount);
    const totalSeconds = entries.reduce((sum, entry) => sum + entry.targetTimeSeconds, 0);
    const totalRewards = entries.reduce((sum, entry) => sum + entry.rewardValue, 0);
    const maxModeReward = Math.max(
      1,
      ...PUZZLE_MODE_COLUMNS.map((column) =>
        entries
          .filter((entry) => entry.mode === column.mode)
          .reduce((sum, entry) => sum + entry.rewardValue, 0)
      )
    );
    const maxModeMoves = Math.max(
      1,
      ...PUZZLE_MODE_COLUMNS.map((column) => {
        const modeEntries = entries.filter((entry) => entry.mode === column.mode);
        if (!modeEntries.length) return 0;
        return modeEntries.reduce((sum, entry) => sum + entry.moveCount, 0) / modeEntries.length;
      })
    );
    const maxModeTime = Math.max(
      1,
      ...PUZZLE_MODE_COLUMNS.map((column) => {
        const modeEntries = entries.filter((entry) => entry.mode === column.mode);
        if (!modeEntries.length) return 0;
        return modeEntries.reduce((sum, entry) => sum + entry.targetTimeSeconds, 0) / modeEntries.length;
      })
    );
    const modeStats = PUZZLE_MODE_COLUMNS.map((column) => {
      const modeEntries = entries.filter((entry) => entry.mode === column.mode);
      const modeSolved = modeEntries.filter((entry) => entry.solved).length;
      const modeRewards = modeEntries.reduce((sum, entry) => sum + entry.rewardValue, 0);
      const modeAverageMoves = modeEntries.length
        ? modeEntries.reduce((sum, entry) => sum + entry.moveCount, 0) / modeEntries.length
        : 0;
      const modeAverageTime = modeEntries.length
        ? modeEntries.reduce((sum, entry) => sum + entry.targetTimeSeconds, 0) / modeEntries.length
        : 0;
      const meta = resolvePuzzleDashboardModeMeta(column.mode, language);
      return {
        ...column,
        title: meta.label,
        meta,
        count: modeEntries.length,
        solved: modeSolved,
        failed: modeEntries.filter((entry) => entry.failed).length,
        successRate: modeEntries.length ? (modeSolved / modeEntries.length) * 100 : 0,
        rewards: modeRewards,
        rewardShare: Math.min(100, (modeRewards / maxModeReward) * 100),
        averageMoves: modeAverageMoves,
        averageTime: modeAverageTime,
        movesShare: Math.min(100, (modeAverageMoves / maxModeMoves) * 100),
        timeShare: Math.min(100, (modeAverageTime / maxModeTime) * 100),
      };
    });

    return {
      total,
      solved,
      failed,
      successRate: total ? (solved / total) * 100 : 0,
      failedRate: total ? (failed / total) * 100 : 0,
      totalAttempts,
      averageAttempts: total ? totalAttempts / total : 0,
      averageMoves: total ? totalMoves / total : 0,
      bestMoves: movementSamples.length ? Math.min(...movementSamples) : 0,
      worstMoves: movementSamples.length ? Math.max(...movementSamples) : 0,
      averageTimeSeconds: total ? totalSeconds / total : 0,
      bestTimeSeconds: entries.length ? Math.min(...entries.map((entry) => entry.targetTimeSeconds)) : 0,
      worstTimeSeconds: entries.length ? Math.max(...entries.map((entry) => entry.targetTimeSeconds)) : 0,
      totalRewards,
      modeStats,
    };
  }, [language, puzzleDashboardEntries]);
  const puzzleDashboardColumns = useMemo(() => {
    const searchTerm = String(puzzleDashboardSearch || "").trim().toLowerCase();
    const visibleModes =
      effectivePuzzleDashboardModeFilter === "all"
        ? PUZZLE_MODE_COLUMNS.map((column) => column.mode)
        : [effectivePuzzleDashboardModeFilter];
    return PUZZLE_MODE_COLUMNS.filter((column) => visibleModes.includes(column.mode)).map((column) => {
      const columnEntries = puzzleDashboardEntries
        .filter((entry) => entry.mode === column.mode)
        .filter((entry) => !searchTerm || entry.searchText.includes(searchTerm))
        .sort((a, b) => {
          if (puzzleDashboardSort === "oldest") return a.createdAtMs - b.createdAtMs;
          if (puzzleDashboardSort === "reward") return b.rewardValue - a.rewardValue;
          if (puzzleDashboardSort === "moves") return a.moveCount - b.moveCount;
          return b.createdAtMs - a.createdAtMs;
        });
      const meta = resolvePuzzleDashboardModeMeta(column.mode, language);
      return {
        ...column,
        title: meta.label,
        meta,
        entries: columnEntries,
        totalCount: puzzleDashboardEntries.filter((entry) => entry.mode === column.mode).length,
      };
    });
  }, [
    effectivePuzzleDashboardModeFilter,
    puzzleDashboardEntries,
    puzzleDashboardSearch,
    puzzleDashboardSort,
    language,
  ]);
  const hasVisiblePuzzleEntries = puzzleDashboardColumns.some(
    (column) => column.entries && column.entries.length > 0
  );
  const effectiveDailyPuzzleModeFilter = useMemo(() => {
    if (dailyPuzzleModeFilter === "all") return "all";
    return resolvePuzzleColumnMode(dailyPuzzleModeFilter);
  }, [dailyPuzzleModeFilter]);
  const dailyPuzzleDashboardColumns = useMemo(() => {
    const searchTerm = String(dailyPuzzleSearch || "").trim().toLowerCase();
    const visibleModes =
      effectiveDailyPuzzleModeFilter === "all"
        ? PUZZLE_MODE_COLUMNS.map((column) => column.mode)
        : [effectiveDailyPuzzleModeFilter];

    return PUZZLE_MODE_COLUMNS.filter((column) => visibleModes.includes(column.mode)).map((column) => {
      const columnEntries = puzzleDashboardEntries
        .filter((entry) => entry.mode === column.mode)
        .filter((entry) => !searchTerm || entry.searchText.includes(searchTerm))
        .sort((a, b) => {
          if (dailyPuzzleSort === "oldest") return a.createdAtMs - b.createdAtMs;
          if (dailyPuzzleSort === "reward") return b.rewardValue - a.rewardValue;
          if (dailyPuzzleSort === "moves") return a.moveCount - b.moveCount;
          return b.createdAtMs - a.createdAtMs;
        });
      const meta = resolvePuzzleDashboardModeMeta(column.mode, language);
      return {
        ...column,
        title: meta.label,
        meta,
        entries: columnEntries,
        totalCount: puzzleDashboardEntries.filter((entry) => entry.mode === column.mode).length,
      };
    });
  }, [
    dailyPuzzleSearch,
    dailyPuzzleSort,
    effectiveDailyPuzzleModeFilter,
    language,
    puzzleDashboardEntries,
  ]);
  const hasVisibleDailyPuzzleEntries = dailyPuzzleDashboardColumns.some(
    (column) => column.entries && column.entries.length > 0
  );
  // Solo el dueño del sitio: eliminar el torneo o puzzle abierto en «Editar».
  const isSiteOwner = isOwnerAccount(user);
  const ownerDeleteFromEditModal = async (kind) => {
    const isPuzzle = kind === "puzzle";
    const targetId = String(isPuzzle ? puzzleEditTargetId : tournamentEditTargetId || "").trim();
    const label = String(isPuzzle ? puzzleEditTargetLabel : tournamentEditTargetLabel || "").trim();
    if (!targetId) return;
    const what = isPuzzle ? "el puzzle" : "el torneo";
    if (!window.confirm(`¿Eliminar ${what} «${label || targetId}»? No se puede deshacer.`)) return;
    try {
      if (isPuzzle) {
        await axios.post("/tournaments/owner/puzzles/delete", { puzzleId: targetId });
        closePuzzleEditModal();
        await fetchPuzzles(true);
      } else {
        await axios.post("/tournaments/owner/tournaments/delete", { tournamentId: targetId });
        closeTournamentEditModal();
        await fetchTournaments();
      }
      notification.success({ message: isPuzzle ? "Puzzle eliminado" : "Torneo eliminado" });
    } catch (error) {
      notification.error({
        message: "No se pudo eliminar",
        description: error?.response?.data?.message || error?.message || "",
      });
    }
  };
  const renderOwnerDeleteButton = (kind) =>
    isSiteOwner ? (
      <button
        type="button"
        onClick={() => ownerDeleteFromEditModal(kind)}
        className="mt-2 rounded border border-[#ff8c8c] bg-[#7f1d1d] px-3 py-1.5 text-xs font-black uppercase tracking-wide text-[#ffe3e3] transition hover:bg-[#b91c1c]"
      >
        {kind === "puzzle" ? "Eliminar puzzle" : "Eliminar torneo"}
      </button>
    ) : null;

  const puzzleEditModal = (
    <Modal
      open={puzzleEditModalOpen}
      onCancel={closePuzzleEditModal}
      onOk={handleSavePuzzleEdit}
      okText="Guardar cambios"
      cancelText="Cancelar"
      confirmLoading={puzzleEditSaving}
      okButtonProps={{ danger: true }}
      cancelButtonProps={{ disabled: puzzleEditSaving }}
      maskClosable={!puzzleEditSaving}
      title={<span className="font-semibold text-[#ffb4b4]">{i18nT("lobby.editarPuzzle")}</span>}
    >
      <div className="space-y-3">
        <div className="rounded border border-[#7f3232] bg-[#301112] p-3">
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#ffb4b4]">{i18nT("lobby.puzzleSeleccionado")}</div>
          <div className="truncate text-sm font-black text-[#ffe3e3]">
            {String(puzzleEditTargetLabel || "Puzzle")}
          </div>
          <div className="mt-1 text-[11px] text-[#ffcccc]">
            ID: {String(puzzleEditTargetId || "sin id")}
          </div>
          {renderOwnerDeleteButton("puzzle")}
        </div>

        <div>
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#9ecbff]">{i18nT("lobby.nombreDelPuzzle")}</div>
          <Input
            value={puzzleEditNameDraft}
            maxLength={120}
            disabled={puzzleEditSaving}
            onChange={(event) => setPuzzleEditNameDraft(String(event?.target?.value || "").slice(0, 120))}
            placeholder={i18nT("lobby.llegaALaCimaEnMovimientos")}
          />
          <div className="mt-1 text-[11px] text-[#8fa8cb]">
            {i18nT("lobby.siLoDejasVacioSeUsaAutomatico")}{" "}
            {buildDefaultPuzzleNameFromRules(
              Math.max(
                1,
                normalizePuzzleTurnLimit(puzzleEditTurnLimitDraft, DEFAULT_PUZZLE_TURN_LIMIT)
              ),
              puzzleEditSolveTimeLimitEnabledDraft
                ? normalizePuzzleSolveTimeLimitSeconds(
                    Number(puzzleEditSolveTimeLimitMinutesDraft || 0) * 60,
                    0
                  )
                : 0,
              language
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#9ecbff]">{i18nT("lobby.movimientosLimite")}</div>
            <InputNumber
              min={1}
              max={MAX_PUZZLE_TURN_LIMIT}
              precision={0}
              value={puzzleEditTurnLimitDraft}
              disabled={puzzleEditSaving}
              onChange={(value) => setPuzzleEditTurnLimitDraft(value)}
              className="w-full"
            />
          </div>
          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#9ecbff]">{i18nT("lobby.recompensaX")}</div>
            <InputNumber
              min={0}
              max={MAX_PUZZLE_REWARD_VALUE}
              precision={0}
              value={puzzleEditRewardValueDraft}
              disabled={puzzleEditSaving}
              onFocus={(event) => event?.target?.select?.()}
              onChange={(value) => setPuzzleEditRewardValueDraft(normalizePuzzleRewardValue(value, 0))}
              className="w-full"
            />
          </div>
        </div>

        <div className="rounded border border-[#2b4f7f] bg-[#0d1f3d] p-3">
          <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#c6dcff]">
            <input
              type="checkbox"
              checked={puzzleEditSolveTimeLimitEnabledDraft}
              disabled={puzzleEditSaving}
              onChange={(event) => {
                const checked = Boolean(event?.target?.checked);
                setPuzzleEditSolveTimeLimitEnabledDraft(checked);
                if (!checked) {
                  setPuzzleEditSolveTimeLimitMinutesDraft(DEFAULT_PUZZLE_SOLVE_TIME_MINUTES);
                }
              }}
            />
            {i18nT("lobby.activarTiempoLimite")}
          </label>
          <div className="mt-2 text-[11px] text-[#98b8e4]">{i18nT("lobby.siLoActivasElPuzzleSeDebeResolver")}</div>
          <div className="mt-2">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#9ecbff]">{i18nT("lobby.minutosLimite")}</div>
            <InputNumber
              min={1}
              max={MAX_PUZZLE_SOLVE_TIME_MINUTES}
              precision={0}
              value={puzzleEditSolveTimeLimitMinutesDraft}
              disabled={!puzzleEditSolveTimeLimitEnabledDraft || puzzleEditSaving}
              onChange={(value) => setPuzzleEditSolveTimeLimitMinutesDraft(value)}
              className="w-full"
            />
          </div>
        </div>

        <div className="rounded border border-[#8f6f2a] bg-[#2a210d] p-3">
          <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#ffe9b9]">
            <input
              type="checkbox"
              checked={puzzleEditResetRewardProgressDraft}
              disabled={puzzleEditSaving}
              onChange={(event) => setPuzzleEditResetRewardProgressDraft(Boolean(event?.target?.checked))}
            />
            {i18nT("lobby.restablecerRecompensaParaVolverAReclamar")}
          </label>
          <div className="mt-2 text-[11px] text-[#ffe0a6]">{i18nT("lobby.borraEstadoDeResueltoReclamadoFalladoDeEste")}</div>
        </div>
      </div>
    </Modal>
  );

  if (isFindMatchesDashboardView) {
    const findMatchModeCards = [
      {
        key: "free",
        title: findMatchesText("scienceModeTitle"),
        subtitle: findMatchesText("scienceSubtitle"),
        detail: findMatchesText("scienceDetail"),
        icon: "/mode-icons/free.png",
        className: "xima-play-mode-science",
      },
      {
        key: "dice",
        title: findMatchesText("chanceModeTitle"),
        subtitle: findMatchesText("chanceSubtitle"),
        detail: findMatchesText("chanceDetail"),
        icon: "/mode-icons/dice.png",
        className: "xima-play-mode-luck",
      },
      {
        key: "cienzar",
        title: findMatchesText("cienzarModeTitle"),
        subtitle: findMatchesText("cienzarSubtitle"),
        detail: findMatchesText("cienzarDetail"),
        icon: "/mode-icons/cienzar.png",
        className: "xima-play-mode-cienzar",
      },
      {
        key: "sciencevsazar",
        title: findMatchesText("scienceVsAzarModeTitle"),
        subtitle: findMatchesText("scienceVsAzarSubtitle"),
        detail: findMatchesText("scienceVsAzarDetail"),
        icon: "/mode-icons/cienzar.png",
        className: "xima-play-mode-sciencevsazar",
      },
    ];
    const findMatchSummaryCards = [
      {
        key: "total",
        label: findMatchesText("challengesCreated"),
        value: findMatchDashboardStats.total,
        detail: findMatchesText("independentMatches"),
        Icon: IoGameControllerOutline,
        tone: "is-blue",
      },
      {
        key: "available",
        label: findMatchesText("available"),
        value: findMatchDashboardStats.available,
        detail: findMatchesText("readyToJoin"),
        Icon: IoPlayOutline,
        tone: "is-green",
      },
      {
        key: "players",
        label: findMatchesText("playersJoined"),
        value: findMatchDashboardStats.joinedPlayers,
        detail: findMatchesText("waitingForOpponent"),
        Icon: IoPeopleOutline,
        tone: "is-gold",
      },
      {
        key: "private",
        label: findMatchesText("withPassword"),
        value: findMatchDashboardStats.privateMatches,
        detail: findMatchesText("requireKey"),
        Icon: IoBookmarkOutline,
        tone: "is-violet",
      },
      {
        key: "paid",
        label: findMatchesText("paid"),
        value: findMatchDashboardStats.paidMatches,
        detail: findMatchesText("requireBonuses"),
        Icon: IoDiamondOutline,
        tone: "is-cyan",
      },
    ];
    const refreshFindMatchesDashboard = () => {
      fetchTournaments().catch((error) => console.log(error));
      fetchLiveMatches().catch((error) => console.log(error));
      fetchModeWinnersLeaderboard(false).catch((error) => console.log(error));
    };

    return (
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: "rgb(80, 80, 200)",
          },
          algorithm: theme.darkAlgorithm,
        }}
      >
        <main className="xima-play-tournaments-screen xima-find-matches-screen">
          <div className="xima-play-tournaments-overlay" aria-hidden="true" />
          <div className="xima-play-tournaments-shell xima-find-matches-shell">
            <header className="xima-play-tournaments-header">
              <div className="xima-play-tournaments-logo" aria-label="XIMA">
                XIMA
              </div>
              <button
                type="button"
                onClick={() => navigate("/game/lobby", { replace: true })}
                className="xima-play-tournaments-back"
              >
                <span aria-hidden="true">&lt;</span>
                {findMatchesText("backToLobby")}
              </button>
              <div className="xima-play-tournaments-title">
                <h1>{findMatchesText("findMatches")}</h1>
                <p>{findMatchesText("intro")}</p>
              </div>
            </header>

            <section
              className="xima-play-tournaments-hero xima-find-matches-hero"
              aria-label={findMatchesText("challengeModesAria")}
            >
              <div className="xima-play-tournaments-mode-grid">
                {findMatchModeCards.map((option) => {
                  const selected = findMatchesModeFilter === option.key;
                  return (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setFindMatchesModeFilter(selected ? "all" : option.key)}
                      className={`xima-play-tournaments-mode-card ${option.className}${
                        selected ? " xima-play-tournaments-selected" : ""
                      }`}
                      aria-pressed={selected}
                    >
                      <span className="xima-play-tournaments-mode-icon">
                        <img src={option.icon} alt="" aria-hidden="true" />
                      </span>
                      <span className="xima-play-tournaments-mode-copy">
                        <strong>{option.title}</strong>
                        <small>{option.subtitle}</small>
                        <small>{option.detail}</small>
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>

            <section
              className="xima-find-matches-summary"
              aria-label={findMatchesText("matchSummaryAria")}
            >
              {findMatchSummaryCards.map((card) => {
                const SummaryIcon = card.Icon;
                return (
                  <article key={card.key} className={`xima-find-matches-summary-card ${card.tone}`}>
                    <span>
                      <SummaryIcon aria-hidden="true" />
                    </span>
                    <div>
                      <small>{card.label}</small>
                      <strong>{card.value}</strong>
                      <em>{card.detail}</em>
                    </div>
                  </article>
                );
              })}
            </section>

            <section className="xima-play-tournaments-layout xima-find-matches-layout">
              <div className="xima-play-tournaments-main-panel xima-find-matches-main-panel">
                <div className="xima-play-tournaments-panel-head xima-find-matches-panel-head">
                  <div className="xima-play-tournaments-head-icon" aria-hidden="true">
                    <IoListOutline />
                  </div>
                  <div>
                    <h2>{findMatchesText("openChallenges")}</h2>
                    <p>{findMatchesText("openChallengesDescription")}</p>
                  </div>
                  <div className="xima-play-tournaments-filters xima-find-matches-filters">
                    <label>
                      <span>{findMatchesText("filterByMode")}</span>
                      <span className="xima-play-tournaments-select-wrap">
                        <select
                          value={findMatchesModeFilter}
                          onChange={(event) => {
                            setFindMatchesModeFilter(event.target.value);
                          }}
                        >
                          <option value="all">{findMatchesText("allModes")}</option>
                          <option value="free">{findMatchesText("scienceShort")}</option>
                          <option value="cienzar">{findMatchesText("cienzarShort")}</option>
                          <option value="sciencevsazar">
                            {findMatchesText("scienceVsAzarShort")}
                          </option>
                          <option value="dice">{findMatchesText("chanceShort")}</option>
                        </select>
                        <IoFilterOutline aria-hidden="true" />
                      </span>
                    </label>
                    <label className="xima-find-matches-search">
                      <span>{findMatchesText("search")}</span>
                      <span className="xima-play-tournaments-select-wrap">
                        <input
                          type="search"
                          value={findMatchesSearch}
                          onChange={(event) =>
                            setFindMatchesSearch(String(event?.target?.value || ""))
                          }
                          placeholder={findMatchesText("playerOrChallengePlaceholder")}
                        />
                        <IoSearchOutline aria-hidden="true" />
                      </span>
                    </label>
                    <label>
                      <span>{findMatchesText("sort")}</span>
                      <span className="xima-play-tournaments-select-wrap">
                        <select
                          value={findMatchesSort}
                          onChange={(event) => setFindMatchesSort(event.target.value)}
                        >
                          <option value="recent">{findMatchesText("mostRecent")}</option>
                          <option value="oldest">{findMatchesText("oldest")}</option>
                          <option value="players">{findMatchesText("mostPlayers")}</option>
                          <option value="time">{findMatchesText("shortestTime")}</option>
                        </select>
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={refreshFindMatchesDashboard}
                      className="xima-play-tournaments-refresh"
                      aria-label={findMatchesText("refreshMatches")}
                    >
                      <IoRefreshOutline aria-hidden="true" />
                    </button>
                  </div>
                </div>

                <div className="xima-play-tournaments-list xima-find-matches-list">
                  {visibleFindMatchEntries.length > 0 ? (
                    visibleFindMatchEntries.map((entry) => {
                      const rowClass =
                        entry.toneClass === "luck"
                          ? "xima-play-tournaments-row-luck"
                          : entry.toneClass === "sciencevsazar"
                          ? "xima-play-tournaments-row-sciencevsazar"
                          : entry.toneClass === "cienzar"
                          ? "xima-play-tournaments-row-cienzar"
                          : "xima-play-tournaments-row-science";
                      const statusLabel = entry.isFull
                        ? findMatchesText("matchFull")
                        : entry.missingPlayers === 1
                        ? findMatchesText("waitingOnePlayer")
                        : findMatchesText("waitingPlayers", { count: entry.missingPlayers });

                      return (
                        <article
                          key={`find-match-${entry.tournamentId}`}
                          className={`xima-play-tournaments-row xima-find-matches-row ${rowClass}`}
                        >
                          <button
                            type="button"
                            className="xima-play-tournaments-thumb xima-find-matches-thumb"
                            onClick={() => {
                              if (!entry.canJoin) return;
                              handleJoin(entry.tournamentId, {
                                autoPlay: true,
                                match: {
                                  ...entry.match,
                                  tournamentUiSequenceNumber: entry.sequenceNumber,
                                  tournamentSequenceNumber: entry.sequenceNumber,
                                  tournamentNumber: entry.sequenceNumber,
                                  displayNumber: entry.sequenceNumber,
                                },
                                tournamentUiSequenceNumber: entry.sequenceNumber,
                                tournamentSequenceNumber: entry.sequenceNumber,
                              });
                            }}
                            disabled={!entry.canJoin}
                            aria-label={findMatchesText("joinMatchAria", { title: entry.title })}
                          >
                            <span className="xima-play-tournaments-thumb-icons" aria-hidden="true">
                              {entry.modeIconSrc ? (
                                <img
                                  src={entry.modeIconSrc}
                                  alt=""
                                  className="xima-play-tournaments-thumb-mode"
                                />
                              ) : (
                                <IoGameControllerOutline aria-hidden="true" />
                              )}
                              {entry.playerCountIconSrc ? (
                                <img
                                  src={entry.playerCountIconSrc}
                                  alt=""
                                  className="xima-play-tournaments-thumb-badge"
                                />
                              ) : null}
                              {entry.matchDurationIconSrc ? (
                                <img
                                  src={entry.matchDurationIconSrc}
                                  alt=""
                                  className="xima-play-tournaments-thumb-badge"
                                />
                              ) : null}
                            </span>
                          </button>

                          <div className="xima-play-tournaments-info xima-find-matches-info">
                            <h3>{entry.title}</h3>
                            <p>{statusLabel}</p>
                            <div className="xima-play-tournaments-meta">
                              <span>
                                <IoPeopleOutline aria-hidden="true" />
                                {entry.connectedPlayers}/{entry.memberSize} {findMatchesText("players")}
                              </span>
                              <span>
                                <IoGameControllerOutline aria-hidden="true" />
                                {findMatchesText("createdBy", { name: entry.creatorName })}
                              </span>
                              <span>
                                <IoCalendarOutline aria-hidden="true" />
                                {entry.createdAtLabel}
                              </span>
                            </div>
                            <div className="xima-play-tournaments-timebox">
                              {entry.sequenceNumber > 0 ? (
                                <span
                                  className="xima-tournament-sequence-chip xima-challenge-sequence-chip"
                                  data-short={formatPublicSequenceShortLabel(
                                    language,
                                    entry.sequenceNumber
                                  )}
                                  title={formatPublicSequenceLabel(
                                    language,
                                    "challenge",
                                    entry.sequenceNumber
                                  )}
                                >
                                  {formatPublicSequenceLabel(
                                    language,
                                    "challenge",
                                    entry.sequenceNumber
                                  )}
                                </span>
                              ) : null}
                              <span className="xima-time-chip-live">
                                <IoPlayOutline aria-hidden="true" />
                                {findMatchesText("live")}
                              </span>
                              {entry.hasPassword ? (
                                <span className="xima-play-tournaments-lock-card">
                                  <IoBookmarkOutline aria-hidden="true" />
                                  <strong>{findMatchesText("withPassword")}</strong>
                                </span>
                              ) : null}
                              {entry.hasPayment ? (
                                <span className="xima-time-chip-payment">
                                  <IoDiamondOutline aria-hidden="true" />
                                  {findMatchesText("payment", { amount: entry.paymentAmount })}
                                </span>
                              ) : null}
                              {entry.shareUrl ? (
                                <button
                                  type="button"
                                  className="xima-find-matches-link-chip"
                                  onClick={() => handleCopyShareLink(entry.shareUrl)}
                                >
                                  {findMatchesText("copyLink")}
                                </button>
                              ) : null}
                            </div>
                          </div>

                          <div className="xima-play-tournaments-mode-detail">
                            <strong>{entry.modeLabel}</strong>
                            <span>
                              <IoPeopleOutline aria-hidden="true" />
                              1 vs {Math.max(1, entry.memberSize - 1)}
                            </span>
                            <span>
                              <IoTimeOutline aria-hidden="true" />
                              {findMatchesText("minutesPerTurn", { minutes: entry.turnTime })}
                            </span>
                            {entry.playerCountIconSrc ? (
                              <span className="xima-find-matches-mini-icon">
                                <img src={entry.playerCountIconSrc} alt="" aria-hidden="true" />
                                {findMatchesText("modality")}
                              </span>
                            ) : null}
                          </div>

                          <div className="xima-play-tournaments-prize xima-find-matches-join-box">
                            <span>{findMatchesText("status")}</span>
                            <strong>
                              <IoPeopleOutline aria-hidden="true" />
                              {findMatchesText("missingPlayers", { count: entry.missingPlayers })}
                            </strong>
                            <button
                              type="button"
                              onClick={() =>
                                handleJoin(entry.tournamentId, {
                                  autoPlay: true,
                                  match: {
                                    ...entry.match,
                                    tournamentUiSequenceNumber: entry.sequenceNumber,
                                    tournamentSequenceNumber: entry.sequenceNumber,
                                    tournamentNumber: entry.sequenceNumber,
                                    displayNumber: entry.sequenceNumber,
                                  },
                                  tournamentUiSequenceNumber: entry.sequenceNumber,
                                  tournamentSequenceNumber: entry.sequenceNumber,
                                })
                              }
                              className="xima-play-tournaments-join"
                              disabled={!entry.canJoin}
                            >
                              {entry.canJoin
                                ? findMatchesText("join")
                                : findMatchesText("complete")}
                            </button>
                          </div>
                        </article>
                      );
                    })
                  ) : (
                    <div className="xima-play-tournaments-empty">
                      {findMatchesText("noOpenChallenges")}
                    </div>
                  )}
                </div>
              </div>

              <aside className="xima-play-tournaments-side">
                <section className="xima-play-tournaments-guide">
                  <h2>{findMatchesText("howItWorks")}</h2>
                  <div className="xima-play-tournaments-guide-step">
                    <img src="/mode-icons/free.png" alt="" aria-hidden="true" />
                    <div>
                      <strong>{findMatchesText("chooseMode")}</strong>
                      <span>{findMatchesText("filterByModes")}</span>
                    </div>
                  </div>
                  <div className="xima-play-tournaments-guide-step">
                    <IoPeopleOutline aria-hidden="true" />
                    <div>
                      <strong>{findMatchesText("joinChallenge")}</strong>
                      <span>{findMatchesText("joinButtonIndependent")}</span>
                    </div>
                  </div>
                  <div className="xima-play-tournaments-guide-step">
                    <IoPlayOutline aria-hidden="true" />
                    <div>
                      <strong>{findMatchesText("playLive")}</strong>
                      <span>{findMatchesText("matchCompleteBoard")}</span>
                    </div>
                  </div>
                </section>

                <section className="xima-play-tournaments-ranking">
                  <div className="xima-play-tournaments-ranking-head">
                    <h2>{findMatchesText("topPlayers")}</h2>
                    <button
                      type="button"
                      onClick={() => navigate("/game/ranking")}
                      aria-label={findMatchesText("viewFullRanking")}
                    >
                      <IoChevronForwardOutline aria-hidden="true" />
                    </button>
                  </div>
                  <div className="xima-play-tournaments-ranking-list">
                    {findMatchesModeWinnersSections.flatMap((section) =>
                      ensureArray(section.players)
                        .slice(0, 2)
                        .map((player, index) => ({
                          key: `${section.key}-${player?.id || player?._id || player?.email || index}`,
                          rank: index + 1,
                          name:
                            extractPrimaryName(
                              player?.displayName ||
                                player?.name ||
                                player?.firstName ||
                                player?.email
                            ) || findMatchesText("playerFallback"),
                          score: formatCimaxScoreValue(
                            player?.cimaxScore ??
                              player?.cimax ??
                              player?.score ??
                              player?.points
                          ),
                          player,
                        }))
                    ).slice(0, 6).map((row) => (
                      <button
                        key={row.key}
                        type="button"
                        onClick={() => openPlayerProfile(row.player)}
                        className="xima-play-tournaments-ranking-row"
                      >
                        <span>{row.rank}</span>
                        <strong>{row.name}</strong>
                        <em>{row.score}</em>
                      </button>
                    ))}
                    {findMatchesModeWinnersSections.every(
                      (section) => ensureArray(section.players).length === 0
                    ) ? (
                      <div className="xima-play-tournaments-ranking-empty">
                        {findMatchesText("noRankingAvailable")}
                      </div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate("/game/ranking")}
                    className="xima-play-tournaments-ranking-link"
                  >
                    <IoTrophyOutline aria-hidden="true" />
                    {findMatchesText("viewFullRanking")}
                  </button>
                </section>
              </aside>
            </section>
          </div>
        </main>
      </ConfigProvider>
    );
  }

  if (isDailyPuzzleDashboardView) {
    const dailySummaryCards = [
      {
        key: "resolved",
        label: getPuzzleDashboardCopy(language, "puzzlesSolved"),
        value: `${formatPuzzleDashboardInteger(
          puzzleDashboardStats.solved,
          language
        )} / ${formatPuzzleDashboardInteger(puzzleDashboardStats.total, language)}`,
        detail: getPuzzleDashboardCopy(language, "percentOfTotal", {
          percent: formatPuzzleDashboardPercent(puzzleDashboardStats.successRate, 1, language),
        }),
        Icon: IoCheckmarkCircleOutline,
        tone: "is-purple",
      },
      {
        key: "attempts",
        label: getPuzzleDashboardCopy(language, "totalAttempts"),
        value: formatPuzzleDashboardInteger(puzzleDashboardStats.totalAttempts, language),
        detail: getPuzzleDashboardCopy(language, "averagePerPuzzle", {
          value: formatPuzzleDashboardDecimal(puzzleDashboardStats.averageAttempts, 2, language),
        }),
        Icon: IoGameControllerOutline,
        tone: "is-blue",
      },
      {
        key: "success",
        label: getPuzzleDashboardCopy(language, "success"),
        value: formatPuzzleDashboardInteger(puzzleDashboardStats.solved, language),
        detail: formatPuzzleDashboardPercent(puzzleDashboardStats.successRate, 1, language),
        Icon: IoTrophyOutline,
        tone: "is-green",
      },
      {
        key: "failures",
        label: getPuzzleDashboardCopy(language, "failures"),
        value: formatPuzzleDashboardInteger(puzzleDashboardStats.failed, language),
        detail: formatPuzzleDashboardPercent(puzzleDashboardStats.failedRate, 1, language),
        Icon: IoCloseCircleOutline,
        tone: "is-red",
      },
      {
        key: "moves",
        label: getPuzzleDashboardCopy(language, "averageMoves"),
        value: formatPuzzleDashboardDecimal(puzzleDashboardStats.averageMoves, 2, language),
        detail: getPuzzleDashboardCopy(language, "bestWorstMoves", {
          best: formatPuzzleDashboardInteger(puzzleDashboardStats.bestMoves, language),
          worst: formatPuzzleDashboardInteger(puzzleDashboardStats.worstMoves, language),
        }),
        Icon: IoStatsChartOutline,
        tone: "is-gold",
      },
      {
        key: "time",
        label: getPuzzleDashboardCopy(language, "averageTime"),
        value: formatPuzzleDashboardTime(puzzleDashboardStats.averageTimeSeconds),
        detail: getPuzzleDashboardCopy(language, "bestWorstTime", {
          best: formatPuzzleDashboardTime(puzzleDashboardStats.bestTimeSeconds),
          worst: formatPuzzleDashboardTime(puzzleDashboardStats.worstTimeSeconds),
        }),
        Icon: IoTimeOutline,
        tone: "is-violet",
      },
      {
        key: "rewards",
        label: getPuzzleDashboardCopy(language, "totalRewards"),
        value: formatPuzzleDashboardInteger(puzzleDashboardStats.totalRewards, language),
        detail: getPuzzleDashboardCopy(language, "bonusesEarned"),
        Icon: IoGiftOutline,
        tone: "is-cyan",
      },
    ];

    const refreshDailyPuzzleDashboard = () => {
      fetchTournaments().catch((error) => console.log(error));
      fetchPuzzles(true).catch((error) => console.log(error));
      fetchLiveMatches().catch((error) => console.log(error));
      fetchModeWinnersLeaderboard(false).catch((error) => console.log(error));
    };

    return (
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: "rgb(80, 80, 200)",
          },
          algorithm: theme.darkAlgorithm,
        }}
      >
        <main className="xima-daily-puzzles-screen">
          <header className="xima-daily-puzzles-topbar">
            <button
              type="button"
              className="xima-daily-nav-button"
              onClick={() => navigate("/game/lobby", { replace: true })}
            >
              <IoChevronBackOutline aria-hidden="true" />
              {getPuzzleDashboardCopy(language, "goToLobby")}
            </button>
            <div className="xima-daily-title">
              <span>
                <IoExtensionPuzzleOutline aria-hidden="true" />
              </span>
              <div>
                <h1>{getPuzzleDashboardCopy(language, "title")}</h1>
                <p>{getPuzzleDashboardCopy(language, "subtitle")}</p>
              </div>
            </div>
            <div className="xima-daily-actions">
              {hasDirectSuperuserAccess ? (
                <button
                  type="button"
                  onClick={handleTogglePuzzleEditMode}
                  className={`xima-daily-edit-toggle ${puzzleEditModeEnabled ? "is-editing" : ""}`}
                >
                  <IoCogOutline aria-hidden="true" />
                  {puzzleEditModeEnabled
                    ? getPuzzleDashboardCopy(language, "editPuzzleOn")
                    : getPuzzleDashboardCopy(language, "editPuzzle")}
                </button>
              ) : null}
              <button type="button" onClick={() => navigate("/game/history")}>
                <IoTimeOutline aria-hidden="true" />
                {getPuzzleDashboardCopy(language, "history")}
              </button>
              <button type="button" onClick={refreshDailyPuzzleDashboard}>
                <IoRefreshOutline aria-hidden="true" />
                {getPuzzleDashboardCopy(language, "refresh")}
                <IoRefreshOutline aria-hidden="true" />
              </button>
            </div>
          </header>

          <section className="xima-daily-summary-grid">
            {dailySummaryCards.map((card) => {
              const CardIcon = card.Icon;
              return (
                <article key={card.key} className={`xima-daily-summary-card ${card.tone}`}>
                  <span>
                    <CardIcon aria-hidden="true" />
                  </span>
                  <div>
                    <small>{card.label}</small>
                    <strong>{card.value}</strong>
                    <em>{card.detail}</em>
                  </div>
                </article>
              );
            })}
          </section>

          <section className="xima-daily-filterbar">
            <div className="xima-daily-mode-filters">
              <strong>{getPuzzleDashboardCopy(language, "filterByMode")}</strong>
              {[
                { key: "all", label: getPuzzleDashboardCopy(language, "all"), Icon: IoListOutline },
                { key: "free", label: getPuzzleDashboardModeLabel(language, "free"), Icon: IoCubeOutline },
                { key: "cienzar", label: getPuzzleDashboardModeLabel(language, "cienzar"), Icon: IoBeakerOutline },
                { key: "dice", label: getPuzzleDashboardModeLabel(language, "dice"), Icon: IoDiceOutline },
              ].map((filter) => {
                const FilterIcon = filter.Icon;
                const active =
                  effectiveDailyPuzzleModeFilter === filter.key ||
                  (filter.key === "all" && effectiveDailyPuzzleModeFilter === "all");
                return (
                  <button
                    key={filter.key}
                    type="button"
                    onClick={() => setDailyPuzzleModeFilter(filter.key)}
                    className={active ? "is-active" : ""}
                  >
                    <FilterIcon aria-hidden="true" />
                    {filter.label}
                  </button>
                );
              })}
            </div>
            <label className="xima-daily-search">
              <input
                type="search"
                value={dailyPuzzleSearch}
                onChange={(event) => setDailyPuzzleSearch(String(event?.target?.value || ""))}
                placeholder={getPuzzleDashboardCopy(language, "searchPuzzle")}
              />
              <IoSearchOutline aria-hidden="true" />
            </label>
            <label className="xima-daily-sort">
              <span>{getPuzzleDashboardCopy(language, "sortBy")}</span>
              <select
                value={dailyPuzzleSort}
                onChange={(event) => setDailyPuzzleSort(String(event?.target?.value || "recent"))}
              >
                <option value="recent">{getPuzzleDashboardCopy(language, "mostRecent")}</option>
                <option value="oldest">{getPuzzleDashboardCopy(language, "oldest")}</option>
                <option value="reward">{getPuzzleDashboardCopy(language, "highestReward")}</option>
                <option value="moves">{getPuzzleDashboardCopy(language, "fewestMoves")}</option>
              </select>
            </label>
          </section>

          {hasDirectSuperuserAccess && puzzleEditModeEnabled ? (
            <section className="xima-daily-edit-alert">
              {getPuzzleDashboardCopy(language, "editModeActive")}
            </section>
          ) : null}

          {puzzlesLoading ? (
            <section className="xima-daily-empty">
              {getPuzzleDashboardCopy(language, "loadingPuzzles")}
            </section>
          ) : hasVisibleDailyPuzzleEntries ? (
            <>
              <section
                className={`xima-daily-mode-grid ${
                  effectiveDailyPuzzleModeFilter === "all" ? "" : "is-single-mode"
                }`}
              >
                {dailyPuzzleDashboardColumns.map((column) => {
                  const ModeIcon = column.meta.Icon;
                  return (
                    <article
                      key={`daily-puzzle-column-${column.mode}`}
                      className={`xima-daily-mode-panel ${column.meta.toneClass}`}
                    >
                      <header>
                        <div>
                          <ModeIcon aria-hidden="true" />
                          <strong>
                            {column.title} ({formatPuzzleDashboardInteger(column.totalCount, language)})
                          </strong>
                        </div>
                      </header>
                      <div className="xima-daily-table">
                        <div className="xima-daily-table-head">
                          <span>{getPuzzleDashboardCopy(language, "puzzle")}</span>
                          <span>{getPuzzleDashboardCopy(language, "attempts")}</span>
                          <span>{getPuzzleDashboardCopy(language, "movesAvgBest")}</span>
                          <span>{getPuzzleDashboardCopy(language, "timeAvgBest")}</span>
                          <span>{getPuzzleDashboardCopy(language, "result")}</span>
                          <span>{getPuzzleDashboardCopy(language, "reward")}</span>
                        </div>
                        <div className="xima-daily-table-scroll">
                          {column.entries.map((entry) => {
                            const puzzle = entry.puzzle;
                            const puzzleId = entry.puzzleId;
                            const puzzleShareUrl = buildLobbyShareUrl("puzzle", puzzleId);
                            const puzzleRewardClaimLoading = claimingPuzzleRewardId === puzzleId;
                            const puzzleLaunchLoading = launchingPuzzleId === puzzleId;
                            const rowStatusClass = entry.failed
                              ? "is-failed"
                              : entry.solved
                              ? "is-success"
                              : "is-pending";
                            const rewardLabel = puzzleRewardClaimLoading
                              ? getPuzzleDashboardCopy(language, "claiming")
                              : entry.failed
                              ? "-"
                              : entry.claimed
                              ? `${entry.rewardValue}X`
                              : entry.rewardCanClaim
                              ? getPuzzleDashboardCopy(language, "claimReward", {
                                  reward: `${entry.rewardValue}X`,
                                })
                              : `${entry.rewardValue}X`;
                            const puzzleEditInteractive =
                              hasDirectSuperuserAccess &&
                              puzzleEditModeEnabled &&
                              Boolean(puzzleId);
                            const puzzleEditSelected =
                              puzzleEditInteractive && puzzleEditTargetId === puzzleId;

                            return (
                              <article
                                key={`daily-puzzle-${column.mode}-${puzzleId || entry.modeOrder}`}
                                className={`xima-daily-row ${rowStatusClass}`}
                              >
                                <div className="xima-daily-cell is-puzzle">
                                  {puzzleEditInteractive ? (
                                    <button
                                      type="button"
                                      onClick={() => openPuzzleEditModal(puzzle)}
                                      className={`xima-daily-puzzle-icon xima-daily-puzzle-edit-icon ${
                                        puzzleEditSelected ? "is-editing" : ""
                                      }`}
                                      title={getPuzzleDashboardCopy(language, "editPuzzle")}
                                    >
                                      <ModeIcon aria-hidden="true" />
                                    </button>
                                  ) : (
                                    <span className="xima-daily-puzzle-icon">
                                      <ModeIcon aria-hidden="true" />
                                    </span>
                                  )}
                                  <span className="xima-daily-puzzle-title">
                                    <strong>#{entry.modeOrder}</strong>
                                    <em>{entry.title}</em>
                                  </span>
                                  {puzzleShareUrl ? (
                                    <button
                                      type="button"
                                      onClick={() => handleCopyShareLink(puzzleShareUrl)}
                                      title={getPuzzleDashboardCopy(language, "copyLink")}
                                    >
                                      {getPuzzleDashboardCopy(language, "link")}
                                    </button>
                                  ) : null}
                                </div>
                                <div className="xima-daily-cell">{entry.attemptCount}</div>
                                <div className="xima-daily-cell">{entry.movementLabel}</div>
                                <div className="xima-daily-cell">{entry.timeLabel} / {entry.bestTimeLabel}</div>
                                <div className="xima-daily-cell">
                                  <button
                                    type="button"
                                    onClick={() => handleStartPuzzle(puzzle)}
                                    disabled={!puzzleId || puzzleLaunchLoading}
                                    className={`xima-daily-result ${rowStatusClass}`}
                                  >
                                    {puzzleLaunchLoading
                                      ? getPuzzleDashboardCopy(language, "opening")
                                      : entry.failed
                                      ? getPuzzleDashboardCopy(language, "failed")
                                      : entry.solved
                                      ? getPuzzleDashboardCopy(language, "success")
                                      : getPuzzleDashboardCopy(language, "solve")}
                                  </button>
                                </div>
                                <div className="xima-daily-cell">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (!entry.rewardCanClaim || puzzleRewardClaimLoading) return;
                                      handleClaimPuzzleReward(puzzle);
                                    }}
                                    disabled={puzzleRewardClaimLoading}
                                    className={`xima-daily-reward ${entry.rewardCanClaim ? "can-claim" : ""}`}
                                  >
                                    {rewardLabel}
                                  </button>
                                </div>
                              </article>
                            );
                          })}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="xima-daily-see-all"
                        onClick={() => {
                          setDailyPuzzleModeFilter(column.mode);
                          setDailyPuzzleSearch("");
                        }}
                      >
                        {getPuzzleDashboardCopy(language, "viewAllPuzzlesWithCount", {
                          count: formatPuzzleDashboardInteger(column.totalCount, language),
                        })}
                      </button>
                    </article>
                  );
                })}
              </section>

              <section className="xima-daily-analytics-grid">
                <article className="xima-daily-analytics-card">
                  <h2>{getPuzzleDashboardCopy(language, "successRateByMode")}</h2>
                  <div className="xima-daily-donut-layout">
                    <div
                      className="xima-daily-donut is-success"
                      style={{
                        "--xima-daily-rate-angle": `${
                          Math.min(100, puzzleDashboardStats.successRate) * 3.6
                        }deg`,
                      }}
                    >
                      <strong>{formatPuzzleDashboardPercent(puzzleDashboardStats.successRate, 0, language)}</strong>
                      <span>{getPuzzleDashboardCopy(language, "overallSuccess")}</span>
                    </div>
                    <div className="xima-daily-legend">
                      {puzzleDashboardStats.modeStats.map((modeStat) => (
                        <span key={`daily-success-${modeStat.mode}`} className={modeStat.meta.toneClass}>
                          <i />
                          {modeStat.title} {formatPuzzleDashboardInteger(modeStat.solved, language)} /{" "}
                          {formatPuzzleDashboardInteger(modeStat.count, language)} (
                          {formatPuzzleDashboardPercent(modeStat.successRate, 0, language)})
                        </span>
                      ))}
                    </div>
                  </div>
                </article>
                <article className="xima-daily-analytics-card">
                  <h2>{getPuzzleDashboardCopy(language, "averageMovesByMode")}</h2>
                  <div className="xima-daily-bars">
                    {puzzleDashboardStats.modeStats.map((modeStat) => (
                      <div key={`daily-moves-${modeStat.mode}`} className={modeStat.meta.toneClass}>
                        <span>{modeStat.title}</span>
                        <div>
                          <i style={{ width: `${Math.max(4, modeStat.movesShare)}%` }} />
                        </div>
                        <strong>{formatPuzzleDashboardDecimal(modeStat.averageMoves, 2, language)}</strong>
                      </div>
                    ))}
                  </div>
                </article>
                <article className="xima-daily-analytics-card">
                  <h2>{getPuzzleDashboardCopy(language, "rewardsEarnedByMode")}</h2>
                  <div className="xima-daily-donut-layout">
                    <div
                      className="xima-daily-donut is-reward"
                      style={{
                        background: (() => {
                          const total = Math.max(1, puzzleDashboardStats.totalRewards);
                          const ciencia =
                            ((puzzleDashboardStats.modeStats.find((item) => item.mode === "free")
                              ?.rewards || 0) /
                              total) *
                            100;
                          const cienzar =
                            ((puzzleDashboardStats.modeStats.find((item) => item.mode === "cienzar")
                              ?.rewards || 0) /
                              total) *
                            100;
                          return `conic-gradient(#0e8bff 0 ${ciencia}%, #19c66b ${ciencia}% ${
                            ciencia + cienzar
                          }%, #e5a900 ${ciencia + cienzar}% 100%)`;
                        })(),
                      }}
                    >
                      <strong>{formatPuzzleDashboardInteger(puzzleDashboardStats.totalRewards, language)}</strong>
                      <span>{getPuzzleDashboardCopy(language, "total")}</span>
                    </div>
                    <div className="xima-daily-legend">
                      {puzzleDashboardStats.modeStats.map((modeStat) => (
                        <span key={`daily-reward-${modeStat.mode}`} className={modeStat.meta.toneClass}>
                          <i />
                          {modeStat.title} {formatPuzzleDashboardInteger(modeStat.rewards, language)}
                        </span>
                      ))}
                    </div>
                  </div>
                </article>
                <article className="xima-daily-analytics-card">
                  <h2>{getPuzzleDashboardCopy(language, "averageTimeByMode")}</h2>
                  <div className="xima-daily-time-grid">
                    {puzzleDashboardStats.modeStats.map((modeStat) => (
                      <div key={`daily-time-${modeStat.mode}`} className={modeStat.meta.toneClass}>
                        <span>{modeStat.title}</span>
                        <div>
                          <i style={{ width: `${Math.max(4, modeStat.timeShare)}%` }} />
                        </div>
                        <strong>{formatPuzzleDashboardTime(modeStat.averageTime)}</strong>
                      </div>
                    ))}
                  </div>
                </article>
              </section>
            </>
          ) : (
            <section className="xima-daily-empty">
              {getPuzzleDashboardCopy(language, "emptyPuzzles")}
            </section>
          )}
        </main>
        {puzzleEditModal}
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "rgb(80, 80, 200)",
        },
        algorithm: theme.darkAlgorithm,
      }}
    >
      <div className={`relative h-full overflow-y-auto p-4 text-white md:p-6 md:overflow-hidden max-md:pb-6 max-md:[&_button]:max-w-full max-md:[&_button]:min-h-[40px] max-md:[&_button]:whitespace-normal max-md:[&_button]:break-words max-md:[&_button]:leading-tight max-md:[&_button]:tracking-normal [@media(max-width:1024px)_and_(orientation:portrait)]:touch-pan-y [@media(max-width:1024px)_and_(orientation:portrait)]:[-webkit-overflow-scrolling:touch] ${portraitTopScrollSpaceClass} ${portraitBottomScrollSpaceClass} [@media(max-width:1024px)_and_(orientation:landscape)]:overflow-x-hidden [@media(max-width:1024px)_and_(orientation:landscape)]:pb-0 ${isIphonePortraitMobile ? "iphone-portrait-top-fix iphone-portrait-tournament-compact" : ""} ${shouldHideLobbyForWaitingRoomOpenRequest ? "hidden" : ""}`}>
        {showOnlyLobbyCustomizationPanel ? (
          <style>
            {`
              .lobby-only-customization-section {
                display: block !important;
                min-height: auto !important;
                overflow: visible !important;
              }

              .lobby-only-customization-section > :not(.lobby-customization-panel-target) {
                display: none !important;
              }

              .lobby-only-customization-section > .lobby-customization-panel-target {
                display: block !important;
                margin-left: auto !important;
                margin-right: auto !important;
                max-width: 760px !important;
                width: 100% !important;
              }
            `}
          </style>
        ) : null}
        <div
          className={`grid grid-cols-1 gap-4 xl:h-full xl:grid-cols-[minmax(0,1fr)_340px] [@media(max-width:1024px)_and_(orientation:landscape)]:h-full [@media(max-width:1024px)_and_(orientation:landscape)]:grid-cols-[minmax(0,1fr)_196px] ${
            isIphoneChromeLandscapePhone
              ? "overflow-y-auto [-webkit-overflow-scrolling:touch]"
              : ""
          }`}
          style={
            showOnlyLobbyCustomizationPanel
              ? {
                  display: "block",
                  margin: "0 auto",
                  maxWidth: 760,
                  width: "100%",
                }
              : isAdminTab
              ? { gridTemplateColumns: "minmax(0, 1fr)" }
              : centerRightGridStyle
          }
        >
          <section
            className={`mobile-landscape-center-text-scale mobile-landscape-safari-flex-parent mobile-landscape-safari-scroll flex min-h-0 flex-col gap-4 [@media(max-width:1024px)_and_(orientation:landscape)]:overflow-y-auto [@media(max-width:1024px)_and_(orientation:landscape)]:pr-1 [@media(max-width:1024px)_and_(orientation:landscape)]:pb-[calc(env(safe-area-inset-bottom)+120px)] ${
              showOnlyLobbyCustomizationPanel ? "lobby-only-customization-section" : ""
            } ${
              isIphoneChromeLandscapePhone
                ? "overflow-visible pr-0 pb-0"
                : ""
            }`}
            style={
              isIphoneChromeLandscapePhone
                ? {
                    overflowY: "visible",
                    overflowX: "visible",
                    paddingRight: 0,
                    paddingBottom: 0,
                    height: "auto",
                    minHeight: "auto",
                  }
                : undefined
            }
            data-sound-zone="left"
          >
            {!isAdminTab && hideLobbyCubeStartPanel && showLobbyModePanel ? lobbyModePanelReturn : null}
            {!isAdminTab && showCreateLobbyModePanelInMobileVideo ? lobbyModePanelReturn : null}
            {!isAdminTab && showLegacyMobileHome ? (
              <div className="xima-legacy-mobile-home mb-3 hidden [@media(max-width:1024px)_and_(orientation:portrait)]:block">
                <div className="xima-legacy-mobile-logo">
                  <img src="/mejor logo 336.png" alt={t("LOGO_XIMA_ALT")} />
                </div>

                <div className="xima-legacy-mobile-grid">
                  <button
                    type="button"
                    className="xima-legacy-mobile-tile xima-legacy-mobile-profile legacy-pos-profile"
                    onClick={openProfileModal}
                    aria-label={tx("Editar perfil", "Edit Profile")}
                  >
                    <span className="xima-legacy-day-disc">
                      <span>{i18nT("lobby.dia")}</span>
                      <small>{i18nT("lobby.resolviendoElPuzzle")}</small>
                    </span>
                    <strong>{displayName || "Jugador"}</strong>
                  </button>

                  <button
                    type="button"
                    className="xima-legacy-mobile-tile legacy-tone-gold legacy-pos-premium"
                    onClick={handlePremiumUnavailableClick}
                    aria-label={t("premium")}
                  >
                    <span className="xima-legacy-mobile-icon">
                      <IoDiamondOutline aria-hidden="true" />
                    </span>
                    <strong>{t("premium")}</strong>
                  </button>

                  {legacyMobileHomeTiles.map((tile) => {
                    const TileIcon = tile.icon;
                    return (
                      <button
                        key={tile.key}
                        type="button"
                        className={`xima-legacy-mobile-tile legacy-tone-${tile.tone} ${tile.position}`}
                        onClick={tile.onClick}
                        aria-label={tile.label}
                      >
                        <span className="xima-legacy-mobile-icon">
                          <TileIcon aria-hidden="true" />
                        </span>
                        <strong>{tile.label}</strong>
                      </button>
                    );
                  })}
                </div>

                <button
                  type="button"
                  className="xima-legacy-science-banner"
                  onClick={handleLegacyScienceClick}
                  aria-label={i18nT("lobby.cimaxCiencia")}
                >
                  <span className="xima-legacy-board-preview">
                    <img src="/assets/images/foto-tablero-ciencia.png" alt={i18nT("lobby.tableroXima")} />
                  </span>
                  <span className="xima-legacy-science-mark" aria-hidden="true">
                    <span className="science-link science-link-blue" />
                    <span className="science-link science-link-yellow" />
                    <span className="science-link science-link-white" />
                    <span className="science-link science-link-green" />
                    <span className="science-link science-link-red" />
                  </span>
                  <span className="xima-legacy-science-copy">
                    <span>CIENCIA</span>
                    <strong>{formatCimaxScoreValue(cimaxScores?.ciencia)} CIMAX</strong>
                  </span>
                </button>
              </div>
            ) : null}
            {!isHistoryTab &&
            !isAdminTab &&
            !showOnlyLobbyCustomizationPanel &&
            !hideLobbyCubeStartPanel &&
            !isLobbyFocusedModeRoute &&
            !showLegacyMobileHome ? (
              <div className="mb-3 hidden rounded-xl border border-[#24406f] bg-[#0a1530d9] p-3 shadow-[0_0_30px_rgba(0,0,0,0.35)] [@media(max-width:1024px)_and_(orientation:portrait)]:block">
                <div className="mobile-landscape-video-row relative rounded-lg border border-[#345a90] bg-[#08162fe6] p-2">
                  <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
                    <div className="flex min-w-0 flex-col items-stretch gap-2">
                      <div className="flex items-center justify-center rounded-lg border border-[#4acbff4d] bg-[#07132b] px-3 py-2">
                        <img
                          src="/mejor logo 336.png"
                          alt={t("LOGO_XIMA_ALT")}
                          className="h-6 w-auto object-contain"
                        />
                      </div>
                      <button
                        type="button"
                        className="mobile-home-cimax-button rounded-lg border border-[#d4d4d4] bg-white px-3 py-2 text-xs font-black uppercase tracking-[0.06em] text-[#1b1b1b] transition-all duration-200 hover:border-[#ffffff] hover:bg-[#f2f2f2] whitespace-nowrap"
                      >
                        <span className="flex items-center justify-center gap-1.5">
                          <img
                            src="/assets/images/monedacriptoxima.png"
                            alt={bonusesLabel}
                            className="h-4 w-4 object-contain [@media(max-width:1024px)_and_(orientation:portrait)]:hidden"
                          />
                          <span>
                            {bonusesLabel}: {walletBalance}X
                          </span>
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={handlePremiumUnavailableClick}
                        className="mobile-home-cimax-button rounded-lg border border-[#74dfff] bg-[#102c4f] px-3 py-2 text-xs font-black uppercase tracking-[0.06em] text-[#d6f3ff] transition-all duration-200 hover:border-[#a6ecff] hover:bg-[#16406f]"
                      >
                        <span className="flex items-center justify-center gap-1.5">
                          <IoDiamondOutline className="text-sm text-[#86ecff]" aria-hidden="true" />
                          <span>{t("premium")}</span>
                        </span>
                      </button>
                    </div>

                    <div className="flex min-h-[120px] items-center justify-center">
                      <img
                        src="/ximalogo.png"
                        alt="XIMA"
                        className="mobile-top-video max-h-[70px] w-auto object-contain"
                      />
                    </div>

                    <div className="flex min-w-0 flex-col items-stretch gap-2">
                      {startScreenCimaxButtons
                        .filter(
                          (entry) =>
                            entry.key === "ciencia" ||
                            entry.key === "cienzar" ||
                            entry.key === "azar"
                        )
                        .map((button) => (
                          <button
                            key={`top-${button.key}`}
                            type="button"
                            className={`mobile-home-cimax-button rounded-lg border px-3 py-2 text-xs font-black uppercase tracking-[0.06em] transition-all duration-200 ${button.className}`}
                          >
                            {(button.key === "ciencia"
                              ? tx("CIMAX CIENCIA", "CIMAX SCIENCE")
                              : button.key === "azar"
                              ? tx("CIMAX AZAR", "CIMAX RANDOM")
                              : button.label)}
                            : {button.score}
                          </button>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
            {!isHistoryTab &&
            !isAdminTab &&
            !hidePortraitCreateGameProfilePanel &&
            !isLobbyFocusedModeRoute &&
            !showLegacyMobileHome ? (
              <div
                className={`lobby-customization-panel-target mb-3 rounded-xl border border-[#24406f] bg-[#0a1530d9] p-3 shadow-[0_0_30px_rgba(0,0,0,0.35)] ${
                  showOnlyLobbyCustomizationPanel
                    ? "block"
                    : "hidden [@media(max-width:1024px)_and_(orientation:portrait)]:block"
                }`}
              >
                {hideLobbyCubeStartPanel ? (
                  <div className="flex flex-col gap-4">
                    {!hideLobbyCreateRegisteredPlayerCard ? (
                      <div className="mobile-landscape-player-card flex min-w-0 flex-col gap-3 overflow-hidden rounded-lg border border-[#4acbff4d] bg-[#07132b] p-4">
                        <div className="min-w-0">
                          <div className="text-[10px] uppercase tracking-[0.18em] text-[#8cb6ff]">
                            {tx("Jugador registrado", "Registered Player")}
                          </div>
                          <div className="mobile-top-player-name mt-0.5 whitespace-normal break-words text-[1.35rem] font-black leading-tight text-white">
                            {displayName}
                          </div>
                        </div>
                        <div className="mt-auto flex items-end justify-between gap-2">
                          <div className="mobile-top-avatar-box flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md border border-[#4acbff80] bg-[#123462] text-base font-black text-[#b9ebff]">
                            {currentAvatarUrl ? (
                              <img
                                src={currentAvatarUrl}
                                alt={displayName}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <span>{userInitials}</span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={openProfileModal}
                            className="rounded border border-[#4fc3ff88] bg-[#11305c] px-3.5 py-2 text-[12px] font-semibold uppercase tracking-wide text-[#b7e4ff] transition hover:border-[#7ee5ff] hover:text-white"
                          >
                            {tx("Editar perfil", "Edit Profile")}
                          </button>
                        </div>
                      </div>
                    ) : null}

                    <div className="mobile-landscape-special-card flex min-w-0 flex-col gap-3 overflow-hidden rounded-lg border border-[#4fc3ff66] bg-[#0d2a4a] p-4">
                      <div className="min-w-0">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8fd4ff]">
                          {tx("Bloque especial", "Special Block")}
                        </div>
                        <div className="mt-0.5 whitespace-normal break-words text-[14px] font-black uppercase leading-tight tracking-wide text-[#e2f3ff]">
                          {selectedSpecialBlock.id === "default_star"
                            ? tx("Estrella por defecto", "Default Star")
                            : selectedSpecialBlock.label}
                        </div>
                      </div>
                      <div className="mt-auto flex items-end justify-between gap-2">
                        <img
                          src={selectedSpecialBlock.image}
                          alt={selectedSpecialBlock.id}
                          className="mobile-top-special-image h-20 w-20 shrink-0 rounded border border-[#66c6ff88] bg-[#12345a] object-cover"
                        />
                        <button
                          type="button"
                          onClick={openSpecialBlockModal}
                          className="rounded border border-[#4fc3ff88] bg-[#11305c] px-3.5 py-2 text-[12px] font-semibold uppercase tracking-wide text-[#b7e4ff] transition hover:border-[#7ee5ff] hover:text-white"
                        >
                          {tx("Editar especial", "Edit Special")}
                        </button>
                      </div>
                    </div>

                    <div className="mobile-landscape-common-card flex min-w-0 flex-col gap-3 overflow-hidden rounded-lg border border-[#4fc3ff66] bg-[#0d2a4a] p-4">
                      <div className="min-w-0">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8fd4ff]">
                          {tx("Bloques comunes", "Common Blocks")}
                        </div>
                        <div className="mt-0.5 whitespace-normal break-words text-[14px] font-black uppercase leading-tight tracking-wide text-[#e2f3ff]">
                          {selectedCommonBlockTheme.label}
                        </div>
                      </div>
                      <div className="mt-auto flex items-end justify-between gap-2">
                        <img
                          src={selectedCommonBlockTheme.image}
                          alt={selectedCommonBlockTheme.id}
                          className="mobile-top-common-image h-20 w-20 shrink-0 rounded border border-[#66c6ff88] bg-[#12345a] object-cover"
                        />
                        <button
                          type="button"
                          onClick={openCommonBlocksModal}
                          className="rounded border border-[#4fc3ff88] bg-[#11305c] px-3.5 py-2 text-[12px] font-semibold uppercase tracking-wide text-[#b7e4ff] transition hover:border-[#7ee5ff] hover:text-white"
                        >
                          {tx("Editar bloques", "Edit Blocks")}
                        </button>
                      </div>
                    </div>

                    <div className="mobile-landscape-board-card flex min-w-0 flex-col gap-3 overflow-hidden rounded-lg border border-[#4fc3ff66] bg-[#0d2a4a] p-4">
                      <div className="min-w-0">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8fd4ff]">
                          {tx("Tablero Xima", "Xima Board")}
                        </div>
                        <div className="mt-0.5 whitespace-normal break-words text-[14px] font-black uppercase leading-tight tracking-wide text-[#e2f3ff]">
                          {selectedBoardTheme.label}
                        </div>
                      </div>
                      <div className="mt-auto flex items-end justify-between gap-2">
                        <div className="mobile-top-board-box flex h-20 w-20 shrink-0 items-center justify-center rounded border border-[#66c6ff88] bg-[#0d223f]">
                          <div
                            className="h-11 w-11 rounded border border-[#ffffff2a]"
                            style={getBoardDualSquaresStyle(selectedBoardTheme)}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={openBoardThemeModal}
                          className="rounded border border-[#4fc3ff88] bg-[#11305c] px-3.5 py-2 text-[12px] font-semibold uppercase tracking-wide text-[#b7e4ff] transition hover:border-[#7ee5ff] hover:text-white"
                        >
                          {tx("Editar tablero", "Edit Board")}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 items-stretch gap-2">
                    <div className="flex h-full min-w-0 flex-col gap-2">
                      {!hideLobbyCreateRegisteredPlayerCard ? (
                      <div className="mobile-landscape-player-card flex min-w-0 flex-1 flex-col gap-1.5 overflow-hidden rounded-lg border border-[#4acbff4d] bg-[#07132b] p-1.5">
                        <div className="min-w-0">
                          <div className="text-[9px] uppercase tracking-[0.18em] text-[#8cb6ff]">
                            {tx("Jugador registrado", "Registered Player")}
                          </div>
                          <div className="mobile-top-player-name mt-0.5 whitespace-normal break-words text-[0.95rem] font-black leading-tight text-white">
                            {displayName}
                          </div>
                        </div>
                        <div className="mt-auto flex items-end justify-between gap-1.5">
                          <div className="mobile-top-avatar-box flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md border border-[#4acbff80] bg-[#123462] text-base font-black text-[#b9ebff]">
                            {currentAvatarUrl ? (
                              <img
                                src={currentAvatarUrl}
                                alt={displayName}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <span>{userInitials}</span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={openProfileModal}
                            className="rounded border border-[#4fc3ff88] bg-[#11305c] px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#b7e4ff] transition hover:border-[#7ee5ff] hover:text-white"
                          >
                            {tx("Editar perfil", "Edit Profile")}
                          </button>
                        </div>
                      </div>
                      ) : null}

                      <div className="mobile-landscape-board-card flex min-w-0 flex-1 flex-col gap-1.5 overflow-hidden rounded-lg border border-[#4fc3ff66] bg-[#0d2a4a] p-1.5">
                        <div className="min-w-0">
                          <div className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#8fd4ff]">
                            {tx("Tablero Xima", "Xima Board")}
                          </div>
                          <div className="mt-0.5 whitespace-normal break-words text-[10px] font-black uppercase leading-tight tracking-wide text-[#e2f3ff]">
                            {selectedBoardTheme.label}
                          </div>
                        </div>
                        <div className="mt-auto flex items-end justify-between gap-1.5">
                          <div className="mobile-top-board-box flex h-14 w-14 shrink-0 items-center justify-center rounded border border-[#66c6ff88] bg-[#0d223f]">
                            <div
                              className="h-9 w-9 rounded border border-[#ffffff2a]"
                              style={getBoardDualSquaresStyle(selectedBoardTheme)}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={openBoardThemeModal}
                            className="rounded border border-[#4fc3ff88] bg-[#11305c] px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#b7e4ff] transition hover:border-[#7ee5ff] hover:text-white"
                          >
                            {tx("Editar tablero", "Edit Board")}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex h-full min-w-0 flex-col gap-2">
                      <div className="mobile-landscape-special-card flex min-w-0 flex-1 flex-col gap-1.5 overflow-hidden rounded-lg border border-[#4fc3ff66] bg-[#0d2a4a] p-1.5">
                        <div className="min-w-0">
                          <div className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#8fd4ff]">
                            {tx("Bloque especial", "Special Block")}
                          </div>
                            <div className="mt-0.5 whitespace-normal break-words text-[10px] font-black uppercase leading-tight tracking-wide text-[#e2f3ff]">
                              {selectedSpecialBlock.id === "default_star"
                                ? tx("Estrella por defecto", "Default Star")
                                : selectedSpecialBlock.label}
                            </div>
                        </div>
                        <div className="mt-auto flex items-end justify-between gap-1.5">
                          <img
                            src={selectedSpecialBlock.image}
                            alt={selectedSpecialBlock.id}
                            className="mobile-top-special-image h-14 w-14 shrink-0 rounded border border-[#66c6ff88] bg-[#12345a] object-cover"
                          />
                          <button
                            type="button"
                            onClick={openSpecialBlockModal}
                            className="rounded border border-[#4fc3ff88] bg-[#11305c] px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#b7e4ff] transition hover:border-[#7ee5ff] hover:text-white"
                          >
                            {tx("Editar especial", "Edit Special")}
                          </button>
                        </div>
                      </div>

                      <div className="mobile-landscape-common-card flex min-w-0 flex-1 flex-col gap-1.5 overflow-hidden rounded-lg border border-[#4fc3ff66] bg-[#0d2a4a] p-1.5">
                        <div className="min-w-0">
                          <div className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#8fd4ff]">
                            {tx("Bloques comunes", "Common Blocks")}
                          </div>
                          <div className="mt-0.5 whitespace-normal break-words text-[10px] font-black uppercase leading-tight tracking-wide text-[#e2f3ff]">
                            {selectedCommonBlockTheme.label}
                          </div>
                        </div>
                        <div className="mt-auto flex items-end justify-between gap-1.5">
                          <img
                            src={selectedCommonBlockTheme.image}
                            alt={selectedCommonBlockTheme.id}
                            className="mobile-top-common-image h-14 w-14 shrink-0 rounded border border-[#66c6ff88] bg-[#12345a] object-cover"
                          />
                          <button
                            type="button"
                            onClick={openCommonBlocksModal}
                            className="rounded border border-[#4fc3ff88] bg-[#11305c] px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#b7e4ff] transition hover:border-[#7ee5ff] hover:text-white"
                          >
                            {tx("Editar bloques", "Edit Blocks")}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : null}

            {!isAdminTab ? (
            <div
              className={
                isHistoryTab
                  ? "xima-history-shell"
                  : "rounded-xl border border-[#24406f] bg-[#0a1530d9] p-4 shadow-[0_0_30px_rgba(0,0,0,0.35)] [@media(max-width:1024px)_and_(orientation:portrait)]:hidden"
              }
            >
              <div
                className={
                  isHistoryTab
                    ? "flex flex-wrap items-center justify-between gap-3"
                    : "w-full"
                }
              >
                {!isHistoryTab ? (
                  <div className="mobile-landscape-home-top-grid grid grid-cols-1 gap-3 [@media(max-width:1024px)_and_(orientation:portrait)]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] [@media(max-width:1024px)_and_(orientation:portrait)]:items-start [@media(max-width:1024px)_and_(orientation:portrait)]:gap-x-3 lg:grid-cols-[minmax(220px,340px)_minmax(220px,1fr)_minmax(220px,340px)] lg:items-center lg:gap-x-6">
                    <div
                      className="mobile-landscape-home-top-row flex flex-col gap-2 [@media(max-width:1024px)_and_(orientation:portrait)]:order-2 [@media(max-width:1024px)_and_(orientation:portrait)]:w-full [@media(max-width:1024px)_and_(orientation:portrait)]:hidden"
                      style={{ display: hideLobbyCreateMobilePanels ? "none" : undefined }}
                    >
                      {!hideLobbyCreateRegisteredPlayerCard ? (
                      <div className="mobile-landscape-player-card flex min-w-0 items-center gap-2 overflow-hidden rounded-lg border border-[#4acbff4d] bg-[#07132b] p-2 [@media(max-width:1024px)_and_(orientation:portrait)]:w-full [@media(max-width:1024px)_and_(orientation:portrait)]:gap-1.5 [@media(max-width:1024px)_and_(orientation:portrait)]:p-1.5">
                        <div className="mobile-top-avatar-box flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md border border-[#4acbff80] bg-[#123462] text-base font-black text-[#b9ebff] [@media(max-width:1024px)_and_(orientation:portrait)]:h-16 [@media(max-width:1024px)_and_(orientation:portrait)]:w-16">
                          {currentAvatarUrl ? (
                            <img
                              src={currentAvatarUrl}
                              alt={displayName}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <span>{userInitials}</span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="text-[9px] uppercase tracking-[0.18em] text-[#8cb6ff]">
                            {tx("Jugador registrado", "Registered Player")}
                          </div>
                          <div className="mobile-top-player-name truncate text-[1.5rem] font-black leading-none text-white [@media(max-width:1024px)]:text-[2.5rem]">
                            {displayName}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={openProfileModal}
                              className="rounded border border-[#4fc3ff88] bg-[#11305c] px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#b7e4ff] transition hover:border-[#7ee5ff] hover:text-white"
                            >
                              {tx("Editar perfil", "Edit Profile")}
                            </button>
                          </div>
                        </div>
                      </div>
                      ) : null}

                      <div className="mobile-landscape-board-card flex min-w-0 items-center gap-2 overflow-hidden rounded-lg border border-[#4fc3ff66] bg-[#0d2a4a] p-2 [@media(max-width:1024px)_and_(orientation:portrait)]:w-full [@media(max-width:1024px)_and_(orientation:portrait)]:gap-1.5 [@media(max-width:1024px)_and_(orientation:portrait)]:p-1.5">
                        <div className="mobile-top-board-box flex h-16 w-16 shrink-0 items-center justify-center rounded border border-[#66c6ff88] bg-[#0d223f] [@media(max-width:1024px)_and_(orientation:portrait)]:h-14 [@media(max-width:1024px)_and_(orientation:portrait)]:w-14">
                          <div
                            className="h-10 w-10 rounded border border-[#ffffff2a] [@media(max-width:1024px)_and_(orientation:portrait)]:h-9 [@media(max-width:1024px)_and_(orientation:portrait)]:w-9"
                            style={getBoardDualSquaresStyle(selectedBoardTheme)}
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#8fd4ff]">
                            {tx("Tablero Xima", "Xima Board")}
                          </div>
                          <div className="truncate text-xs font-black uppercase tracking-wide text-[#e2f3ff]">
                            {selectedBoardTheme.label}
                          </div>
                          <button
                            type="button"
                            onClick={openBoardThemeModal}
                            className="mt-1 rounded border border-[#4fc3ff88] bg-[#11305c] px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#b7e4ff] transition hover:border-[#7ee5ff] hover:text-white"
                          >
                            {tx("Editar tablero", "Edit Board")}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="mobile-landscape-video-row relative flex min-h-[120px] flex-col items-center justify-center gap-2 rounded-lg border border-[#345a90] bg-[#08162fe6] p-2 [@media(max-width:1024px)_and_(orientation:portrait)]:order-1 [@media(max-width:1024px)_and_(orientation:portrait)]:hidden">
                      <img
                        src="/ximalogo.png"
                        alt="XIMA"
                        className="mobile-top-video max-h-[70px] w-auto object-contain"
                      />
                      <button
                        type="button"
                        className="mobile-home-cimax-button mt-1 rounded-lg border border-[#d4d4d4] bg-white px-3 py-2 text-xs font-black uppercase tracking-[0.06em] text-[#1b1b1b] transition-all duration-200 hover:border-[#ffffff] hover:bg-[#f2f2f2] whitespace-nowrap lg:absolute lg:bottom-2 lg:left-2 lg:mt-0"
                      >
                        <span className="flex items-center justify-center gap-1.5">
                          <img
                            src="/assets/images/monedacriptoxima.png"
                            alt={bonusesLabel}
                            className="h-4 w-4 object-contain [@media(max-width:1024px)_and_(orientation:portrait)]:hidden"
                          />
                          <span>
                            {bonusesLabel}: {walletBalance}X
                          </span>
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={handlePremiumUnavailableClick}
                        className="mobile-home-cimax-button absolute bottom-2 right-2 hidden rounded-lg border border-[#74dfff] bg-[#102c4f] px-3 py-2 text-xs font-black uppercase tracking-[0.06em] text-[#d6f3ff] transition-all duration-200 hover:border-[#a6ecff] hover:bg-[#16406f] [@media(min-width:1025px)]:inline-flex"
                      >
                        <span className="flex items-center justify-center gap-1.5">
                          <IoDiamondOutline className="text-sm text-[#86ecff]" aria-hidden="true" />
                          <span>{t("premium")}</span>
                        </span>
                      </button>
                    </div>

                    <div
                      className="mobile-landscape-home-bottom-row flex flex-col gap-2 [@media(max-width:1024px)_and_(orientation:portrait)]:order-3 [@media(max-width:1024px)_and_(orientation:portrait)]:w-full [@media(max-width:1024px)_and_(orientation:portrait)]:hidden"
                      style={{ display: hideLobbyCreateMobilePanels ? "none" : undefined }}
                    >
                      <div className="mobile-landscape-special-card flex min-w-0 items-center gap-2 overflow-hidden rounded-lg border border-[#4fc3ff66] bg-[#0d2a4a] p-2 [@media(max-width:1024px)_and_(orientation:portrait)]:w-full [@media(max-width:1024px)_and_(orientation:portrait)]:gap-1.5 [@media(max-width:1024px)_and_(orientation:portrait)]:p-1.5">
                        <img
                          src={selectedSpecialBlock.image}
                          alt={selectedSpecialBlock.id}
                          className="mobile-top-special-image h-16 w-16 shrink-0 rounded border border-[#66c6ff88] bg-[#12345a] object-cover [@media(max-width:1024px)_and_(orientation:portrait)]:h-14 [@media(max-width:1024px)_and_(orientation:portrait)]:w-14"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#8fd4ff]">
                            {tx("Bloque especial", "Special Block")}
                          </div>
                          <div className="truncate text-xs font-black uppercase tracking-wide text-[#e2f3ff]">
                            {selectedSpecialBlock.id === "default_star"
                              ? tx("Estrella por defecto", "Default Star")
                              : selectedSpecialBlock.label}
                          </div>
                          <button
                            type="button"
                            onClick={openSpecialBlockModal}
                            className="mt-1 rounded border border-[#4fc3ff88] bg-[#11305c] px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#b7e4ff] transition hover:border-[#7ee5ff] hover:text-white"
                          >
                            {tx("Editar especial", "Edit Special")}
                          </button>
                        </div>
                      </div>

                      <div className="mobile-landscape-common-card flex min-w-0 items-center gap-2 overflow-hidden rounded-lg border border-[#4fc3ff66] bg-[#0d2a4a] p-2 [@media(max-width:1024px)_and_(orientation:portrait)]:w-full [@media(max-width:1024px)_and_(orientation:portrait)]:gap-1.5 [@media(max-width:1024px)_and_(orientation:portrait)]:p-1.5">
                        <img
                          src={selectedCommonBlockTheme.image}
                          alt={selectedCommonBlockTheme.id}
                          className="mobile-top-common-image h-16 w-16 shrink-0 rounded border border-[#66c6ff88] bg-[#12345a] object-cover [@media(max-width:1024px)_and_(orientation:portrait)]:h-14 [@media(max-width:1024px)_and_(orientation:portrait)]:w-14"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#8fd4ff]">
                            {tx("Bloques comunes", "Common Blocks")}
                          </div>
                          <div className="truncate text-xs font-black uppercase tracking-wide text-[#e2f3ff]">
                            {selectedCommonBlockTheme.label}
                          </div>
                          <button
                            type="button"
                            onClick={openCommonBlocksModal}
                            className="mt-1 rounded border border-[#4fc3ff88] bg-[#11305c] px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#b7e4ff] transition hover:border-[#7ee5ff] hover:text-white"
                          >
                            {tx("Editar bloques", "Edit Blocks")}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}

                {isHistoryTab ? (
                  <div className="xima-history-hero">
                    <div className="xima-history-title-wrap">
                      <div className="xima-history-title-icon">
                        <IoTimeOutline aria-hidden="true" />
                      </div>
                      <div className="min-w-0">
                        <h1 className="xima-history-title">{historyText("title")}</h1>
                        <p className="xima-history-subtitle">
                          {historyText("subtitle")}
                        </p>
                      </div>
                    </div>
                    <div className="xima-history-actions">
                      <button
                        type="button"
                        onClick={() => navigate("/game/stats")}
                        className="xima-history-top-action xima-history-top-action-blue"
                      >
                        <IoStatsChartOutline aria-hidden="true" />
                        {historyText("viewStats")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setHistoryShowAllMatches((prev) => !prev)}
                        className="xima-history-top-action xima-history-top-action-blue"
                      >
                        <IoListOutline aria-hidden="true" />
                        {historyShowAllMatches
                          ? historyText("viewPaginated")
                          : historyText("viewAll")}
                      </button>
                      <button
                        type="button"
                        onClick={() => fetchHistory()}
                        className="xima-history-top-action xima-history-top-action-purple"
                      >
                        <IoRefreshOutline aria-hidden="true" />
                        {historyText("refreshHistory")}
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
            ) : null}

            {!isAdminTab && shouldEmbedLobbyDesignEditor ? (
              <div className="hidden min-h-[520px] rounded-xl border border-[#24406f] bg-[#0a1530d9] p-3 shadow-[0_0_30px_rgba(0,0,0,0.35)] [@media(min-width:1025px)]:block">
                <div className="h-[clamp(420px,54vh,640px)] overflow-y-auto rounded-lg bg-[#1f1f1f] p-4 text-white">
                  {!lobbyDesignActiveEditor ? (
                    <div className="flex h-full min-h-[360px] items-center justify-center rounded-lg border border-dashed border-[#355b90] bg-[#0a1a35] p-6 text-center">
                      <div>
                        <div className="text-sm font-black uppercase tracking-[0.22em] text-[#8fd4ff]">{i18nT("lobby.modoDisenador")}</div>
                        <div className="mt-2 text-2xl font-black uppercase tracking-[0.08em] text-white">{i18nT("lobby.eligeUnaHerramienta")}</div>
                        <div className="mx-auto mt-3 max-w-xl text-sm leading-6 text-[#a9c4e9]">{i18nT("lobby.presionaEditarPerfilEditarEspecialEditarBloq759f4a")}</div>
                      </div>
                    </div>
                  ) : null}

                  {lobbyDesignActiveEditor === "profile" ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 rounded-lg border border-[#21436d] bg-[#0a1a35] p-3">
                        <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-lg border border-[#4acbff80] bg-[#123462] text-2xl font-black text-[#b9ebff]">
                          {profileDraftAvatar ? (
                            <img
                              src={profileDraftAvatar}
                              alt={`${displayName} ${i18nT("common.profileAvatarPlaceholder")}`}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <span>{userInitials}</span>
                          )}
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-white">{displayName}</div>
                          <div className="mt-1 text-xs text-[#8fd4ff]">{i18nT("lobby.editaTuInformacionYSeleccionaTuFotoDe")}</div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                        <div className="space-y-4 rounded-lg border border-[#21436d] bg-[#0a1a35] p-3">
                          <div className="text-xs font-bold uppercase tracking-[0.16em] text-[#8fd4ff]">{i18nT("lobby.informacionPersonal")}</div>
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <label className="block">
                              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#b5d9ff]">{i18nT("lobby.nombre")}</span>
                              <input
                                type="text"
                                value={profileDraftFirstName}
                                onChange={(event) =>
                                  setProfileDraftFirstName(
                                    String(event?.target?.value || "").slice(0, PROFILE_NAME_MAX_LENGTH)
                                  )
                                }
                                className="w-full rounded border border-[#355b90] bg-[#0f264b] px-3 py-2 text-sm text-[#ecf6ff] outline-none transition focus:border-[#79d8ff]"
                                placeholder={i18nT("lobby.tuNombre")}
                                autoComplete="given-name"
                              />
                            </label>
                            <label className="block">
                              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#b5d9ff]">{i18nT("lobby.apellido")}</span>
                              <input
                                type="text"
                                value={profileDraftLastName}
                                onChange={(event) =>
                                  setProfileDraftLastName(
                                    String(event?.target?.value || "").slice(0, PROFILE_NAME_MAX_LENGTH)
                                  )
                                }
                                className="w-full rounded border border-[#355b90] bg-[#0f264b] px-3 py-2 text-sm text-[#ecf6ff] outline-none transition focus:border-[#79d8ff]"
                                placeholder={i18nT("lobby.tuApellido")}
                                autoComplete="family-name"
                              />
                            </label>
                          </div>

                          <div className="grid grid-cols-1 gap-3">
                            <label className="block">
                              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#b5d9ff]">{i18nT("lobby.correoOpcional")}</span>
                              <input
                                type="email"
                                value={profileDraftContactEmail}
                                onChange={(event) =>
                                  setProfileDraftContactEmail(
                                    String(event?.target?.value || "")
                                      .slice(0, PROFILE_CONTACT_EMAIL_MAX_LENGTH)
                                      .toLowerCase()
                                  )
                                }
                                className="w-full rounded border border-[#355b90] bg-[#0f264b] px-3 py-2 text-sm text-[#ecf6ff] outline-none transition focus:border-[#79d8ff]"
                                placeholder={i18nT("profile.correoEjemploCom")}
                                autoComplete="email"
                              />
                            </label>
                          </div>

                          <div className="rounded border border-[#2d4771] bg-[#0d213f] p-3">
                            <div className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-[#8fd4ff]">{i18nT("lobby.cambiarContrasenaOpcional")}</div>
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                              <label className="block">
                                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#b5d9ff]">{i18nT("lobby.actual")}</span>
                                <input
                                  type="password"
                                  value={profileDraftCurrentPassword}
                                  onChange={(event) =>
                                    setProfileDraftCurrentPassword(event?.target?.value || "")
                                  }
                                  className="w-full rounded border border-[#355b90] bg-[#0f264b] px-3 py-2 text-sm text-[#ecf6ff] outline-none transition focus:border-[#79d8ff]"
                                  placeholder={i18nT("lobby.contrasenaActual")}
                                  autoComplete="current-password"
                                />
                              </label>
                              <label className="block">
                                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#b5d9ff]">{i18nT("lobby.nueva")}</span>
                                <input
                                  type="password"
                                  value={profileDraftNewPassword}
                                  onChange={(event) =>
                                    setProfileDraftNewPassword(event?.target?.value || "")
                                  }
                                  className="w-full rounded border border-[#355b90] bg-[#0f264b] px-3 py-2 text-sm text-[#ecf6ff] outline-none transition focus:border-[#79d8ff]"
                                  placeholder={i18nT("lobby.nuevaContrasena")}
                                  autoComplete="new-password"
                                />
                              </label>
                              <label className="block">
                                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#b5d9ff]">{i18nT("lobby.confirmar")}</span>
                                <input
                                  type="password"
                                  value={profileDraftConfirmPassword}
                                  onChange={(event) =>
                                    setProfileDraftConfirmPassword(event?.target?.value || "")
                                  }
                                  className="w-full rounded border border-[#355b90] bg-[#0f264b] px-3 py-2 text-sm text-[#ecf6ff] outline-none transition focus:border-[#79d8ff]"
                                  placeholder={i18nT("lobby.repiteLaContrasena")}
                                  autoComplete="new-password"
                                />
                              </label>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-4 rounded-lg border border-[#21436d] bg-[#0a1a35] p-3">
                          <div>
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-[#8fd4ff]">{i18nT("lobby.subirFoto")}</label>
                            <label className="inline-flex cursor-pointer items-center rounded border border-[#4fc3ff88] bg-[#11305c] px-3 py-2 text-sm font-semibold text-[#d7efff] transition hover:border-[#7ee5ff]">
                              {i18nT("lobby.elegirArchivo")}
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleAvatarUpload}
                              />
                            </label>
                            <div className="mt-1 text-xs text-[#92a9d2]">{i18nT("lobby.formatoRecomendadoJpgPngMaximoMb")}</div>
                          </div>

                          <div>
                            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8fd4ff]">{i18nT("lobby.avataresPreestablecidos")}</div>
                            <div className="max-h-[36vh] overflow-y-auto pr-1">
                              <div className="grid grid-cols-2 gap-2">
                                {AI_AVATAR_PRESETS.map((preset) => {
                                  const selected =
                                    stripAssetVersion(profileDraftAvatar) ===
                                    stripAssetVersion(preset.src);
                                  return (
                                    <button
                                      key={`embedded-profile-avatar-${preset.id}`}
                                      type="button"
                                      onClick={() => {
                                        setProfileDraftAvatar(preset.src);
                                        setProfileDraftAvatarFile(null);
                                      }}
                                      className={`rounded border p-1 transition ${
                                        selected
                                          ? "border-[#51ff74] bg-[#193b2a]"
                                          : "border-[#3c5f8f] bg-[#0f2448] hover:border-[#8fd4ff]"
                                      }`}
                                    >
                                      <img
                                        src={preset.src}
                                        alt={preset.name}
                                        className="h-24 w-full rounded object-cover"
                                        onError={(event) => {
                                          event.currentTarget.style.display = "none";
                                        }}
                                      />
                                      <div className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-[#bfdcff]">
                                        {preset.name}
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 pt-2 md:grid-cols-4">
                        <button
                          type="button"
                          onClick={() => {
                            setProfileDraftAvatar("");
                            setProfileDraftAvatarFile(null);
                          }}
                          disabled={profileSavingPersonal}
                          className="rounded border border-[#6f7ea0] bg-[#1b2a4a] px-2 py-2 text-xs font-semibold uppercase text-[#ccdaf7] transition hover:border-[#9fb4e6] disabled:cursor-not-allowed disabled:opacity-60"
                        >{i18nT("lobby.sinFoto")}</button>
                        <button
                          type="button"
                          onClick={resetProfileAvatar}
                          disabled={profileSavingPersonal}
                          className="rounded border border-[#d68d4a] bg-[#4f2f17] px-2 py-2 text-xs font-semibold uppercase text-[#ffd8ae] transition hover:border-[#ffb86b] disabled:cursor-not-allowed disabled:opacity-60"
                        >{i18nT("lobby.restablecer")}</button>
                        <button
                          type="button"
                          onClick={closeProfileModal}
                          disabled={profileSavingPersonal}
                          className="rounded border border-[#5f6f95] bg-[#192742] px-2 py-2 text-xs font-semibold uppercase text-[#c6d8ff] transition hover:border-[#8ea3d4] disabled:cursor-not-allowed disabled:opacity-60"
                        >{i18nT("lobby.cancelar")}</button>
                        <button
                          type="button"
                          onClick={saveProfileChanges}
                          disabled={profileSavingPersonal}
                          className="rounded border border-[#33d368] bg-[#14662f] px-2 py-2 text-xs font-semibold uppercase text-[#cbffe0] transition hover:border-[#6bf0a1] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {profileSavingPersonal ? i18nT("lobby.guardando") : i18nT("lobby.guardarPerfil")}
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {lobbyDesignActiveEditor === "special" ? (
                    <div className="space-y-4">
                      <div>
                        <div className="text-lg font-semibold text-[#8fd4ff]">{i18nT("lobby.bloqueEspecial")}</div>
                        <div className="text-sm text-[#a9c4e9]">{i18nT("lobby.eligeElBloqueEspecialParaTuPerfil")}</div>
                      </div>
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
                        {SKIN_SECTION_ORDER.map((section) => {
                          const entries = ensureArray(specialSkinSections?.[section.key]);
                          return (
                            <div
                              key={`embedded-special-section-${section.key}`}
                              className="min-h-0 rounded border border-[#2c4a78] bg-[#0b1b38] p-2"
                            >
                              <div className="mb-2 flex items-center justify-between">
                                <div className={`text-xs font-bold uppercase tracking-[0.14em] ${section.accent}`}>
                                  {section.label}
                                </div>
                                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#9ac6f7]">
                                  {entries.length}
                                </div>
                              </div>
                              {entries.length === 0 ? (
                                <div className="text-[11px] text-[#7fa2d3]">{i18nT("lobby.sinSkinsEnEstaCategoria")}</div>
                              ) : (
                                <div className="h-[34vh] max-h-[360px] min-h-[220px] space-y-2 overflow-y-auto pr-1">
                                  {entries.map((entry) => {
                                    const preset = entry?.preset || {};
                                    const isSelected = draftSpecialBlockId === preset.id;
                                    const isLocked = entry?.requiredProduct?.id && !entry?.unlocked;
                                    const accessType = entry?.accessType || "";
                                    const productLabel =
                                      String(
                                        entry?.resolvedProduct?.name?.es ||
                                          entry?.resolvedProduct?.name?.en ||
                                          entry?.requiredProduct?.id ||
                                          ""
                                      ).trim() || "Producto";
                                    const accessLabel =
                                      accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER
                                        ? entry?.unlocked
                                          ? "Habilitado"
                                          : `Ganada: requiere Top #1 ${
                                              MODE_LABELS[
                                                String(entry?.requiredProduct?.earnedMode || "") === "azar"
                                                  ? "dice"
                                                  : String(entry?.requiredProduct?.earnedMode || "")
                                              ] || ""
                                            }`.trim()
                                        : entry?.requiredProduct?.id
                                          ? entry?.unlocked
                                            ? "Desbloqueado"
                                            : `De pago: ${productLabel}`
                                          : "Gratis";

                                    return (
                                      <button
                                        key={`embedded-special-card-${preset.id}`}
                                        type="button"
                                        onClick={() => handleDraftSpecialSkinSelection(preset.id)}
                                        className={`w-full rounded border p-2 text-left transition ${
                                          isSelected
                                            ? "border-[#4dff92] bg-[#143927]"
                                            : isLocked && accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER
                                              ? "border-[#b679f8] bg-[#30174f] hover:border-[#d7afff]"
                                              : isLocked
                                                ? "border-[#d2a04f] bg-[#3f2e12] hover:border-[#ffcf72]"
                                                : "border-[#3c5f8f] bg-[#0f2448] hover:border-[#8fd4ff]"
                                        }`}
                                      >
                                        <div className="mb-2 flex items-center gap-2">
                                          <img
                                            src={preset.image}
                                            alt={preset.label}
                                            className="h-12 w-12 rounded border border-[#66c6ff88] bg-[#12345a] object-cover"
                                          />
                                          <div
                                            className="min-w-0 truncate text-xs font-semibold uppercase tracking-wide text-[#d5ecff]"
                                            title={preset.label}
                                          >
                                            {preset.label}
                                          </div>
                                        </div>
                                        <div
                                          className="truncate text-[10px] font-semibold uppercase tracking-[0.14em] text-[#b9d7ff]"
                                          title={accessLabel}
                                        >
                                          {accessLabel}
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div className="grid grid-cols-2 gap-2 pt-1">
                        <button
                          type="button"
                          onClick={closeSpecialBlockModal}
                          className="rounded border border-[#6f7ea0] bg-[#1b2a4a] px-2 py-2 text-xs font-semibold uppercase text-[#ccdaf7] transition hover:border-[#9fb4e6]"
                        >{i18nT("lobby.cancelar")}</button>
                        <button
                          type="button"
                          onClick={saveSpecialBlockSelection}
                          className="rounded border border-[#33d368] bg-[#14662f] px-2 py-2 text-xs font-semibold uppercase text-[#cbffe0] transition hover:border-[#6bf0a1]"
                        >{i18nT("lobby.guardar")}</button>
                      </div>
                    </div>
                  ) : null}

                  {lobbyDesignActiveEditor === "common" ? (
                    <div className="space-y-4">
                      <div>
                        <div className="text-lg font-semibold text-[#8fd4ff]">{i18nT("lobby.bloquesComunes")}</div>
                        <div className="text-sm text-[#a9c4e9]">{i18nT("lobby.seleccionaLaImagenQueQuieresUsarParaLos")}</div>
                      </div>
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
                        {SKIN_SECTION_ORDER.map((section) => {
                          const entries = ensureArray(commonSkinSections?.[section.key]);
                          return (
                            <div
                              key={`embedded-common-section-${section.key}`}
                              className="min-h-0 rounded border border-[#2c4a78] bg-[#0b1b38] p-2"
                            >
                              <div className="mb-2 flex items-center justify-between">
                                <div className={`text-xs font-bold uppercase tracking-[0.14em] ${section.accent}`}>
                                  {section.label}
                                </div>
                                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#9ac6f7]">
                                  {entries.length}
                                </div>
                              </div>
                              {entries.length === 0 ? (
                                <div className="text-[11px] text-[#7fa2d3]">{i18nT("lobby.sinSkinsEnEstaCategoria")}</div>
                              ) : (
                                <div className="h-[34vh] max-h-[360px] min-h-[220px] space-y-2 overflow-y-auto pr-1">
                                  {entries.map((entry) => {
                                    const preset = entry?.preset || {};
                                    const isSelected = draftCommonBlockThemeId === preset.id;
                                    const isLocked = entry?.requiredProduct?.id && !entry?.unlocked;
                                    const accessType = entry?.accessType || "";
                                    const productLabel =
                                      String(
                                        entry?.resolvedProduct?.name?.es ||
                                          entry?.resolvedProduct?.name?.en ||
                                          entry?.requiredProduct?.id ||
                                          ""
                                      ).trim() || "Producto";
                                    const accessLabel =
                                      accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER
                                        ? entry?.unlocked
                                          ? "Habilitado"
                                          : `Ganada: requiere Top #1 ${
                                              MODE_LABELS[
                                                String(entry?.requiredProduct?.earnedMode || "") === "azar"
                                                  ? "dice"
                                                  : String(entry?.requiredProduct?.earnedMode || "")
                                              ] || ""
                                            }`.trim()
                                        : entry?.requiredProduct?.id
                                          ? entry?.unlocked
                                            ? "Desbloqueado"
                                            : `De pago: ${productLabel}`
                                          : "Gratis";

                                    return (
                                      <button
                                        key={`embedded-common-card-${preset.id}`}
                                        type="button"
                                        onClick={() => handleDraftCommonSkinSelection(preset.id)}
                                        className={`w-full rounded border p-2 text-left transition ${
                                          isSelected
                                            ? "border-[#4dff92] bg-[#143927]"
                                            : isLocked && accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER
                                              ? "border-[#b679f8] bg-[#30174f] hover:border-[#d7afff]"
                                              : isLocked
                                                ? "border-[#d2a04f] bg-[#3f2e12] hover:border-[#ffcf72]"
                                                : "border-[#3c5f8f] bg-[#0f2448] hover:border-[#8fd4ff]"
                                        }`}
                                      >
                                        <div className="mb-2 flex items-center gap-2">
                                          <img
                                            src={preset.image}
                                            alt={preset.label}
                                            className="h-12 w-12 shrink-0 rounded border border-[#66c6ff88] bg-[#12345a] object-cover"
                                          />
                                          <div
                                            className="min-w-0 truncate text-xs font-semibold uppercase tracking-wide text-[#d5ecff]"
                                            title={preset.label}
                                          >
                                            {preset.label}
                                          </div>
                                        </div>
                                        <div
                                          className="truncate text-[10px] font-semibold uppercase tracking-[0.14em] text-[#b9d7ff]"
                                          title={accessLabel}
                                        >
                                          {accessLabel}
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div className="grid grid-cols-2 gap-2 pt-1">
                        <button
                          type="button"
                          onClick={closeCommonBlocksModal}
                          className="rounded border border-[#6f7ea0] bg-[#1b2a4a] px-2 py-2 text-xs font-semibold uppercase text-[#ccdaf7] transition hover:border-[#9fb4e6]"
                        >{i18nT("lobby.cancelar")}</button>
                        <button
                          type="button"
                          onClick={saveCommonBlocksSelection}
                          className="rounded border border-[#33d368] bg-[#14662f] px-2 py-2 text-xs font-semibold uppercase text-[#cbffe0] transition hover:border-[#6bf0a1]"
                        >{i18nT("lobby.guardar")}</button>
                      </div>
                    </div>
                  ) : null}

                  {lobbyDesignActiveEditor === "board" ? (
                    <div className="space-y-4">
                      <div>
                        <div className="text-lg font-semibold text-[#8fd4ff]">{i18nT("lobby.colorDelTablero")}</div>
                        <div className="text-sm text-[#a9c4e9]">{i18nT("lobby.eligeUnTemaPredefinidoParaElTableroDe")}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.values(BOARD_THEME_PRESET_MAP).map((preset) => {
                          const isSelected = draftBoardThemeId === preset.id;
                          return (
                            <button
                              key={`embedded-board-${preset.id}`}
                              type="button"
                              onClick={() => setDraftBoardThemeId(preset.id)}
                              className={`rounded border p-2 text-left transition ${
                                isSelected
                                  ? "border-[#4dff92] bg-[#143927]"
                                  : "border-[#3c5f8f] bg-[#0f2448] hover:border-[#8fd4ff]"
                              }`}
                            >
                              <div className="mb-2 flex items-center gap-2">
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded border border-[#ffffff26] bg-[#0d223f]">
                                  <div
                                    className="h-8 w-8 rounded border border-[#ffffff33]"
                                    style={getBoardDualSquaresStyle(preset)}
                                  />
                                </div>
                                <div className="text-xs font-semibold uppercase tracking-wide text-[#d5ecff]">
                                  {preset.label}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      <div className="grid grid-cols-2 gap-2 pt-1">
                        <button
                          type="button"
                          onClick={closeBoardThemeModal}
                          className="rounded border border-[#6f7ea0] bg-[#1b2a4a] px-2 py-2 text-xs font-semibold uppercase text-[#ccdaf7] transition hover:border-[#9fb4e6]"
                        >{i18nT("lobby.cancelar")}</button>
                        <button
                          type="button"
                          onClick={saveBoardThemeSelection}
                          className="rounded border border-[#33d368] bg-[#14662f] px-2 py-2 text-xs font-semibold uppercase text-[#cbffe0] transition hover:border-[#6bf0a1]"
                        >{i18nT("lobby.guardar")}</button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {!isNewsTab &&
            !isAdminTab &&
            !isHistoryTab &&
            !hideLobbyCubeStartPanel &&
            !isLobbyFocusedModeRoute ? (
              pendingFriendTotal > 0 ? (
                <div className="rounded-xl border border-[#5f4a1e] bg-[#211707eb] p-3 shadow-[0_0_28px_rgba(0,0,0,0.32)]">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#f3c87a]">{i18nT("lobby.social")}</div>
                      <div className="text-base font-black text-[#ffe5bd]">{i18nT("lobby.tienesNotificacionesEnAmigos")}</div>
                      <div className="mt-1 text-sm text-[#ffd7a2]">
                        {i18nT("lobby.solicitudes")}: {pendingFriendRequests} ·{" "}
                        {i18nT("lobby.invitaciones")}: {pendingFriendInvites}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => navigate("/game/friends")}
                        className="rounded border border-[#ffb86f] bg-[#5e320f] px-3 py-1 text-xs font-bold uppercase tracking-wide text-[#ffe6c2] transition hover:border-[#ffd39a] hover:text-white"
                      >{i18nT("lobby.irAAmigos")}</button>
                      <button
                        type="button"
                        onClick={() => {
                          if (typeof refreshSocialAlert === "function") {
                            refreshSocialAlert().catch((error) => console.log(error));
                          }
                        }}
                        className="rounded border border-[#d6a86d] bg-[#3b2713] px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#ffdcb5] transition hover:border-[#ffd39a] hover:text-white"
                      >
                        {t("ACTUALIZAR")}
                      </button>
                    </div>
                  </div>
                </div>
              ) : null
            ) : null}

            {!isNewsTab &&
            !isAdminTab &&
            !isHistoryTab &&
            !hideLobbyCubeStartPanel &&
            !isLobbyFocusedModeRoute &&
            !showLegacyMobileHome ? (
              <div className="rounded-xl border border-[#24406f] bg-[#070f24e6] p-3 shadow-[0_0_30px_rgba(0,0,0,0.35)]">
                <div className="mb-3 flex flex-wrap items-center justify-center gap-2">
                  {startScreenCimaxButtons.map((button) => (
                    <button
                      key={button.key}
                      type="button"
                      className={`mobile-home-cimax-button rounded-lg border px-3 py-2 text-xs font-black uppercase tracking-[0.06em] transition-all duration-200 ${button.className} [@media(max-width:1024px)_and_(orientation:portrait)]:hidden`}
                    >
                      {(button.key === "ciencia"
                        ? tx("CIMAX CIENCIA", "CIMAX SCIENCE")
                        : button.key === "azar"
                        ? tx("CIMAX AZAR", "CIMAX RANDOM")
                        : button.label)}
                      : {button.score}
                    </button>
                  ))}
                  {shouldShowManualReconnectButton ? (
                    <button
                      type="button"
                      onClick={handleVolverAMiPartida}
                      disabled={manualReconnectLoading}
                      className={`mobile-home-return-match-button rounded-lg border px-4 py-2 text-xs font-black uppercase tracking-[0.08em] transition-all duration-200 ${
                        manualReconnectLoading
                          ? "cursor-not-allowed border-[#8f8f8f] bg-[#686868] text-[#f4f4f4]"
                          : "border-[#9a9a9a] bg-[#787878] text-[#f4f4f4] hover:border-[#b6b6b6] hover:bg-[#8a8a8a]"
                      }`}
                    >
                      {manualReconnectLoading
                        ? t("CARGANDO_PARTIDA_GUARDADA")
                        : t("VOLVER_A_MI_PARTIDA")}
                    </button>
                  ) : null}
                </div>
                <div className="mb-1 grid w-full grid-cols-4 items-end gap-2 md:gap-3 [@media(max-width:1024px)_and_(orientation:portrait)]:gap-1">
                  <div className="flex items-center justify-center">
                    <img
                      src={TOURNAMENT_BUTTON_ICON_SRC}
                      alt={i18nT("lobby.torneos")}
                      className="h-auto w-auto max-w-full object-contain"
                      style={{
                        maxHeight: usePhoneQuickViewButtonStack
                          ? "clamp(0.56rem, 1.07vw + 0.12rem, 1.03rem)"
                          : "clamp(2.52rem, 5.54vw + 0.5rem, 5.42rem)",
                        transform: "scale(0.9)",
                        transformOrigin: "center",
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-center">
                    <img
                      src={INDEPENDENT_BUTTON_ICON_SRC}
                      alt={i18nT("lobby.crearJuego")}
                      className="h-auto w-auto max-w-[52%] object-contain"
                      style={{
                        maxHeight: usePhoneQuickViewButtonStack
                          ? "clamp(0.88rem, 1.7vw + 0.18rem, 1.63rem)"
                          : "clamp(2.6rem, 5vw + 0.5rem, 5rem)",
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-center">
                    <img
                      src={FRIENDS_BUTTON_ICON_SRC}
                      alt={i18nT("lobby.tusAmigos")}
                      className="h-auto w-auto max-w-[52%] object-contain"
                      style={{
                        maxHeight: usePhoneQuickViewButtonStack
                          ? "clamp(0.88rem, 1.7vw + 0.18rem, 1.63rem)"
                          : "clamp(2.6rem, 5vw + 0.5rem, 5rem)",
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-center">
                    <img
                      src={PUZZLE_BUTTON_ICON_SRC}
                      alt={i18nT("lobby.puzzle")}
                      className="h-auto w-auto max-w-full object-contain"
                      style={{
                        maxHeight: usePhoneQuickViewButtonStack
                          ? "clamp(0.62rem, 1.19vw + 0.13rem, 1.14rem)"
                          : "clamp(2.8rem, 6.16vw + 0.56rem, 6.02rem)",
                        transform: "scale(0.81)",
                        transformOrigin: "center",
                      }}
                    />
                  </div>
                </div>
                <div className="grid w-full grid-cols-4 gap-2 md:gap-3 [@media(max-width:1024px)_and_(orientation:portrait)]:gap-1">
                  <button
                    type="button"
                    data-xima-menu-key="torneos"
                    onClick={() =>
                      handleQuickViewTopButtonPress("torneos", () => {
                        setMatchmakingQuickView("torneos");
                      })
                    }
                    className={`flex w-[94%] items-center justify-center justify-self-center transform-gpu rounded-xl border px-2 py-2 text-center font-black uppercase tracking-[0.04em] transition-all duration-200 [@media(max-width:1024px)_and_(orientation:portrait)]:px-1 [@media(max-width:1024px)_and_(orientation:portrait)]:py-1 ${
                      matchmakingQuickView === "torneos"
                        ? "border-[#cf9dff] bg-[#5a2290] text-white shadow-[0_0_16px_rgba(157,103,230,0.45)]"
                        : "border-[#cf9dff] bg-transparent text-[#cf9dff] hover:border-[#e0c1ff] active:bg-[#5a2290] active:text-white"
                    }`}
                    style={{
                      fontSize: usePhoneQuickViewButtonStack
                        ? "clamp(0.17rem, 0.41vw + 0.05rem, 0.32rem)"
                        : "clamp(0.52rem, 1.15vw + 0.18rem, 1.5rem)",
                      lineHeight: usePhoneQuickViewButtonStack ? 1.02 : 1.08,
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      transform:
                        quickViewButtonPulseKey === "torneos"
                          ? "scale(1.16)"
                          : matchmakingQuickView === "torneos"
                          ? "scale(1.08)"
                          : "scale(1)",
                    }}
                  >
                    <span
                      className={`flex w-full items-center justify-center ${
                        usePhoneQuickViewButtonStack ? "flex-col gap-1" : "gap-2"
                      }`}
                    >
                      <span
                        className={`min-w-0 text-center ${
                          usePhoneQuickViewButtonStack
                            ? "w-full whitespace-normal break-words leading-[1.05]"
                            : "flex-1 leading-[1.08]"
                        }`}
                      >
                        {tx("Torneo", "Tournament")} ({tournamentLobbies.length})
                      </span>
                    </span>
                  </button>
                  <button
                    type="button"
                    data-xima-menu-key="independientes"
                    data-xima-chat-anchor="independent-matches"
                    onClick={handleLegacyCreateGameClick}
                    className={`flex w-[94%] items-center justify-center justify-self-center transform-gpu rounded-xl border px-2 py-2 text-center font-black uppercase tracking-[0.04em] transition-all duration-200 [@media(max-width:1024px)_and_(orientation:portrait)]:px-1 [@media(max-width:1024px)_and_(orientation:portrait)]:py-1 ${
                      matchmakingQuickView === "independientes"
                        ? "border-[#cf9dff] bg-[#5a2290] text-white shadow-[0_0_16px_rgba(157,103,230,0.45)]"
                        : "border-[#cf9dff] bg-transparent text-[#cf9dff] hover:border-[#e0c1ff] active:bg-[#5a2290] active:text-white"
                    }`}
                    style={{
                      fontSize: usePhoneQuickViewButtonStack
                        ? "clamp(0.17rem, 0.41vw + 0.05rem, 0.32rem)"
                        : "clamp(0.52rem, 1.15vw + 0.18rem, 1.5rem)",
                      lineHeight: usePhoneQuickViewButtonStack ? 1.02 : 1.08,
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      transform:
                        quickViewButtonPulseKey === "independientes"
                          ? "scale(1.16)"
                          : matchmakingQuickView === "independientes"
                          ? "scale(1.08)"
                          : "scale(1)",
                    }}
                  >
                    <span
                      className={`flex w-full items-center justify-center text-center ${
                        usePhoneQuickViewButtonStack ? "flex-col gap-1" : "gap-2 leading-[1.08]"
                      }`}
                    >
                      <span
                        className={`min-w-0 text-center ${
                          usePhoneQuickViewButtonStack
                            ? "w-full whitespace-normal break-words leading-[1.05]"
                            : ""
                        }`}
                      >
                        {tx("Crear juego", "Create game")} ({independentMatches.length})
                      </span>
                    </span>
                  </button>
                  <button
                    type="button"
                    data-xima-menu-key="friends"
                    data-xima-chat-anchor="friends-match"
                    onClick={() =>
                      handleQuickViewTopButtonPress("friends", () => {
                        navigate("/game/friends");
                      })
                    }
                    className={`flex w-[94%] items-center justify-center justify-self-center transform-gpu rounded-xl border border-[#cf9dff] bg-transparent px-2 py-2 text-center font-black uppercase tracking-[0.04em] text-[#cf9dff] transition-all duration-200 hover:border-[#e0c1ff] active:bg-[#5a2290] active:text-white [@media(max-width:1024px)_and_(orientation:portrait)]:px-1 [@media(max-width:1024px)_and_(orientation:portrait)]:py-1`}
                    style={{
                      fontSize: usePhoneQuickViewButtonStack
                        ? "clamp(0.17rem, 0.41vw + 0.05rem, 0.32rem)"
                        : "clamp(0.52rem, 1.15vw + 0.18rem, 1.5rem)",
                      lineHeight: usePhoneQuickViewButtonStack ? 1.02 : 1.08,
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      transform: quickViewButtonPulseKey === "friends" ? "scale(1.16)" : "scale(1)",
                    }}
                  >
                    <span
                      className={`flex w-full items-center justify-center text-center ${
                        usePhoneQuickViewButtonStack
                          ? "flex-col gap-1"
                          : "gap-2 leading-[1.08]"
                      }`}
                    >
                      <span className="min-w-0 text-center">
                        <span
                          className={
                            usePhoneQuickViewButtonStack
                              ? "block w-full whitespace-normal break-words leading-[1.05]"
                              : ""
                          }
                        >
                          {tx("Tus amigos", "Your friends")}
                        </span>
                      </span>
                    </span>
                  </button>
                  <button
                    type="button"
                    data-xima-menu-key="puzzles"
                    onClick={() =>
                      handleQuickViewTopButtonPress("puzzles", () => {
                        setMatchmakingQuickView("puzzles");
                      })
                    }
                    className={`flex w-[94%] items-center justify-center justify-self-center transform-gpu rounded-xl border px-2 py-2 text-center font-black uppercase tracking-[0.04em] transition-all duration-200 [@media(max-width:1024px)_and_(orientation:portrait)]:px-1 [@media(max-width:1024px)_and_(orientation:portrait)]:py-1 ${
                      matchmakingQuickView === "puzzles"
                        ? "border-[#cf9dff] bg-[#5a2290] text-white shadow-[0_0_16px_rgba(157,103,230,0.45)]"
                        : "border-[#cf9dff] bg-transparent text-[#cf9dff] hover:border-[#e0c1ff] active:bg-[#5a2290] active:text-white"
                    }`}
                    style={{
                      fontSize: usePhoneQuickViewButtonStack
                        ? "clamp(0.17rem, 0.41vw + 0.05rem, 0.32rem)"
                        : "clamp(0.52rem, 1.15vw + 0.18rem, 1.5rem)",
                      lineHeight: usePhoneQuickViewButtonStack ? 1.02 : 1.08,
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      transform:
                        quickViewButtonPulseKey === "puzzles"
                          ? "scale(1.16)"
                          : matchmakingQuickView === "puzzles"
                          ? "scale(1.08)"
                          : "scale(1)",
                    }}
                  >
                    <span
                      className={`flex w-full items-center justify-center ${
                        usePhoneQuickViewButtonStack ? "flex-col gap-1" : "gap-2"
                      }`}
                    >
                      <span
                        className={`min-w-0 text-center ${
                          usePhoneQuickViewButtonStack
                            ? "w-full whitespace-normal break-words leading-[1.05]"
                            : "flex-1 leading-[1.08]"
                        }`}
                      >
                        Puzzle ({puzzles.length})
                      </span>
                    </span>
                  </button>
                </div>
              </div>
            ) : null}

            {!isNewsTab &&
            !isAdminTab &&
            !isHistoryTab &&
            !hideLobbyCubeStartPanel &&
            (!isLobbyFocusedModeRoute ||
              effectiveMatchmakingQuickView === "puzzles" ||
              effectiveMatchmakingQuickView === "torneos") ? (
              <div className="rounded-xl border border-[#24406f] bg-[#070f24e6] p-3 shadow-[0_0_30px_rgba(0,0,0,0.35)]">
                {!showCreateLobbyModePanelInMobileVideo ? lobbyModePanelReturn : null}
                {effectiveMatchmakingQuickView !== "puzzles" ? (
                <div className="mb-3 flex items-center justify-end gap-3">
                  {hasDirectAdminAccess && effectiveMatchmakingQuickView === "torneos" ? (
                    <button
                      type="button"
                      onClick={handleToggleTournamentEditMode}
                      className={`rounded border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide transition ${
                        tournamentEditModeEnabled
                          ? "border-[#ffb4b4] bg-[#c32222] text-white hover:bg-[#d93030]"
                          : "border-[#ff7f7f88] bg-[#5a1313] text-[#ffd0d0] hover:border-[#ffb2b2] hover:text-white"
                      }`}
                    >
                      {tournamentEditModeEnabled ? "Editar Torneo ON" : "Editar Torneo"}
                    </button>
                  ) : null}
                  {hasDirectSuperuserAccess && effectiveMatchmakingQuickView === "puzzles" ? (
                    <button
                      type="button"
                      onClick={handleTogglePuzzleEditMode}
                      className={`rounded border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide transition ${
                        puzzleEditModeEnabled
                          ? "border-[#ffb4b4] bg-[#c32222] text-white hover:bg-[#d93030]"
                          : "border-[#ff7f7f88] bg-[#5a1313] text-[#ffd0d0] hover:border-[#ffb2b2] hover:text-white"
                      }`}
                    >
                      {puzzleEditModeEnabled ? "Editar Puzzle ON" : "Editar Puzzle"}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      fetchTournaments().catch((error) => console.log(error));
                      fetchPuzzles(true).catch((error) => console.log(error));
                      fetchLiveMatches().catch((error) => console.log(error));
                      fetchModeWinnersLeaderboard(false).catch((error) =>
                        console.log(error)
                      );
                    }}
                    className="rounded border border-[#4fc3ff88] bg-[#11305c] px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#b7e4ff] transition hover:border-[#7ee5ff] hover:text-white"
                  >
                    {tx("Actualizar", "Refresh")}
                  </button>
                </div>
                ) : null}

                <div
                  className={
                    effectiveMatchmakingQuickView === "torneos"
                      ? "max-md:max-h-none max-md:overflow-visible md:max-h-[520px] md:overflow-hidden [@media(max-width:1024px)_and_(orientation:landscape)]:max-h-[520px] [@media(max-width:1024px)_and_(orientation:landscape)]:overflow-hidden"
                      : effectiveMatchmakingQuickView === "puzzles"
                      ? "xima-puzzles-host"
                      : "space-y-3 max-md:max-h-none max-md:overflow-visible max-md:pr-0 md:max-h-[520px] md:overflow-y-auto md:pr-1 [@media(max-width:1024px)_and_(orientation:landscape)]:max-h-[520px] [@media(max-width:1024px)_and_(orientation:landscape)]:overflow-y-auto [@media(max-width:1024px)_and_(orientation:landscape)]:pr-1"
                  }
                >
                  {effectiveMatchmakingQuickView === "torneos" ? (
                    <div
                      className={`grid grid-cols-1 gap-3 lg:h-full [@media(max-width:1024px)_and_(orientation:landscape)]:h-full [@media(max-width:1024px)_and_(orientation:landscape)]:grid-cols-1 ${
                        !hideLobbyCubeStartPanel &&
                        (!isLobbyFocusedModeRoute || isLobbyTournamentModeRoute)
                          ? "lg:grid-cols-[minmax(0,3.93fr)_minmax(0,1.07fr)]"
                          : "lg:grid-cols-1"
                      }`}
                    >
                      <div className="space-y-3 md:max-h-[520px] md:overflow-y-auto md:pr-1 [@media(max-width:1024px)_and_(orientation:landscape)]:max-h-[520px] [@media(max-width:1024px)_and_(orientation:landscape)]:overflow-y-auto [@media(max-width:1024px)_and_(orientation:landscape)]:pr-1">
                        {hasDirectAdminAccess && tournamentEditModeEnabled ? (
                          <div className="rounded border border-[#ff7f7f88] bg-[#3c1111] px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-[#ffd6d6]">
                            Modo edicion activo: presiona la imagen del torneo para editarlo.
                          </div>
                        ) : null}
                        <div>
                          {featuredRegisteredTournamentLobby ? (
                            (() => {
                              const match = featuredRegisteredTournamentLobby;
                              const registeredPlayers = ensureArray(match?.registeredPlayers);
                              const registeredCount = Math.max(
                                0,
                                Number(match?.registeredPlayersCount) || registeredPlayers.length
                              );
                              const visibleRegisteredCount =
                                resolveVisibleJoinedCount(registeredCount);
                              const tournamentRewardValue = resolveTournamentRewardValue(match);
                              const hasTournamentPayment = Number(tournamentRewardValue) > 0;
                              const memberSize = Math.max(1, Number(match?.memberSize) || 1);
                              const tournamentDisplayMode = resolveTournamentDisplayMode(match);
                              const modeTheme = resolveModeColorTheme(tournamentDisplayMode, {
                                hasTournamentPayment: isLobbyTournamentModeRoute
                                  ? false
                                  : hasTournamentPayment,
                              });
                              const modeLabel = String(formatModeLabel(tournamentDisplayMode)).toUpperCase();
                              const modeIconSrc = getModeIconSrc(tournamentDisplayMode);
                              const playerCountIconSrc = getPlayerCountIconSrc(memberSize);
                              const matchDurationIconSrc = getMatchDurationIconSrc(
                                match?.havingTime
                              );
                              const registeredModeIconNode = modeIconSrc ? (
                                <img
                                  src={modeIconSrc}
                                  alt={modeLabel}
                                  className="h-full w-full object-contain"
                                />
                              ) : (
                                <span
                                  className={`text-[10px] font-black uppercase ${modeTheme.iconFallbackTextClass}`}
                                >
                                  {modeLabel.slice(0, 3)}
                                </span>
                              );
                              const tournamentId = getTournamentEntryId(match);
                              const tournamentSequenceNumber =
                                tournamentLobbyFallbackSequenceById[tournamentId] ||
                                resolvePublicSequenceNumber(match) ||
                                0;
                              const tournamentJoinEnabled = Boolean(tournamentId);
                              const registeredDeleteOperationKey = `live-tournament:${String(
                                tournamentId || ""
                              ).trim()}`;
                              const registeredDeleteLoading =
                                adminActionKey === registeredDeleteOperationKey;
                              const canDeleteRegisteredTournament =
                                canDeleteTournamentFromLivePanel && Boolean(tournamentId);
                              const handleOpenTournamentFromRegisteredCard = () => {
                                if (!tournamentJoinEnabled) return;
                                openTournamentWaitingRoom({
                                  ...match,
                                  tournamentUiSequenceNumber: tournamentSequenceNumber,
                                  tournamentSequenceNumber,
                                  tournamentNumber: tournamentSequenceNumber,
                                  displayNumber: tournamentSequenceNumber,
                                });
                              };
                              return (
                                <div className="mb-4">
                                  <div className="mobile-tournament-section-title mb-2 text-[22px] font-semibold uppercase tracking-[0.16em] text-[#96ffd8]">
                                    {tx("Inscrito", "Registered")}
                                  </div>
                                  <div
                                    onClick={handleOpenTournamentFromRegisteredCard}
                                    onKeyDown={(event) => {
                                      if (event.key !== "Enter" && event.key !== " ") return;
                                      event.preventDefault();
                                      handleOpenTournamentFromRegisteredCard();
                                    }}
                                    role={tournamentJoinEnabled ? "button" : undefined}
                                    tabIndex={tournamentJoinEnabled ? 0 : undefined}
                                    className={`mobile-landscape-tournament-card rounded-xl border p-3 [@media(max-width:1024px)_and_(orientation:portrait)]:px-2 [@media(max-width:1024px)_and_(orientation:portrait)]:py-1 ${modeTheme.cardClass} ${modeTheme.cardShadowClass} ${
                                      tournamentJoinEnabled
                                        ? "cursor-pointer transition hover:brightness-110"
                                        : ""
                                    }`}
                                  >
                                    <div className="flex flex-wrap items-start justify-between gap-3 [@media(max-width:1024px)_and_(orientation:portrait)]:gap-1">
                                      <div className="flex min-w-0 items-start gap-3 [@media(max-width:1024px)_and_(orientation:portrait)]:gap-1">
                                        <div className="flex shrink-0 flex-col items-start gap-2 [@media(max-width:1024px)_and_(orientation:portrait)]:flex-row [@media(max-width:1024px)_and_(orientation:portrait)]:items-center [@media(max-width:1024px)_and_(orientation:portrait)]:gap-1 [@media(min-width:1025px)]:flex-row [@media(min-width:1025px)]:items-center [@media(min-width:1025px)]:gap-3">
                                          <div
                                            className={`mobile-tournament-mode-icon-shell flex h-[36px] w-[36px] shrink-0 items-center justify-center overflow-hidden rounded-lg border p-1 [@media(min-width:1025px)]:h-[54px] [@media(min-width:1025px)]:w-[54px] ${modeTheme.iconClass}`}
                                          >
                                            {registeredModeIconNode}
                                          </div>
                                          {playerCountIconSrc ? (
                                            <div
                                              className={`mobile-tournament-mode-icon-shell flex h-[36px] w-[36px] shrink-0 items-center justify-center overflow-hidden rounded-lg border p-1 [@media(min-width:1025px)]:h-[54px] [@media(min-width:1025px)]:w-[54px] ${modeTheme.iconClass}`}
                                            >
                                              <img
                                                src={playerCountIconSrc}
                                                alt={`Jugadores ${memberSize}`}
                                                className="h-full w-full object-contain"
                                              />
                                            </div>
                                          ) : null}
                                          {matchDurationIconSrc ? (
                                            <div
                                              className={`mobile-tournament-mode-icon-shell flex h-[36px] w-[36px] shrink-0 items-center justify-center overflow-hidden rounded-lg border p-1 [@media(min-width:1025px)]:h-[54px] [@media(min-width:1025px)]:w-[54px] ${modeTheme.iconClass}`}
                                            >
                                              <img
                                                src={matchDurationIconSrc}
                                                alt={`Tiempo ${Math.max(1, Number(match?.havingTime) || 1)} minutos`}
                                                className="h-full w-full object-contain"
                                              />
                                            </div>
                                          ) : null}
                                        </div>
                                        <div className="min-w-0">
                                          <div
                                            className={`mobile-tournament-name truncate text-[10px] font-black [@media(min-width:1025px)]:text-[15px] ${modeTheme.titleTextClass}`}
                                          >
                                            {getLocalizedTournamentName(match?.tournamentName, "Torneo")}
                                          </div>
                                          <div
                                            className={`mobile-landscape-tournament-meta mt-1 flex flex-wrap items-center gap-1 text-[6px] [@media(max-width:1024px)_and_(orientation:portrait)]:mt-0 [@media(min-width:1025px)]:mt-2 [@media(min-width:1025px)]:gap-1.5 [@media(min-width:1025px)]:text-[9px] ${modeTheme.metaTextClass}`}
                                          >
                                            {tournamentSequenceNumber > 0 ? (
                                              <span
                                                className="mobile-tournament-sequence-badge rounded border border-[#ff65d8] bg-[#4a124a] px-1 py-0.5 text-[6px] font-black uppercase text-[#ffe1ff] shadow-[0_0_10px_rgba(255,101,216,0.22)] [@media(min-width:1025px)]:px-1.5 [@media(min-width:1025px)]:py-[3px] [@media(min-width:1025px)]:text-[9px]"
                                                data-short={formatPublicSequenceShortLabel(
                                                  language,
                                                  tournamentSequenceNumber
                                                )}
                                                title={formatPublicSequenceLabel(
                                                  language,
                                                  "tournament",
                                                  tournamentSequenceNumber
                                                )}
                                              >
                                                {formatPublicSequenceLabel(
                                                  language,
                                                  "tournament",
                                                  tournamentSequenceNumber
                                                )}
                                              </span>
                                            ) : null}
                                            <span
                                              className={`rounded border px-1 py-0.5 text-[6px] font-semibold [@media(min-width:1025px)]:px-1.5 [@media(min-width:1025px)]:py-[3px] [@media(min-width:1025px)]:text-[9px] ${modeTheme.metaBadgeClass}`}
                                            >
                                              {t("ESTADO")}: Activo
                                            </span>
                                            <span
                                              className={`rounded border px-1 py-0.5 text-[6px] font-semibold [@media(min-width:1025px)]:px-1.5 [@media(min-width:1025px)]:py-[3px] [@media(min-width:1025px)]:text-[9px] ${modeTheme.metaBadgeClass}`}
                                            >
                                              {modeLabel}
                                            </span>
                                            <span
                                              className={`rounded border px-1 py-0.5 text-[6px] font-semibold [@media(min-width:1025px)]:px-1.5 [@media(min-width:1025px)]:py-[3px] [@media(min-width:1025px)]:text-[9px] ${modeTheme.metaBadgeClass}`}
                                            >
                                              {t("TIEMPO")}: {String(
                                                Math.max(1, Number(match?.havingTime) || 1)
                                              ).padStart(2, "0")}
                                              :00
                                            </span>
                                            {canDeleteRegisteredTournament ? (
                                              <button
                                                type="button"
                                                onClick={(event) => {
                                                  event.stopPropagation();
                                                  handleDeleteTournamentFromLivePanel(match);
                                                }}
                                                disabled={registeredDeleteLoading}
                                                className="rounded border border-[#ff8c8c] bg-[#621f1f] px-1 py-0.5 text-[6px] font-bold uppercase tracking-wide text-[#ffdede] transition hover:border-[#ffc3c3] hover:bg-[#8b2a2a] hover:text-white [@media(min-width:1025px)]:px-1.5 [@media(min-width:1025px)]:py-[3px] [@media(min-width:1025px)]:text-[9px] disabled:cursor-not-allowed disabled:opacity-60"
                                              >
                                                {registeredDeleteLoading ? "..." : "Eliminar"}
                                              </button>
                                            ) : null}
                                            <span
                                              className={`rounded border px-1 py-0.5 text-[6px] font-semibold [@media(min-width:1025px)]:px-1.5 [@media(min-width:1025px)]:py-[3px] [@media(min-width:1025px)]:text-[9px] ${modeTheme.countBadgeClass}`}
                                            >
                                              {t("UNIDOS")}: {visibleRegisteredCount}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleOpenTournamentFromRegisteredCard();
                                        }}
                                        disabled={!tournamentJoinEnabled}
                                        className={`mobile-landscape-tournament-join tournament-join-pulse rounded border px-[0.525rem] py-[0.2625rem] text-[6.3px] font-black uppercase tracking-wide transition [@media(max-width:1024px)_and_(orientation:portrait)]:hidden [@media(min-width:1025px)]:px-3 [@media(min-width:1025px)]:py-1.5 [@media(min-width:1025px)]:text-[9px] disabled:cursor-not-allowed disabled:opacity-60 ${modeTheme.joinButtonClass}`}
                                      >
                                        {t("JOIN")}
                                      </button>
                                    </div>
                                  </div>
                                  <div className="my-4 border-t border-[#365a8b]" />
                                </div>
                              );
                            })()
                          ) : null}
                          <div className="mobile-tournament-section-title mb-2 text-[22px] font-semibold uppercase tracking-[0.16em] text-[#8fd4ff]">
                            {tx("Torneos en vivo", "Live tournaments")}
                          </div>
                          {visibleTournamentLobbiesVigentes.length > 0 ? (
                            <div className="space-y-2">
                              {visibleTournamentLobbiesVigentes.map((match) => {
                                const registeredPlayers = ensureArray(match?.registeredPlayers);
                                const registeredCount = Math.max(
                                  0,
                                  Number(match?.registeredPlayersCount) || registeredPlayers.length
                                );
                                const visibleRegisteredCount =
                                  resolveVisibleJoinedCount(registeredCount);
                                const tournamentRewardValue =
                                  resolveTournamentRewardValue(match);
                                const tournamentWinnerPrizeValue =
                                  resolveTournamentWinnerPrizeValue(match);
                                const hasMatchPassword = Boolean(match?.hasMatchPassword);
                                const hasTournamentPayment = Number(tournamentRewardValue) > 0;
                                const memberSize = Math.max(1, Number(match?.memberSize) || 1);
                                const hasTournamentWinnerPrize =
                                  Number(tournamentWinnerPrizeValue) > 0;
                                const isPersistentMatch = isPersistentQuickTournament(match);
                                const tournamentDisplayMode = resolveTournamentDisplayMode(match);
                                const modeLabel = String(
                                  formatModeLabel(tournamentDisplayMode)
                                ).toUpperCase();
                                const tournamentId = String(match?._id || match?.id || "").trim();
                                const tournamentSequenceNumber =
                                  tournamentLobbyFallbackSequenceById[tournamentId] ||
                                  resolvePublicSequenceNumber(match) ||
                                  0;
                                const modeIconSrc = getModeIconSrc(tournamentDisplayMode);
                                const playerCountIconSrc = getPlayerCountIconSrc(memberSize);
                                const matchDurationIconSrc = getMatchDurationIconSrc(
                                  match?.havingTime
                                );
                                const modeTheme = resolveModeColorTheme(tournamentDisplayMode, {
                                  hasTournamentPayment: isLobbyTournamentModeRoute
                                    ? false
                                    : hasTournamentPayment,
                                });
                                const modeIconShellClass = getTransparentModeIconShellClass(
                                  modeTheme.iconClass
                                );
                                const tournamentEditInteractive =
                                  hasDirectAdminAccess &&
                                  tournamentEditModeEnabled &&
                                  Boolean(tournamentId);
                                const tournamentEditSelected =
                                  tournamentEditInteractive &&
                                  tournamentEditTargetId === tournamentId;
                                const tournamentModeIconShellClass = tournamentEditInteractive
                                  ? tournamentEditSelected
                                    ? "cursor-pointer border-[#ffd1d1] bg-[#8f1e1e]"
                                    : "cursor-pointer border-[#ff8f8f] bg-[#571919] hover:border-[#ffd1d1] hover:bg-[#7a2323]"
                                  : modeIconShellClass;
                                const tournamentModeIconNode = modeIconSrc ? (
                                  <img
                                    src={modeIconSrc}
                                    alt={modeLabel}
                                    className="h-full w-full object-contain"
                                  />
                                ) : (
                                  <span
                                    className={`text-[10px] font-black uppercase ${modeTheme.iconFallbackTextClass}`}
                                  >
                                    {modeLabel.slice(0, 3)}
                                  </span>
                                );
                                const tournamentJoinEnabled = Boolean(match?._id);
                                const liveDeleteOperationKey = `live-tournament:${tournamentId}`;
                                const liveDeleteLoading = adminActionKey === liveDeleteOperationKey;
                                const canDeleteLiveTournament =
                                  canDeleteTournamentFromLivePanel && Boolean(tournamentId);
                                const handleOpenTournamentFromCard = () => {
                                  if (!tournamentJoinEnabled) return;
                                  openTournamentWaitingRoom({
                                    ...match,
                                    tournamentUiSequenceNumber: tournamentSequenceNumber,
                                    tournamentSequenceNumber,
                                    tournamentNumber: tournamentSequenceNumber,
                                    displayNumber: tournamentSequenceNumber,
                                  });
                                };
                                return (
                                  <div
                                    key={`tournament-lobby-${String(match?._id || "")}`}
                                    onClick={handleOpenTournamentFromCard}
                                    onKeyDown={(event) => {
                                      if (event.key !== "Enter" && event.key !== " ") return;
                                      event.preventDefault();
                                      handleOpenTournamentFromCard();
                                    }}
                                    role={tournamentJoinEnabled ? "button" : undefined}
                                    tabIndex={tournamentJoinEnabled ? 0 : undefined}
                                    className={`mobile-landscape-tournament-card rounded-xl border p-3 [@media(max-width:1024px)_and_(orientation:portrait)]:px-2 [@media(max-width:1024px)_and_(orientation:portrait)]:py-1 ${modeTheme.cardClass} ${modeTheme.cardShadowClass} ${
                                      tournamentJoinEnabled
                                        ? "cursor-pointer transition hover:brightness-110"
                                        : ""
                                    }`}
                                  >
                                    <div className="flex flex-wrap items-start justify-between gap-3 [@media(max-width:1024px)_and_(orientation:portrait)]:gap-1">
                                      <div className="flex min-w-0 items-start gap-3 [@media(max-width:1024px)_and_(orientation:portrait)]:gap-1">
                                        <div className="flex shrink-0 flex-col items-start gap-2 [@media(max-width:1024px)_and_(orientation:portrait)]:flex-row [@media(max-width:1024px)_and_(orientation:portrait)]:items-center [@media(max-width:1024px)_and_(orientation:portrait)]:gap-1 [@media(min-width:1025px)]:flex-row [@media(min-width:1025px)]:items-center [@media(min-width:1025px)]:gap-3">
                                          {tournamentEditInteractive ? (
                                            <button
                                              type="button"
                                              onClick={() => openTournamentEditModal(match)}
                                              className={`mobile-tournament-mode-icon-shell flex h-[36px] w-[36px] shrink-0 items-center justify-center overflow-hidden rounded-lg border p-1 transition [@media(min-width:1025px)]:h-[54px] [@media(min-width:1025px)]:w-[54px] ${tournamentModeIconShellClass}`}
                                              title={i18nT("lobby.editarTorneo")}
                                            >
                                              {tournamentModeIconNode}
                                            </button>
                                          ) : (
                                            <div
                                              className={`mobile-tournament-mode-icon-shell flex h-[36px] w-[36px] shrink-0 items-center justify-center overflow-hidden rounded-lg border p-1 [@media(min-width:1025px)]:h-[54px] [@media(min-width:1025px)]:w-[54px] ${tournamentModeIconShellClass}`}
                                            >
                                              {tournamentModeIconNode}
                                            </div>
                                          )}
                                          {playerCountIconSrc ? (
                                            <div
                                              className={`mobile-tournament-mode-icon-shell flex h-[36px] w-[36px] shrink-0 items-center justify-center overflow-hidden rounded-lg border p-1 [@media(min-width:1025px)]:h-[54px] [@media(min-width:1025px)]:w-[54px] ${modeTheme.iconClass}`}
                                            >
                                              <img
                                                src={playerCountIconSrc}
                                                alt={`Jugadores ${memberSize}`}
                                                className="h-full w-full object-contain"
                                              />
                                            </div>
                                          ) : null}
                                          {matchDurationIconSrc ? (
                                            <div
                                              className={`mobile-tournament-mode-icon-shell flex h-[36px] w-[36px] shrink-0 items-center justify-center overflow-hidden rounded-lg border p-1 [@media(min-width:1025px)]:h-[54px] [@media(min-width:1025px)]:w-[54px] ${modeTheme.iconClass}`}
                                            >
                                              <img
                                                src={matchDurationIconSrc}
                                                alt={`Tiempo ${Math.max(1, Number(match?.havingTime) || 1)} minutos`}
                                                className="h-full w-full object-contain"
                                              />
                                            </div>
                                          ) : null}
                                          {hasMatchPassword ? (
                                            <div
                                              className={`mobile-tournament-mode-icon-shell flex h-[36px] w-[36px] shrink-0 items-center justify-center overflow-hidden rounded-lg border p-1 [@media(min-width:1025px)]:h-[54px] [@media(min-width:1025px)]:w-[54px] ${modeTheme.iconClass}`}
                                            >
                                              <img
                                                src={MATCH_PASSWORD_ICON_SRC}
                                                alt={i18nT("lobby.conContrasena")}
                                                className="h-full w-full object-contain"
                                              />
                                            </div>
                                          ) : null}
                                        </div>
                                        <div className="min-w-0">
                                          <div
                                            className={`mobile-tournament-name truncate text-[10px] font-black [@media(min-width:1025px)]:text-[15px] ${modeTheme.titleTextClass}`}
                                          >
                                            {getLocalizedTournamentName(
                                              match?.tournamentName,
                                              "Torneo"
                                            )}
                                          </div>
                                          <div
                                            className={`mobile-landscape-tournament-meta mt-1 flex flex-wrap items-center gap-1 text-[6px] [@media(max-width:1024px)_and_(orientation:portrait)]:mt-0 [@media(min-width:1025px)]:mt-2 [@media(min-width:1025px)]:gap-1.5 [@media(min-width:1025px)]:text-[9px] ${modeTheme.metaTextClass}`}
                                          >
                                            {tournamentSequenceNumber > 0 ? (
                                              <span
                                                className="mobile-tournament-sequence-badge rounded border border-[#ff65d8] bg-[#4a124a] px-1 py-0.5 text-[6px] font-black uppercase text-[#ffe1ff] shadow-[0_0_10px_rgba(255,101,216,0.22)] [@media(min-width:1025px)]:px-1.5 [@media(min-width:1025px)]:py-[3px] [@media(min-width:1025px)]:text-[9px]"
                                                data-short={formatPublicSequenceShortLabel(
                                                  language,
                                                  tournamentSequenceNumber
                                                )}
                                                title={formatPublicSequenceLabel(
                                                  language,
                                                  "tournament",
                                                  tournamentSequenceNumber
                                                )}
                                              >
                                                {formatPublicSequenceLabel(
                                                  language,
                                                  "tournament",
                                                  tournamentSequenceNumber
                                                )}
                                              </span>
                                            ) : null}
                                            {isPersistentMatch ? (
                                              <span
                                                className={`rounded border px-1 py-0.5 text-[6px] font-semibold [@media(min-width:1025px)]:px-1.5 [@media(min-width:1025px)]:py-[3px] [@media(min-width:1025px)]:text-[9px] ${modeTheme.metaBadgeClass}`}
                                              >
                                                <span className="[@media(max-width:1024px)_and_(orientation:portrait)]:hidden">
                                                  {t("TERMINA_EN")}
                                                </span>
                                                <span className="hidden [@media(max-width:1024px)_and_(orientation:portrait)]:inline">{i18nT("lobby.termina")}</span>
                                                : {formatCountdownLabel(match.quickRemainingMs)}
                                              </span>
                                            ) : (
                                              <span
                                                className={`rounded border px-1 py-0.5 text-[6px] font-semibold [@media(min-width:1025px)]:px-1.5 [@media(min-width:1025px)]:py-[3px] [@media(min-width:1025px)]:text-[9px] ${modeTheme.metaBadgeClass}`}
                                              >
                                                {t("ESTADO")}: Activo
                                              </span>
                                            )}
                                            <span
                                              className={`rounded border px-1 py-0.5 text-[6px] font-semibold [@media(min-width:1025px)]:px-1.5 [@media(min-width:1025px)]:py-[3px] [@media(min-width:1025px)]:text-[9px] ${modeTheme.metaBadgeClass}`}
                                            >
                                              {t("TIEMPO")}: {String(
                                                Math.max(1, Number(match?.havingTime) || 1)
                                              ).padStart(2, "0")}
                                              :00
                                            </span>
                                            <span
                                              className={`hidden rounded border px-1 py-0.5 text-[6px] font-semibold [@media(max-width:1024px)_and_(orientation:portrait)]:inline-flex [@media(min-width:1025px)]:px-1.5 [@media(min-width:1025px)]:py-[3px] [@media(min-width:1025px)]:text-[9px] ${modeTheme.countBadgeClass}`}
                                            >
                                              {t("UNIDOS")}: {visibleRegisteredCount}
                                            </span>
                                            {hasTournamentWinnerPrize ? (
                                              <span
                                                className={`rounded border px-1 py-0.5 text-[6px] font-semibold [@media(min-width:1025px)]:px-1.5 [@media(min-width:1025px)]:py-[3px] [@media(min-width:1025px)]:text-[9px] ${modeTheme.metaBadgeClass}`}
                                              >
                                                Premio: {tournamentWinnerPrizeValue}X
                                              </span>
                                            ) : null}
                                            {hasTournamentPayment ? (
                                              <span className="mobile-inline-meta-button rounded border border-[#ffffffde] bg-white px-1 py-0.5 text-[6px] font-semibold text-[#10264a] [@media(min-width:1025px)]:px-1.5 [@media(min-width:1025px)]:py-[3px] [@media(min-width:1025px)]:text-[9px]">
                                                Pago {tournamentRewardValue}
                                              </span>
                                            ) : null}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {canDeleteLiveTournament ? (
                                          <button
                                            type="button"
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              handleDeleteTournamentFromLivePanel(match);
                                            }}
                                            disabled={liveDeleteLoading}
                                            className="rounded border border-[#ff8c8c] bg-[#621f1f] px-[0.1875rem] py-[0.09375rem] text-[4.5px] font-bold uppercase tracking-wide text-[#ffdede] transition hover:border-[#ffc3c3] hover:bg-[#8b2a2a] hover:text-white [@media(min-width:1025px)]:px-1.5 [@media(min-width:1025px)]:py-[3px] [@media(min-width:1025px)]:text-[9px] disabled:cursor-not-allowed disabled:opacity-60"
                                          >
                                            {liveDeleteLoading ? "..." : "Eliminar"}
                                          </button>
                                        ) : null}
                                        <span
                                          className={`mobile-landscape-tournament-count rounded border px-[0.1875rem] py-[0.09375rem] text-[4.5px] font-semibold [@media(min-width:1025px)]:px-1.5 [@media(min-width:1025px)]:py-[3px] [@media(min-width:1025px)]:text-[9px] [@media(max-width:1024px)_and_(orientation:portrait)]:hidden ${modeTheme.countBadgeClass}`}
                                        >
                                          {t("UNIDOS")}: {visibleRegisteredCount}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleOpenTournamentFromCard();
                                          }}
                                          disabled={!tournamentJoinEnabled}
                                          className={`mobile-landscape-tournament-join tournament-join-pulse rounded border px-[0.525rem] py-[0.2625rem] text-[6.3px] font-black uppercase tracking-wide transition [@media(max-width:1024px)_and_(orientation:portrait)]:hidden [@media(min-width:1025px)]:px-3 [@media(min-width:1025px)]:py-1.5 [@media(min-width:1025px)]:text-[9px] disabled:cursor-not-allowed disabled:opacity-60 ${modeTheme.joinButtonClass}`}
                                        >
                                          {t("JOIN")}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="rounded-lg border border-dashed border-[#38557f] bg-[#091834] p-4 text-sm text-[#93b4dc]">{i18nT("lobby.noHayTorneosActivosEnEsteMomento")}</div>
                          )}

                          <div className="my-4 border-t border-[#365a8b]" />

                          <div className="mobile-tournament-section-title mb-2 text-[22px] font-semibold uppercase tracking-[0.16em] text-[#8fd4ff]">
                            {t("PROXIMOS_TORNEOS")}
                          </div>
                          {visibleTournamentLobbiesPorEmpezar.length > 0 ? (
                            <div className="space-y-2">
                              {visibleTournamentLobbiesPorEmpezar.map((match) => {
                                const registeredPlayers = ensureArray(match?.registeredPlayers);
                                const registeredCount = Math.max(
                                  0,
                                  Number(match?.registeredPlayersCount) || registeredPlayers.length
                                );
                                const visibleRegisteredCount =
                                  resolveVisibleJoinedCount(registeredCount);
                                const tournamentRewardValue =
                                  resolveTournamentRewardValue(match);
                                const tournamentWinnerPrizeValue =
                                  resolveTournamentWinnerPrizeValue(match);
                                const hasTournamentPayment = Number(tournamentRewardValue) > 0;
                                const hasMatchPassword = Boolean(match?.hasMatchPassword);
                                const memberSize = Math.max(1, Number(match?.memberSize) || 1);
                                const hasTournamentWinnerPrize =
                                  Number(tournamentWinnerPrizeValue) > 0;
                                const tournamentDisplayMode = resolveTournamentDisplayMode(match);
                                const modeLabel = String(
                                  formatModeLabel(tournamentDisplayMode)
                                ).toUpperCase();
                                const tournamentId = String(match?._id || match?.id || "").trim();
                                const tournamentSequenceNumber =
                                  tournamentLobbyFallbackSequenceById[tournamentId] ||
                                  resolvePublicSequenceNumber(match) ||
                                  0;
                                const modeIconSrc = getModeIconSrc(tournamentDisplayMode);
                                const playerCountIconSrc = getPlayerCountIconSrc(memberSize);
                                const matchDurationIconSrc = getMatchDurationIconSrc(
                                  match?.havingTime
                                );
                                const startRemainingMs = getTournamentStartRemainingMs(match);
                                const waitingTheme = resolveModeColorTheme(tournamentDisplayMode, {
                                  hasTournamentPayment: isLobbyTournamentModeRoute
                                    ? false
                                    : hasTournamentPayment,
                                  waiting: true,
                                });
                                const modeIconShellClass = getTransparentModeIconShellClass(
                                  waitingTheme.iconClass
                                );
                                const tournamentEditInteractive =
                                  hasDirectAdminAccess &&
                                  tournamentEditModeEnabled &&
                                  Boolean(tournamentId);
                                const tournamentEditSelected =
                                  tournamentEditInteractive &&
                                  tournamentEditTargetId === tournamentId;
                                const tournamentModeIconShellClass = tournamentEditInteractive
                                  ? tournamentEditSelected
                                    ? "cursor-pointer border-[#ffd1d1] bg-[#8f1e1e]"
                                    : "cursor-pointer border-[#ff8f8f] bg-[#571919] hover:border-[#ffd1d1] hover:bg-[#7a2323]"
                                  : modeIconShellClass;
                                const tournamentModeIconNode = modeIconSrc ? (
                                  <img
                                    src={modeIconSrc}
                                    alt={modeLabel}
                                    className="h-full w-full object-contain"
                                  />
                                ) : (
                                  <span
                                    className={`text-[10px] font-black uppercase ${waitingTheme.iconFallbackTextClass}`}
                                  >
                                    {modeLabel.slice(0, 3)}
                                  </span>
                                );
                                const tournamentJoinEnabled = Boolean(match?._id);
                                const upcomingDeleteOperationKey = `live-tournament:${tournamentId}`;
                                const upcomingDeleteLoading =
                                  adminActionKey === upcomingDeleteOperationKey;
                                const canDeleteUpcomingTournament =
                                  canDeleteTournamentFromLivePanel && Boolean(tournamentId);
                                const handleOpenTournamentFromCard = () => {
                                  if (!tournamentJoinEnabled) return;
                                  openTournamentWaitingRoom({
                                    ...match,
                                    tournamentUiSequenceNumber: tournamentSequenceNumber,
                                    tournamentSequenceNumber,
                                    tournamentNumber: tournamentSequenceNumber,
                                    displayNumber: tournamentSequenceNumber,
                                  });
                                };
                                return (
                                  <div
                                    key={`tournament-lobby-upcoming-${String(match?._id || "")}`}
                                    onClick={handleOpenTournamentFromCard}
                                    onKeyDown={(event) => {
                                      if (event.key !== "Enter" && event.key !== " ") return;
                                      event.preventDefault();
                                      handleOpenTournamentFromCard();
                                    }}
                                    role={tournamentJoinEnabled ? "button" : undefined}
                                    tabIndex={tournamentJoinEnabled ? 0 : undefined}
                                    className={`mobile-landscape-tournament-card relative rounded-xl border p-3 [@media(max-width:1024px)_and_(orientation:portrait)]:px-2 [@media(max-width:1024px)_and_(orientation:portrait)]:py-1 ${waitingTheme.cardClass} ${waitingTheme.cardShadowClass} ${
                                      tournamentJoinEnabled
                                        ? "cursor-pointer transition hover:brightness-110"
                                        : ""
                                    }`}
                                  >
                                    <div className="flex flex-wrap items-start justify-between gap-3 [@media(max-width:1024px)_and_(orientation:portrait)]:gap-1">
                                      <div className="flex min-w-0 items-start gap-3 [@media(max-width:1024px)_and_(orientation:portrait)]:gap-1 [@media(max-width:1024px)_and_(orientation:portrait)]:pb-0 [@media(max-width:1024px)_and_(orientation:portrait)]:pr-0">
                                        <div className="flex shrink-0 flex-col items-start gap-2 [@media(max-width:1024px)_and_(orientation:portrait)]:flex-row [@media(max-width:1024px)_and_(orientation:portrait)]:items-center [@media(max-width:1024px)_and_(orientation:portrait)]:gap-1 [@media(min-width:1025px)]:flex-row [@media(min-width:1025px)]:items-center [@media(min-width:1025px)]:gap-3">
                                          {tournamentEditInteractive ? (
                                            <button
                                              type="button"
                                              onClick={() => openTournamentEditModal(match)}
                                              className={`mobile-tournament-mode-icon-shell flex h-[36px] w-[36px] shrink-0 items-center justify-center overflow-hidden rounded-lg border p-1 transition [@media(min-width:1025px)]:h-[54px] [@media(min-width:1025px)]:w-[54px] ${tournamentModeIconShellClass}`}
                                              title={i18nT("lobby.editarTorneo")}
                                            >
                                              {tournamentModeIconNode}
                                            </button>
                                          ) : (
                                            <div
                                              className={`mobile-tournament-mode-icon-shell flex h-[36px] w-[36px] shrink-0 items-center justify-center overflow-hidden rounded-lg border p-1 [@media(min-width:1025px)]:h-[54px] [@media(min-width:1025px)]:w-[54px] ${tournamentModeIconShellClass}`}
                                            >
                                              {tournamentModeIconNode}
                                            </div>
                                          )}
                                          {playerCountIconSrc ? (
                                            <div
                                              className={`mobile-tournament-mode-icon-shell flex h-[36px] w-[36px] shrink-0 items-center justify-center overflow-hidden rounded-lg border p-1 [@media(min-width:1025px)]:h-[54px] [@media(min-width:1025px)]:w-[54px] ${waitingTheme.iconClass}`}
                                            >
                                              <img
                                                src={playerCountIconSrc}
                                                alt={`Jugadores ${memberSize}`}
                                                className="h-full w-full object-contain"
                                              />
                                            </div>
                                          ) : null}
                                          {matchDurationIconSrc ? (
                                            <div
                                              className={`mobile-tournament-mode-icon-shell flex h-[36px] w-[36px] shrink-0 items-center justify-center overflow-hidden rounded-lg border p-1 [@media(min-width:1025px)]:h-[54px] [@media(min-width:1025px)]:w-[54px] ${waitingTheme.iconClass}`}
                                            >
                                              <img
                                                src={matchDurationIconSrc}
                                                alt={`Tiempo ${Math.max(1, Number(match?.havingTime) || 1)} minutos`}
                                                className="h-full w-full object-contain"
                                              />
                                            </div>
                                          ) : null}
                                          {hasMatchPassword ? (
                                            <div
                                              className={`mobile-tournament-mode-icon-shell flex h-[36px] w-[36px] shrink-0 items-center justify-center overflow-hidden rounded-lg border p-1 [@media(min-width:1025px)]:h-[54px] [@media(min-width:1025px)]:w-[54px] ${waitingTheme.iconClass}`}
                                            >
                                              <img
                                                src={MATCH_PASSWORD_ICON_SRC}
                                                alt={i18nT("lobby.conContrasena")}
                                                className="h-full w-full object-contain"
                                              />
                                            </div>
                                          ) : null}
                                        </div>
                                        <div className="min-w-0">
                                          <div
                                            className={`mobile-tournament-name truncate text-[10px] font-black [@media(min-width:1025px)]:text-[15px] ${waitingTheme.titleTextClass}`}
                                          >
                                            {getLocalizedTournamentName(
                                              match?.tournamentName,
                                              "Torneo"
                                            )}
                                          </div>
                                          <div
                                            className={`mobile-landscape-tournament-meta mt-1 flex flex-wrap items-center gap-1 text-[6px] [@media(max-width:1024px)_and_(orientation:portrait)]:mt-0 [@media(min-width:1025px)]:mt-2 [@media(min-width:1025px)]:gap-1.5 [@media(min-width:1025px)]:text-[9px] ${waitingTheme.metaTextClass}`}
                                          >
                                            {tournamentSequenceNumber > 0 ? (
                                              <span
                                                className="mobile-tournament-sequence-badge rounded border border-[#ff65d8] bg-[#4a124a] px-1 py-0.5 text-[6px] font-black uppercase text-[#ffe1ff] shadow-[0_0_10px_rgba(255,101,216,0.22)] [@media(min-width:1025px)]:px-1.5 [@media(min-width:1025px)]:py-[3px] [@media(min-width:1025px)]:text-[9px]"
                                                data-short={formatPublicSequenceShortLabel(
                                                  language,
                                                  tournamentSequenceNumber
                                                )}
                                                title={formatPublicSequenceLabel(
                                                  language,
                                                  "tournament",
                                                  tournamentSequenceNumber
                                                )}
                                              >
                                                {formatPublicSequenceLabel(
                                                  language,
                                                  "tournament",
                                                  tournamentSequenceNumber
                                                )}
                                              </span>
                                            ) : null}
                                            <span
                                              className={`rounded border px-1 py-0.5 text-[6px] font-semibold [@media(min-width:1025px)]:px-1.5 [@media(min-width:1025px)]:py-[3px] [@media(min-width:1025px)]:text-[9px] ${waitingTheme.metaBadgeClass}`}
                                            >
                                              Empieza: {formatCountdownLabel(startRemainingMs)}
                                            </span>
                                            <span
                                              className={`rounded border px-1 py-0.5 text-[6px] font-semibold [@media(min-width:1025px)]:px-1.5 [@media(min-width:1025px)]:py-[3px] [@media(min-width:1025px)]:text-[9px] ${waitingTheme.metaBadgeClass}`}
                                            >
                                              {t("TIEMPO")}: {String(
                                                Math.max(1, Number(match?.havingTime) || 1)
                                              ).padStart(2, "0")}
                                              :00
                                            </span>
                                            <span
                                              className={`hidden rounded border px-1 py-0.5 text-[6px] font-semibold [@media(max-width:1024px)_and_(orientation:portrait)]:inline-flex [@media(min-width:1025px)]:px-1.5 [@media(min-width:1025px)]:py-[3px] [@media(min-width:1025px)]:text-[9px] ${waitingTheme.countBadgeClass}`}
                                            >
                                              {t("UNIDOS")}: {visibleRegisteredCount}
                                            </span>
                                            {hasTournamentWinnerPrize ? (
                                              <span
                                                className={`rounded border px-1 py-0.5 text-[6px] font-semibold [@media(min-width:1025px)]:px-1.5 [@media(min-width:1025px)]:py-[3px] [@media(min-width:1025px)]:text-[9px] ${waitingTheme.metaBadgeClass}`}
                                              >
                                                Premio: {tournamentWinnerPrizeValue}X
                                              </span>
                                            ) : null}
                                            {hasTournamentPayment ? (
                                            <span className="mobile-inline-meta-button rounded border border-[#ffffffde] bg-white px-1 py-0.5 text-[6px] font-semibold text-[#10264a] [@media(min-width:1025px)]:px-1.5 [@media(min-width:1025px)]:py-[3px] [@media(min-width:1025px)]:text-[9px]">
                                              Pago {tournamentRewardValue}
                                            </span>
                                            ) : null}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2 [@media(max-width:1024px)_and_(orientation:portrait)]:absolute [@media(max-width:1024px)_and_(orientation:portrait)]:bottom-2 [@media(max-width:1024px)_and_(orientation:portrait)]:right-2 [@media(max-width:1024px)_and_(orientation:portrait)]:top-2 [@media(max-width:1024px)_and_(orientation:portrait)]:flex-col [@media(max-width:1024px)_and_(orientation:portrait)]:items-end [@media(max-width:1024px)_and_(orientation:portrait)]:justify-between">
                                        {canDeleteUpcomingTournament ? (
                                          <button
                                            type="button"
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              handleDeleteTournamentFromLivePanel(match);
                                            }}
                                            disabled={upcomingDeleteLoading}
                                            className="rounded border border-[#ff8c8c] bg-[#621f1f] px-[0.1875rem] py-[0.09375rem] text-[4.5px] font-bold uppercase tracking-wide text-[#ffdede] transition hover:border-[#ffc3c3] hover:bg-[#8b2a2a] hover:text-white [@media(min-width:1025px)]:px-1.5 [@media(min-width:1025px)]:py-[3px] [@media(min-width:1025px)]:text-[9px] disabled:cursor-not-allowed disabled:opacity-60"
                                          >
                                            {upcomingDeleteLoading ? "..." : "Eliminar"}
                                          </button>
                                        ) : null}
                                        <span
                                          className={`mobile-landscape-tournament-count rounded border px-[0.1875rem] py-[0.09375rem] text-[4.5px] font-semibold [@media(min-width:1025px)]:px-1.5 [@media(min-width:1025px)]:py-[3px] [@media(min-width:1025px)]:text-[9px] [@media(max-width:1024px)_and_(orientation:portrait)]:hidden ${waitingTheme.countBadgeClass}`}
                                        >
                                          {t("UNIDOS")}: {visibleRegisteredCount}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleOpenTournamentFromCard();
                                          }}
                                          disabled={!tournamentJoinEnabled}
                                          className={`mobile-landscape-tournament-join tournament-join-pulse rounded border px-[0.525rem] py-[0.2625rem] text-[6.3px] font-black uppercase tracking-wide transition [@media(max-width:1024px)_and_(orientation:portrait)]:hidden [@media(min-width:1025px)]:px-3 [@media(min-width:1025px)]:py-1.5 [@media(min-width:1025px)]:text-[9px] [@media(max-width:1024px)_and_(orientation:portrait)]:!border-[#79ffa5] [@media(max-width:1024px)_and_(orientation:portrait)]:!bg-[#2de06c] [@media(max-width:1024px)_and_(orientation:portrait)]:!text-[#0f321d] disabled:cursor-not-allowed disabled:opacity-60 ${waitingTheme.joinButtonClass}`}
                                        >
                                          {t("JOIN")}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="rounded-lg border border-dashed border-[#38557f] bg-[#091834] p-4 text-sm text-[#93b4dc]">{i18nT("lobby.noHayTorneosPorEmpezarEnEsteMomento")}</div>
                          )}
                        </div>
                      </div>

                      {!hideLobbyCubeStartPanel &&
                      (!isLobbyFocusedModeRoute || isLobbyTournamentModeRoute) ? (
                        <aside
                          className={`rounded-xl border border-[#355b8e] bg-[#0b1c3f] p-3 shadow-[inset_0_0_0_1px_rgba(111,172,255,0.24)] md:max-h-[520px] md:overflow-y-auto [@media(max-width:1024px)_and_(orientation:landscape)]:max-h-[520px] [@media(max-width:1024px)_and_(orientation:landscape)]:overflow-y-auto ${
                            isLobbyTournamentModeRoute
                              ? "hidden [@media(min-width:1025px)]:block"
                              : ""
                          }`}
                        >
                        <div className="mb-2 text-sm font-black uppercase tracking-[0.16em] text-[#9ed6ff]">
                          {t("MEJORES_JUGADORES")}
                        </div>
                        {modeWinnersLoading ? (
                          <div className="flex h-28 items-center justify-center">
                            <Spin size="small" />
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {modeWinnersSections.map((section) => (
                              (() => {
                                const sectionTheme = resolveModeColorTheme(section.key);
                                return (
                                  <div
                                    key={`mode-winners-${section.key}`}
                                    className={`rounded-lg border p-2 ${sectionTheme.cardClass} ${sectionTheme.cardShadowClass}`}
                                  >
                                    <div className="mb-2 flex items-center justify-between gap-2">
                                      <div
                                        className={`text-[11px] font-black uppercase tracking-[0.12em] ${sectionTheme.iconFallbackTextClass}`}
                                      >
                                        {section.title}
                                      </div>
                                      <div
                                        className={`text-[10px] font-black uppercase tracking-[0.14em] ${sectionTheme.iconFallbackTextClass}`}
                                      >
                                        CIMAX
                                      </div>
                                    </div>
                                    <div className="space-y-1.5">
                                      {[0, 1, 2].map((slotIndex) => {
                                        const player = section.players[slotIndex] || null;
                                        const rank = slotIndex + 1;
                                        const cimaxScore = Math.max(
                                          0,
                                          Math.round(Number(player?.cimaxScore) || 0)
                                        );
                                        const displayName = String(player?.displayName || "").trim();
                                        const avatarUrl = resolveAvatarSrc(player?.avatarUrl || "");
                                        const initials = getInitials(displayName || "Jugador");
                                        const canOpenProfile =
                                          Boolean(player) &&
                                          Boolean(resolveProfileTargetFromUserLike(player));
                                        return (
                                          <div
                                            key={`mode-winner-${section.key}-${rank}`}
                                            className={`flex items-center justify-between rounded border px-2 py-1.5 text-xs ${sectionTheme.metaBadgeClass} ${
                                              canOpenProfile
                                                ? "cursor-pointer transition hover:brightness-110"
                                                : ""
                                            }`}
                                            onClick={
                                              canOpenProfile
                                                ? () => openPlayerProfile(player)
                                                : undefined
                                            }
                                            role={canOpenProfile ? "button" : undefined}
                                            tabIndex={canOpenProfile ? 0 : undefined}
                                            onKeyDown={
                                              canOpenProfile
                                                ? (event) => {
                                                    if (
                                                      event.key !== "Enter" &&
                                                      event.key !== " "
                                                    ) {
                                                      return;
                                                    }
                                                    event.preventDefault();
                                                    openPlayerProfile(player);
                                                  }
                                                : undefined
                                            }
                                          >
                                            <div className="flex min-w-0 items-center pr-2">
                                              <span
                                                className={`mr-1 font-black ${sectionTheme.iconFallbackTextClass}`}
                                              >
                                                #{rank}
                                              </span>
                                              {player ? (
                                                avatarUrl ? (
                                                  <img
                                                    src={avatarUrl}
                                                    alt={displayName || "Jugador"}
                                                    className="mr-2 h-7 w-7 rounded-full border border-white/30 object-cover"
                                                  />
                                                ) : (
                                                  <div className="mr-2 flex h-7 w-7 items-center justify-center rounded-full border border-white/30 bg-black/20 text-[10px] font-black text-white/90">
                                                    {initials}
                                                  </div>
                                                )
                                              ) : (
                                                <div className="mr-2 flex h-7 w-7 items-center justify-center rounded-full border border-white/20 bg-black/20 text-[10px] font-black text-white/70">
                                                  --
                                                </div>
                                              )}
                                              <span
                                                className={`truncate font-semibold ${sectionTheme.titleTextClass}`}
                                              >
                                                {displayName || "---"}
                                              </span>
                                            </div>
                                            <span
                                              className={`shrink-0 rounded border px-2 py-0.5 font-black ${sectionTheme.modeBadgeClass}`}
                                            >
                                              {player ? formatCimaxScoreValue(cimaxScore) : "---"}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })()
                            ))}
                          </div>
                        )}
                        </aside>
                      ) : null}
                    </div>
                  ) : null}

                  {effectiveMatchmakingQuickView === "independientes" ? (
                  <div>
                    <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8fd4ff]">
                      {t("PARTIDOS_INDEPENDIENTES")}
                    </div>
                    {independentMatches.length > 0 ? (
                      <div className="space-y-2">
                        {independentMatches.map((match) => {
                          const tournamentId = String(match?._id || "").trim();
                          const joinedPlayers = getJoinedUiPlayers(tournamentId, match?.users);
                          const joinedUiCount = ensureArray(
                            joinedUiPlayersByTournamentId[tournamentId]
                          ).length;
                          const connectedPlayers = Math.max(
                            0,
                            Number(match?.connectedPlayers) || 0,
                            joinedUiCount
                          );
                          const memberSize = Math.max(1, Number(match?.memberSize) || 1);
                          const isMatchFull = connectedPlayers >= memberSize;
                          const missingPlayers = Math.max(0, memberSize - connectedPlayers);
                          const independentShareUrl = buildLobbyShareUrl(
                            "independent",
                            match?._id
                          );
                          const modeLabel = String(
                            formatModeLabel(match?.mode || "")
                          ).toUpperCase();
                          const hasMatchPassword = Boolean(match?.hasMatchPassword);
                          const independentMatchName = getLocalizedTournamentName(
                            match?.tournamentName,
                            "Partido"
                          );
                          const independentCreatorName = (() => {
                            const titleText = String(independentMatchName || "").trim();
                            const creatorFromTitleMatch = titleText.match(
                              /desafio creado por\s+(.+?)(?:\s*\(cimax.*\))?$/i
                            );
                            const creatorFromTitle = extractPrimaryName(
                              creatorFromTitleMatch?.[1] || ""
                            );
                            if (creatorFromTitle) return creatorFromTitle;

                            const fallbackCandidates = [
                              match?.createdByName,
                              match?.createdByDisplayName,
                              match?.creatorName,
                              match?.ownerName,
                              match?.createdBy,
                            ];
                            for (const candidate of fallbackCandidates) {
                              const parsed = extractPrimaryName(candidate);
                              if (parsed) return parsed;
                            }
                            return "Jugador";
                          })();
                          const independentCreatorProfilePlayer =
                            joinedPlayers.find((player) =>
                              Boolean(resolveProfileTargetFromUserLike(player))
                            ) || null;
                          const independentCreatorProfileTarget =
                            independentCreatorProfilePlayer
                              ? resolveProfileTargetFromUserLike(
                                  independentCreatorProfilePlayer
                                )
                              : null;
                          const independentCreatorProfileLabel =
                            String(
                              independentCreatorProfilePlayer?.name ||
                                independentCreatorProfilePlayer?.displayName ||
                                independentCreatorName
                            ).trim() || independentCreatorName;
                          const modeIconSrc = getModeIconSrc(match?.mode);
                          const playerCountIconSrc = getPlayerCountIconSrc(memberSize);
                          const matchDurationIconSrc = getMatchDurationIconSrc(
                            match?.havingTime
                          );
                          const modeTheme = resolveModeColorTheme(match?.mode);
                          const modeIconShellClass = getTransparentModeIconShellClass(
                            modeTheme.iconClass
                          );
                          const independentJoinedLabel = isMatchFull
                            ? "JUGANDO"
                            : `${t("UNIDOS")}: ${connectedPlayers}`;
                          const independentJoinEnabled = Boolean(match?._id) && !isMatchFull;
                          const handleOpenIndependentFromCard = () => {
                            if (!independentJoinEnabled) return;
                            const independentSequenceNumber =
                              independentFallbackSequenceById[String(match?._id || "").trim()] ||
                              resolvePublicSequenceNumber(match) ||
                              0;
                            handleJoin(String(match?._id || ""), {
                              autoPlay: true,
                              match: {
                                ...match,
                                tournamentUiSequenceNumber: independentSequenceNumber,
                                tournamentSequenceNumber: independentSequenceNumber,
                                tournamentNumber: independentSequenceNumber,
                                displayNumber: independentSequenceNumber,
                              },
                              tournamentUiSequenceNumber: independentSequenceNumber,
                              tournamentSequenceNumber: independentSequenceNumber,
                            });
                          };
                          return (
                            <div
                              key={`independent-match-${String(match?._id || "")}`}
                              onClick={handleOpenIndependentFromCard}
                              onKeyDown={(event) => {
                                if (event.key !== "Enter" && event.key !== " ") return;
                                event.preventDefault();
                                handleOpenIndependentFromCard();
                              }}
                              role={independentJoinEnabled ? "button" : undefined}
                              tabIndex={independentJoinEnabled ? 0 : undefined}
                              className={`rounded-lg border p-3 [@media(max-width:1024px)_and_(orientation:portrait)]:px-1 [@media(max-width:1024px)_and_(orientation:portrait)]:py-0.5 ${modeTheme.cardClass} ${modeTheme.cardShadowClass} ${
                                independentJoinEnabled
                                  ? "cursor-pointer transition hover:brightness-110"
                                  : ""
                              }`}
                            >
                              <div className="flex flex-wrap items-center justify-between gap-3 [@media(max-width:1024px)_and_(orientation:portrait)]:justify-start [@media(max-width:1024px)_and_(orientation:portrait)]:gap-0.5">
                                <div className="flex min-w-0 items-center gap-3 [@media(max-width:1024px)_and_(orientation:portrait)]:gap-0.5">
                                  <div
                                    className={`flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border p-1 [@media(max-width:1024px)_and_(orientation:portrait)]:h-12 [@media(max-width:1024px)_and_(orientation:portrait)]:w-12 [@media(max-width:1024px)_and_(orientation:portrait)]:p-0 ${modeIconShellClass}`}
                                  >
                                    {modeIconSrc ? (
                                      <img
                                        src={modeIconSrc}
                                        alt={modeLabel}
                                        className="h-full w-full object-contain"
                                      />
                                    ) : (
                                      <span
                                        className={`text-[10px] font-black uppercase ${modeTheme.iconFallbackTextClass}`}
                                      >
                                        {modeLabel.slice(0, 3)}
                                      </span>
                                    )}
                                  </div>
                                  {playerCountIconSrc ? (
                                    <div
                                      className={`flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border p-1 [@media(max-width:1024px)_and_(orientation:portrait)]:h-12 [@media(max-width:1024px)_and_(orientation:portrait)]:w-12 [@media(max-width:1024px)_and_(orientation:portrait)]:p-0 ${modeTheme.iconClass}`}
                                    >
                                      <img
                                        src={playerCountIconSrc}
                                        alt={`Jugadores ${memberSize}`}
                                        className="h-full w-full object-contain"
                                      />
                                    </div>
                                  ) : null}
                                  {matchDurationIconSrc ? (
                                    <div
                                      className={`flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border p-1 [@media(max-width:1024px)_and_(orientation:portrait)]:h-12 [@media(max-width:1024px)_and_(orientation:portrait)]:w-12 [@media(max-width:1024px)_and_(orientation:portrait)]:p-0 ${modeTheme.iconClass}`}
                                    >
                                      <img
                                        src={matchDurationIconSrc}
                                        alt={`Tiempo ${Math.max(1, Number(match?.havingTime) || 1)} minutos`}
                                        className="h-full w-full object-contain"
                                      />
                                    </div>
                                  ) : null}
                                  {hasMatchPassword ? (
                                    <div
                                      className={`flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border p-1 [@media(max-width:1024px)_and_(orientation:portrait)]:h-12 [@media(max-width:1024px)_and_(orientation:portrait)]:w-12 [@media(max-width:1024px)_and_(orientation:portrait)]:p-0 ${modeTheme.iconClass}`}
                                    >
                                      <img
                                        src={MATCH_PASSWORD_ICON_SRC}
                                        alt={i18nT("lobby.conContrasena")}
                                        className="h-full w-full object-contain"
                                      />
                                    </div>
                                  ) : null}
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      if (!independentCreatorProfileTarget) return;
                                      openPlayerProfile(independentCreatorProfileTarget);
                                    }}
                                    disabled={!independentCreatorProfileTarget}
                                    className={`hidden max-w-[8.5rem] shrink items-center justify-center truncate rounded border px-2 py-1 text-[10px] font-semibold transition [@media(max-width:1024px)_and_(orientation:portrait)]:inline-flex [@media(max-width:1024px)_and_(orientation:portrait)]:self-center [@media(max-width:1024px)_and_(orientation:portrait)]:px-1 [@media(max-width:1024px)_and_(orientation:portrait)]:py-0.5 disabled:cursor-not-allowed disabled:opacity-60 ${modeTheme.metaBadgeClass}`}
                                  >
                                    {independentCreatorProfileLabel}
                                  </button>
                                  <span
                                    className={`hidden shrink-0 rounded border px-2 py-1 text-[10px] font-semibold [@media(max-width:1024px)_and_(orientation:portrait)]:inline-flex [@media(max-width:1024px)_and_(orientation:portrait)]:px-1 [@media(max-width:1024px)_and_(orientation:portrait)]:py-0.5 ${modeTheme.countBadgeClass}`}
                                  >
                                    {independentJoinedLabel}
                                  </span>
                                  <div className="min-w-0">
                                    <div
                                      className={`truncate text-sm font-black uppercase tracking-wide ${modeTheme.titleTextClass}`}
                                    >
                                      <span className="[@media(max-width:1024px)_and_(orientation:portrait)]:hidden">
                                        {independentMatchName}
                                      </span>
                                      <span className="hidden">{i18nT("lobby.desafio")}</span>
                                    </div>
                                    <div
                                      className={`mt-1 flex flex-wrap items-center gap-2 text-xs ${modeTheme.metaTextClass}`}
                                    >
                                      <span
                                        className={`rounded border px-2 py-0.5 font-semibold [@media(max-width:1024px)_and_(orientation:portrait)]:hidden ${modeTheme.modeBadgeClass}`}
                                      >
                                        {modeLabel}
                                      </span>
                                      <span
                                        className={`rounded border px-2 py-0.5 font-semibold [@media(max-width:1024px)_and_(orientation:portrait)]:hidden ${modeTheme.metaBadgeClass}`}
                                      >
                                        {t("JUGADORES")}: {memberSize}
                                      </span>
                                      <span
                                        className={`rounded border px-2 py-0.5 font-semibold [@media(max-width:1024px)_and_(orientation:portrait)]:hidden ${modeTheme.metaBadgeClass}`}
                                      >
                                        {t("TIEMPO")}: {Math.max(1, Number(match?.havingTime) || 1)} min
                                      </span>
                                      {missingPlayers > 0 ? (
                                        <span
                                          className={`rounded border px-2 py-0.5 font-semibold [@media(max-width:1024px)_and_(orientation:portrait)]:hidden ${modeTheme.metaBadgeClass}`}
                                        >
                                          {missingPlayers === 1 ? t("ESPERANDO_1_JUGADOR") : `Esperando ${missingPlayers} jugadores`}
                                        </span>
                                      ) : null}
                                      {missingPlayers > 0 && independentShareUrl ? (
                                        <button
                                          type="button"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            handleCopyShareLink(independentShareUrl);
                                          }}
                                          className={`mobile-inline-meta-button rounded border px-2 py-0.5 font-semibold transition [@media(max-width:1024px)_and_(orientation:portrait)]:hidden ${modeTheme.shareButtonClass}`}
                                        >
                                          {tx("Copiar enlace", "Copy Link")}
                                        </button>
                                      ) : null}
                                      <span
                                        className={`rounded border px-2 py-0.5 font-semibold [@media(max-width:1024px)_and_(orientation:portrait)]:hidden ${modeTheme.metaBadgeClass}`}
                                      >
                                        {independentCreatorName}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`rounded border px-2 py-1 text-xs font-semibold [@media(max-width:1024px)_and_(orientation:portrait)]:hidden ${modeTheme.countBadgeClass}`}
                                  >
                                    {isMatchFull ? "JUGANDO" : `${t("UNIDOS")}: ${connectedPlayers}`}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleOpenIndependentFromCard();
                                    }}
                                    disabled={!independentJoinEnabled}
                                    className={`rounded border px-3 py-1 text-xs font-black uppercase tracking-wide transition [@media(max-width:1024px)_and_(orientation:portrait)]:hidden disabled:cursor-not-allowed disabled:opacity-60 ${modeTheme.joinButtonClass}`}
                                  >
                                    {t("JOIN")}
                                  </button>
                                </div>
                              </div>
                              {joinedPlayers.length > 0 ? (
                                <div className="mt-2 flex flex-wrap items-center gap-2 [@media(max-width:1024px)_and_(orientation:portrait)]:hidden">
                                  {joinedPlayers.map((joinedPlayer, joinedIndex) => {
                                    const profileTarget =
                                      resolveProfileTargetFromUserLike(joinedPlayer);
                                    const label = String(
                                      joinedPlayer?.name ||
                                        joinedPlayer?.displayName ||
                                        joinedPlayer?.secondary ||
                                        `Jugador ${joinedIndex + 1}`
                                    ).trim();
                                    return (
                                      <button
                                        key={`independent-player-${tournamentId}-${joinedIndex}-${label}`}
                                        type="button"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          if (!profileTarget) return;
                                          openPlayerProfile(profileTarget);
                                        }}
                                        disabled={!profileTarget}
                                        className="rounded border border-[#4f6cc9] bg-[#142455] px-2 py-0.5 text-xs text-[#d7e5ff] transition hover:border-[#7ca2ff] hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                                      >
                                        {label}
                                      </button>
                                    );
                                  })}
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed border-[#38557f] bg-[#091834] p-4 text-sm text-[#93b4dc]">{i18nT("lobby.noHayPartidosIndependientesDisponibles")}</div>
                    )}
                  </div>
                  ) : null}

                  {effectiveMatchmakingQuickView === "puzzles" ? (
                    <div className="xima-puzzles-dashboard">
                      <header className="xima-puzzles-header">
                        <div className="xima-puzzles-title">
                          <span>
                            <IoExtensionPuzzleOutline aria-hidden="true" />
                          </span>
                          <div>
                            <h2>{getPuzzleDashboardCopy(language, "title")}</h2>
                            <p>{getPuzzleDashboardCopy(language, "subtitle")}</p>
                          </div>
                        </div>
                        <div className="xima-puzzles-header-actions">
                          {hasDirectSuperuserAccess ? (
                            <button
                              type="button"
                              onClick={handleTogglePuzzleEditMode}
                              className={`xima-puzzles-ghost-button ${
                                puzzleEditModeEnabled ? "is-danger" : ""
                              }`}
                            >
                              {puzzleEditModeEnabled
                                ? getPuzzleDashboardCopy(language, "editPuzzleOn")
                                : getPuzzleDashboardCopy(language, "editPuzzle")}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => {
                              fetchTournaments().catch((error) => console.log(error));
                              fetchPuzzles(true).catch((error) => console.log(error));
                              fetchLiveMatches().catch((error) => console.log(error));
                              fetchModeWinnersLeaderboard(false).catch((error) =>
                                console.log(error)
                              );
                            }}
                            className="xima-puzzles-ghost-button"
                          >
                            {getPuzzleDashboardCopy(language, "refresh")}
                            <IoRefreshOutline aria-hidden="true" />
                          </button>
                        </div>
                      </header>

                      <section className="xima-puzzles-summary-grid">
                        {[
                          {
                            key: "resolved",
                            label: getPuzzleDashboardCopy(language, "puzzlesSolved"),
                            value: `${formatPuzzleDashboardInteger(
                              puzzleDashboardStats.solved,
                              language
                            )} / ${formatPuzzleDashboardInteger(puzzleDashboardStats.total, language)}`,
                            detail: getPuzzleDashboardCopy(language, "percentOfTotal", {
                              percent: formatPuzzleDashboardPercent(
                                puzzleDashboardStats.successRate,
                                1,
                                language
                              ),
                            }),
                            Icon: IoCheckmarkCircleOutline,
                            tone: "is-purple",
                          },
                          {
                            key: "attempts",
                            label: getPuzzleDashboardCopy(language, "totalAttempts"),
                            value: formatPuzzleDashboardInteger(
                              puzzleDashboardStats.totalAttempts,
                              language
                            ),
                            detail: getPuzzleDashboardCopy(language, "averagePerPuzzle", {
                              value: formatPuzzleDashboardDecimal(
                                puzzleDashboardStats.averageAttempts,
                                2,
                                language
                              ),
                            }),
                            Icon: IoGameControllerOutline,
                            tone: "is-blue",
                          },
                          {
                            key: "success",
                            label: getPuzzleDashboardCopy(language, "success"),
                            value: formatPuzzleDashboardInteger(puzzleDashboardStats.solved, language),
                            detail: formatPuzzleDashboardPercent(
                              puzzleDashboardStats.successRate,
                              1,
                              language
                            ),
                            Icon: IoTrophyOutline,
                            tone: "is-green",
                          },
                          {
                            key: "failures",
                            label: getPuzzleDashboardCopy(language, "failures"),
                            value: formatPuzzleDashboardInteger(puzzleDashboardStats.failed, language),
                            detail: formatPuzzleDashboardPercent(
                              puzzleDashboardStats.failedRate,
                              1,
                              language
                            ),
                            Icon: IoCloseCircleOutline,
                            tone: "is-red",
                          },
                          {
                            key: "moves",
                            label: getPuzzleDashboardCopy(language, "averageMoves"),
                            value: formatPuzzleDashboardDecimal(
                              puzzleDashboardStats.averageMoves,
                              2,
                              language
                            ),
                            detail: getPuzzleDashboardCopy(language, "bestWorstMoves", {
                              best: formatPuzzleDashboardInteger(
                                puzzleDashboardStats.bestMoves,
                                language
                              ),
                              worst: formatPuzzleDashboardInteger(
                                puzzleDashboardStats.worstMoves,
                                language
                              ),
                            }),
                            Icon: IoStatsChartOutline,
                            tone: "is-gold",
                          },
                          {
                            key: "rewards",
                            label: getPuzzleDashboardCopy(language, "totalRewards"),
                            value: formatPuzzleDashboardInteger(
                              puzzleDashboardStats.totalRewards,
                              language
                            ),
                            detail: getPuzzleDashboardCopy(language, "bonusesEarned"),
                            Icon: IoGiftOutline,
                            tone: "is-cyan",
                          },
                        ].map((card) => {
                          const CardIcon = card.Icon;
                          return (
                            <article key={card.key} className={`xima-puzzles-summary-card ${card.tone}`}>
                              <span>
                                <CardIcon aria-hidden="true" />
                              </span>
                              <div>
                                <small>{card.label}</small>
                                <strong>{card.value}</strong>
                                <em>{card.detail}</em>
                              </div>
                            </article>
                          );
                        })}
                      </section>

                      <section className="xima-puzzles-filterbar">
                        <div className="xima-puzzles-mode-filters">
                          <strong>{getPuzzleDashboardCopy(language, "filterByMode")}</strong>
                          {[
                            { key: "all", label: getPuzzleDashboardCopy(language, "all"), Icon: IoListOutline },
                            { key: "free", label: getPuzzleDashboardModeLabel(language, "free"), Icon: IoCubeOutline },
                            { key: "cienzar", label: getPuzzleDashboardModeLabel(language, "cienzar"), Icon: IoBeakerOutline },
                            { key: "dice", label: getPuzzleDashboardModeLabel(language, "dice"), Icon: IoDiceOutline },
                          ].map((filter) => {
                            const FilterIcon = filter.Icon;
                            const active =
                              effectivePuzzleDashboardModeFilter === filter.key ||
                              (filter.key === "all" && effectivePuzzleDashboardModeFilter === "all");
                            return (
                              <button
                                key={filter.key}
                                type="button"
                                onClick={() => setPuzzleDashboardModeFilter(filter.key)}
                                className={active ? "is-active" : ""}
                              >
                                <FilterIcon aria-hidden="true" />
                                {filter.label}
                              </button>
                            );
                          })}
                        </div>
                        <label className="xima-puzzles-search">
                          <IoSearchOutline aria-hidden="true" />
                          <input
                            type="search"
                            value={puzzleDashboardSearch}
                            onChange={(event) =>
                              setPuzzleDashboardSearch(String(event?.target?.value || ""))
                            }
                            placeholder={getPuzzleDashboardCopy(language, "searchPuzzle")}
                          />
                        </label>
                        <label className="xima-puzzles-sort">
                          <span>{getPuzzleDashboardCopy(language, "sortBy")}</span>
                          <select
                            value={puzzleDashboardSort}
                            onChange={(event) =>
                              setPuzzleDashboardSort(String(event?.target?.value || "recent"))
                            }
                          >
                            <option value="recent">{getPuzzleDashboardCopy(language, "mostRecent")}</option>
                            <option value="oldest">{getPuzzleDashboardCopy(language, "oldest")}</option>
                            <option value="reward">{getPuzzleDashboardCopy(language, "highestReward")}</option>
                            <option value="moves">{getPuzzleDashboardCopy(language, "fewestMoves")}</option>
                          </select>
                        </label>
                      </section>

                      {hasDirectSuperuserAccess && puzzleEditModeEnabled ? (
                        <div className="xima-puzzles-edit-alert">
                          {getPuzzleDashboardCopy(language, "editModeActive")}
                        </div>
                      ) : null}
                      {puzzlesLoading ? (
                        <div className="xima-puzzles-empty">
                          {getPuzzleDashboardCopy(language, "loadingPuzzles")}
                        </div>
                      ) : hasVisiblePuzzleEntries ? (
                        <>
                          <section className="xima-puzzles-mode-grid">
                            {puzzleDashboardColumns.map((column) => {
                              const ModeIcon = column.meta.Icon;
                              return (
                                <article
                              key={`puzzle-column-${column.mode}`}
                                  className={`xima-puzzles-mode-panel ${column.meta.toneClass}`}
                            >
                                  <header>
                                    <div>
                                      <ModeIcon aria-hidden="true" />
                                      <strong>
                                        {column.title} ({formatPuzzleDashboardInteger(column.totalCount, language)})
                                      </strong>
                                    </div>
                                    <span>
                                      {formatPuzzleDashboardPercent(
                                        puzzleDashboardStats.modeStats.find(
                                          (item) => item.mode === column.mode
                                        )?.successRate || 0,
                                        0,
                                        language
                                      )}
                                    </span>
                                  </header>
                              {column.entries.length > 0 ? (
                                    <div className="xima-puzzles-table">
                                      <div className="xima-puzzles-table-head">
                                        <span>{getPuzzleDashboardCopy(language, "puzzle")}</span>
                                        <span>{getPuzzleDashboardCopy(language, "attempts")}</span>
                                        <span>{getPuzzleDashboardCopy(language, "moves")}</span>
                                        <span>{getPuzzleDashboardCopy(language, "time")}</span>
                                        <span>{getPuzzleDashboardCopy(language, "result")}</span>
                                        <span>{getPuzzleDashboardCopy(language, "reward")}</span>
                                      </div>
                                      <div className="xima-puzzles-table-scroll">
                                  {column.entries.map(({ puzzle }, columnIndex) => {
                                          const entry = column.entries[columnIndex];
                                    const puzzleId = String(
                                      puzzle?.id || puzzle?._id || puzzle?.tournamentId || ""
                                    ).trim();
                                    const puzzleShareUrl = buildLobbyShareUrl("puzzle", puzzleId);
                                    const puzzleRewardClaimLoading =
                                      claimingPuzzleRewardId === puzzleId;
                                          const puzzleLaunchLoading = launchingPuzzleId === puzzleId;
                                          const puzzleRewardLabel = puzzleRewardClaimLoading
                                      ? getPuzzleDashboardCopy(language, "claiming")
                                            : entry.failed
                                            ? "-"
                                            : entry.claimed
                                            ? `${entry.rewardValue}X`
                                            : entry.rewardCanClaim
                                            ? getPuzzleDashboardCopy(language, "claimReward", {
                                                reward: `${entry.rewardValue}X`,
                                              })
                                            : `${entry.rewardValue}X`;
                                    const puzzleEditInteractive =
                                      hasDirectSuperuserAccess &&
                                      puzzleEditModeEnabled &&
                                      Boolean(puzzleId);
                                    const puzzleEditSelected =
                                      puzzleEditInteractive && puzzleEditTargetId === puzzleId;
                                          const rowStatusClass = entry.failed
                                            ? "is-failed"
                                            : entry.solved
                                            ? "is-success"
                                            : "is-pending";
                                    return (
                                      <div
                                        key={`puzzle-entry-${column.mode}-${puzzleId || columnIndex}`}
                                                className={`xima-puzzles-row ${rowStatusClass}`}
                                      >
                                                <div className="xima-puzzles-cell is-puzzle">
                                            {puzzleEditInteractive ? (
                                              <button
                                                type="button"
                                                onClick={() => openPuzzleEditModal(puzzle)}
                                                        className={`xima-puzzles-row-icon ${
                                                          puzzleEditSelected ? "is-editing" : ""
                                                        }`}
                                                title={getPuzzleDashboardCopy(language, "editPuzzle")}
                                              >
                                                        <ModeIcon aria-hidden="true" />
                                              </button>
                                            ) : (
                                                      <span className="xima-puzzles-row-icon">
                                                        <ModeIcon aria-hidden="true" />
                                                      </span>
                                            )}
                                                  <div>
                                                    <strong>#{entry.modeOrder}</strong>
                                                    <small>{entry.title}</small>
                                          </div>
                                                  {puzzleShareUrl ? (
                                            <button
                                              type="button"
                                                      onClick={() => handleCopyShareLink(puzzleShareUrl)}
                                                      className="xima-puzzles-copy-button"
                                                    >
                                                      {getPuzzleDashboardCopy(language, "link")}
                                                    </button>
                                                  ) : null}
                                                </div>
                                                <div className="xima-puzzles-cell">
                                                  {entry.attemptCount}
                                                </div>
                                                <div className="xima-puzzles-cell">
                                                  {entry.movementLabel}
                                                </div>
                                                <div className="xima-puzzles-cell">
                                                  <span>{entry.timeLabel}</span>
                                                  <small>{entry.bestTimeLabel}</small>
                                                </div>
                                                <div className="xima-puzzles-cell">
                                                  <button
                                                    type="button"
                                                    onClick={() => handleStartPuzzle(puzzle)}
                                                    disabled={!puzzleId || puzzleLaunchLoading}
                                                    className={`xima-puzzles-result-button ${rowStatusClass}`}
                                                  >
                                                    {puzzleLaunchLoading
                                                      ? getPuzzleDashboardCopy(language, "opening")
                                                      : entry.failed
                                                      ? getPuzzleDashboardCopy(language, "failed")
                                                      : entry.solved
                                                      ? getPuzzleDashboardCopy(language, "success")
                                                      : getPuzzleDashboardCopy(language, "solve")}
                                                  </button>
                                                </div>
                                                <div className="xima-puzzles-cell">
                                                  <button
                                                    type="button"
                                              onClick={() => {
                                                      if (!entry.rewardCanClaim || puzzleRewardClaimLoading) return;
                                                handleClaimPuzzleReward(puzzle);
                                              }}
                                              disabled={puzzleRewardClaimLoading}
                                                    className={`xima-puzzles-reward-button ${
                                                      entry.rewardCanClaim ? "can-claim" : ""
                                                    }`}
                                            >
                                              {puzzleRewardLabel}
                                            </button>
                                                </div>
                                      </div>
                                    );
                                  })}
                                      </div>
                                </div>
                              ) : (
                                    <div className="xima-puzzles-empty is-compact">
                                  {getPuzzleDashboardCopy(language, "emptyMode")}
                                </div>
                              )}
                                  <button
                                    type="button"
                                    className="xima-puzzles-see-all"
                                    onClick={() => {
                                      setPuzzleDashboardModeFilter(column.mode);
                                      setPuzzleDashboardSearch("");
                                    }}
                                  >
                                    {getPuzzleDashboardCopy(language, "viewAllPuzzlesWithCount", {
                                      count: formatPuzzleDashboardInteger(column.totalCount, language),
                                    })}
                                  </button>
                                </article>
                              );
                            })}
                          </section>
                          <section className="xima-puzzles-analytics-grid">
                            <article className="xima-puzzles-analytics-card">
                              <h3>{getPuzzleDashboardCopy(language, "successRateByMode")}</h3>
                              <div className="xima-puzzles-donut-row">
                                <div
                                  className="xima-puzzles-donut is-success"
                                  style={{
                                    "--xima-puzzles-rate-angle": `${
                                      Math.min(100, puzzleDashboardStats.successRate) * 3.6
                                    }deg`,
                                  }}
                                >
                                  <strong>
                                    {formatPuzzleDashboardPercent(
                                      puzzleDashboardStats.successRate,
                                      0,
                                      language
                                    )}
                                  </strong>
                                  <span>{getPuzzleDashboardCopy(language, "overallSuccess")}</span>
                                </div>
                                <div className="xima-puzzles-legend-list">
                                  {puzzleDashboardStats.modeStats.map((modeStat) => (
                                    <span key={`success-${modeStat.mode}`} className={modeStat.meta.toneClass}>
                                      <i />
                                      {modeStat.title} {formatPuzzleDashboardInteger(modeStat.solved, language)} /{" "}
                                      {formatPuzzleDashboardInteger(modeStat.count, language)} (
                                      {formatPuzzleDashboardPercent(modeStat.successRate, 0, language)})
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </article>
                            <article className="xima-puzzles-analytics-card">
                              <h3>{getPuzzleDashboardCopy(language, "averageMovesByMode")}</h3>
                              <div className="xima-puzzles-bars">
                                {puzzleDashboardStats.modeStats.map((modeStat) => (
                                  <div key={`moves-${modeStat.mode}`} className={modeStat.meta.toneClass}>
                                    <span>{modeStat.title}</span>
                                    <div>
                                      <i style={{ width: `${Math.max(4, modeStat.movesShare)}%` }} />
                                    </div>
                                    <strong>{formatPuzzleDashboardDecimal(modeStat.averageMoves, 2, language)}</strong>
                                  </div>
                                ))}
                              </div>
                            </article>
                            <article className="xima-puzzles-analytics-card">
                              <h3>{getPuzzleDashboardCopy(language, "rewardsEarnedByMode")}</h3>
                              <div className="xima-puzzles-donut-row">
                                <div
                                  className="xima-puzzles-donut is-reward"
                                  style={{
                                    background: (() => {
                                      const total = Math.max(1, puzzleDashboardStats.totalRewards);
                                      const ciencia =
                                        ((puzzleDashboardStats.modeStats.find((item) => item.mode === "free")
                                          ?.rewards || 0) /
                                          total) *
                                        100;
                                      const cienzar =
                                        ((puzzleDashboardStats.modeStats.find((item) => item.mode === "cienzar")
                                          ?.rewards || 0) /
                                          total) *
                                        100;
                                      return `conic-gradient(#0e8bff 0 ${ciencia}%, #19c66b ${ciencia}% ${
                                        ciencia + cienzar
                                      }%, #e5a900 ${ciencia + cienzar}% 100%)`;
                                    })(),
                                  }}
                                >
                                  <strong>
                                    {formatPuzzleDashboardInteger(
                                      puzzleDashboardStats.totalRewards,
                                      language
                                    )}
                                  </strong>
                                  <span>{getPuzzleDashboardCopy(language, "total")}</span>
                                </div>
                                <div className="xima-puzzles-legend-list">
                                  {puzzleDashboardStats.modeStats.map((modeStat) => (
                                    <span key={`reward-${modeStat.mode}`} className={modeStat.meta.toneClass}>
                                      <i />
                                      {modeStat.title} {formatPuzzleDashboardInteger(modeStat.rewards, language)}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </article>
                            <article className="xima-puzzles-analytics-card">
                              <h3>{getPuzzleDashboardCopy(language, "averageTimeByMode")}</h3>
                              <div className="xima-puzzles-bars">
                                {puzzleDashboardStats.modeStats.map((modeStat) => (
                                  <div key={`time-${modeStat.mode}`} className={modeStat.meta.toneClass}>
                                    <span>{modeStat.title}</span>
                                    <div>
                                      <i style={{ width: `${Math.max(4, modeStat.timeShare)}%` }} />
                                    </div>
                                    <strong>{formatPuzzleDashboardTime(modeStat.averageTime)}</strong>
                                  </div>
                                ))}
                              </div>
                            </article>
                          </section>
                        </>
                      ) : (
                        <div className="xima-puzzles-empty">
                          {puzzleModeFilter
                            ? getPuzzleDashboardCopy(language, "emptySavedByMode", {
                                mode: formatFreeBoxBaseModeLabel(
                                  puzzleModeFilter,
                                  language
                                ).toLowerCase(),
                              })
                            : getPuzzleDashboardCopy(language, "emptySaved")}
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {!isNewsTab &&
            !isAdminTab &&
            !isHistoryTab &&
            !hideLobbyCubeStartPanel &&
            !hideMobileCreateGameGlobalChat &&
            !hideLobbyCreateMobilePanels &&
            !hideTournamentSelectionAndFriendsInPortrait &&
            !hideOnlineFriendsPanelOnPhoneMatchmaking &&
            !hidePuzzleAuxPanelsInPortrait &&
            !isLobbyFocusedModeRoute ? (
              <div className="rounded-xl border border-[#c8a347] bg-[#070f24e6] p-3 shadow-[0_0_30px_rgba(0,0,0,0.35)]">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#f2cf7a]">
                      {t("AMIGOS_ONLINE")}
                    </div>
                    <div className="text-base font-black text-[#ffe3a5]">
                      {t("AMIGOS_CONECTADOS_AHORA")}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => fetchOnlineFriends(false).catch((error) => console.log(error))}
                    className="rounded border border-[#c8a347] bg-[#11305c] px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#ffe8b2] transition hover:border-[#f3d583] hover:text-white"
                  >
                    {t("ACTUALIZAR")}
                  </button>
                </div>

                {onlineFriendsLoading ? (
                  <div className="rounded-lg border border-dashed border-[#38557f] bg-[#091834] p-4 text-sm text-[#93b4dc]">
                    {tx("Cargando amigos online...", "Loading friends online...")}
                  </div>
                ) : onlineFriends.length > 0 ? (
                  <div className="space-y-2">
                    {onlineFriends.map((friendItem) => {
                      const friendUser = friendItem?.user || {};
                      const friendStatusKey = String(friendItem?.status || "")
                        .trim()
                        .toLowerCase();
                      const statusMeta =
                        FRIEND_PRESENCE_META[friendStatusKey] || FRIEND_PRESENCE_META.online;
                      const friendName = String(
                        friendUser?.displayName ||
                          friendUser?.firstName ||
                          friendUser?.email ||
                          tx("Amigo", "Friend")
                      ).trim();
                      const friendCode = String(friendUser?.playerCode || "")
                        .trim()
                        .toUpperCase();
                      const hasProfileRoute = Boolean(friendCode);

                      return (
                        <div
                          key={String(friendItem?.friendshipId || friendUser?.id || friendUser?.email || friendName)}
                          className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-[#0b1d3d] p-3 ${
                            hasProfileRoute
                              ? "cursor-pointer border-[#c8a347] transition hover:border-[#f3d583] hover:bg-[#123160]"
                              : "border-[#c8a347]"
                          }`}
                          onClick={() => {
                            if (!hasProfileRoute) return;
                            navigate(`/game/player/${encodeURIComponent(friendCode)}`);
                          }}
                          role={hasProfileRoute ? "button" : undefined}
                          tabIndex={hasProfileRoute ? 0 : undefined}
                          onKeyDown={(event) => {
                            if (!hasProfileRoute) return;
                            if (event.key !== "Enter" && event.key !== " ") return;
                            event.preventDefault();
                            navigate(`/game/player/${encodeURIComponent(friendCode)}`);
                          }}
                        >
                          <div className="min-w-0">
                            <div className="truncate text-sm font-black uppercase tracking-wide text-[#e7f3ff]">
                              {friendName}
                            </div>
                            <div className="mt-1 text-xs text-[#9fc3ea]">
                              {friendCode ? `Code: ${friendCode}` : "Sin codigo"}
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center justify-end gap-2">
                            <span
                              className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${statusMeta.badgeClass}`}
                            >
                              {statusMeta.label}
                            </span>
                            {hasProfileRoute ? (
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  navigate(`/game/player/${encodeURIComponent(friendCode)}`);
                                }}
                                className="rounded border border-[#57c4ff] bg-[#114a7b] px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-[#dff5ff] transition hover:border-[#7fe0ff] hover:text-white"
                              >{i18nT("lobby.verPerfil")}</button>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-[#38557f] bg-[#091834] p-4 text-sm text-[#93b4dc]">
                    {t("NO_HAY_AMIGOS_ONLINE_EN_ESTE_MOMENTO")}
                  </div>
                )}
              </div>
            ) : null}

            {isNewsTab ? (
              <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-[#24406f] bg-[#070f24e6] p-2 shadow-[0_0_30px_rgba(0,0,0,0.35)]">
                <div className="mb-3 rounded border border-[#2a446f] bg-[#07152e] p-3">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-[22px] font-semibold uppercase tracking-[0.16em] text-[#9ecbff]">
                        {t("MENSAJES_DE_NOTICIAS")}
                      </div>
                      <div className="text-2xl font-black text-[#e8f3ff]">
                        {canEditNewsBoardMessages
                          ? tx(
                              "Escribe aqui y guarda en esta misma seccion",
                              "Write here and save to this section"
                            )
                          : tx(
                              "Noticias publicadas por administradores y superusuarios",
                              "News published by administrators and superusers"
                            )}
                      </div>
                    </div>
                    <span className="rounded border border-[#3b5f92] bg-[#10274d] px-3 py-1 text-[22px] font-semibold uppercase tracking-wide text-[#c8e6ff]">
                      {orderedNewsBoardMessages.length} {tx("mensajes", "messages")}
                    </span>
                  </div>

                  {canEditNewsBoardMessages ? (
                    <div className="rounded border border-[#35628e] bg-[#0a1b39] p-4">
                      <textarea
                        value={newsBoardInput}
                        onChange={(event) => setNewsBoardInput(event.target.value)}
                        onKeyDown={(event) => {
                          if (!event.ctrlKey && !event.metaKey) return;
                          if (event.key !== "Enter") return;
                          event.preventDefault();
                          handleSaveNewsBoardMessage();
                        }}
                        placeholder={t("ESCRIBE_TU_MENSAJE_PARA_NOTICIAS")}
                        className="h-48 w-full resize-none rounded border border-[#3f6ca2] bg-[#08162f] p-4 text-2xl text-[#ebf5ff] outline-none transition focus:border-[#73d1ff]"
                      />
                      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                        <div className="text-[22px] text-[#9ecbff]">
                          Caracteres: {String(newsBoardInput || "").length}
                        </div>
                        <button
                          type="button"
                          onClick={handleSaveNewsBoardMessage}
                          disabled={!String(newsBoardInput || "").trim()}
                          className="rounded border border-[#4fc3ff88] bg-[#11305c] px-4 py-2 text-[22px] font-semibold uppercase tracking-wide text-[#b7e4ff] transition hover:border-[#7ee5ff] hover:text-white disabled:cursor-not-allowed disabled:border-[#3a4860] disabled:bg-[#1b2233] disabled:text-[#7d8ea8]"
                        >
                          {t("GUARDAR_MENSAJE")}
                        </button>
                      </div>
                    </div>
                  ) : null}

                  <div className="mt-3 max-h-[320px] space-y-2 overflow-y-auto pr-1">
                    {orderedNewsBoardMessages.length > 0 ? (
                      orderedNewsBoardMessages.map((entry, index) => {
                        const entryId = String(entry?.id || "").trim();
                        const isEditingMessage =
                          Boolean(newsBoardEditingMessageId) &&
                          Boolean(entryId) &&
                          newsBoardEditingMessageId === entryId;
                        return (
                          <div
                            key={String(entry?.id || `news-message-${index}`)}
                            className="rounded border border-[#35557f] bg-[#0d2448] p-3"
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="text-2xl font-semibold uppercase tracking-wide text-[#cae7ff]">
                                {entry?.author || "Jugador"}
                              </div>
                              <div className="flex flex-wrap items-center gap-2">
                                <div className="text-xl text-[#92b9e6]">
                                  {moment(entry?.createdAt || Date.now()).format("YYYY-MM-DD HH:mm")}
                                </div>
                                {canDeleteNewsBoardMessage(entry) ? (
                                  <button
                                    type="button"
                                    onClick={() => handleStartEditNewsBoardMessage(entry)}
                                    className="rounded border border-[#7bb9ff] bg-[#12386a] px-2 py-1 text-lg font-bold uppercase tracking-wide text-[#d8ecff] transition hover:border-[#a6d3ff] hover:text-white"
                                  >
                                    {tx("Editar", "Edit")}
                                  </button>
                                ) : null}
                                {canDeleteNewsBoardMessage(entry) ? (
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteNewsBoardMessage(entry?.id)}
                                    className="rounded border border-[#ff9a9a] bg-[#5a1a1a] px-2 py-1 text-lg font-bold uppercase tracking-wide text-[#ffd7d7] transition hover:border-[#ffc3c3] hover:text-white"
                                  >
                                    {tx("Eliminar", "Delete")}
                                  </button>
                                ) : null}
                              </div>
                            </div>
                            {isEditingMessage ? (
                              <div className="mt-2 rounded border border-[#3f6ca2] bg-[#08162f] p-2">
                                <textarea
                                  value={newsBoardEditingInput}
                                  onChange={(event) => setNewsBoardEditingInput(event.target.value)}
                                  onKeyDown={(event) => {
                                    if (!event.ctrlKey && !event.metaKey) return;
                                    if (event.key !== "Enter") return;
                                    event.preventDefault();
                                    handleUpdateNewsBoardMessage(entryId);
                                  }}
                                  className="h-28 w-full resize-none rounded border border-[#3f6ca2] bg-[#08162f] p-3 text-2xl text-[#ebf5ff] outline-none transition focus:border-[#73d1ff]"
                                  placeholder={tx(
                                    "Edita el mensaje y guarda los cambios",
                                    "Edit the message and save changes"
                                  )}
                                />
                                <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={handleCancelEditNewsBoardMessage}
                                    className="rounded border border-[#6a7f9f] bg-[#1a2a44] px-3 py-1 text-lg font-semibold uppercase tracking-wide text-[#d7e6ff] transition hover:border-[#9db5dd] hover:text-white"
                                  >
                                    {tx("Cancelar", "Cancel")}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleUpdateNewsBoardMessage(entryId)}
                                    disabled={!String(newsBoardEditingInput || "").trim()}
                                    className="rounded border border-[#4fc3ff88] bg-[#11305c] px-3 py-1 text-lg font-semibold uppercase tracking-wide text-[#b7e4ff] transition hover:border-[#7ee5ff] hover:text-white disabled:cursor-not-allowed disabled:border-[#3a4860] disabled:bg-[#1b2233] disabled:text-[#7d8ea8]"
                                  >
                                    {tx("Guardar cambios", "Save changes")}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="mt-2 whitespace-pre-wrap text-2xl text-[#e4f1ff]">
                                {entry?.text || ""}
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="rounded border border-dashed border-[#3a5f94] bg-[#0b1d3d] p-3 text-2xl text-[#98badf]">
                        {t("NO_HAY_MENSAJES_GUARDADOS")}
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded border border-[#2a446f] bg-[#07152e] p-3">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#9ecbff]">
                        {t("CALENDARIO_DE_TORNEOS")}
                      </div>
                      <div className="text-sm font-black text-[#e8f3ff]">
                        {tx(
                          "Disponibles ahora y programados para el futuro",
                          "Available now and scheduled for the future"
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => fetchTournaments().catch((error) => console.log(error))}
                      className="rounded border border-[#4fc3ff88] bg-[#11305c] px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#b7e4ff] transition hover:border-[#7ee5ff] hover:text-white"
                    >
                      {t("ACTUALIZAR")}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                    <div className="rounded border border-[#35628e] bg-[#0a1b39] p-2">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#9fd7ff]">
                        {t("TORNEOS")} ({newsTournamentCalendar.available.length})
                      </div>
                      {newsTournamentCalendar.available.length > 0 ? (
                        <div className="max-h-[320px] space-y-2 overflow-y-auto pr-1">
                          {newsTournamentCalendar.available.map((record, index) => {
                            const startLabel = record?.hasStartDate
                              ? moment(record.startMs).format("YYYY-MM-DD HH:mm")
                              : tx("Sin fecha fija", "No fixed date");
                            const memberSize = Math.max(1, Number(record?.memberSize) || 1);
                            return (
                              <div
                                key={`news-available-${String(record?._id || `idx-${index}`)}`}
                                className="rounded border border-[#3a5f94] bg-[#0b1d3d] p-2"
                              >
                                <div className="truncate text-sm font-black text-[#e7f3ff]">
                                  {getLocalizedTournamentName(record?.tournamentName, "Torneo")}
                                </div>
                                <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-[#9fc3ea]">
                                  <span className="rounded border border-[#35557f] bg-[#0d2448] px-1.5 py-0.5 font-semibold text-[#b9deff]">
                                    {record.modeLabel}
                                  </span>
                                  <span className="rounded border border-[#365d9b] bg-[#162b53] px-1.5 py-0.5 font-semibold text-[#d4e4ff]">
                                    {t("JUGADORES")}: {memberSize}
                                  </span>
                                  <span className="rounded border border-[#8d6ed8] bg-[#322255] px-1.5 py-0.5 font-semibold text-[#e8d7ff]">
                                    {tx("Turno", "Turn")}: {Math.max(1, Number(record?.havingTime) || 1)} min
                                  </span>
                                </div>
                                <div className="mt-1 text-[11px] text-[#88acd8]">
                                  {tx("Inicio", "Start")}: {startLabel}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="rounded border border-dashed border-[#3a5f94] bg-[#0b1d3d] p-3 text-sm text-[#98badf]">
                          {tx(
                            "No hay torneos disponibles en este momento.",
                            "No tournaments available at this moment."
                          )}
                        </div>
                      )}
                    </div>

                    <div className="rounded border border-[#4f5f9c] bg-[#121b45] p-2">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#c9d6ff]">
                        {t("PROXIMOS_TORNEOS")} ({newsTournamentCalendar.upcoming.length})
                      </div>
                      {newsTournamentCalendar.upcoming.length > 0 ? (
                        <div className="max-h-[320px] space-y-2 overflow-y-auto pr-1">
                          {newsTournamentCalendar.upcoming.map((record, index) => {
                            const memberSize = Math.max(1, Number(record?.memberSize) || 1);
                            return (
                              <div
                                key={`news-upcoming-${String(record?._id || `idx-${index}`)}`}
                                className="rounded border border-[#5467a8] bg-[#1a2352] p-2"
                              >
                                <div className="truncate text-sm font-black text-[#eef3ff]">
                                  {getLocalizedTournamentName(
                                    record?.tournamentName,
                                    "Torneo Programado"
                                  )}
                                </div>
                                <div className="mt-1 text-[11px] text-[#c1d2ff]">
                                  {tx("Fecha", "Date")}: {moment(record.startMs).format("dddd, YYYY-MM-DD")}
                                </div>
                                <div className="mt-1 text-[11px] text-[#dbe5ff]">
                                  {tx("Hora", "Time")}: {moment(record.startMs).format("hh:mm A")}
                                </div>
                                <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-[#c8d8ff]">
                                  <span className="rounded border border-[#6c7fbe] bg-[#2c386f] px-1.5 py-0.5 font-semibold">
                                    {record.modeLabel}
                                  </span>
                                  <span className="rounded border border-[#6c7fbe] bg-[#2c386f] px-1.5 py-0.5 font-semibold">
                                    {t("JUGADORES")}: {memberSize}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="rounded border border-dashed border-[#4f5f9c] bg-[#1a2352] p-3 text-sm text-[#c1d2ff]">
                          {tx("No hay torneos futuros programados.", "No future tournaments scheduled.")}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {isAdminTab ? (
              <div className="xima-admin-scroll min-h-0 flex-1 overflow-auto rounded-xl">
                {!hasDirectAdminAccess ? (
                  <div className="rounded-lg border border-dashed border-[#7a5a2a] bg-[#1f1307] p-4 text-sm text-[#ffdcae]">{i18nT("lobby.noTienesPermisosParaAbrirElPanelDe")}</div>
                ) : !adminEnabled ? (
                  <div className="rounded-lg border border-[#8b6b2f] bg-[#1f1307] p-4 text-sm text-[#ffe3bd]">{i18nT("lobby.cargandoPanelDeAdmin")}</div>
                ) : (
                  <AdminPanel
                    adminRooms={adminRooms}
                    adminUsers={adminUsers}
                    adminPuzzles={adminPuzzles}
                    adminIndependentChallenges={adminIndependentChallenges}
                    adminOrders={adminOrders}
                    adminLoading={adminLoading}
                    adminActionKey={adminActionKey}
                    currentUserEmail={currentUserEmailNormalized}
                    isSiteOwner={isSiteOwner}
                    onRefresh={handleAdminRefresh}
                    onPurgeRooms={handleAdminPurgeRooms}
                    onDeleteRoom={handleAdminDeleteRoom}
                    onDeleteChallenge={handleAdminDeleteIndependentChallenge}
                    onDeletePuzzle={handleAdminDeletePuzzle}
                    onSetUserPremium={handleAdminSetUserPremium}
                    onResetUserPassword={handleAdminResetUserPassword}
                    onDeleteUser={handleAdminDeleteUser}
                    onOrderStatus={handleAdminOrderStatusUpdate}
                    onOrderNote={handleAdminOrderNoteUpdate}
                    formatOrderAddress={formatAdminOrderAddress}
                    getQuickRemainingMs={getQuickTournamentRemainingMs}
                    formatCountdown={formatCountdownLabel}
                    getTournamentStatus={getTournamentStatus}
                    getTournamentName={getLocalizedTournamentName}
                    resolveModeKey={resolvePuzzleColumnMode}
                    puzzleOrderById={adminPuzzleOrderById}
                    create={adminCreateTournamentApi}
                    onEditTournament={(record) => openTournamentEditModal(record, { force: true })}
                    editTournamentOpen={tournamentEditModalOpen}
                  />
                )}
              </div>
            ) : null}

            {isHistoryTab ? (
              <>
              <div className="xima-history-screen">
                <div className="xima-history-profile-band">
                  <div className="xima-history-player-card">
                    <div className="xima-history-avatar">
                      {currentAvatarUrl ? (
                        <img src={currentAvatarUrl} alt={displayName} />
                      ) : (
                        <span>{userInitials}</span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="xima-history-player-name">{displayName}</div>
                      <div className="xima-history-player-level">
                        {formatHistoryLevelLabel(
                          language,
                          Math.max(
                            1,
                            Math.round(Number(historyResultStats.wins + historyResultStats.losses) || 1),
                          ),
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="xima-history-stat-grid">
                    <div className="xima-history-stat">
                      <IoTrophyOutline aria-hidden="true" />
                      <span>{historyText("totalMatches")}</span>
                      <strong>
                        {formatHistoryNumber(Math.max(historyTotalMatches, historyPageRows.length), language)}
                      </strong>
                    </div>
                    <div className="xima-history-stat is-win">
                      <IoMedalOutline aria-hidden="true" />
                      <span>{historyText("wins")}</span>
                      <strong>{formatHistoryNumber(historyResultStats.wins, language)}</strong>
                      <em>{formatHistoryPercent(historyResultStats.winRate, language)}</em>
                    </div>
                    <div className="xima-history-stat is-loss">
                      <IoCloseCircleOutline aria-hidden="true" />
                      <span>{historyText("losses")}</span>
                      <strong>{formatHistoryNumber(historyResultStats.losses, language)}</strong>
                      <em>{formatHistoryPercent(historyResultStats.lossRate, language)}</em>
                    </div>
                    <div className="xima-history-stat is-streak">
                      <IoFlameOutline aria-hidden="true" />
                      <span>{historyText("currentStreak")}</span>
                      <strong>{formatHistoryNumber(historyResultStats.streak, language)}</strong>
                    </div>
                  </div>
                </div>

                <div className="xima-history-main-grid">
                  <aside className="xima-history-filters-panel">
                    <div className="xima-history-filter-title">
                      <IoFilterOutline aria-hidden="true" />
                      <span>{historyText("filters")}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setHistoryResultFilter("all");
                          setHistoryModeFilter("all");
                          setHistoryPlayerSearch("");
                          setHistoryDateFilter("all");
                          setHistorySortOrder("recent");
                          setHistoryPage(1);
                        }}
                      >
                        {historyText("clearAll")}
                      </button>
                    </div>

                    <div className="xima-history-filter-group">
                      <div className="xima-history-filter-label">{historyText("result")}</div>
                      <div className="xima-history-filter-buttons two">
                        {[
                          { key: "all", label: historyText("all"), icon: IoListOutline },
                          { key: "wins", label: historyText("won"), icon: IoTrophyOutline },
                          { key: "losses", label: historyText("lost"), icon: IoCloseCircleOutline },
                        ].map((option) => {
                          const OptionIcon = option.icon;
                          const isSelected = historyResultFilter === option.key;
                          return (
                            <button
                              key={`history-result-filter-${option.key}`}
                              type="button"
                              onClick={() => {
                                setHistoryResultFilter(option.key);
                                setHistoryPage(1);
                              }}
                              className={isSelected ? "is-active" : ""}
                            >
                              <OptionIcon aria-hidden="true" />
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="xima-history-filter-group">
                      <div className="xima-history-filter-label">{historyText("gameMode")}</div>
                      <div className="xima-history-filter-buttons two">
                        {[
                          {
                            key: "all",
                            label: formatHistoryModeFilterLabel(language, "all", historyModeCounts.all),
                            icon: IoListOutline,
                          },
                          {
                            key: "azar",
                            label: formatHistoryModeFilterLabel(language, "azar", historyModeCounts.azar),
                            icon: IoDiceOutline,
                          },
                          {
                            key: "cienzar",
                            label: formatHistoryModeFilterLabel(language, "cienzar", historyModeCounts.cienzar),
                            icon: IoBeakerOutline,
                          },
                          {
                            key: "ciencia",
                            label: formatHistoryModeFilterLabel(language, "ciencia", historyModeCounts.ciencia),
                            icon: IoCubeOutline,
                          },
                        ].map((option) => {
                          const OptionIcon = option.icon;
                          const isSelected = historyModeFilter === option.key;
                          return (
                            <button
                              key={`history-mode-filter-${option.key}`}
                              type="button"
                              onClick={() => {
                                setHistoryModeFilter(option.key);
                                setHistoryPage(1);
                              }}
                              className={isSelected ? "is-active" : ""}
                            >
                              <OptionIcon aria-hidden="true" />
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="xima-history-filter-group">
                      <div className="xima-history-filter-label">{historyText("player")}</div>
                      <label className="xima-history-search">
                        <IoSearchOutline aria-hidden="true" />
                        <input
                          value={historyPlayerSearch}
                          onChange={(event) => {
                            setHistoryPlayerSearch(event?.target?.value || "");
                            setHistoryPage(1);
                          }}
                          placeholder={historyText("playerSearchPlaceholder")}
                        />
                        {historyPlayerSearch ? (
                          <button
                            type="button"
                            onClick={() => {
                              setHistoryPlayerSearch("");
                              setHistoryPage(1);
                            }}
                            aria-label={historyText("clearSearch")}
                          >
                            <IoCloseCircleOutline aria-hidden="true" />
                          </button>
                        ) : null}
                      </label>
                    </div>

                    <div className="xima-history-filter-group">
                      <div className="xima-history-filter-label">{historyText("dateRange")}</div>
                      <label className="xima-history-select">
                        <IoCalendarOutline aria-hidden="true" />
                        <select
                          value={historyDateFilter}
                          onChange={(event) => {
                            setHistoryDateFilter(event?.target?.value || "all");
                            setHistoryPage(1);
                          }}
                        >
                          <option value="all">{historyText("allDates")}</option>
                          <option value="today">{historyText("today")}</option>
                          <option value="week">{historyText("thisWeek")}</option>
                          <option value="month">{historyText("thisMonth")}</option>
                        </select>
                      </label>
                    </div>

                    <button
                      type="button"
                      className="xima-history-filter-apply"
                      onClick={() => setHistoryPage(1)}
                    >
                      <IoFilterOutline aria-hidden="true" />
                      {historyText("applyFilters")}{" "}
                      {historyActiveFilterCount
                        ? `(${formatHistoryNumber(historyActiveFilterCount, language)})`
                        : ""}
                    </button>
                    <button
                      type="button"
                      className="xima-history-filter-save"
                      onClick={() => {
                        if (typeof window !== "undefined") {
                          try {
                            window.localStorage.setItem(
                              "xima_history_filter_v1",
                              JSON.stringify({
                                result: historyResultFilter,
                                mode: historyModeFilter,
                                player: historyPlayerSearch,
                                date: historyDateFilter,
                                sort: historySortOrder,
                              })
                            );
                          } catch (error) {
                            console.log(error);
                          }
                        }
                        notification.success({
                          message: historyText("filterSavedTitle"),
                          description: historyText("filterSavedDescription"),
                        });
                      }}
                    >
                      <IoBookmarkOutline aria-hidden="true" />
                      {historyText("saveFilter")}
                    </button>
                  </aside>

                  <div className="xima-history-list-panel history-list">
                    <div className="xima-history-list-toolbar">
                      <div>
                        <strong>
                          {formatHistoryMatchesFoundLabel(language, historyFilteredRows.length)}
                        </strong>
                        <span>
                          {formatHistoryShowingLabel(
                            language,
                            historyFilteredRows.length,
                            Math.max(historyTotalMatches, historyPageRows.length),
                          )}
                        </span>
                      </div>
                      <label className="xima-history-sort">
                        <span>{historyText("sortBy")}</span>
                        <select
                          value={historySortOrder}
                          onChange={(event) => setHistorySortOrder(event?.target?.value || "recent")}
                        >
                          <option value="recent">{historyText("mostRecent")}</option>
                          <option value="oldest">{historyText("oldest")}</option>
                        </select>
                      </label>
                    </div>

                    {historyLoading ? (
                      <div className="xima-history-loading">
                        <Spin size="large" />
                      </div>
                    ) : historyFilteredRows.length > 0 ? (
                      <div className="xima-history-table-wrap match-history-list">
                        <div className="xima-history-table-head">
                          <span>{historyText("result")}</span>
                          <span>{historyText("mode")}</span>
                          <span>{historyText("players")}</span>
                          <span>{historyText("playerStrength")}</span>
                          <span>{historyText("level")}</span>
                          <span>{historyText("averagePower")}</span>
                          <span>{historyText("date")}</span>
                          <span>{historyText("replay")}</span>
                        </div>
                        <div className="xima-history-rows">
                          {historyFilteredRows.map((record, index) => {
                            const participants = ensureArray(record?.participants);
                            const currentParticipant =
                              participants.find(
                                (player) =>
                                  player?.isCurrentUser === true ||
                                  isCurrentUserHistoryParticipant(player)
                              ) || participants[0] || null;
                            const rivalParticipants = participants.filter(
                              (player) =>
                                player !== currentParticipant &&
                                player?.isCurrentUser !== true &&
                                !isCurrentUserHistoryParticipant(player)
                            );
                            const labels = rivalParticipants
                              .filter((player) => !isHistoryBotParticipant(player))
                              .map((player) => resolvePreferredPlayerLabel(player))
                              .filter(Boolean);
                            const modeLooksLikeBot = String(record?.mode || "")
                              .trim()
                              .toLowerCase()
                              .includes("bot");
                            const isBotMatch =
                              modeLooksLikeBot ||
                              rivalParticipants.some((player) => isHistoryBotParticipant(player));
                            const rivalPlayer = rivalParticipants[0] || null;
                            const rivalName = isBotMatch
                              ? "BOT"
                              : labels.length
                              ? labels.join(" vs ")
                              : historyText("opponent");
                            const currentName =
                              resolvePreferredPlayerLabel(currentParticipant) || displayName;
                            const currentAvatar = String(
                              currentParticipant?.avatarUrl ||
                                currentParticipant?.avatar ||
                                currentAvatarUrl ||
                                ""
                            ).trim();
                            const rivalAvatar = String(
                              rivalPlayer?.avatarUrl ||
                                rivalPlayer?.avatar ||
                                getStoredAvatarByEmail(
                                  rivalPlayer?.email || rivalPlayer?.userId || rivalName
                                ) ||
                                ""
                            ).trim();
                            const currentInitial = String(currentName || "X")
                              .trim()
                              .slice(0, 1)
                              .toUpperCase();
                            const rivalInitial = String(rivalName || "R")
                              .trim()
                              .slice(0, 1)
                              .toUpperCase();
                            const normalizedMode = normalizeModeAlias(record?.mode, "");
                            const isScienceVsAzarHistoryMode =
                              normalizedMode === "sciencevsazar" ||
                              normalizedMode === "localsciencevsazar" ||
                              normalizedMode === "botlocalsciencevsazar";
                            const modeLabel = formatHistoryModeLabel(language, record?.mode);
                            const ModeGlyph =
                              normalizedMode === "dice"
                                ? IoDiceOutline
                                : isScienceVsAzarHistoryMode
                                ? IoBeakerOutline
                                : normalizedMode === "cienzar"
                                ? IoBeakerOutline
                                : IoCubeOutline;
                            const modeToneClass =
                              normalizedMode === "dice"
                                ? "is-azar"
                                : isScienceVsAzarHistoryMode
                                ? "is-sciencevsazar"
                                : normalizedMode === "cienzar"
                                ? "is-cienzar"
                                : "is-ciencia";
                            const outcomeStatus = String(record?.outcome || "")
                              .trim()
                              .toLowerCase();
                            const outcomeToneClass =
                              outcomeStatus === "loss"
                                ? "is-loss"
                                : outcomeStatus === "draw"
                                ? "is-draw"
                                : "is-win";
                            const OutcomeGlyph =
                              outcomeStatus === "loss" ? IoCloseCircleOutline : IoTrophyOutline;
                            const outcomeLabel =
                              outcomeStatus === "loss"
                                ? historyText("defeat")
                                : outcomeStatus === "draw"
                                ? historyText("draw")
                                : historyText("victory");
                            const cimaxDelta = Number.isFinite(Number(record?.cimaxDelta))
                              ? Math.round(Number(record?.cimaxDelta))
                              : 0;
                            const cimaxDeltaAbs = Math.abs(cimaxDelta);
                            const outcomeSignedDelta =
                              outcomeStatus === "loss"
                                ? -cimaxDeltaAbs
                                : outcomeStatus === "draw"
                                ? 0
                                : cimaxDeltaAbs;
                            const gameForce = Number.isFinite(Number(record?.historyMatchForce))
                              ? formatHistoryMetricValue(record.historyMatchForce, { language })
                              : "-";
                            const gameLevel = Number.isFinite(Number(record?.historyMatchLevel))
                              ? formatHistoryMetricValue(record.historyMatchLevel, { language })
                              : "-";
                            const gamePotency = Number.isFinite(Number(record?.historyMatchPotency))
                              ? formatHistoryMetricValue(record.historyMatchPotency, {
                                decimals: 2,
                                language,
                              })
                              : "-";
                            const forcePercent = Math.max(
                              6,
                              Math.min(
                                100,
                                Math.round((Number(record?.historyMatchForce) || 0) * 2.5),
                              )
                            );
                            const rowTimeMs = resolveHistoryRecordTimeMs(record);
                            const rowDateLabel = formatHistoryDateLabel(language, rowTimeMs, nowMs);
                            const rowTimeLabel = formatHistoryTimeLabel(language, rowTimeMs);

                            return (
                              <div
                                key={`history-redesign-row-${String(record?.id || `idx-${index}`)}`}
                                className={`xima-history-row ${outcomeToneClass}`}
                              >
                                <div className="xima-history-result-cell">
                                  <OutcomeGlyph aria-hidden="true" />
                                  <div>
                                    <strong>{outcomeLabel}</strong>
                                    <span>
                                      {formatHistorySignedMetricValue(outcomeSignedDelta, {
                                        language,
                                      })}
                                    </span>
                                  </div>
                                </div>
                                <div className={`xima-history-mode-cell ${modeToneClass}`}>
                                  <ModeGlyph aria-hidden="true" />
                                  <span>{modeLabel}</span>
                                </div>
                                <div className="xima-history-players-cell">
                                  <div className="xima-history-mini-player">
                                    <div className="xima-history-mini-avatar">
                                      {currentAvatar ? (
                                        <img src={currentAvatar} alt={currentName} />
                                      ) : (
                                        <span>{currentInitial}</span>
                                      )}
                                    </div>
                                    <div>
                                      <strong>{currentName}</strong>
                                      <span>{formatHistoryLevelLabel(language, gameLevel)}</span>
                                    </div>
                                  </div>
                                  <em>VS</em>
                                  <div className="xima-history-mini-player">
                                    <div className="xima-history-mini-avatar is-rival">
                                      {rivalAvatar && !isBotMatch ? (
                                        <img src={rivalAvatar} alt={rivalName} />
                                      ) : (
                                        <span>{rivalInitial}</span>
                                      )}
                                    </div>
                                    <div>
                                      <strong>{rivalName}</strong>
                                      <span>{isBotMatch ? historyText("system") : historyText("opponent")}</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="xima-history-force-cell">
                                  <strong>{gameForce}</strong>
                                  <div>
                                    <span style={{ width: `${forcePercent}%` }} />
                                  </div>
                                </div>
                                <div className="xima-history-number-cell">{gameLevel}</div>
                                <div className="xima-history-number-cell">{gamePotency}</div>
                                <div className="xima-history-date-cell">
                                  <strong>{rowDateLabel}</strong>
                                  <span>{rowTimeLabel}</span>
                                </div>
                                <div className="xima-history-replay-cell">
                                  <button
                                    type="button"
                                    disabled={!record?.hasReplay}
                                    onClick={() => handleReplayOpen(record)}
                                    aria-label={historyText("viewReplay")}
                                  >
                                    <IoPlayOutline aria-hidden="true" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="xima-history-empty">
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={historyText("emptyDescription")}
                        />
                      </div>
                    )}

                    <div className="xima-history-pagination">
                      <span>
                        {formatHistoryShowingMatchesLabel(
                          language,
                          historyFilteredRows.length,
                          historyTotalMatches,
                        )}
                      </span>
                      {!historyShowAllMatches ? (
                        <div className="xima-history-page-buttons">
                          <button
                            type="button"
                            disabled={historyPage <= 1}
                            onClick={() => setHistoryPage((prev) => Math.max(1, prev - 1))}
                            aria-label={historyText("previousPage")}
                          >
                            <IoChevronBackOutline aria-hidden="true" />
                          </button>
                          {historyVisiblePages.map((pageToken) =>
                            typeof pageToken === "string" ? (
                              <span key={`history-page-new-${pageToken}`}>...</span>
                            ) : (
                              <button
                                key={`history-page-new-${pageToken}`}
                                type="button"
                                onClick={() => setHistoryPage(pageToken)}
                                className={pageToken === historyPage ? "is-active" : ""}
                              >
                                {formatHistoryNumber(pageToken, language)}
                              </button>
                            )
                          )}
                          <button
                            type="button"
                            disabled={historyPage >= historyTotalPages}
                            onClick={() =>
                              setHistoryPage((prev) => Math.min(historyTotalPages, prev + 1))
                            }
                            aria-label={historyText("nextPage")}
                          >
                            <IoChevronForwardOutline aria-hidden="true" />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
              <div className="hidden">
                <div className="mb-3 flex items-center gap-3 rounded-lg border border-[#3f79b2] bg-[#0b2142] p-3">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md border border-[#58baff88] bg-[#123462] text-lg font-black text-[#b9ebff]">
                    {currentAvatarUrl ? (
                      <img
                        src={currentAvatarUrl}
                        alt={displayName}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span>{userInitials}</span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-xl font-black text-white">{displayName}</div>
                  </div>
                </div>

                {historyLoading ? (
                  <div className="flex h-[280px] items-center justify-center">
                    <Spin size="large" />
                  </div>
                ) : (
                  <div className="flex h-full min-h-0 flex-col rounded border border-[#2a446f] bg-[#07152e] p-2 history-list">
                          {historyPageRows.length > 0 ? (
                      <div className="mobile-landscape-safari-flex-child match-history-list min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
                            <div
                              className={`grid ${historyHeaderGridClass} history-header match-history-header history-table-header border-b border-[#2f4f7a] px-2 py-2 font-semibold uppercase tracking-[0.09em] text-[#9ecbff]`}
                              style={{ fontSize: `${historyHeaderFontSize}px` }}
                            >
                              <div className="min-w-0 truncate">{historyModeHeaderLabel}</div>
                              <div className="min-w-0 truncate">{historyResultHeaderLabel}</div>
                              {!isHistoryMobileResultView ? (
                                <div className="min-w-0 truncate">
                                  <span className="[@media(max-width:1024px)_and_(orientation:portrait)]:hidden">{i18nT("lobby.fuerzaDeJuego")}</span>
                                  <span className="hidden [@media(max-width:1024px)_and_(orientation:portrait)]:inline">
                                    F/G
                                  </span>
                                </div>
                              ) : null}
                              {!isHistoryMobileResultView ? (
                                <div className="min-w-0 truncate">
                                  <span className="[@media(max-width:1024px)_and_(orientation:portrait)]:hidden">{i18nT("lobby.nivelDeJuego")}</span>
                                  <span className="hidden [@media(max-width:1024px)_and_(orientation:portrait)]:inline">
                                    N/J
                                  </span>
                                </div>
                              ) : null}
                              {!isHistoryMobileResultView ? (
                                <div className="min-w-0 truncate">
                                  <span className="[@media(max-width:1024px)_and_(orientation:portrait)]:hidden">{i18nT("lobby.potenciaDeJuego")}</span>
                                  <span className="hidden [@media(max-width:1024px)_and_(orientation:portrait)]:inline">
                                    P/J
                                  </span>
                                </div>
                              ) : null}
                              <div className="min-w-0 truncate">{i18nT("lobby.rival")}</div>
                              <div className="min-w-0 truncate text-right">{historyReplayHeaderLabel}</div>
                            </div>
                          <div className="space-y-2 py-2">
                            {historyPageRows.map((record, index) => {
                              const participants = ensureArray(record?.participants);
                              const rivalParticipants = participants.filter(
                                (player) =>
                                  player?.isCurrentUser !== true &&
                                  !isCurrentUserHistoryParticipant(player)
                              );
                              const labels = rivalParticipants
                                .filter((player) => !isHistoryBotParticipant(player))
                                .map((player) => resolvePreferredPlayerLabel(player))
                                .filter(Boolean);
                              const modeLooksLikeBot = String(record?.mode || "")
                                .trim()
                                .toLowerCase()
                                .includes("bot");
                              const isBotMatch =
                                modeLooksLikeBot ||
                                rivalParticipants.some((player) => isHistoryBotParticipant(player));
                              const modeLabel = formatModeLabel(record?.mode);
                              const modeIconSrc = getModeIconSrc(record?.mode);
                              const outcomeStatus = String(record?.outcome || "")
                                .trim()
                                .toLowerCase();
                              const cimaxDelta = Number.isFinite(Number(record?.cimaxDelta))
                                ? Math.round(Number(record?.cimaxDelta))
                                : 0;
                              const cimaxDeltaAbs = Math.abs(cimaxDelta);
                              const outcomeSignedDelta =
                                outcomeStatus === "loss"
                                  ? -cimaxDeltaAbs
                                  : outcomeStatus === "draw"
                                  ? 0
                                  : cimaxDeltaAbs;
                              const walletDelta = Number.isFinite(Number(record?.walletDelta))
                                ? Math.round(Number(record?.walletDelta))
                                : 0;
                              const gameForce = Number.isFinite(Number(record?.historyMatchForce))
                                ? formatHistoryMetricValue(record.historyMatchForce)
                                : "—";
                              const gameLevel = Number.isFinite(Number(record?.historyMatchLevel))
                                ? formatHistoryMetricValue(record.historyMatchLevel)
                                : "—";
                              const gamePotency = Number.isFinite(Number(record?.historyMatchPotency))
                                ? formatHistoryMetricValue(record.historyMatchPotency, {
                                    decimals: 2,
                                  })
                                : "—";
                              const mobileOutcomeSign =
                                outcomeStatus === "loss" ? "-" : outcomeStatus === "draw" ? "+/-" : "+";
                              const outcomeSignCircleClass =
                                outcomeStatus === "loss"
                                  ? "border-[#ff7a7a] bg-[#451717] text-[#ff9c9c]"
                                  : outcomeStatus === "draw"
                                  ? "border-[#77b3ff] bg-[#142b4b] text-[#b5d8ff]"
                                  : "border-[#5fff95] bg-[#103b1f] text-[#7bffab]";
                              const outcomeSignTextClass = isHistoryPortraitPhoneView
                                ? outcomeStatus === "draw"
                                  ? "text-[1.35em]"
                                  : "text-[1.85em]"
                                : outcomeStatus === "draw"
                                ? "text-[0.58em]"
                                : "text-[1.08em]";
                              const isHistoryPortraitReplayRow =
                                isHistoryPortraitPhoneView && Boolean(record?.hasReplay);
                              const historyMobileResultCirclePortraitClass = isHistoryPortraitPhoneView
                                ? "[@media(max-width:1024px)_and_(orientation:portrait)]:h-[2.88em] [@media(max-width:1024px)_and_(orientation:portrait)]:w-[2.88em]"
                                : isHistoryPortraitReplayRow
                                ? "[@media(max-width:1024px)_and_(orientation:portrait)]:h-[0.72em] [@media(max-width:1024px)_and_(orientation:portrait)]:w-[0.72em] [@media(max-width:1024px)_and_(orientation:portrait)]:text-[0.55em]"
                                : "[@media(max-width:1024px)_and_(orientation:portrait)]:h-[1.1em] [@media(max-width:1024px)_and_(orientation:portrait)]:w-[1.1em]";
                              const outcomeCimaxBadgeClass =
                                outcomeStatus === "loss"
                                  ? "border-[#ff9a9a] bg-[#4a1717] text-[#ffd2d2]"
                                  : outcomeStatus === "draw"
                                  ? "border-[#8ec0ff] bg-[#18355a] text-[#d4e9ff]"
                                  : "border-[#7effb0] bg-[#154728] text-[#d7ffe8]";
                              const historyMobileResultCimaxPortraitClass = isHistoryPortraitPhoneView
                                ? "[@media(max-width:1024px)_and_(orientation:portrait)]:min-h-[2.88em] [@media(max-width:1024px)_and_(orientation:portrait)]:min-w-[2.88em] [@media(max-width:1024px)_and_(orientation:portrait)]:px-[0.48em] [@media(max-width:1024px)_and_(orientation:portrait)]:text-[1.85em]"
                                : "px-1.5 text-[1.02em]";
                              const historyMobileReplayButtonClass = isHistoryPortraitReplayRow
                                ? "min-w-[41px] px-1 py-0.5 text-[5px] font-black uppercase tracking-tight"
                                : "min-w-[82px] px-2 py-1 text-[10px] font-black uppercase tracking-tight";
                              return (
                                <div
                                  key={`history-row-${String(record?.id || `idx-${index}`)}`}
                                  className={`grid ${historyRowGridClass} history-card match-history-card items-center rounded border border-[#1f3559] bg-[#0b1a34] px-2 py-2 text-[#dce8ff]`}
                                  style={{ fontSize: `${historyRowFontSize}px` }}
                                >
                                  <div className="flex items-center">
                                    {modeIconSrc ? (
                                      <img
                                        src={modeIconSrc}
                                        alt={modeLabel}
                                        className={`max-w-full rounded-md border border-[#4c77a8] bg-[#081b39] object-contain p-0.5 ${
                                          isHistoryMobileResultView ? "h-11 w-11" : "h-16 w-16"
                                        }`}
                                      />
                                    ) : (
                                      <span
                                        className={`rounded border border-[#4c77a8] bg-[#081b39] font-black uppercase text-[#dce8ff] ${
                                          isHistoryMobileResultView
                                            ? "px-1.5 py-1 text-[10px]"
                                            : "px-2.5 py-1.5 text-sm"
                                        }`}
                                      >
                                        {modeLabel.slice(0, 3)}
                                      </span>
                                    )}
                                  </div>
                                  <div>
                                    {isHistoryMobileResultView ? (
                                      isHistoryPortraitPhoneView ? (
                                        <div className="flex items-center gap-1.5">
                                          <span
                                            className={`inline-flex h-[1.45em] w-[1.45em] shrink-0 items-center justify-center rounded-full border font-black leading-none ${historyMobileResultCirclePortraitClass} ${outcomeSignCircleClass} ${outcomeSignTextClass}`}
                                          >
                                            {mobileOutcomeSign}
                                          </span>
                                          <span
                                            className={`inline-flex shrink-0 items-center justify-center rounded-[0.72em] border font-black leading-none ${historyMobileResultCimaxPortraitClass} ${outcomeCimaxBadgeClass}`}
                                          >
                                            {formatHistorySignedMetricValue(outcomeSignedDelta)}
                                          </span>
                                        </div>
                                      ) : (
                                        <div className="flex items-center gap-1">
                                          <span
                                            className={`inline-flex h-[1.45em] w-[1.45em] shrink-0 items-center justify-center rounded-full border font-black leading-none ${historyMobileResultCirclePortraitClass} ${outcomeSignCircleClass} ${outcomeSignTextClass}`}
                                          >
                                            {mobileOutcomeSign}
                                          </span>
                                          {!isHistoryPortraitReplayRow ? (
                                            <div className="min-w-0 leading-tight">
                                              <div className="truncate text-[10px] font-bold text-[#dce8ff]">
                                                {formatHistorySignedMetricValue(outcomeSignedDelta)}
                                              </div>
                                              <div className="truncate text-[10px] font-bold text-[#9ecbff]">
                                                DINERO {formatHistoryDeltaAmount(walletDelta)}
                                              </div>
                                              <div className="truncate text-[10px] font-bold text-[#7ec6ff]">
                                                F/G {gameForce}
                                              </div>
                                              <div className="truncate text-[10px] font-bold text-[#7ec6ff]">
                                                N/J {gameLevel}
                                              </div>
                                              <div className="truncate text-[10px] font-bold text-[#7ec6ff]">
                                                P/J {gamePotency}
                                              </div>
                                            </div>
                                          ) : null}
                                        </div>
                                      )
                                    ) : (
                                      <div className="flex items-center gap-2">
                                        <span
                                          className={`inline-flex h-[1.5em] w-[1.5em] shrink-0 items-center justify-center rounded-full border font-black leading-none ${outcomeSignCircleClass} ${outcomeSignTextClass}`}
                                        >
                                          {mobileOutcomeSign}
                                        </span>
                                        {record?.cimaxDelta !== null &&
                                        Number.isFinite(Number(record?.cimaxDelta)) ? (
                                          <span className="text-xs font-black text-[#7ec6ff]">
                                            {formatHistorySignedMetricValue(outcomeSignedDelta)}
                                          </span>
                                        ) : null}
                                      </div>
                                    )}
                                  </div>
                                  {!isHistoryMobileResultView ? (
                                    <>
                                      <div className="truncate">
                                        {gameForce}
                                      </div>
                                      <div className="truncate">
                                        {gameLevel}
                                      </div>
                                      <div className="truncate">
                                        {gamePotency}
                                      </div>
                                    </>
                                  ) : null}
                                  <div
                                    className={`min-w-0 truncate text-[#dce8ff] ${
                                      isHistoryMobileResultView
                                        ? isHistoryPortraitReplayRow
                                          ? "text-[6px] font-semibold"
                                          : "text-[10px] font-semibold"
                                        : ""
                                    }`}
                                  >
                                    <div className="min-w-0">
                                      {isBotMatch ? (
                                        <span
                                          className="font-black uppercase tracking-[0.08em] text-[#ffe267]"
                                          style={{ fontSize: `${historyBotLabelFontSize}px` }}
                                        >
                                          BOT
                                        </span>
                                      ) : labels.length ? (
                                        labels.join(" vs ")
                                      ) : (
                                        "-"
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex min-w-0 justify-end">
                                    <Button
                                      size={
                                        isHistoryPortraitReplayRow
                                          ? "small"
                                          : isHistoryMobileResultView
                                          ? "middle"
                                          : "large"
                                      }
                                      type="primary"
                                      disabled={!record?.hasReplay}
                                      onClick={() => handleReplayOpen(record)}
                                      className={
                                        isHistoryMobileResultView
                                          ? historyMobileReplayButtonClass
                                          : "min-w-[120px] px-4 py-2 text-base font-black"
                                      }
                                      style={{ height: "auto" }}
                                    >{i18nT("lobby.replay")}</Button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                      </div>
                    ) : (
                      <div className="flex min-h-[260px] items-center justify-center">
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="Aun no tienes partidas finalizadas."
                        />
                      </div>
                    )}

                    <div
                      className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-[#2f4f7a] pt-2 text-[#9ecbff]"
                      style={{ fontSize: `${11 * HISTORY_TEXT_SCALE}px` }}
                    >
                      <span>
                        {t("MOSTRANDO_DE_PARTIDAS")
                          .replace("{shown}", String(historyPageRows.length))
                          .replace("{total}", String(historyTotalMatches))}
                      </span>
                      {!historyShowAllMatches ? (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            disabled={historyPage <= 1}
                            onClick={() => setHistoryPage((prev) => Math.max(1, prev - 1))}
                            className="rounded border border-[#4fc3ff88] bg-[#11305c] px-2 py-1 font-semibold uppercase tracking-wide text-[#b7e4ff] transition hover:border-[#7ee5ff] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                            style={{ fontSize: `${10 * HISTORY_TEXT_SCALE}px` }}
                          >{i18nT("lobby.prev")}</button>
                          {historyVisiblePages.map((pageToken) =>
                            typeof pageToken === "string" ? (
                              <span
                                key={`history-page-${pageToken}`}
                                className="px-1 font-semibold text-[#88abd6]"
                              >
                                ...
                              </span>
                            ) : (
                              <button
                                key={`history-page-${pageToken}`}
                                type="button"
                                onClick={() => setHistoryPage(pageToken)}
                                className={`rounded border px-2 py-1 font-semibold transition ${
                                  pageToken === historyPage
                                    ? "border-[#7ee5ff] bg-[#1a4378] text-white"
                                    : "border-[#3a5f94] bg-[#0b1d3d] text-[#dce8ff] hover:border-[#7ee5ff] hover:text-white"
                                }`}
                                style={{ fontSize: `${10 * HISTORY_TEXT_SCALE}px` }}
                              >
                                {pageToken}
                              </button>
                            )
                          )}
                          <button
                            type="button"
                            disabled={historyPage >= historyTotalPages}
                            onClick={() =>
                              setHistoryPage((prev) => Math.min(historyTotalPages, prev + 1))
                            }
                            className="rounded border border-[#4fc3ff88] bg-[#11305c] px-2 py-1 font-semibold uppercase tracking-wide text-[#b7e4ff] transition hover:border-[#7ee5ff] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                            style={{ fontSize: `${10 * HISTORY_TEXT_SCALE}px` }}
                          >{i18nT("lobby.next")}</button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
              </>
            ) : null}
          </section>

          {centerRightResizableLayout &&
          !isPhoneLandscapeViewport &&
          !showOnlyLobbyCustomizationPanel &&
          !isLobbyFocusedModeRoute &&
          !isAdminTab &&
          !isHistoryTab ? (
            <div
              role="separator"
              aria-label={i18nT("lobby.resizeCenterAndRightPanels")}
              aria-orientation="vertical"
              onPointerDown={handleCenterRightResizeStart}
              className="hidden w-[6px] shrink-0 cursor-col-resize rounded bg-[#5ab6ff88] transition hover:bg-[#89d4ffdd] active:bg-[#7fc8ff] xl:block [@media(max-width:1024px)_and_(orientation:landscape)]:block"
            />
          ) : null}

          {!showOnlyLobbyCustomizationPanel &&
          !isLobbyFocusedModeRoute &&
          !isAdminTab &&
          !isHistoryTab ? (
          <aside
            className={`xima-right-action-panel mobile-landscape-right-text-scale flex min-h-0 flex-col gap-3 ${
              isIphoneChromeLandscapePhone ? "overflow-visible" : "overflow-hidden"
            } rounded-xl border border-[#3a4fb8] bg-gradient-to-b from-[#1a2a7a] via-[#101a4f] to-[#070c2d] shadow-[0_0_35px_rgba(10,20,70,0.8)] ${
              isIphoneSamsungLandscape && isRightPanelCollapsed ? "p-2" : "p-4"
            } [@media(max-width:1024px)_and_(orientation:portrait)]:overflow-visible`}
            data-sound-zone="right"
          >
            {!hideTournamentSelectionAndFriendsInPortrait &&
            !hidePuzzleAuxPanelsInPortrait &&
            !hideLobbyCubeStartPanel &&
            !hideNewsSelectionPanelInPortrait &&
            !isLobbyFocusedModeRoute ? (
              <div className="flex items-center justify-between gap-2">
                <div
                  className={`font-black uppercase tracking-wide text-[#ff8bee] ${
                    isIphoneSamsungLandscape ? "text-sm" : "text-lg"
                  }`}
                >
                  {t("PANEL_DE_SELECCION")}
                </div>
              </div>
            ) : null}

            {!hideTournamentSelectionAndFriendsInPortrait &&
            !hidePuzzleAuxPanelsInPortrait &&
            !hideNewsSelectionPanelInPortrait &&
            !hideLobbyCubeStartPanel &&
            !isLobbyFocusedModeRoute &&
            !(isIphoneSamsungLandscape && isRightPanelCollapsed) ? (
            <div
              className={`scrollbar-gamified space-y-3 [@media(max-width:1024px)_and_(orientation:portrait)]:flex-none [@media(max-width:1024px)_and_(orientation:portrait)]:overflow-visible [@media(max-width:1024px)_and_(orientation:portrait)]:pr-0 [@media(max-width:1024px)_and_(orientation:portrait)]:pb-0 ${
                isIphoneChromeLandscapePhone
                  ? "overflow-visible pr-0 pb-0"
                  : "min-h-0 flex-1 overflow-y-auto pr-1 [@media(max-width:1024px)_and_(orientation:landscape)]:pb-[calc(env(safe-area-inset-bottom)+120px)]"
              }`}
            >
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8fd4ff]">
                  {t("COMO_QUIERES_JUGAR")}
                </div>
                <div className="flex flex-col gap-2">
                  {PLAY_TYPE_OPTIONS.map((option) => {
                    const isSelected = playType === option.key;
                    return (
                      <button
                        key={option.key}
                        type="button"
                        data-xima-play-type-option={option.key}
                        data-xima-chat-anchor={option.key === "bot" ? "play-type-bot" : undefined}
                        onClick={() => handlePlayTypeSelect(option.key)}
                        disabled={creating}
                        className={`rounded px-3 py-3 text-left text-sm font-black uppercase transition ${
                          isSelected ? "border-2" : "border"
                        } ${
                          isSelected
                            ? option.activeClass
                            : option.idleClass
                        } disabled:cursor-not-allowed disabled:opacity-60`}
                      >
                        {t(option.labelKey)}
                      </button>
                    );
                  })}
                </div>
              </div>
            

            {showOnlineModeStep ? (
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8fd4ff]">
                  {t("MODO_DE_JUEGO")}
                </div>
                <div className="mobile-landscape-choice-grid grid grid-cols-3 gap-2">
                  {ONLINE_MODE_BUTTONS.map((button) => {
                    const modeTheme = resolveModeSelectionTheme(button.value);
                    const isSelected =
                      normalizeModeAlias(formData.mode, "") ===
                        normalizeModeAlias(button.value, "");
                    return (
                      <button
                        key={button.value}
                        type="button"
                        onClick={() => handleModeChange(button.value, { markComplete: true })}
                        className={`mobile-landscape-mode-button rounded border px-2 py-3 text-sm font-black uppercase transition ${
                          isSelected ? modeTheme.selectedClass : modeTheme.idleClass
                        }`}
                      >
                        <span
                          className={`mobile-landscape-mode-label ${
                            isSelected ? modeTheme.selectedLabelClass : modeTheme.idleLabelClass
                          }`}
                        >
                          {button.value === "ciencia"
                            ? tx("Ciencia", "Science")
                            : button.value === "azar"
                            ? tx("Azar", "Chance")
                            : button.value === "sciencevsazar"
                            ? tx("Ciencia V/S Azar", "Science V/S Chance")
                            : button.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {showPlayersStep ? (
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8fd4ff]">
                  {t("CANTIDAD_DE_JUGADORES")}
                </div>
                <div className="mobile-landscape-choice-grid grid grid-cols-3 gap-2">
                  {MEMBER_OPTIONS.map((value) => {
                    const lockedForStandardUser = !allowedMemberOptions.includes(value);
                    const isSelected =
                      panelProgress.playersSelected && Number(formData.memberSize) === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => handleMemberSizeSelect(value)}
                        disabled={creating || lockedForStandardUser}
                        className={`mobile-landscape-member-button rounded border px-2 py-2 text-xs font-bold uppercase transition disabled:cursor-not-allowed disabled:opacity-55 ${
                          isSelected
                            ? "border-[#4fc3ff] bg-[#14507d] text-[#d9f4ff]"
                            : lockedForStandardUser
                            ? "border-[#455487] bg-transparent text-[#7d87b4]"
                            : "border-[#4fc3ff] bg-transparent text-[#8fd4ff] hover:border-[#7ee5ff] active:bg-[#14507d] active:text-[#d9f4ff]"
                        }`}
                      >
                        {value === 2
                          ? t("UNO_VS_UNO")
                          : value === 3
                          ? t("UNO_VS_DOS")
                          : value === 4
                          ? t("UNO_VS_TRES")
                          : `1 vs ${value - 1}`}
                      </button>
                    );
                  })}
                </div>
                {!canUseExtendedPlayerOptions ? (
                  <div className="mt-2 text-[11px] text-[#93a7dd]">{i18nT("lobby.soloSuperusuariosPuedenHabilitarPartidasDeOJ2dee41")}</div>
                ) : null}
              </div>
            ) : null}

            {playType === "local" && panelProgress.playersSelected ? (
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8fd4ff]">
                  {t("NOMBRES_LOCALES")}
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {Array.from({ length: requiredLocalPlayers }, (_, index) => (
                    <input
                      key={`local-player-${index + 2}`}
                      type="text"
                      value={localPlayerNames[index] || ""}
                      onChange={(event) => handleLocalNameChange(index, event.target.value)}
                      placeholder={index === 0 ? t("NOMBRE_JUGADOR_2") : `Nombre Jugador ${index + 2}`}
                      className="rounded border border-[#5f6bad] bg-[#101941] px-3 py-2 text-sm text-white outline-none transition placeholder:text-[#7f93d2] focus:border-[#4fc3ff]"
                    />
                  ))}
                </div>
              </div>
            ) : null}

            {showTimeStep ? (
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8fd4ff]">
                  {t("TIEMPO")}
                </div>
                <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                  {TIME_OPTIONS.map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handleTurnTimeSelect(value)}
                      className={`rounded border px-2 py-2 text-xs font-bold uppercase transition ${
                        panelProgress.timeSelected &&
                        Number(formData.havingTime) === value
                          ? "border-[#be67ff] bg-[#5a2290] text-[#f0d3ff]"
                          : "border-[#be67ff] bg-transparent text-[#d9a2ff] hover:border-[#d08cff] active:bg-[#5a2290] active:text-[#f0d3ff]"
                      }`}
                    >
                      {String(value).padStart(2, "0")}:00
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {playType === "online" && showTimeStep ? (
              <div className="rounded border border-[#3b5f91] bg-[#0d1f3d] p-3">
                <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#dceaff]">
                  <input
                    type="checkbox"
                    checked={Boolean(formData.hasMatchPassword)}
                    disabled={creating}
                    onChange={(event) =>
                      handleMatchPasswordEnabledChange(Boolean(event?.target?.checked))
                    }
                  />
                  {tx("Activar contraseña del partido", "Activate match password")}
                </label>
                <div className="mt-2 text-[11px] text-[#98b8e4]">
                  {tx(
                    "Solo podrán unirse quienes tengan la contraseña",
                    "Only those who have the password can join"
                  )}
                </div>
                {formData.hasMatchPassword ? (
                  <input
                    type="password"
                    maxLength={MATCH_PASSWORD_MAX_LENGTH}
                    value={formData.matchPassword}
                    onChange={(event) => handleMatchPasswordChange(event.target.value)}
                    placeholder={tx("Contraseña para unirse", "Password to join")}
                    className="mt-2 w-full rounded border border-[#5f6bad] bg-[#101941] px-3 py-2 text-sm text-white outline-none transition placeholder:text-[#7f93d2] focus:border-[#ffd35a]"
                  />
                ) : null}
              </div>
            ) : null}

            {canConfigureSuperTournamentDuration ? (
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8fd4ff]">{i18nT("lobby.tiempoDeTorneoMin")}</div>
                <input
                  type="number"
                  min={SUPER_TOURNAMENT_MIN_MINUTES}
                  max={SUPER_TOURNAMENT_MAX_MINUTES}
                  step={1}
                  value={superTournamentMinutesInput}
                  onChange={(event) => setSuperTournamentMinutesInput(event.target.value)}
                  placeholder={String(DEFAULT_SUPER_TOURNAMENT_MINUTES)}
                  className="w-full rounded border border-[#5f6bad] bg-[#101941] px-3 py-2 text-sm text-white outline-none transition placeholder:text-[#7f93d2] focus:border-[#4fc3ff]"
                />
                <div className="mt-2 text-[11px] text-[#93a7dd]">{i18nT("lobby.mantieneElTorneoAbiertoParaNuevasEntradasAun4ba36b")}</div>
                {normalizedSuperTournamentMinutes < SUPER_TOURNAMENT_MIN_MINUTES ? (
                  <div className="mt-1 text-[11px] text-[#ff9eb2]">
                    Minimo: {SUPER_TOURNAMENT_MIN_MINUTES} minutos.
                  </div>
                ) : null}

                <div className="mt-4">
                  <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#8fd4ff]">
                    <input
                      type="checkbox"
                      checked={Boolean(formData.tournamentPaymentEnabled)}
                      disabled={creating}
                      onChange={(event) =>
                        handleTournamentPaymentEnabledChange(Boolean(event?.target?.checked))
                      }
                    />
                    {i18nT("lobby.pagoParaUnirse")}
                  </label>
                  <div className="mt-1 text-[11px] text-[#93a7dd]">{i18nT("lobby.activaParaCobrarUnaCuotaDeEntradaAl")}</div>
                </div>

                <div className="mt-4">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8fd4ff]">{i18nT("lobby.cuotaParaEntrarX")}</div>
                  <input
                    type="number"
                    min={1}
                    max={MAX_TOURNAMENT_REWARD_VALUE}
                    step={1}
                    value={String(formData.tournamentRewardValue ?? "")}
                    onChange={(event) => handleTournamentRewardValueChange(event.target.value)}
                    disabled={!formData.tournamentPaymentEnabled}
                    placeholder="1"
                    className="w-full rounded border border-[#5f6bad] bg-[#101941] px-3 py-2 text-sm text-white outline-none transition placeholder:text-[#7f93d2] focus:border-[#4fc3ff] disabled:opacity-50"
                  />
                  <div className="mt-1 text-[11px] text-[#93a7dd]">{i18nT("lobby.seMuestraComoPagoJuntoAlBotonDe")}</div>
                </div>

                <div className="mt-4">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8fd4ff]">{i18nT("lobby.premioDelGanadorX")}</div>
                  <input
                    type="number"
                    min={0}
                    max={MAX_TOURNAMENT_REWARD_VALUE}
                    step={1}
                    value={String(formData.tournamentWinnerPrizeValue ?? "")}
                    onChange={(event) =>
                      handleTournamentWinnerPrizeValueChange(event.target.value)
                    }
                    className="w-full rounded border border-[#5f6bad] bg-[#101941] px-3 py-2 text-sm text-white outline-none transition placeholder:text-[#7f93d2] focus:border-[#4fc3ff] disabled:opacity-50"
                    placeholder="0"
                  />
                  <div className="mt-1 text-[11px] text-[#93a7dd]">{i18nT("lobby.montoQueRecibeAutomaticamenteElGanadorAlTermcbde3a")}</div>
                </div>

                <div className="mt-4">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8fd4ff]">{i18nT("lobby.nombreDelTorneo")}</div>
                  <input
                    type="text"
                    value={formData.tournamentName}
                    onChange={(event) => handleTournamentNameChange(event.target.value)}
                    placeholder="Ej: Hoy es viernes 25 de octubre la gran final"
                    className="w-full rounded border border-[#5f6bad] bg-[#101941] px-3 py-2 text-sm text-white outline-none transition placeholder:text-[#7f93d2] focus:border-[#4fc3ff]"
                  />
                  <div className="mt-1 text-[11px] text-[#93a7dd]">{i18nT("lobby.siLoDejasVacioSeAsignaAutomaticamenteUn")}</div>
                </div>

                <div className="mt-4">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8fd4ff]">{i18nT("lobby.fechaDeCreacion")}</div>
                  <input
                    type="date"
                    value={String(formData.createdAtDate || "")}
                    onChange={(event) => handleCreationDateChange(event.target.value)}
                    className="xima-date-input w-full rounded border border-[#5f6bad] bg-[#101941] px-3 py-2 text-sm text-white outline-none transition focus:border-[#4fc3ff]"
                  />
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2">
                  <div>
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8fd4ff]">{i18nT("lobby.fechaDeInicio")}</div>
                    <input
                      type="date"
                      value={startDateInputValue}
                      onChange={(event) => handleStartDateChange(event.target.value)}
                      className="xima-date-input w-full rounded border border-[#5f6bad] bg-[#101941] px-3 py-2 text-sm text-white outline-none transition focus:border-[#4fc3ff]"
                    />
                  </div>
                  <div>
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8fd4ff]">{i18nT("lobby.horaDeInicio")}</div>
                    <input
                      type="time"
                      value={startTimeInputValue}
                      onChange={(event) => handleStartTimeChange(event.target.value)}
                      className="xima-date-input w-full rounded border border-[#5f6bad] bg-[#101941] px-3 py-2 text-sm text-white outline-none transition focus:border-[#4fc3ff]"
                    />
                  </div>
                </div>

                <div className="mt-3 text-[11px] text-[#a8bbef]">
                  Participantes por juego: {Math.max(1, Number(formData.memberSize) || 1)}
                </div>
              </div>
            ) : null}

            {playType === "bot" ? (
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8fd4ff]">{i18nT("lobby.modoBot")}</div>
                <div className="mb-3 grid grid-cols-3 gap-2">
                  {BOT_MODE_OPTIONS.map((option) => {
                    const normalizedSelectedMode = normalizeBotModeValue(formData.mode, "botlocalfree");
                    const isSelected = normalizedSelectedMode === option.value;
                    const modeTheme = resolveModeSelectionTheme(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        disabled={creating || option.disabled}
                        onClick={() => handleBotModeSelect(option.value)}
                        className={`rounded border px-2 py-2 text-xs font-black uppercase transition disabled:cursor-not-allowed disabled:opacity-55 ${
                          isSelected ? modeTheme.selectedClass : modeTheme.idleClass
                        }`}
                      >
                        <span
                          className={isSelected ? modeTheme.selectedLabelClass : modeTheme.idleLabelClass}
                        >
                          {option.label}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8fd4ff]">
                  {t("CANTIDAD_DE_JUGADORES")}
                </div>
                <div className="mb-3 grid grid-cols-3 gap-2">
                  {BOT_MEMBER_OPTIONS.map((value) => {
                    const lockedForStandardUser = !allowedMemberOptions.includes(value);
                    const isSelected = Number(formData.memberSize) === value;
                    return (
                      <button
                        key={`bot-member-size-${value}`}
                        type="button"
                        disabled={creating || lockedForStandardUser}
                        onClick={() => handleMemberSizeSelect(value)}
                        className={`rounded border px-2 py-2 text-xs font-black uppercase transition disabled:cursor-not-allowed disabled:opacity-55 ${
                          isSelected
                            ? "border-[#4fc3ff] bg-[#14507d] text-[#d9f4ff]"
                            : lockedForStandardUser
                            ? "border-[#455487] bg-transparent text-[#7d87b4]"
                            : "border-[#4fc3ff] bg-transparent text-[#8fd4ff] hover:border-[#7ee5ff] active:bg-[#14507d] active:text-[#d9f4ff]"
                        }`}
                      >
                        {`1v${Math.max(1, value - 1)}`}
                      </button>
                    );
                  })}
                </div>

                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8fd4ff]">
                  {t("TIEMPO")}
                </div>
                <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-4">
                  {TIME_OPTIONS.map((value) => (
                    <button
                      key={`bot-time-${value}`}
                      type="button"
                      onClick={() => handleTurnTimeSelect(value)}
                      className={`rounded border px-2 py-2 text-xs font-bold uppercase transition ${
                        Number(formData.havingTime) === value
                          ? "border-[#be67ff] bg-[#5a2290] text-[#f0d3ff]"
                          : "border-[#be67ff] bg-transparent text-[#d9a2ff] hover:border-[#d08cff] active:bg-[#5a2290] active:text-[#f0d3ff]"
                      }`}
                    >
                      {String(value).padStart(2, "0")}:00
                    </button>
                  ))}
                </div>

                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8fd4ff]">
                  {tx("Dificultad Bot", "Bot Difficulty")}
                </div>
                <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                  {BOT_DIFFICULTY_OPTIONS.map((option) => {
                    const isSelected =
                      normalizeBotDifficulty(formData.botDifficulty) === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleBotDifficultySelect(option.value)}
                        className={`rounded border px-2 py-2 text-xs font-bold uppercase transition ${
                          isSelected
                            ? "border-[#ffad4f] bg-[#6a3a16] text-[#ffe7c2]"
                            : "border-[#ffad4f] bg-transparent text-[#ffc374] hover:border-[#ffbe6b] active:bg-[#6a3a16] active:text-[#ffe7c2]"
                        }`}
                      >
                        {option.value === "easy"
                          ? tx("Fácil", "Easy")
                          : option.value === "medium"
                          ? tx("Medio", "Medium")
                          : option.value === "hard"
                          ? tx("Difícil", "Hard")
                          : tx("Injusto", "Unfair")}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {playType === "online" ||
            playType === "freebox" ||
            playType === "bot" ||
            playType === "local" ? (
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() =>
                    handleQuickPlay({
                      mode:
                        playType === "freebox"
                          ? "freebox"
                          : playType === "bot"
                          ? normalizeSelectableBotModeValue(formData.mode, "botlocalfree")
                          : playType === "local"
                          ? LOCAL_MODE_MAP[formData.mode] || "localdice"
                          : formData.mode,
                      memberSize: normalizeStoredMemberSize(
                        playType,
                        formData.memberSize,
                        canUseExtendedPlayerOptions
                      ),
                      botDifficulty:
                        playType === "online"
                          ? "injusto"
                          : playType === "bot"
                          ? normalizeBotDifficulty(formData.botDifficulty)
                          : "injusto",
                      havingTime:
                        playType === "online" || playType === "local" || playType === "bot"
                          ? Number(formData.havingTime) || DEFAULT_TURN_TIME
                          : DEFAULT_TURN_TIME,
                       localPlayers:
                        playType === "local"
                          ? localPlayerNames.slice(0, requiredLocalPlayers)
                          : [],
                      isIndependentMatch: matchmakingQuickView === "independientes",
                      freeBoxRules: playType === "freebox",
                      freeBoxBaseMode: playType === "freebox" ? formData.mode : undefined,
                    })
                  }
                  disabled={creating || !canStartQuickPlay}
                  className={`w-full rounded-xl border-2 px-3 py-3 text-lg font-black uppercase transition ${
                    canStartQuickPlay
                      ? "border-[#ffb35c] bg-gradient-to-r from-[#ff8a1f] to-[#ff6a00] text-white shadow-[0_0_18px_rgba(255,138,31,0.45)] hover:brightness-110 active:brightness-95"
                      : "cursor-not-allowed border-[#6d7590] bg-transparent text-[#a5b0c8]"
                  }`}
                >
                  {primaryActionLabel}
                </button>
                {!canStartQuickPlay && playType === "online" ? (
                  <div className="mt-2 text-[11px] text-[#93a7dd]">
                    {tx(
                      "Completa: modo, jugadores y tiempo.",
                      "Complete: mode, players and time."
                    )}
                  </div>
                ) : null}
                {!canStartQuickPlay && playType === "local" ? (
                  <div className="mt-2 text-[11px] text-[#93a7dd]">
                    {tx(
                      "Completa: modo, jugadores, nombres y tiempo.",
                      "Complete: mode, players, names and time."
                    )}
                  </div>
                ) : null}
                {!canStartQuickPlay && playType === "freebox" ? (
                  <div className="mt-2 text-[11px] text-[#93a7dd]">
                    {tx("Completa: jugadores.", "Complete: players.")}
                  </div>
                ) : null}
                {!canStartQuickPlay &&
                canConfigureSuperTournamentDuration &&
                normalizedSuperTournamentMinutes < SUPER_TOURNAMENT_MIN_MINUTES ? (
                  <div className="mt-2 text-[11px] text-[#ff9eb2]">
                    Completa: tiempo de torneo ({SUPER_TOURNAMENT_MIN_MINUTES}+ min).
                  </div>
                ) : null}
              </div>
            ) : null}

            {!isNewsTab &&
            !isAdminTab &&
            !isHistoryTab &&
            !hideLobbyCubeStartPanel &&
            !hideMobileCreateGameGlobalChat &&
            !hideLobbyCreateMobilePanels &&
            !hidePuzzleAuxPanelsInPortrait &&
            !isLobbyFocusedModeRoute ? (
              <div className="mt-4 flex min-h-[220px] flex-col rounded-xl border border-[#2fe67a] bg-gradient-to-b from-[#103225] via-[#0d291f] to-[#081b15] p-3 shadow-[0_0_28px_rgba(16,80,44,0.45)] md:mt-auto md:h-[25%] md:max-h-[25%] [@media(max-width:1024px)_and_(orientation:landscape)]:mt-auto [@media(max-width:1024px)_and_(orientation:landscape)]:h-[25%] [@media(max-width:1024px)_and_(orientation:landscape)]:max-h-[25%]">
                <div className="mb-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold uppercase tracking-[0.16em] text-[#79ffb2]">
                      {t("CHAT_GLOBAL")}
                    </div>
                    <div className="flex items-center gap-2">
                      {hasDirectAdminAccess ? (
                        <button
                          type="button"
                          onClick={handleClearGlobalChat}
                          className="rounded border border-[#ff9a9a] bg-[#5a1a1a] px-2 py-1 text-xs font-bold uppercase tracking-wide text-[#ffd7d7] transition hover:border-[#ffc3c3] hover:text-white"
                        >
                          {tx("Limpiar", "Clear")}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => setChatFloatingOpen(true)}
                        className="rounded border border-[#4dff9a] bg-[#11482b] px-2 py-1 text-xs font-bold uppercase tracking-wide text-[#deffe8] transition hover:bg-[#1b6a42]"
                      >
                        {tx("Flotante", "Floating")}
                      </button>
                    </div>
                  </div>
                  <div className="text-sm text-[#baf8d4]">
                    {t("MENSAJES_COMPARTIDOS_EN_EL_LOBBY")}
                  </div>
                </div>

                <div
                  ref={globalChatScrollRef}
                  className="mb-2 min-h-0 flex-1 space-y-1 overflow-y-auto rounded border border-[#39b86d] bg-[#06150f] p-2"
                >
                  {(globalChatMessages.length > 0
                    ? globalChatMessages
                    : [
                        {
                          id: "system-empty-chat",
                          sender: "Sistema",
                          createdAt: new Date().toISOString(),
                          text: t("SISTEMA_CHAT_GLOBAL_LISTO"),
                        },
                      ]
                  ).map((message) => {
                    const timeLabel = new Date(message.createdAt || Date.now()).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    });
                    return (
                      <div
                        key={message.id}
                        className="rounded border border-[#1c6f44] bg-[#0a2419] px-2 py-1"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-bold uppercase tracking-wide text-[#8bffc0]">
                            {message.sender}
                          </span>
                          <span className="text-xs text-[#71b894]">{timeLabel}</span>
                        </div>
                        <div className="break-words text-sm text-[#d9ffea]">{message.text}</div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={globalChatInput}
                    maxLength={GLOBAL_CHAT_MAX_LENGTH}
                    onChange={(event) => setGlobalChatInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter") return;
                      event.preventDefault();
                      handleGlobalChatSend();
                    }}
                    placeholder={t("ESCRIBE_UN_MENSAJE_GLOBAL")}
                    className="min-w-0 flex-1 rounded border border-[#2a9458] bg-[#081c14] px-3 py-2 text-sm text-[#d9ffea] outline-none transition placeholder:text-[#8bc6a5] focus:border-[#58ff9e]"
                  />
                  <button
                    type="button"
                    onClick={handleGlobalChatSend}
                    disabled={!String(globalChatInput || "").trim()}
                    className="rounded border border-[#4dff9a] bg-[#17683d] px-3 py-2 text-sm font-bold uppercase tracking-wide text-[#deffe8] transition hover:bg-[#1d7f4a] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t("ENVIAR")}
                  </button>
                </div>
              </div>
            ) : null}
              </div>
            ) : null}
          </aside>
          ) : null}
        </div>
      </div>

      {!isLobbyFocusedModeRoute && chatFloatingOpen ? (
        <div
          className="fixed z-40 flex flex-col rounded-xl border border-[#2fe67a] bg-gradient-to-b from-[#103225] via-[#0d291f] to-[#081b15] p-3 shadow-[0_0_34px_rgba(16,80,44,0.62)]"
          style={{
            left: `${chatFloatingPosition.x}px`,
            top: `${chatFloatingPosition.y}px`,
            width: `${chatFloatingSize.width}px`,
            height: `${chatFloatingSize.height}px`,
            minWidth: `${CHAT_FLOATING_MIN_WIDTH}px`,
            minHeight: `${CHAT_FLOATING_MIN_HEIGHT}px`,
            maxWidth: "98vw",
            maxHeight: "96vh",
            overflow: "hidden",
          }}
        >
          <div
            className={`mb-2 flex items-center justify-between gap-2 ${
              chatFloatingMaximized ? "cursor-move" : ""
            }`}
            onMouseDown={handleFloatingChatDragStart}
          >
            <div>
              <div
                className="font-semibold uppercase tracking-[0.16em] text-[#79ffb2]"
                style={{ fontSize: `${0.9 * chatFloatingTextScale}rem` }}
              >
                {t("CHAT_GLOBAL")}
              </div>
              <div
                className="text-[#baf8d4]"
                style={{ fontSize: `${0.84 * chatFloatingTextScale}rem` }}
              >
                {chatFloatingMaximized
                  ? "Maximizado: arrastra con clic izquierdo sostenido en esta barra."
                  : t("MENSAJES_COMPARTIDOS_EN_EL_LOBBY")}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {hasDirectAdminAccess ? (
                <button
                  type="button"
                  onClick={handleClearGlobalChat}
                  className="rounded border border-[#ff9a9a] bg-[#5a1a1a] px-2 py-1 font-bold uppercase tracking-wide text-[#ffd7d7] transition hover:border-[#ffc3c3] hover:text-white"
                  style={{ fontSize: `${0.78 * chatFloatingTextScale}rem` }}
                >
                  {tx("Limpiar", "Clear")}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setChatFloatingMaximized((prev) => !prev)}
                className="rounded border border-[#4dff9a] bg-[#11482b] px-2 py-1 font-bold uppercase tracking-wide text-[#deffe8] transition hover:bg-[#1b6a42]"
                style={{ fontSize: `${0.78 * chatFloatingTextScale}rem` }}
              >
                {chatFloatingMaximized ? "Minimizar" : "Maximizar"}
              </button>
              <button
                type="button"
                onClick={() => setChatFloatingOpen(false)}
                className="rounded border border-[#8ad9b2] bg-[#0d3a24] px-2 py-1 font-bold uppercase tracking-wide text-[#deffe8] transition hover:bg-[#145234]"
                style={{ fontSize: `${0.78 * chatFloatingTextScale}rem` }}
              >
                {t("CERRAR")}
              </button>
            </div>
          </div>

          <div
            ref={globalChatFloatingScrollRef}
            className="mb-2 min-h-0 flex-1 space-y-1 overflow-y-auto rounded border border-[#39b86d] bg-[#06150f] p-2"
          >
            {(globalChatMessages.length > 0
              ? globalChatMessages
              : [
                  {
                    id: "system-empty-chat-floating",
                    sender: "Sistema",
                    createdAt: new Date().toISOString(),
                    text: t("SISTEMA_CHAT_GLOBAL_LISTO"),
                  },
                ]
            ).map((message) => {
              const timeLabel = new Date(message.createdAt || Date.now()).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              });
              return (
                <div
                  key={`floating-${message.id}`}
                  className="rounded border border-[#1c6f44] bg-[#0a2419] px-2 py-1"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className="truncate font-bold uppercase tracking-wide text-[#8bffc0]"
                      style={{ fontSize: `${0.9 * chatFloatingTextScale}rem` }}
                    >
                      {message.sender}
                    </span>
                    <span
                      className="text-[#71b894]"
                      style={{ fontSize: `${0.8 * chatFloatingTextScale}rem` }}
                    >
                      {timeLabel}
                    </span>
                  </div>
                  <div
                    className="break-words text-[#d9ffea]"
                    style={{ fontSize: `${0.98 * chatFloatingTextScale}rem` }}
                  >
                    {message.text}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            <input
              type="text"
              value={globalChatInput}
              maxLength={GLOBAL_CHAT_MAX_LENGTH}
              onChange={(event) => setGlobalChatInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== "Enter") return;
                event.preventDefault();
                handleGlobalChatSend();
              }}
              placeholder={t("ESCRIBE_UN_MENSAJE_GLOBAL")}
              className="min-w-0 flex-1 rounded border border-[#2a9458] bg-[#081c14] px-3 py-2 text-[#d9ffea] outline-none transition placeholder:text-[#8bc6a5] focus:border-[#58ff9e]"
              style={{ fontSize: `${0.98 * chatFloatingTextScale}rem` }}
            />
            <button
              type="button"
              onClick={handleGlobalChatSend}
              disabled={!String(globalChatInput || "").trim()}
              className="rounded border border-[#4dff9a] bg-[#17683d] px-3 py-2 font-bold uppercase tracking-wide text-[#deffe8] transition hover:bg-[#1d7f4a] disabled:cursor-not-allowed disabled:opacity-50"
              style={{ fontSize: `${0.92 * chatFloatingTextScale}rem` }}
            >
              {t("ENVIAR")}
            </button>
          </div>

          <div
            className="absolute -left-1 -top-1 h-3 w-3 cursor-nwse-resize rounded-full border border-[#67d899] bg-[#0d3a24]"
            onMouseDown={(event) => handleFloatingChatResizeStart("nw", event)}
          />
          <div
            className="absolute -right-1 -top-1 h-3 w-3 cursor-nesw-resize rounded-full border border-[#67d899] bg-[#0d3a24]"
            onMouseDown={(event) => handleFloatingChatResizeStart("ne", event)}
          />
          <div
            className="absolute -left-1 -bottom-1 h-3 w-3 cursor-nesw-resize rounded-full border border-[#67d899] bg-[#0d3a24]"
            onMouseDown={(event) => handleFloatingChatResizeStart("sw", event)}
          />
          <div
            className="absolute -bottom-1 -right-1 h-3 w-3 cursor-nwse-resize rounded-full border border-[#67d899] bg-[#0d3a24]"
            onMouseDown={(event) => handleFloatingChatResizeStart("se", event)}
          />
        </div>
      ) : null}

      <Modal
        open={adminModalOpen}
        onCancel={() => setAdminModalOpen(false)}
        footer={null}
        maskClosable={false}
        title={<span className="font-semibold text-[#f2bf73]">{i18nT("lobby.administrador")}</span>}
      >
        <div className="max-h-[62vh] space-y-4 overflow-y-auto pr-1">
          <div className="rounded border border-[#7e5c2d] bg-[#201406] p-3 text-sm text-[#ffdeb2]">{i18nT("lobby.accesoAdminParaGestionarSalasActivasPuzzlesY")}</div>
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-[#f2bf73]">{i18nT("lobby.contrasena")}</label>
            <input
              type="password"
              value={adminPasswordInput}
              onChange={(event) => setAdminPasswordInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleAdminLogin().catch((error) => console.log(error));
                }
              }}
              className="w-full rounded border border-[#7e5c2d] bg-[#0f1528] px-3 py-2 text-sm text-white outline-none transition focus:border-[#f2bf73]"
              placeholder={i18nT("lobby.ingresaContrasena")}
              autoComplete="current-password"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setAdminModalOpen(false)}
              className="rounded border border-[#6f7ea0] bg-[#1b2a4a] px-2 py-2 text-xs font-semibold uppercase text-[#ccdaf7] transition hover:border-[#9fb4e6]"
            >{i18nT("lobby.cancelar")}</button>
            <button
              type="button"
              onClick={() => handleAdminLogin()}
              disabled={adminLoading}
              className="rounded border border-[#d39d3d] bg-[#5a3a10] px-2 py-2 text-xs font-semibold uppercase text-[#ffe1b5] transition hover:border-[#f2bf73] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {adminLoading ? "Validando..." : "Entrar"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={replayModalOpen}
        onCancel={handleReplayClose}
        footer={null}
        width={isReplayMobileViewport ? "100vw" : replayPanelSize.width}
        style={
          isReplayMobileViewport
            ? {
                top: 0,
                margin: 0,
                paddingBottom: 0,
                width: "100vw",
                maxWidth: "100vw",
              }
            : { top: 0, margin: 0, paddingBottom: 0 }
        }
        rootClassName={`xima-replay-modal xima-replay-r7${isReplayMobileViewport ? " xima-replay-modal-mobile" : ""}`}
        closable={false}
        maskClosable={false}
        destroyOnClose
        modalRender={(modal) => (
          isReplayMobileViewport ? (
            <div className="xima-replay-mobile-shell">{modal}</div>
          ) : (
            <div
              className="fixed"
              style={{
                left: `${replayPanelPosition.x}px`,
                top: `${replayPanelPosition.y}px`,
                width: `${replayPanelSize.width}px`,
                height: `${replayPanelSize.height}px`,
                zIndex: 1100,
              }}
            >
              {modal}
              {replayPanelInteracting ? (
                <div
                  className="fixed inset-0"
                  style={{ zIndex: 1, cursor: replayPanelResizeRef.current ? "nwse-resize" : "move" }}
                />
              ) : null}
              <div
                className="absolute -top-2 left-5 right-5 h-4 cursor-ns-resize"
                onMouseDown={(event) => handleReplayPanelResizeStart("n", event)}
              />
              <div
                className="absolute -bottom-2 left-5 right-5 h-4 cursor-ns-resize"
                onMouseDown={(event) => handleReplayPanelResizeStart("s", event)}
              />
              <div
                className="absolute -left-2 bottom-5 top-5 w-4 cursor-ew-resize"
                onMouseDown={(event) => handleReplayPanelResizeStart("w", event)}
              />
              <div
                className="absolute -right-2 bottom-5 top-5 w-4 cursor-ew-resize"
                onMouseDown={(event) => handleReplayPanelResizeStart("e", event)}
              />
              <div
                className="absolute -left-2 -top-2 h-6 w-6 cursor-nwse-resize"
                title={i18nT("lobby.redimensionar")}
                onMouseDown={(event) => handleReplayPanelResizeStart("nw", event)}
              />
              <div
                className="absolute -right-2 -top-2 h-6 w-6 cursor-nesw-resize"
                title={i18nT("lobby.redimensionar")}
                onMouseDown={(event) => handleReplayPanelResizeStart("ne", event)}
              />
              <div
                className="absolute -bottom-2 -left-2 h-6 w-6 cursor-nesw-resize"
                title={i18nT("lobby.redimensionar")}
                onMouseDown={(event) => handleReplayPanelResizeStart("sw", event)}
              />
              <div
                className="absolute -bottom-1 -right-1 flex h-7 w-7 cursor-nwse-resize items-center justify-center rounded-br-lg rounded-tl-lg border border-[#67d899] bg-[#0d3a24] text-sm font-black text-[#c9fbe0] shadow-[0_0_10px_rgba(84,211,125,0.7)]"
                title={i18nT("lobby.redimensionar")}
                onMouseDown={(event) => handleReplayPanelResizeStart("se", event)}
              >
                ↘
              </div>
            </div>
          )
        )}
        bodyStyle={
          isReplayMobileViewport
            ? {
                height: "auto",
                maxHeight: "none",
                overflowY: "auto",
                overflowX: "hidden",
                padding: 0,
              }
            : { height: `${replayPanelSize.height}px`, overflow: "hidden", padding: 0 }
        }
        title={null}
      >
          <div
            className="r7-layout"
            style={{
              gridTemplateColumns: `${replayR7SideWidth}px minmax(0, 1fr) ${replayR7SideWidth}px`,
            }}
          >
            <aside className="r7-col r7-left">
              <section className="r7-card r7-head" onMouseDown={handleReplayPanelDragStart}>
                <button
                  type="button"
                  className="r7-back-big"
                  onClick={handleReplayClose}
                  title={replayReturnLabel}
                >
                  <span aria-hidden="true">←</span>
                  {String(location.state?.replayReturnTo || "").startsWith("/game/history")
                    ? "Ir al historial"
                    : location.state?.replayReturnTo === "/game/championship"
                    ? "Ir al Campeonato"
                    : replayReturnLabel}
                </button>
                <div className="r7-head-top">
                  <div className="r7-head-text">
                    <small>
                      {[
                        getLocalizedTournamentName(
                          replaySelectedMatch?.tournamentName,
                          i18nT("lobby.matchFallback")
                        ),
                        replayR7ModeLabel,
                        Number.isFinite(Number(replaySelectedMatch?.cimax)) &&
                        replaySelectedMatch?.cimax !== null &&
                        replaySelectedMatch?.cimax !== ""
                          ? `${Number(replaySelectedMatch.cimax) > 0 ? "+" : ""}${Number(
                              replaySelectedMatch.cimax
                            )} CIMAX`
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </small>
                    <h2>{i18nT("lobby.replay")}</h2>
                  </div>
                </div>
              </section>

              {replayReviewActive ? (
                <section className="r7-card r7-review">
                  <div className="r7-label">★ Análisis del bot</div>
                  {replayAnalysisAccess && !replayAnalysisAccess.premium ? (
                    <div className="r7-review-quota">
                      {Number(replayAnalysisAccess.remaining) > 0
                        ? `Te ${Number(replayAnalysisAccess.remaining) === 1 ? "queda 1 análisis" : `quedan ${Number(replayAnalysisAccess.remaining)} análisis`} nuevos hoy (gratis: ${Number(replayAnalysisAccess.limit) || 3} al día)`
                        : "Hoy ya no te quedan análisis nuevos gratis"}
                    </div>
                  ) : null}
                  {!replayReview || replayReview.state === "queued" || replayReview.state === "running" ? (
                    <div className="r7-review-progress">
                      <span>El bot está revisando cada jugada… {Math.round(Number(replayReview?.progress) || 0)} %</span>
                      <div>
                        <i style={{ width: `${Math.round(Number(replayReview?.progress) || 0)}%` }} />
                      </div>
                    </div>
                  ) : replayReview.state === "error" ? (
                    <>
                      <p className={`r7-review-error${replayReview.premium ? " is-premium" : ""}`}>
                        {replayReview.premium ? "⭐ " : ""}
                        {replayReview.error || "No se pudo analizar la partida."}
                      </p>
                      {replayReview.premium ? (
                        <button type="button" className="r7-premium-btn" onClick={handleReplayGoPremium}>
                          ⭐ Hazte Premium
                        </button>
                      ) : null}
                    </>
                  ) : (
                    <>
                      {replayReview?.meta?.humanBeatBot ? (
                        <p className="r7-review-bot">
                          🤖 «Aún me estoy preparando. Juegas muy bien, mejor que yo, pero permíteme darte mi consejo.»
                        </p>
                      ) : null}
                      {replayReviewCurrentMove ? (
                        <div className="r7-review-current">
                          <img src={`/replay-analysis/${replayReviewCurrentMove.kind}.png?v=2`} alt="" />
                          <span>
                            <strong>
                              {REPLAY_REVIEW_LABELS[replayReviewCurrentMove.kind] || replayReviewCurrentMove.kind}
                            </strong>
                            <small>
                              {replayReviewCurrentMove.bestIsPlayed
                                ? "Es la jugada que habría hecho el bot (flecha verde)"
                                : replayReviewCurrentMove.best
                                ? `El bot prefería ${String(replayReviewCurrentMove.best.from || "fuera")}→${String(replayReviewCurrentMove.best.to || "")} (flecha verde)`
                                : "Es la jugada que habría hecho el bot"}
                            </small>
                          </span>
                        </div>
                      ) : null}
                      {false && replayReviewNextMove && replayReviewNextMove.best ? (
                        <p className="r7-review-next">
                          ➜ Flecha verde: para la jugada siguiente el bot recomienda{" "}
                          <strong>{String(replayReviewNextMove.best.notation || "").toUpperCase()}</strong>
                        </p>
                      ) : null}
                      {ensureArray(replayReview?.result?.players).map((summary) => {
                        const playerIndex = Number(summary?.playerIndex);
                        const name =
                          replayR7Data.players[playerIndex]?.name ||
                          replayReview?.meta?.players?.[playerIndex]?.name ||
                          `Jugador ${playerIndex + 1}`;
                        return (
                          <div
                            key={`r7-review-player-${playerIndex}`}
                            className="r7-review-player"
                            style={{ "--r7-pc": REPLAY_R7_PLAYER_COLORS[playerIndex] || "#8d95a5" }}
                          >
                            <div className="r7-review-player-head">
                              <strong title={name}>{name}</strong>
                              <span>
                                {summary?.accuracy == null ? "—" : `${summary.accuracy}%`}
                                <small> precisión</small>
                              </span>
                            </div>
                            <div className="r7-review-counts">
                              {REPLAY_REVIEW_KINDS.filter((kind) => Number(summary?.counts?.[kind.id]) > 0).map(
                                (kind) => (
                                  <span key={kind.id} title={kind.label} className={`r7-kind r7-kind-${kind.id}`}>
                                    <img src={`/replay-analysis/${kind.id}.png?v=2`} alt="" />
                                    <em>{kind.label}</em>
                                    <b>{summary.counts[kind.id]}</b>
                                  </span>
                                )
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </section>
              ) : null}

              <section className="r7-card r7-tools">
                <div className="r7-tool-row">
                  <button
                    type="button"
                    className="r7-tool"
                    onClick={handleReplayAnalysisUndo}
                    disabled={!replayAnalysisActive || replayAnalysisUndoCount <= 0}
                    title={i18nT("lobby.replayAnalysisUndo")}
                  >
                    <span className="r7-tool-ico">↶</span>
                    <span>Deshacer</span>
                  </button>
                  <button
                    type="button"
                    className={`r7-tool${replayArrowsActive ? " is-on" : ""}`}
                    onClick={handleReplayArrowsToggle}
                    disabled={!replayHasMatch}
                    aria-pressed={replayArrowsActive}
                  >
                    <span className="r7-tool-ico">➚</span>
                    <span>{i18nT("lobby.replayArrows")}</span>
                  </button>
                  <button
                    type="button"
                    className={`r7-tool${!replayCameraFollowEnabled ? " is-on" : ""}`}
                    onClick={() => {
                      if (replayCameraFollowEnabled) toggleReplayCameraFollow();
                    }}
                    aria-pressed={!replayCameraFollowEnabled}
                    title={i18nT("lobby.replayCamaraLibre")}
                  >
                    <span className="r7-tool-ico">🎥</span>
                    <span>Cámara libre</span>
                  </button>
                  <button
                    type="button"
                    className={`r7-tool${replayCameraFollowEnabled ? " is-on" : ""}`}
                    onClick={toggleReplayCameraFollow}
                    aria-pressed={replayCameraFollowEnabled}
                    title={i18nT("lobby.replayCameraFollow")}
                  >
                    <span className="r7-tool-ico">📷</span>
                    <span>Seguir cámara</span>
                    <em>
                      {replayCameraFollowEnabled
                        ? i18nT("lobby.activated")
                        : i18nT("lobby.deactivated")}
                    </em>
                  </button>
                </div>
                {replayArrowsActive ? (
                  <div
                    className="r7-arrow-colors"
                    role="radiogroup"
                    aria-label={i18nT("lobby.replayArrows")}
                  >
                    {REPLAY_R7_ARROW_KEYS.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        role="radio"
                        aria-checked={replayArrowColor === option.id}
                        title={option.name}
                        onClick={() => setReplayArrowColor(option.id)}
                        className={`r7-color-dot${replayArrowColor === option.id ? " is-on" : ""}`}
                        style={{ backgroundColor: option.hex }}
                      />
                    ))}
                    <button
                      type="button"
                      className="r7-btn r7-btn-sm"
                      onClick={() =>
                        postReplayEventsToIframe([{ eventName: "REPLAY_ARROWS_CLEAR", payload: "" }])
                      }
                    >
                      {i18nT("lobby.replayArrowsClear")}
                    </button>
                  </div>
                ) : null}
                {replayAnalysisActive ? (
                  <div className="r7-hint">{i18nT("lobby.replayAnalysisHint")}</div>
                ) : null}
                <button
                  type="button"
                  className={`r7-review-btn${replayReviewActive ? " is-on" : ""}`}
                  onClick={() => setReplayReviewActive((prev) => !prev)}
                  disabled={!replayHasMatch}
                >
                  ★ {replayReviewActive ? "Quitar análisis" : "Analizar partida"}
                </button>
                <ReplayVideoDownload
                  matchId={String(replaySelectedMatch?.id || "")}
                  canReview={replayR7Data.players.length === 2}
                  matchName={getLocalizedTournamentName(
                    replaySelectedMatch?.tournamentName,
                    i18nT("lobby.matchFallback")
                  )}
                  isAdmin={
                    hasDirectAdminAccess ||
                    userRoleCandidates.some((value) => value === "owner")
                  }
                  unityUrl={`${UNITY_IFRAME_BASE_PATH}?v=${encodeURIComponent(
                    UNITY_DEPLOY_VERSION
                  )}&replay=1&video=1&mid=${encodeURIComponent(String(replaySelectedMatch?.id || ""))}`}
                  previewHost={replayVideoPreviewHost}
                  onPreviewChange={setReplayVideoPreview}
                  playbackSpeed={replayPlaybackSpeed}
                  onGoPremium={handleReplayGoPremium}
                />
              </section>

              <ReplayBlockVisualsToggle
                value={replayBlockVisualsMode}
                onChange={handleReplayBlockVisualsChange}
                hasRealDesigns={!replayDesignSource || replayHasRealDesigns}
              />

              <ReplayVoiceControl active={replayModalOpen} onCommand={handleReplayVoiceCommand} />

              <section className="r7-card r7-keys">
                <div className="r7-label">Colores de flecha</div>
                {REPLAY_R7_ARROW_KEYS.map((option) => (
                  <button
                    key={`r7-key-${option.id}`}
                    type="button"
                    className={`r7-key-row${
                      replayArrowsActive && replayArrowColor === option.id ? " is-on" : ""
                    }`}
                    onClick={() => setReplayArrowColor(option.id)}
                  >
                    <span className="r7-key-dot" style={{ backgroundColor: option.hex }} />
                    <span className="r7-key-name">{option.name}</span>
                    <kbd>{option.key}</kbd>
                  </button>
                ))}
                <p className="r7-note">Clic derecho sobre una flecha la borra</p>
              </section>
            </aside>

            <main
              ref={setReplayBoardElement}
              className={`r7-card r7-board${replayVideoPreviewRect ? " is-video-preview" : ""}`}
            >
              {replayHasMatch ? (
                <iframe
                  ref={replayIframeRef}
                  key={`replay-iframe-${replayFrameNonce}-${String(replaySelectedMatch?.id || "na")}`}
                  src={replayIframeSrc}
                  title={i18nT("lobby.ximaReplay")}
                  tabIndex={0}
                  allow="autoplay; fullscreen"
                  className="r7-iframe"
                  style={
                    replayVideoPreviewRect
                      ? {
                          position: "absolute",
                          left: replayVideoPreviewRect.left,
                          top: replayVideoPreviewRect.top,
                          width: replayVideoPreviewRect.width,
                          height: replayVideoPreviewRect.height,
                        }
                      : undefined
                  }
                />
              ) : (
                <div className="r7-iframe r7-loading">
                  <Spin size="small" />
                </div>
              )}
              {replayVideoPreviewRect ? (
                <div
                  ref={setReplayVideoPreviewHost}
                  className="r7-video-preview-host"
                  style={{
                    left: replayVideoPreviewRect.left,
                    top: replayVideoPreviewRect.top,
                    width: replayVideoPreviewRect.width,
                    height: replayVideoPreviewRect.height,
                  }}
                />
              ) : null}
            </main>

            <aside className="r7-card r7-moves-card">
              <div className="r7-moves-head">
                <span className="r7-label">Jugadas</span>
                <small>· toca una para ir directo</small>
              </div>
              <div
                className="r7-moves-cols"
                style={{
                  gridTemplateColumns: `28px repeat(${replayR7Data.players.length}, minmax(0, 1fr))`,
                }}
              >
                <span>#</span>
                {replayR7Data.players.map((player) => (
                  <span key={`r7-col-${player.index}`} title={player.name}>
                    <i style={{ backgroundColor: REPLAY_R7_PLAYER_COLORS[player.index] }} />
                    {player.name}
                  </span>
                ))}
              </div>
              <div ref={replayTimelineScrollRef} className="r7-moves">
                {replayLoading && replayTimeline.length === 0 ? (
                  <div className="r7-empty">
                    <Spin size="small" />
                  </div>
                ) : replayTimeline.length === 0 ? (
                  <div className="r7-empty">{i18nT("lobby.noHayEventosDeReplay")}</div>
                ) : (
                  replayR7Data.rows.map((row) => (
                    <div
                      key={`r7-row-${row.number}`}
                      className="r7-move-row"
                      style={{
                        gridTemplateColumns: `28px repeat(${replayR7Data.players.length}, minmax(0, 1fr))`,
                      }}
                    >
                      <span className="r7-move-num">{row.number}</span>
                      {replayR7Data.players.map((player) => {
                        const entry = row.cells[player.index];
                        if (!entry) {
                          return <span key={`r7-cell-${row.number}-${player.index}`} />;
                        }
                        const targetStep = Number(entry.index) + 1;
                        const isCurrentStep = targetStep === replayCurrentStep;
                        const isFuture = targetStep > replayCurrentStep;
                        const notation =
                          String(entry?.notation || "").trim() ||
                          String(entry?.label || "").trim() ||
                          `#${targetStep}`;
                        const dice = ensureArray(entry?.diceValues);
                        return (
                          <button
                            type="button"
                            key={`r7-cell-${row.number}-${player.index}`}
                            ref={isCurrentStep ? replayActiveTimelineItemRef : undefined}
                            disabled={!replayCanSelectTimelineStep}
                            title={`#${targetStep} ${String(entry?.playerName || "")}: ${notation}`}
                            onClick={() => {
                              handleReplayGoToStep(targetStep).catch((error) => console.log(error));
                            }}
                            className={`r7-move${isCurrentStep ? " is-current" : ""}${
                              isFuture ? " is-future" : ""
                            }`}
                          >
                            {replayReviewKindByStep[targetStep] ? (
                              <img
                                className="r7-move-kind"
                                src={`/replay-analysis/${replayReviewKindByStep[targetStep]}.png?v=2`}
                                alt={REPLAY_REVIEW_LABELS[replayReviewKindByStep[targetStep]] || ""}
                                title={REPLAY_REVIEW_LABELS[replayReviewKindByStep[targetStep]] || ""}
                              />
                            ) : null}
                            <span className="r7-move-text">{notation}</span>
                            {dice.length > 0 ? <small>{dice.join("/")}</small> : null}
                          </button>
                        );
                      })}
                    </div>
                  ))
                )}
              </div>
              <div className="r7-speed-dock">
                <div className="r7-speed-dock-label">
                  Velocidad de los bloques
                  <strong>{replayPlaybackSpeedLabel}</strong>
                </div>
                <div className="r7-speed-dock-buttons">
                  <button
                    type="button"
                    onClick={handleReplayDecreaseSpeed}
                    disabled={!replayCanDecreaseSpeed}
                  >
                    🐢 {i18nT("lobby.lento")}
                  </button>
                  <button
                    type="button"
                    onClick={handleReplayIncreaseSpeed}
                    disabled={!replayCanIncreaseSpeed}
                  >
                    {i18nT("lobby.rapido")} 🐇
                  </button>
                </div>
              </div>
            </aside>

            <footer className="r7-card r7-bar">
              <div
                className={`r7-scrub${replayCanSelectTimelineStep ? "" : " is-busy"}`}
                onPointerDown={(event) => {
                  if (!replayCanSelectTimelineStep || replayTotalSteps <= 0) return;
                  const element = event.currentTarget;
                  try {
                    element.setPointerCapture(event.pointerId);
                  } catch (error) {
                    // Sin captura el arrastre sigue funcionando dentro de la barra.
                  }
                  setReplayScrubStep(getReplayScrubStepFromEvent(event, element));
                }}
                onPointerMove={(event) => {
                  if (replayScrubStep === null) return;
                  setReplayScrubStep(getReplayScrubStepFromEvent(event, event.currentTarget));
                }}
                onPointerUp={(event) => {
                  if (replayScrubStep === null) return;
                  const step = getReplayScrubStepFromEvent(event, event.currentTarget);
                  setReplayScrubStep(null);
                  handleReplayGoToStep(step).catch((error) => console.log(error));
                }}
                onPointerCancel={() => setReplayScrubStep(null)}
              >
                <div className="r7-scrub-track">
                  {(() => {
                    const total = Math.max(1, Number(replayTotalSteps) || 0);
                    const shownStep = replayScrubStep === null ? replayCurrentStep : replayScrubStep;
                    const percent = clampValue((shownStep / total) * 100, 0, 100);
                    return (
                      <>
                        <div className="r7-scrub-fill" style={{ width: `${percent}%` }} />
                        {replayTimeline.map((entry) => {
                          const step = Number(entry.index) + 1;
                          return (
                            <i
                              key={`r7-tick-${entry.index}`}
                              className={step > shownStep ? "is-future" : ""}
                              style={{
                                left: `${(step / total) * 100}%`,
                                backgroundColor:
                                  REPLAY_R7_PLAYER_COLORS[Number(entry?.playerIndex)] || "#8d95a5",
                              }}
                            />
                          );
                        })}
                        <span className="r7-scrub-knob" style={{ left: `${percent}%` }} />
                      </>
                    );
                  })()}
                </div>
              </div>
              <div className="r7-bar-row">
                <div className="r7-bar-left">
                  <span className="r7-shortcuts">
                    {replayRestarting ? (
                      i18nT("lobby.restartingBoard")
                    ) : !replayReady ? (
                      i18nT("lobby.initializingReplay")
                    ) : (
                      <>
                        <kbd>Espacio</kbd>
                        <kbd>←</kbd>
                        <kbd>→</kbd>
                      </>
                    )}
                  </span>
                </div>
                <div className="r7-bar-center">
                  <button
                    type="button"
                    className="r7-btn"
                    onClick={() => {
                      handleReplayRestart().catch((error) => console.log(error));
                    }}
                    disabled={!replayCanRestart}
                  >
                    ⟲ {i18nT("lobby.reiniciar")}
                  </button>
                  <button
                    type="button"
                    className="r7-btn"
                    onClick={() => {
                      handleReplayPrevious().catch((error) => console.log(error));
                    }}
                    disabled={!replayCanGoBack && !replayAnalysisActive}
                  >
                    {i18nT("lobby.replayPrevious")}
                  </button>
                  <button
                    type="button"
                    className="r7-play"
                    onClick={() => {
                      if (!replayCanTogglePlay && !replayAnalysisActive) return;
                      handleReplayPlayToggleFromUi().catch((error) => console.log(error));
                    }}
                    disabled={!replayCanTogglePlay && !replayAnalysisActive}
                    title={replayPlaying ? i18nT("lobby.replayPause") : i18nT("lobby.replayPlay")}
                    aria-label={replayPlaying ? i18nT("lobby.replayPause") : i18nT("lobby.replayPlay")}
                  >
                    {replayPlaying ? "❚❚" : "▶"}
                  </button>
                  <button
                    type="button"
                    className="r7-btn"
                    onClick={() => {
                      handleReplayNextFromUi().catch((error) => console.log(error));
                    }}
                    disabled={!replayCanStepForward && !replayAnalysisActive}
                  >
                    {i18nT("lobby.replayNext")}
                  </button>
                  <span className="r7-sep" />
                  <button
                    type="button"
                    className={`r7-btn${replayAnalysisActive ? " r7-exit" : " r7-analyze"}`}
                    onClick={() => {
                      handleReplayAnalysisToggle().catch((error) => console.log(error));
                    }}
                    disabled={!replayCanRestart}
                  >
                    {replayAnalysisActive
                      ? `⟲ ${i18nT("lobby.replayRestore")}`
                      : `🔍 ${i18nT("lobby.replayAnalyze")}`}
                  </button>
                </div>
                <div className="r7-bar-right">
                  <span className="r7-counter">
                    <strong>{replayCurrentStep}</strong> / {replayTotalSteps}
                  </span>
                </div>
              </div>
            </footer>
          </div>
      </Modal>

      <Modal
        open={tournamentEditModalOpen}
        onCancel={closeTournamentEditModal}
        onOk={handleSaveTournamentEdit}
        okText="Guardar cambios"
        cancelText="Cancelar"
        confirmLoading={tournamentEditSaving}
        okButtonProps={{ danger: true }}
        cancelButtonProps={{ disabled: tournamentEditSaving }}
        maskClosable={!tournamentEditSaving}
        title={<span className="font-semibold text-[#ffb4b4]">{i18nT("lobby.editarTorneob07503")}</span>}
      >
        <div className="space-y-3">
          <div className="rounded border border-[#7f3232] bg-[#301112] p-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#ffb4b4]">{i18nT("lobby.torneoSeleccionado")}</div>
            <div className="truncate text-sm font-black text-[#ffe3e3]">
              {String(tournamentEditTargetLabel || "Torneo")}
            </div>
            <div className="mt-1 text-[11px] text-[#ffcccc]">
              ID: {String(tournamentEditTargetId || "sin id")}
            </div>
            {renderOwnerDeleteButton("tournament")}
          </div>

          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#9ecbff]">{i18nT("lobby.nombreDelTorneo")}</div>
            <Input
              value={tournamentEditNameDraft}
              maxLength={120}
              disabled={tournamentEditSaving}
              onChange={(event) =>
                setTournamentEditNameDraft(String(event?.target?.value || "").slice(0, 120))
              }
              placeholder={i18nT("lobby.torneoEspecial")}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#9ecbff]">{i18nT("lobby.fechaDeInicio")}</div>
              <input
                type="date"
                value={tournamentEditStartDateDraft}
                disabled={tournamentEditSaving}
                onChange={(event) => setTournamentEditStartDateDraft(String(event?.target?.value || ""))}
                className="xima-date-input w-full rounded border border-[#5f6bad] bg-[#101941] px-3 py-2 text-sm text-white outline-none transition focus:border-[#4fc3ff]"
              />
            </div>
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#9ecbff]">{i18nT("lobby.horaDeInicio")}</div>
              <input
                type="time"
                value={tournamentEditStartTimeDraft}
                disabled={tournamentEditSaving}
                onChange={(event) => setTournamentEditStartTimeDraft(String(event?.target?.value || ""))}
                className="xima-date-input w-full rounded border border-[#5f6bad] bg-[#101941] px-3 py-2 text-sm text-white outline-none transition focus:border-[#4fc3ff]"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#9ecbff]">{i18nT("lobby.duracionDias")}</div>
              <InputNumber
                min={1}
                max={3650}
                precision={0}
                value={tournamentEditDurationDraft}
                disabled={tournamentEditSaving}
                onChange={(value) => setTournamentEditDurationDraft(value)}
                className="w-full"
              />
            </div>
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#9ecbff]">{i18nT("lobby.recompensaX")}</div>
              <InputNumber
                min={0}
                max={MAX_TOURNAMENT_REWARD_VALUE}
                precision={0}
                value={tournamentEditRewardValueDraft}
                disabled={tournamentEditSaving}
                onChange={(value) => setTournamentEditRewardValueDraft(value)}
                className="w-full"
              />
            </div>
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#9ecbff]">{i18nT("lobby.premioGanadorX")}</div>
              <InputNumber
                min={0}
                max={MAX_TOURNAMENT_REWARD_VALUE}
                precision={0}
                value={tournamentEditWinnerPrizeValueDraft}
                disabled={tournamentEditSaving}
                onChange={(value) => setTournamentEditWinnerPrizeValueDraft(value)}
                className="w-full"
              />
            </div>
          </div>

          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#9ecbff]">{i18nT("lobby.tiempoPorTurno")}</div>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
              {TOURNAMENT_EDIT_HAVING_TIME_OPTIONS.map((value) => (
                <button
                  key={`edit-tournament-time-${value}`}
                  type="button"
                  disabled={tournamentEditSaving}
                  onClick={() => setTournamentEditHavingTimeDraft(value)}
                  className={`rounded border px-2 py-2 text-xs font-bold uppercase transition disabled:cursor-not-allowed disabled:opacity-60 ${
                    Number(tournamentEditHavingTimeDraft) === value
                      ? "border-[#be67ff] bg-[#5a2290] text-[#f0d3ff]"
                      : "border-[#be67ff] bg-transparent text-[#d9a2ff] hover:border-[#d08cff] active:bg-[#5a2290] active:text-[#f0d3ff]"
                  }`}
                >
                  {String(value).padStart(2, "0")}:00
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#9ecbff]">{i18nT("lobby.cantidadDeJugadores")}</div>
            <div className="grid grid-cols-3 gap-2">
              {MEMBER_OPTIONS.map((value) => (
                <button
                  key={`edit-tournament-member-${value}`}
                  type="button"
                  disabled={tournamentEditSaving}
                  onClick={() => setTournamentEditMemberSizeDraft(value)}
                  className={`rounded border px-2 py-2 text-xs font-bold uppercase transition disabled:cursor-not-allowed disabled:opacity-60 ${
                    Number(tournamentEditMemberSizeDraft) === value
                      ? "border-[#4fc3ff] bg-[#14507d] text-[#d9f4ff]"
                      : "border-[#4fc3ff] bg-transparent text-[#8fd4ff] hover:border-[#7ee5ff] active:bg-[#14507d] active:text-[#d9f4ff]"
                  }`}
                >
                  {value === 2
                    ? t("UNO_VS_UNO")
                    : value === 3
                    ? t("UNO_VS_DOS")
                    : value === 4
                    ? t("UNO_VS_TRES")
                    : `1 vs ${value - 1}`}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      {puzzleEditModal}

      <Modal
        open={tournamentWaitingOpen}
        onCancel={closeTournamentWaitingRoom}
        width={1485}
        centered
        closable={false}
        maskClosable={false}
        footer={null}
        rootClassName="xima-waiting-room-modal-root"
        className="xima-waiting-room-modal"
      >
        <div className="xima-waiting-room">
          <div className="xima-waiting-header">
            <button
              type="button"
              onClick={closeTournamentWaitingRoom}
              className="xima-waiting-back"
              aria-label={i18nT("lobby.volver")}
            >
              &lt;
            </button>
            <div>
              <h2>{i18nT("lobby.salaDeEspera553d7d")}</h2>
              <p>{i18nT("lobby.juegaYLlegaALaCima")}</p>
            </div>
          </div>

          <section className="xima-waiting-info-panel">
            <h3>{i18nT("lobby.informacionDelTorneo")}</h3>
            {tournamentWaitingSequenceNumber > 0 ? (
              <div className="xima-waiting-info-card">
                <span className="xima-waiting-info-icon xima-waiting-gold">#</span>
                <span>
                  <small>Numero del torneo</small>
                  <strong className="xima-waiting-gold-text">
                    {tournamentWaitingSequenceLabel}
                  </strong>
                  <em>Identificador visible en el lobby</em>
                </span>
              </div>
            ) : null}
            <div className="xima-waiting-info-card">
              <span className="xima-waiting-info-icon xima-waiting-gold">
                <IoTrophyOutline />
              </span>
              <span>
                <small>{i18nT("lobby.recompensa")}</small>
                <strong className="xima-waiting-green">
                  {tournamentWaitingWinnerPrize > 0
                    ? `${tournamentWaitingWinnerPrize}X`
                    : "SIN PREMIO"}
                </strong>
                <em>{i18nT("lobby.paraElGanador")}</em>
              </span>
            </div>
            <div className="xima-waiting-info-card">
              <span className="xima-waiting-info-icon">
                <img src={MATCH_PASSWORD_ICON_SRC} alt="" />
              </span>
              <span>
                <small>{i18nT("lobby.contrasena")}</small>
                <strong>
                  {tournamentWaitingRecord?.hasMatchPassword ? "REQUERIDA" : "ABIERTA"}
                </strong>
                <em>
                  {tournamentWaitingRecord?.hasMatchPassword
                    ? "validada antes de entrar"
                    : "sin clave de acceso"}
                </em>
              </span>
            </div>
            <div className="xima-waiting-info-card">
              <span className="xima-waiting-info-icon xima-waiting-gold">$</span>
              <span>
                <small>{i18nT("lobby.costoDelTorneo")}</small>
                <strong className="xima-waiting-gold-text">
                  {tournamentWaitingEntryFee > 0 ? `${tournamentWaitingEntryFee}X` : "GRATIS"}
                </strong>
                <em>{i18nT("lobby.porJugador")}</em>
              </span>
            </div>
            <div className="xima-waiting-info-card">
              <span className="xima-waiting-info-icon">
                {tournamentWaitingModeIconSrc ? (
                  <img src={tournamentWaitingModeIconSrc} alt="" />
                ) : (
                  <IoCubeOutline />
                )}
              </span>
              <span>
                <small>{i18nT("lobby.modoDeJuego")}</small>
                <strong className="xima-waiting-cyan">{tournamentWaitingModeLabel}</strong>
                <em>{formatModeLabel(tournamentWaitingDisplayMode)}</em>
              </span>
            </div>
            <div className="xima-waiting-info-card">
              <span className="xima-waiting-info-icon">
                {tournamentWaitingPlayerIconSrc ? (
                  <img src={tournamentWaitingPlayerIconSrc} alt="" />
                ) : (
                  <IoPeopleOutline />
                )}
              </span>
              <span>
                <small>{i18nT("lobby.jugadoresPorPartida")}</small>
                <strong className="xima-waiting-cyan">
                  {tournamentWaitingMemberSize} JUGADORES
                </strong>
                <em>{tournamentWaitingPlayers.length}/{tournamentWaitingMemberSize} unidos</em>
              </span>
            </div>
            <div className="xima-waiting-note">
              <IoCubeOutline />
              <span>{i18nT("lobby.respetaALosDemasJugadoresJuegaLimpioY")}</span>
            </div>
          </section>

          <section className="xima-waiting-chat-panel">
            <h3>{i18nT("lobby.chatDeLaSala")}</h3>
            <div className="xima-waiting-chat-list">
              {tournamentWaitingChatRows.map((message) => (
                <div key={message.id} className="xima-waiting-chat-row">
                  <strong>{message.sender}:</strong>
                  <span>{message.text}</span>
                </div>
              ))}
            </div>
            <div className="xima-waiting-chat-compose">
              <input
                value={tournamentWaitingChatInput}
                onChange={(event) => setTournamentWaitingChatInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== "Enter") return;
                  event.preventDefault();
                  handleTournamentWaitingChatSend();
                }}
                placeholder={i18nT("lobby.escribeUnMensaje")}
                maxLength={180}
              />
              <button
                type="button"
                onClick={handleTournamentWaitingChatSend}
                disabled={!String(tournamentWaitingChatInput || "").trim()}
              >
                &gt;
              </button>
            </div>
          </section>

          <section className="xima-waiting-center">
            <div className="xima-waiting-countdown">
              <span>{tournamentWaitingCountdownLabel.title}</span>
              <strong>{tournamentWaitingCountdownLabel.value}</strong>
            </div>
            <div className="xima-waiting-stage">
              <span aria-hidden="true" />
            </div>
            <div className="xima-waiting-how">
              <h3>{i18nT("lobby.comoFunciona")}</h3>
              <div className="xima-waiting-how-steps">
                <span><IoPeopleOutline /><small>{i18nT("lobby.compiteContraOtroJugador")}</small></span>
                <b>&gt;</b>
                <span><IoCubeOutline /><small>{i18nT("lobby.ganaCimasAlObtenerLaVictoria")}</small></span>
                <b>&gt;</b>
                <span><IoDiamondOutline /><small>{i18nT("lobby.sumaCimasYSubePosiciones")}</small></span>
                <b>&gt;</b>
                <span><IoTrophyOutline className="xima-waiting-trophy-icon" /><small>{i18nT("lobby.elJugadorConMasCimasGanaElTorneo")}</small></span>
              </div>
            </div>
          </section>

          <aside className="xima-waiting-positions">
            <h3>{i18nT("lobby.posicionesDelTorneo")}</h3>
            <div className="xima-waiting-position-list">
              {tournamentWaitingPositionRows.map((player) => {
                const canOpenProfile = Boolean(player?.profileTarget) && !player?.isPlaceholder;
                const presenceStatus = player?.tournamentPresenceStatus || "offline";
                const presenceLabel =
                  presenceStatus === "playing"
                    ? "Jugando una partida"
                    : presenceStatus === "searching"
                    ? "Buscando rival"
                    : presenceStatus === "waiting"
                    ? "En la sala de espera"
                    : "Fuera de la sala";
                return (
                  <button
                    key={`waiting-position-${player.id}`}
                    type="button"
                    className={`xima-waiting-position-row rank-${player.position}${
                      player.isPlaceholder ? " xima-waiting-position-empty" : ""
                    }${
                      presenceStatus === "waiting" ? " xima-waiting-position-active" : ""
                    }${
                      presenceStatus === "searching" || presenceStatus === "playing"
                        ? ` is-${presenceStatus}`
                        : ""
                    }`}
                    disabled={!canOpenProfile}
                    onClick={canOpenProfile ? () => openPlayerProfile(player.profileTarget) : undefined}
                  >
                    <span className="xima-waiting-rank">{player.position <= 3 ? player.position : player.position}</span>
                    <span className="xima-waiting-avatar">
                      {player.avatarUrl ? (
                        <img src={player.avatarUrl} alt={player.displayName} />
                      ) : (
                        player.initials
                      )}
                      {!player.isPlaceholder ? (
                        <span
                          className={`xima-waiting-active-indicator is-${presenceStatus}`}
                          title={presenceLabel}
                          aria-label={presenceLabel}
                        />
                      ) : null}
                    </span>
                    <span className="xima-waiting-player-name">
                      <strong>
                        <span className="xima-waiting-player-label">{player.displayName}</span>
                      </strong>
                      <small>{player.secondary || "XIMA"}</small>
                    </span>
                    <span className="xima-waiting-player-score">
                      <small>CIMAX</small>
                      <strong>{formatCimaxScoreValue(player.points)}</strong>
                    </span>
                  </button>
                );
              })}
            </div>
          </aside>

          <div className="xima-waiting-status">
            <img src="/mejor-logo-336.png" alt="XIMA" className="xima-waiting-status-logo" />
          </div>

          <div className="xima-waiting-actions">
            <div className="xima-waiting-left-actions">
              <button
                type="button"
                onClick={closeTournamentWaitingRoom}
                className="xima-waiting-exit"
              >{i18nT("lobby.salirDeLaSala")}</button>
              <img src="/logomuino.png" alt={i18nT("lobby.muino")} className="xima-waiting-muino-logo" />
            </div>
            <div className="xima-waiting-right-actions">
              <button
                type="button"
                onClick={() => handleCopyShareLink(tournamentWaitingShareUrl)}
                disabled={!tournamentWaitingShareUrl}
                className="xima-waiting-invite"
              >{i18nT("lobby.invitar")}</button>
              <button
                type="button"
                onClick={playFromTournamentWaitingRoom}
                disabled={!tournamentWaitingCanPlay}
                className="xima-waiting-play"
              >{i18nT("lobby.jugar")}</button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={profileModalOpen}
        onCancel={closeProfileModal}
        footer={null}
        maskClosable={false}
        width={1120}
        title={<span className="font-semibold text-[#8fd4ff]">{i18nT("lobby.perfilDelJugador")}</span>}
      >
        <div className="max-h-[88vh] space-y-4 overflow-y-auto pr-1">
          <div className="flex items-center gap-3 rounded-lg border border-[#21436d] bg-[#0a1a35] p-3">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-lg border border-[#4acbff80] bg-[#123462] text-2xl font-black text-[#b9ebff]">
              {profileDraftAvatar ? (
                <img
                  src={profileDraftAvatar}
                  alt={`${displayName} ${i18nT("common.profileAvatarPlaceholder")}`}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span>{userInitials}</span>
              )}
            </div>
            <div>
              <div className="text-sm font-semibold text-white">{displayName}</div>
              <div className="mt-1 text-xs text-[#8fd4ff]">{i18nT("lobby.editaTuInformacionYSeleccionaTuFotoDe")}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div className="space-y-4 rounded-lg border border-[#21436d] bg-[#0a1a35] p-3">
              <div className="text-xs font-bold uppercase tracking-[0.16em] text-[#8fd4ff]">{i18nT("lobby.informacionPersonal")}</div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#b5d9ff]">{i18nT("lobby.nombre")}</span>
                  <input
                    type="text"
                    value={profileDraftFirstName}
                    onChange={(event) =>
                      setProfileDraftFirstName(
                        String(event?.target?.value || "").slice(0, PROFILE_NAME_MAX_LENGTH)
                      )
                    }
                    className="w-full rounded border border-[#355b90] bg-[#0f264b] px-3 py-2 text-sm text-[#ecf6ff] outline-none transition focus:border-[#79d8ff]"
                    placeholder={i18nT("lobby.tuNombre")}
                    autoComplete="given-name"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#b5d9ff]">{i18nT("lobby.apellido")}</span>
                  <input
                    type="text"
                    value={profileDraftLastName}
                    onChange={(event) =>
                      setProfileDraftLastName(
                        String(event?.target?.value || "").slice(0, PROFILE_NAME_MAX_LENGTH)
                      )
                    }
                    className="w-full rounded border border-[#355b90] bg-[#0f264b] px-3 py-2 text-sm text-[#ecf6ff] outline-none transition focus:border-[#79d8ff]"
                    placeholder={i18nT("lobby.tuApellido")}
                    autoComplete="family-name"
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#b5d9ff]">{i18nT("lobby.correoOpcional")}</span>
                  <input
                    type="email"
                    value={profileDraftContactEmail}
                    onChange={(event) =>
                      setProfileDraftContactEmail(
                        String(event?.target?.value || "")
                          .slice(0, PROFILE_CONTACT_EMAIL_MAX_LENGTH)
                          .toLowerCase()
                      )
                    }
                    className="w-full rounded border border-[#355b90] bg-[#0f264b] px-3 py-2 text-sm text-[#ecf6ff] outline-none transition focus:border-[#79d8ff]"
                    placeholder={i18nT("profile.correoEjemploCom")}
                    autoComplete="email"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#b5d9ff]">{i18nT("lobby.telefonoOpcional")}</span>
                  <input
                    type="text"
                    value={profileDraftPhone}
                    onChange={(event) =>
                      setProfileDraftPhone(
                        normalizeProfilePhoneInput(event?.target?.value || "").trim()
                      )
                    }
                    inputMode="tel"
                    className="w-full rounded border border-[#355b90] bg-[#0f264b] px-3 py-2 text-sm text-[#ecf6ff] outline-none transition focus:border-[#79d8ff]"
                    placeholder="+1 555 123 4567"
                    autoComplete="tel"
                  />
                </label>
              </div>

              <div className="rounded border border-[#2d4771] bg-[#0d213f] p-3">
                <div className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-[#8fd4ff]">{i18nT("lobby.cambiarContrasenaOpcional")}</div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#b5d9ff]">{i18nT("lobby.actual")}</span>
                    <input
                      type="password"
                      value={profileDraftCurrentPassword}
                      onChange={(event) => setProfileDraftCurrentPassword(event?.target?.value || "")}
                      className="w-full rounded border border-[#355b90] bg-[#0f264b] px-3 py-2 text-sm text-[#ecf6ff] outline-none transition focus:border-[#79d8ff]"
                      placeholder={i18nT("lobby.contrasenaActual")}
                      autoComplete="current-password"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#b5d9ff]">{i18nT("lobby.nueva")}</span>
                    <input
                      type="password"
                      value={profileDraftNewPassword}
                      onChange={(event) => setProfileDraftNewPassword(event?.target?.value || "")}
                      className="w-full rounded border border-[#355b90] bg-[#0f264b] px-3 py-2 text-sm text-[#ecf6ff] outline-none transition focus:border-[#79d8ff]"
                      placeholder={i18nT("lobby.nuevaContrasena")}
                      autoComplete="new-password"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#b5d9ff]">{i18nT("lobby.confirmar")}</span>
                    <input
                      type="password"
                      value={profileDraftConfirmPassword}
                      onChange={(event) => setProfileDraftConfirmPassword(event?.target?.value || "")}
                      className="w-full rounded border border-[#355b90] bg-[#0f264b] px-3 py-2 text-sm text-[#ecf6ff] outline-none transition focus:border-[#79d8ff]"
                      placeholder={i18nT("lobby.repiteLaContrasena")}
                      autoComplete="new-password"
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="space-y-4 rounded-lg border border-[#21436d] bg-[#0a1a35] p-3">
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-[#8fd4ff]">{i18nT("lobby.subirFoto")}</label>
                <label className="inline-flex cursor-pointer items-center rounded border border-[#4fc3ff88] bg-[#11305c] px-3 py-2 text-sm font-semibold text-[#d7efff] transition hover:border-[#7ee5ff]">
                  {i18nT("lobby.elegirArchivo")}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarUpload}
                  />
                </label>
                <div className="mt-1 text-xs text-[#92a9d2]">{i18nT("lobby.formatoRecomendadoJpgPngMaximoMb")}</div>
              </div>

              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#8fd4ff]">{i18nT("lobby.avataresPreestablecidos")}</div>
                <div className="max-h-[48vh] overflow-y-auto pr-1">
                  <div className="grid grid-cols-2 gap-2">
                    {AI_AVATAR_PRESETS.map((preset) => {
                      const selected =
                        stripAssetVersion(profileDraftAvatar) === stripAssetVersion(preset.src);
                      return (
                        <button
                          key={preset.id}
                          type="button"
                          onClick={() => {
                            setProfileDraftAvatar(preset.src);
                            setProfileDraftAvatarFile(null);
                          }}
                          className={`rounded border p-1 transition ${
                            selected
                              ? "border-[#51ff74] bg-[#193b2a]"
                              : "border-[#3c5f8f] bg-[#0f2448] hover:border-[#8fd4ff]"
                          }`}
                        >
                          <img
                            src={preset.src}
                            alt={preset.name}
                            className="h-24 w-full rounded object-cover"
                            onError={(event) => {
                              event.currentTarget.style.display = "none";
                            }}
                          />
                          <div className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-[#bfdcff]">
                            {preset.name}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 pt-2 md:grid-cols-4">
            <button
              type="button"
              onClick={() => {
                setProfileDraftAvatar("");
                setProfileDraftAvatarFile(null);
              }}
              disabled={profileSavingPersonal}
              className="rounded border border-[#6f7ea0] bg-[#1b2a4a] px-2 py-2 text-xs font-semibold uppercase text-[#ccdaf7] transition hover:border-[#9fb4e6] disabled:cursor-not-allowed disabled:opacity-60"
            >{i18nT("lobby.sinFoto")}</button>
            <button
              type="button"
              onClick={resetProfileAvatar}
              disabled={profileSavingPersonal}
              className="rounded border border-[#d68d4a] bg-[#4f2f17] px-2 py-2 text-xs font-semibold uppercase text-[#ffd8ae] transition hover:border-[#ffb86b] disabled:cursor-not-allowed disabled:opacity-60"
            >{i18nT("lobby.restablecer")}</button>
            <button
              type="button"
              onClick={closeProfileModal}
              disabled={profileSavingPersonal}
              className="rounded border border-[#5f6f95] bg-[#192742] px-2 py-2 text-xs font-semibold uppercase text-[#c6d8ff] transition hover:border-[#8ea3d4] disabled:cursor-not-allowed disabled:opacity-60"
            >{i18nT("lobby.cancelar")}</button>
            <button
              type="button"
              onClick={saveProfileChanges}
              disabled={profileSavingPersonal}
              className="rounded border border-[#33d368] bg-[#14662f] px-2 py-2 text-xs font-semibold uppercase text-[#cbffe0] transition hover:border-[#6bf0a1] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {profileSavingPersonal ? i18nT("lobby.guardando") : i18nT("lobby.guardarPerfil")}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={specialBlockModalOpen}
        onCancel={closeSpecialBlockModal}
        footer={null}
        maskClosable={false}
        width={1200}
        title={<span className="font-semibold text-[#8fd4ff]">{i18nT("lobby.bloqueEspecial")}</span>}
      >
        <div className="max-h-[78vh] space-y-4 overflow-y-auto pr-1">
          <div className="text-sm text-[#a9c4e9]">{i18nT("lobby.eligeElBloqueEspecialParaTuPerfil")}</div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
            {SKIN_SECTION_ORDER.map((section) => {
              const entries = ensureArray(specialSkinSections?.[section.key]);
              return (
                <div
                  key={`special-section-${section.key}`}
                  className="min-h-0 rounded border border-[#2c4a78] bg-[#0b1b38] p-2"
                >
                  <div className="mb-2 flex items-center justify-between">
                    <div className={`text-xs font-bold uppercase tracking-[0.14em] ${section.accent}`}>
                      {section.label}
                    </div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#9ac6f7]">
                      {entries.length}
                    </div>
                  </div>
                  {entries.length === 0 ? (
                    <div className="text-[11px] text-[#7fa2d3]">{i18nT("lobby.sinSkinsEnEstaCategoria")}</div>
                  ) : (
                    <div className="h-[46vh] max-h-[420px] min-h-[260px] space-y-2 overflow-y-auto pr-1">
                      {entries.map((entry) => {
                        const preset = entry?.preset || {};
                        const isSelected = draftSpecialBlockId === preset.id;
                        const isLocked = entry?.requiredProduct?.id && !entry?.unlocked;
                        const accessType = entry?.accessType || "";
                        const productLabel =
                          String(
                            entry?.resolvedProduct?.name?.es ||
                              entry?.resolvedProduct?.name?.en ||
                              entry?.requiredProduct?.id ||
                              ""
                          ).trim() || "Producto";
                        const accessLabel =
                          accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER
                            ? entry?.unlocked
                              ? "Habilitado"
                              : `Ganada: requiere Top #1 ${
                                  MODE_LABELS[
                                    String(entry?.requiredProduct?.earnedMode || "") === "azar"
                                      ? "dice"
                                      : String(entry?.requiredProduct?.earnedMode || "")
                                  ] || ""
                                }`.trim()
                            : entry?.requiredProduct?.id
                              ? entry?.unlocked
                                ? "Desbloqueado"
                                : `De pago: ${productLabel}`
                              : "Gratis";

                        return (
                          <button
                            key={`special-card-${preset.id}`}
                            type="button"
                            onClick={() => handleDraftSpecialSkinSelection(preset.id)}
                            className={`w-full rounded border p-2 text-left transition ${
                              isSelected
                                ? "border-[#4dff92] bg-[#143927]"
                                : isLocked && accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER
                                  ? "border-[#b679f8] bg-[#30174f] hover:border-[#d7afff]"
                                  : isLocked
                                    ? "border-[#d2a04f] bg-[#3f2e12] hover:border-[#ffcf72]"
                                    : "border-[#3c5f8f] bg-[#0f2448] hover:border-[#8fd4ff]"
                            }`}
                          >
                            <div className="mb-2 flex items-center gap-2">
                              <img
                                src={preset.image}
                                alt={preset.label}
                                className="h-12 w-12 rounded border border-[#66c6ff88] bg-[#12345a] object-cover"
                              />
                              <div
                                className="min-w-0 truncate text-xs font-semibold uppercase tracking-wide text-[#d5ecff]"
                                title={preset.label}
                              >
                                {preset.label}
                              </div>
                            </div>
                            <div
                              className="truncate text-[10px] font-semibold uppercase tracking-[0.14em] text-[#b9d7ff]"
                              title={accessLabel}
                            >
                              {accessLabel}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-2 gap-2 pt-1">
            <button
              type="button"
              onClick={closeSpecialBlockModal}
              className="rounded border border-[#6f7ea0] bg-[#1b2a4a] px-2 py-2 text-xs font-semibold uppercase text-[#ccdaf7] transition hover:border-[#9fb4e6]"
            >{i18nT("lobby.cancelar")}</button>
            <button
              type="button"
              onClick={saveSpecialBlockSelection}
              className="rounded border border-[#33d368] bg-[#14662f] px-2 py-2 text-xs font-semibold uppercase text-[#cbffe0] transition hover:border-[#6bf0a1]"
            >{i18nT("lobby.guardar")}</button>
          </div>
        </div>
      </Modal>

      <Modal
        open={boardThemeModalOpen}
        onCancel={closeBoardThemeModal}
        footer={null}
        maskClosable={false}
        width={1040}
        title={<span className="font-semibold text-[#8fd4ff]">{i18nT("lobby.colorDelTablero")}</span>}
      >
        <div className="max-h-[78vh] space-y-4 overflow-y-auto pr-1">
          <div className="text-sm text-[#a9c4e9]">{i18nT("lobby.eligeUnTemaPredefinidoParaElTableroDe")}</div>

          <div className="grid grid-cols-2 gap-2">
            {Object.values(BOARD_THEME_PRESET_MAP).map((preset) => {
              const isSelected = draftBoardThemeId === preset.id;
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => setDraftBoardThemeId(preset.id)}
                  className={`rounded border p-2 text-left transition ${
                    isSelected
                      ? "border-[#4dff92] bg-[#143927]"
                      : "border-[#3c5f8f] bg-[#0f2448] hover:border-[#8fd4ff]"
                  }`}
                >
                  <div className="mb-2 flex items-center gap-2">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded border border-[#ffffff26] bg-[#0d223f]">
                      <div
                        className="h-8 w-8 rounded border border-[#ffffff33]"
                        style={getBoardDualSquaresStyle(preset)}
                      />
                    </div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-[#d5ecff]">
                      {preset.label}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-2 gap-2 pt-1">
            <button
              type="button"
              onClick={closeBoardThemeModal}
              className="rounded border border-[#6f7ea0] bg-[#1b2a4a] px-2 py-2 text-xs font-semibold uppercase text-[#ccdaf7] transition hover:border-[#9fb4e6]"
            >{i18nT("lobby.cancelar")}</button>
            <button
              type="button"
              onClick={saveBoardThemeSelection}
              className="rounded border border-[#33d368] bg-[#14662f] px-2 py-2 text-xs font-semibold uppercase text-[#cbffe0] transition hover:border-[#6bf0a1]"
            >{i18nT("lobby.guardar")}</button>
          </div>
        </div>
      </Modal>

      <Modal
        open={commonBlocksModalOpen}
        onCancel={closeCommonBlocksModal}
        footer={null}
        maskClosable={false}
        width={1200}
        title={<span className="font-semibold text-[#8fd4ff]">{i18nT("lobby.bloquesComunes")}</span>}
      >
        <div className="max-h-[78vh] space-y-4 overflow-y-auto pr-1">
          <div className="text-sm text-[#a9c4e9]">{i18nT("lobby.seleccionaLaImagenQueQuieresUsarParaLos")}</div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
            {SKIN_SECTION_ORDER.map((section) => {
              const entries = ensureArray(commonSkinSections?.[section.key]);
              return (
                <div
                  key={`common-section-${section.key}`}
                  className="min-h-0 rounded border border-[#2c4a78] bg-[#0b1b38] p-2"
                >
                  <div className="mb-2 flex items-center justify-between">
                    <div className={`text-xs font-bold uppercase tracking-[0.14em] ${section.accent}`}>
                      {section.label}
                    </div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#9ac6f7]">
                      {entries.length}
                    </div>
                  </div>
                  {entries.length === 0 ? (
                    <div className="text-[11px] text-[#7fa2d3]">{i18nT("lobby.sinSkinsEnEstaCategoria")}</div>
                  ) : (
                    <div className="h-[46vh] max-h-[420px] min-h-[260px] space-y-2 overflow-y-auto pr-1">
                      {entries.map((entry) => {
                        const preset = entry?.preset || {};
                        const isSelected = draftCommonBlockThemeId === preset.id;
                        const isLocked = entry?.requiredProduct?.id && !entry?.unlocked;
                        const accessType = entry?.accessType || "";
                        const productLabel =
                          String(
                            entry?.resolvedProduct?.name?.es ||
                              entry?.resolvedProduct?.name?.en ||
                              entry?.requiredProduct?.id ||
                              ""
                          ).trim() || "Producto";
                        const accessLabel =
                          accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER
                            ? entry?.unlocked
                              ? "Habilitado"
                              : `Ganada: requiere Top #1 ${
                                  MODE_LABELS[
                                    String(entry?.requiredProduct?.earnedMode || "") === "azar"
                                      ? "dice"
                                      : String(entry?.requiredProduct?.earnedMode || "")
                                  ] || ""
                                }`.trim()
                            : entry?.requiredProduct?.id
                              ? entry?.unlocked
                                ? "Desbloqueado"
                                : `De pago: ${productLabel}`
                              : "Gratis";

                        return (
                          <button
                            key={`common-card-${preset.id}`}
                            type="button"
                            onClick={() => handleDraftCommonSkinSelection(preset.id)}
                            className={`w-full rounded border p-2 text-left transition ${
                              isSelected
                                ? "border-[#4dff92] bg-[#143927]"
                                : isLocked && accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER
                                  ? "border-[#b679f8] bg-[#30174f] hover:border-[#d7afff]"
                                  : isLocked
                                    ? "border-[#d2a04f] bg-[#3f2e12] hover:border-[#ffcf72]"
                                    : "border-[#3c5f8f] bg-[#0f2448] hover:border-[#8fd4ff]"
                            }`}
                          >
                            <div className="mb-2 flex items-center gap-2">
                              <img
                                src={preset.image}
                                alt={preset.label}
                                className="h-12 w-12 shrink-0 rounded border border-[#66c6ff88] bg-[#12345a] object-cover"
                              />
                              <div
                                className="min-w-0 truncate text-xs font-semibold uppercase tracking-wide text-[#d5ecff]"
                                title={preset.label}
                              >
                                {preset.label}
                              </div>
                            </div>
                            <div
                              className="truncate text-[10px] font-semibold uppercase tracking-[0.14em] text-[#b9d7ff]"
                              title={accessLabel}
                            >
                              {accessLabel}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-2 gap-2 pt-1">
            <button
              type="button"
              onClick={closeCommonBlocksModal}
              className="rounded border border-[#6f7ea0] bg-[#1b2a4a] px-2 py-2 text-xs font-semibold uppercase text-[#ccdaf7] transition hover:border-[#9fb4e6]"
            >{i18nT("lobby.cancelar")}</button>
            <button
              type="button"
              onClick={saveCommonBlocksSelection}
              className="rounded border border-[#33d368] bg-[#14662f] px-2 py-2 text-xs font-semibold uppercase text-[#cbffe0] transition hover:border-[#6bf0a1]"
            >{i18nT("lobby.guardar")}</button>
          </div>
        </div>
      </Modal>

      <Modal
        open={Boolean(lockedSkinPrompt)}
        onCancel={() => setLockedSkinPrompt(null)}
        footer={null}
        maskClosable={false}
        title={
          <span className="font-semibold text-[#ffffff]">
            {lockedSkinPrompt?.accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER
              ? "Skin Ganada"
              : "Skin de Tienda"}
          </span>
        }
      >
        <div className="space-y-4">
          <div className="rounded border border-[#8b5b2b] bg-[#2f1f12] p-3 text-sm text-[#ffffff]">
            {String(lockedSkinPrompt?.message || "").trim() ||
              (lockedSkinPrompt?.accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER
                ? "Este skin se desbloquea solo para el mejor jugador de torneos."
                : "Lo siento este Skin solo esta disponible si lo compras en la tienda.")}
          </div>
          <div className="text-xs uppercase tracking-[0.16em] text-[#e8f2ff]">
            Skin: <span className="font-semibold text-[#ffffff]">{lockedSkinPrompt?.skinLabel}</span>
          </div>
          <div className="text-xs uppercase tracking-[0.16em] text-[#e8f2ff]">
            Producto:{" "}
            <span className="font-semibold text-[#ffffff]">{lockedSkinPrompt?.productLabel}</span>
          </div>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <button
              type="button"
              onClick={() => setLockedSkinPrompt(null)}
              className="rounded border border-[#6f7ea0] bg-[#1b2a4a] px-2 py-2 text-xs font-semibold uppercase text-[#ccdaf7] transition hover:border-[#9fb4e6]"
            >{i18nT("lobby.cerrar")}</button>
            {lockedSkinPrompt?.accessType === STORE_PRODUCT_ACCESS_EARNED_TOP_PLAYER ? (
              <button
                type="button"
                onClick={() => {
                  setLockedSkinPrompt(null);
                  setSpecialBlockModalOpen(false);
                  setCommonBlocksModalOpen(false);
                  setMatchmakingQuickView("torneos");
                  fetchModeWinnersLeaderboard(false).catch((error) => console.log(error));
                }}
                className="rounded border border-[#c492ff] bg-[#4a2570] px-2 py-2 text-xs font-semibold uppercase text-[#f3e8ff] transition hover:border-[#ddb9ff]"
              >{i18nT("lobby.verRanking")}</button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  const blockType =
                    lockedSkinPrompt?.blockType === BLOCK_TYPE_COMMON
                      ? BLOCK_TYPE_COMMON
                      : BLOCK_TYPE_SPECIAL;
                  const productId = normalizeStoreProductId(lockedSkinPrompt?.productId || "");
                  setLockedSkinPrompt(null);
                  setSpecialBlockModalOpen(false);
                  setCommonBlocksModalOpen(false);
                  if (!productId) {
                    navigate("/game/store");
                    return;
                  }
                  navigate(`/game/store?type=${blockType}&product=${encodeURIComponent(productId)}`);
                }}
                className="rounded border border-[#5fd6ff] bg-[#0d4e76] px-2 py-2 text-xs font-semibold uppercase text-[#e8f8ff] transition hover:border-[#8de8ff]"
              >{i18nT("lobby.irALaTienda")}</button>
            )}
          </div>
        </div>
      </Modal>
    </ConfigProvider>
  );
};

export default Index;



